// The single source of truth for the catalog. `translations.ts` derives
// BOTH the `TRANSLATION` type and the dotted-path `i18n` object from
// this object, so a key is written exactly once — here.
//
// Do NOT add `as const`: it would narrow every leaf to its Spanish
// literal and make `i18n.X.Y` typed as the text instead of the key.
export const ES = {
  WELCOME: 'Bienvenido a Neo App',
  HEALTH_STATUS: {
    TITLE: 'Estado de NeoCRM',
    SUBTITLE: 'Estado en tiempo real de nuestros servicios',
    OVERALL_OPERATIONAL: 'Todos los sistemas operativos',
    OVERALL_DEGRADED: 'Rendimiento degradado en algunos servicios',
    OVERALL_DOWN: 'Interrupción en uno o más servicios',
    STATUS_OPERATIONAL: 'Operativo',
    STATUS_DEGRADED: 'Degradado',
    STATUS_DOWN: 'Interrupción',
    CATEGORY_NEOCRM: 'Servicios de NeoCRM',
    CATEGORY_INFRASTRUCTURE: 'Infraestructura',
    CATEGORY_OTHER: 'Otros',
    UPTIME_30D: 'Disponibilidad 30d: {{value}}%',
    LATENCY_MS: '{{value}} ms',
    INCIDENTS_TITLE: 'Incidentes recientes',
    INCIDENTS_EMPTY: 'Sin incidentes en los últimos 30 días.',
    INCIDENT_ONGOING: 'En curso',
    INCIDENT_RESOLVED: 'Resuelto',
    INCIDENT_SINCE: 'Desde {{date}}',
    INCIDENT_RESOLVED_AT: 'Resuelto {{date}}',
    LAST_CHECKED: 'Última verificación: {{time}}',
    STALE: '(datos con retraso)',
    LOAD_ERROR:
      'No pudimos cargar el estado en este momento. Intenta de nuevo en unos minutos.',
    NO_DATA: 'Sin datos de verificación aún',
  },
  FORM_VALIDATIONS: {
    REQUIRED: '{{field}} es requerido.',
    MIN_CHARACTERS: '{{field}} debe tener al menos {{length}} carácteres.',
    MAX_CHARACTERS: '{{field}} debe tener menos de {{length}} caracteres.',
    INVALID_USERNAME_FORMAT:
      'El nombre de usuario solo puede contener letras, números, guiones bajos y guiones.',
    DONT_MATCH: '{{field}} no son iguales',
    INVALID_PHONE: 'Número de teléfono inválido',
    INVALID_PHONE_FORMAT: 'El formato del teléfono no es válido',
    INVALID_PHONE_LENGTH: 'El teléfono debe tener entre 10 y 15 dígitos',
    EMAIL_REQUIRED: 'El correo electrónico es requerido',
    INVALID_EMAIL: 'El correo electrónico no es válido',
    PASSWORD_REQUIRED: 'La contraseña es requerida',
    PASSWORD_MIN_8: 'La contraseña debe tener al menos 8 caracteres',
    CONFIRM_PASSWORD_REQUIRED: 'Debes confirmar tu contraseña',
    PASSWORDS_DO_NOT_MATCH: 'Las contraseñas no coinciden',
    PASSWORD_REQUIREMENTS_NOT_MET: 'La contraseña no cumple con los requisitos',
    COUNTRY_REQUIRED: 'Debes seleccionar un país',
    ACCEPT_TERMS_REQUIRED:
      'Debes aceptar los términos y condiciones para continuar',
  },
  INBOX: {
    OPEN: 'Notificaciones',
    TITLE: 'Notificaciones',
    EMPTY: 'No tienes notificaciones.',
    MARK_ALL_READ: 'Marcar todo como leído',
    DISMISS: 'Descartar',
    FILTER_ALL: 'Todos',
    TYPE_UNKNOWN: 'Otro',
    VIEW_ALL: 'Ver todas',
    SHOW_ONLY_UNREAD: 'Sólo no leídas',
    GROUP_TODAY: 'Hoy',
    GROUP_YESTERDAY: 'Ayer',
    PRIORITY: {
      CRITICAL: 'Crítico',
      HIGH: 'Alto',
      NORMAL: 'Normal',
      LOW: 'Bajo',
    },
    ACTIONS: {
      OPEN_INVOICE: 'Ver factura',
      REMIND_LATER: 'Recordar mañana',
      RETRY_PAYMENT: 'Reintentar pago',
      REVIEW: 'Revisar',
      REVIEW_ACTIVITY: 'Ver actividad',
      VIEW_INVOICE: 'Ver factura',
      OPEN_POS: 'Abrir POS',
      OPEN_INVOICE_EDIT: 'Abrir factura',
      OPEN_MENU: 'Abrir menú',
      VIEW_PO: 'Ver orden de compra',
      VIEW_SHIFT: 'Ver turno',
      VIEW_PRODUCT: 'Ver producto',
      VIEW_PERIOD: 'Ver período',
      VIEW_ENTRY: 'Ver asiento',
      VIEW_BUDGET: 'Ver presupuesto',
      VIEW_BATCH: 'Ver lote e-CF',
      VIEW_CLAIM: 'Ver reclamo',
      MANAGE_SUB: 'Gestionar suscripción',
      GO_HOME: 'Ir al inicio',
      UPDATE_CARD: 'Actualizar tarjeta',
    },
    DETAIL: {
      INVOICE_NUMBER: 'Factura',
      ORDER_NUMBER: 'Orden',
      TOTAL: 'Total',
      INVOICE_TOTAL: 'Total factura',
      AMOUNT: 'Monto',
      CUSTOMER: 'Cliente',
      RETURNED_BY: 'Devuelto por',
      PROVIDER: 'Proveedor',
      DUE_DATE: 'Vence',
      END_DATE: 'Fin',
      REASON: 'Motivo',
      REQUESTED_AT: 'Solicitado',
      DEVICE: 'Dispositivo',
      BRANCH: 'Sucursal',
      CASHIER: 'Cajero',
      VARIANCE: 'Diferencia',
      CLOSED_AT: 'Cierre',
      PRODUCT: 'Producto',
      CURRENT_STOCK: 'Existencia',
      THRESHOLD: 'Mínimo',
      PERIOD: 'Período',
      JOURNAL_ENTRY: 'Asiento',
      IMBALANCE: 'Descuadre',
      BUDGET: 'Presupuesto',
      ACTUAL: 'Real',
      EXECUTION: 'Ejecución',
      FAILED_COUNT: 'Comprobantes fallidos',
      ITEM: 'Concepto',
      CLAIM_ID: 'Reclamo',
      APPROVAL_TYPE: 'Tipo de aprobación',
      CREDIT_LIMIT: 'Límite de crédito',
      OUTSTANDING_BALANCE: 'Saldo pendiente',
      PROJECTED_OUTSTANDING: 'Saldo proyectado',
      PLAN: 'Plan',
      ACCOUNT: 'Cuenta',
      PROMO: 'Promoción',
      VALID_UNTIL: 'Válido hasta',
      MESSAGE: 'Mensaje',
      ACTIVE_UNTIL: 'Vigente hasta',
      PAYMENT_METHOD: 'Método de pago',
      PAID_AT: 'Fecha de pago',
      NEXT_BILLING: 'Próximo cobro',
      CARD_EXPIRES: 'Vence',
    },
    TYPE: {
      SECURITY_PASSWORD_RESET_REQUESTED: {
        TITLE: 'Solicitud de cambio de contraseña',
        SUMMARY:
          'Solicitaste un cambio de contraseña. Revisa tu correo para completar la acción.',
      },
      SECURITY_PASSWORD_CHANGED: {
        TITLE: 'Contraseña cambiada',
        SUMMARY: 'Tu contraseña fue actualizada.',
      },
      SECURITY_EMAIL_VERIFICATION_SENT: {
        TITLE: 'Verificación de correo enviada',
        SUMMARY: 'Enviamos un enlace de verificación a tu correo.',
      },
      SECURITY_ACCOUNT_EXISTS_ATTEMPT: {
        TITLE: 'Intento de registro con tu correo',
        SUMMARY: 'Alguien intentó registrarse con esta cuenta.',
      },
      ACCOUNT_WELCOME: {
        TITLE: 'Bienvenido a Neogeniux',
        SUMMARY: 'Tu cuenta está lista. Comienza a explorar la plataforma.',
      },
      SALES_INVOICE_SENT: {
        TITLE: 'Factura enviada',
        SUMMARY: 'Se envió una factura al cliente.',
      },
      SALES_INVOICE_CONFIRMATION: {
        TITLE: 'Confirmación de factura',
        SUMMARY: 'Se registró la factura.',
      },
      INVOICE_RETURNED_TO_SELLER: {
        TITLE: 'Ticket devuelto por caja',
        SUMMARY:
          'Caja te devolvió el ticket para editar. Ábrelo en POS para modificarlo y volver a enviarlo.',
      },
      MARKETING_PROMO: {
        TITLE: 'Promoción disponible',
        SUMMARY: 'Nueva promoción disponible para tu cuenta.',
      },
      PROCUREMENT_PURCHASE_ORDER_SENT: {
        TITLE: 'Orden de compra enviada',
        SUMMARY: 'Se envió una orden de compra al proveedor.',
      },
      BILLING_INVOICE_ISSUED: {
        TITLE: 'Nueva factura de suscripción',
        SUMMARY: 'Se generó una factura de tu suscripción.',
      },
      BILLING_INVOICE_DUE: {
        TITLE: 'Factura por vencer',
        SUMMARY: 'Una factura de tu suscripción está próxima a vencer.',
      },
      BILLING_INVOICE_PAID: {
        TITLE: 'Pago procesado',
        SUMMARY: 'Cobramos tu suscripción correctamente.',
      },
      BILLING_CARD_EXPIRING_SOON: {
        TITLE: 'Tu tarjeta vence pronto',
        SUMMARY:
          'Actualiza tu método de pago para evitar interrupciones en el cobro.',
      },
      BILLING_FISCAL_DOCUMENT_READY: {
        TITLE: 'Comprobante fiscal listo',
        SUMMARY: 'Ya puedes descargar el comprobante fiscal.',
      },
      BILLING_PAYMENT_FAILED: {
        TITLE: 'Pago rechazado',
        SUMMARY:
          'No pudimos procesar tu pago. Verifica tu método de pago para reactivar.',
      },
      BILLING_SUBSCRIPTION_DEACTIVATED: {
        TITLE: 'Suscripción desactivada',
        SUMMARY: 'Tu suscripción fue desactivada por falta de pago.',
      },
      SUBSCRIPTION_LIFECYCLE_GRACE_T_MINUS_3: {
        TITLE: 'Renovación en 3 días',
        SUMMARY:
          'Agrega tu método de pago para que se cobre automáticamente y no pierdas el acceso.',
      },
      SUBSCRIPTION_LIFECYCLE_GRACE_ENDED: {
        TITLE: 'Suscripción vencida',
        SUMMARY: 'Agrega tu método de pago para continuar sin interrupciones.',
      },
      SUBSCRIPTION_LIFECYCLE_PAST_DUE_DAY_3: {
        TITLE: 'Pago pendiente — día 3',
        SUMMARY: 'Tu suscripción está atrasada 3 días. Regulariza el pago.',
      },
      SUBSCRIPTION_LIFECYCLE_PAST_DUE_DAY_7: {
        TITLE: 'Pago pendiente — día 7',
        SUMMARY:
          'Tu suscripción está atrasada 7 días. Actúa hoy para evitar suspensión.',
      },
      SUBSCRIPTION_LIFECYCLE_SUSPEND_WARN_24H: {
        TITLE: 'Suspensión en 24 horas',
        SUMMARY:
          'Tu cuenta será suspendida en 24 horas si no regularizas el pago.',
      },
      SUBSCRIPTION_LIFECYCLE_SUSPENDED: {
        TITLE: 'Cuenta suspendida',
        SUMMARY:
          'Tu cuenta fue suspendida. Agrega un método de pago desde el panel de facturación para restablecer el acceso.',
      },
      ACCOUNTING_PERIOD_CLOSING_REMINDER: {
        TITLE: 'Recordatorio de cierre de período',
        SUMMARY: 'Un período contable está por cerrar.',
      },
      ACCOUNTING_GL_IMBALANCE_ALERT: {
        TITLE: 'Alerta de desbalance contable',
        SUMMARY: 'Un asiento contable no balancea. Requiere revisión.',
      },
      ACCOUNTING_BUDGET_OVERAGE: {
        TITLE: 'Presupuesto excedido',
        SUMMARY: 'Uno de tus presupuestos cruzó el umbral configurado.',
      },
      ECF_MAX_RETRY_REACHED: {
        TITLE: 'e-CF: reintentos agotados',
        SUMMARY:
          'Un lote de e-CF alcanzó el máximo de reintentos y requiere aceptación manual.',
      },
      POS_CASH_SHIFT_CLOSED: {
        TITLE: 'Turno de caja cerrado',
        SUMMARY: 'Se cerró un turno de caja en tu sucursal.',
      },
      INVENTORY_LOW_STOCK: {
        TITLE: 'Inventario bajo',
        SUMMARY: 'Un producto cayó por debajo del stock mínimo.',
      },
      PAYMENT_CLAIMS_FILED: {
        TITLE: 'Nueva reclamación',
        SUMMARY: 'Un negocio abrió una reclamación de pago.',
      },
      PAYMENT_CLAIMS_APPROVED: {
        TITLE: 'Reclamación aprobada',
        SUMMARY: 'Se aprobó una reclamación de pago con crédito.',
      },
      PAYMENT_CLAIMS_REJECTED: {
        TITLE: 'Reclamación rechazada',
        SUMMARY: 'Una reclamación de pago fue rechazada.',
      },
      APPROVALS_PENDING: {
        TITLE: 'Aprobación pendiente',
        SUMMARY:
          'Un usuario solicitó tu aprobación. Toca "Revisar" para abrir la solicitud.',
      },
      APPROVALS_REQUESTED: {
        TITLE: 'Solicitud enviada',
        SUMMARY:
          'Tu solicitud fue enviada a los aprobadores. Te avisaremos cuando reciba respuesta.',
      },
      APPROVALS_APPROVED: {
        TITLE: 'Solicitud aprobada',
        SUMMARY: 'Tu solicitud de aprobación fue aprobada.',
      },
      APPROVALS_REJECTED: {
        TITLE: 'Solicitud rechazada',
        SUMMARY: 'Tu solicitud de aprobación fue rechazada.',
      },
      SYSTEM_ANNOUNCEMENT_INFO: {
        TITLE: 'Aviso del sistema',
        SUMMARY: 'Aviso general del equipo Neogeniux.',
      },
      SYSTEM_ANNOUNCEMENT_WARNING: {
        TITLE: 'Aviso importante',
        SUMMARY: 'Revisa este aviso importante del equipo.',
      },
      SYSTEM_ANNOUNCEMENT_CRITICAL: {
        TITLE: 'Aviso crítico',
        SUMMARY: 'Acción requerida sobre un aviso crítico.',
      },
      PLATFORM_FISCAL_NCF_LOW: {
        TITLE: 'Secuencia NCF por agotarse',
        SUMMARY:
          'Una secuencia NCF de la plataforma está por agotarse o vencer. Registra o extiende la secuencia.',
      },
      PLATFORM_FISCAL_CREDIT_BLOCKED: {
        TITLE: 'Comprobante bloqueado: falta RNC',
        SUMMARY:
          'No se pudo emitir el comprobante: el tipo asignado requiere RNC y el cliente no tiene uno registrado.',
      },
      PLATFORM_FISCAL_NO_SEQUENCE: {
        TITLE: 'Sin secuencia NCF disponible',
        SUMMARY:
          'No se pudo emitir el comprobante: no hay secuencia NCF disponible para el tipo asignado.',
      },
      PLATFORM_FISCAL_CREDIT_NOTE_NO_SEQUENCE: {
        TITLE: 'Sin secuencia para nota de crédito',
        SUMMARY:
          'No se pudo emitir la nota de crédito: no hay secuencia NCF (B04/E34) disponible.',
      },
      PLATFORM_FISCAL_NO_TAX_DEFINITION: {
        TITLE: 'Falta configuración de ITBIS',
        SUMMARY:
          'No se pudo emitir el comprobante con ITBIS: el negocio de la plataforma no tiene el impuesto ITBIS (18%) configurado.',
      },
      BILLING_OVERAGE_RECONCILE_STRANDED: {
        TITLE: 'Excedente sin facturar',
        SUMMARY:
          'Hay excedente e-CF acumulado sin facturar pendiente de conciliación.',
      },
      BILLING_INVOICE_REFUNDED: {
        TITLE: 'Factura reembolsada',
        SUMMARY: 'Se registró un reembolso sobre una factura de suscripción.',
      },
    },
  },
  HTTP_ERRORS: {
    RESTAURANT_TAB_ITEM_VOID_APPROVAL_REQUIRED:
      'Anular este ítem requiere el permiso de anulación o la aprobación de un gerente.',
    RESTAURANT_VOID_APPROVAL_INVALID:
      'La aprobación del gerente es inválida, expiró, ya se usó o no corresponde a este ítem.',
    AUTHENTICATION_FAILED:
      'Usuario y/o contraseña  incorrectos. Verifica tus credenciales.',
    EMAIL_NOT_VERIFIED:
      'Correo no verificado. Por favor verifica tu correo antes de iniciar sesión.',
    ACCOUNT_LOCKED:
      'Cuenta temporalmente bloqueada debido a múltiples intentos fallidos de inicio de sesión. Intenta más tarde.',
    MAX_ATTEMPTS_EXCEEDED:
      'Has superado el límite de intentos. Inicia sesión nuevamente.',
    ACCOUNT_DISABLED:
      'Esta cuenta ha sido deshabilitada. Contacta al administrador para más información.',
    CATEGORY_GENERIC_PROTECTED:
      'La categoría genérica no se puede eliminar: es la categoría predeterminada que se asigna automáticamente a los productos nuevos.',
    USER_NOT_ASSOCIATED_WITH_BUSINESS:
      'El usuario no está asociado con un negocio.',
    ACCOUNT_NOT_FULLY_ACTIVATED:
      'La cuenta no ha sido completamente activada. Por favor verifica tu correo primero.',
    REFRESH_TOKEN_EXPIRED:
      'Tu sesión ha expirado. Por favor inicia sesión nuevamente.',
    REFRESH_TOKEN_REVOKED:
      'Tu sesión fue cerrada. Por favor inicia sesión nuevamente.',
    REFRESH_TOKEN_REUSE_DETECTED:
      'Se detectó un problema de seguridad con tu sesión. Por favor inicia sesión nuevamente.',
    SESSION_LIMIT_EXCEEDED:
      'Se excedió el límite de sesiones activas. Cierra sesión en otros dispositivos e intenta de nuevo.',
    EMAIL_ALREADY_REGISTERED:
      'Este correo ya está registrado. Por favor usa un correo diferente o intenta iniciar sesión.',
    USER_EMAIL_ALREADY_EXISTS: 'Otro usuario del negocio ya usa este correo.',
    USER_USERNAME_ALREADY_EXISTS:
      'Otro usuario del negocio ya usa este nombre de usuario.',
    INVALID_VERIFICATION_TOKEN:
      'Token de verificación inválido. Por favor intenta registrarte nuevamente.',
    VERIFICATION_TOKEN_EXPIRED:
      'El token de verificación ha expirado. Por favor intenta registrarte nuevamente.',
    ACCOUNT_ALREADY_VERIFIED:
      'La cuenta ya ha sido verificada. Puedes iniciar sesión ahora.',
    EMAIL_SEND_FAILED:
      'No se pudo enviar el correo de verificación. Por favor intenta más tarde.',
    INVALID_RESET_TOKEN: 'Token de restablecimiento de contraseña inválido.',
    RESET_TOKEN_EXPIRED:
      'El token de restablecimiento de contraseña ha expirado.',
    RESET_TOKEN_ALREADY_USED:
      'El token de restablecimiento de contraseña ya ha sido utilizado.',
    INTERNAL_SERVER_ERROR:
      'Error interno del servidor. Por favor intenta más tarde.',
    BAD_REQUEST:
      'La solicitud contiene datos inválidos. Por favor verifica tu entrada.',
    ROLE_PERMISSION_BRANCH_REQUIRED:
      'Debes seleccionar una sucursal activa para editar los permisos del rol.',
    FORBIDDEN: 'No tienes permiso para acceder a este recurso.',
    NOT_FOUND: 'El recurso solicitado no fue encontrado.',
    PROCUREMENT_PO_NO_ITEMS:
      'La orden de compra requiere al menos una línea con producto.',
    PROCUREMENT_PO_BRANCH_REQUIRED:
      'Selecciona una sucursal en la barra lateral antes de guardar la orden de compra.',
    RESELLER_USER_NOT_ACCEPTED_YET:
      'Este reseller aún no ha aceptado la invitación. Reenvía la invitación en lugar de resetear contraseña.',
    RESELLER_USER_DISABLED:
      'El usuario del reseller está deshabilitado. Reactívalo antes de enviar un link de reset.',
    QUOTA_EXCEEDED:
      'Alcanzaste el límite de tu plan. Actualiza tu suscripción o contacta a soporte para ampliar la cuota.',
    FEATURE_NOT_AVAILABLE:
      'Esta funcionalidad no está incluida en tu plan actual. Contacta a soporte para activarla.',
    CAPABILITY_DENIED:
      'Tu plan no incluye acceso a esta funcionalidad. Contacta a soporte para activarla.',
    STRIPE_BILLING_UNAVAILABLE:
      'No pudimos actualizar tu facturación en Stripe. Verifica tu método de pago en /account y reintenta.',
    STRIPE_PM_ATTACHED_TO_DIFFERENT_CUSTOMER:
      'Esta tarjeta ya está asociada a otra cuenta en Stripe. Usa una tarjeta distinta o contacta a soporte.',
    PLAN_NOT_BILLABLE:
      'El plan seleccionado no está configurado para facturación recurrente. Contacta a soporte.',
    CHANGE_APPLY_FAILED:
      'No pudimos aplicar el cambio. Reintentaremos automáticamente; revisa tu método de pago.',
    PAYMENT_CLAIM_REASON_REQUIRED:
      'Debes describir el motivo de la reclamación.',
    PAYMENT_CLAIM_INVALID_AMOUNT: 'El monto de la reclamación no es válido.',
    PAYMENT_CLAIM_PENDING_EXISTS:
      'Ya tienes una reclamación pendiente sobre este cobro. Espera la revisión antes de enviar otra.',
    PAYMENT_CLAIM_NOT_FOUND: 'No se encontró la reclamación.',
    PAYMENT_CLAIM_ALREADY_REVIEWED:
      'Esta reclamación ya fue revisada y no puede modificarse.',
    PAYMENT_CLAIM_REJECT_REASON_REQUIRED:
      'Debes indicar el motivo del rechazo.',
    PAYMENT_CLAIM_NO_SUBSCRIPTION:
      'No se puede aplicar el crédito porque no hay suscripción activa.',
    ECF_INVOICE_NOT_FOUND:
      'Factura no encontrada. Refresca la lista e intenta nuevamente.',
    SALES_ORDER_DELIVERY_DATE_REQUIRED:
      'La fecha de entrega es obligatoria para órdenes tipo Pedido o Mostrador.',
    SALES_ORDER_PAID_IMMUTABLE:
      'La orden ya fue facturada y pagada — no se puede modificar ni cancelar.',
    SALES_ORDER_ITEM_LOCKED:
      'Este ítem ya fue enviado a producción — no se puede modificar ni eliminar. Puedes agregar ítems nuevos a la orden.',
    SALES_ORDER_HEADER_DISCOUNT_EXCEEDS_SUBTOTAL:
      'El descuento del encabezado no puede ser mayor que el subtotal de la orden.',
    SALES_ORDER_HAS_INVOICE:
      'La orden ya tiene una factura asociada — no se puede cancelar. Emite una nota de crédito sobre la factura para reversar.',
    SALES_ORDER_SENT_TO_PRODUCTION:
      'La orden ya fue enviada a producción — no se puede cancelar. Marca como entregada o gestiona la reversa desde la factura.',
    SALES_ORDER_COMMANDA_STARTED:
      'La cocina ya comenzó a preparar alguna comanda de esta orden — no se puede cancelar. Debes coordinar con la cocina.',
    ECF_NCF_TYPE_UNMAPPED:
      'El tipo de factura no tiene un NCF electrónico configurado.',
    ECF_NCF_NOT_ELECTRONIC:
      'Esta factura usa un NCF tradicional (serie B) y no puede enviarse a la facturación electrónica (e-CF) de la DGII.',
    ECF_NOTE_MISSING_REFERENCE:
      'Esta nota de crédito/débito no tiene una factura original vinculada (o la original no tiene NCF), por lo que no se puede construir la Información de Referencia que exige la DGII. Vincula la factura original antes de emitir.',
    DEBIT_NOTE_REQUIRES_ORIGINAL:
      'Una nota de débito debe hacer referencia a una factura original. Selecciona la factura que estás ajustando.',
    DEBIT_NOTE_ORIGINAL_NOT_FOUND:
      'La factura original seleccionada no se encontró o no tiene NCF.',
    NOTE_ORIGINAL_SERIES_MISMATCH:
      'La factura original debe ser del mismo tipo de comprobante que la nota: una nota electrónica (serie E) debe referenciar una factura electrónica (serie E), y una nota tradicional (serie B) una factura serie B.',
    INVOICE_CANCELLED_NO_PAYMENT:
      'No se puede registrar un pago sobre una factura anulada.',
    ECF_NCF_SEQUENCE_EXHAUSTED:
      'No hay NCFs disponibles en la secuencia. Configura o extiende la secuencia en Configuración → NCF.',
    ECF_REPAIR_ALREADY_ACCEPTED:
      'No se puede reparar: DGII ya aceptó este e-CF. Emite una nota de crédito para corregir el registro fiscal.',
    ECF_REPAIR_PAID_INVOICE_DELTA:
      'No se puede reparar: la factura está pagada/parcial y la reparación cambiaría el total. Emite una nota de crédito.',
    RENTAL_FIANZA_EXCEEDS_TOTAL:
      'La fianza calculada supera el total de los ítems. Ajusta el tipo o valor de fianza en Configuración → Alquileres, o quítala.',
    RENTAL_CANCEL_REQUIRES_CREDIT_NOTE:
      'No se puede cancelar: la factura del alquiler es un e-CF ya enviado a DGII. Emite una nota de crédito primero.',
    RENTAL_LOCKED_IN_PRODUCTION:
      'No se puede editar: el pedido ya está en producción o en una etapa posterior.',
    RENTAL_LOCKED_PAID: 'No se puede editar: el pedido ya está pagado.',
    RENTAL_PICKUP_NO_EMAIL:
      'El cliente no tiene un correo registrado. Actualiza el contacto del cliente.',
    RENTAL_PICKUP_NO_PHONE:
      'El cliente no tiene un teléfono registrado. Actualiza el contacto del cliente.',
    RENTAL_PICKUP_TOKEN_EXPIRED:
      'El código de recogido venció. Reenvía el código al cliente para generar uno nuevo.',
    RENTAL_PICKUP_TOKEN_INVALID:
      'Código de recogido inválido. Verifica el código o reenvíalo al cliente.',
    RENTAL_NO_ACTIVE_SHIFT:
      'No se puede emitir un reembolso sin un turno de caja abierto para el operador.',
    RENTAL_ALREADY_INVOICED: 'Este alquiler ya tiene una factura emitida.',
    RENTAL_ALREADY_RETURNED: 'La devolución de este alquiler ya fue procesada.',
    RENTAL_AVAILABILITY_CONFLICT:
      'Uno o más artículos no están disponibles en las fechas seleccionadas.',
    RENTAL_COMMANDA_STARTED:
      'No se puede modificar: el alquiler ya inició producción.',
    RENTAL_DELIVERY_REQUIRES_FULL_PAYMENT:
      'No se puede entregar el alquiler hasta que esté pagado por completo.',
    RENTAL_ABONO_PENDING:
      'Debes cobrar el abono mínimo antes de reservar o confirmar el pedido.',
    RENTAL_ADD_ITEMS_INVALID_STATE:
      'Solo se pueden agregar artículos mientras el pedido está confirmado, en producción o listo.',
    RENTAL_ADD_ITEMS_LOCKED_INVOICED:
      'No se pueden agregar artículos después de emitir la factura fiscal. Cancela y recrea el pedido.',
    RENTAL_ADD_ITEMS_EMPTY: 'No se agregaron artículos.',
    RENTAL_FIANZA_MISMATCH:
      'El monto de la fianza no coincide con el requerido.',
    RENTAL_FIANZA_REQUIRED: 'Debes cobrar la fianza antes de continuar.',
    RENTAL_INVALID_TRANSITION: 'Cambio de estado no válido para este alquiler.',
    RENTAL_LATE_REFUND_FORBIDDEN:
      'No se puede reembolsar la fianza después del plazo de devolución.',
    RENTAL_NOT_FOUND: 'Alquiler no encontrado.',
    RENTAL_NOT_READY_FOR_INVOICE: 'El alquiler no está listo para facturar.',
    RENTAL_RESERVATION_ALREADY_CONVERTED:
      'La reservación ya fue convertida en pedido.',
    RENTAL_RESERVATION_EXPIRED: 'La reservación venció.',
    RENTAL_RESERVATION_NOT_FOUND: 'Reservación no encontrada.',
    RENTAL_RETURN_DATE_REQUIRED: 'Indica la fecha de devolución.',
    RENTAL_STOCK_INSUFFICIENT:
      'No hay stock suficiente para uno o más artículos del alquiler.',
    RENTAL_ZERO_PRICE_FORBIDDEN:
      'No se permiten artículos con precio cero en el alquiler.',
    RENTAL_CUSTOMER_REQUIRED:
      'Asigna un cliente específico con correo y teléfono antes de avanzar el pedido.',
    RENTAL_SETTLEMENT_UNRESOLVED:
      'No se puede cerrar el alquiler mientras haya un cargo por daños o liquidación pendiente de pago.',
    RENTAL_DAMAGE_ADJUST_NOT_PENDING:
      'Solo se puede ajustar el cargo de un pedido que está pendiente de cobro por daños.',
    RENTAL_DAMAGE_ADJUST_INVALID_AMOUNT:
      'El nuevo cargo debe estar entre 0 y el monto pendiente actual.',
    RENTAL_NO_REFUNDABLE_DEPOSIT:
      'No queda saldo de fianza por devolver en este alquiler.',
    ECF_ACTIVATION_REQUIRES_CERTIFICATE:
      'No se puede activar e-CF: no hay un certificado DGII vigente cargado. Completa el asistente de Facturación Electrónica primero.',
    ECF_CERTIFICATE_DGII_AUTH_FAILED:
      'DGII rechazó el certificado. Verifica que el RNC esté delegado y que el certificado + contraseña sean válidos.',
    ECF_CERTIFICATE_PASSPHRASE_INVALID:
      'La contraseña no corresponde a este certificado .p12. Revisa la contraseña e inténtalo de nuevo.',
    ECF_CERTIFICATE_FILE_INVALID:
      'El archivo no es un certificado .p12 válido o no contiene un certificado. Sube el archivo original emitido por la entidad certificadora.',
    ECF_ENVIRONMENT_REQUIRES_CERTIFICATE:
      'No se puede cambiar el ambiente: no hay un certificado DGII vigente para el ambiente seleccionado. Sube el certificado primero en /configuration/ecf-certificates.',
    PRE_FACTURA_DISABLED_FOR_SURFACE:
      'La Pre Factura está deshabilitada en esta área. Debes emitir un comprobante fiscal (B/E) en su lugar.',
    PRE_FACTURA_NO_FISCAL_SEQUENCE:
      'La Pre Factura está deshabilitada en esta área y el negocio no tiene una secuencia fiscal (B/E) disponible. Registra una secuencia B01/B02/E31/E32 primero.',
    PRE_FACTURA_REQUIRES_FISCAL_SEQUENCE:
      'No puedes deshabilitar la Pre Factura: el negocio no tiene una secuencia fiscal (B/E) emisible. Registra una secuencia B01/B02/E31/E32 primero.',
    BRANCH_ACCESS_DENIED:
      'Ya no tienes acceso a esa sucursal. Te cambiamos a una sucursal disponible.',
    USER_BRANCH_ACCESS_REQUIRED:
      'Para dar acceso a varias sucursales, asigna al menos una sucursal con un rol.',
    ALL_BRANCHES_ACCESS_DENIED:
      'Esta vista incluye todas las sucursales y tu usuario no tiene acceso a todas.',
    USER_BRANCH_ACCESS_INVALID:
      'Una de las sucursales o roles asignados no pertenece a este negocio.',
    USER_SELF_BRANCH_ACCESS_FORBIDDEN:
      'No puedes cambiar tu propio acceso a sucursales. Pídeselo a un administrador.',
    TAX_DEFAULT_CANNOT_DELETE:
      'No puedes eliminar el impuesto predeterminado. Marca otro impuesto como predeterminado antes de eliminarlo.',
    TAX_SERVICE_CHARGE_CANNOT_DELETE:
      'No puedes eliminar la Propina Legal: facturación, POS, restaurante y el reporte de Propina 10% dependen de ella. Desactívala si no la usas.',
    TAX_DEFAULT_MISSING:
      'No se encontró un impuesto predeterminado para el negocio. Configura uno en /configuration/tax.',
    TAX_RATE_CHANGE_BLOCKED_ECF:
      'No se puede cambiar la tasa de un impuesto canónico DGII (ITBIS_18/ITBIS_16) con Facturación Electrónica activa. DGII exige la tasa fija del código; deshabilita e-CF antes de cambiar la tasa.',
    TAX_RATE_DUPLICATE:
      'Ya existe un impuesto activo con la tasa {{rate}}%. Reutilízalo o elige una tasa diferente.',
    TAX_CANONICAL_RATE_IMMUTABLE:
      'La tasa de los impuestos canónicos DGII (ITBIS_18/ITBIS_16/EXENTO/NO_APLICA) no se puede cambiar. Crea un impuesto nuevo con la nueva tasa y desactiva este.',
    REQUIRES_ONBOARDING:
      'Tu cuenta requiere completar el proceso de configuración. Serás redirigido automáticamente.',
    SUBSCRIPTION_CANCELLED:
      'La suscripción de tu empresa ha sido cancelada. Por favor contacta al soporte en info@neogeniux.com para reactivar tu cuenta.',
    SUBSCRIPTION_PAST_DUE:
      'Tu suscripción está vencida. Agrega un método de pago para continuar usando la plataforma.',
    RULES_ENGINE_UNBALANCED:
      'El asiento contable no cuadra: los débitos y créditos resueltos por la regla no coinciden. Revisa la configuración de cuentas o reintenta la operación. O comunicate con soporte si el problema persiste.',
    RECONCILE_LINES_UNBALANCED:
      'Las líneas propuestas no cuadran. La suma de débitos debe ser igual a la suma de créditos, hasta el centavo.',
    RECONCILE_ACCOUNT_INVALID:
      'Una de las cuentas seleccionadas no existe, está archivada o no acepta asientos (es una cuenta cabecera).',
    PARKED_EVENT_NOT_UNBALANCED:
      'Este evento no está en estado "parado por descuadre". Solo los eventos que fallaron con RULES_ENGINE_UNBALANCED pueden reconciliarse manualmente; para el resto, corrige la configuración y reintenta.',
    SUBSCRIPTION_SUSPENDED:
      'Tu suscripción está suspendida. Actualiza tu facturación para restaurar el acceso.',
    SUBSCRIPTION_INVOICE_OVERDUE:
      'Tienes una factura de suscripción vencida. Págala para continuar emitiendo comprobantes.',
    CATALOG_SUBSCRIPTION_PAST_DUE:
      'No puedes cambiar de plan mientras tengas una factura pendiente. Paga tu factura vencida primero para continuar.',
    BILLING_INTERVAL_LOCKED_UNPAID_INVOICE:
      'No puedes cambiar el ciclo de facturación mientras tengas una factura pendiente. Paga o anula la factura actual primero.',
    BILLING_ISSUED_AT_IN_FUTURE: 'La fecha de emisión no puede ser futura.',
    BILLING_COLLECTION_SKIPPED:
      'No se pudo procesar el cobro de esta factura en este momento. Actualiza la página e inténtalo de nuevo; si el problema persiste, contacta a soporte.',
    INVOICE_CREDIT_TERM_UNKNOWN:
      'El término de factura seleccionado no está disponible para este negocio.',
    BILLING_INVALID_PERIOD:
      'No se pudo generar la factura: el período de facturación es inválido. Contacta a soporte.',
    ADDON_REDUCTION_CAPACITY_IN_USE:
      'No puedes reducir esta capacidad porque todavía la estás usando. Libera {{mustFreeUp}} antes de continuar.',
    ADDON_REDUCTION_CAPACITY_IN_USE_BRANCH:
      'Para reducir esta capacidad debes eliminar el inventario de la sucursal, depurar sus facturas y reasignar sus usuarios. Comunícate con el administrador si lo necesitas. Debes liberar {{mustFreeUp}} sucursal(es).',
    ADDON_REDUCTION_CAPACITY_IN_USE_WAREHOUSE:
      'Para reducir esta capacidad debes eliminar el inventario del almacén. Debes liberar {{mustFreeUp}} almacén(es).',
    ADDON_REDUCTION_CAPACITY_IN_USE_USER:
      'Para reducir esta capacidad debes desactivar o eliminar los usuarios que exceden el límite. Debes liberar {{mustFreeUp}} usuario(s).',
    ADDON_REDUCTION_CAPACITY_IN_USE_KDS:
      'Para reducir esta capacidad debes desactivar o eliminar las pantallas KDS que exceden el límite. Debes liberar {{mustFreeUp}} pantalla(s).',
    ADDON_REDUCTION_SLOTS_INVALID:
      'La cantidad de espacios a reducir debe ser mayor que cero.',
    ADDON_REDUCTION_EXCEEDS_ACTIVE:
      'No puedes reducir más espacios de los que tiene contratados el negocio.',
    BILLING_SUBSCRIPTION_CANCELLING:
      'Esta suscripción está marcada para cancelarse al final del período. No se puede generar una nueva factura.',
    BILLING_COLLECTION_AUTHORITY_MISMATCH:
      'Esta factura la cobra el proveedor de suscripción (Stripe/PayPal). El sistema local no puede procesarla — revisa el panel del proveedor.',
    BILLING_INVOICE_ALREADY_PAID:
      'Esta factura ya se pagó. Revisa el detalle para ver el recibo.',
    BILLING_PAYMENT_IN_FLIGHT:
      'Otro cobro está en curso para esta factura. Espera unos segundos y vuelve a intentar.',
    CHANGE_REQUEST_COMPLIANCE_BLOCKED:
      'El negocio excede los límites del plan destino. Reduce los recursos en exceso antes de aplicar la baja.',
    PREPAYMENT_PROVIDER_MANAGED:
      'La suscripción la cobra Stripe/PayPal automáticamente. No se puede registrar un prepago manual — el proveedor externo controla el cobro.',
    PREPAYMENT_OVERLAPPING_INVOICE:
      'Ya existe una factura recurrente pendiente para el período actual. Resuélvela primero (paga, anula o ajusta) antes de registrar el prepago.',
    PREPAYMENT_INVALID_MONTHS:
      'La cantidad de meses debe ser un número entero entre 1 y 24.',
    PREPAYMENT_SUBSCRIPTION_CANCELLING:
      'La suscripción está marcada para cancelarse al final del período. No se puede aplicar un prepago sobre una suscripción en cancelación.',
    PREPAYMENT_CARD_FAILED:
      'La tarjeta guardada rechazó el prepago. Verifica el método de pago o intenta más tarde. La factura queda abierta y se reintentará automáticamente.',
    PREPAYMENT_NO_PAYMENT_METHOD:
      'No hay una tarjeta guardada. Agrega un método de pago en la sección "Métodos de Pago" antes de prepagar.',
    PREPAYMENT_INVALID_MONTHS_COUNT:
      'La cantidad de meses del prepago debe ser 1, 3, 6 o 12.',
    CREDIT_NOTE_SEQUENCE_NOT_CONFIGURED:
      'La secuencia de notas de crédito no está configurada para esta sucursal. Configúrala en sadmin antes de emitir la nota de crédito.',
    CREDIT_NOTE_SEQUENCE_MISSING_ETYPE:
      'La factura original es electrónica (E-type). Configura la secuencia E34 en sadmin antes de emitir la nota de crédito.',
    CREDIT_NOTE_SEQUENCE_MISMATCH_B_TYPE:
      'La factura original es de tipo B. La secuencia de notas de crédito configurada es electrónica (E34); configura B04 en sadmin.',
    CREDIT_NOTE_ORIGINAL_NOT_FISCAL:
      'La factura original no es un comprobante electrónico (no tiene NCF de tipo E), por lo que no se puede emitir una nota de crédito electrónica (E34) que la referencie. Emite una nota de crédito no fiscal o reversa una factura e-CF.',
    ECF_E32_LARGE_AMOUNT_REQUIRES_RNC:
      'Facturas de Consumo Electrónicas ≥ RD$250,000 requieren el RNC/Cédula del comprador (o Identificador Extranjero cuando el cliente es extranjero). Actualiza el cliente antes de emitir.',
    CREDIT_LIMIT_REQUIRED_NOT_SET:
      'No se pueden emitir facturas a crédito para clientes sin límite de crédito configurado. Asigna un límite de crédito al cliente antes de continuar.',
    CREDIT_APPROVAL_REQUIRED:
      'Esta factura excede el límite de crédito del cliente. Solicita una aprobación al supervisor y vuelve a intentar.',
    CREDIT_APPROVAL_INVALID:
      'El código de aprobación no es válido, ya fue usado, expiró o no corresponde a este cliente / monto / término. Solicita una nueva aprobación.',
    PRODUCTS_EDIT_FIELD_FORBIDDEN:
      'No tienes permiso para modificar este campo del producto. Contacta a un administrador si necesitas acceso.',
    INVALID_ONBOARDING_TOKEN:
      'El enlace de activación no es válido. Por favor solicita uno nuevo.',
    ONBOARDING_TOKEN_EXPIRED:
      'El enlace de activación ha expirado. Por favor solicita uno nuevo.',
    ONBOARDING_ALREADY_COMPLETED:
      'El proceso de configuración ya fue completado. Puedes iniciar sesión.',
    ONBOARDING_PASSWORD_INVALID:
      'La contraseña ingresada es incorrecta. Por favor intenta nuevamente.',
    ACCOUNT_EXISTS_WITH_GOOGLE:
      'Este correo está registrado con Google. Por favor usa "Iniciar sesión con Google" para acceder a tu cuenta.',
    ACCOUNT_EXISTS_WITH_EMAIL:
      'No se puede iniciar sesión con Google para este correo. Por favor usa tu correo y contraseña, o prueba con otra cuenta de Google.',
    ACCOUNT_EMAIL_ALREADY_EXISTS:
      'Ya existe una cuenta registrada con este correo electrónico.',
    GOOGLE_ACCOUNT_NOT_REGISTERED:
      'No existe una cuenta con este correo de Google. Te llevamos al registro para que crees tu cuenta y elijas tu plan.',
    SELECT_PARENT_CATEGORY: 'Seleccionar categoría padre',
    TRIAL_EXPIRED:
      'Tu período de prueba gratuito ha expirado. Actualiza tu plan para continuar usando las funciones financieras.',
    PREPAID_PACK_SALES_DISABLED:
      'Los paquetes prepagados de e-CF aún no están disponibles para la compra.',
    PREPAID_PACK_PAYMENT_METHOD_REQUIRED:
      'Para comprar un paquete prepagado necesitas una tarjeta guardada o PayPal: la capacidad se activa solo cuando se confirma el pago. Agrega un método de pago e inténtalo de nuevo.',
    PLAN_LIMIT_INVOICES:
      'Has alcanzado el límite de e-CF (facturas electrónicas) de tu plan este mes. Actualiza tu plan o compra e-CF adicionales para continuar.',
    PLAN_LIMIT_INVOICES_TOTAL:
      'Has alcanzado el límite total de facturas mensuales de tu plan. Actualiza tu plan para continuar facturando.',
    PLAN_LIMIT_PRODUCTS:
      'Has alcanzado el límite de productos de tu plan. Actualiza tu plan para agregar más productos.',
    PLAN_LIMIT_USERS:
      'Has alcanzado el límite de usuarios de tu plan. Actualiza tu plan para agregar más usuarios.',
    PLAN_LIMIT_WAREHOUSES:
      'Has alcanzado el límite de almacenes de tu plan. Actualiza tu plan para agregar más almacenes.',
    BRANCH_EMPTY_REQUIRES_OWN_WAREHOUSE:
      'Una sucursal sin productos debe crear su propio almacén.',
    BRANCH_DELETE_HEADQUARTERS: 'La sede principal no se puede eliminar.',
    BRANCH_DELETE_LAST_BRANCH:
      'El negocio debe conservar al menos una sucursal.',
    BRANCH_DELETE_INVALID_TARGET:
      'La sucursal destino no es válida. Elige otra sucursal activa del negocio.',
    BRANCH_DELETE_CONFIRMATION_MISMATCH:
      'El nombre escrito no coincide con el de la sucursal.',
    BRANCH_DELETE_BLOCKED:
      'La sucursal tiene trabajo pendiente (turnos, cuentas, borradores, e-CF, conteos, dispositivos sin conexión o usuarios duplicados). Resuélvelo antes de eliminarla.',
    BRANCH_DELETED:
      'Esta sucursal fue eliminada. Recarga la página y selecciona una sucursal activa.',
    PLAN_LIMIT_BRANCHES:
      'Has alcanzado el límite de sucursales de tu plan. Actualiza tu plan para agregar más sucursales.',
    PLAN_LIMIT_KDS_NOT_AVAILABLE:
      'El sistema KDS no está disponible en tu plan actual. Actualiza tu plan para habilitarlo.',
    PRODUCT_COST_REQUIRED:
      'El costo del producto es obligatorio según la configuración del negocio. Ingresa un costo (0 es válido para costo cero real).',
    PRODUCT_COST_EDIT_FORBIDDEN:
      'No tienes permiso para editar el costo del producto. El costo se actualiza automáticamente desde las compras.',
    PRODUCT_SKU_DUPLICATE:
      'Ya existe un producto con este SKU en esta sucursal. Usa un SKU diferente.',
    PRODUCT_VARIANT_IN_RECIPE:
      'Una de las variantes que intentas eliminar se usa como ingrediente en una receta. Quítala de la receta primero.',
    PRODUCT_PRESENTATION_DUPLICATE:
      'Hay dos presentaciones con la misma unidad sin nombres distintos. Asigna un nombre único a cada una.',
    SALES_VENDOR_NAME_REQUIRED:
      'El vendedor requiere un usuario vinculado o un nombre.',
    SALES_VENDOR_DUPLICATE_NAME:
      'Ya existe un vendedor con ese nombre en este alcance.',
    SALES_VENDOR_NOT_FOUND: 'Vendedor no encontrado.',
    SALES_VENDOR_NOT_ASSIGNABLE_TO_BRANCH:
      'Este vendedor no puede asignarse a una venta de otra sucursal.',
    SALES_VENDOR_INACTIVE: 'El vendedor está inactivo y no puede asignarse.',
    PURCHASE_ORDER_CANCELLED_RECEIVE:
      'No se puede recibir mercancía de un documento cancelado.',
    PRODUCT_VARIANT_PARENT_NOT_TRANSACTABLE:
      'Este producto tiene variantes y no se puede comprar ni vender directamente. Agrega una variante específica.',
    KDS_NODE_HAS_LIVE_ORDERS:
      'No se puede eliminar el nodo: tiene órdenes activas pendientes. Completa o cancela las órdenes antes de eliminarlo.',
    KDS_NODE_NOT_ENTRY:
      'Solo los nodos de entrada pueden recibir órdenes directamente. Ajusta la jerarquía del nodo antes de continuar.',
    KDS_NODE_CYCLE_DETECTED:
      'La cadena de nodos forma un ciclo. Revisa los nodos siguientes para corregir la configuración.',
    KDS_READER_CANNOT_MUTATE:
      'Solo puedes ver las órdenes de este KDS. Pide un nivel de acceso mayor (OPERADOR o SUPERVISOR) para mover, completar o cancelar tareas.',
    PLAN_CHANGE_COOLDOWN:
      'Tu plan fue modificado recientemente. Debes esperar {{hours}} hora(s) más antes de volver a cambiarlo.',
    FREE_PLAN_NON_ADMIN:
      'Tu empresa está en el plan gratuito. Solo el administrador puede acceder a la plataforma.',
    PIN_NOT_CONFIGURED: 'El PIN no está configurado para esta cuenta',
    NCF_SEQUENCE_DUPLICATE:
      'Ya existe una secuencia NCF para este tipo, serie y prefijo. Edita la secuencia existente en lugar de crear una nueva.',
    NCF_SEQUENCE_EXHAUSTED:
      'Ya existe una secuencia NCF para este tipo, serie y prefijo y se agotó. Actualiza la secuencia existente para extender el número final.',
    NCF_RANGE_OVERLAP:
      'El rango de NCF se solapa con otro rango existente para el mismo tipo. Los rangos por sucursal deben ser disjuntos.',
    NCF_NOT_CONFIGURED:
      'No hay NCF disponibles en el rango de esta sucursal. Asigna o extiende el rango de la sucursal.',
    PLATFORM_FISCAL_CREDIT_BLOCKED:
      'No se pudo emitir el comprobante: el tipo asignado a este cliente requiere RNC y el cliente no tiene uno registrado. Pídele el RNC o cambia el tipo de comprobante.',
    PLATFORM_FISCAL_NO_SEQUENCE:
      'No se pudo emitir el comprobante: no hay secuencia NCF disponible para el tipo asignado. Registra o extiende la secuencia en Configuración → NCF.',
    PLATFORM_FISCAL_CREDIT_NOTE_NO_SEQUENCE:
      'No se pudo emitir la nota de crédito: no hay secuencia NCF (B04/E34) disponible. Registra o extiende la secuencia en Configuración → NCF.',
    PLATFORM_FISCAL_NO_TAX_DEFINITION:
      'No se pudo emitir el comprobante con ITBIS: el negocio de la plataforma no tiene el impuesto ITBIS (18%) configurado. Complétalo en la configuración fiscal (Impuestos).',
    NCF_RANGE_IN_USE:
      'No se puede cambiar la sucursal de un rango de NCF que ya emitió números. Solo los rangos sin usar pueden reasignarse.',
    NCF_SPLIT_INVALID_TILING:
      'Los tramos no cubren el rango exactamente (hay un hueco, un solapamiento, o no llegan al final).',
    INVOICE_FREE_ITEMS_NOT_ALLOWED:
      'La facturación libre no está habilitada. Actívala en Configuración → Negocio o actualiza tu plan para incluir esta función.',
    INVOICE_FREE_ITEM_MISSING_DESCRIPTION:
      'Los ítems libres deben incluir una descripción.',
    INVOICE_FREE_ITEM_INVALID_PRICE:
      'Los ítems libres deben tener un precio unitario válido (mayor o igual a 0).',
    INVOICE_ITEM_PRICE_BELOW_MIN:
      'Uno o más ítems tienen un precio por debajo del mínimo permitido para este cliente. Solicita autorización a un administrador con el permiso "Vender por debajo del precio mínimo".',
    DOCUMENT_BACKDATE_NOT_ALLOWED:
      'La fecha del documento es anterior al día de negocio actual. Registrar con fecha anterior requiere el permiso "Registrar documentos con fecha anterior". Solicita autorización a un administrador.',
    INVOICE_BULK_IMPORT_DATA_CORRUPTED:
      'La importación de facturas tiene datos corruptos y no se puede reanudar. Cancela este proceso e inicia una nueva importación.',
    INVOICE_BULK_IMPORT_CANNOT_RESUME:
      'No se puede reanudar la importación de facturas en su estado actual.',
    INVOICE_BULK_IMPORT_NO_ITEMS_TO_PROCESS:
      'No hay facturas pendientes para reanudar en esta importación.',
    CAPTCHA_REQUIRED:
      'Por favor completa la verificación de seguridad para continuar.',
    CAPTCHA_INVALID: 'Verificación de seguridad inválida. Vuelve a intentarlo.',
    ACCOUNTING_PERIOD_LOCKED:
      'El período contable está bloqueado. No se pueden registrar asientos.',
    NO_RESELLER_ASSIGNMENT:
      'Este negocio no tiene un reseller asignado activo — no hay comisión que liquidar.',
    CURRENCY_UNRESOLVED: 'No se pudo resolver la moneda de la factura.',
    INVOICE_NOT_VOIDABLE:
      'Solo una factura recurrente pendiente (borrador/abierta/fallida) puede ajustarse y liquidarse.',
    CONFLICTING_ACTIVE_ANNUAL_SUB:
      'Este negocio ya tiene una suscripción anual activa.',
    ADJUST_SETTLE_STEP_FAILED:
      'La operación de ajustar y liquidar falló. Los pasos reversibles se revirtieron; revisa el estado antes de reintentar.',
    ZERO_COMMISSION_BALANCE:
      'No hay saldo de comisión del reseller para liquidar tras el cobro.',
    ADJUST_SETTLE_PREVIEW_STALE:
      'El total anual cambió desde la vista previa. Vuelve a abrir el modal y revísalo.',
    RESELLER_ACCRUAL_NOT_SETTLEABLE:
      'Esta comisión ya no se puede liquidar (ya está en un pago o fue pagada/anulada).',
    ACCOUNTING_PERIOD_NOT_OPEN:
      'El período contable no está abierto. Solo se aceptan asientos en períodos abiertos o en gracia.',
    ACCOUNTING_PERIOD_NOT_CLOSED_FOR_ADJUSTMENT:
      'Los asientos de ajuste solo se permiten en períodos cerrados (no bloqueados).',
    ACCOUNTING_PERIOD_NOT_CLOSED:
      'Solo los períodos cerrados pueden bloquearse de forma definitiva.',
    ACCOUNTING_PERIOD_ALREADY_LOCKED: 'El período ya está bloqueado.',
    ACCOUNTING_PERIOD_NOT_LOCKED:
      'Solo los períodos bloqueados pueden desbloquearse.',
    ACCOUNTING_LOCK_MODE_STRICT:
      'La política de bloqueo del negocio es estricta. Los períodos bloqueados no pueden desbloquearse.',
    ACCOUNTING_UNLOCK_REASON_REQUIRED:
      'El desbloqueo requiere un motivo para la auditoría.',
    ACCOUNTING_ADJUSTMENT_NOT_PERMITTED:
      'No tienes permiso para registrar asientos de ajuste (accounting.journal.create_adjustment).',
    PRODUCT_IMAGE_MANAGE_NOT_PERMITTED:
      'No tienes permiso para gestionar imágenes de productos (inventory.products.image.manage).',
    ACCOUNTING_GRACE_DAYS_INVALID:
      'Los días de gracia deben ser un número entero mayor o igual a cero.',
    ACCOUNTING_LOCK_MODE_INVALID:
      'La política de bloqueo debe ser "strict" o "flexible".',
    FISCAL_YEAR_NOT_OPEN:
      'El año fiscal no está abierto. Solo años abiertos pueden cerrarse.',
    FISCAL_YEAR_NOT_CLOSED:
      'El año fiscal no está cerrado. Solo años cerrados pueden reabrirse.',
    FISCAL_YEAR_NO_PERIODS:
      'El año fiscal no tiene períodos contables. Genera los períodos antes de cerrar.',
    FISCAL_YEAR_CLOSING_JE_MISSING:
      'No se encuentra el asiento de cierre referenciado por el año fiscal. Contacta al administrador.',
    FISCAL_YEAR_RETAINED_EARNINGS_MISSING:
      'La cuenta de resultados acumulados no está configurada. Configúrala en Configuración Contable antes de cerrar el año.',
    ACCOUNT_CODE_DUPLICATE:
      'Ya existe una cuenta contable con este código en el catálogo.',
    ACCOUNTING_NOT_ENABLED:
      'La función de Contabilidad no está activa para este negocio. Activala antes de ejecutar un backfill.',
    ACCOUNTING_BACKFILL_NOT_CANCELLABLE:
      'La ejecución ya finalizó o fue cancelada — no puede cancelarse nuevamente.',
    FIXED_ASSET_CATEGORY_CODE_DUPLICATE:
      'Ya existe una categoría de activos fijos con este código.',
    FIXED_ASSET_CATEGORY_NOT_FOUND: 'La categoría de activos fijos no existe.',
    FIXED_ASSET_CATEGORY_ACCOUNTS_REQUIRED:
      'Las 3 cuentas contables son obligatorias: cuenta del activo, depreciación acumulada y gasto de depreciación.',
    FIXED_ASSET_CATEGORY_ACCOUNTS_INCOMPLETE:
      'La categoría seleccionada no tiene configuradas las cuentas contables. Configúralas antes de registrar el activo.',
    FIXED_ASSET_CODE_DUPLICATE: 'Ya existe un activo fijo con este código.',
    FIXED_ASSET_NOT_IN_SERVICE:
      'El activo no está en servicio — sólo activos en servicio pueden editarse, transferirse, revaluarse o darse de baja.',
    FIXED_ASSET_TRANSFER_NO_TARGET:
      'La transferencia requiere al menos una sucursal o un centro de costo destino.',
    FIXED_ASSET_REVALUE_ZERO: 'El cambio de valor debe ser distinto de cero.',
    FIXED_ASSET_DATE_INVALID:
      'La fecha enviada no es válida. Usa el selector de calendario y reintenta.',
    BUDGET_NAME_DUPLICATE:
      'Ya existe un presupuesto con este nombre para el año fiscal seleccionado.',
    BUDGET_NOT_DRAFT:
      'El presupuesto ya no está en borrador. Solo los borradores pueden editarse o eliminarse.',
    BUDGET_TRANSITION_INVALID:
      'Transición de estado no permitida. Verifica el estado actual del presupuesto.',
    BANK_ACCOUNT_NOT_FOUND: 'La cuenta bancaria no existe.',
    BANK_ACCOUNT_ARCHIVED:
      'La cuenta bancaria está archivada. Reactívala para continuar.',
    BANK_ACCOUNT_CODE_REQUIRED: 'El código de la cuenta bancaria es requerido.',
    BANK_ACCOUNT_NAME_REQUIRED: 'El nombre de la cuenta bancaria es requerido.',
    BANK_ACCOUNT_CODE_DUPLICATE:
      'Ya existe una cuenta bancaria con este código.',
    BANK_ACCOUNT_GL_NOT_FOUND: 'La cuenta contable seleccionada no existe.',
    BANK_ACCOUNT_GL_NOT_POSTABLE:
      'La cuenta contable es una cuenta cabecera. Selecciona una cuenta hoja postable.',
    BANK_ACCOUNT_NO_GL_LINK:
      'La cuenta bancaria no tiene cuenta contable enlazada. Configúrala en Cuentas Bancarias.',
    BANK_STATEMENT_NOT_FOUND: 'El estado de cuenta no existe.',
    BANK_STATEMENT_EMPTY: 'El archivo del estado de cuenta no tiene filas.',
    BANK_STATEMENT_ROW_INVALID:
      'Una de las filas del estado de cuenta está incompleta. Revisa el mapeo de columnas.',
    BANK_STATEMENT_ROW_NO_AMOUNT:
      'Una fila no tiene ni débito ni crédito. Revisa el mapeo de columnas.',
    BANK_STATEMENT_DATE_INVALID:
      'No se pudo interpretar una fecha del estado. Formato esperado YYYY-MM-DD o DD/MM/YYYY.',
    BANK_STATEMENT_AMOUNT_INVALID:
      'No se pudo interpretar un monto. Usa números decimales (ej. 1250.00).',
    BANK_RECONCILIATION_ALREADY_OPEN:
      'Ya existe una sesión de conciliación para este estado de cuenta.',
    BANK_RECON_LINE_MISMATCH:
      'La línea no pertenece a esta sesión de conciliación.',
    BANK_RECON_LINE_ALREADY_MATCHED:
      'La línea ya está conciliada. Desemparéjala primero.',
    BANK_RECON_LINE_NOT_MATCHED: 'La línea no está conciliada.',
    BANK_RECON_NO_STATEMENT:
      'La sesión de conciliación no tiene estado de cuenta enlazado.',
    BANK_RECON_UNMATCHED_LINES:
      'No se puede finalizar mientras haya líneas sin conciliar.',
    BANK_RECON_LOCKED:
      'La sesión de conciliación está finalizada y no puede modificarse.',
    GENERIC_CUSTOMER_READ_ONLY:
      'El cliente genérico no puede modificarse — es el cliente mostrador por defecto en facturas y POS.',
    CUSTOMER_MERGE_INTO_SELF: 'No puedes combinar un cliente consigo mismo.',
    CUSTOMER_MERGE_NOT_FOUND:
      'Uno o ambos clientes no se encontraron para este negocio.',
    CUSTOMER_MERGE_GENERIC_FORBIDDEN:
      'El cliente genérico no puede combinarse.',
    PROVIDER_MERGE_INTO_SELF: 'No puedes combinar un proveedor consigo mismo.',
    PROVIDER_MERGE_NOT_FOUND:
      'Uno o ambos proveedores no se encontraron para este negocio.',
    CUSTOMER_DELETE_HAS_RECORDS:
      'Este cliente tiene registros asociados o saldo pendiente, y no se puede eliminar. Combínalo con otro cliente en su lugar y/o verifica.',
    PROVIDER_DELETE_HAS_RECORDS:
      'Este proveedor tiene registros asociados y no se puede eliminar. Combínalo con otro proveedor en su lugar.',
    EXPENSE_CATEGORY_NOT_FOUND: 'La categoría de gasto no fue encontrada.',
    EXPENSE_CATEGORY_ACCOUNT_NOT_FOUND:
      'La cuenta contable seleccionada no existe.',
    EXPENSE_CATEGORY_ACCOUNT_NOT_POSTABLE:
      'La cuenta seleccionada está archivada o no es postable. Elige una cuenta hoja activa.',
    STOCK_ADJUST_REASON_NOT_FOUND: 'La razón de ajuste no fue encontrada.',
    STOCK_ADJUST_REASON_ACCOUNT_NOT_FOUND:
      'La cuenta contable seleccionada no existe.',
    STOCK_ADJUST_REASON_ACCOUNT_NOT_POSTABLE:
      'La cuenta seleccionada está archivada o no es postable.',
    ACCOUNTING_ROUTING_ACCOUNT_NOT_FOUND:
      'La cuenta seleccionada para el override no existe.',
    ACCOUNTING_ROUTING_ACCOUNT_NOT_POSTABLE:
      'La cuenta seleccionada está archivada o no es postable.',
    JOURNAL_PREVIEW_NO_RULE:
      'No hay una regla de contabilización activa para este tipo de documento.',
    JOURNAL_PREVIEW_EVENT_NOT_FOUND:
      'No se encontró el evento financiero asociado al documento.',
    JOURNAL_PREVIEW_FAILED: 'No se pudo generar la vista previa contable.',
    RULES_ENGINE_PRODUCT_CATEGORY_NOT_FOUND:
      'Una categoría de producto en la factura está inactiva o eliminada.',
    RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_MISSING:
      'La regla requiere especificar el rol (ingreso/costo/inventario/devoluciones).',
    RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_INVALID:
      'El rol especificado para la categoría es inválido.',
    RULES_ENGINE_PRODUCT_CATEGORY_PATH_MISSING:
      'La regla requiere la ruta JSON al categoryId.',
    RULES_ENGINE_EXPENSE_CATEGORY_NOT_FOUND:
      'La categoría de gasto referenciada está inactiva o eliminada.',
    RULES_ENGINE_EXPENSE_CATEGORY_PATH_MISSING:
      'La regla requiere la ruta JSON al expenseCategoryId.',
    RULES_ENGINE_STOCK_ADJUST_REASON_NOT_FOUND:
      'La razón de ajuste referenciada está inactiva o eliminada.',
    RULES_ENGINE_STOCK_ADJUST_REASON_PATH_MISSING:
      'La regla requiere la ruta JSON al reasonId.',
    SUPPLIER_PAYMENT_ISR_AMOUNT_INVALID:
      'El monto de ISR retenido no coincide con la tasa DGII × subtotal. Verifica el porcentaje aplicado.',
    SUPPLIER_PAYMENT_ISR_TYPE_MISSING:
      'Selecciona la categoría de retención ISR (persona física / jurídica / publicidad).',
    SUPPLIER_INVOICE_PAYMENT_EXCEEDS_BALANCE:
      'El monto del pago excede el balance pendiente de la factura.',
    SUPPLIER_CREDIT_NOTE_NOT_RECEIVABLE:
      'Una nota de crédito no se recibe: usa "Procesar" para aplicarla.',
    SUPPLIER_CREDIT_NOTE_PARENT_CANCELLED:
      'Esta nota de crédito acredita una factura anulada y no se puede anular.',
    PURCHASE_ORDER_ALREADY_CANCELLED: 'Este documento ya está anulado.',
    SUPPLIER_INVOICE_NOT_PAYABLE:
      'Esta factura no puede recibir pagos (anulada o no encontrada).',
    SUPPLIER_PAYMENT_OVERPAYMENT:
      'El pago excede el saldo pendiente del documento.',
    SUPPLIER_PAYMENT_ALREADY_VOIDED: 'Este pago ya está anulado.',
    SUPPLIER_PAYMENT_VOID_REASON_REQUIRED:
      'Indica el motivo para anular el pago.',
    SUPPLIER_PAYMENT_DUPLICATE_REFERENCE:
      'Ya existe un pago con esa referencia externa.',
    SUPPLIER_PAYMENT_NOT_FOUND: 'El pago no existe o ya no está disponible.',
    EXPENSE_NOT_FOUND: 'El gasto no existe o ya no está disponible.',
    EXPENSE_NOT_PAYABLE:
      'Solo un gasto aprobado a crédito puede recibir pagos.',
    EXPENSE_HAS_PAYMENTS:
      'El gasto tiene pagos registrados. Anula los pagos primero.',
    EXPENSE_ALREADY_VOIDED: 'Este gasto ya fue anulado.',
    EXPENSE_VOID_REASON_REQUIRED: 'Indica el motivo de la anulación.',
    EXPENSE_VOID_USE_ANNUL:
      'Un gasto no se cancela editándolo: usa la acción Anular e indica el motivo.',
    EXPENSE_CREDIT_PROVIDER_REQUIRED:
      'Un gasto a crédito necesita un proveedor.',
    INVOICE_ANNULMENT_REASON_INVALID:
      'Selecciona una razón de anulación válida (DGII 01-11).',
    INVOICE_ECF_ALREADY_SUBMITTED:
      'Esta factura ya fue enviada a la DGII y no puede anularse. Debe emitirse una nota de crédito para revertir su efecto fiscal.',
    INVOICE_ANNUL_NOT_PERMITTED:
      'No tienes permiso para anular facturas. Solicita el permiso "Anular factura" al administrador.',
    INVOICE_TYPE_PERMISSION_DENIED:
      'No tienes permiso para crear este tipo de documento. Solicita el permiso correspondiente al administrador.',
    NCF_EXHAUSTION_WARNING:
      'Una secuencia NCF está cerca de agotarse o expirar. Solicita una nueva autorización a la DGII.',
    INVOICE_NOT_FOUND: 'No se encontró la factura solicitada.',
    INVOICE_ALREADY_PAID:
      'Esta factura ya está pagada; no hay saldo pendiente para dar de baja.',
    INVOICE_TOTAL_CHANGED:
      'Otro terminal modificó la cuenta. El total cambió — revisa el nuevo monto antes de cobrar.',
    INVOICE_NOT_A_DRAFT:
      'Solo tickets en estado borrador enviados a caja pueden cobrarse por este flujo.',
    INVOICE_NOT_A_DRAFT_USE_CREDIT_NOTE:
      'Esta factura ya no es un borrador; para revertirla emite una nota de crédito en lugar de eliminarla.',
    CONDUCE_NOT_A_CONDUCE: 'Este documento no es un conduce.',
    RESTAURANT_ACCOUNT_PAYMENT_LOCKED:
      'Otro cajero está cobrando esta mesa. Espera a que termine o a que se libere.',
    RESTAURANT_DISCOUNT_BOTH_MODES:
      'Un descuento de mesa es porcentual o de monto fijo, no ambos a la vez.',
    CONDUCE_NOT_EDITABLE:
      'Un conduce no se puede editar una vez creado. Conviértelo en factura o devuelve sus ítems.',
    CONDUCE_ALREADY_CONVERTED:
      'Este conduce ya está asociado a una factura; no se permite devolver ítems desde el conduce. Ajusta la factura asociada.',
    CONDUCE_ALREADY_CANCELLED: 'Este conduce ya está anulado.',
    CONDUCE_RETURN_EXCEEDS_DISPATCHED:
      'La cantidad a devolver excede la cantidad despachada disponible en el conduce.',
    INVOICE_NOT_ELIGIBLE_FOR_CREDIT_NOTE:
      'Esta factura no admite nota de crédito. Solo se permite sobre facturas emitidas (no borradores ni anuladas).',
    INVOICE_ALREADY_FINALIZED:
      'Esta factura ya fue cobrada y no puede reprocesarse.',
    INVOICE_HAS_PENDING_PAYMENTS:
      'Esta factura ya tiene pagos registrados de un intento anterior. Recárgala; si persiste, contacta soporte para reconciliar antes de reintentar.',
    INVOICE_CANCELLED:
      'Esta factura fue cancelada y no puede recibir un ajuste de baja de saldo.',
    INVOICE_NO_OUTSTANDING:
      'Esta factura no tiene saldo pendiente para dar de baja.',
    INVOICE_WRITE_OFF_EXCEEDS_OUTSTANDING:
      'El monto a dar de baja excede el saldo pendiente de la factura.',
    INVOICE_WRITE_OFF_INVALID_AMOUNT:
      'El monto a dar de baja debe ser mayor a cero.',
    INVOICE_WRITE_OFF_INVALID_REASON:
      'Selecciona una razón válida para dar de baja el saldo.',
    INVOICE_WRITE_OFF_COMMENT_REQUIRED:
      'Debes escribir un comentario que explique la baja de saldo.',
    INVOICE_WRITE_OFF_COMMENT_TOO_LONG:
      'El comentario no puede exceder los 500 caracteres.',
    INVOICE_WRITE_OFF_POST_FAILED:
      'No fue posible registrar el asiento contable de la baja de saldo. Intenta nuevamente en unos minutos.',
    INVENTORY_RESET_INVALID_CONFIRMATION:
      'Debes escribir la palabra RESET en mayúsculas para confirmar el reinicio del inventario.',
    INVENTORY_RESET_BRANCH_NOT_FOUND:
      'La sucursal seleccionada no pertenece a tu negocio o no existe.',
    INVENTORY_RESET_HQ_MISSING:
      'Este negocio no tiene una casa matriz configurada. Contacta a soporte.',
    OPERATIONS_RESET_INVALID_CONFIRMATION:
      'Debes escribir la palabra RESET en mayúsculas para confirmar la eliminación de operaciones.',
    OPERATIONS_RESET_INVALID_ID:
      'El identificador del negocio no tiene un formato válido. Vuelve a iniciar sesión e intenta de nuevo.',
    ECF_APPROVAL_XLSX_WRONG_SHEET:
      'El archivo XLSX no contiene la hoja "ACEECF_Generadas". Descarga el archivo correcto desde la sección "Aprobaciones Comerciales" del portal DGII e inténtalo de nuevo.',
  },
  COMMISSION_CAMPAIGNS: {
    TITLE: 'Campañas de comisión',
    SUBTITLE:
      'Bonos de comisión por tiempo limitado que se suman a la comisión base del vendedor.',
    NEW_CAMPAIGN: 'Nueva campaña',
    EDIT_CAMPAIGN: 'Editar campaña',
    EMPTY: 'No hay campañas.',
    COL_NAME: 'Nombre',
    COL_WINDOW: 'Vigencia',
    COL_SCOPE: 'Alcance',
    COL_ACTIVE: 'Activa',
    COL_RULES: 'Reglas',
    COL_ACTIONS: 'Acciones',
    INFINITE: 'Indefinida',
    F_NAME: 'Nombre',
    F_START: 'Fecha de inicio',
    F_END: 'Fecha de fin',
    F_END_HINT: 'Déjala vacía para una campaña indefinida.',
    F_SCOPE: 'Alcance',
    SCOPE_ALL: 'Todos los vendedores',
    SCOPE_VENDORS: 'Vendedores específicos',
    F_ACTIVE: 'Activa',
    F_NOTE: 'Nota',
    F_VENDORS: 'Vendedores incluidos',
    SELECT_VENDORS: 'Seleccionar vendedores',
    NO_VENDORS: 'No hay vendedores',
    RULES_TITLE: 'Reglas de bono',
    ADD_RULE: 'Agregar regla',
    REMOVE_RULE: 'Quitar regla',
    F_RULE_TYPE: 'Tipo de regla',
    RULE_CATEGORY: 'Por categoría',
    RULE_QTY_TIER: 'Por cantidad',
    RULE_FLAT: 'Monto fijo',
    F_CATEGORY: 'Categoría',
    SELECT_CATEGORY: 'Seleccionar categoría',
    NO_CATEGORIES: 'No hay categorías',
    F_MIN_QTY: 'Cantidad mínima',
    F_BONUS_TYPE: 'Tipo de bono',
    BONUS_PERCENTAGE: 'Porcentaje',
    BONUS_FLAT: 'Monto fijo',
    F_BONUS_VALUE: 'Valor del bono',
    SAVED_SUCCESS: 'Campaña guardada.',
    DELETE_TITLE: 'Eliminar campaña',
    DELETE_MESSAGE: '¿Seguro que deseas eliminar esta campaña?',
    ACTIVE_YES: 'Sí',
    ACTIVE_NO: 'No',
    RULES_COUNT: '{{count}} regla(s)',
    REPORT_COL_CAMPAIGN: 'Campaña',
    FILTER_LABEL: 'Campaña',
    FILTER_ALL: 'Todas las campañas',
    MANAGE: 'Gestionar campañas',
  },
  SALES_COMMISSION: {
    SECTION_DEFAULT_LABEL: 'Comisión Vendedores',
    VENDORS_TITLE: 'Vendedores',
    VENDORS_SUBTITLE: 'Gestiona los vendedores y su comisión por venta fija.',
    SETTINGS_TITLE: 'Configuración de comisiones',
    SETTINGS_SUBTITLE:
      'Controla cómo se habilita, acumula y muestra la comisión por vendedor.',
    VENDOR_PENDING_COL: 'Comisión pendiente',
    VENDOR_VIEW_REPORT: 'Ver reporte',
    VENDOR_PERM_BULB:
      'Al vincular este usuario queda como vendedor: en sus propias ventas la comisión se le atribuye automáticamente y el modal de cobro no pide elegir vendedor. Para que ADEMÁS pueda asignar ventas a OTROS vendedores durante el cobro, su rol necesita el permiso «Asignar vendedor en cobro» (sales.vendor.assign).',
    VENDOR_PERM_MISSING:
      'El rol «{{role}}» de este usuario no tiene el permiso «Asignar vendedor en cobro», por lo que no podrá atribuir ventas a otros vendedores en el cobro.',
    VENDOR_PERM_SCOPE_HINT:
      'Se otorgará a todos los usuarios con el rol «{{role}}».',
    VENDOR_PERM_GRANT: 'Otorgar permiso',
    VENDOR_PERM_GRANTED: 'Permiso otorgado al rol «{{role}}».',
    VENDOR_NAME: 'Nombre',
    VENDOR_SOURCE: 'Origen del vendedor',
    FROM_USER: 'Usuario existente',
    STANDALONE: 'Nombre nuevo',
    PICK_USER: 'Seleccionar usuario',
    ADD_VENDOR: 'Agregar vendedor',
    EDIT_VENDOR: 'Editar vendedor',
    NO_VENDORS: 'No hay vendedores',
    COMMISSION_TYPE: 'Tipo de comisión',
    COMMISSION_VALUE: 'Valor de comisión',
    TYPE_PER_INVOICE: 'Monto fijo por factura',
    TYPE_PERCENTAGE: 'Porcentaje del subtotal',
    TYPE_PER_QUANTITY: 'Monto por unidad',
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    LINKED_USER: 'Usuario',
    ENABLE_LABEL: 'Comisiones por vendedor',
    ENABLE_HELP:
      'Habilita asignar un vendedor al cobrar y acumular su comisión.',
    ACCRUAL_MODE_LABEL: 'Acumulación de comisión',
    ACCRUAL_MODE_HELP:
      'Define cuándo se acumula la comisión y si una cancelación directa de factura la revierte. La nota de crédito siempre revierte.',
    PAY_EXCEEDS_LABEL: 'Pagar comisión aunque supere el total',
    PAY_EXCEEDS_HELP:
      'Cuando está desactivado, la comisión que supera el total de la factura no se acumula (protección de margen). Activado mantiene el comportamiento actual.',
    ACCRUAL_MODE_FINALIZED_ONLY: 'Solo facturas finalizadas (recomendado)',
    ACCRUAL_MODE_ALL_STATUSES: 'Todos los estados + revertir al cancelar',
    ACCRUAL_MODE_FINALIZED_PLUS_CANCEL_GUARD:
      'Solo finalizadas + revertir al cancelar',
    MANAGE_VENDORS: 'Gestionar vendedores',
    ATTRIBUTED_TO: 'Vendedor',
    SELECT_VENDOR: 'Seleccionar vendedor',
    VENDOR_REQUIRED: 'Debes seleccionar un vendedor para continuar.',
    REPORT_TITLE: 'Comisiones por vendedor',
    REPORT_SUBTITLE:
      'Comisión acumulada, revertida y neta por vendedor en el rango seleccionado.',
    REPORT_COL_VENDOR: 'Vendedor',
    REPORT_COL_ACCRUED: 'Acumulado',
    REPORT_COL_REVERSED: 'Revertido',
    REPORT_COL_ADJUSTMENT: 'Ajuste por reasignación',
    REPORT_COL_NET: 'Neto',
    REPORT_COL_INVOICES: 'Facturas',
    REPORT_DATE_FROM: 'Desde',
    REPORT_DATE_TO: 'Hasta',
    REPORT_DATE_MODE_LABEL: 'Atribución por fecha',
    REPORT_DATE_MODE_SALE: 'Fecha de la venta',
    REPORT_DATE_MODE_MOVEMENT: 'Fecha del movimiento',
    REPORT_INVOICE_TYPE: 'Tipo de factura',
    REPORT_ALL_VENDORS: 'Todos los vendedores',
    REPORT_ALL_TYPES: 'Todos los tipos',
    REPORT_EMPTY: 'No hay comisiones en el rango seleccionado.',
    REPORT_INACTIVE_FLAG:
      'Las comisiones por vendedor están desactivadas. Los datos mostrados pueden estar incompletos.',
    REPORT_INACTIVE_FLAG_CTA: 'Activar en Configuración',
    REASSIGN_VENDOR: 'Reasignar vendedor',
    REASSIGN_VENDOR_TITLE: 'Reasignar vendedor de la factura',
    REASSIGN_VENDOR_DESCRIPTION:
      'La comisión se reatribuirá al nuevo vendedor según su tasa actual.',
    REASSIGN_VENDOR_SUCCESS: 'Vendedor reasignado correctamente.',
    STATUS_PENDING: 'Pendiente',
    STATUS_PAID: 'Pagada',
    STATUS_CANCELLED: 'Cancelada',
    REPORT_COL_STATUS: 'Estado',
    REPORT_COL_ACTIONS: 'Acciones',
    PAYOUT_STALE_HINT:
      'El monto cambió por una devolución posterior al pago. Vuelve a marcar.',
    MARK_PAYOUT: 'Marcar',
    MARK_PAYOUT_TITLE: 'Marcar comisión',
    MARK_PAYOUT_DESCRIPTION:
      'Registra el estado del pago de la comisión de este vendedor para el período filtrado.',
    MARK_PAYOUT_STATUS_LABEL: 'Estado',
    MARK_PAYOUT_NOTE_LABEL: 'Nota',
    MARK_PAYOUT_SUCCESS: 'Comisión actualizada.',
    PAY_ALL: 'Pagar todas',
    PAY_ALL_CONFIRM_TITLE: 'Pagar todas las comisiones pendientes',
    PAY_ALL_CONFIRM_DESC:
      'Se marcarán {{count}} comisión(es) pendiente(s) como pagadas por un total de {{amount}} en el período seleccionado. ¿Continuar?',
    PAY_ALL_SUCCESS:
      '{{count}} comisión(es) marcada(s) como pagada(s) ({{amount}}).',
    PAY_ALL_FILTERS_HINT:
      'Quita los filtros (vendedor, estado, tipo, campaña) para pagar todas las comisiones pendientes del período.',
    VER_FACTURAS: 'Ver facturas',
    VER_FACTURAS_TITLE: 'Facturas de la comisión',
    INVOICE_COL_NUMBER: 'Factura',
    INVOICE_COL_DATE: 'Fecha',
    INVOICE_COL_CUSTOMER: 'Cliente',
    INVOICE_COL_TOTAL: 'Total factura',
    STATUS_FILTER_LABEL: 'Estado de pago',
    STATUS_FILTER_ALL: 'Todos',
    TILE_PAID: 'Comisión pagada',
    TILE_PENDING: 'Comisión pendiente',
    TILE_TOP_VENDOR_SALES: 'Top vendedor · ventas',
    TILE_TOP_VENDOR_COMMISSION: 'Top vendedor · comisión',
  },
  THEME: {
    SWITCH_TO_DARK: 'Cambiar a modo oscuro',
    SWITCH_TO_LIGHT: 'Cambiar a modo claro',
  },
  COMMON: {
    // Cross-surface: supplier invoices, procurement purchase orders, their
    // lists, exports and print templates. Lived as PURCHASE_ORDERS.
    // SHIPPING_COST ('Costo de Envío') AND PROCUREMENT_ORDERS.SHIPPING_COST
    // ('Costo de envío') — same label, drifted capitalisation. Promoted here
    // per the cross-surface i18n rule when the supplier-invoice form became
    // the seventh call site.
    SHIPPING_COST: 'Costo de envío',
    DETAILS: 'Detalles',
    REFRESH: 'Actualizar',
    LOADING: 'Cargando...',
    LOADING_DEFAULT: 'Estamos trabajando algo para ti…',
    VALIDATING_CONTRACT: 'Validando estado del contrato...',
    DATE: 'Fecha',
    TIME: 'Hora',
    AMOUNT: 'Monto',
    SAVE: 'Guardar',
    CANCEL: 'Cancelar',
    ADD_MORE_DATA: 'Agregar más datos',
    PRINT: 'Imprimir',
    DELETE: 'Eliminar',
    CREATE_SIMILAR: 'Crear similar',
    DISMISS: 'Descartar',
    EDIT: 'Editar',
    VIEW_DETAIL: 'Ver detalle',
    ACTIONS: 'Acciones',
    CONFIRM_DELETE_TITLE: 'Confirmar eliminación',
    CONFIRM_DELETE_MESSAGE: '¿Estás seguro de que deseas eliminar esto?',
    CONFIRM: 'Confirmar',
    YES: 'Sí',
    NO: 'No',
    DAYS: 'días',
    SUBMIT: 'Enviar',
    CLOSE: 'Cerrar',
    COPIED: 'Copiado al portapapeles',
    SEARCH_PLACEHOLDER: 'Buscar...',
    ADD_NEW: 'Agregar nuevo',
    BACK: 'Atrás',
    CONTINUE: 'Continuar',
    SELECT_PLACEHOLDER: 'Seleccionar...',
    SELECT_ALL_PLACEHOLDER: 'Seleccionar todo...',
    NO_DATA: 'No hay datos disponibles',
    SAVING: 'Guardando...',
    CLEAN: 'Limpiar',
    REMOVE: 'Remover',
    CONFIRMATION_DELETE_TITLE: 'Confirmar eliminación',
    CONFIRMATION_DELETE_DESCRIPTION:
      '¿Estás seguro de que deseas eliminar esto?',
    THIS_ACTION_CANNOT_BE_UNDONE: 'Esta acción no se puede deshacer.',
    VALIDATION_REQUIRED: 'Campo requerido.',
    VALIDATION_MIN_NUMBER: 'El valor debe ser mayor o igual a {{min}}.',
    VALIDATION_MAX_NUMBER: 'El valor debe ser menor o igual a {{max}}.',
    VALIDATION_NONZERO: 'El valor debe ser distinto de cero.',
    VALIDATION_DATE_REQUIRED: 'Selecciona una fecha.',
    RETRY: 'Reintentar',
    PROCESSING: 'Procesando...',
    OFFLINE_GRACE_EXPIRED:
      'Esta terminal lleva demasiado tiempo sin conexión. Vuelve a conectarte para seguir creando registros.',
    OUTBOX_FULL:
      'La cola de sincronización está llena. Conéctate para enviar los registros pendientes antes de continuar.',
    OFFLINE_INDICATOR_OFFLINE: 'Sin conexión',
    OFFLINE_INDICATOR_SYNCING: 'Sincronizando…',
    OFFLINE_INDICATOR_PENDING: '{{count}} pendiente(s)',
    OFFLINE_INDICATOR_TOOLTIP:
      'La terminal está trabajando con datos locales. Se sincronizará al volver la conexión.',
    DEVICE_MODE_OFFLINE_ONLY: 'Modo solo offline',
    DEVICE_MODE_OFFLINE_ONLY_TOOLTIP:
      'Este terminal está configurado en modo solo offline. Solo se permiten conexiones para activación, latido, anuncios y plan.',
    DEVICE_OFFLINE_ONLY_BLOCKED_TOAST:
      'Este terminal está en modo solo offline y no puede acceder a esta función.',
    TERMINAL_TITLE: 'Terminal',
    TERMINAL_SUBTITLE:
      'Información de la clave de registro vinculada a esta terminal NeoPOS.',
    TERMINAL_KEY_CARD_TITLE: 'Clave de esta terminal',
    TERMINAL_KEY_PREFIX_LABEL: 'Código (primeros 4)',
    TERMINAL_KEY_EXPIRES_LABEL: 'Válida hasta',
    TERMINAL_KEY_NEVER_EXPIRES: 'Sin vencimiento',
    TERMINAL_KEY_LABEL_LABEL: 'Etiqueta',
    TERMINAL_DEVICE_STATUS_LABEL: 'Estado',
    TERMINAL_DEVICE_STATUS_ACTIVE: 'Activa',
    TERMINAL_DEVICE_STATUS_SUSPENDED: 'Suspendida',
    TERMINAL_DEVICE_STATUS_REVOKED: 'Revocada',
    TERMINAL_DEVICE_STATUS_LOST: 'Perdida',
    TERMINAL_DEVICE_STATUS_DUPLICATE_FINGERPRINT: 'Duplicada',
    TERMINAL_DEVICE_MODE_LABEL: 'Modo',
    TERMINAL_ENROLLED_AT_LABEL: 'Activada el',
    TERMINAL_UPDATE_KEY: 'Cambiar clave',
    TERMINAL_REMOVE_KEY: 'Eliminar clave',
    TERMINAL_REMOVE_CONFIRM_TITLE: '¿Eliminar la clave de esta terminal?',
    TERMINAL_REMOVE_CONFIRM_BODY:
      'La terminal seguirá funcionando durante {{days}} día(s) de gracia. Pasado ese plazo deberás contactar a ventas para emitir una nueva clave. Puedes cancelar la solicitud en cualquier momento dentro del período de gracia.',
    TERMINAL_REMOVE_CONFIRM_OK: 'Sí, solicitar eliminación',
    TERMINAL_REMOVAL_REQUESTED_TITLE: 'Eliminación solicitada',
    TERMINAL_REMOVAL_REQUESTED_BODY:
      'Esta terminal dejará de funcionar el {{date}}. Cancela la solicitud antes de esa fecha si cambias de opinión.',
    TERMINAL_REMOVAL_CANCEL: 'Cancelar eliminación',
    TERMINAL_REMOVAL_GRACE_BANNER:
      'Eliminación de terminal solicitada. La terminal se bloqueará en {{days}} día(s), el {{date}}.',
    TERMINAL_REVOKED_GRACE_BANNER:
      'La clave de esta terminal fue revocada. Quedan {{days}} día(s) (hasta el {{date}}) para contactar a ventas: info@neogeniux.com',
    TERMINAL_KEY_REVOKED_BANNER:
      'La clave de registro fue revocada. Esta terminal dejará de funcionar en breve — contacta a ventas (info@neogeniux.com) para emitir una nueva clave.',
    TERMINAL_KEY_EXPIRING_SOON_BANNER:
      'La clave de registro expira en {{days}} día(s) (el {{date}}). Solicita una clave nueva al administrador antes de esa fecha para evitar interrupciones.',
    TERMINAL_APP_UPDATE_REQUIRED_BANNER:
      'Se requiere actualizar a la versión {{version}}. Estamos descargando la actualización automáticamente — la terminal se reiniciará cuando termine.',
    TERMINAL_REVOKED_LOGIN_BLOCKED_TITLE: 'Terminal bloqueada',
    TERMINAL_REVOKED_LOGIN_BLOCKED_BODY:
      'La clave de registro de esta terminal expiró o fue revocada y el período de gracia terminó. Contacta a ventas para emitir una nueva clave: info@neogeniux.com',
    TERMINAL_CONTACT_SALES_EMAIL: 'info@neogeniux.com',
    TERMINAL_NO_KEY_INFO:
      'No se encontró información de la clave de registro asociada a esta terminal.',
    TERMINAL_UPDATE_KEY_HINT:
      'Para cambiar la clave debes primero eliminar la actual (queda un período de gracia) y luego volver a activar la terminal con la nueva clave entregada por ventas.',
    TERMINAL_NOT_ENROLLED_TITLE: 'Terminal no activada',
    TERMINAL_NOT_ENROLLED_BODY:
      'Esta terminal no tiene una clave de registro activa. Esto sucede cuando la clave fue revocada, la terminal nunca fue activada, o estás usando un dispositivo distinto al que enroló la clave. Contacta a ventas (info@neogeniux.com) para emitir una nueva clave y luego actívala con el botón a continuación.',
    TERMINAL_ACTIVATE_KEY_TITLE: 'Activar nueva clave',
    TERMINAL_ACTIVATE_KEY_HELPER:
      'Ingresa la nueva clave de registro entregada por ventas. La terminal quedará vinculada a esta clave hasta que la elimines o sea revocada.',
    TERMINAL_ACTIVATE_KEY_CODE_LABEL: 'Clave de registro',
    TERMINAL_ACTIVATE_KEY_CODE_PLACEHOLDER: 'XXXX-XXXX-XXXX-XXXX-XXXX',
    TERMINAL_ACTIVATE_KEY_LABEL_FIELD: 'Etiqueta (opcional)',
    TERMINAL_ACTIVATE_KEY_LABEL_PLACEHOLDER: 'Caja 1 — Sucursal Principal',
    TERMINAL_ACTIVATE_KEY_SUBMIT: 'Activar',
    TERMINAL_ACTIVATE_KEY_OPEN: 'Activar nueva clave',
    TERMINAL_ACTIVATE_KEY_REPLACE_OPEN: 'Reemplazar con nueva clave',
    TERMINAL_ACTIVATE_KEY_SUCCESS: 'Terminal activada',
    TERMINAL_ACTIVATE_KEY_ERROR_ALREADY_ENROLLED:
      'Esta terminal ya está activada. Solicita la eliminación de la clave actual antes de activar una nueva.',
    TERMINAL_ACTIVATE_KEY_ERROR_FALLBACK:
      'No se pudo activar la clave. Verifica el código e inténtalo de nuevo.',
    SUCCESS: 'Éxito',
    ERROR: 'Error',
    PENDING: 'Pendiente',
    COMPLETED: 'Completado',
    FAILED: 'Fallido',
    CANCELLED: 'Cancelado',
    CREATED_SUCCESSFULLY: 'creado exitosamente',
    UPDATED_SUCCESSFULLY: 'actualizado exitosamente',
    DELETED_SUCCESSFULLY: 'eliminado exitosamente',
    ERROR_CREATING: 'Error al crear',
    ERROR_UPDATING: 'Error al actualizar',
    ERROR_DELETING: 'Error al eliminar',
    GENERIC_ERROR: 'Ha ocurrido un error inesperado',
    NO_PERMISSION: 'No tienes permisos para realizar esta acción',
    NOTIFICATION: 'Notificación',
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    DELETED: 'Eliminado',
    REPLACE: 'Reemplazar',
    ERROR_DELETING_FILE: 'Error al eliminar el archivo',
    RESULTS_FOUND: 'Resultados encontrados',
    NO_LIMIT: 'Sin límite',
    IMPORT: 'Importar',
    EXPORT: 'Exportar',
    EXPORT_PDF: 'Exportar a PDF',
    EXPORT_EXCEL: 'Exportar a Excel',
    EXPORT_CSV: 'Exportar a CSV',
    EXPORT_TEXT: 'Exportar a Texto',
    NEXT: 'Siguiente',
    PREVIOUS: 'Anterior',
    PAGE_LABEL: 'Página {{page}} / {{totalPages}} · {{total}} en total',
    REQUIRED: 'Requerido',
    DOWNLOAD: 'Descargar',
    VIEW: 'Ver',
    PREVIEW: 'Vista Previa',
    FILTER: 'Filtrar',
    ACTIVE_FILTER: 'Filtrado',
    FILTERS: 'Filtros',
    SORT_BY: 'Ordenar por',
    LOAD_MORE: 'Cargar más',
    APPLY: 'Aplicar',
    ASC: 'Ascendente',
    DESC: 'Descendente',
    STATUS: 'Estado',
    TOTAL: 'Total',
    PAID: 'Pagado',
    REMAINING: 'Pendiente',
    UNITS: 'unidades',
    PER_UNIT: 'por unidad',
    CLEAR: 'Limpiar',
    SELECT_ALL: 'Seleccionar todos',
    CLICK_TO_SELECT: 'Clic para seleccionar',
    CLICK_TO_DESELECT: 'Clic para quitar',
    CLEAR_FILTERS: 'Limpiar filtros',
    RESET_FILTERS: 'Restablecer filtros',
    FROM_DATE: 'Desde',
    TO_DATE: 'Hasta',
    RESTORE: 'Restaurar',
    UPDATE: 'Actualizar',
    UPDATING: 'Actualizando',
    CREATE: 'Crear',
    CREATE_NEW: 'Crear nuevo',
    DESCRIPTION: 'Descripción',
    CREATING: 'Creando',
    COPY: 'Copiar',
    OPTIONAL: 'opcional',
    ERROR_LOADING: 'Error al cargar',
    RESET: 'Restablecer',
    SAVE_CHANGES: 'Guardar cambios',
    TIP: 'Consejo',
    DRAG_COLUMN_BORDERS_TO_RESIZE:
      'Arrastra los bordes de las columnas para redimensionar',
    DRAG_TO_RESIZE_COLUMN: 'Arrastra para redimensionar columna',
    INFORMATION: 'Información',
    SOMETHING_WENT_WRONG: '¡Ups! Algo pasó',
    ERROR_REPORTED:
      'Estamos investigando. Refresca la pantalla y, si el problema persiste, reporta al administrador.',
    TECHNICAL_DETAILS: 'Detalles Técnicos (para desarrolladores)',
    TRY_AGAIN: 'Intentar de nuevo',
    VALIDATE: 'Validar',
    GO_HOME: 'Ir al inicio',
    CONTACT_SUPPORT:
      'Si el problema persiste, contacta al administrador y comparte el detalle técnico.',
    SHOW_ERROR: 'Ver error',
    HIDE_ERROR: 'Ocultar error',
    ERROR_REFERENCE: 'Referencia',
    ERROR_REPORT_COPIED: 'Detalle copiado. Compártelo con el administrador.',
    COPY_ERROR_DETAILS: 'Copiar detalle',
    ACCESS_RESTRICTED_TITLE: 'Acceso restringido',
    ACCESS_RESTRICTED_DESCRIPTION:
      'Actualmente no tienes acceso a esta funcionalidad.',
    COLUMNS_CONFIGURE: 'Configurar Columnas',
    SHOW_ALL: 'Mostrar Todo',
    HIDE_ALL: 'Ocultar Todo',
    RESET_COLUMNS: 'Restaurar predeterminado',
    SHOW_MORE: 'Ver más',
    SHOW_LESS: 'Ver menos',
    ALL: 'Todos',
    SCOPE_FILTER_LABEL: 'Alcance',
    SCOPE_FILTER_ALL: 'Todos',
    SCOPE_FILTER_ACCOUNTING: 'Contables',
    SCOPE_FILTER_OPERATIONAL: 'Operacionales',
    NOTES: 'Notas',
    NONE: 'Ninguno',
    MIN_ABBR: 'Mín',
    ADDITIONAL_COST: 'Costo adicional',
    SELECTED: 'Seleccionado(s)',
    CONFIGURE_VISIBLE_COLUMNS_FOR_ROLE:
      'Configurar columnas visibles para el rol:',
    CONFIRMATION_SAVE_CHANGES: '¿Confirmar cambios?',
    CLICK_SAVE_TO_APPLY_CHANGES: 'Haz clic en Guardar para aplicar los cambios',
    ALL_CHANGES_SAVED: 'Todos los cambios han sido guardados',
    ARE_YOU_SURE_WANT_TO_SAVE: '¿Estás seguro de que deseas guardar',
    COLUMN_VISIBILITY_CHANGES: 'cambios en la visibilidad de columnas?',
    STEP: 'Paso',
    OF: 'de',
    CURRENT: 'Actual',
    COMPLETE: 'Completado',
    COMPLETE_ONBOARDING: 'Completar Incorporación',
    REQUIRED_STEP: 'Paso Requerido',
    OPTIONAL_STEP: 'Paso Opcional',
    DEFAULT: 'Predeterminado',
    NO_RESULTS: 'Sin resultados',
    NEW: 'Nuevo',
    NOT_CONFIGURED: 'No configurado',
    CLICK_TO_CONFIGURE: 'Clic para configurar',
    EDIT_INVOICE_FIELD_HINT:
      'Este cambio solo afecta la factura, no los datos del negocio',
    UNSAVED_CHANGES_TITLE: 'Cambios sin guardar',
    UNSAVED_CHANGES_BRANCH_DESCRIPTION:
      'Tienes cambios sin guardar en el formulario. Si cambias de sucursal, se perderán los cambios actuales.',
    DISCARD_CHANGES: 'Descartar cambios',
    KEEP_EDITING: 'Seguir editando',
    BUSINESS_LEVEL: 'Nivel de negocio',
  },
  MENU: {
    PLATFORMSETTINGS: 'Configuración de la Plataforma',
    CUSTOMERS: 'Clientes',
    PROVIDERS: 'Proveedores',
    DASHBOARD: 'Resumen',
    POS: 'Punto de Venta',
    INVOICE: 'Facturación',
    PRODUCTS: 'Productos',
    CATEGORIES: 'Categorías',
    WAREHOUSES: 'Almacenes',
    MODIFIERS: 'Modificadores',
    USERS: 'Usuarios',
    SHIFTS: 'Turnos y Cuadre de Caja',
    REPORTS: 'Reportes',
    CONFIGURATION: 'Configuración',
    LOGOUT: 'Cerrar sesión',
    PROFILE: 'Perfil',
    SETTINGS: 'Ajustes',
    HELP: 'Ayuda',
    PURCHASEORDERS: 'Órdenes de Compra',
    PURCHASE_ORDERS: 'Órdenes de Compra',
    INVENTORY: 'Inventario',
    RECEIPTS: 'Recibos',
    OPENCLOSESHIFTS: 'Apertura/Cierre de Turnos',
    STOCKMOVEMENTS: 'Movimientos de Inventario',
    STOCKADJUSTMENTS: 'Ajustes de Inventario',
    SALESREPORT: 'Reporte de Ventas',
    SALESCOMMISSION: 'Comisiones',
    INVENTORYREPORT: 'Reporte de Inventario',
    USERACTIVITY: 'Actividad de Usuario',
    FINANCIALSUMMARY: 'Resumen Financiero',
    PAYMENTTYPES: 'Tipos de Pago',
    DAILYSUMMARY: 'Resumen Diario',
    IMAGE: 'Imagen',
    INVOICELIST: 'Facturas',
    // Configuration children
    BUSINESS: 'Empresa',
    BUSINESSSETTINGS: 'Configuración del Negocio',
    INVOICETEMPLATE: 'Plantilla de Factura',
    'ROLES&PERMISSIONS': 'Permisos de Roles',
    ROLE_PERMISSIONS: 'Permisos de Roles',
    NCF: 'NCF',
    TAXES: 'Impuestos',
    KDS: 'KDS',
    PAYMENTS: 'Pagos',
    APPROVALS: 'Aprobaciones',
    PROMOTIONS: 'Promociones',
    BRANCHOFFICES: 'Sucursales',
    ECFONBOARDING: 'Facturación Electrónica',
    ECF_BATCH: 'ECF por Lotes',
    RESTAURANT: 'Restaurante',
    FLOORPLAN: 'Plano del Salón',
    SERVICE: 'Servicio',
    FOLLOW_UP_REMINDERS: 'Recordatorios',
    SYNCRECONCILIATION: 'Conciliación de Sincronización',
    SYNCRECONCILIATION_COMING_SOON:
      'La resolución de conflictos de sincronización entre dispositivos offline y la nube llegará en una próxima entrega.',
  },
  PERMISSIONS: {
    CATEGORIES: {
      DASHBOARD: 'Resumen',
      POS: 'Punto de Venta',
      INVENTORY: 'Inventario',
      INVOICES: 'Facturación',
      CUSTOMERS: 'Clientes',
      PROVIDERS: 'Proveedores',
      USERS: 'Usuarios',
      ROLES: 'Roles',
      SETTINGS: 'Configuración',
      REPORTS: 'Reportes',
      TAXES: 'Impuestos',
      PAYMENTS: 'Pagos',
      PURCHASE_ORDERS: 'Órdenes de Compra',
      NCF: 'NCF',
      CONFIGURATION: 'Configuración del Sistema',
      ROLES_PERMISSIONS: 'Permisos de Roles',
      SHIFTS: 'Turnos y Cuadres de Caja',
      COLUMN_VISIBILITY: 'Personalización de tablas',
      APPROVALS: 'Aprobaciones',
      QUOTES: 'Cotizaciones',
      SALES_ORDERS: 'Órdenes de Venta',
      AUDIT_TRAIL: 'Auditoría',
      RENTALS: 'Alquileres',
    },
    NAMES: {
      // Dashboard
      'dashboard.view': 'Ver Panel de Control',
      'dashboard.analytics': 'Ver Analíticas',
      // POS
      'pos.access': 'Acceder al POS',
      'pos.create_sale': 'Crear Ventas',
      'pos.apply_discount': 'Aplicar Descuentos',
      'pos.void': 'Anular Transacción',
      'pos.view_history': 'Ver Historial de Ventas',
      // Inventory - Products
      'inventory.products.view': 'Ver Productos y Servicios',
      'inventory.products.create': 'Crear Productos y/o Servicios',
      'inventory.products.image.manage': 'Gestionar Imágenes de Productos',
      'inventory.products.image.report': 'Reportar Imagen de Producto',
      'inventory.products.edit': 'Editar Productos y/o Servicios',
      'inventory.products.toggle_status':
        'Cambiar Estado de Productos y/o Servicios',
      'inventory.products.delete': 'Eliminar Productos y/o Servicios',
      'inventory.products.restore': 'Restaurar Productos y/o Servicios',
      'inventory.products.bulk_import': 'Importar Productos en Lote',
      'inventory.products.bulk_export': 'Exportar Productos en Lote',
      'inventory.products.bulk_update': 'Actualización Masiva de Productos',
      'inventory.products.bulk_update_prices':
        'Actualización Masiva de Precios',
      'inventory.products.edit_cost': 'Editar Costo del Producto',
      // Procurement — Purchase Orders
      'procurement.purchase_orders.view': 'Ver Órdenes de Compra',
      'procurement.purchase_orders.create': 'Crear Órdenes de Compra',
      'procurement.purchase_orders.edit': 'Editar Órdenes de Compra',
      'procurement.purchase_orders.delete': 'Eliminar Órdenes de Compra',
      'procurement.purchase_orders.receive':
        'Recibir Ítems de Órdenes de Compra',
      'procurement.purchase_orders.convert':
        'Convertir Orden de Compra a Factura de Proveedor',
      // Accounting — Bank Reconciliation
      'accounting.bank_reconciliation.import': 'Importar Extracto Bancario',
      'accounting.bank_reconciliation.match': 'Conciliar Líneas del Extracto',
      'accounting.bank_reconciliation.view': 'Ver Conciliación Bancaria',
      'accounting.bank_reconciliation.lock': 'Cerrar Conciliación',
      // Accounting — Journal
      'accounting.journal.create': 'Crear Asiento',
      'accounting.journal.create_adjustment': 'Crear Asiento de Ajuste',
      'accounting.journal.reverse': 'Reversar Asiento',
      'accounting.cancelled_journal.view': 'Ver Asientos Anulados',
      // Accounting — Reports
      'accounting.reports.balance_sheet.view': 'Ver Balance General',
      'accounting.reports.trial_balance.view': 'Ver Balanza de Comprobación',
      'accounting.reports.trial_balance.export':
        'Exportar Balanza de Comprobación',
      'accounting.reports.sums_and_balances.view':
        'Ver Balance de Sumas y Saldos',
      'accounting.reports.cash_flow.view': 'Ver Flujo de Efectivo',
      'accounting.reports.cash_flow.export': 'Exportar Flujo de Efectivo',
      'accounting.reports.expenses.export': 'Exportar Reporte de Gastos',
      'accounting.reports.budget_execution.view':
        'Ver Reportes Presupuestarios',
      'accounting.reports.budget_execution.export':
        'Exportar Reportes Presupuestarios',
      // Configuration — Taxes
      'configuration.taxes.set_default': 'Fijar Impuesto por Defecto',
      // Configuration — Bank Accounts
      'configuration.bank_account.view': 'Ver Cuentas Bancarias',
      // Configuration — Accounting
      'configuration.accounting.access': 'Acceder a Contabilidad',
      // Configuration — e-CF Certificates
      'configuration.ecf_certificates.delete': 'Eliminar Certificados e-CF',
      // Notifications
      'notifications.view': 'Ver Notificaciones',
      // Operations
      'operations.reset': 'Reiniciar Datos Operacionales',
      // Notifications
      'notifications.dismiss': 'Descartar Notificaciones',
      'notifications.preferences': 'Preferencias de Notificaciones',
      // Configuration — Payment Types
      'configuration.payment_type.delete': 'Eliminar Tipo de Pago',
      // Configuration — Shifts
      'configuration.shifts.export': 'Exportar Turnos',
      // Configuration — Branches
      'configuration.branches.delete': 'Eliminar Sucursales',
      // Configuration — e-CF
      'configuration.ecf.onboarding': 'Onboarding e-CF',
      'inventory.products.manage_modifiers':
        'Gestionar Modificadores de Producto',
      'inventory.products.view_stock': 'Ver Niveles de Stock',
      // Inventory - Categories
      'inventory.categories.view': 'Ver Categorías',
      'inventory.categories.create': 'Crear Categorías',
      'inventory.categories.edit': 'Editar Categorías',
      'inventory.categories.delete': 'Eliminar Categorías',
      'inventory.categories.restore': 'Restaurar Categorías',
      // Inventory - Stock Movements
      'inventory.stock_movements.view': 'Ver Movimientos de Stock',
      'inventory.stock_movements.create': 'Crear Ajustes de Stock',
      'inventory.stock_movements.approve': 'Aprobar Movimientos de Stock',
      // Invoices
      'invoices.view': 'Ver Facturas',
      'invoices.create': 'Crear Factura',
      'invoices.edit': 'Editar Factura',
      'invoices.delete': 'Eliminar Factura',
      'invoices.restore': 'Restaurar Factura',
      'invoices.view_details': 'Ver Detalles de Factura',
      'invoices.print': 'Imprimir Factura',
      'invoices.email': 'Enviar Factura por Email',
      'invoices.approve': 'Aprobar Factura',
      'invoices.cancel': 'Cancelar Factura',
      'invoices.write_off': 'Ajustar saldo y/o pago de Factura',
      'invoices.delivery.edit': 'Modificar delivery en factura emitida',
      'invoices.process_payment': 'Procesar Pago',
      // Customers
      'customers.view': 'Ver Clientes',
      'customers.create': 'Crear Cliente',
      'customers.edit': 'Editar Cliente',
      'customers.delete': 'Eliminar Cliente',
      'customers.restore': 'Restaurar Cliente',
      'customers.view_history': 'Ver Historial de Cliente',
      'customers.export': 'Exportar Clientes',
      // Providers
      'providers.view': 'Ver Proveedores',
      'providers.create': 'Crear Proveedor',
      'providers.edit': 'Editar Proveedor',
      'providers.delete': 'Eliminar Proveedor',
      'providers.restore': 'Restaurar Proveedor',
      'providers.view_history': 'Ver Historial de Proveedor',
      // Purchase Orders
      'inventory.purchase_orders.view': 'Ver Órdenes de Compra',
      'inventory.purchase_orders.create': 'Crear Orden de Compra',
      'inventory.purchase_orders.edit': 'Editar Orden de Compra',
      'inventory.purchase_orders.delete': 'Eliminar Orden de Compra',
      'inventory.purchase_orders.restore': 'Restaurar Orden de Compra',
      'inventory.purchase_orders.approve': 'Aprobar Orden de Compra',
      'inventory.purchase_orders.receive': 'Recibir Orden de Compra',
      'inventory.purchase_orders.cancel': 'Cancelar Orden de Compra',
      // Shifts (under Configuration)
      'configuration.shifts.view': 'Ver Turnos',
      // Reports
      'reports.sales.view': 'Ver Reporte de Ventas',
      'reports.sales.export': 'Exportar Reporte de Ventas',
      'reports.inventory.view': 'Ver Reporte de Inventario',
      'reports.inventory.export': 'Exportar Reporte de Inventario',
      'reports.financial.view': 'Ver Reporte Financiero',
      'reports.financial.export': 'Exportar Reporte Financiero',
      'reports.payments.view': 'Ver Reportes de Pagos',
      'reports.payments.export': 'Exportar Reportes de Pagos',
      'reports.user_activity.view': 'Ver Actividad de Usuarios',
      'reports.user_activity.export': 'Exportar Actividad de Usuarios',
      'reports.daily_summary.view': 'Ver Resumen Diario',
      'reports.daily_summary.export': 'Exportar Resumen Diario',
      // Users
      'users.view': 'Ver Usuarios',
      'users.create': 'Crear Usuario',
      'users.edit': 'Editar Usuario',
      'users.delete': 'Eliminar Usuario',
      'users.restore': 'Restaurar Usuario',
      'users.manage_roles': 'Gestionar Roles de Usuario',
      'users.reset_password': 'Restablecer contraseña de Usuario',
      'users.toggle_status': 'Activar/Desactivar Usuario',
      // Configuration
      'configuration.view': 'Ver Configuración',
      'configuration.business.view': 'Ver Información de Empresa',
      'configuration.business.edit': 'Editar Información de Empresa',
      'configuration.roles_permissions.view_roles': 'Ver Roles',
      'configuration.roles_permissions.create_role': 'Crear Rol',
      'configuration.roles_permissions.edit_role': 'Editar Rol',
      'configuration.roles_permissions.view_permissions': 'Ver Permisos',
      'configuration.roles_permissions.assign': 'Asignar Permisos',
      'configuration.roles_permissions.remove': 'Eliminar Permisos',
      'configuration.roles_permissions.edit': 'Editar Rol',
      'configuration.roles_permissions.delete': 'Eliminar Rol',
      'configuration.business_settings.view': 'Ver Configuración Operacional',
      'configuration.business_settings.edit':
        'Editar Configuración Operacional',
      'configuration.taxes.view': 'Ver Configuración de Impuestos',
      'configuration.taxes.create': 'Crear Regla de Impuesto',
      'configuration.taxes.edit': 'Editar Regla de Impuesto',
      'configuration.taxes.delete': 'Eliminar Regla de Impuesto',
      'configuration.ncf.view': 'Ver Secuencias NCF',
      'configuration.ncf.create': 'Crear Secuencia NCF',
      'configuration.ncf.edit': 'Editar Secuencia NCF',
      'configuration.ncf.delete': 'Eliminar Secuencia NCF',
      'configuration.pos.view': 'Ver Configuración de POS',
      'configuration.pos.edit': 'Editar Configuración de POS',
      // Approvals
      'approvals.view': 'Ver Aprobaciones',
      'approvals.approve': 'Aprobar/Rechazar Solicitudes',
      'approvals.self_approve': 'Aprobar Sus Propias Solicitudes',
      // Inventory - Additional
      'inventory.access': 'Acceder al Inventario',
      'inventory.products.import': 'Importar Productos',
      'inventory.products.export': 'Exportar Productos',
      'inventory.products.manage_price_tiers': 'Gestionar Escalas de Precios',
      'inventory.products.assign_price_tiers': 'Asignar Escalas de Precios',
      'inventory.warehouses.view': 'Ver Almacenes',
      'inventory.warehouses.create': 'Crear Almacén',
      'inventory.warehouses.edit': 'Editar Almacén',
      'inventory.warehouses.delete': 'Eliminar Almacén',
      'inventory.warehouses.restore': 'Restaurar Almacén',
      'inventory.stock_movement.view': 'Ver Movimiento de Stock',
      'inventory.stock_movement.create': 'Crear Movimiento de Stock',
      'inventory.merma.record': 'Registrar Merma',
      'inventory.kds.view': 'Ver KDS',
      'inventory.kds.mark_ready': 'Marcar como Listo (KDS)',
      'inventory.kds.mark_cancelled': 'Marcar como Cancelado (KDS)',
      // Invoices - Additional
      'invoices.access': 'Acceder a Facturación',
      // Reports - Additional
      'reports.access': 'Acceder a Reportes',
      'reports.payment_types.view': 'Ver Tipos de Pago',
      // Column Visibility
      'column.VIEW_COLUMN_GROUP_PRODUCT_BASIC_INFO':
        'Ver Información Básica del Producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_PRICING': 'Ver Costo del Producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_SELLING_PRICE':
        'Ver Precio de Venta del Producto',
      'column.EDIT_COLUMN_GROUP_PRODUCT_PRICING': 'Editar Costo del Producto',
      'column.EDIT_COLUMN_GROUP_PRODUCT_SELLING_PRICE':
        'Editar Precio de Venta del Producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_INVENTORY':
        'Ver Inventario del Producto',
      'column.EDIT_COLUMN_GROUP_PRODUCT_INVENTORY':
        'Editar Umbrales de Inventario del Producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_PROVIDERS':
        'Ver Proveedores del Producto',
      'column.EDIT_COLUMN_GROUP_PRODUCT_PROVIDERS':
        'Editar Proveedor del Producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_AUDIT_TRAIL':
        'Ver Historial de Auditoría del Producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_IMAGES': 'Ver Imágenes del Producto',
      'column.VIEW_COLUMN_GROUP_INVOICE_BASIC_INFO':
        'Ver Información Básica de Factura',
      'column.VIEW_COLUMN_GROUP_INVOICE_ITEMS': 'Ver Elementos de Factura',
      'column.VIEW_COLUMN_GROUP_INVOICE_PRICING': 'Ver Precios de Factura',
      'column.VIEW_COLUMN_GROUP_INVOICE_PAYMENT':
        'Ver Información de Pago de Factura',
      'column.VIEW_COLUMN_GROUP_INVOICE_AUDIT_TRAIL':
        'Ver Historial de Auditoría de Factura',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_BASIC_INFO':
        'Ver Información Básica del Cliente',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_ADDRESS': 'Ver Dirección del Cliente',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_FINANCIAL':
        'Ver Información Financiera del Cliente',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_AUDIT_TRAIL':
        'Ver Historial de Auditoría del Cliente',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_BASIC_INFO':
        'Ver Información Básica de Orden de Compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_ITEMS':
        'Ver Elementos de Orden de Compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_PRICING':
        'Ver Precios de Orden de Compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_STATUS':
        'Ver Estado de Orden de Compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_AUDIT_TRAIL':
        'Ver Historial de Auditoría de Orden de Compra',
      'column.VIEW_COLUMN_GROUP_STOCK_MOVEMENT_BASIC_INFO':
        'Ver Información Básica de Movimiento',
      'column.VIEW_COLUMN_GROUP_STOCK_MOVEMENT_LOCATION':
        'Ver Ubicación de Movimiento',
      'column.VIEW_COLUMN_GROUP_STOCK_MOVEMENT_AUDIT':
        'Ver Auditoría de Movimiento',
      'column.VIEW_COLUMN_GROUP_INVENTORY_BASIC_INFO':
        'Ver Información Básica de Inventario',
      'column.VIEW_COLUMN_GROUP_INVENTORY_QUANTITIES':
        'Ver Cantidades de Inventario',
      'column.VIEW_COLUMN_GROUP_INVENTORY_AUDIT_TRAIL':
        'Ver Historial de Auditoría de Inventario',
      'column.VIEW_COLUMN_GROUP_INTERNAL_NOTES': 'Ver Notas Internas',
      // Restaurant
      'restaurant.access': 'Acceder a Restaurante',
      'restaurant.floor_plan.view': 'Ver Plano del Salón',
      'restaurant.floor_plan.edit': 'Editar Plano del Salón',
      'restaurant.environment.create': 'Crear Salón',
      'restaurant.environment.delete': 'Eliminar Salón',
      'restaurant.tables.view': 'Ver Mesas',
      'restaurant.tables.open': 'Abrir Mesa',
      'restaurant.tables.close': 'Cerrar Mesa',
      'restaurant.tab.view': 'Ver Cuentas',
      'restaurant.tab.create': 'Crear Cuenta',
      'restaurant.tab.edit': 'Editar Cuenta',
      'restaurant.tab.delete': 'Eliminar Cuenta',
      'restaurant.tab.tax.remove': 'Quitar Impuesto de Cuenta',
      'restaurant.tab_item.add': 'Agregar Ítems a Cuenta',
      'restaurant.tab_item.void': 'Anular Ítems de Cuenta',
      'restaurant.tab_item.transfer': 'Transferir Ítem de Cuenta',
      'restaurant.commanda.reprint': 'Reimprimir Comanda',
      // Configuration - Branches
      'configuration.branches.view': 'Ver Sucursales',
      'configuration.branches.create': 'Crear Sucursal',
      'configuration.branches.edit': 'Editar Sucursal',
      // Configuration - Delivery
      'configuration.delivery.view': 'Ver Configuración de Delivery',
      'configuration.delivery.create': 'Crear Configuración de Delivery',
      'configuration.delivery.edit': 'Editar Configuración de Delivery',
      'configuration.delivery.delete': 'Eliminar Configuración de Delivery',
      // Configuration - Promotions
      'configuration.promotions.view': 'Ver Promociones',
      'configuration.promotions.create': 'Crear Promoción',
      'configuration.promotions.edit': 'Editar Promoción',
      'configuration.promotions.delete': 'Eliminar Promoción',
      'configuration.promotions.activate': 'Activar/Desactivar Promoción',
      // Configuration - eCF
      'configuration.ecf_certificates.view': 'Ver Certificados eCF',
      'configuration.ecf_certificates.manage': 'Gestionar Certificados eCF',
      'configuration.ecf_batch.view': 'Ver Procesamiento Masivo eCF',
      'configuration.ecf_batch.process': 'Procesar Lote eCF',
      // Customers - Credit
      'customers.adjust_credit': 'Ajustar Crédito de Cliente',
      'customers.view_credit_history': 'Ver Historial de Crédito',
      'customers.apply_credit': 'Aplicar Crédito a Factura',
      // Follow-up reminders
      'follow_up_reminders.view': 'Ver Recordatorios de Seguimiento',
      'follow_up_reminders.create': 'Crear Recordatorio de Seguimiento',
      'follow_up_reminders.mark': 'Gestionar Recordatorios de Seguimiento',
      // Invoices - additional
      'invoices.bulk_import': 'Importar Facturas en Lote',
      'invoices.create_quote': 'Cotizaciones',
      'invoices.export_quotes': 'Exportar Cotizaciones',
      'invoices.create_credit_note': 'Crear Nota de Crédito',
      'invoices.approve_credit_note': 'Aprobar Nota de Crédito',
      'invoices.tax.remove': 'Quitar Impuesto de Factura',
      'documents.backdate': 'Registrar documentos con fecha anterior',
      // Payments
      'payments.view': 'Ver Pagos',
      'payments.create': 'Recibir Pago',
      'payments.edit': 'Editar Pago',
      'payments.delete': 'Eliminar Pago',
      'payments.export': 'Exportar Pagos',
      'payments.make_expense': 'Registrar Pago de Gasto',
      // POS
      'pos.tax.remove': 'Quitar Impuesto en POS',
      'pos.supervised_removal.bypass': 'Omitir aprobación de POS supervisado',
      'sales.items.edit_price': 'Editar Precio Unitario en Ventas',
      'sales.items.price_below_min':
        'Vender por debajo del precio mínimo del cliente',
      'inventory.products.quick_create': 'Crear producto rápido desde búsqueda',
      'invoices.items.free_create': 'Agregar ítems libres a facturas',
      // Inventory - Debit Notes
      'inventory.debit_notes.create': 'Crear Nota de Débito',
      'inventory.debit_notes.edit': 'Editar Nota de Débito',
      'inventory.debit_notes.delete': 'Eliminar Nota de Débito',
      // Inventory - Purchase Orders extra
      'inventory.purchase_orders.print': 'Imprimir Orden de Compra',
      // Inventory - Stock Counting
      'inventory.stock_counting.view': 'Ver Conteo Físico',
      'inventory.stock_counting.create': 'Crear Conteo Físico',
      'inventory.stock_counting.manage': 'Gestionar Conteo Físico',
      // Shifts
      'shifts.cash_vouchers': 'Gestionar Vales de Caja',
      'shifts.reconciliation': 'Ver Cuadre de Turno',
      'shifts.daily_summary': 'Ver Resumen Diario de Sucursal',
      // Audit trail
      'documents.history.view': 'Ver Historial de Documentos',
      // Reseller (hidden from business perm-picker; documented here
      // for completeness so the reseller portal renders labels).
      'reseller.dashboard.view': 'Reseller — Ver Panel',
      'reseller.businesses.view': 'Reseller — Ver Businesses',
      'reseller.businesses.create': 'Reseller — Crear Businesses',
      'reseller.businesses.impersonate': 'Reseller — Impersonar Businesses',
      'reseller.commissions.view': 'Reseller — Ver Commissions',
      'reseller.payouts.view': 'Reseller — Ver Payouts',
      'reseller.contracts.accept': 'Reseller — Aceptar Contratos',
      // Rentals (Alquileres)
      'rentals.view': 'Ver Alquileres',
      'rentals.pedido.view': 'Ver Pedido',
      'rentals.pedido.create': 'Crear Pedido',
      'rentals.pedido.edit': 'Editar Pedido',
      'rentals.pedido.cancel': 'Cancelar Pedido',
      'rentals.pedido.confirm': 'Confirmar Pedido',
      'rentals.pedido.send_to_production': 'Enviar a Producción',
      'rentals.pedido.mark_ready': 'Marcar Listo',
      'rentals.pedido.mark_delivered': 'Marcar Entregado',
      'rentals.pedido.process_return': 'Procesar Devolución',
      'rentals.pedido.finalize': 'Finalizar Pedido',
      'rentals.damage_charge.adjust': 'Ajustar Cargo por Daños',
      'rentals.fianza.override': 'Anular Requisito de Fianza',
      'rentals.items.zero_price': 'Ítems con Precio Cero',
      'rentals.late_return.refund_anyway': 'Reembolsar Aun con Retraso',
      'rentals.reservations.view': 'Ver Reservaciones',
      'rentals.reservations.create': 'Crear Reservaciones',
      'rentals.reservations.edit': 'Editar Reservaciones',
      'rentals.reservations.confirm': 'Confirmar Reservaciones',
      'rentals.reservations.cancel': 'Cancelar Reservaciones',
      'rentals.reservations.convert': 'Convertir Reservaciones',
      'rentals.calendars.view': 'Ver Calendarios',
      'rentals.pickup.view': 'Ver Recogido',
      'rentals.pickup.collect': 'Registrar Recogido',
      'rentals.pickup.resend_email': 'Reenviar Correo de Recogido',
    },
    DESCRIPTIONS: {
      // Dashboard
      'dashboard.view': 'Acceder a la página del panel y ver métricas',
      'dashboard.analytics': 'Ver widgets de analíticas detalladas',
      // POS
      'pos.access': 'Abrir interfaz del punto de venta',
      'pos.create_sale': 'Procesar transacciones de venta',
      'pos.apply_discount': 'Aplicar descuentos a las ventas',
      'pos.void': 'Cancelar/anular una transacción',
      'pos.view_history': 'Ver transacciones pasadas',
      // Inventory - Products
      'inventory.products.view': 'Ver lista de productos',
      'inventory.products.create': 'Agregar nuevos productos',
      'inventory.products.image.manage':
        'Subir, reemplazar y eliminar imágenes de productos',
      'inventory.products.image.report':
        'Reportar una imagen incorrecta del banco compartido',
      'inventory.products.edit': 'Modificar productos existentes',
      'inventory.products.toggle_status': 'Activar o desactivar productos',
      'inventory.products.delete': 'Eliminación suave de productos',
      'inventory.products.restore': 'Restaurar productos eliminados',
      'inventory.products.bulk_import': 'Importar productos desde CSV',
      'inventory.products.bulk_export': 'Exportar productos a CSV',
      'inventory.products.bulk_update':
        'Cambiar impuesto, unidad de medida, proveedor, categoría, flag de impuesto incluido, mínimo/máximo de stock y punto de reorden en muchos productos a la vez',
      'inventory.products.bulk_update_prices':
        'Subir, bajar o fijar precio de venta en muchos productos a la vez — filtrar por categoría, productos específicos o todos',
      'inventory.products.edit_cost':
        'Modificar el costo del producto cuando el costo lo gestiona el sistema desde compras (modo last_purchase)',
      // Procurement — Purchase Orders
      'procurement.purchase_orders.view':
        'Ver la lista de órdenes de compra de procurement',
      'procurement.purchase_orders.create':
        'Crear nuevas órdenes de compra de procurement',
      'procurement.purchase_orders.edit':
        'Modificar órdenes de compra de procurement',
      'procurement.purchase_orders.delete':
        'Eliminar órdenes de compra de procurement',
      'procurement.purchase_orders.receive':
        'Registrar recepciones (parciales o totales) contra una orden de compra de procurement',
      'procurement.purchase_orders.convert':
        'Convertir una orden de compra de procurement en una Factura de Proveedor',
      // Accounting — Bank Reconciliation
      'accounting.bank_reconciliation.import': 'Subir extracto en CSV / OFX',
      'accounting.bank_reconciliation.match':
        'Conciliar líneas del extracto con asientos contables',
      'accounting.bank_reconciliation.view':
        'Explorar extractos bancarios y estado de conciliación',
      'accounting.bank_reconciliation.lock':
        'Finalizar la conciliación del período',
      // Accounting — Journal
      'accounting.journal.create': 'Registrar un asiento manual',
      'accounting.journal.create_adjustment':
        'Registrar asientos de ajuste en períodos cerrados (no bloqueados)',
      'accounting.journal.reverse': 'Crear un asiento reverso',
      'accounting.cancelled_journal.view': 'Ver asientos reversados o anulados',
      // Accounting — Reports
      'accounting.reports.balance_sheet.view': 'Reporte Balance General',
      'accounting.reports.trial_balance.view':
        'Reporte Balanza de Comprobación',
      'accounting.reports.trial_balance.export':
        'Exportar Balanza de Comprobación',
      'accounting.reports.sums_and_balances.view':
        'Reporte Balance de Sumas y Saldos',
      'accounting.reports.cash_flow.view': 'Reporte Flujo de Efectivo',
      'accounting.reports.cash_flow.export': 'Exportar Flujo de Efectivo',
      'accounting.reports.expenses.export': 'Exportar Reporte de Gastos',
      'accounting.reports.budget_execution.view': 'Reportes Presupuestarios',
      'accounting.reports.budget_execution.export':
        'Exportar Reportes Presupuestarios',
      // Configuration — Taxes
      'configuration.taxes.set_default':
        'Cambiar cuál impuesto es el predeterminado del negocio. El predeterminado se aplica automáticamente al crear producto, agregar ítem a factura, POS, restaurante, orden de compra e importación masiva cuando no se especifica un impuesto explícito.',
      // Configuration — Bank Accounts
      'configuration.bank_account.view':
        'Explorar el registro de cuentas bancarias',
      // Configuration — Accounting
      'configuration.accounting.access':
        'Abrir Configuración → Contabilidad (menú padre)',
      // Configuration — e-CF Certificates
      'configuration.ecf_certificates.delete':
        'Eliminar certificados digitales cargados para la firma de e-CF',
      // Notifications
      'notifications.view':
        'Leer la campana de notificaciones in-app del usuario actual (listar, resumir, marcar como leído).',
      // Operations
      'operations.reset':
        'Elimina cada fila transaccional (facturas, pagos, órdenes de compra, turnos, contabilidad, etc.) para que el negocio reinicie con libros limpios. La configuración (catálogo, clientes, proveedores, secuencias) se preserva.',
      // Notifications
      'notifications.dismiss':
        'Descartar una notificación de la campana del usuario actual. La verificación de propiedad garantiza que la notificación es para este usuario o su rol.',
      'notifications.preferences':
        'Leer y actualizar las preferencias de canal (email / in-app / push) por tipo de evento del usuario actual.',
      // Configuration — Payment Types
      'configuration.payment_type.delete': 'Eliminar un tipo de pago.',
      // Configuration — Shifts
      'configuration.shifts.export':
        'Descargar la lista de turnos (filtros actuales) como PDF / Excel / CSV / TXT.',
      // Configuration — Branches
      'configuration.branches.delete': 'Eliminar sucursales.',
      // Configuration — e-CF
      'configuration.ecf.onboarding':
        'Acceder al asistente /configuration/ecf-onboarding (link en el sidebar + ruta). Sadmin puede revocarlo por business vía /sadmin/business-features → Módulos y permisos.',
      'inventory.products.manage_modifiers':
        'Agregar/editar modificadores de producto',
      'inventory.products.view_stock': 'Ver niveles de stock',
      // Inventory - Categories
      'inventory.categories.view': 'Ver lista de categorías',
      'inventory.categories.create': 'Agregar nuevas categorías',
      'inventory.categories.edit': 'Modificar categorías',
      'inventory.categories.delete': 'Eliminar categorías',
      'inventory.categories.restore': 'Restaurar categorías eliminadas',
      // Inventory - Stock Movements
      'inventory.stock_movements.view': 'Ver historial de movimientos de stock',
      'inventory.stock_movements.create': 'Crear ajustes de stock',
      'inventory.stock_movements.approve': 'Aprobar cambios de stock',
      // Invoices
      'invoices.view': 'Ver lista de facturas',
      'invoices.create': 'Crear nuevas facturas',
      'invoices.edit': 'Modificar facturas',
      'invoices.delete': 'Eliminación suave de facturas',
      'invoices.restore': 'Restaurar facturas eliminadas',
      'invoices.view_details': 'Ver detalles completos de la factura',
      'invoices.print': 'Imprimir/PDF factura',
      'invoices.email': 'Enviar factura por correo electrónico',
      'invoices.approve': 'Aprobar facturas',
      'invoices.cancel': 'Cancelar facturas',
      'invoices.write_off':
        'Perdonar manualmente un saldo pendiente en una factura (deuda incobrable / disputa resuelta / saldo pequeño / cortesía). Registra un asiento contra la cuenta de gasto por incobrables y deja una fila inmutable en el log de ajustes con el operador, la razón y un comentario obligatorio. No puede exceder el saldo pendiente ni ejecutarse sobre facturas ya pagadas o canceladas.',
      'invoices.delivery.edit':
        'Modifica el método de entrega y/o el repartidor asignado en una factura ya emitida, sin tocar los totales, subtotal, impuestos ni el costo de envío guardado. El modal muestra una advertencia cuando el mínimo del nuevo método es superior al costo de envío guardado — la reconciliación de la tarifa queda a cargo del negocio. Cada cambio queda registrado en el Historial de la factura.',
      'invoices.process_payment':
        'Procesar pagos de facturas pendientes (para flujo de cajero)',
      // Customers
      'customers.view': 'Ver lista de clientes',
      'customers.create': 'Agregar nuevos clientes',
      'customers.edit': 'Modificar información del cliente',
      'customers.delete': 'Eliminación suave de clientes',
      'customers.restore': 'Restaurar clientes eliminados',
      'customers.view_history': 'Ver historial de compras del cliente',
      'customers.export': 'Exportar lista de clientes',
      // Providers
      'providers.view': 'Ver lista de proveedores',
      'providers.create': 'Agregar nuevos proveedores',
      'providers.edit': 'Modificar información del proveedor',
      'providers.delete': 'Eliminación suave de proveedores',
      'providers.restore': 'Restaurar proveedores eliminados',
      'providers.view_history': 'Ver historial de compras con el proveedor',
      // Purchase Orders
      'inventory.purchase_orders.view': 'Ver lista de órdenes de compra',
      'inventory.purchase_orders.create': 'Crear nuevas órdenes de compra',
      'inventory.purchase_orders.edit': 'Modificar órdenes de compra',
      'inventory.purchase_orders.delete': 'Eliminar órdenes de compra',
      'inventory.purchase_orders.restore':
        'Restaurar órdenes de compra eliminadas',
      'inventory.purchase_orders.approve': 'Aprobar órdenes de compra',
      'inventory.purchase_orders.receive':
        'Marcar orden de compra como recibida',
      'inventory.purchase_orders.cancel': 'Cancelar órdenes de compra',
      // Shifts (under Configuration)
      'configuration.shifts.view': 'Ver lista de turnos',
      // Standalone shifts
      'shifts.open_close': 'Acceso rápido para abrir y cerrar turnos',
      // Reports
      'reports.sales.view': 'Ver reportes de ventas',
      'reports.sales.export': 'Exportar datos de ventas',
      'reports.inventory.view': 'Ver reportes de inventario',
      'reports.inventory.export': 'Exportar datos de inventario',
      'reports.financial.view': 'Ver reportes financieros',
      'reports.financial.export': 'Exportar datos financieros',
      'reports.payments.view': 'Ver análisis de pagos',
      'reports.payments.export': 'Exportar datos de pagos',
      'reports.user_activity.view': 'Ver registros de actividad de usuarios',
      'reports.user_activity.export': 'Exportar datos de actividad',
      'reports.daily_summary.view': 'Ver reportes diarios',
      'reports.daily_summary.export': 'Exportar datos diarios',
      // Users
      'users.view': 'Ver lista de usuarios',
      'users.create': 'Agregar nuevos usuarios',
      'users.edit': 'Modificar información del usuario',
      'users.delete': 'Eliminación suave de usuarios',
      'users.restore': 'Restaurar usuarios eliminados',
      'users.manage_roles': 'Asignar roles a usuarios',
      'users.reset_password': 'Restablecer contraseñas de usuarios',
      'users.toggle_status': 'Habilitar/deshabilitar usuarios',
      // Inventory - Additional
      'inventory.access': 'Acceder al módulo de inventario (menú padre)',
      'inventory.products.import': 'Importar productos desde archivo',
      'inventory.products.export': 'Exportar productos a archivo',
      'inventory.products.manage_price_tiers':
        'Crear y editar escalas de precios',
      'inventory.products.assign_price_tiers':
        'Asignar escalas de precios a productos',
      'inventory.warehouses.view': 'Ver lista de almacenes',
      'inventory.warehouses.create': 'Crear nuevos almacenes',
      'inventory.warehouses.edit': 'Editar información de almacenes existentes',
      'inventory.warehouses.delete': 'Eliminar almacenes',
      'inventory.warehouses.restore': 'Restaurar almacenes eliminados',
      'inventory.stock_movement.view': 'Ver detalles de movimiento de stock',
      'inventory.stock_movement.create': 'Crear nuevos movimientos de stock',
      'inventory.merma.record':
        'Registrar merma (pérdida por rotación, corte, mal manejo o vencimiento) y ver el reporte agregado.',
      'inventory.kds.view': 'Ver pantalla de cocina (KDS)',
      'inventory.kds.mark_ready': 'Marcar órdenes como listas',
      'inventory.kds.mark_cancelled': 'Marcar órdenes como canceladas',
      // Invoices - Additional
      'invoices.access': 'Acceder al módulo de facturación (menú padre)',
      // Reports - Additional
      'reports.access': 'Acceder al módulo de reportes (menú padre)',
      'reports.payment_types.view': 'Ver análisis de tipos de pago',
      // Configuration
      'configuration.view': 'Ver opciones de configuración del sistema',
      'configuration.business.view': 'Ver información de la empresa',
      'configuration.business.edit': 'Modificar información de la empresa',
      'configuration.business_settings.view':
        'Ver configuraciones operacionales',
      'configuration.business_settings.edit':
        'Modificar configuraciones operacionales',
      'configuration.roles_permissions.view_roles': 'Ver lista de roles',
      'configuration.roles_permissions.create_role': 'Crear nuevos roles',
      'configuration.roles_permissions.edit_role': 'Modificar roles existentes',
      'configuration.roles_permissions.view_permissions':
        'Ver todos los permisos disponibles',
      'configuration.roles_permissions.assign': 'Asignar permisos a roles',
      'configuration.roles_permissions.remove': 'Quitar permisos de roles',
      'configuration.roles_permissions.edit': 'Modificar roles',
      'configuration.roles_permissions.delete': 'Eliminar roles',
      'configuration.pos.view': 'Ver configuraciones del punto de venta',
      'configuration.pos.edit': 'Modificar configuraciones del punto de venta',
      'configuration.taxes.view': 'Ver todas las configuraciones de impuestos',
      'configuration.taxes.create': 'Crear nuevas reglas de impuestos',
      'configuration.taxes.edit': 'Modificar reglas de impuestos',
      'configuration.taxes.delete': 'Eliminar reglas de impuestos',
      'configuration.ncf.view': 'Ver secuencias de numeración NCF',
      'configuration.ncf.create': 'Crear nuevas secuencias NCF',
      'configuration.ncf.edit': 'Modificar secuencias NCF',
      'configuration.ncf.delete': 'Eliminar secuencias NCF',
      // Approvals
      'approvals.view': 'Ver solicitudes de aprobación pendientes',
      'approvals.approve': 'Aprobar o rechazar solicitudes de aprobación',
      'approvals.self_approve':
        'Permite al usuario consumir su propio PIN o tarjeta para aprobar sus propias solicitudes vía POST /approvals/manager-approve. Escape para restaurantes u operaciones de un solo usuario.',
      // Column Visibility
      'column.VIEW_COLUMN_GROUP_PRODUCT_BASIC_INFO':
        'Ver código, nombre, descripción y otros datos básicos del producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_PRICING':
        'Ver precio de costo, último costo de compra y el historial de precios',
      'column.VIEW_COLUMN_GROUP_PRODUCT_SELLING_PRICE':
        'Ver precio de venta y niveles de precio',
      'column.EDIT_COLUMN_GROUP_PRODUCT_PRICING':
        'Modificar el precio de costo y el último costo de compra',
      'column.EDIT_COLUMN_GROUP_PRODUCT_SELLING_PRICE':
        'Modificar el precio de venta y los niveles de precio',
      'column.VIEW_COLUMN_GROUP_PRODUCT_INVENTORY':
        'Ver stock disponible y niveles de inventario',
      'column.EDIT_COLUMN_GROUP_PRODUCT_INVENTORY':
        'Modificar mínimo de stock, máximo de stock y punto de reorden',
      'column.VIEW_COLUMN_GROUP_PRODUCT_PROVIDERS':
        'Ver proveedores asignados al producto',
      'column.EDIT_COLUMN_GROUP_PRODUCT_PROVIDERS':
        'Cambiar el proveedor asignado a un producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_AUDIT_TRAIL':
        'Ver historial de cambios del producto',
      'column.VIEW_COLUMN_GROUP_PRODUCT_IMAGES': 'Ver imágenes del producto',
      'column.VIEW_COLUMN_GROUP_INVOICE_BASIC_INFO':
        'Ver número, fecha, cliente en facturas',
      'column.VIEW_COLUMN_GROUP_INVOICE_ITEMS':
        'Ver lista de productos en factura',
      'column.VIEW_COLUMN_GROUP_INVOICE_PRICING':
        'Ver precios y descuentos en factura',
      'column.VIEW_COLUMN_GROUP_INVOICE_PAYMENT':
        'Ver información de pago de factura',
      'column.VIEW_COLUMN_GROUP_INVOICE_AUDIT_TRAIL':
        'Ver historial de cambios de factura',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_BASIC_INFO':
        'Ver nombre, contacto, identificación del cliente',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_ADDRESS':
        'Ver dirección y ubicación del cliente',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_FINANCIAL':
        'Ver límite de crédito y deuda del cliente',
      'column.VIEW_COLUMN_GROUP_CUSTOMER_AUDIT_TRAIL':
        'Ver historial de cambios del cliente',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_BASIC_INFO':
        'Ver número, fecha, proveedor en orden de compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_ITEMS':
        'Ver productos en orden de compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_PRICING':
        'Ver precios en orden de compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_STATUS':
        'Ver estado de orden de compra',
      'column.VIEW_COLUMN_GROUP_PURCHASE_ORDER_AUDIT_TRAIL':
        'Ver historial de cambios de orden de compra',
      'column.VIEW_COLUMN_GROUP_STOCK_MOVEMENT_BASIC_INFO':
        'Ver tipo y detalles básicos del movimiento',
      'column.VIEW_COLUMN_GROUP_STOCK_MOVEMENT_LOCATION':
        'Ver almacén origen y destino',
      'column.VIEW_COLUMN_GROUP_STOCK_MOVEMENT_AUDIT':
        'Ver información de auditoría del movimiento',
      'column.VIEW_COLUMN_GROUP_INVENTORY_BASIC_INFO':
        'Ver código y nombre del producto en inventario',
      'column.VIEW_COLUMN_GROUP_INVENTORY_QUANTITIES':
        'Ver cantidad disponible y cantidades por almacén',
      'column.VIEW_COLUMN_GROUP_INVENTORY_AUDIT_TRAIL':
        'Ver historial de cambios de inventario',
      'column.VIEW_COLUMN_GROUP_INTERNAL_NOTES':
        'Ver notas internas y comentarios privados',
      // Restaurant
      'restaurant.access':
        'Acceder al módulo de restaurante (plano y servicio en mesas)',
      'restaurant.floor_plan.view':
        'Ver salones, mesas y decoraciones en el plano',
      'restaurant.floor_plan.edit':
        'Agregar, mover, redimensionar y eliminar mesas y decoraciones',
      'restaurant.environment.create': 'Crear salones / zonas del restaurante',
      'restaurant.environment.delete': 'Eliminar salones del restaurante',
      'restaurant.tables.view': 'Ver mesas y su estado actual en modo servicio',
      'restaurant.tables.open': 'Abrir mesa y crear cuenta',
      'restaurant.tables.close': 'Cerrar cuenta de mesa',
      'restaurant.tab.view': 'Ver cuentas abiertas y sus ítems',
      'restaurant.tab.create': 'Agregar nueva cuenta a una mesa',
      'restaurant.tab.edit': 'Renombrar o modificar cuentas abiertas',
      'restaurant.tab.delete': 'Eliminar cuenta de una mesa',
      'restaurant.tab.tax.remove':
        'Quitar una línea de impuesto de la cuenta antes de cerrarla',
      'restaurant.tab_item.add': 'Agregar ítems (con notas) a una cuenta',
      'restaurant.tab_item.void': 'Anular ítems en una cuenta',
      'restaurant.tab_item.transfer':
        'Mover, duplicar o dividir un ítem entre cuentas abiertas de la misma sucursal',
      'restaurant.commanda.reprint':
        'Reimprimir una comanda después de la primera impresión automática (p. ej. papel perdido, atasco de impresora)',
      // Configuration - Branches
      'configuration.branches.view': 'Ver configuración de sucursales',
      'configuration.branches.create': 'Crear nuevas sucursales',
      'configuration.branches.edit': 'Modificar sucursales existentes',
      // Configuration - Delivery
      'configuration.delivery.view': 'Ver métodos de entrega y conductores',
      'configuration.delivery.create': 'Crear métodos de entrega y conductores',
      'configuration.delivery.edit':
        'Modificar métodos de entrega y conductores',
      'configuration.delivery.delete':
        'Eliminar métodos de entrega y conductores',
      // Configuration - Promotions
      'configuration.promotions.view': 'Ver promociones y descuentos',
      'configuration.promotions.create':
        'Agregar nuevas promociones y descuentos',
      'configuration.promotions.edit': 'Modificar promociones existentes',
      'configuration.promotions.delete': 'Eliminar promociones',
      'configuration.promotions.activate': 'Activar o desactivar promociones',
      // Configuration - eCF
      'configuration.ecf_certificates.view':
        'Ver y administrar certificados DGII (P12/PFX)',
      'configuration.ecf_certificates.manage':
        'Subir, eliminar y administrar certificados DGII',
      'configuration.ecf_batch.view':
        'Ver tablero de procesamiento masivo de e-CF',
      'configuration.ecf_batch.process':
        'Ejecutar procesamiento masivo de e-CF pendientes y reintentar fallidos',
      // Customers
      'customers.adjust_credit':
        'Ajustes manuales del saldo de crédito del cliente',
      'customers.view_credit_history':
        'Ver historial de transacciones de crédito',
      'customers.apply_credit': 'Aplicar saldo de crédito a facturas',
      // Follow-up reminders
      'follow_up_reminders.view':
        'Ver recordatorios de seguimiento post-venta y manuales',
      'follow_up_reminders.create':
        'Programar manualmente un recordatorio de seguimiento',
      'follow_up_reminders.mark':
        'Marcar recordatorios como contactados o descartarlos',
      // Invoices - extras
      'invoices.bulk_import': 'Importar facturas desde CSV para migración',
      'invoices.create_quote': 'Crear y gestionar cotizaciones',
      'invoices.export_quotes':
        'Descargar el listado de cotizaciones en PDF / Excel / CSV / TXT.',
      'invoices.create_credit_note':
        'Crear notas de crédito parciales o totales',
      'invoices.approve_credit_note':
        'Aprobar notas de crédito pendientes fuera de ventana o sobre umbral',
      'invoices.tax.remove':
        'Quitar línea de impuesto antes de guardar o cerrar la factura',
      'documents.backdate':
        'Crear documentos (factura, orden de compra, pago, activo fijo, gasto, …) con fecha anterior al día de negocio actual. Afecta turno, contabilidad y secuencia NCF; queda registrado para auditoría.',
      // Payments
      'payments.view': 'Ver lista de pagos',
      'payments.create': 'Recibir pagos de clientes',
      'payments.edit': 'Modificar pagos existentes',
      'payments.delete': 'Eliminar o anular pagos',
      'payments.export': 'Exportar lista de pagos',
      'payments.make_expense':
        'Registrar pagos de gastos a proveedores o generales',
      // POS
      'pos.tax.remove':
        'Quitar una línea de impuesto del carrito POS antes de cobrar',
      'pos.supervised_removal.bypass':
        'Quitar o cancelar artículos del carrito sin aprobación de un gerente cuando el POS supervisado está activo.',
      'sales.items.edit_price':
        'Sobrescribir manualmente el precio unitario en el carrito POS, líneas de factura y consumo de restaurante',
      'sales.items.price_below_min':
        'Guardar facturas con un precio unitario por debajo del mínimo aplicable (tarifa del cliente o precio de venta del producto). Cajeros sin este permiso ven una advertencia y no pueden confirmar.',
      'inventory.products.quick_create':
        'Crear un producto nuevo desde el buscador de la factura o de la orden de compra cuando no hay coincidencias',
      'invoices.items.free_create':
        'Agregar líneas a facturas y cotizaciones sin necesidad de un producto registrado',
      // Inventory - Debit Notes
      'inventory.debit_notes.create':
        'Crear notas de débito (B03) para cargos adicionales',
      'inventory.debit_notes.edit': 'Modificar notas de débito existentes',
      'inventory.debit_notes.delete': 'Eliminar notas de débito',
      // Inventory - PO extras
      'inventory.purchase_orders.print':
        'Imprimir órdenes de compra y notas de débito',
      // Inventory - Stock Counting
      'inventory.stock_counting.view':
        'Ver sesiones de conteo físico de inventario',
      'inventory.stock_counting.create':
        'Crear nuevas sesiones de conteo físico',
      'inventory.stock_counting.manage':
        'Agregar ítems, actualizar cantidades y completar conteos',
      // Shifts
      'shifts.cash_vouchers':
        'Crear y eliminar vales de entrada/salida durante los turnos',
      'shifts.reconciliation':
        'Ver detalle de cuadre y transacciones por turno',
      'shifts.daily_summary':
        'Ver resúmenes diarios consolidados de la sucursal',
      // Audit trail
      'documents.history.view':
        'Acceder a la pestaña Historial en facturas, cotizaciones, órdenes de compra, pagos, órdenes de procurement y órdenes de venta — muestra cada cambio con usuario, fecha/hora y detalle por campo. Endpoint: GET /document-history.',
      // Reseller portal
      'reseller.dashboard.view':
        'Accede al panel del reseller (mosaicos resumen: businesses asignados, comisión MTD por moneda, último payout).',
      'reseller.businesses.view':
        'Lista de businesses asignados al reseller con estado enriquecido (trial, próximo pago, vencido) y detalle individual.',
      'reseller.businesses.create':
        'Provisiona un nuevo business bajo el reseller (conjunto mínimo: solo planes públicos, trial por defecto del plan, sin selección de addons, con rate-limit).',
      'reseller.businesses.impersonate':
        'Emite un JWT de impersonación con capacidades restringidas y entra a un business asignado a este reseller. Cada sesión queda auditada en reseller_impersonation_session.',
      'reseller.commissions.view':
        'Accede al ledger de comisiones del reseller + balance corriente por moneda.',
      'reseller.payouts.view':
        'Accede al historial de payouts del reseller (draft / approved / paid / cancelled) agrupado por moneda.',
      'reseller.contracts.accept':
        'Firma la versión actual del reseller_contract_version. Requerido antes de que cualquier otro endpoint /reseller/* responda.',
      // Rentals (Alquileres)
      'rentals.view':
        'Acceso al grupo de menú Alquileres (Pedidos, Reservaciones, Calendarios, Recogido).',
      'rentals.pedido.view':
        'Ver lista y detalle de Pedidos de alquiler (documento operativo RENT-{sucursal}-{secuencia}).',
      'rentals.pedido.create':
        'Crear un Pedido de alquiler con ítems, fianza y período.',
      'rentals.pedido.edit':
        'Editar encabezado e ítems del Pedido antes de producción.',
      'rentals.pedido.cancel':
        'Cancelar un Pedido en estado borrador / reservado / confirmado. Bloqueado después de producción.',
      'rentals.pedido.confirm':
        'Transición borrador → confirmado. Requiere fianza pagada o permiso de anulación de fianza.',
      'rentals.pedido.send_to_production':
        'Transición confirmado → en producción. Emite ticket KDS y movimiento de reserva de stock.',
      'rentals.pedido.mark_ready':
        'Transición en producción → listo. Dispara el correo de recogido y el token QR.',
      'rentals.pedido.mark_delivered':
        'Transición listo → entregado al cliente. Requiere paymentStatus=paid.',
      'rentals.pedido.process_return':
        'Capturar inspección de devolución por ítem y decidir reembolso o confiscación de fianza.',
      'rentals.pedido.finalize':
        'Emitir exactamente una factura fiscal a partir de un alquiler completado. Idempotente.',
      'rentals.damage_charge.adjust':
        'Reducir o anular (poner en 0) el cargo pendiente por daños o artículos no devueltos de un Pedido.',
      'rentals.fianza.override':
        'Confirmar un Pedido sin cobrar el monto de fianza resuelto.',
      'rentals.items.zero_price':
        'Agregar ítems al Pedido con precio unitario = 0 (cortesía o combo).',
      'rentals.late_return.refund_anyway':
        'Anular la regla de confiscación por retraso y reembolsar la fianza después del plazo.',
      'rentals.reservations.view':
        'Ver lista y detalle de reservaciones de alquiler (RES-{sucursal}-{secuencia}).',
      'rentals.reservations.create':
        'Crear una reservación de alquiler con ítems y ventana de expiración.',
      'rentals.reservations.edit':
        'Editar encabezado e ítems de la reservación antes de la conversión.',
      'rentals.reservations.confirm':
        'Transición reservación borrador → confirmada (ocupa la ventana de stock).',
      'rentals.reservations.cancel':
        'Cancelar una reservación y liberar su ventana de stock.',
      'rentals.reservations.convert':
        'Convertir una reservación confirmada en un nuevo Pedido de alquiler (conversión única).',
      'rentals.calendars.view':
        'Ver calendario agregado de Pedidos y reservaciones (día / semana / mes / año).',
      'rentals.pickup.view':
        'Ver Pedidos listos para recogido y superficie de entrega vinculada por QR.',
      'rentals.pickup.collect':
        'Escanear QR (o anular manualmente) para mover un Pedido a entregado al cliente.',
      'rentals.pickup.resend_email':
        'Regenerar el token de recogido y reenviar el correo Listo-para-recoger.',
    },
    COLUMN_GROUPS: {
      // Products
      PRODUCT_BASIC_INFO: 'Información Básica',
      PRODUCT_PRICING: 'Precios',
      PRODUCT_INVENTORY: 'Inventario',
      PRODUCT_PROVIDERS: 'Proveedores',
      PRODUCT_AUDIT_TRAIL: 'Historial de Auditoría',
      PRODUCT_IMAGES: 'Imágenes',
      // Invoices
      INVOICE_BASIC_INFO: 'Información Básica',
      INVOICE_ITEMS: 'Elementos/Artículos',
      INVOICE_PRICING: 'Precios y Descuentos',
      INVOICE_PAYMENT: 'Información de Pago',
      INVOICE_AUDIT_TRAIL: 'Historial de Auditoría',
      // Customers
      CUSTOMER_BASIC_INFO: 'Información Básica',
      CUSTOMER_ADDRESS: 'Dirección',
      CUSTOMER_FINANCIAL: 'Información Financiera',
      CUSTOMER_AUDIT_TRAIL: 'Historial de Auditoría',
      // Purchase Orders
      PURCHASE_ORDER_BASIC_INFO: 'Información Básica',
      PURCHASE_ORDER_ITEMS: 'Elementos/Artículos',
      PURCHASE_ORDER_PRICING: 'Precios y Descuentos',
      PURCHASE_ORDER_STATUS: 'Estado y Referencia',
      PURCHASE_ORDER_AUDIT_TRAIL: 'Historial de Auditoría',
      // Stock Movements
      STOCK_MOVEMENT_BASIC_INFO: 'Información Básica',
      STOCK_MOVEMENT_LOCATION: 'Ubicación',
      STOCK_MOVEMENT_AUDIT: 'Auditoría',
      // Inventory
      INVENTORY_BASIC_INFO: 'Información Básica',
      INVENTORY_QUANTITIES: 'Cantidades',
      INVENTORY_AUDIT_TRAIL: 'Historial de Auditoría',
      // Internal/Notes
      INTERNAL_NOTES: 'Notas Internas',
    },
  },
  SIDEBAR: {
    ACCOUNT: 'Cuenta',
    PROFILE: 'Perfil',
    LOGOUT: 'Cerrar Sesión',
    PAYMENTS_METHODS: 'Métodos de Pago',
    FREE: 'Plan Personalizado',
    STARTUP: 'Plan Personalizado',
    PROFESIONAL: 'Plan Personalizado',
    CUSTOM_PLAN: 'Plan Personalizado',
  },
  LOGIN: {
    TITLE: 'Iniciar sesión',
    DESCRIPTION: 'Accede a tu cuenta para continuar',
    EMAIL_PLACEHOLDER: 'Email o nombre de usuario',
    EMAIL_INPUT_PLACEHOLDER: 'tucorreo@dominio.com o usuario',
    PASSWORD_PLACEHOLDER: 'Contraseña',
    FORGOT_PASSWORD: '¿Olvidaste tu contraseña?',
    LOGIN_BUTTON: 'Iniciar sesión',
    LOADING: 'Cargando...',
    ERROR_TITLE: 'Error de inicio de sesión',
    CARD_INVALID: 'Tarjeta no válida o no asignada a un usuario activo.',
    OR_CONTINUE_WITH: 'O continuar con',
    SIGN_IN_WITH_GOOGLE: 'Iniciar sesión con Google',
    GOOGLE_AUTH_FAILED:
      'Error al iniciar sesión con Google. Por favor intente de nuevo.',
    NO_ACCOUNT: '¿No tienes una cuenta?',
    REGISTER: 'Regístrate',
    SELECT_BUSINESS_TITLE: 'Selecciona tu empresa',
    SELECT_BUSINESS_DESCRIPTION:
      'Tu correo y contraseña están asociados a más de una empresa. Elige a cuál deseas entrar.',
    SELECT_BUSINESS_NO_DETAIL: 'Sin detalles disponibles',
    CAPTCHA_PROMPT:
      'Detectamos varios intentos fallidos. Por favor verifica que eres humano para continuar.',
  },
  FINANCIAL_EVENTS: {
    MODULE: {
      invoice: 'Factura',
      payment: 'Pago',
      purchase_order: 'Orden de compra',
      stock_movement: 'Movimiento de inventario',
      credit_note: 'Nota de crédito',
      refund: 'Reembolso',
      cash_voucher: 'Comprobante de caja',
      expense: 'Gasto',
    },
    SUBTYPE: {
      'invoice.issued': 'Factura emitida',
      'invoice.issued.cash': 'Factura emitida — contado',
      'invoice.issued.credit': 'Factura emitida — crédito',
      'invoice.catchup': 'Factura (reconciliación)',
      'payment.received': 'Pago recibido',
      'payment.disbursed': 'Pago emitido',
      'payment.catchup': 'Pago (reconciliación)',
      'purchase_order.received': 'OC recibida',
      'purchase_order.debit_note': 'Nota de débito (OC)',
      'purchase_order.catchup': 'OC (reconciliación)',
      'stock_movement.adjustment': 'Ajuste de inventario',
      'stock_movement.damaged': 'Inventario dañado',
      'stock_movement.lost': 'Inventario perdido',
      'stock_movement.transfer': 'Transferencia de inventario',
      'stock_movement.initial': 'Inventario inicial',
      'credit_note.issued': 'Nota de crédito emitida',
      'credit_note.pending': 'Nota de crédito pendiente',
      'credit_note.approved': 'Nota de crédito aprobada',
      'credit_note.catchup': 'Nota de crédito (reconciliación)',
      'refund.pending': 'Reembolso pendiente',
      'refund.completed': 'Reembolso completado',
      'refund.catchup': 'Reembolso (reconciliación)',
      'cash_voucher.ingreso': 'Ingreso de caja',
      'cash_voucher.egreso': 'Egreso de caja',
      'cash_voucher.catchup': 'Comprobante de caja (reconciliación)',
      'expense.recorded': 'Gasto registrado',
    },
  },
  FINANCE: {
    TITLE: 'Finanzas',
    COMING_SOON:
      'El módulo de Finanzas estará disponible próximamente. Gastos, Cuentas por Cobrar y Cuentas por Pagar llegan en la siguiente entrega.',
    GASTOS: 'Gastos',
    AR: 'Cuentas por Cobrar',
    AP: 'Cuentas por Pagar',
    GASTOS_SUBTITLE:
      'Registra gastos operativos, asígnalos a centros de costo y emítelos como eventos financieros para el motor contable.',
    NEW_GASTO: 'Nuevo gasto',
    EDIT_GASTO: 'Editar gasto',
    DELETE_CONFIRM: '¿Eliminar este gasto?',
    SEARCH_PLACEHOLDER: 'Buscar por descripción o referencia',
    FILTER_ALL_STATUSES: 'Todos los estados',
    COLUMN_DATE: 'Fecha',
    COLUMN_DESCRIPTION: 'Descripción',
    COLUMN_PROVIDER: 'Proveedor',
    COLUMN_AMOUNT: 'Monto',
    COLUMN_STATUS: 'Estado',
    COLUMN_ACTIONS: 'Acciones',
    COLUMN_CURRENCY_LABEL: 'Moneda',
    COLUMN_REFERENCE: 'Referencia',
    FIELD_DATE: 'Fecha del gasto',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_AMOUNT: 'Monto',
    FIELD_REFERENCE: 'Referencia',
    FIELD_PAYMENT_METHOD: 'Método de pago',
    FIELD_STATUS: 'Estado',
    FIELD_PROVIDER: 'Proveedor',
    FIELD_ITBIS: 'ITBIS',
    CREDIT_HINT:
      'Un gasto a crédito queda como cuenta por pagar al proveedor; regístrale el pago desde Gastos o Cuentas por Pagar.',
    ITBIS_ELIGIBILITY_HINT:
      'Si el negocio no puede acreditar el ITBIS, inclúyelo en el monto y deja este campo en 0.',
    STATUS_DRAFT: 'Borrador',
    STATUS_PENDING_APPROVAL: 'Pendiente de aprobación',
    STATUS_APPROVED: 'Aprobado',
    STATUS_POSTED: 'Contabilizado',
    STATUS_REJECTED: 'Rechazado',
    STATUS_CANCELLED: 'Anulado',
    PAYMENT_CASH: 'Efectivo',
    PAYMENT_CARD: 'Tarjeta',
    PAYMENT_TRANSFER: 'Transferencia',
    PAYMENT_CHEQUE: 'Cheque',
    PAYMENT_CREDIT: 'Crédito',
    ACTION_EDIT: 'Editar',
    ACTION_VOID: 'Anular',
    ACTION_APPROVE: 'Aprobar',
    TOAST_CREATED: 'Gasto creado',
    TOAST_UPDATED: 'Gasto actualizado',
    TOAST_VOIDED: 'Gasto anulado',
    VOID_TITLE: 'Anular gasto',
    VOID_DESCRIPTION:
      'El gasto queda registrado como Anulado con el motivo, y se revierte su asiento (y su cuenta por pagar si fue a crédito). Si tiene pagos, anúlalos primero.',
    VOID_REASON_LABEL: 'Motivo de la anulación',
    VOID_REASON_PLACEHOLDER: 'Ej. gasto duplicado',
    VOID_CONFIRM: 'Anular gasto',
    VOIDED_REASON: 'Motivo de anulación',
    VOIDED_AT: 'Anulado el',
    TOAST_APPROVED: 'Gasto aprobado',
    TOAST_ERROR: 'No se pudo completar la acción',
    EMPTY_STATE: 'Aún no hay gastos registrados.',
    LINK_PURCHASE_ORDER: 'Pagar una Factura Proveedor (opcional)',
    // Picking an invoice writes BOTH rows on purpose
    // (gastos.page.tsx:149-204): a payment with
    // `direction=expense + purchaseOrderId`, which flips the AP
    // movement to paid, AND a mirrored expense row so /expenses shows
    // what the operator just registered. An earlier draft of this copy
    // claimed "no un gasto nuevo" — that was simply false.
    LINK_PURCHASE_ORDER_HELP:
      'Al seleccionar una factura, esto registra un PAGO contra ella (salda su balance) y además lo lista en Gastos.',
    LINK_PURCHASE_ORDER_PLACEHOLDER:
      'Selecciona una factura con saldo pendiente',
    PO_OPEN_BALANCE: 'Saldo pendiente',
    CREATE_CONFIRM_TITLE: '¿Registrar este gasto?',
    CREATE_CONFIRM_DESC:
      'Una vez registrado, el gasto se considera un hecho contable y no podrás editarlo ni eliminarlo. Verifica monto, proveedor y descripción antes de confirmar.',
    POSTED_NOT_DELETABLE:
      'No se puede eliminar un gasto ya contabilizado. Revierte el asiento primero.',
    AR_SUBTITLE:
      'Balance abierto por cliente, agrupado en cubos de antigüedad (0-30, 31-60, 61-90, 91+ días).',
    AR_AS_OF: 'Al',
    AR_COL_CUSTOMER: 'Cliente',
    AR_COL_CURRENT: 'Al día',
    AR_BUCKET_0_30: '0-30 días',
    AR_BUCKET_31_60: '31-60 días',
    AR_BUCKET_61_90: '61-90 días',
    AR_BUCKET_91_PLUS: '91+ días',
    AR_COL_TOTAL: 'Total',
    AR_ROW_TOTALS: 'Totales',
    AR_PROJECT_NOW: 'Reproyectar ahora',
    AR_PROJECT_TOAST: 'Proyector AR ejecutado: {{n}} movimientos creados',
    AR_EMPTY: 'No hay cuentas por cobrar abiertas.',
    AR_DRILL_OPEN: 'Ver movimientos',
    AR_MOVEMENTS_TITLE: 'Movimientos',
    AR_MOV_DATE: 'Fecha',
    AR_MOV_DOC: 'Documento',
    AR_MOV_DUE: 'Vence',
    AR_MOV_DEBIT: 'Débito',
    AR_MOV_CREDIT: 'Crédito',
    AR_MOV_STATUS: 'Estado',
    AP_SUBTITLE:
      'Balance abierto por proveedor, agrupado en cubos de antigüedad (0-30, 31-60, 61-90, 91+ días).',
    AP_AS_OF: 'Al',
    AP_COL_PROVIDER: 'Proveedor',
    AP_PROJECT_NOW: 'Reproyectar ahora',
    AP_PROJECT_TOAST: 'Proyector AP ejecutado: {{n}} movimientos creados',
    AP_EMPTY: 'No hay cuentas por pagar abiertas.',
    AP_DRILL_OPEN: 'Ver movimientos',
    AP_MOVEMENTS_TITLE: 'Movimientos',
    BUCKET_ALL: 'Todos',
    SEARCH_CUSTOMER: 'Buscar cliente',
    SEARCH_PROVIDER: 'Buscar proveedor',
    DATE_FROM: 'Desde',
    DATE_TO: 'Hasta',
    TOTAL_SHOWN: 'Total mostrado',
    EXPORT_CSV: 'Exportar',
    EXPORT_TOAST: 'Exportación lista',
    EXPORT_FILENAME_AR: 'cuentas-por-cobrar.csv',
    EXPORT_FILENAME_AP: 'cuentas-por-pagar.csv',
    RELATED_PAYMENTS_BTN: 'Ver pagos',
    RELATED_PAYMENTS_TITLE: 'Pagos relacionados — {{ref}}',
    RELATED_PAYMENTS_EMPTY: 'No hay pagos registrados para este documento.',
    COL_OPEN_BALANCE: 'Saldo abierto',
    NO_CUSTOMER_FOUND: 'Cliente no encontrado',
    NO_PROVIDER_FOUND: 'Proveedor no encontrado',
    ADD_PAYMENT_BTN: 'Agregar pago',
    ADD_EXPENSE_BTN: 'Registrar egreso',
    MOV_STATUS_OPEN: 'Abierto',
    MOV_STATUS_PARTIAL: 'Parcial',
    MOV_STATUS_PAID: 'Pagado',
    MOV_STATUS_CLOSED: 'Cerrado',
    MOV_STATUS_CANCELLED: 'Cancelado',
    MOV_STATUS_REVERSED: 'Reversado',
  },
  FINANCE_CALENDAR: {
    TITLE: 'Calendario financiero',
    SUBTITLE:
      'Vencimientos por cobrar y por pagar — vista de calendario en vivo sobre el libro mayor auxiliar.',
    LOADING: 'Cargando calendario…',
    VIEW_MONTH: 'Mes',
    VIEW_WEEK: 'Semana',
    VIEW_DAY: 'Día',
    VIEW_LIST: 'Lista',
    VIEW_YEAR: 'Año',
    EXPORT_TITLE: 'Calendario financiero',
    DIRECTION_ALL: 'Todos',
    DIRECTION_RECEIVABLE: 'Por cobrar',
    DIRECTION_PAYABLE: 'Por pagar',
    TOTAL_RECEIVABLE: 'Por cobrar',
    TOTAL_PAYABLE: 'Por pagar',
    TOTAL_NET: 'Neto',
    INCLUDE_PAID: 'Incluir pagados',
    TODAY: 'Hoy',
    PREVIOUS: 'Anterior',
    NEXT: 'Siguiente',
    EMPTY_DAY: 'Sin vencimientos.',
    EMPTY_RANGE: 'No hay vencimientos en este período.',
    STATUS_PENDING: 'Pendiente',
    STATUS_PARTIAL: 'Parcial',
    STATUS_PAID: 'Pagado',
    STATUS_OVERDUE: 'Vencido',
    DUE_TODAY: 'Dias de vencimiento',
    DUE_IN_DAYS: 'Vence en {{days}} días',
    OVERDUE_BY_DAYS: 'Vencido hace {{days}} días',
    DRAWER_TITLE: 'Detalle del vencimiento',
    DRAWER_DOCUMENT: 'Documento',
    DRAWER_COUNTERPARTY: 'Cliente / Proveedor',
    DRAWER_DUE_DATE: 'Fecha de vencimiento',
    DRAWER_AMOUNT_OWED: 'Saldo pendiente',
    DRAWER_AMOUNT_ORIGINAL: 'Monto original',
    DRAWER_ACTION_PAY_AR: 'Registrar cobro',
    DRAWER_ACTION_PAY_AP: 'Registrar pago',
    DRAWER_ACTION_OPEN_DOCUMENT: 'Abrir documento',
    UPCOMING_TITLE: 'Próximos vencimientos',
    UPCOMING_EMPTY: 'Sin vencimientos próximos.',
    UPCOMING_VIEW_ALL: 'Ver calendario',
    MIGRATION_HINT:
      'Recordatorios ahora es un calendario en vivo — mismos datos, sin espera del cron.',
  },
  ACCOUNTING: {
    FA_TITLE: 'Activos Fijos',
    FA_SUBTITLE:
      'Registro de activos fijos + depreciaciones. Cada alta, transferencia, baja o revaluación emite un evento financiero que se postea automáticamente al libro mayor.',
    FA_NEW: 'Nuevo activo',
    FA_CREATE_TITLE: 'Registrar activo fijo',
    FA_EDIT_TITLE: 'Editar activo fijo',
    FA_COL_CODE: 'Código',
    FA_COL_NAME: 'Nombre',
    FA_COL_CATEGORY: 'Categoría',
    FA_COL_ACQUIRED: 'Adquirido',
    FA_COL_COST: 'Costo',
    FA_COL_NET: 'Valor neto',
    FA_COL_STATUS: 'Estado',
    FA_STATUS_IN_SERVICE: 'En servicio',
    FA_STATUS_DISPOSED: 'Dado de baja',
    FA_STATUS_SOLD: 'Vendido',
    FA_STATUS_SCRAPPED: 'Descartado',
    FA_STATUS_LOST: 'Extraviado',
    FA_FIELD_CODE: 'Código',
    FA_FIELD_NAME: 'Nombre',
    FA_FIELD_DESCRIPTION: 'Descripción',
    FA_FIELD_CATEGORY: 'Categoría',
    FA_FIELD_COST_CENTER: 'Centro de costo',
    FA_FIELD_ACQUISITION_DATE: 'Fecha de adquisición',
    FA_FIELD_ACQUISITION_COST: 'Costo de adquisición',
    FA_FIELD_SALVAGE: 'Valor residual',
    FA_FIELD_LIFE: 'Vida útil (meses)',
    FA_FIELD_METHOD: 'Método de depreciación',
    FA_FIELD_LOCATION: 'Ubicación',
    FA_FIELD_SERIAL: 'Número de serie',
    FA_METHOD_STRAIGHT: 'Línea recta',
    FA_METHOD_DECLINING: 'Saldos decrecientes',
    FA_TOAST_CREATED: 'Activo registrado.',
    FA_TOAST_UPDATED: 'Activo actualizado.',
    FA_TOAST_TRANSFERRED: 'Transferencia registrada.',
    FA_TOAST_DISPOSED: 'Baja registrada.',
    FA_TOAST_REVALUED: 'Revaluación registrada.',
    FA_TOAST_DEPRECIATED:
      'Depreciación ejecutada: {{recorded}} registradas, {{skipped}} omitidas.',
    FA_ACTION_TRANSFER: 'Transferir',
    FA_ACTION_DISPOSE: 'Dar de baja',
    FA_ACTION_REVALUE: 'Revaluar',
    FA_ACTION_DEPRECIATE: 'Ejecutar depreciación',
    FA_VIEW_DETAIL: 'Ver detalle',
    FA_DETAIL_TITLE: 'Detalle del activo',
    FA_DETAIL_REGISTER_COUNT: 'Activos en servicio',
    FA_DETAIL_REGISTER_ACQUISITION: 'Costo total',
    FA_DETAIL_REGISTER_ACCUMULATED: 'Depreciación acumulada',
    FA_DETAIL_REGISTER_NET: 'Valor neto',
    FA_MOVEMENTS_TITLE: 'Movimientos',
    FA_MOVEMENTS_EMPTY: 'Sin movimientos registrados.',
    FA_MOVE_TYPE_TRANSFER: 'Transferencia',
    FA_MOVE_TYPE_DISPOSAL: 'Baja',
    FA_MOVE_TYPE_REVALUATION: 'Revaluación',
    FA_MOVE_TYPE_IMPAIRMENT: 'Deterioro',
    FA_DEPRECIATIONS_TITLE: 'Depreciaciones',
    FA_DEPRECIATIONS_EMPTY: 'Sin depreciaciones aplicadas.',
    FA_DEP_COL_DATE: 'Fecha',
    FA_DEP_COL_AMOUNT: 'Monto',
    FA_DEP_COL_NET_AFTER: 'Neto post',
    FA_TRANSFER_TITLE: 'Transferir activo',
    FA_TRANSFER_TO_BRANCH: 'Sucursal destino',
    FA_TRANSFER_TO_CC: 'Centro de costo destino',
    FA_TRANSFER_REASON: 'Motivo',
    FA_TRANSFER_DATE: 'Fecha',
    FA_DISPOSE_TITLE: 'Dar de baja',
    FA_DISPOSE_FINAL_STATUS: 'Estado final',
    FA_DISPOSE_DATE: 'Fecha',
    FA_DISPOSE_SALE_AMOUNT: 'Monto de venta (solo si Vendido)',
    FA_DISPOSE_REASON: 'Motivo',
    FA_REVALUE_TITLE: 'Revaluar activo',
    FA_REVALUE_DATE: 'Fecha',
    FA_REVALUE_CHANGE: 'Cambio de valor (firmado)',
    FA_REVALUE_HINT:
      'Positivo = revaluación (aumento de valor). Negativo = deterioro / discapacidad.',
    FA_REVALUE_REASON: 'Motivo',
    FA_DEPRECIATE_TITLE: 'Ejecutar depreciación',
    FA_DEPRECIATE_AS_OF: 'Fecha de referencia',
    FA_DEPRECIATE_RUN_ALL: 'Ejecutar para todos los activos',
    FA_DEPRECIATE_NO_PERIOD:
      'No hay período contable abierto para la fecha indicada.',
    FA_DEPRECIATE_HELP_TITLE: '¿Cómo se calcula?',
    FA_DEPRECIATE_HELP_SL:
      'Línea recta: (costo − valor residual) ÷ vida útil en meses. Ej.: activo 60,000 a 60 meses → 1,000/mes. Aplica el mismo monto cada período.',
    FA_DEPRECIATE_HELP_DB:
      'Saldos decrecientes (doble): (2 ÷ vida útil) × valor neto actual. Ej.: 24,000 a 24 meses → mes 1 = 2,000, mes 2 = (2÷24) × 22,000 ≈ 1,833. Decrece cada mes.',
    FA_DEPRECIATE_HELP_IDEM:
      'Idempotente: una sola fila por activo por período. Re-ejecutar el mismo período no duplica — los activos ya depreciados quedan como "omitidos".',
    FA_DEPRECIATE_HELP_SALVAGE:
      'Tope final: el último mes se ajusta para que el valor neto caiga exactamente en el valor residual; nunca sobre-deprecia.',
    FA_DEPRECIATE_OUTCOME_RECORDED: 'Registradas: {{n}}',
    FA_DEPRECIATE_OUTCOME_SKIPPED: 'Omitidas (ya depreciadas): {{n}}',
    FA_DEPRECIATE_OUTCOME_FULL: 'Completamente depreciadas: {{n}}',
    FA_FILTER_ALL_STATUSES: 'Todos los estados',
    FA_FILTER_ALL_CATEGORIES: 'Todas las categorías',
    FA_EMPTY: 'No hay activos fijos registrados.',
    FA_NEEDS_ACCOUNTING_HINT:
      'Activá Contabilidad en el plan del negocio para que las depreciaciones y bajas se posteen al libro mayor automáticamente.',
    FA_HELP_CATEGORY:
      'Cada categoría define vida útil + método de depreciación por defecto, que el activo hereda al seleccionarla.',
    FA_HELP_CATEGORY_LINK: 'Administrar categorías →',
    FA_HELP_COST_CENTER:
      'El centro de costo enlaza el gasto del activo al departamento o proyecto correspondiente.',
    FA_HELP_COST_CENTER_LINK: 'Administrar centros de costo →',
    BUD_TITLE: 'Presupuestos',
    BUD_SUBTITLE:
      'Crea presupuestos por año fiscal, sucursal y centro de costo. Las alertas de sobreejecución se disparan automáticamente cuando se cruzan los umbrales 80%, 100% y 120%.',
    BUD_NEW: 'Nuevo presupuesto',
    BUD_CREATE_TITLE: 'Crear presupuesto',
    BUD_EDIT_TITLE: 'Editar presupuesto',
    BUD_FIELD_NAME: 'Nombre',
    BUD_FIELD_DESCRIPTION: 'Descripción',
    BUD_FIELD_FISCAL_YEAR: 'Año fiscal',
    BUD_FIELD_TYPE: 'Tipo',
    BUD_FIELD_CURRENCY: 'Moneda',
    BUD_TYPE_ANNUAL: 'Anual',
    BUD_TYPE_MONTHLY: 'Mensual',
    BUD_STATUS_DRAFT: 'Borrador',
    BUD_STATUS_APPROVED: 'Aprobado',
    BUD_STATUS_ARCHIVED: 'Archivado',
    BUD_COL_NAME: 'Nombre',
    BUD_COL_FISCAL_YEAR: 'Año fiscal',
    BUD_COL_TYPE: 'Tipo',
    BUD_COL_STATUS: 'Estado',
    BUD_COL_CURRENCY: 'Moneda',
    BUD_EMPTY: 'Sin presupuestos. Crea uno para empezar.',
    BUD_FILTER_ALL_STATUSES: 'Todos los estados',
    BUD_FILTER_ALL_YEARS: 'Todos los años',
    BUD_ACTION_APPROVE: 'Aprobar',
    BUD_ACTION_ARCHIVE: 'Archivar',
    BUD_ACTION_VIEW_DETAIL: 'Ver detalle',
    BUD_APPROVE_CONFIRM:
      '¿Aprobar este presupuesto? Una vez aprobado las líneas no pueden editarse.',
    BUD_ARCHIVE_CONFIRM:
      '¿Archivar este presupuesto? Esta acción es terminal y no puede revertirse.',
    BUD_DELETE_CONFIRM:
      '¿Eliminar este presupuesto? Solo los borradores pueden eliminarse.',
    BUD_TOAST_CREATED: 'Presupuesto creado.',
    BUD_TOAST_UPDATED: 'Presupuesto actualizado.',
    BUD_TOAST_APPROVED: 'Presupuesto aprobado.',
    BUD_TOAST_ARCHIVED: 'Presupuesto archivado.',
    BUD_TOAST_DELETED: 'Presupuesto eliminado.',
    BUD_DETAIL_TITLE: 'Detalle del presupuesto',
    BUD_DETAIL_BACK: 'Volver a presupuestos',
    BUD_LINES_TITLE: 'Líneas del presupuesto',
    BUD_LINES_EMPTY: 'Sin líneas. Agrega una o importa desde CSV.',
    BUD_LINE_NEW: 'Nueva línea',
    BUD_LINE_CREATE_TITLE: 'Agregar línea',
    BUD_LINE_EDIT_TITLE: 'Editar línea',
    BUD_LINE_FIELD_ACCOUNT: 'Cuenta contable',
    BUD_LINE_FIELD_PERIOD: 'Período',
    BUD_LINE_FIELD_BRANCH: 'Sucursal',
    BUD_LINE_FIELD_COST_CENTER: 'Centro de costo',
    BUD_LINE_FIELD_AMOUNT: 'Monto',
    BUD_LINE_FIELD_NOTES: 'Notas',
    BUD_LINE_COL_ACCOUNT: 'Cuenta',
    BUD_LINE_COL_PERIOD: 'Período',
    BUD_LINE_COL_BRANCH: 'Sucursal',
    BUD_LINE_COL_AMOUNT: 'Monto',
    BUD_LINE_TOAST_CREATED: 'Línea agregada.',
    BUD_LINE_TOAST_UPDATED: 'Línea actualizada.',
    BUD_LINE_TOAST_DELETED: 'Línea eliminada.',
    BUD_LINE_DELETE_CONFIRM: '¿Eliminar esta línea del presupuesto?',
    BUD_IMPORT_TITLE: 'Importar líneas desde CSV',
    BUD_IMPORT_SUBTITLE:
      'Carga un CSV con columnas: Código de cuenta, Código de período, Monto, Código de sucursal (opcional), Código de centro de costo (opcional), Moneda (opcional), Notas (opcional).',
    BUD_IMPORT_BUTTON: 'Importar CSV',
    BUD_IMPORT_TEMPLATE: 'Descargar plantilla',
    BUD_IMPORT_TEMPLATE_HINT:
      'Reemplaza los códigos con los reales de tu catálogo de cuentas + períodos.',
    BUD_IMPORT_PICK_FILE: 'Selecciona archivo .csv',
    BUD_IMPORT_PARSING: 'Procesando archivo…',
    BUD_IMPORT_PREVIEW: 'Filas detectadas: {{count}}',
    BUD_IMPORT_SUBMIT: 'Subir al servidor',
    BUD_IMPORT_SUBMITTING: 'Subiendo…',
    BUD_IMPORT_RESULT:
      'Total: {{total}} · Insertadas: {{inserted}} · Actualizadas: {{updated}} · Omitidas: {{skipped}} · Fallidas: {{failed}}',
    BUD_IMPORT_ERRORS: 'Errores por fila',
    BUD_IMPORT_TOAST_DONE: 'Importación completada.',
    BUD_IMPORT_COL_ACCOUNT: 'Código de cuenta',
    BUD_IMPORT_COL_PERIOD: 'Código de período',
    BUD_IMPORT_COL_AMOUNT: 'Monto',
    BUD_IMPORT_COL_BRANCH: 'Código de sucursal',
    BUD_IMPORT_COL_COST_CENTER: 'Código de centro de costo',
    BUD_IMPORT_COL_CURRENCY: 'Moneda',
    BUD_IMPORT_COL_NOTES: 'Notas',
    BUD_IMPORT_ERR_ROW: 'Fila {{row}}',
    BUD_IMPORT_ERR_FIELD_ACCOUNT: 'Código de cuenta',
    BUD_IMPORT_ERR_FIELD_PERIOD: 'Código de período',
    BUD_IMPORT_ERR_FIELD_BRANCH: 'Código de sucursal',
    BUD_IMPORT_ERR_FIELD_COST_CENTER: 'Código de centro de costo',
    BUD_IMPORT_ERR_FIELD_AMOUNT: 'Monto',
    BUD_IMPORT_ERR_ACCOUNT_NOT_FOUND:
      'La cuenta "{{code}}" no existe en el catálogo.',
    BUD_IMPORT_ERR_PERIOD_NOT_FOUND: 'El período "{{code}}" no existe.',
    BUD_IMPORT_ERR_BRANCH_NOT_FOUND: 'La sucursal "{{code}}" no existe.',
    BUD_IMPORT_ERR_COST_CENTER_NOT_FOUND:
      'El centro de costo "{{code}}" no existe.',
    BUD_IMPORT_ERR_AMOUNT_INVALID:
      'El monto "{{value}}" no es un número válido.',
    BUD_IMPORT_ERR_UNKNOWN: '{{message}}',
    BUD_IMPORT_ERR_FIELD_DUPLICATE: 'Duplicado',
    BUD_IMPORT_ERR_DUPLICATE_IN_CSV:
      'Duplicado de la fila {{row}} en este CSV (misma cuenta / período / sucursal / centro de costo).',
    BUD_IMPORT_ERR_DUPLICATE_RACE:
      'Otra importación insertó esta línea al mismo tiempo. Vuelve a intentarlo si necesitas actualizar el monto.',
    BUD_IMPORT_MODE_LABEL: '¿Qué hacer con líneas que ya existen?',
    BUD_IMPORT_MODE_SKIP: 'Omitir duplicados',
    BUD_IMPORT_MODE_UPDATE: 'Actualizar líneas existentes',
    BUD_IMPORT_MODE_REPLACE: 'Reemplazar todas las líneas',
    BUD_IMPORT_MODE_HINT_SKIP:
      'Si una línea ya existe (misma cuenta / período / sucursal / centro de costo), se conserva sin cambios.',
    BUD_IMPORT_MODE_HINT_UPDATE:
      'Si una línea ya existe, se reemplaza el monto, la moneda y las notas con los valores del CSV.',
    BUD_IMPORT_MODE_HINT_REPLACE:
      'Elimina TODAS las líneas activas del presupuesto antes de insertar las del CSV. Úsalo solo cuando el CSV sea la nueva fuente de verdad.',
    BUD_EXECUTION_TITLE: 'Ejecución del presupuesto',
    BUD_EXECUTION_REFRESH: 'Refrescar vista',
    BUD_EXECUTION_REFRESHING: 'Refrescando…',
    BUD_EXECUTION_TOAST_REFRESHED: 'Vista de ejecución actualizada.',
    BUD_EXEC_TILE_BUDGET: 'Presupuesto total',
    BUD_EXEC_TILE_ACTUAL: 'Ejecutado',
    BUD_EXEC_TILE_VARIANCE: 'Variación',
    BUD_EXEC_TILE_PCT: '% Ejecución',
    BUD_EXEC_TILE_OVERAGE: 'Líneas en sobregiro',
    BUD_EXEC_TILE_FULL: 'Líneas al 100%',
    BUD_EXEC_COL_BUDGET: 'Presupuesto',
    BUD_EXEC_COL_ACTUAL: 'Ejecutado',
    BUD_EXEC_COL_VARIANCE: 'Variación',
    BUD_EXEC_COL_PCT: '%',
    BUD_EXEC_BADGE_OVERAGE: 'Sobregiro',
    BUD_EXEC_BADGE_WARN: '> 80%',
    BUD_EXEC_BADGE_OK: 'En rango',
    BUD_NOT_DRAFT_BANNER:
      'Este presupuesto ya fue {{status}}. Las líneas son de solo lectura.',
    TITLE: 'Contabilidad',
    COMING_SOON:
      'El módulo de Contabilidad estará disponible próximamente. Diario General, Asientos, Mayor, Conciliación, Activos, Presupuestos y Reportes llegan en las siguientes entregas.',
    DAILY_JOURNAL: 'Diario General',
    JOURNAL: 'Asientos Contables',
    GENERAL_LEDGER: 'Mayor General',
    CANCELLED_JOURNAL: 'Diarios Cancelados',
    BANK_RECONCILIATION: 'Conciliación Bancaria',
    FIXED_ASSETS: 'Activos Fijos',
    DEPRECIATIONS: 'Depreciaciones',
    BUDGETS: 'Presupuestos',
    REPORTS: 'Reportes Financieros',
    FA_LIST_SEARCH_PLACEHOLDER: 'Buscar por código o nombre…',
    FA_LIST_PAGINATION:
      'Página {{page}} de {{totalPages}} — {{total}} activos en total.',
    FA_LIST_COL_ACCUMULATED: 'Depreciación acumulada',
    DEP_SUBTITLE:
      'Resumen del registro contable + historial de depreciaciones por activo.',
    DEP_KPI_ASSETS: 'Activos en servicio',
    DEP_KPI_ACQUISITION: 'Costo de adquisición',
    DEP_KPI_ACCUMULATED: 'Depreciación acumulada',
    DEP_KPI_NET_VALUE: 'Valor neto',
    DEP_ASSET_HISTORY: 'Historial por activo',
    DEP_SELECT_ASSET: 'Selecciona un activo…',
    DEP_SELECT_ASSET_LABEL: 'Seleccionar activo',
    DEP_PICKER_HELP_ARIA: 'Ayuda sobre el selector de activos',
    DEP_PICKER_HELP_TITLE: '¿Por qué está vacío?',
    DEP_PICKER_HELP_BODY:
      'Solo aparecen activos con estado "En servicio". Los activos dados de baja, vendidos, desechados o perdidos no se incluyen. Si no ves ninguno, primero registra un activo desde Activos Fijos.',
    DEP_NO_IN_SERVICE_ASSETS:
      'Sin activos en servicio. Registra uno desde Activos Fijos para ver su historial aquí.',
    DEP_EMPTY_PICKER_TITLE: 'No hay activos en servicio',
    DEP_EMPTY_PICKER_BODY:
      'Para ver depreciaciones primero necesitas registrar al menos un activo fijo con estado "En servicio".',
    DEP_EMPTY_PICKER_CTA: 'Ir a Activos Fijos →',
    DEP_NO_ENTRIES: 'Este activo aún no tiene depreciaciones registradas.',
    DEP_PICK_ASSET_HINT:
      'Elige un activo para ver su historial de depreciaciones.',
    DEP_COL_PERIOD: 'Periodo',
    DEP_COL_POSTED_AT: 'Aplicado',
    DEP_COL_AMOUNT: 'Monto',
    DEP_COL_ACCUMULATED: 'Acumulado',
    DEP_COL_NET_VALUE: 'Valor neto',
    DEP_RUN_NOW: 'Ejecutar ahora',
    DEP_RUN_SUCCESS: 'Depreciación ejecutada para el periodo abierto actual.',
    DEP_RUN_FAILED: 'No se pudo ejecutar la depreciación.',
    DEP_NO_OPEN_PERIOD:
      'No hay un periodo contable abierto. Abre uno antes de ejecutar la depreciación.',
    JE_SUBTITLE:
      'Registra los movimientos contables manuales del negocio. Cada asiento debe estar balanceado (debe = haber) antes de poder contabilizarse.',
    JE_NEW: 'Nuevo asiento',
    JE_CREATE_TITLE: 'Crear asiento contable',
    JE_NUMBER: 'Número',
    JE_PERIOD: 'Período',
    JE_ENTRY_DATE: 'Fecha del asiento',
    JE_DESCRIPTION: 'Descripción',
    JE_REFERENCE: 'Referencia',
    JE_ACCOUNT: 'Cuenta',
    JE_COST_CENTER: 'Centro de costo',
    JE_CC_EMPTY_HINT:
      'No tienes centros de costo configurados. Los centros de costo son opcionales — se usan para segmentar el P&G por departamento, sucursal o proyecto. Crea uno desde Configuración → Centros de Costo si los necesitas.',
    JE_CC_EMPTY_LINK: 'Crear centro de costo',
    JE_DIRECTION: 'Tipo',
    JE_AMOUNT: 'Monto',
    JE_DEBIT: 'Debe',
    JE_CREDIT: 'Haber',
    JE_STATUS: 'Estado',
    JE_STATUS_DRAFT: 'Borrador',
    JE_STATUS_POSTED: 'Contabilizado',
    JE_STATUS_REVERSED: 'Reversado',
    JE_STATUS_VOIDED: 'Anulado',
    JE_POST: 'Contabilizar',
    JE_POST_NOW: 'Contabilizar al guardar',
    JE_POST_CONFIRM: '¿Contabilizar este asiento? No podrá editarse después.',
    JE_POSTING: 'Contabilizando…',
    JE_VOID: 'Anular',
    JE_VOID_CONFIRM:
      '¿Anular este borrador? Solo los borradores pueden anularse; los asientos contabilizados se reversan.',
    JE_VOIDING: 'Anulando…',
    JE_REVERSE: 'Reversar',
    JE_REVERSE_CONFIRM:
      '¿Reversar este asiento? Se creará un asiento espejo con débitos y créditos invertidos.',
    JE_REVERSING: 'Reversando…',
    JE_ADD_LINE: 'Agregar línea',
    JE_BALANCED: 'Balanceado',
    JE_NEED_TWO_LINES:
      'El asiento necesita al menos dos líneas con cuenta y monto.',
    JE_FILTER_ALL_PERIODS: 'Todos los períodos',
    JE_FILTER_ALL_STATUSES: 'Todos los estados',
    JE_SEARCH_PLACEHOLDER: 'Buscar por número, descripción o referencia…',
    JE_EMPTY: 'No hay asientos que coincidan con el filtro.',
    JE_TOAST_DRAFT_CREATED: 'Borrador creado.',
    JE_TOAST_POSTED: 'Asiento contabilizado.',
    JE_TOAST_VOIDED: 'Asiento anulado.',
    JE_TOAST_REVERSED: 'Asiento reversado.',
    JE_LEDGER_SELECT_ACCOUNT:
      'Selecciona una cuenta para ver sus movimientos y saldo.',
    JE_PERIOD_MANAGE_HINT: 'Gestiona períodos en',
    JE_PERIOD_MANAGE_LINK: 'Configuración → Períodos Contables',
    JE_PERIOD_EMPTY:
      'No hay períodos abiertos. Crea uno antes de registrar asientos.',
    JE_DATE_FROM: 'Desde',
    JE_DATE_TO: 'Hasta',
    JE_BALANCE: 'Saldo',
    JE_CLOSING_DEBIT: 'Cierre debe',
    JE_CLOSING_CREDIT: 'Cierre haber',
    JE_MATERIALIZED_BALANCE: 'Saldo materializado',
    JE_NOT_MATERIALIZED: '(sin datos materializados)',
    JE_TOTALS: 'Totales',
    JE_REVERSAL_OF: 'Reverso de',
    JE_EDIT: 'Editar borrador',
    JE_EDIT_TITLE: 'Editar asiento (borrador)',
    JE_TOAST_DRAFT_UPDATED: 'Borrador actualizado.',
    TB_TITLE: 'Balanza de Comprobación',
    TB_SUBTITLE:
      'Saldos iniciales, movimientos y saldos finales por cuenta del período.',
    TB_PERIOD: 'Período',
    TB_BRANCH: 'Sucursal',
    TB_BRANCH_ALL: 'Todas las sucursales',
    TB_GENERATE: 'Generar',
    TB_EXPORT_CSV: 'Exportar CSV',
    TB_EMPTY: 'No hay movimientos en este período.',
    TB_PICK_PERIOD: 'Selecciona un período para generar la balanza.',
    TB_COL_CODE: 'Código',
    TB_COL_NAME: 'Cuenta',
    TB_COL_TYPE: 'Tipo',
    TB_COL_DEBIT: 'Débito',
    TB_COL_CREDIT: 'Crédito',
    TB_COL_BALANCE: 'Saldo',
    TB_TOTALS: 'Totales',
    TB_GENERATED_AT: 'Generado',
    REPORT_TAB_TB: 'Balanza',
    REPORT_TAB_IS: 'Estado de Resultados',
    REPORT_TAB_BS: 'Balance General',
    REPORT_TAB_SB: 'Sumas y Saldos',
    REPORT_TAB_CF: 'Flujo de Efectivo',
    REPORT_TAB_AL: 'Auxiliares',
    REPORT_TAB_EX: 'Gastos',
    REPORT_TAB_BE: 'Presupuestario',
    REPORT_EXPORT_EXCEL: 'Exportar a Excel',
    REPORT_EXPORT_PDF: 'Exportar a PDF',
    REPORT_PICK_PERIOD: 'Selecciona un período',
    REPORT_PICK_ACCOUNT: 'Selecciona una cuenta',
    REPORT_PICK_BUDGET: 'Selecciona un presupuesto',
    REPORT_FROM_DATE: 'Desde',
    REPORT_TO_DATE: 'Hasta',
    REPORT_BRANCH: 'Sucursal',
    REPORT_BRANCH_ALL: 'Todas',
    REPORT_EMPTY: 'No hay datos para mostrar.',
    REPORT_GENERATED_AT: 'Generado',
    AUDIT_TITLE: 'Auditoría Contable',
    AUDIT_SUBTITLE: 'Eventos del módulo contable con cadena de auditoría.',
    AUDIT_EMPTY: 'No hay eventos para los filtros seleccionados.',
    AUDIT_COL_DATE: 'Fecha',
    AUDIT_COL_ENTITY: 'Entidad',
    AUDIT_COL_EVENT: 'Acción',
    AUDIT_COL_ENTITY_ID: 'ID',
    AUDIT_COL_ACTOR: 'Usuario',
    AUDIT_FILTER_ENTITY: 'Tipo de entidad',
    AUDIT_FILTER_EVENT: 'Tipo de evento',
    AUDIT_FILTER_SEARCH: 'Buscar',
    DASH_TITLE: 'Dashboard Contable',
    DASH_SUBTITLE: 'Métricas clave del módulo contable.',
    DASH_TILE_JE_TOTAL: 'Asientos totales',
    DASH_TILE_JE_POSTED: 'Asientos posteados',
    DASH_TILE_JE_DRAFT: 'Asientos borrador',
    DASH_TILE_OPEN_PERIODS: 'Períodos abiertos',
    DASH_TILE_QUEUE_PENDING: 'Eventos pendientes',
    DASH_TILE_QUEUE_FAILED: 'Eventos fallidos',
    DASH_TILE_OPEN_FY: 'Años fiscales abiertos',
    DASH_OPEN_FY_LIST: 'Años fiscales abiertos',
    DASH_NO_OPEN_FY: 'No hay años fiscales abiertos.',
    DASH_TREND_JE_TOTAL: 'Total histórico del módulo',
    DASH_OF_TOTAL: 'del total',
    DASH_CHART_JE_TITLE: 'Distribución de asientos',
    DASH_CHART_QUEUE_TITLE: 'Estado del procesador',
    DASH_QUICK_ACCESS: 'Acceso rápido',
    JOURNAL_SUBTITLE: 'Crear y revisar asientos contables.',
    GL_SUBTITLE: 'Mayor general por cuenta.',
    REPORTS_SUBTITLE: 'Balanza, Estado de Resultados, Balance General y más.',
    REPORTS_NO_PERIODS_TITLE: 'No hay períodos contables registrados',
    REPORTS_NO_PERIODS_BODY:
      'Los reportes financieros (Balanza, Sumas y Saldos, Estado de Resultados, Balance General) requieren un período contable abierto para calcular saldos. Sigue estos pasos para registrar el primer período:',
    REPORTS_NO_PERIODS_STEP_1:
      'Abre Configuración → Contabilidad → Años Fiscales.',
    REPORTS_NO_PERIODS_STEP_2:
      'Pulsa "Nuevo año fiscal", define las fechas de inicio y fin (ej. 1 ene → 31 dic) y guarda.',
    REPORTS_NO_PERIODS_STEP_3:
      'Marca el año como "Abierto" — el sistema genera automáticamente los 12 períodos mensuales (o el calendario que elijas).',
    REPORTS_NO_PERIODS_STEP_4:
      'Regresa a Reportes Financieros y selecciona el período en el filtro de arriba.',
    REPORTS_NO_PERIODS_GO_BTN: 'Ir a Años Fiscales',
    REPORTS_NO_BUDGETS_TITLE: 'No hay presupuestos registrados',
    REPORTS_NO_BUDGETS_BODY:
      'El reporte de ejecución de presupuesto compara lo planificado contra lo ejecutado. Sin presupuestos aprobados no hay nada que comparar. Crea uno primero:',
    REPORTS_NO_BUDGETS_STEP_1:
      'Verifica que tengas un año fiscal abierto (Configuración → Contabilidad → Años Fiscales).',
    REPORTS_NO_BUDGETS_STEP_2:
      'Abre Contabilidad → Presupuestos y pulsa "Nuevo presupuesto" — define nombre, tipo (anual o mensual) y año fiscal.',
    REPORTS_NO_BUDGETS_STEP_3:
      'Agrega las líneas por cuenta + monto y marca el presupuesto como "Aprobado". Vuelve a este reporte y selecciónalo en el filtro.',
    REPORTS_NO_BUDGETS_GO_BTN: 'Ir a Presupuestos',
    FINANCIAL_HEALTH_TITLE: 'Salud Financiera',
    FINANCIAL_HEALTH_SUBTITLE:
      'Centro de integridad financiera. Detecta inconsistencias en facturas, notas de crédito, impuestos, inventario y contabilidad antes de que se conviertan en problemas.',
    FINANCIAL_HEALTH_SCORE: 'Salud del sistema',
    FINANCIAL_HEALTH_RUN_ALL: 'Ejecutar auditoría',
    FINANCIAL_HEALTH_CATEGORY: 'Categoría',
    FINANCIAL_HEALTH_OPEN_ISSUES: 'Incidencias abiertas',
    FINANCIAL_HEALTH_NO_ISSUES:
      'Sin incidencias — todos los documentos consistentes.',
    FINANCIAL_HEALTH_RULE_CODE: 'Código',
    FINANCIAL_HEALTH_DOCUMENT: 'Documento',
    FINANCIAL_HEALTH_EXPECTED: 'Esperado',
    FINANCIAL_HEALTH_ACTUAL: 'Registrado',
    FINANCIAL_HEALTH_DELTA: 'Diferencia',
    FINANCIAL_HEALTH_SEVERITY: 'Severidad',
    FINANCIAL_HEALTH_SUGGESTION: 'Recomendación',
    FINANCIAL_HEALTH_ACKNOWLEDGE: 'Reconocer',
    FINANCIAL_HEALTH_RESOLVE: 'Resolver',
    FINANCIAL_HEALTH_IGNORE: 'Ignorar',
    SYS_TITLE: 'Estado del Sistema Contable',
    SYS_SUBTITLE: 'Diagnóstico del procesador, períodos y eventos parados.',
    SYS_PARKED_EVENTS: 'Eventos parados (fallidos)',
    SYS_PENDING_EVENTS: 'Eventos pendientes',
    SYS_PENDING_HINT:
      'El procesador contable los toma cada ~60 segundos. Si llevan mucho tiempo aquí, revisa el estado del procesador o reinicia el servicio.',
    SYS_PENDING_OLDEST: 'Desde {{date}}',
    SYS_TICK_NOW: 'Procesar ahora',
    SYS_TICK_NOW_SUCCESS:
      'Procesamiento ejecutado: {{processed}} procesados, {{failed}} fallidos.',
    SYS_TICK_NOW_MORE_FAILURES:
      '+ {{count}} más — revisa la sección Eventos Estacionados para el detalle completo.',
    SYS_NO_PENDING: 'No hay eventos en cola.',
    SYS_NO_PARKED: 'No hay eventos parados. Todo en orden.',
    PARKED_RECONCILE_BUTTON: 'Reconciliar',
    PARKED_RECONCILE_TITLE: 'Reconciliar evento contable',
    PARKED_RECONCILE_DESCRIPTION:
      'Ajusta los montos de débitos y créditos hasta que cuadren. Al guardar, se registra un asiento manual y el evento queda resuelto.',
    PARKED_RECONCILE_REASON_LABEL: 'Motivo de la reconciliación',
    PARKED_RECONCILE_REASON_PLACEHOLDER:
      'Explica brevemente el ajuste (ej: se corrigió el mapeo de ITBIS).',
    PARKED_RECONCILE_ADD_LINE: 'Agregar línea',
    PARKED_RECONCILE_REMOVE_LINE: 'Eliminar línea',
    PARKED_RECONCILE_LINE_ACCOUNT: 'Cuenta',
    PARKED_RECONCILE_LINE_DEBIT: 'Débito',
    PARKED_RECONCILE_LINE_CREDIT: 'Crédito',
    PARKED_RECONCILE_LINE_MEMO: 'Concepto',
    PARKED_RECONCILE_TOTAL_DEBIT: 'Total débitos',
    PARKED_RECONCILE_TOTAL_CREDIT: 'Total créditos',
    PARKED_RECONCILE_DIFFERENCE: 'Diferencia',
    PARKED_RECONCILE_BALANCED: 'Cuadra',
    PARKED_RECONCILE_SAVE: 'Guardar y publicar',
    PARKED_RECONCILE_CANCEL: 'Cancelar',
    PARKED_RECONCILE_SUCCESS: 'Evento reconciliado. Asiento publicado.',
    PARKED_RECONCILE_FAILED: 'No se pudo reconciliar el evento.',
    PARKED_AUTO_RECONCILE: 'Conciliar automático',
    PARKED_AUTO_RECONCILE_APPLIED:
      'Propuesta generada a partir del documento. Revisa los montos antes de guardar.',
    PARKED_AUTO_RECONCILE_WARN_TITLE:
      'Propuesta automática — revisa los montos antes de guardar',
    PARKED_AUTO_RECONCILE_WARN_NO_SNAPSHOT:
      'No hay un desglose previo del motor de reglas. Arma las líneas manualmente.',
    PARKED_AUTO_RECONCILE_WARN_NO_TOTAL:
      'El documento no trae un total; se cuadró según la configuración actual del asiento.',
    PARKED_AUTO_RECONCILE_WARN_MULTIPLE:
      'Hay varias cuentas de liquidación candidatas; se ajustó la de mayor monto. Verifica la cuenta correcta.',
    PARKED_AUTO_RECONCILE_WARN_CANNOT:
      'No se pudo cuadrar automáticamente sin tocar las líneas de impuestos. Completa el desglose manualmente.',
    PARKED_RECONCILE_EMPTY_ACCOUNT:
      'No hay cuentas contables activas para seleccionar.',
    PARKED_RECONCILE_PICK_ACCOUNT: 'Selecciona una cuenta',
    PARKED_RECONCILE_NEED_TWO_LINES:
      'Se requieren al menos dos líneas (una débito y una crédito).',
    PARKED_RECONCILE_NEED_BOTH_SIDES:
      'Debes incluir al menos una línea de débito y una de crédito.',
    PARKED_RECONCILE_MIN_AMOUNT: 'El monto debe ser mayor a cero.',
    PARKED_RECONCILE_ONE_SIDE_ONLY:
      'Cada línea solo puede tener débito o crédito, no ambos.',
    PARKED_RECONCILE_REASON_REQUIRED:
      'Ingresa un motivo para la reconciliación.',
    SYS_PERIOD_HEALTH: 'Estado de períodos',
    SYS_FY_HEALTH: 'Estado de años fiscales',
    SYS_NO_PERIODS: 'No hay períodos contables configurados.',
    SYS_NO_PERIODS_HINT:
      'Sin períodos abiertos, ningún asiento puede contabilizarse. Crea el período mensual correspondiente para continuar.',
    SYS_NO_PERIODS_CTA: 'Ir a Períodos Contables',
    SYS_NO_FY: 'No hay años fiscales configurados.',
    SYS_NO_FY_HINT:
      'El año fiscal define qué períodos están vigentes. Abre el año fiscal del ejercicio actual para habilitar la contabilización.',
    SYS_NO_FY_CTA: 'Ir a Años Fiscales',
    SYS_RECENT_DRAFTS: 'Asientos borrador recientes',
    SYS_NO_DRAFTS: 'No hay asientos en borrador.',
    SYS_PERIOD_OPEN: 'Abierto',
    SYS_PERIOD_GRACE: 'En gracia',
    SYS_PERIOD_CLOSED: 'Cerrado',
    SYS_PERIOD_LOCKED: 'Bloqueado',
    SYS_FY_OPEN: 'Abierto',
    SYS_FY_CLOSED: 'Cerrado',
    SYS_FY_LOCKED: 'Bloqueado',
    SYS_MODULE_FIXED_ASSET: 'Activos Fijos',
    SYS_MODULE_INVOICE: 'Facturación',
    SYS_MODULE_PAYMENT: 'Pagos',
    SYS_MODULE_STOCK_MOVEMENT: 'Inventario',
    SYS_MODULE_EXPENSE: 'Gastos',
    SYS_MODULE_PURCHASE_ORDER: 'Órdenes de Compra',
    SYS_MODULE_REFUND: 'Devoluciones',
    SYS_MODULE_BANK: 'Banco',
    SYS_MODULE_BUDGET: 'Presupuestos',
    SYS_SUBTYPE_FA_ACQUIRED: 'Adquisición',
    SYS_SUBTYPE_FA_DEPRECIATED: 'Depreciación',
    SYS_SUBTYPE_FA_REVALUATION: 'Revaluación',
    SYS_SUBTYPE_FA_IMPAIRMENT: 'Deterioro',
    SYS_SUBTYPE_FA_TRANSFERRED: 'Traslado',
    SYS_SUBTYPE_FA_DISPOSED: 'Baja',
    SYS_SUBTYPE_INVOICE_ISSUED_CASH: 'Factura al contado',
    SYS_SUBTYPE_INVOICE_ISSUED_CREDIT: 'Factura a crédito',
    SYS_SUBTYPE_INVOICE_ISSUED_CASH_OP: 'Pre Factura al contado (operacional)',
    SYS_SUBTYPE_INVOICE_ISSUED_CREDIT_OP: 'Pre Factura a crédito (operacional)',
    SYS_SUBTYPE_INVOICE_ISSUED_OP: 'Pre Factura emitida (operacional)',
    SYS_SUBTYPE_INVOICE_CANCELLED: 'Factura cancelada',
    SYS_SUBTYPE_PAYMENT_RECEIVED: 'Pago recibido',
    SYS_SUBTYPE_PAYMENT_REFUNDED: 'Pago reembolsado',
    SYS_SUBTYPE_EXPENSE_RECORDED: 'Gasto registrado',
    SYS_SUBTYPE_STOCK_ISSUED: 'Movimiento de inventario',
    SYS_SUBTYPE_INVOICE_ISSUED: 'Factura emitida',
    SYS_SUBTYPE_PAYMENT_DISBURSED: 'Pago realizado',
    SYS_SUBTYPE_STOCK_SALE: 'Salida por venta',
    SYS_SUBTYPE_STOCK_PURCHASE: 'Entrada por compra',
    SYS_SUBTYPE_STOCK_ADJUSTMENT: 'Ajuste de inventario',
    SYS_SUBTYPE_STOCK_TRANSFER: 'Traslado entre sucursales',
    SYS_SUBTYPE_STOCK_RETURN: 'Devolución de inventario',
    SYS_SUBTYPE_PO_RECEIVED: 'Orden de compra recibida',
    SYS_SUBTYPE_PO_DEBIT_NOTE: 'Nota de débito de compra',
    SYS_SUBTYPE_REFUND_PENDING: 'Reembolso pendiente',
    SYS_SUBTYPE_REFUND_COMPLETED: 'Reembolso completado',
    SYS_SUBTYPE_REFUND_PROCESSED: 'Reembolso procesado',
    SYS_SUBTYPE_CN_APPROVED: 'Nota de crédito aprobada',
    SYS_SUBTYPE_CV_DROP: 'Retiro de efectivo',
    SYS_SUBTYPE_CV_PICKUP: 'Levantamiento de efectivo',
    SYS_SUBTYPE_FY_OPENED: 'Año fiscal abierto',
    SYS_SUBTYPE_SHIFT_CLOSED: 'Cierre de turno',
    SYS_SUBTYPE_SHIFT_OPENED: 'Apertura de turno',
    SYS_SUBTYPE_PO_SUPPLIER_CREDIT_NOTE: 'Nota de crédito de proveedor',
    SYS_SUBTYPE_PO_SUPPLIER_CREDIT_NOTE_REVERSED:
      'Anulación de nota de crédito de proveedor',
    SYS_SUBTYPE_STOCK_DAMAGED: 'Inventario dañado',
    SYS_SUBTYPE_STOCK_LOST: 'Inventario perdido',
    SYS_SUBTYPE_STOCK_INITIAL: 'Inventario inicial',
    SYS_SUBTYPE_INVOICE_OPENING_BALANCE: 'Saldo inicial de cliente',
    SYS_SUBTYPE_PO_OPENING_BALANCE: 'Saldo inicial de proveedor',
    SYS_SUBTYPE_CV_INGRESO: 'Vale de caja — ingreso',
    SYS_SUBTYPE_CV_EGRESO: 'Vale de caja — egreso',
    SYS_SUBTYPE_INVOICE_CANCELLED_OP: 'Pre Factura cancelada (operacional)',
    SYS_SUBTYPE_CN_ISSUED: 'Nota de crédito emitida',
    SYS_SUBTYPE_FA_SOLD: 'Venta',
    SYS_SUBTYPE_FA_SCRAPPED: 'Desecho',
    SYS_SUBTYPE_FA_LOST: 'Pérdida',
    SYS_SUBTYPE_SUPPLIER_PAYMENT_RECORDED: 'Pago a proveedor registrado',
    SYS_SUBTYPE_PROCUREMENT_PO_RECEIVED: 'OC de abastecimiento recibida',
    SYS_SUBTYPE_AR_WRITE_OFF: 'Castigo de cuenta por cobrar',
    SYS_SUBTYPE_ROUNDING_OVERPAID: 'Redondeo — pagó más',
    SYS_SUBTYPE_ROUNDING_WRITTEN_OFF: 'Redondeo — pagó menos',
    SYS_MODULE_CREDIT_NOTE: 'Notas de Crédito',
    SYS_MODULE_CASH_VOUCHER: 'Vales de Caja',
    SYS_MODULE_FISCAL_YEAR: 'Año Fiscal',
    SYS_MODULE_SHIFT: 'Turno de caja',
    SYS_MODULE_ACCOUNTS_RECEIVABLE: 'Cuentas por Cobrar',
    SYS_MODULE_PROCUREMENT_PO: 'Abastecimiento',
    SYS_MODULE_ROUNDING_ADJUSTMENT: 'Ajuste por Redondeo',
    SYS_MODULE_SUPPLIER_PAYMENT: 'Pagos a Proveedores',
    AUDIT_ENT_JOURNAL_ENTRY: 'Asiento contable',
    AUDIT_ENT_JOURNAL_ENTRY_LINE: 'Línea de asiento',
    AUDIT_ENT_ACCOUNTING_PERIOD: 'Período contable',
    AUDIT_ENT_FISCAL_YEAR: 'Año fiscal',
    AUDIT_ENT_ACCOUNTING_CONFIG: 'Configuración contable',
    AUDIT_ENT_POSTING_RULE: 'Regla contable',
    AUDIT_ENT_POSTING_RULE_VERSION: 'Versión de regla',
    AUDIT_ENT_BUDGET: 'Presupuesto',
    AUDIT_ENT_BUDGET_LINE: 'Línea de presupuesto',
    AUDIT_ENT_FIXED_ASSET: 'Activo fijo',
    AUDIT_ENT_FIXED_ASSET_CATEGORY: 'Categoría de activo',
    AUDIT_ENT_FIXED_ASSET_DEPRECIATION: 'Depreciación de activo',
    AUDIT_ENT_FIXED_ASSET_MOVEMENT: 'Movimiento de activo',
    AUDIT_ENT_BANK_ACCOUNT: 'Cuenta bancaria',
    AUDIT_ENT_BANK_RECONCILIATION: 'Conciliación bancaria',
    AUDIT_ENT_BANK_STATEMENT_LINE: 'Línea de extracto bancario',
    AUDIT_ENT_PERIOD_CLOSE_AUDIT: 'Auditoría de cierre de período',
    AUDIT_ENT_FISCAL_YEAR_CLOSE_AUDIT: 'Auditoría de cierre de año fiscal',
    AUDIT_EV_CREATED: 'Creado',
    AUDIT_EV_UPDATED: 'Actualizado',
    AUDIT_EV_DELETED: 'Eliminado',
    AUDIT_EV_RESTORED: 'Restaurado',
    AUDIT_EV_COMMITTED: 'Confirmado',
    AUDIT_EV_POSTED: 'Contabilizado',
    AUDIT_EV_PUBLISHED: 'Publicado',
    AUDIT_EV_SUPERSEDED: 'Reemplazado',
    AUDIT_EV_DRAFT: 'Borrador',
    AUDIT_EV_OPENED: 'Abierto',
    AUDIT_EV_CLOSED: 'Cerrado',
    AUDIT_EV_REOPENED: 'Reabierto',
    AUDIT_EV_LOCKED: 'Bloqueado',
    AUDIT_EV_UNLOCKED: 'Desbloqueado',
    AUDIT_EV_APPROVED: 'Aprobado',
    AUDIT_EV_REJECTED: 'Rechazado',
    AUDIT_EV_CANCELLED: 'Cancelado',
    AUDIT_EV_REVERSED: 'Reversado',
    AUDIT_EV_PARKED: 'Parado',
    AUDIT_EV_RETRIED: 'Reintentado',
    AUDIT_EV_REPLAYED: 'Reprocesado',
    AUDIT_EV_DEPRECIATED: 'Depreciado',
    AUDIT_EV_TRANSFERRED: 'Transferido',
    AUDIT_EV_DISPOSED: 'Dado de baja',
    AUDIT_EV_RECONCILED: 'Conciliado',
    SB_TITLE: 'Balance de Sumas y Saldos',
    SB_SUBTITLE: 'Totales acumulados por cuenta al cierre del período.',
    SB_COL_CODE: 'Código',
    SB_COL_NAME: 'Cuenta',
    SB_COL_TYPE: 'Tipo',
    SB_COL_DEBIT_SUM: 'Suma Débito',
    SB_COL_CREDIT_SUM: 'Suma Crédito',
    SB_COL_BALANCE: 'Saldo',
    CF_TITLE: 'Flujo de Efectivo',
    CF_SUBTITLE: 'Movimientos por cuenta de efectivo en el período.',
    CF_COL_ACCOUNT: 'Cuenta',
    CF_COL_INFLOW: 'Entradas',
    CF_COL_OUTFLOW: 'Salidas',
    CF_COL_NET: 'Neto',
    AL_TITLE: 'Auxiliares por Cuenta',
    AL_SUBTITLE: 'Detalle de asientos contabilizados con saldo corriente.',
    AL_OPENING: 'Saldo inicial',
    AL_CLOSING: 'Saldo final',
    AL_COL_DATE: 'Fecha',
    AL_COL_ENTRY: 'Asiento',
    AL_COL_DESCRIPTION: 'Descripción',
    AL_COL_DEBIT: 'Débito',
    AL_COL_CREDIT: 'Crédito',
    AL_COL_BALANCE: 'Saldo',
    EX_TITLE: 'Reporte de Gastos',
    EX_SUBTITLE: 'Gastos y costos por cuenta en el período.',
    EX_COL_CODE: 'Código',
    EX_COL_NAME: 'Cuenta',
    EX_COL_AMOUNT: 'Monto',
    EX_TOTAL: 'Total de gastos',
    BE_TITLE: 'Reporte Presupuestario',
    BE_SUBTITLE: 'Ejecución por línea presupuestaria contra el real.',
    BE_COL_ACCOUNT: 'Cuenta',
    BE_COL_PERIOD: 'Período',
    BE_COL_BUDGET: 'Presupuesto',
    BE_COL_ACTUAL: 'Real',
    BE_COL_VARIANCE: 'Variación',
    BE_COL_PCT: '% Ejec.',
    BE_COL_STATUS: 'Estado',
    BE_STATUS_OK: 'OK',
    BE_STATUS_WARN: 'Alerta',
    BE_STATUS_OVERAGE: 'Excedido',
    IS_TITLE: 'Estado de Resultados',
    IS_SUBTITLE:
      'Ingresos, costos y gastos del período. Utilidad bruta y neta.',
    IS_REVENUE: 'Ingresos',
    IS_COGS: 'Costos de venta',
    IS_EXPENSE: 'Gastos operativos',
    IS_GROSS_PROFIT: 'Utilidad bruta',
    IS_NET_INCOME: 'Utilidad neta',
    IS_AMOUNT: 'Monto',
    IS_EMPTY: 'No hay movimientos de P&G en este período.',
    BS_TITLE: 'Balance General',
    BS_SUBTITLE:
      'Posición financiera acumulada a la fecha indicada. Activo = Pasivo + Capital.',
    BS_AS_OF_DATE: 'Fecha de corte',
    BS_ASSETS: 'Activos',
    BS_LIABILITIES: 'Pasivos',
    BS_EQUITY: 'Capital',
    BS_RETAINED_EARNINGS: 'Utilidades retenidas (calculado)',
    BS_TOTAL_ASSETS: 'Total activos',
    BS_TOTAL_LIAB_EQUITY: 'Total pasivo + capital',
    BS_AMOUNT: 'Monto',
    BS_EMPTY: 'Sin saldos acumulados a esta fecha.',
    BS_PICK_DATE: 'Selecciona una fecha de corte.',
    BS_BALANCED_OK: 'Balance cuadrado.',
    BS_NOT_BALANCED: 'Balance no cuadra (Δ {{delta}}).',
    JE_AUTO_PREFIX: 'Auto:',
    JE_AUTO_INVOICE_ISSUED: 'Factura emitida (crédito)',
    JE_AUTO_INVOICE_ISSUED_CASH: 'Factura emitida (contado)',
    JE_AUTO_INVOICE_CATCHUP: 'Factura — reproceso histórico',
    JE_AUTO_PAYMENT_RECEIVED: 'Pago recibido',
    JE_AUTO_PAYMENT_DISBURSED: 'Pago emitido',
    JE_AUTO_PAYMENT_CATCHUP: 'Pago — reproceso histórico',
    JE_AUTO_PO_RECEIVED: 'Recepción de orden de compra',
    JE_AUTO_PO_SUPPLIER_CREDIT_NOTE: 'Nota de crédito de proveedor',
    JE_AUTO_PO_SUPPLIER_CREDIT_NOTE_REVERSED:
      'Anulación de nota de crédito de proveedor',
    JE_AUTO_PO_CATCHUP: 'Orden de compra — reproceso histórico',
    JE_AUTO_CN_CATCHUP: 'Nota de crédito — reproceso histórico',
    JE_AUTO_REFUND_PENDING: 'Devolución pendiente',
    JE_AUTO_REFUND_COMPLETED: 'Devolución completada',
    JE_AUTO_REFUND_PROCESSED: 'Devolución procesada',
    JE_AUTO_REFUND_CATCHUP: 'Devolución — reproceso histórico',
    JE_AUTO_CASH_VOUCHER_IN: 'Entrada de caja',
    JE_AUTO_CASH_VOUCHER_OUT: 'Salida de caja',
    JE_AUTO_CASH_VOUCHER_CATCHUP: 'Caja — reproceso histórico',
    JE_AUTO_EXPENSE_RECORDED: 'Gasto registrado',
    JE_AUTO_STOCK_SALE: 'Movimiento de inventario (venta)',
    JE_AUTO_STOCK_ADJUSTMENT: 'Ajuste de inventario',
    JE_AUTO_STOCK_DAMAGED: 'Mercancía dañada',
    JE_AUTO_STOCK_INITIAL: 'Inventario inicial',
    JE_AUTO_STOCK_LOST: 'Mercancía perdida',
    JE_AUTO_STOCK_MERMA: 'Merma de inventario',
    JE_AUTO_INVOICE_ISSUED_CREDIT: 'Factura emitida (crédito)',
    JE_AUTO_INVOICE_ISSUED_OPERATIONAL: 'Factura emitida (operacional)',
    JE_AUTO_INVOICE_ISSUED_CASH_OPERATIONAL:
      'Factura emitida (contado — operacional)',
    JE_AUTO_INVOICE_ISSUED_CREDIT_OPERATIONAL:
      'Factura emitida (crédito — operacional)',
    JE_AUTO_INVOICE_CANCELLED: 'Factura anulada',
    JE_AUTO_INVOICE_CANCELLED_OPERATIONAL: 'Factura anulada (operacional)',
    JE_AUTO_SHIFT_CLOSED: 'Cierre de turno',
    JE_AUTO_SUPPLIER_PAYMENT_RECORDED: 'Pago a proveedor',
    JE_AUTO_SUPPLIER_PAYMENT_REVERSED: 'Reversión de pago a proveedor',
    JE_AUTO_ROUNDING_WRITTEN_OFF: 'Ajuste de redondeo (baja)',
    JE_AUTO_ROUNDING_OVERPAID: 'Ajuste de redondeo (sobrepago)',
    JE_AUTO_AR_WRITE_OFF: 'Baja de saldo por cobrar',
    JE_AUTO_CREDIT_NOTE_ISSUED: 'Nota de crédito emitida',
    JE_AUTO_DEBIT_NOTE_ISSUED: 'Nota de débito emitida',
    JE_AUTO_PROCUREMENT_PO_RECEIVED:
      'Recepción de orden de compra (procurement)',
    JE_AUTO_FIXED_ASSET_DEPRECIATION: 'Depreciación de activo fijo',
    JE_AUTO_FIXED_ASSET_REVALUATION: 'Revaluación de activo fijo',
    JE_AUTO_FIXED_ASSET_IMPAIRMENT: 'Deterioro de activo fijo',
    JE_AUTO_FIXED_ASSET_DISPOSAL: 'Baja de activo fijo',
    JE_IS_ADJUSTMENT: 'Asiento de ajuste (período cerrado)',
    JE_IS_ADJUSTMENT_HINT:
      'Permite registrar en períodos cerrados. Requiere permiso especial.',
    JE_REF_INVOICE: 'Ver factura',
    BR_TITLE: 'Conciliación Bancaria',
    BR_SUBTITLE:
      'Importa estados de cuenta y enlaza cada transacción con su asiento contable.',
    BR_HINT_BANK_ACCOUNTS:
      'Las cuentas bancarias se administran desde Configuración → Contabilidad → Cuentas Bancarias. Asegúrate de que cada cuenta tenga vinculada una cuenta contable (GL) para que aparezca aquí.',
    BR_HINT_GO_BANK_ACCOUNTS: 'Ir a Cuentas Bancarias',
    BR_NO_BANK_ACCOUNTS: 'No hay cuentas bancarias registradas todavía.',
    BR_NO_BANK_ACCOUNTS_CTA: 'Crear cuenta bancaria',
    BR_SELECT_PROMPT_TITLE: 'Selecciona una cuenta bancaria',
    BR_SELECT_PROMPT_DESC:
      'Elige la cuenta que deseas conciliar para ver sus estados de cuenta importados y sesiones de conciliación.',
    BR_PICK_ACCOUNT: 'Cuenta bancaria',
    BR_STATEMENTS: 'Estados de cuenta',
    BR_NO_STATEMENTS: 'No hay estados de cuenta importados para esta cuenta.',
    BR_STMT_DATE: 'Fecha del estado',
    BR_OPENING: 'Saldo inicial',
    BR_CLOSING: 'Saldo final',
    BR_SOURCE_FILE: 'Archivo',
    BR_NO_SESSION: 'Sin sesión',
    BR_OPEN_SESSION: 'Abrir',
    BR_START_SESSION: 'Iniciar conciliación',
    BR_SESSION: 'Sesión de conciliación',
    BR_SESSION_OPENED: 'Sesión iniciada.',
    BR_MATCHED:
      '{{matched}} línea(s) conciliada(s) · {{unmatched}} pendiente(s).',
    BR_AUTO_MATCH: 'Auto-conciliar',
    BR_AUTO_MATCH_TOAST:
      'Auto-conciliación: {{matched}} línea(s) emparejadas, {{remaining}} pendiente(s).',
    BR_FINALIZE: 'Finalizar y bloquear',
    BR_FINALIZED: 'Conciliación finalizada y bloqueada.',
    BR_NO_LINES: 'El estado no tiene líneas.',
    BR_MATCH: 'Emparejar con asiento',
    BR_UNMATCH: 'Desemparejar',
    BR_PICK_JE_LINE: 'Selecciona asiento',
    BR_NO_CANDIDATES: 'Sin candidatos',
    BR_NO_CANDIDATES_HINT:
      'No hay asientos contables disponibles para emparejar. Verifica: 1) que la cuenta bancaria tenga una cuenta contable (GL) enlazada en Configuración → Cuentas Bancarias, 2) que existan asientos POSTEADOS (no borradores) contra esa cuenta dentro del rango de fechas del estado de cuenta.',
    BR_LOCKED_TITLE: 'Conciliación bloqueada · solo lectura',
    BR_LOCKED_HINT:
      'Esta sesión ya fue finalizada. Puedes revisar las coincidencias pero no agregar, editar o desemparejar líneas.',
    BR_SCOPE_LABEL: 'Buscando en cuenta {{gl}} · período {{start}} → {{end}}',
    BR_IMPORT: 'Importar estado',
    BR_IMPORT_TITLE: 'Importar estado de cuenta (CSV)',
    BR_IMPORTED: 'Estado de cuenta importado.',
    BR_PERIOD_START: 'Inicio del período',
    BR_PERIOD_END: 'Fin del período',
    BR_CSV_FILE: 'Archivo CSV',
    BR_ROWS_DETECTED: '{{count}} fila(s) detectadas',
    BR_LINE_STATUS_UNMATCHED: 'Sin conciliar',
    BR_LINE_STATUS_MATCHED: 'Conciliada',
    BR_LINE_STATUS_ADJUSTED: 'Ajustada',
    BR_LINE_STATUS_IGNORED: 'Ignorada',
    BR_SESSION_STATUS_OPEN: 'Abierta',
    BR_SESSION_STATUS_IN_PROGRESS: 'En progreso',
    BR_SESSION_STATUS_COMPLETED: 'Completada',
    BR_SESSION_STATUS_LOCKED: 'Bloqueada',
    BR_MAP_TITLE: 'Mapeo de columnas',
    BR_MAP_DATE: 'Columna fecha',
    BR_MAP_DESC: 'Columna descripción',
    BR_MAP_DEBIT: 'Columna débito',
    BR_MAP_CREDIT: 'Columna crédito',
    BR_MAP_REF: 'Columna referencia',
    JE_REF_PURCHASE_ORDER: 'Ver orden de compra',
    JE_REF_EXPENSE: 'Ver gastos',
    JE_REF_PAYMENT: 'Ver pagos',
    JE_REF_STOCK_MOVEMENT: 'Ver movimiento de inventario',
    JE_SOURCE: 'Origen',
  },
  CONFIGURATION_ACCOUNTING: {
    TITLE: 'Configuración Contable',
    PEA_TITLE: 'Cuentas por Tipo de Compra (DGII 606)',
    PEA_SUBTITLE:
      'Define a qué cuenta contable se carga cada tipo de bienes y servicios del Formato 606. El tipo que selecciones en la factura de proveedor decide la cuenta: los tipos 01 a 09 son gastos, el 10 es inventario y el 11 es activo fijo.',
    PEA_COL_TYPE: 'Tipo de bienes y servicios',
    PEA_COL_DEFAULT_ROLE: 'Naturaleza',
    PEA_COL_ACCOUNT: 'Cuenta contable',
    PEA_ROLE_EXPENSE: 'Gasto',
    PEA_ROLE_INVENTORY: 'Inventario',
    PEA_ROLE_UNMAPPED: 'Sin asignar',
    PEA_UNMAPPED_BADGE: 'Falta asignar',
    PEA_UNMAPPED_WARNING:
      'Hay {{count}} tipo(s) sin cuenta asignada. Una factura con ese tipo no generará su asiento contable hasta que asignes una cuenta — la factura, el 606 y el inventario sí se registran.',
    PEA_FIXED_ASSET_HINT:
      'El tipo 11 (Compras de Activos Fijos) no trae cuenta por defecto: debes elegir la cuenta de activo fijo que corresponda a tu catálogo.',
    PEA_SELECT_ACCOUNT: 'Seleccionar cuenta',
    PEA_CLEAR_ACCOUNT: 'Sin asignar',
    PEA_SAVE: 'Guardar cambios',
    PEA_SAVED: 'Cuentas por tipo de compra actualizadas',
    PEA_SAVE_ERROR: 'No se pudieron guardar las cuentas',
    PEA_NO_CHANGES: 'No hay cambios por guardar',
    PEA_READ_ONLY:
      'No tienes permiso para editar el ruteo contable. Solo puedes consultar.',
    COMING_SOON:
      'La configuración contable estará disponible próximamente. Catálogo de cuentas, períodos, reglas contables y más llegan en la siguiente entrega.',
    COA_TITLE: 'Catálogo de Cuentas',
    COA_SUBTITLE:
      'Cuentas contables del negocio. Define códigos, jerarquía y tipo para que los asientos y reportes se agrupen correctamente.',
    COA_NEW_ACCOUNT: 'Nueva cuenta',
    COA_CREATE_TITLE: 'Crear cuenta',
    COA_EDIT_TITLE: 'Editar cuenta',
    COA_COL_CODE: 'Código',
    COA_COL_NAME: 'Nombre',
    COA_COL_DESCRIPTION: 'Descripción',
    COA_COL_TYPE: 'Tipo',
    COA_COL_CURRENCY: 'Moneda',
    COA_COL_POSTABLE: 'Aceptable',
    COA_COL_STATUS: 'Estado',
    COA_FIELD_PARENT: 'Cuenta padre',
    COA_FIELD_NO_PARENT: '— Sin cuenta padre —',
    COA_FIELD_CURRENCY: 'Moneda',
    COA_FIELD_SORT_ORDER: 'Orden',
    COA_FIELD_IS_POSTABLE: 'Acepta movimientos directos',
    COA_FILTER_ALL_TYPES: 'Todos los tipos',
    COA_FILTER_ALL_STATUSES: 'Todos los estados',
    COA_SEARCH_PLACEHOLDER: 'Buscar por código o nombre…',
    COA_STATUS_ACTIVE: 'Activa',
    COA_STATUS_ARCHIVED: 'Archivada',
    COA_EMPTY: 'No hay cuentas que coincidan con el filtro.',
    COA_ARCHIVE_CONFIRM: '¿Archivar esta cuenta?',
    COA_TOAST_CREATED: 'Cuenta creada.',
    COA_TOAST_UPDATED: 'Cuenta actualizada.',
    COA_TOAST_ARCHIVED: 'Cuenta archivada.',
    COA_TYPE_ASSET: 'Activo',
    COA_TYPE_LIABILITY: 'Pasivo',
    COA_TYPE_EQUITY: 'Patrimonio',
    COA_TYPE_REVENUE: 'Ingreso',
    COA_TYPE_COGS: 'Costo',
    COA_TYPE_EXPENSE: 'Gasto',
    COA_SEED_DEFAULTS: 'Importar plan + configurar defaults',
    COA_SEED_TOAST:
      'Catálogo actualizado — {{inserted}} cuentas creadas, {{skipped}} ya existían, {{wired}} valores por defecto preconfigurados.',
    COA_BULK_TITLE: 'Importar catálogo de cuentas',
    COA_BULK_SUBTITLE:
      'Sube un CSV / XLSX / XLS con tu plan contable. Detectamos jerarquía por código padre o por prefijo del código.',
    COA_BULK_CTA: 'Importar archivo',
    COA_BULK_DRAG_DROP: 'Arrastra o selecciona el archivo de cuentas.',
    COA_BULK_SUPPORTED: 'Formatos soportados: CSV, XLSX, XLS.',
    COA_BULK_TEMPLATE_TITLE: 'Descargar plantilla',
    COA_BULK_TEMPLATE_HELP:
      'CSV con los encabezados que el importador detecta automáticamente.',
    COA_BULK_TEMPLATE_CTA: 'Descargar',
    COA_BULK_MAPPING_HELP:
      'Asocia las columnas del archivo a los campos canónicos del catálogo. Los marcados con (Requerido) son obligatorios.',
    COA_BULK_MAP_REQUIRED: 'Mapea los campos requeridos para continuar.',
    COA_BULK_COMPLETE: 'Importación enviada',
    COA_BULK_COMPLETE_HELP:
      'El proceso continuará en segundo plano. Las cuentas aparecerán en tu plan contable en unos segundos.',
    COA_BULK_SHOWING_ROWS: 'Mostrando {{count}} de {{total}} filas.',
    COA_BULK_LOAD_MORE: 'Cargar {{count}} más',
    COA_BULK_LOAD_ALL: 'Cargar todas',
    COA_EXPAND_ALL: 'Expandir todas',
    COA_COLLAPSE_ALL: 'Contraer todas',
    COA_ADD_CHILD: 'Agregar cuenta hija',
    EXPENSE_CAT_TITLE: 'Categorías de Gastos',
    EXPENSE_CAT_SUBTITLE:
      'Buckets reutilizables (Servicios, Marketing, Reparaciones) que enrutan el gasto a la cuenta contable correspondiente.',
    EXPENSE_CAT_NEW: 'Nueva categoría',
    EXPENSE_CAT_EMPTY: 'Aún no has creado categorías de gastos.',
    EXPENSE_CAT_COL_CODE: 'Código',
    EXPENSE_CAT_COL_NAME: 'Nombre',
    EXPENSE_CAT_COL_ACCOUNT: 'Cuenta contable',
    EXPENSE_CAT_FALLBACK_BADGE: 'Predeterminado',
    EXPENSE_CAT_CREATE_TITLE: 'Crear categoría de gasto',
    EXPENSE_CAT_EDIT_TITLE: 'Editar categoría de gasto',
    EXPENSE_CAT_DELETE_CONFIRM: '¿Eliminar categoría?',
    EXPENSE_CAT_TOAST_CREATED: 'Categoría creada.',
    EXPENSE_CAT_TOAST_UPDATED: 'Categoría actualizada.',
    EXPENSE_CAT_TOAST_DELETED: 'Categoría eliminada.',
    EXPENSE_CAT_ACCOUNT_PLACEHOLDER:
      'Sin cuenta — usa el predeterminado global',
    ADJ_REASON_TITLE: 'Razones de Ajuste de Inventario',
    ADJ_REASON_SUBTITLE:
      'Códigos que clasifican ajustes (daños, mermas, vencimientos, robos) y enrutan la pérdida a su cuenta contable.',
    ADJ_REASON_NEW: 'Nueva razón',
    ADJ_REASON_EMPTY: 'Aún no has creado razones de ajuste.',
    ADJ_REASON_COL_CODE: 'Código',
    ADJ_REASON_COL_NAME: 'Nombre',
    ADJ_REASON_COL_DIRECTION: 'Dirección',
    ADJ_REASON_DIR_LOSS: 'Pérdida',
    ADJ_REASON_DIR_INCOME: 'Ingreso',
    ADJ_REASON_CREATE_TITLE: 'Crear razón de ajuste',
    ADJ_REASON_EDIT_TITLE: 'Editar razón de ajuste',
    ADJ_REASON_DELETE_CONFIRM: '¿Eliminar razón?',
    ADJ_REASON_TOAST_CREATED: 'Razón creada.',
    ADJ_REASON_TOAST_UPDATED: 'Razón actualizada.',
    ADJ_REASON_TOAST_DELETED: 'Razón eliminada.',
    CC_TITLE: 'Centros de Costos',
    CC_SUBTITLE:
      'Clasificación por departamento, proyecto o sucursal usada en asientos y reportes.',
    CC_NEW: 'Nuevo centro',
    CC_CREATE_TITLE: 'Crear centro de costo',
    CC_EDIT_TITLE: 'Editar centro de costo',
    CC_TOAST_CREATED: 'Centro creado.',
    CC_TOAST_UPDATED: 'Centro actualizado.',
    CC_TOAST_ARCHIVED: 'Centro archivado.',
    CONCEPT_TITLE: 'Conceptos Contables',
    CONCEPT_SUBTITLE:
      'Etiquetas para clasificar asientos manuales y eventos automáticos.',
    CONCEPT_NEW: 'Nuevo concepto',
    CONCEPT_CREATE_TITLE: 'Crear concepto contable',
    CONCEPT_EDIT_TITLE: 'Editar concepto contable',
    CONCEPT_TOAST_CREATED: 'Concepto creado.',
    CONCEPT_TOAST_UPDATED: 'Concepto actualizado.',
    CONCEPT_TOAST_ARCHIVED: 'Concepto archivado.',
    PT_TITLE: 'Tipos de Pago',
    PT_SUBTITLE: 'Mapea métodos de pago de caja a cuentas contables.',
    PT_NEW: 'Nuevo tipo',
    PT_CREATE_TITLE: 'Crear tipo de pago',
    PT_EDIT_TITLE: 'Editar tipo de pago',
    PT_COL_METHOD: 'Método',
    PT_COL_GL_ACCOUNT: 'Cuenta contable',
    PT_NO_GL_ACCOUNT: '— Sin cuenta contable —',
    PT_KIND_CASH: 'Efectivo',
    PT_KIND_CARD: 'Tarjeta',
    PT_KIND_TRANSFER: 'Transferencia',
    PT_KIND_CHEQUE: 'Cheque',
    PT_KIND_CREDIT: 'Crédito',
    PT_KIND_CUSTOM: 'Otro',
    PT_COL_POS_METHOD: 'Botón POS',
    PT_POS_METHOD_NONE: 'Ninguno',
    PT_POS_METHOD_CASH: 'Efectivo',
    PT_POS_METHOD_CREDIT_CARD: 'Tarjeta de Crédito',
    PT_POS_METHOD_DEBIT_CARD: 'Tarjeta de Débito',
    PT_POS_METHOD_TRANSFER: 'Transferencia',
    PT_POS_METHOD_CUSTOMER_CREDIT: 'Crédito Cliente',
    PT_TOAST_CREATED: 'Tipo de pago creado.',
    PT_TOAST_UPDATED: 'Tipo de pago actualizado.',
    PT_TOAST_ARCHIVED: 'Tipo de pago archivado.',
    FY_TITLE: 'Años Fiscales',
    FY_SUBTITLE: 'Apertura, cierre y bloqueo del año fiscal del negocio.',
    FY_NEW: 'Nuevo año fiscal',
    FY_CREATE_TITLE: 'Crear año fiscal',
    FY_START_DATE: 'Inicio',
    FY_END_DATE: 'Fin',
    FY_END_DATE_AFTER_START:
      'La fecha de fin debe ser posterior o igual a la fecha de inicio.',
    FY_STATUS_OPEN: 'Abierto',
    FY_STATUS_CLOSED: 'Cerrado',
    FY_STATUS_LOCKED: 'Bloqueado',
    FY_CLOSE: 'Cerrar año',
    FY_REOPEN: 'Reabrir año',
    FY_LOCK: 'Bloquear año',
    FY_CLOSE_CONFIRM: '¿Cerrar este año fiscal?',
    FY_CLOSE_CONFIRM_BUTTON: 'Confirmar cierre',
    FY_CLOSE_PREVIEW_TITLE: 'Previsualización de cierre — {{code}}',
    FY_CLOSE_LAST_PERIOD: 'Último período',
    FY_CLOSE_NET_INCOME: 'Resultado del ejercicio',
    FY_CLOSE_RETAINED_EARNINGS: 'Cuenta de resultados acumulados',
    FY_CLOSE_WARNINGS: 'No se puede cerrar — corrige antes de continuar:',
    FY_CLOSE_NO_ACTIVITY: 'Sin actividad en cuentas nominales este año.',
    FY_CLOSE_COL_CODE: 'Código',
    FY_CLOSE_COL_ACCOUNT: 'Cuenta',
    FY_CLOSE_COL_DIRECTION: 'Dirección',
    FY_CLOSE_COL_AMOUNT: 'Monto',
    FY_REOPEN_CONFIRM: 'Reabrir año fiscal',
    FY_REOPEN_HINT:
      'Se generará un asiento que reversa el cierre. Los períodos quedan en estado "cerrado" hasta que se reabran individualmente.',
    FY_REOPEN_REASON: 'Razón',
    FY_REOPEN_REASON_PLACEHOLDER:
      'Ajuste post-auditoría, corrección de error, etc.',
    FY_AUDIT_TITLE: 'Historial de cierre — {{code}}',
    FY_AUDIT_TITLE_SHORT: 'Historial',
    FY_AUDIT_REASON: 'Razón',
    FY_AUDIT_EMPTY: 'Sin eventos registrados para este año.',
    NTF_TITLE: 'Notificaciones Contables',
    NTF_SUBTITLE:
      'Activa o desactiva las alertas automáticas por categoría. Las alertas se envían diariamente por correo al administrador.',
    NTF_ENABLED: 'Activa',
    NTF_DISABLED: 'Inactiva',
    NTF_FIRE: 'Enviar ahora',
    NTF_FIRE_TOAST: 'Se enviaron {{count}} notificaciones.',
    NTF_PERIOD: 'Períodos contables',
    NTF_PERIOD_DESC:
      'Alerta cuando un período está por cerrarse (próximos 7 días) y aún hay actividad pendiente.',
    NTF_RECON: 'Conciliación bancaria',
    NTF_RECON_DESC:
      'Alerta cuando hay líneas bancarias sin conciliar por más de 7 días.',
    NTF_AR: 'Cuentas por cobrar',
    NTF_AR_DESC:
      'Alerta cuando hay facturas vencidas por más de 30 días, agrupadas por cliente.',
    NTF_AP: 'Cuentas por pagar',
    NTF_AP_DESC:
      'Alerta cuando hay órdenes de compra con vencimiento en los próximos 7 días.',
    NTF_BUDGET: 'Presupuestos',
    NTF_BUDGET_DESC:
      'Alertas de sobregiro y advertencia de presupuesto. Las dispara el motor en tiempo real cuando se contabiliza un asiento.',
    NTF_ASSET: 'Activos fijos',
    NTF_ASSET_DESC:
      'Alerta cuando un activo en servicio tiene más de 35 días sin depreciación registrada.',
    FY_LOCK_CONFIRM: '¿Bloquear permanentemente este año?',
    FY_TOAST_CREATED: 'Año fiscal creado.',
    FY_TOAST_CLOSED: 'Año fiscal cerrado.',
    FY_TOAST_REOPENED: 'Año fiscal reabierto.',
    FY_TOAST_LOCKED: 'Año fiscal bloqueado.',
    FY_VIEW_PERIODS: 'Ver períodos',
    FY_GENERATE_PERIODS: 'Generar períodos automáticamente',
    FY_GEN_NONE: 'No generar',
    FY_GEN_MONTHLY: 'Mensual',
    FY_GEN_QUARTERLY: 'Trimestral',
    FY_GEN_ANNUAL: 'Anual',
    PER_TITLE: 'Períodos Contables',
    PER_SUBTITLE:
      'Ventanas de contabilización dentro del año fiscal — abrir, gracia, cerrar, reabrir y bloquear.',
    PER_TYPE: 'Tipo',
    PER_FILTER_ALL_FY: 'Todos los años fiscales',
    PER_STATUS_OPEN: 'Abierto',
    PER_STATUS_GRACE: 'En gracia',
    PER_STATUS_CLOSED: 'Cerrado',
    PER_STATUS_LOCKED: 'Bloqueado',
    PER_GRACE: 'Activar gracia',
    PER_CLOSE: 'Cerrar período',
    PER_REOPEN: 'Reabrir período',
    PER_LOCK: 'Bloquear período',
    PER_GRACE_CONFIRM: '¿Pasar este período a modo gracia?',
    PER_CLOSE_CONFIRM: '¿Cerrar este período?',
    PER_REOPEN_CONFIRM: '¿Reabrir este período?',
    PER_LOCK_CONFIRM: '¿Bloquear permanentemente este período?',
    PER_UNLOCK: 'Desbloquear período',
    PER_UNLOCK_CONFIRM: '¿Desbloquear este período?',
    PER_UNLOCK_REASON_REQUIRED:
      'Debes ingresar un motivo para registrar el desbloqueo en auditoría.',
    PER_UNLOCK_REASON_LABEL: 'Motivo del desbloqueo (requerido)',
    PER_UNLOCK_REASON_PLACEHOLDER:
      'Ej: factura recibida tarde, ajuste solicitado por auditoría…',
    PER_LOCK_REASON_OPTIONAL: 'El motivo se registra en la bitácora.',
    PER_LOCK_REASON_LABEL: 'Motivo (opcional)',
    PER_LOCK_REASON_PLACEHOLDER: 'Ej: cierre fiscal Q2 firmado por contraloría',
    PER_LOCK_AUDIT: 'Historial de bloqueos',
    PER_LOCK_AUDIT_EMPTY: 'Este período no ha sido bloqueado ni desbloqueado.',
    PER_LOCK_MODE: 'Política de bloqueo',
    PER_GRACE_REMAINING: '{{days}} días de gracia restantes',
    PER_TOAST_GRACED: 'Período en modo gracia.',
    PER_TOAST_CLOSED: 'Período cerrado.',
    PER_TOAST_REOPENED: 'Período reabierto.',
    PER_TOAST_LOCKED: 'Período bloqueado.',
    PER_TOAST_UNLOCKED: 'Período desbloqueado.',
    PER_HINT_FY_REQUIRED:
      'Para crear períodos contables primero debes registrar un año fiscal. Al crear el año fiscal puedes generarlos automáticamente (mensual / trimestral / anual).',
    PER_HINT_GO_FY: 'Ir a Años Fiscales',
    PER_VIEW_MOVEMENTS: 'Ver movimientos',
    PER_GENERATE_BTN: 'Generar períodos',
    PER_GENERATE_TITLE: 'Generar períodos para un año fiscal',
    PER_GENERATE_FY_LABEL: 'Año fiscal',
    PER_GENERATE_PT_LABEL: 'Frecuencia',
    PER_GENERATE_HINT:
      'Crea los períodos del año fiscal seleccionado. Solo aplica cuando el año aún no tiene períodos.',
    PER_GENERATE_TOAST_CREATED: '{{count}} períodos generados.',
    PER_GENERATE_TOAST_SKIPPED:
      'Este año ya tiene períodos. No se generó nada.',
    CLOSE_CFG_TITLE: 'Política de Cierre',
    CLOSE_CFG_SUBTITLE:
      'Días de gracia, política de bloqueo, reapertura y comportamiento automático al cerrar/abrir períodos.',
    CLOSE_CFG_SAVED: 'Política de cierre guardada.',
    CLOSE_CFG_GRACE_DAYS_LABEL: 'Días de gracia',
    CLOSE_CFG_GRACE_DAYS_HELP:
      'Días adicionales después de que termina el período donde aún se pueden registrar asientos antes de cerrarlo.',
    CLOSE_CFG_LOCK_MODE_LABEL: 'Política de bloqueo',
    CLOSE_CFG_LOCK_MODE_FLEXIBLE: 'Flexible — permite desbloqueo con permiso',
    CLOSE_CFG_LOCK_MODE_STRICT: 'Estricta — bloqueo definitivo (auditoría)',
    CLOSE_CFG_LOCK_MODE_FLEXIBLE_HELP:
      'Los períodos bloqueados pueden desbloquearse a estado "cerrado" por un usuario con el permiso configuration.accounting.period.unlock y motivo registrado.',
    CLOSE_CFG_LOCK_MODE_STRICT_HELP:
      'Los períodos bloqueados son definitivos. Ningún usuario puede desbloquearlos. Usar solo cuando la disciplina de cierre esté consolidada.',
    CLOSE_CFG_REOPENING_LABEL: 'Permitir reapertura de períodos cerrados',
    CLOSE_CFG_REOPENING_HELP:
      'Si está activo, un usuario con permiso puede reabrir un período cerrado (vuelve a "abierto"). Se aplica solo a períodos NO bloqueados.',
    CLOSE_CFG_AUTO_OPEN_LABEL: 'Abrir período siguiente automáticamente',
    CLOSE_CFG_AUTO_OPEN_HELP:
      'Al cerrar un período, el siguiente se abre automáticamente para evitar gaps.',
    CLOSE_CFG_CARRY_LABEL: 'Trasladar saldos al período siguiente',
    CLOSE_CFG_CARRY_HELP:
      'El saldo final del período se usa como saldo inicial del siguiente (carry-forward).',
    BANK_ACCT_TITLE: 'Cuentas Bancarias',
    BANK_ACCT_SUBTITLE:
      'Registro de las cuentas bancarias del negocio. Cada cuenta se enlaza a una cuenta contable para reconciliación.',
    BANK_ACCT_NEW: 'Nueva cuenta bancaria',
    BANK_ACCT_CREATE_TITLE: 'Crear cuenta bancaria',
    BANK_ACCT_EDIT_TITLE: 'Editar cuenta bancaria',
    BANK_ACCT_BANK_LABEL: 'Banco',
    BANK_ACCT_NUMBER_LABEL: 'Número de cuenta',
    BANK_ACCT_GL_LABEL: 'Cuenta contable (GL)',
    BANK_ACCT_EMPTY: 'Aún no se han registrado cuentas bancarias.',
    BANK_ACCT_SAVED: 'Cuenta bancaria guardada.',
    BANK_ACCT_ARCHIVED: 'Cuenta bancaria archivada.',
    BANK_ACCT_ARCHIVE_CONFIRM:
      '¿Archivar esta cuenta bancaria? Podrás reactivarla luego.',
    CFG_TITLE: 'Configuración Contable',
    CFG_SUBTITLE:
      'Cuentas por defecto que el motor de reglas usa al postear eventos automáticos (ingresos, costos, AR, AP, impuestos, etc.).',
    CFG_BASE_CURRENCY: 'Moneda base',
    CFG_BASE_CURRENCY_HINT: 'Cambiar en Configuración del Negocio',
    CFG_TOAST_SAVED: 'Configuración contable guardada.',
    CFG_REVENUE_LABEL: 'Ingresos por defecto',
    CFG_REVENUE_HELP:
      'Cuenta de ingresos a la que va el crédito de cada factura cuando no hay una regla más específica.',
    CFG_COGS_LABEL: 'Costo de ventas',
    CFG_COGS_HELP:
      'Cuenta de costo a la que se carga el costo de mercancía vendida.',
    CFG_AR_LABEL: 'Cuentas por Cobrar',
    CFG_AR_HELP:
      'Cuenta auxiliar para clientes — débito en facturas a crédito.',
    CFG_AP_LABEL: 'Cuentas por Pagar',
    CFG_AP_HELP:
      'Cuenta auxiliar para proveedores — crédito en órdenes de compra a crédito.',
    CFG_RETAINED_LABEL: 'Utilidades Retenidas',
    CFG_RETAINED_HELP:
      'Cuenta de destino del cierre de año (ingresos − gastos).',
    CFG_CASH_LABEL: 'Caja / Bancos por defecto',
    CFG_CASH_HELP:
      'Cuenta de efectivo usada cuando un pago no resuelve a un tipo de pago específico.',
    CFG_ITBIS_LABEL: 'ITBIS por Pagar',
    CFG_ITBIS_HELP:
      'Cuenta de pasivo donde se acumula el ITBIS cobrado en ventas.',
    CFG_ITBIS_ADVANCE_LABEL: 'ITBIS Adelantado',
    CFG_ITBIS_ADVANCE_HELP:
      'Cuenta de activo donde se acumula el ITBIS pagado en compras (recuperable contra ITBIS por pagar al filtrar la 606).',
    CFG_ISR_LABEL: 'ISR por Pagar',
    CFG_ISR_HELP: 'Cuenta de pasivo donde se acumula el ISR provisional.',
    CFG_ISR_RETAINED_LABEL: 'ISR Retenido a Proveedores',
    CFG_ISR_RETAINED_HELP:
      'Cuenta de pasivo donde se acumula el ISR retenido a proveedores (servicios profesionales, alquileres) — distinto del ISR propio de la empresa.',
    CFG_PROPINA_LABEL: 'Propina Legal por Pagar',
    CFG_PROPINA_HELP:
      'Cuenta de pasivo donde se acumula la propina legal (10%) cobrada al cliente. NO es ingreso del negocio — se entrega al personal de servicio.',
    CFG_OPERATIONAL_REVENUE_LABEL: 'Ingresos Operacionales (Pre Factura)',
    CFG_OPERATIONAL_REVENUE_HELP:
      'Cuenta de ingresos donde se registran las ventas operacionales (Pre Factura). Separadas de los ingresos fiscales para facilitar los reportes contables filtrables.',
    CFG_OPERATIONAL_TAX_LABEL: 'ITBIS por Pagar (Operacional)',
    CFG_OPERATIONAL_TAX_HELP:
      'Cuenta de pasivo donde se acumula el ITBIS cobrado en ventas operacionales (Pre Factura). Separada del ITBIS fiscal para permitir conciliación DGII.',
    CFG_DAMAGED_GOODS_LABEL: 'Pérdida por Mercancía Dañada',
    CFG_DAMAGED_GOODS_HELP:
      'Cuenta de gasto (P&L) donde se registra la pérdida cuando un producto se marca como dañado. La contrapartida es Inventario.',
    CFG_INVENTORY_SHRINKAGE_LABEL: 'Merma de Inventario',
    CFG_INVENTORY_SHRINKAGE_HELP:
      'Cuenta de gasto (P&L) donde se registran las pérdidas por inventario extraviado o robado. La contrapartida es Inventario.',
    CFG_INVENTORY_ADJUSTMENT_LABEL: 'Ajuste de Inventario',
    CFG_INVENTORY_ADJUSTMENT_HELP:
      'Cuenta contrapartida (P&L) para ajustes manuales de inventario. Ajustes positivos acreditan aquí; negativos debitan aquí.',
    CFG_INTER_WAREHOUSE_TRANSFER_LABEL: 'Transferencia entre Almacenes',
    CFG_INTER_WAREHOUSE_TRANSFER_HELP:
      'Cuenta puente para transferencias entre almacenes cuando cada almacén usa una cuenta de inventario distinta. Se omite si ambos almacenes comparten la misma cuenta.',
    CFG_OTHER_INCOME_LABEL: 'Otros Ingresos',
    CFG_OTHER_INCOME_HELP:
      'Cuenta de ingresos (P&L) donde se acreditan los vouchers de caja de ingreso (efectivo entrante no relacionado a una venta).',
    CFG_OTHER_EXPENSE_LABEL: 'Otros Gastos',
    CFG_OTHER_EXPENSE_HELP:
      'Cuenta de gasto (P&L) donde se debitan los vouchers de caja de egreso (efectivo saliente no relacionado a una compra).',
    CFG_REVALUATION_SURPLUS_LABEL: 'Superávit por Revaluación',
    CFG_REVALUATION_SURPLUS_HELP:
      'Cuenta de patrimonio (IFRS/OCI) donde se acreditan las revaluaciones al alza de activos fijos. Mantiene la apreciación fuera de los ingresos operativos.',
    CFG_REVALUATION_LOSS_LABEL: 'Pérdida por Revaluación',
    CFG_REVALUATION_LOSS_HELP:
      'Cuenta de gasto (P&L) donde se debitan las revaluaciones a la baja de activos fijos. Separada de descuentos por devoluciones para reportes limpios.',
    CFG_IMPAIRMENT_LOSS_LABEL: 'Pérdida por Deterioro',
    CFG_IMPAIRMENT_LOSS_HELP:
      'Cuenta de gasto (P&L) donde se debita la pérdida por deterioro de activos fijos. Separada de la pérdida por revaluación para permitir análisis por estado de resultados.',
    CFG_INVENTORY_LABEL: 'Inventario',
    CFG_INVENTORY_HELP:
      'Cuenta de activo que se acredita cuando se registra el costo de la venta (contrapartida del COGS).',
    CFG_BANK_LABEL: 'Banco por defecto',
    CFG_BANK_HELP:
      'Cuenta bancaria usada cuando el cobro/pago no resuelve a una cuenta específica por tipo de pago.',
    CFG_DISCOUNT_LABEL: 'Descuentos sobre Ventas',
    CFG_DISCOUNT_HELP:
      'Cuenta contra-ingresos donde se cargan los descuentos promocionales o manuales para mantener los ingresos brutos.',
    CFG_RETURNS_LABEL: 'Devoluciones y Rebajas',
    CFG_RETURNS_HELP:
      'Cuenta contra-ingresos que se debita cuando se procesa una devolución o nota de crédito.',
    CFG_DEFAULT_EXPENSE_LABEL: 'Cuenta de Gastos por defecto',
    CFG_DEFAULT_EXPENSE_HELP:
      'Cuenta de gastos usada cuando un gasto se registra sin asignar una cuenta contable específica. Evita que los eventos queden pendientes.',
    TAM_TITLE: 'Mapeo Impuestos → Cuentas',
    TAM_SUBTITLE:
      'Una cuenta por pagar y una por cobrar para cada impuesto. El motor de reglas las usa al desglosar el ITBIS / retenciones en cada asiento.',
    TAM_COL_TAX: 'Impuesto',
    TAM_COL_PAYABLE: 'Cuenta por pagar',
    TAM_COL_RECOVERABLE: 'Cuenta recuperable',
    TAM_TOAST_SAVED: 'Mapeos guardados — {{n}} actualizados.',
    TAM_SAVE_N: 'Guardar ({{n}})',
    BF_TITLE: 'Backfill contable',
    BF_SUBTITLE:
      'Reprocesa eventos financieros históricos para que el libro mayor refleje la actividad previa a activar Contabilidad.',
    BF_NEW_RUN: 'Nuevo backfill',
    BF_SCHEDULE_TITLE: 'Programar backfill',
    BF_SCOPE_LABEL: 'Alcance',
    BF_SCOPE_ALL: 'Todos los eventos',
    BF_SCOPE_LAST_MONTHS: 'Últimos N meses',
    BF_SCOPE_FROM_DATE: 'Desde una fecha',
    BF_SCOPE_FROM_TODAY: 'Desde hoy en adelante',
    BF_MONTHS_LABEL: 'Cantidad de meses',
    BF_FROM_DATE_LABEL: 'Desde',
    BF_TO_DATE_LABEL: 'Hasta (opcional)',
    BF_SCHEDULE: 'Programar',
    BF_SCHEDULING: 'Programando…',
    BF_CANCEL: 'Cancelar',
    BF_CANCEL_CONFIRM:
      '¿Cancelar este backfill en curso? Los eventos ya procesados se mantienen; el resto queda pendiente.',
    BF_CANCELLING: 'Cancelando…',
    BF_VIEW_EVENTS: 'Ver eventos',
    BF_NO_RUNS: 'No hay backfills ejecutados todavía.',
    BF_NO_EVENTS: 'Aún no se procesaron eventos en esta ejecución.',
    BF_COL_REQUESTED_AT: 'Solicitado',
    BF_COL_SCOPE: 'Alcance',
    BF_COL_STATUS: 'Estado',
    BF_COL_PROGRESS: 'Progreso',
    BF_COL_POSTED: 'Posteados',
    BF_COL_FAILED: 'Fallidos',
    BF_RUN_STATUS_QUEUED: 'En cola',
    BF_RUN_STATUS_RUNNING: 'Ejecutando',
    BF_RUN_STATUS_COMPLETED: 'Completado',
    BF_RUN_STATUS_FAILED: 'Falló',
    BF_RUN_STATUS_CANCELLED: 'Cancelado',
    BF_EVENT_STATUS_QUEUED: 'En cola',
    BF_EVENT_STATUS_PROCESSED: 'Procesado',
    BF_EVENT_STATUS_POSTED: 'Posteado',
    BF_EVENT_STATUS_FAILED: 'Falló',
    BF_EVENT_STATUS_SKIPPED: 'Saltado',
    BF_EVENT_COL_FINANCIAL_EVENT: 'Evento financiero',
    BF_EVENT_COL_JE: 'Asiento',
    BF_EVENT_COL_STATUS: 'Estado',
    BF_EVENT_COL_ERROR: 'Detalle',
    BF_DETAIL_ALREADY_POSTED: 'Ya estaba posteado — no se reprocesó.',
    BF_DETAIL_NOT_FOUND: 'Evento no encontrado.',
    BF_DETAIL_ACCOUNTING_DISABLED:
      'Contabilidad inactiva para este negocio cuando el evento se procesó.',
    BF_DETAIL_NO_RULE:
      'No hay regla de contabilización activa para este tipo de evento. Configurá una en Reglas de Contabilización.',
    BF_DETAIL_NO_OPEN_PERIOD:
      'No hay un período abierto que cubra la fecha del evento. Abrí el período correspondiente.',
    BF_DETAIL_FY_CLOSED:
      'El año fiscal del período del evento está cerrado. Reabrí el año o ajustá el alcance.',
    BF_DETAIL_CATCHUP_REEMIT:
      'Evento de re-emisión (catchup) — solo se postea desde el backfill.',
    BF_DETAIL_UNKNOWN: 'Error: {{message}}',
    BF_FILTER_STATUS: 'Filtrar por estado',
    BF_FILTER_ALL_STATUSES: 'Todos los estados',
    BF_TOAST_SCHEDULED: 'Backfill programado — verá el progreso abajo.',
    BF_TOAST_CANCELLED: 'Backfill cancelado.',
    BF_PROGRESS_LABEL: '{{processed}} / {{total}}',
    BF_RUN_DETAIL_TITLE: 'Ejecución de backfill',
    BF_RUN_DETAIL_SUBTITLE:
      'Detalle por evento — usa el filtro de estado para ver fallos o saltados.',
    BF_NEEDS_ACCOUNTING_HINT:
      'Activá Contabilidad en el plan del negocio antes de programar un backfill. Sin esto el motor no procesa los eventos históricos.',
    FAC_TITLE: 'Categorías de Activos Fijos',
    FAC_SUBTITLE:
      'Define la vida útil + el método de depreciación por defecto para cada tipo de activo. Los activos creados con la categoría heredan estos valores.',
    FAC_NEW: 'Nueva categoría',
    FAC_CREATE_TITLE: 'Crear categoría',
    FAC_EDIT_TITLE: 'Editar categoría',
    FAC_COL_CODE: 'Código',
    FAC_COL_NAME: 'Nombre',
    FAC_COL_LIFE: 'Vida útil (m)',
    FAC_COL_METHOD: 'Método',
    FAC_FIELD_LIFE: 'Vida útil por defecto (meses)',
    FAC_FIELD_METHOD: 'Método de depreciación por defecto',
    FAC_FIELD_ASSET_ACC: 'Cuenta del activo (obligatoria)',
    FAC_FIELD_ACC_DEP_ACC: 'Cuenta de depreciación acumulada (obligatoria)',
    FAC_FIELD_DEP_EXP_ACC: 'Cuenta de gasto de depreciación (obligatoria)',
    FAC_FIELD_DESCRIPTION: 'Descripción',
    FAC_METHOD_STRAIGHT: 'Línea recta',
    FAC_METHOD_DECLINING: 'Saldos decrecientes',
    FAC_TOAST_CREATED: 'Categoría creada.',
    FAC_TOAST_UPDATED: 'Categoría actualizada.',
    FAC_TOAST_DELETED: 'Categoría eliminada.',
    FAC_DELETE_CONFIRM:
      '¿Eliminar esta categoría? Los activos asociados conservan sus valores actuales.',
    FAC_EMPTY: 'Sin categorías. Crea una para empezar.',
    BF_LEAVE_TITLE: 'Backfill en ejecución',
    BF_LEAVE_CONFIRM:
      'Hay un backfill ejecutándose y esta página es la única que muestra el progreso en vivo. Si salís, el proceso continúa en el servidor pero ya no verás el avance hasta que vuelvas. ¿Querés salir de todas formas?',
    BF_LEAVE_STAY: 'Quedarme',
    BF_LEAVE_GO: 'Salir',
    BF_POLLING_BANNER:
      'Sondeo activo cada 2 s mientras hay backfills en cola o ejecutándose. Cierra esta página para detener el sondeo (el backfill sigue ejecutándose en el servidor).',
    PR_TITLE: 'Motor de Reglas Contables',
    PR_SUBTITLE:
      'Define cómo cada evento financiero se convierte en un asiento contable. Cada regla versiona sus líneas; sólo una versión está activa a la vez.',
    PR_NEW: 'Nueva regla',
    PR_CREATE_TITLE: 'Crear regla contable',
    PR_EDIT_TITLE: 'Editar regla contable',
    PR_COL_NAME: 'Nombre',
    PR_COL_SOURCE: 'Módulo',
    PR_COL_SUBTYPE: 'Subtipo',
    PR_COL_PRIORITY: 'Prioridad',
    PR_COL_STATUS: 'Estado',
    PR_COL_ACTIVE_VERSION: 'Versión activa',
    PR_COL_DRAFT_VERSION: 'Borrador',
    PR_FIELD_RULE_NAME: 'Nombre',
    PR_FIELD_SOURCE_MODULE: 'Módulo origen',
    PR_FIELD_SOURCE_SUBTYPE: 'Subtipo (ej. Pago recibido)',
    PR_FIELD_PRIORITY: 'Prioridad',
    PR_FIELD_BRANCH: 'Sucursal (opcional)',
    PR_FIELD_DESCRIPTION: 'Descripción',
    PR_VERSION_NEW: 'Nueva versión',
    PR_VERSION_CREATE_TITLE: 'Crear nueva versión',
    PR_VERSION_NOTES: 'Notas',
    PR_VERSION_NUMBER: '#',
    PR_VERSION_STATUS: 'Estado',
    PR_VERSION_STATUS_DRAFT: 'Borrador',
    PR_VERSION_STATUS_ACTIVE: 'Activa',
    PR_VERSION_STATUS_SUPERSEDED: 'Reemplazada',
    PR_STATUS_ACTIVE: 'Activa',
    PR_STATUS_INACTIVE: 'Inactiva',
    PR_DIRECTION_DEBIT: 'Débito',
    PR_DIRECTION_CREDIT: 'Crédito',
    PR_VERSION_PUBLISH: 'Publicar',
    PR_VERSION_PUBLISH_CONFIRM:
      '¿Publicar esta versión? La activa actual quedará reemplazada inmediatamente.',
    PR_VERSION_PUBLISHED_AT: 'Publicada',
    PR_VERSION_ADD_LINE: 'Agregar línea',
    PR_LINE_DIRECTION: 'Tipo',
    PR_LINE_ACCOUNT_RESOLVER: 'Resolución de cuenta',
    PR_LINE_ACCOUNT: 'Cuenta',
    PR_LINE_CONFIG_KEY: 'Clave de configuración',
    PR_LINE_JSON_PATH: 'JSONPath',
    PR_LINE_AMOUNT_RESOLVER: 'Resolución de monto',
    PR_LINE_AMOUNT_PATH: 'Ruta del monto',
    PR_LINE_AMOUNT_STATIC: 'Monto fijo',
    PR_LINE_DESCRIPTION: 'Descripción',
    PR_LINE_COST_CENTER_PATH: 'JSONPath centro de costo',
    PR_RESOLVER_STATIC: 'Estática',
    PR_RESOLVER_CONFIG: 'Config',
    PR_RESOLVER_TAX: 'Mapa de impuesto',
    PR_RESOLVER_PAYMENT_TYPE: 'Tipo de pago',
    PR_RESOLVER_JSONPATH: 'JSONPath',
    PR_RESOLVER_SUM: 'Suma',
    PR_SANDBOX_TITLE: 'Sandbox de resolución',
    PR_SANDBOX_PAYLOAD: 'Carga de prueba (JSON)',
    PR_SANDBOX_RUN: 'Resolver',
    PR_SANDBOX_RESULT_OK: 'Resolución exitosa',
    PR_SANDBOX_RESULT_ERR: 'Error de resolución',
    PR_SANDBOX_BALANCED: 'Líneas balanceadas',
    PR_SANDBOX_UNBALANCED: 'Líneas no balanceadas',
    PR_SANDBOX_HELP_TITLE: 'Cómo usar este sandbox',
    PR_SANDBOX_HELP_WHY:
      'Este sandbox simula la regla con datos de prueba sin afectar tu contabilidad real. Úsalo para verificar que las cuentas y montos se resuelvan correctamente ANTES de publicar la versión y emitir asientos automáticos.',
    PR_SANDBOX_HELP_STEP_1:
      'Edita la carga JSON para que se parezca al evento real (factura, pago, etc.).',
    PR_SANDBOX_HELP_STEP_2:
      'Pulsa "Resolver" para ejecutar la regla contra esa carga.',
    PR_SANDBOX_HELP_STEP_3:
      'Revisa que las cuentas, direcciones (DÉBITO/CRÉDITO) y montos coincidan con lo esperado. Si hay error, lee el mensaje y ajusta la regla o el JSON.',
    PR_SEED_DEFAULTS: 'Insertar reglas por defecto',
    PR_SEED_ALL: 'Instalar todas',
    PR_MISSING_TITLE: 'Reglas por defecto que faltan',
    PR_MISSING_DESCRIPTION:
      'Cada regla lista abajo tiene una plantilla lista para usar. Instálalas una por una o todas a la vez — son idempotentes: si ya existe, se omite.',
    PR_INSTALL_DEFAULT: 'Instalar regla por defecto',
    PR_SEED_TOAST:
      'Insertado — {{created}} reglas creadas, {{skipped}} omitidas.',
    PR_EMPTY:
      'No hay reglas contables todavía. Pulsa Insertar reglas por defecto” para empezar.',
    PR_NO_VERSIONS:
      'Esta regla aún no tiene versiones. Crea una versión inicial con sus líneas para activarla.',
    PR_VERSION_PUBLISH_ON_CREATE: 'Publicar y activar al guardar',
    PR_LINE_VALIDATION_BALANCE:
      'Debe haber al menos una línea de débito y una de crédito.',
    PR_HELP_TITLE: '¿Para qué sirve una versión de regla contable?',
    PR_HELP_WHAT_IS:
      'Una regla contable convierte un evento del sistema (factura, pago, gasto, compra) en un asiento contable balanceado. Las versiones permiten cambiar el cómo se contabiliza sin perder el historial: las versiones anteriores siguen vinculadas a los asientos que ya generaron.',
    PR_HELP_WHY:
      'Sin una versión activa con líneas, el motor no sabe qué cuentas debitar/acreditar — por eso aparece el mensaje "No hay regla de contabilización activa" en la vista del documento.',
    PR_HELP_FLOW_TITLE: 'Flujo de contabilización',
    PR_HELP_FLOW_STEP_1:
      '1. El usuario crea un documento (ej. factura cobrada en efectivo).',
    PR_HELP_FLOW_STEP_2:
      '2. El sistema emite un evento (sourceModule + sourceSubtype, ej. invoice + invoice.issued.cash).',
    PR_HELP_FLOW_STEP_3:
      '3. El motor busca la regla activa de ese evento y aplica sus líneas — cada línea resuelve una cuenta y un monto desde el documento.',
    PR_HELP_FLOW_STEP_4:
      '4. Se crea el asiento contable (journal_entry) balanceado y se refleja en el Mayor General + reportes.',
    PR_HELP_EXAMPLE_TITLE: 'Ejemplo: factura cobrada en efectivo',
    PR_HELP_EXAMPLE_BODY:
      'Débito: Cuenta de Caja (monto = total). Crédito: Ingresos por Ventas (monto = subtotal). Crédito: ITBIS por Pagar (monto = tax). Las tres líneas suman a cero (débito = crédito) y el asiento queda balanceado automáticamente.',
    PR_HELP_RESOLVER_NOTE:
      'Resolvers — static: cuenta fija que eliges del catálogo. config: cuenta global desde Configuración → Contabilidad → Predeterminados. jsonpath/tax/payment_type: cuenta dinámica leída del documento (ej. payment.account_id).',
    PR_SANDBOX_CHEATSHEET_TITLE:
      'Guía del sandbox — cómo armar el payload y entender el resultado',
    PR_SANDBOX_PAYLOAD_REF_TITLE: 'Campos del payload por tipo de evento',
    PR_SANDBOX_PAYLOAD_REF_INTRO:
      'El payload simula el documento real que el sistema enviará al motor. Cada línea de la regla usa rutas JSONPath (ej. $.total) para leer un valor del payload. Estas son las rutas esperadas por tipo de evento:',
    PR_SANDBOX_EXAMPLES_TITLE: 'Ejemplos listos para probar',
    PR_SANDBOX_EXAMPLE_LOAD: 'Cargar ejemplo',
    PR_SANDBOX_EXAMPLE_INVOICE_CASH: 'Factura cobrada en efectivo',
    PR_SANDBOX_EXAMPLE_INVOICE_CREDIT: 'Factura a crédito',
    PR_SANDBOX_EXAMPLE_PAYMENT_RECEIVED: 'Pago recibido',
    PR_SANDBOX_EXAMPLE_PURCHASE_ORDER: 'Orden de compra recibida',
    PR_SANDBOX_ERRORS_TITLE: 'Errores comunes y cómo resolverlos',
    PR_SANDBOX_ERROR_UNDEFINED_HINT:
      '"$.X → undefined" — la línea usa una ruta JSONPath que no existe en el payload. Verifica que el campo esté escrito igual (mayúsculas / minúsculas / corchetes para arrays).',
    PR_SANDBOX_ERROR_UNBALANCED_HINT:
      '"Líneas no balanceadas" — los débitos no igualan a los créditos. Revisa que cada línea use el monto correcto (ej. total vs subtotal vs tax) y que sumen al total del documento.',
    PR_SANDBOX_ERROR_TAX_MAP_HINT:
      '"Mapa de impuestos faltante" — el resolver tax necesita un mapeo en Configuración → Contabilidad → Impuestos para vincular el ITBIS con su cuenta de pasivo.',
    PR_SEED_NAME_CASH_INVOICE: 'Factura al contado → ingresos + ITBIS',
    PR_SEED_NAME_CREDIT_INVOICE: 'Factura a crédito → CxC + ingresos + ITBIS',
    PR_SEED_NAME_OP_CASH_INVOICE:
      'Pre Factura al contado → ingresos operacionales + ITBIS operacional',
    PR_SEED_NAME_OP_CREDIT_INVOICE:
      'Pre Factura a crédito → CxC + ingresos operacionales + ITBIS operacional',
    PR_SEED_NAME_OP_FALLBACK_INVOICE:
      'Pre Factura (fallback) → ingresos operacionales + ITBIS operacional',
    PR_SEED_NAME_CUSTOMER_PAYMENT: 'Pago recibido de cliente',
    PR_SEED_NAME_PURCHASE_ORDER_RECEIVED:
      'Orden de compra recibida → inventario + CxP',
    PR_SEED_NAME_VENDOR_PAYMENT: 'Pago realizado a proveedor',
    PR_SEED_NAME_EXPENSE_RECORDED: 'Gasto registrado → gasto + caja',
    PR_SEED_NAME_STOCK_MOVEMENT:
      'Movimiento de inventario → costo + inventario',
    PR_SEED_NAME_REFUND: 'Reembolso / nota de crédito',
    PR_SEED_NAME_CREDIT_TERM_FISCAL_INVOICE:
      'Factura fiscal a crédito → CxC + ingresos + ITBIS',
    PR_SEED_NAME_SUPPLIER_CREDIT_NOTE:
      'Nota de crédito de proveedor → CxP + reversión del gasto',
    PR_SEED_NAME_STOCK_DAMAGED:
      'Inventario dañado → pérdida por daño + inventario',
    PR_SEED_NAME_STOCK_LOST: 'Inventario perdido → merma + inventario',
    PR_SEED_NAME_STOCK_ADJUSTMENT:
      'Ajuste de inventario → inventario ± ajuste de inventario',
    PR_SEED_NAME_STOCK_INITIAL:
      'Inventario inicial → inventario + utilidades retenidas',
    PR_SEED_NAME_CUSTOMER_OPENING_BALANCE:
      'Saldo inicial de cliente → CxC + utilidades retenidas',
    PR_SEED_NAME_PROVIDER_OPENING_BALANCE:
      'Saldo inicial de proveedor → utilidades retenidas + CxP',
    PR_SEED_NAME_CV_INGRESO: 'Vale de caja (ingreso) → caja + otros ingresos',
    PR_SEED_NAME_CV_EGRESO: 'Vale de caja (egreso) → otros gastos + caja',
    PR_SEED_NAME_INVOICE_CANCELLED:
      'Factura cancelada → reversión de ingresos + ITBIS / caja + CxC',
    PR_SEED_NAME_OP_INVOICE_CANCELLED:
      'Pre Factura cancelada → reversión de ingresos operacionales + ITBIS operacional / caja + CxC',
    PR_SEED_NAME_CN_ISSUED:
      'Nota de crédito emitida → devoluciones + ITBIS / CxC (sin aprobación)',
    PR_SEED_NAME_CN_APPROVED:
      'Nota de crédito aprobada → devoluciones + ITBIS / CxC',
    PR_SEED_NAME_REFUND_COMPLETED: 'Reembolso completado → devoluciones / caja',
    PR_SEED_NAME_PAYMENT_REFUNDED: 'Pago reembolsado → CxC / caja',
    PR_SEED_NAME_FA_SOLD:
      'Activo fijo vendido → caja + ganancia o pérdida / activo (valor neto)',
    PR_SEED_NAME_FA_DISPOSED: 'Activo fijo dado de baja → pérdida / activo',
    PR_SEED_NAME_FA_SCRAPPED:
      'Activo fijo desechado o perdido → pérdida / activo',
    PR_SEED_NAME_FA_LOST: 'Activo fijo perdido → pérdida / activo',
    PR_SEED_NAME_FA_ACQUIRED:
      'Activo fijo adquirido → capitalización del activo + CxP',
    PR_SEED_NAME_FA_DEPRECIATED:
      'Depreciación de activo fijo → gasto + depreciación acumulada',
    PR_SEED_NAME_FA_REVALUATION:
      'Revaluación de activo fijo → superávit (patrimonio) o pérdida (resultados)',
    PR_SEED_NAME_FA_IMPAIRMENT:
      'Deterioro de activo fijo → pérdida por deterioro / activo',
    PR_SEED_NAME_SUPPLIER_PAYMENT:
      'Pago a proveedor → abono a CxP + retenciones',
    PR_SEED_NAME_PROCUREMENT_PO_RECEIVED:
      'OC de abastecimiento recibida → inventario + CxP',
    PR_SEED_NAME_SHIFT_CLOSED:
      'Cierre de turno → diferencia de caja (sobrante o faltante)',
    PR_SEED_NAME_AR_WRITE_OFF:
      'Castigo de cuenta por cobrar → cuentas incobrables + CxC',
    PR_SEED_NAME_ROUNDING_OVERPAID:
      'Redondeo (el cliente pagó más) → CxC + ingreso por ajuste',
    PR_SEED_NAME_ROUNDING_WRITTEN_OFF:
      'Redondeo (el cliente pagó menos) → gasto por ajuste + CxC',
    PR_TOAST_CREATED: 'Regla creada.',
    PR_TOAST_UPDATED: 'Regla actualizada.',
    PR_TOAST_ARCHIVED: 'Regla archivada.',
    PR_TOAST_VERSION_CREATED: 'Versión creada (borrador).',
    PR_TOAST_VERSION_PUBLISHED: 'Versión publicada.',
    PR_ARCHIVE_CONFIRM:
      '¿Archivar esta regla? Dejará de aplicarse a nuevos eventos.',
  },
  ACCOUNTING_ROUTING: {
    ROUTING_TITLE: 'Contabilización',
    ROUTING_EDIT_BTN: 'Editar contabilización',
    ROUTING_EDITOR_TITLE: 'Editar enrutamiento contable',
    ROUTING_SAVED: 'Enrutamiento guardado.',
    ROUTING_EMPTY: '—',
    ROUTING_FALLBACK_HINT: 'Predeterminado: {{code}} — {{name}}',
    ROUTING_CLEAR_HINT: 'Deja vacío para usar el predeterminado global.',
    ROUTING_OVERRIDE_HINT:
      'Esta entidad sobrescribe {{count}} cuenta(s) predeterminada(s).',
    ROUTING_ROLE_REVENUE: 'Ingresos',
    ROUTING_ROLE_COGS: 'Costo de ventas',
    ROUTING_ROLE_INVENTORY: 'Inventario',
    ROUTING_ROLE_RETURNS: 'Devoluciones',
    ROUTING_ROLE_EXPENSE: 'Gasto',
    ROUTING_ROLE_LOSS: 'Pérdida',
    ROUTING_ROLE_ASSET: 'Activo',
    ROUTING_ROLE_ACCUMULATED_DEPRECIATION: 'Depreciación acumulada',
    ROUTING_ROLE_DEPRECIATION_EXPENSE: 'Gasto por depreciación',
    ROUTING_SOURCE_GLOBAL_DEFAULT: 'Predeterminado',
    ROUTING_SOURCE_CATEGORY_OVERRIDE: 'Categoría',
    ROUTING_SOURCE_ENTITY_OVERRIDE: 'Entidad',
    ROUTING_SOURCE_SNAPSHOT: 'Snapshot',
    ROUTING_SOURCE_MANUAL: 'Manual',
    ROUTING_SOURCE_LEGACY: 'Histórico',
    PREVIEW_TITLE: 'Vista contable',
    PREVIEW_ERROR_TITLE: 'No se pudo generar la vista contable',
    PREVIEW_UNBALANCED_TITLE: 'Asiento contable desbalanceado',
    PREVIEW_UNBALANCED_BODY:
      'Los débitos y créditos resueltos por la regla no coinciden. Ajusta los montos en la tabla y guarda para registrar el asiento manualmente.',
    PREVIEW_BALANCED: 'Balanceado',
    PREVIEW_UNBALANCED: 'Desbalanceado',
    PREVIEW_OPERATIONAL_TITLE: 'Alcance operacional',
    PREVIEW_OPERATIONAL_BODY:
      'Este documento tiene alcance operacional. Genera asiento contable en cuentas operacionales separadas — visible en reportes cuando el filtro incluye alcance operacional.',
    PREVIEW_SCOPE_OPERATIONAL: 'Operacional',
    PREVIEW_COL_ACCOUNT: 'Cuenta',
    PREVIEW_COL_DEBIT: 'Débito',
    PREVIEW_COL_CREDIT: 'Crédito',
    PREVIEW_COL_SOURCE: 'Origen',
    PREVIEW_NO_RULE_STEPS_TITLE:
      'Para que este documento se contabilice, define una regla:',
    PREVIEW_NO_RULE_STEP_1:
      'Abre Configuración → Contabilidad → Reglas de contabilización.',
    PREVIEW_NO_RULE_STEP_2: 'Pulsa "Nueva regla" y dale un nombre descriptivo.',
    PREVIEW_NO_RULE_STEP_3:
      'Selecciona el módulo origen y el subtipo (ej. purchase_order → purchase_order.received).',
    PREVIEW_NO_RULE_STEP_4:
      'Agrega las líneas de débito y crédito con sus cuentas y la base del cálculo.',
    PREVIEW_NO_RULE_STEP_5:
      'Publica la versión y márcala como activa para empezar a contabilizar.',
    PREVIEW_NO_RULE_GO_BTN: 'Ir a Reglas de contabilización',
  },
  CONFIGURATION_ACCOUNTING_SCOPE: {
    TITLE: 'Alcance Contable',
    SUBTITLE:
      'Decide qué documentos se contabilizan en el Mayor General y cuáles solo se manejan como operativos.',
    TOGGLE_LABEL: 'Transacciones operacionales',
    TOGGLE_ON_HELP:
      'Activado: los documentos operativos se generan y se muestran en el Mayor junto a los contables.',
    TOGGLE_OFF_HELP:
      'Desactivado: los documentos operativos se ocultan del Mayor, reportes y dashboards (siguen existiendo en operaciones).',
    MAPPING_TITLE: 'Clasificación por tipo de documento',
    MAPPING_HELP:
      'Selecciona si cada tipo se considera contable u operativo. Cada fila aplica tanto a la serie B (papel) como a la serie E (e-CF) — comparten el mismo tipo de documento. Los tipos fiscales DGII siempre son contables.',
    DOCUMENT_KIND: 'Familia',
    DOCUMENT_TYPE: 'Tipo',
    SCOPE: 'Alcance',
    SCOPE_ACCOUNTING: 'Contable',
    SCOPE_OPERATIONAL: 'Operativo',
    PINNED_FISCAL: 'Fijo (DGII)',
    EMPTY: 'No hay reglas de mapeo cargadas todavía.',
    SAVED: 'Mapeo actualizado.',
  },
  FORGOT_PASSWORD: {
    FORGOT_PASSWORD: '¿Olvidó su contraseña?',
    ADD_EMAIL_STABLISH_PASSWORD:
      'Ingrese su correo electrónico y le enviaremos un enlace para restablecer su contraseña',
    EMAIL_PLACEHOLDER: 'Correo electrónico',
    EMAIL_INPUT_PLACEHOLDER: 'correo@dominio.com',
    SEND_LINK: 'Enviar enlace de recuperación',
    BACK_TO_LOGIN: 'Volver al Login',
    CHECK_YOUR_EMAIL: 'Revisa tu correo electrónico',
    EMAIL_SENT_DESCRIPTION:
      'Hemos enviado un enlace para restablecer tu contraseña a {{email}}. El enlace expirará en 1 hora.',
    DIDNT_RECEIVE_EMAIL: '¿No recibiste el correo?',
    CHECK_SPAM_FOLDER:
      'Revisa tu carpeta de spam o correo no deseado. Si aún no lo encuentras, intenta reenviar el enlace.',
    RESEND_EMAIL: 'Reenviar correo',
    RESEND_COOLDOWN: 'Reenviar en {{seconds}}s',
    EMAIL_RESENT: 'El correo de recuperación ha sido reenviado',
    ACCOUNT_LOCKED:
      'Tu cuenta está bloqueada por múltiples intentos fallidos. Contacta a tu administrador.',
  },
  RESET_PASSWORD: {
    PASSWORD: 'contraseña',
    CONFIRM_PASSWORD: 'Confirmar contraseña',
    RESET_YOUR_PASSWORD: 'Reinicia tu contraseña',
    INSERT_YOUR_PASSWORD:
      'Ingresa tu nueva contraseña y repite para reiniciar tu contraseña.',
    CHANGE_PASSWORD: 'Cambiar contraseña',
    CANCEL: 'Cancelar',
    INVALID_TOKEN: 'El enlace de recuperación es inválido o ha expirado',
    ERROR_RESETTING_PASSWORD:
      'Ocurrió un error al restablecer tu contraseña. Por favor intenta de nuevo.',
    INVALID_LINK_TITLE: 'Enlace inválido',
    INVALID_LINK_DESCRIPTION:
      'El enlace para restablecer tu contraseña es inválido o ha expirado. Por favor solicita un nuevo enlace.',
    REQUEST_NEW_LINK: 'Solicitar nuevo enlace',
    PASSWORD_CHANGED_TITLE: '¡contraseña actualizada!',
    PASSWORD_CHANGED_DESCRIPTION:
      'Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión con tu nueva contraseña.',
    GO_TO_LOGIN: 'Ir al inicio de sesión',
    PASSWORD_REQUIREMENTS_TITLE: 'Requisitos de contraseña',
    PASSWORD_REQUIREMENTS:
      'Mínimo 8 caracteres, al menos una mayúscula, una minúscula y un número o carácter especial.',
    PASSWORD_LOWERCASE_REQUIRED:
      'La contraseña debe contener al menos una letra minúscula',
    PASSWORD_UPPERCASE_REQUIRED:
      'La contraseña debe contener al menos una letra mayúscula',
    PASSWORD_NUMBER_REQUIRED:
      'La contraseña debe contener al menos un número o carácter especial',
  },
  VERIFY_EMAIL: {
    VERIFYING_EMAIL: 'Verificando tu correo',
    PLEASE_WAIT: 'Por favor espera mientras verificamos tu correo...',
    EMAIL_VERIFIED: '¡Tu correo ha sido verificado!',
    REDIRECTING_TO_ONBOARDING:
      'Serás redirigido a la página de incorporación en unos momentos...',
    REDIRECTING_TO_LOGIN:
      'Tu cuenta ya está configurada. Serás redirigido al inicio de sesión...',
    VERIFICATION_FAILED: 'Falló la verificación del correo',
    BACK_TO_REGISTRATION: 'Volver al registro',
    NO_TOKEN_PROVIDED: 'No se proporcionó un token de verificación',
  },
  EMAIL_TEMPLATES: {
    WELCOME: {
      SUBJECT: 'Registro Neo App',
      GREETING: 'Bienvenido a Neo App 👋',
      ACCOUNT_CREATED: 'Tu cuenta ha sido creada exitosamente.',
      GO_TO_DASHBOARD: 'Ir al Panel Principal',
      DISCLAIMER:
        'Si no creaste esta cuenta, por favor contacta a nuestro equipo de soporte. info@neogeniux.com',
    },
    VERIFICATION: {
      SUBJECT: 'Verifica tu Correo Electrónico',
      TITLE: 'Verifica tu Correo Electrónico ✉️',
      GREETING: 'Hola,',
      BODY: 'Por favor confirma tu dirección de correo electrónico para activar tu cuenta.',
      BUTTON_TEXT: 'Verificar Correo',
      LINK_INSTRUCTION: 'O copia y pega este enlace en tu navegador:',
      EXPIRY: 'Este enlace expirará en 24 horas.',
    },
    PASSWORD_RESET: {
      SUBJECT: 'Restablecer tu contraseña',
      TITLE: 'Restablecer tu contraseña 🔒',
      GREETING: 'Hola,',
      BODY: 'Haz clic en el botón de abajo para restablecer tu contraseña.',
      BUTTON_TEXT: 'Restablecer contraseña',
      LINK_INSTRUCTION: 'O copia y pega este enlace en tu navegador:',
      DISCLAIMER: 'Si no solicitaste esto, simplemente ignora este correo.',
    },
    ACCOUNT_EXISTS: {
      SUBJECT: 'Intento de Registro de Cuenta',
      TITLE: 'Intento de Registro de Cuenta 🔐',
      GREETING: 'Hola,',
      BODY: 'Alguien (esperamos que tú) intentó crear una nueva cuenta con esta dirección de correo electrónico.',
      LOGIN_METHOD_EMAIL: 'tu correo y contraseña',
      LOGIN_METHOD_GOOGLE: 'Iniciar sesión con Google',
      ACCOUNT_EXISTS_TEXT:
        'Ya tienes una cuenta en Neo App. Para acceder, por favor usa',
      BUTTON_TEXT: 'Ir a Iniciar Sesión',
      DISCLAIMER:
        'Si no intentaste registrarte, puedes ignorar este correo con seguridad.',
      FORGOT_PASSWORD_HINT:
        'Si olvidaste tu contraseña, puedes restablecerla desde la página de inicio de sesión.',
    },
  },
  DASHBOARD: {
    DATE_FILTER_LABEL: 'Resumen del:',
    DATE_FILTER_LABEL_RANGE: 'Resumen entre:',
    DATE_RANGE_LABEL: 'Filtrar por rango de fechas',
    DATE_RANGE_FROM: 'Desde',
    DATE_RANGE_TO: 'Hasta',
    DATE_RANGE_APPLY: 'Aplicar',
    DATE_RANGE_CLEAR: 'Limpiar',
    DATE_RANGE_BUTTON: 'Rango',
    DATE_RANGE_BADGE: 'Rango activo',
    // AR/AP dashboard tile. Rendered only for tenants holding the
    // Finanzas module perms (finanzas.ar.view / finanzas.ap.view).
    AR_AP_TITLE: 'Cuentas por Cobrar y Pagar',
    AR_AP_RECEIVABLE: 'Te deben (por cobrar)',
    AR_AP_PAYABLE: 'Debes (por pagar)',
    AR_AP_NET: 'Balance neto',
    AR_AP_AS_OF: 'Saldos abiertos al día de hoy.',
    AR_AP_OVERDUE: 'Vencido',
    // Oldest bucket carrying a balance. `/aging` aggregates per cliente /
    // proveedor, so these name the age range, not an invoice number.
    AR_AP_BUCKET_91: 'Más de 90 días',
    AR_AP_BUCKET_61: '61 a 90 días',
    AR_AP_BUCKET_31: '31 a 60 días',
    AR_AP_BUCKET_0: 'Hasta 30 días',
    OPEN_TABLES_TITLE: 'Mesas Abiertas',
    OPEN_TABLES_EMPTY: 'No hay mesas abiertas en este momento.',
    OPEN_TABLES_COUNT: 'Mesas',
    OPEN_TABLES_GUESTS: 'Comensales',
    OPEN_TABLES_TOTAL: 'Total en mesas abiertas',
    OPEN_TABLES_ITEMS: '{{count}} ítems',
    OPEN_TABLES_ITEMS_AT: 'Abierta desde {{time}}',
    SUMMARY: 'Resumen',
    SEE_BRANCH: 'Ver sucursal',
    NO_BRANCH: 'No existe la sucursal',
    TOTAL_TODAY_SALES: 'Ventas',
    TOTAL_TODAY_CREDIT_NOTES: 'Notas de Crédito',
    TOTAL_VS: 'vs ayer',
    HELP_TOTAL_TODAY_SALES:
      'Suma del total (con ITBIS) de las facturas emitidas hoy. Coincide con la columna "Total" de la lista de facturas para el mismo día. Excluye cotizaciones, notas de crédito, borradores y facturas canceladas. Un pago recibido hoy sobre una factura anterior NO cuenta aquí.',
    HELP_TOTAL_PROFIT:
      'Ganancia realizada hoy = (precio − costo) × cantidad, prorrateada por los pagos recibidos hoy. Un pago parcial suma solo su parte del margen; el pago que cierra la factura recibe únicamente el margen que le corresponde. Excluye notas de crédito y cotizaciones.',
    HELP_TOTAL_EXPENSES:
      'Suma de los gastos registrados hoy (fecha del gasto), excluyendo gastos cancelados.',
    HELP_TOTAL_INVOICES:
      'Cantidad de facturas emitidas hoy (fiscal_credit, consumo, invoice). Excluye cotizaciones y notas de crédito.',
    HELP_TOTAL_TODAY_CREDIT_NOTES:
      'Suma del total (con ITBIS) de las notas de crédito emitidas hoy. Se muestra solo cuando hay actividad de N/C hoy o ayer.',
    NET_SALES: 'Ventas Netas',
    GROSS_SALES_LABEL: 'Ventas Brutas',
    NET_PROFIT: 'Ganancias Netas',
    GROSS_PROFIT_TILE_LABEL: 'Ganancias Brutas',
    CREDIT_NOTES_BREAKDOWN_LABEL: 'Notas de Crédito',
    AVERAGE_TICKET_TILE: 'Ticket Promedio',
    INVOICES_ISSUED_TILE: 'Facturas Emitidas',
    ELECTRONIC_INVOICES_LABEL: 'Electrónicos',
    NORMAL_INVOICES_LABEL: 'Normales',
    HELP_NET_SALES:
      'Ventas brutas del periodo menos el total de notas de crédito emitidas en el mismo periodo. Refleja el ingreso real después de devoluciones.',
    HELP_NET_PROFIT:
      'Ganancia realizada menos el margen de las notas de crédito emitidas en el periodo. Excluye ITBIS.',
    HELP_AVERAGE_TICKET:
      'Ventas Netas dividido por el número de facturas emitidas (fiscal_credit, consumo, invoice). Excluye cotizaciones y notas de crédito.',
    TOTAL_PROFIT: 'Ganancias Totales',
    TOTAL_EXPENSES: 'Gastos del Día',
    TOTAL_INVOICES: 'Comprobantes',
    PRINT_COL_CONCEPT: 'Concepto',
    PRINT_COL_TOTAL: 'Total',
    PRINT_COL_VARIATION: 'Variación',
    PRINT_COL_PERIOD: 'Período',
    PRINT_COL_SALES: 'Ventas',
    PRINT_COL_GOAL: 'Meta',
    PRINT_COL_PCT: '%',
    SALES_TREND_TITLE: 'Tendencia de Ventas',
    SALES_VS_EXPENSES_TREND_TITLE: 'Tendencia de Ventas vs Costo vs Gastos',
    HOURLY_COST_UNAVAILABLE:
      'Vista por hora: los gastos y la utilidad operativa solo están disponibles en rangos de varios días.',
    CHART_TYPE_TOGGLE: 'Cambiar tipo de gráfico (línea / barras)',
    HOUR_FORMAT_TOGGLE: 'Cambiar formato de hora (12h / 24h)',
    COST_LABEL: 'Costo',
    GROSS_PROFIT_LABEL: 'Ganancia Bruta',
    MARGIN_LABEL: 'Margen',
    OPERATING_PROFIT_LABEL: 'Utilidad Operativa',
    UNKNOWN_COST_ROW: 'Ventas sin costo: {{amount}}',
    PARTIAL_PROFIT_BADGE: 'Utilidad Parcial',
    PROFIT_TILE_UNKNOWN_COST_WARNING:
      '⚠ Costo desconocido en {{amount}} de ventas ({{pct}}%). La ganancia asume costo 0 para esas líneas.',
    COST_COVERAGE_LABEL: 'Utilidad calculada sobre el {{pct}}% de las ventas',
    COST_COVERAGE_COMPLETE: 'Completa',
    COST_COVERAGE_PARTIAL: 'Parcial',
    UNKNOWN_COST_WARNING:
      'Ventas sin costo registrado: {{amount}} — {{pct}}% de las ventas del período',
    LAST_UNKNOWN_COST_SALE: 'Última venta sin costo: {{relative}}',
    LEGACY_COST_NOTE:
      'Las ventas anteriores al {{date}} pueden contener costos heredados ({{amount}})',
    SALES_VS_PURCHASES_TITLE: 'Ventas vs Compras',
    SALES_LABEL: 'Ventas',
    PURCHASES_LABEL: 'Compras',
    NO_DATA: 'Sin datos para el período seleccionado',
    SALES_BREAKDOWN: 'Desglose de Ventas',
    GROSS_LABEL: 'Bruto',
    REFUNDS_LABEL: 'Reembolsos',
    CREDIT_NOTES_LABEL: 'Notas de Crédito',
    NET_LABEL: 'Neto',
    TOTAL_BRANCHS: 'Total Sucursales',
    ACTIVE_NOW: ' activas ahora',
    TOTAL_SALES_VS_GOAL: 'Ventas Totales vs Meta',
    TOP_PRODUCTS: 'Top 5 Productos más vendidos',
    SOLD: 'vendidos',
    EMPTY_TOP_PRODUCTS: 'No hay productos para mostrar',
    STOCK: 'Cantidad',
    INVENTORY_ALERTS: 'Alertas de Inventario',
    INVENTORY_ALERT_DESCRIPTION: 'Por favor, revisa y actualiza el inventario.',
    LOW_STOCK: 'Bajo',
    CRITICAL_STOCK: '⚠️ Advertencia de Stock',
    OVERSTOCK: '📦 Sobrepedido',
    REORDER_NEEDED: '🔄 Requiere Reorden',
    CREATE_PO_FROM_REORDER: 'Crear Orden de Compra',
    CREATE_PO_ALL_REORDER: 'Crear OC con todos los productos de reorden',
    START_TIME_LABEL: 'Iniciado',
    END_TIME_LABEL: 'Finalizada',
    NO_SHIFT_MESSAGE: 'No hay Turnos de Caja activos en este momento',
    ACTIVE_SHIFTS: 'Cajas Activas',
    TOTAL_SALES: 'Ventas Totales',
    TOTAL_RECEIVED: 'Total recibido',
    GO_TO_RECONCILIATION: 'Cuadre de Caja',
    SHIFT_STATUS_ACTIVE: 'Activa',
    SHIFT_STATUS_CLOSED: 'Cerrada',
    NO_DATA_AVAILABLE: 'No hay datos disponibles',
    GOAL_DISTRIBUTION: 'Distribución de la Meta',
    GOAL_DAY: 'Día',
    GOAL_WEEK: 'Semana',
    GOAL_MONTH: 'Mes',
    DAILY_GOAL: 'Meta Diaria',
    WEEKLY_GOAL: 'Meta Semanal',
    MONTHLY_GOAL: 'Meta Mensual',
    MODIFY_GOAL: 'Modificar meta',
    GOAL_SOURCE_HINT:
      'Esta meta se estableció con base en el volumen de ventas que indicaste al registrarte o al actualizar las configuraciones de la empresa. Puedes ajustarla en cualquier momento desde la configuración.',
    EMPTY_INVENTORY_ALERTS: 'No hay alertas de inventario en este momento',
    UNSOLD_PRODUCTS: 'Sin Ventas',
    UNSOLD_PRODUCTS_DESCRIPTION:
      'Productos sin ventas en el periodo configurado',
    UNSOLD_PRODUCTS_INFO:
      'Los productos aparecen aquí después de {{days}} días sin ventas desde su fecha de creación o desde su última venta. Puedes ajustar este periodo en la configuración del negocio.',
    UNSOLD_PRODUCTS_EMPTY: 'Todos los productos tienen ventas recientes',
    UNSOLD_LAST_SALE: 'Última venta',
    UNSOLD_NEVER_SOLD: 'Nunca vendido',
    UNSOLD_DAYS_WITHOUT_SALE: '{{days}} días sin venta',
    UNSOLD_TIED_UP_CAPITAL: 'Capital inmovilizado',
    UNSOLD_PERIOD_LABEL: 'Periodo: {{days}} días',
  },
  POS: {
    CANT_CONVERT_QUOTE_TO_ORDER:
      'No se pudo convertir la cotización en orden de venta',
    SERIAL_ENTRY_TITLE: 'Capturar seriales',
    SERIAL_ENTRY_DESCRIPTION:
      'Ingresa {{count}} número(s) de serie para {{product}}.',
    SERIAL_PLACEHOLDER: 'Serial #{{n}}',
    SERIAL_DUPLICATE_LINE: 'Serial duplicado en esta línea.',
    SERIAL_DUPLICATE_INVOICE:
      'Serial ya capturado en otra línea de la factura.',
    SERIAL_PROGRESS: '{{captured}} de {{total}} seriales capturados',
    SERIAL_REQUIRED_BADGE: 'Serial requerido',
    SERIAL_REQUIRED_LINE_HINT: 'Capturar seriales ({{count}})',
    SERIAL_REQUIRED_FOR_PRODUCT:
      'Este producto requiere capturar un serial por unidad antes de continuar.',
    TITLE: 'Punto de Venta',
    CASHIER: 'Cajero',
    SELLER: 'Vendedor',
    MODIFIERS: 'Modificadores',
    SHOPPING_CART: 'Carrito',
    NO_PRODUCTS_IN_CATEGORY: 'No hay productos en esta categoría',
    ITEM: 'producto',
    ITEMS: 'productos',
    NO_PRODUCTS_FOUND: 'No se encontraron productos',
    TAX: 'Impuesto',
    SUBTOTAL: 'Subtotal',
    TOTAL: 'Total',
    PAY: 'Cobrar',
    SEND_EMAIL: 'Enviar Email',
    EMAIL_INVOICE: 'Enviar Factura por Email',
    EMAIL_INVOICE_PLACEHOLDER: 'correo@ejemplo.com',
    EMAIL_INVOICE_SUCCESS: 'Factura enviada exitosamente',
    EMAIL_INVOICE_ERROR: 'Error al enviar la factura por email',
    WHATSAPP_INVOICE: 'Enviar por WhatsApp',
    WHATSAPP_PHONE_PLACEHOLDER: 'Número de WhatsApp',
    SEND_WHATSAPP: 'Enviar WhatsApp',
    COPY_LINK: 'Copiar enlace',
    LINK_COPIED: 'Enlace copiado al portapapeles',
    LINK_COPY_ERROR: 'No se pudo copiar el enlace',
    SEARCH_PRODUCTS: 'Buscar por nombre, código de barras o modificador...',
    ALL: 'Todos',
    SELECT_MODIFIER: 'Seleccionar Modificador',
    QUANTITY: 'Cantidad',
    ADD_TO_CART: 'Agregar al Carrito',
    SEARCH_MODIFIERS: 'Buscar modificadores...',
    NO_MODIFIERS_FOUND: 'No se encontraron modificadores',
    NO_MODIFIERS: 'No hay modificadores disponibles',
    SELECTED: 'Seleccionado',
    SKU: 'SKU',
    BARCODE: 'Código de Barras',
    OUT_OF_STOCK: 'Sin Stock',
    STOCK_EXCEEDED:
      'Stock insuficiente para "{{name}}". Disponible: {{available}}',
    STOCK: 'Cantidad',
    CART: 'Carrito',
    INCREASE_QUANTITY: 'Aumentar cantidad',
    DECREASE_QUANTITY: 'Disminuir cantidad',
    REMOVE_FROM_CART: 'Quitar del carrito',
    CUSTOMER: 'Cliente',
    SAVE_TICKET: 'Guardar',
    SUPERVISED_REMOVE_TITLE: 'Aprobación para quitar artículo',
    SUPERVISED_REMOVE_DESC:
      'Quitar este artículo del carrito requiere la aprobación de un gerente o administrador.',
    SUPERVISED_CANCEL_TITLE: 'Aprobación para cancelar la factura',
    SUPERVISED_NAVIGATE_TITLE: 'Aprobación para salir de la pantalla',
    SUPERVISED_LOSE_DESC:
      'Perderás los artículos que tienes en pantalla. Continuar requiere la aprobación de un gerente o administrador.',
    SUPERVISED_CONTINUE: 'Continuar',
    SUPERVISED_APPROVAL_TITLE: 'Autorización del gerente',
    SUPERVISED_ITEMS_HEADER: 'Artículos a remover',
    SUPERVISED_REASON_LABEL: 'Motivo (opcional)',
    SUPERVISED_REASON_PLACEHOLDER: 'Ej.: el cliente cambió de opinión',
    SUPERVISED_REMOVED_SECTION_TITLE: 'Artículos removidos (aprobados)',
    SUPERVISED_REMOVED_EMPTY: 'No se removieron artículos en este turno.',
    SUPERVISED_COL_APPROVER: 'Aprobó',
    SUPERVISED_COL_REASON: 'Motivo',
    SAVE_TICKET_TITLE: 'Guardar como Borrador',
    SAVE_TICKET_DESCRIPTION:
      '¿Desea guardar este ticket como borrador? El ticket se guardará sin afectar el inventario ni el balance del cliente.',
    SAVE_TICKET_SUCCESS: 'Ticket guardado como borrador exitosamente',
    SAVE_TICKET_ERROR: 'Error al guardar el ticket',
    DRAFT_TICKETS: 'Tickets Guardados',
    NO_DRAFT_TICKETS: 'No hay tickets guardados',
    LOAD_DRAFT_TICKET: 'Cargar Ticket',
    LOAD_DRAFT_TICKET_TITLE: 'Cargar Ticket Guardado',
    LOAD_DRAFT_TICKET_DESCRIPTION:
      '¿Desea cargar este ticket? Se reemplazará el contenido actual del carrito.',
    LOAD_DRAFT_SUCCESS: 'Ticket cargado exitosamente',
    LOAD_DRAFT_ERROR: 'Error al cargar el ticket',
    NEW_TICKET: 'Nuevo',
    DELETE_DRAFT_TICKET: 'Eliminar ticket',
    DELETE_DRAFT_TICKET_TITLE: 'Eliminar Ticket Guardado',
    DELETE_DRAFT_TICKET_DESCRIPTION:
      '¿Está seguro que desea eliminar este ticket guardado? Esta acción no se puede deshacer.',
    DISCOUNT: 'Descuento',
    PAYMENT_DETAILS: 'Detalles de Pago',
    PAYMENT_DESCRIPTION: 'Seleccione el tipo de factura y el método de pago',
    PAYMENT_DESCRIPTION_INVOICE_STEP: 'Seleccione el tipo de factura',
    PAYMENT_DESCRIPTION_PAYMENT_STEP: 'Ingrese el método y monto de pago',
    PAYMENT_DESCRIPTION_CONFIRMATION_STEP:
      'Revise los detalles antes de confirmar',
    TOTAL_TO_PAY: 'Total a Pagar',
    INVOICE_TYPE: 'Tipo de Factura',
    DOCUMENT_TYPE: 'Tipo de Documento',
    INVOICE_TERM: 'Término de Factura',
    CREDITO_FISCAL: 'Factura Crédito Fiscal',
    CREDITO_FISCAL_DESC: 'Para empresas con RNC',
    CONSUMO: 'Factura para Consumidor Final',
    CONSUMO_DESC: 'Para consumidores finales',
    SIMPLE_INVOICE: 'Pre Factura',
    SIMPLE_INVOICE_DESC: 'Pre Factura sin valor fiscal',
    CREDIT_NOTE: 'Nota de Crédito',
    CREDIT_NOTE_DESC: 'Para devoluciones o ajustes',
    CUSTOMER_CREDIT: 'Crédito del cliente',
    CREDIT_NOTE_SEARCH_PLACEHOLDER:
      'Buscar nota de crédito por número o NCF...',
    SEARCH_CREDIT_NOTE: 'Buscar',
    USE_CREDIT_NOTE: 'Usar',
    AVAILABLE_CREDIT_NOTES: 'Notas de crédito disponibles',
    CREDIT_NOTE_REQUIRED: 'Seleccione una nota de crédito',
    CREDIT_NOTE_SEARCH_REQUIRED: 'Ingrese un número/NCF para buscar',
    CREDIT_NOTE_NOT_FOUND: 'No se encontró la nota de crédito',
    CREDIT_NOTE_EXCEEDS_REMAINING:
      'El monto excede el balance disponible de la nota de crédito.',
    PRINT_CREDIT_NOTE_BALANCE: 'Imprimir balance de nota de crédito',
    CUSTOMER_CREDIT_EXCEEDS_TOTAL:
      'El crédito del cliente excede el total a pagar. Agregue más artículos al carrito.',
    VIEW_MORE_OPTIONS: 'Ver más opciones',
    VIEW_LESS_OPTIONS: 'Ver menos opciones',
    PAYMENT_METHOD: 'Método de Pago',
    CASH: 'Efectivo',
    CREDIT_CARD: 'Tarjeta de Crédito',
    DEBIT_CARD: 'Tarjeta de Débito',
    TRANSFER: 'Transferencia',
    CHECK: 'Cheque',
    CONFIRM_PAYMENT: 'Confirmar Pago',
    RNC_NUMBER: 'Número de RNC',
    ECF_E32_LARGE_AMOUNT_RNC_BANNER:
      'DGII exige RNC/Cédula del comprador para Facturas de Consumo Electrónicas (E32) con monto total ≥ RD$250,000. Ingresa el documento antes de continuar.',
    RNC_MANUAL_ENTRY_HINT:
      'No pudimos validar automáticamente. Completa el RNC/Cédula y la razón social manualmente para continuar.',
    RNC_PLACEHOLDER: 'Ingrese el RNC (ej: 123456789)',
    NCF_SEQUENCE_MISSING: 'sin secuencia configurada',
    VALIDATE: 'Validar',
    VALIDATING: 'Validando...',
    RNC_VALID: 'RNC válido',
    RNC_INVALID: 'RNC / Cédula no válido',
    RNC_REQUIRED: 'El RNC es requerido',
    RNC_VALIDATION_ERROR: 'Error al validar el RNC',
    FORCE_TAX_REQUIRED_TITLE: 'Impuesto requerido para este comprobante',
    FORCE_TAX_REQUIRED_DESC:
      'El comprobante {{ncf}} requiere un impuesto (ITBIS). Seleccione el impuesto que se aplicará a esta venta antes de continuar.',
    FORCE_TAX_PLACEHOLDER: 'Seleccione el impuesto a aplicar',
    FORCE_TAX_NO_OPTIONS:
      'No hay impuestos configurados. Configure ITBIS en Configuración → Impuestos.',
    FORCE_TAX_PREVIEW: 'Aplicará {{name}} = RD$ {{amount}}',
    BUSINESS_NAME: 'Nombre de la Empresa',
    NEXT: 'Siguiente',
    ADD_PAYMENT_METHOD: 'Agregar Método de Pago',
    REMOVE: 'Eliminar',
    AMOUNT: 'Monto',
    REMAINING: 'Restante',
    TOTAL_PAID: 'Total Pagado',
    PAYMENT_SUMMARY: 'Resumen de Pago',
    PAYMENT_BREAKDOWN: 'Desglose de Pago',
    DISTRIBUTE_REMAINING: 'Distribuir Restante',
    PAYMENT_COMPLETE: 'Pago Completo',
    PAYMENT_INCOMPLETE: 'Pago Incompleto',
    PAYMENT_CONFIRMATION: 'Confirmación de Pago',
    CONFIRM_AND_PRINT: 'Confirmar e Imprimir',
    PRINT: 'Imprimir',
    INVOICE_DETAILS: 'Detalles de Factura',
    PAYMENT_METHODS: 'Métodos de Pago',
    REFERENCE_NUMBER: 'Número de Referencia',
    ENTER_REFERENCE: 'Ingrese número de referencia',
    BANK: 'Banco',
    SELECT_BANK: 'Seleccione banco',
    PAYMENT_REFERENCE: 'Referencia de Pagos',
    NO_PAYMENTS_REGISTERED: 'No hay pagos registrados',
    REFERENCE: 'Referencia',
    DATE: 'Fecha',
    NCF_NUMBER: 'Número de Comprobante Fiscal (NCF)',
    LOADING_NCF: 'Obteniendo NCF...',
    NCF_NOT_AVAILABLE: 'NCF no disponible',
    PROCESSING: 'Procesando...',
    PAYMENT_TAKING_TOO_LONG: 'El pago está tardando más de lo normal',
    PAYMENT_TAKING_TOO_LONG_HINT:
      'Conexión lenta o sin internet. Puedes reintentar — si el pago ya se registró en el servidor no se duplicará.',
    INVOICE_SUCCESS_TITLE: '¡Factura Procesada Exitosamente!',
    INVOICE_SUCCESS_DESC:
      'La factura se ha guardado y el pago ha sido registrado correctamente.',
    SENDING: 'Enviando...',
    CLOSE: 'Cerrar',
    OVERPAYMENT: 'Cambio:',
    ENTER_BUSINESS_NAME: 'Ingrese nombre de la empresa',
    NCF_TYPE_LABEL: 'Tipo',
    OPTIONAL: 'opcional',
    INVOICE_NUMBER: 'Factura',
    NCF: 'NCF',
    DATE_TIME: 'Fecha y Hora',
    ITEM_CODE: 'Código',
    ITEM_CODE_SHORT: 'Ref.',
    ITEM_NAME: 'Nombre',
    ITEM_NAME_SHORT: 'Nom.',
    ITEM_DESCRIPTION: 'Descripción',
    ITEM_DESCRIPTION_SHORT: 'Desc.',
    ITEM_QUANTITY: 'Cant.',
    ITEM_PRICE: 'Precio',
    ITEM_TOTAL: 'Total',
    TOTAL_ITEMS: 'Total de artículos',
    TERMS_AND_CONDITIONS: 'Términos y Condiciones',
    ECF_QR_VERIFICATION: 'Escanea para verificar en DGII',
    ECF_SECURITY_CODE: 'Código de Seguridad',
    ECF_SIGNING_DATE: 'Fecha Firma Digital',
    NCF_EXPIRY: 'Fecha Vencimiento',
    NCF_MODIFIED: 'NCF Modificado',
    REFERENCE_LABEL: 'Ref',
    BANK_BHD: 'BHD León',
    BANK_POPULAR: 'Banco Popular',
    BANK_BANRESERVAS: 'Banreservas',
    BANK_SCOTIABANK: 'Scotiabank',
    GENERIC_CUSTOMER_ERROR:
      'No se pudo obtener información del cliente genérico',
    PAPER_SIZE: 'Tamaño del papel',
    SELECT_PAPER_SIZE: 'Seleccionar tamaño',
    CART_EMPTY_ERROR:
      'Debe agregar productos al carrito antes de procesar el pago',
    SALE_NOTE: 'Venta realizada desde POS',
    OPERATOR_NOTE_LABEL: 'Notas',
    DEFAULT_CUSTOMER: 'Cliente POS',
    INVOICE_CREATED_SUCCESS: 'Factura creada y pago procesado correctamente',
    QUOTE_SAVED_SUCCESS: 'Cotización guardada correctamente',
    SAVE_QUOTE: 'Guardar Cotización',
    INVOICE_UPDATED_PAYMENT_SUCCESS:
      'Factura actualizada y pago procesado correctamente',
    INVOICE_PAYMENT_ERROR: 'Error al procesar el pago y guardar la factura',
    NO_INVOICE_DATA: 'No hay datos de factura disponibles para imprimir',
    INVOICE_PRINTED_SUCCESS: 'Factura impresa correctamente',
    INVOICE_PRINT_ERROR: 'Error al imprimir la factura',
    INVOICE_PRINT_UNEXPECTED_ERROR: 'Error inesperado al imprimir la factura',
    INVOICE_SENT_TO_EMAIL: 'Email enviado a {{email}}',
    CANCEL_INVOICE: 'Cancelar Factura',
    UNKNOWN_ERROR: 'Error desconocido',
    ENTER_FULLSCREEN: 'Expandir pantalla',
    EXIT_FULLSCREEN: 'Salir de pantalla completa',
    TOUCH_KEYBOARD_ON: 'Desactivar teclado táctil',
    TOUCH_KEYBOARD_OFF: 'Activar teclado táctil',
    TAX_REQUIRED_FOR_NCF:
      'Agrega el impuesto desde el apartado de configuración para usar facturas con comprobante fiscal.',
    NOT_ENABLED_TITLE: 'POS no disponible',
    NOT_ENABLED_DESCRIPTION:
      'No tienes acceso al punto de venta (POS). Contacta a un administrador para habilitar esta funcionalidad.',
    CART_RECOVERED_FROM_PREVIOUS_SESSION:
      'Carrito recuperado del cierre anterior',
  },
  POS_FAST: {
    METHOD_CASH: 'Efectivo',
    METHOD_CARD: 'Tarjeta',
    METHOD_TRANSFER: 'Transferencia',
    METHOD_CUSTOMER_CREDIT: 'Crédito Cliente',
    INVOICE_TYPE_SIMPLE: 'Pre Factura',
    INVOICE_TYPE_CONSUMO: 'Consumo (B02)',
    INVOICE_TYPE_FISCAL_CREDIT: 'Crédito Fiscal (B01)',
    CHARGE_BUTTON: 'Cobrar',
    CONFIRM_BUTTON: 'Confirmar pago',
    SUMMARY_TITLE: 'Resumen del pago',
    SUMMARY_HINT:
      'Revisa los detalles antes de confirmar. Las validaciones fiscales corren al confirmar.',
    SUCCESS_TITLE: 'Pago procesado',
    SUCCESS_HINT: 'Envía o reimprime cuando estés listo.',
    COMMANDA_ON_CLOSE:
      'La comanda de cocina se imprimirá cuando cierres esta ventana.',
    REPRINT: 'Imprimir',
    EMAIL: 'Enviar por correo',
    WHATSAPP: 'Enviar por WhatsApp',
    WHATSAPP_LABEL: 'Número WhatsApp del cliente',
    WHATSAPP_PLACEHOLDER: 'Ej. 8095551234',
    WHATSAPP_SEND: 'Abrir WhatsApp',
    EMAIL_LABEL: 'Correo del cliente',
    EMAIL_PLACEHOLDER: 'cliente@ejemplo.com',
    EMAIL_SEND: 'Enviar',
    TOTAL: 'Total',
    SUBTOTAL_LABEL: 'Subtotal',
    DISCOUNT_LABEL: 'Descuento',
    TAX_LABEL: 'Impuesto',
    CANCEL: 'Cancelar',
    BACK: 'Atrás',
    CLOSE: 'Cerrar',
    CONTINUE: 'Continuar',
    PROCESSING: 'Procesando…',
    REQUIRED: 'Requerido',
    PAYMENT_METHODS_SECTION: 'Método de pago',
    INVOICE_TYPE_SECTION: 'Tipo de comprobante',
    RNC_TITLE: 'Ingresar RNC del cliente',
    RNC_PLACEHOLDER: 'RNC',
    METHOD_LABEL: 'Método',
    TYPE_LABEL: 'Tipo',
    CUSTOMER_LABEL: 'Cliente',
    MISSING_TAXES_ERROR:
      'El negocio no tiene impuestos configurados. Configúralos antes de emitir comprobantes fiscales.',
    MISSING_TAX_ID_ERROR:
      'El negocio no tiene RNC configurado. Configúralo antes de emitir comprobantes fiscales.',
    SHORT_PAYMENT_ERROR:
      'El monto cobrado es menor al total. Ajusta los montos o agrega otro método.',
    PAYMENTS_SECTION: 'Pago',
    PAYMENTS_SECTION_SPLIT: 'Pagos',
    SPLIT_PAYMENT: 'Dividir pago',
    AMOUNT_LABEL: 'Monto',
    REMOVE_PAYMENT: 'Quitar pago',
    PAID_LABEL: 'Pagado',
    TOTAL_LABEL: 'Total',
    REMAINING_LABEL: 'Restante',
    CHANGE_LABEL: 'Cambio',
    EXACT_FILL: 'Exacto',
    CLEAR_AMOUNT: 'Limpiar',
    MORE_INVOICE_TYPES: 'Más comprobantes',
    BANK_LABEL: 'Banco',
    SELECT_BANK: 'Selecciona un banco',
    NO_BANKS_CONFIGURED:
      'No hay bancos configurados. Agrégalos en Configuración → Empresa.',
    BANK_HELP_HINT: 'Los bancos se administran en Configuración → Empresa.',
    BANK_ADD_CTA: 'Agregar banco en configuración',
    BANK_REQUIRED_ERROR:
      'Selecciona el banco para los pagos por transferencia antes de continuar.',
    VALIDATE: 'Validar',
    RNC_REQUIRED_ERROR:
      'Valida el RNC del cliente antes de cobrar este comprobante fiscal.',
    REFERENCE_LABEL: 'Número de referencia',
    REFERENCE_PLACEHOLDER: 'Voucher / autorización',
    CREDIT_NOTE_LABEL: 'Nota de crédito',
    CREDIT_NOTE_PLACEHOLDER: 'Número o NCF de la nota de crédito',
    CREDIT_NOTE_REMAINING: 'Disponible',
    CREDIT_NOTE_REQUIRED_ERROR:
      'Busca y selecciona la nota de crédito antes de cobrar.',
    SEARCH: 'Buscar',
  },
  PAYMENT: {
    PARTIAL_PAYMENT_ALLOWED: 'Pago parcial permitido',
    PARTIAL_PAYMENT_INFO:
      'Para facturas a crédito, puede realizar pagos parciales',
    FULL_PAYMENT_REQUIRED: 'Pago completo requerido',
    CASH_PAYMENT_INFO: 'Los pagos al contado deben cubrir el monto total',
  },
  // Cross-surface physical-printer config (POS + Invoice per-branch cards;
  // restaurant converges onto this namespace in a later phase).
  PRINT_PREFERENCES: {
    SECTION_TITLE: 'Mis impresoras',
    SECTION_DESCRIPTION:
      'Cambia las impresoras y los ajustes de impresión solo para ti, en esta sucursal. Lo que no cambies sigue la configuración de la sucursal.',
    OPEN: 'Configurar mis impresoras',
    MODAL_TITLE: 'Mis impresoras',
    MODAL_DESCRIPTION:
      'Solo afecta tus impresiones en esta sucursal. Lo que dejes en "Heredar" sigue la configuración de la sucursal.',
    FLAG_INHERIT: 'Heredar',
    FLAG_ON: 'Sí',
    FLAG_OFF: 'No',
    OWN_PRINTER: 'Usar una impresora propia',
    SAVE: 'Guardar mis impresoras',
    RESET: 'Restablecer a la sucursal',
    SAVED: 'Preferencias de impresión guardadas',
    RESET_DONE: 'Se restableció la configuración de la sucursal',
    PRINTER_UNAVAILABLE:
      'Tu impresora no está en este equipo; se usa la de la sucursal.',
    SCOPE_LABEL: 'Aplicar a',
    SCOPE_ALL: 'Todas mis computadoras',
    SCOPE_COMPUTER: 'Solo esta computadora ({{hostname}})',
    COMPUTERS_TITLE: 'Computadoras con impresoras propias',
    COMPUTER_REMOVE: 'Quitar',
  },
  PRINTER_CONFIG: {
    TAURI_DEVICE: 'Impresora',
    TAURI_LOADING: 'Cargando impresoras…',
    TAURI_REFRESH: 'Actualizar lista',
    TAURI_UNREACHABLE:
      'La app neoprint no está en ejecución en esta terminal. Instálala y vuelve a intentar.',
    NO_PRINTER: 'Ninguna (usar el diálogo de impresión del navegador)',
    PROBE: 'Probar conexión',
    PROBE_OK: 'Conexión con la impresora exitosa',
    PAPER_SIZE: 'Tamaño de papel',
    PAPER_DEFAULT: 'Según la preferencia del documento',
    OUTPUT: 'Formato de impresión',
    OUTPUT_PDF: 'PDF por el controlador (inyección de tinta, láser)',
    OUTPUT_ESCPOS: 'Directo ESC/POS (impresora térmica de recibos)',
    OUTPUT_HINT:
      'Si tu impresora térmica imprime texto y códigos extraños en lugar de la factura, elige ESC/POS.',
    NOT_RUNNING_SHORT: 'neoprint no está en ejecución en esta terminal.',
    FORMAT_DETECTED: 'Formato: {{format}} (detectado automáticamente)',
    FORMAT_UNKNOWN:
      'No se pudo detectar el formato de esta impresora. Elige uno:',
    FORMAT_ESCPOS: 'ESC/POS (térmica)',
    FORMAT_PDF: 'PDF (controlador)',
    COMMANDA_NOT_PRINTED:
      'No se pudo imprimir la comanda en la impresora configurada.',
    FALLBACK_TO_DIALOG:
      'No se pudo imprimir en la impresora configurada; se abrió el diálogo de impresión.',
    // Card section labels per surface-document.
    COMMANDA_PRINTER: 'Impresora de comanda',
    INVOICE_PRINTER: 'Impresora de factura',
    RESTAURANT_DEVICES_HINT:
      'La configuración de los dispositivos de impresión se maneja directamente desde el apartado de planos de salón, al configurar el plano del salón en las opciones del salón.',
    RESTAURANT_DEVICES_LINK: 'Ir a planos de salón',
    SECTION_HINT:
      'Elige la impresora de esta sucursal. Sin impresora se usa el diálogo de impresión del navegador.',
  },
  PRODUCTS: {
    INHERIT_INCLUDED_TAX_TITLE: 'Aplicar la misma configuración de impuestos',
    INHERIT_INCLUDED_TAX_INTRO_SINGLE:
      'Notamos que la mayoría de tus productos incluyen {{taxes}} en el precio. ¿Quieres aplicar la misma configuración a este producto?',
    INHERIT_INCLUDED_TAX_INTRO_MULTI:
      'Tus productos usan más de una configuración de impuestos incluidos. Elige la que quieras aplicar a este producto o guarda sin aplicar ninguna.',
    INHERIT_INCLUDED_TAX_CLUSTER_LABEL:
      '{{taxes}} · usado en {{count}} producto(s)',
    INHERIT_INCLUDED_TAX_APPLY: 'Sí, aplicar',
    INHERIT_INCLUDED_TAX_APPLY_MULTI: 'Aplicar seleccionado',
    INHERIT_INCLUDED_TAX_SKIP: 'No, guardar como está',
    INHERIT_INCLUDED_TAX_OPTION_NONE: 'No aplicar ninguna',
    REQUIRES_SERIAL: 'Requiere serial',
    REQUIRES_SERIAL_HELP:
      'Pide número de serie por cada unidad al vender este producto.',
    TAX_EXCLUDED_HINT: 'Sin Imp. {{price}}',
    TAX_TYPE: 'Tipo de impuesto',
    TAX_TYPE_ITBIS_18: 'ITBIS 18%',
    TAX_TYPE_ITBIS_16: 'ITBIS 16%',
    TAX_TYPE_EXEMPT: 'Exento',
    TAX_TYPE_NON_TAXABLE: 'No aplica',
    TAX_RATE: 'Tasa',
    TAX_INCLUDED: 'Precio incluye impuesto',
    TAX_INCLUDED_INHERIT_TITLE: '¿Aplicar precio con impuesto incluido?',
    TAX_INCLUDED_INHERIT_DESC:
      'Algunos de tus productos ya usan precios con impuesto incluido. ¿Quieres aplicar lo mismo a este producto?',
    TAX_INCLUDED_INHERIT_CONFIRM: 'Sí, aplicar',
    TAX_INCLUDED_INHERIT_CANCEL: 'No, dejar como está',
    TAX_INCLUDED_HELP:
      'Si está activo, el precio de venta ya incluye el ITBIS. El sistema calculará la base y el impuesto al facturar.',
    WITH_TAX: 'Precio sin impuesto al facturar',
    INCLUDED_TAXES_LABEL: 'Impuestos adicionales incluidos en el precio',
    INCLUDED_TAXES_HELP:
      'Selecciona los impuestos (Propina, Servicio, etc.) que también están dentro del precio de venta. El sistema los desglosará al facturar.',
    INCLUDED_TAX_SURFACE_ALL: 'Todas',
    INCLUDED_TAX_SURFACE_HELP: 'Aplica este impuesto solo en:',
    SURFACE_RESTAURANT: 'Restaurante',
    SURFACE_POS: 'Punto de Venta',
    SURFACE_INVOICE: 'Factura',
    SURFACE_SALES_ORDER: 'Orden de Venta',
    SURFACE_MOBILE_POS: 'POS Móvil',
    SURFACE_PURCHASE: 'Compras',
    SURFACE_RENTAL: 'Alquileres',
    INCLUDED_TAXES_ADD_BUTTON: 'Agregar impuesto incluido',
    INCLUDED_TAXES_EMPTY:
      'No hay impuestos habilitados para incluir en el precio. Ve a /configuration/tax y activa "Puede incluirse dentro del precio del producto" en el impuesto que quieras usar (por ejemplo, Propina).',
    INCLUDED_TAXES_PREVIEW: 'Base sin impuesto',
    UNIT_OF_MEASURE: 'Unidad de medida',
    UNIT_OF_MEASURE_PLACEHOLDER: 'Selecciona una unidad',
    MODIFIER: 'Modificador',
    TITLE: 'Productos y Servicios',
    SUBTITLE: 'Administre los productos y servicios del sistema.',
    ADD_PRODUCT: 'Nuevo Producto y/o Servicio',
    ALL_CATEGORIES: 'Todas las categorías',
    ALL_WAREHOUSES: 'Todos los almacenes',
    SEARCH_PLACEHOLDER: 'Buscar productos...',
    NO_CATEGORIES: 'No hay categorías disponibles',
    NO_PRODUCTS: 'No hay productos disponibles',
    NAME: 'Nombre',
    PRICE: 'Precio',
    MARGIN_COLUMN: 'Ganancia',
    MARGIN_TOOLTIP_TITLE: 'Cálculo de ganancia',
    MARGIN_TOOLTIP_FORMULA:
      'Precio: {{price}}\nCosto: {{cost}}\nGanancia: {{profit}}\nMargen sobre precio: {{marginPct}}% (ganancia ÷ precio)\nMargen sobre costo: {{markupPct}}% (ganancia ÷ costo)\n\nMisma fórmula que usa el dashboard por línea de factura: base - (costo × cantidad), sin permitir valores negativos.',
    CATEGORY: 'Categoría',
    STOCK: 'Cantidad',
    STOCK_READONLY_HINT:
      'Use el botón "Agregar Movimiento de Stock" para modificar la cantidad',
    STOCK_NEW_PRODUCT_HINT:
      'Se asignará el stock inicial al guardar el producto por primera vez',
    PRODUCT_TYPE: 'Tipo de producto',
    PRODUCT_TYPE_GOOD: 'Producto',
    PRODUCT_TYPE_SERVICE: 'Servicio',
    PRODUCT_TYPE_RAW_MATERIAL: 'Materia prima',
    RECIPE_USABLE: 'Materia prima',
    MIN_STOCK_LEVEL: 'Stock Mínimo',
    MAX_STOCK_LEVEL: 'Stock Máximo',
    REORDER_POINT: 'Punto de Reorden',
    DESCRIPTION: 'Descripción',
    MODIFIERS: 'Modificadores',
    NO_PRODUCTS_FOUND: 'No se encontraron productos',
    AVAILABLE_FOR_SELLING: 'Disponible',
    NOT_AVAILABLE_FOR_SELLING: 'No disponible',
    CREATE_NEW_PRODUCT: 'Crear nuevo producto',
    ACTIONS: 'Acciones',
    VIEW: 'Ver',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    ADD_PRODUCT_DESCRIPTION:
      'Complete el formulario para crear o editar un producto.',
    UPLOAD_STATUS: 'Estado de la carga',
    UPLOADING: 'Cargando...',
    START_UPLOAD: 'Iniciar carga',
    PENDING: 'Pendiente',
    COMPLETED: 'Completado',
    ERROR: 'Error',
    READY: 'Listo',
    CLICK_TO_UPLOAD_FILES_OR_DRAG_AND_DROP:
      'Haga clic para cargar archivos o arrastrar y soltar',
    CLICK_TO_UPLOAD_A_FILE_OR_DRAG_AND_DROP:
      'Haga clic para cargar un archivo o arrastrar y soltar',
    MB_EACH: 'MB cada uno',
    MAX_FILE_SIZE: 'Tamaño máximo de archivo {{countMB}} MB cada uno',
    UP_TO_FILES: 'Hasta {{count}} archivos',
    SKU: 'Referencia/SKU',
    GENERATE_SKU: 'Generar código automático',
    BARCODE: 'Código de Barras',
    COST: 'Costo',
    COST_USED_FOR_SALES: 'Costo utilizado para ventas',
    BATCH_CALC_TOGGLE: 'Calcular costo/precio por unidad',
    BATCH_CALC_TOTAL_COST: 'Costo total del lote',
    BATCH_CALC_TOTAL_PRICE: 'Precio total del lote',
    BATCH_CALC_QUANTITY: 'Cantidad',
    BATCH_CALC_APPLY: 'Calcular',
    BATCH_CALC_RESULT: 'Costo/{{unit}}: {{cost}} · Precio/{{unit}}: {{price}}',
    PRICE_FROM_MARKUP:
      'Precio calculado con {{markup}}% de ganancia predeterminada (Configuración › Inventario).',
    COST_HELPER_THREE_STATE: 'Vacío = costo desconocido · 0 = costo cero real',
    LAST_PURCHASE_COST_LABEL: 'Última compra: {{amount}}',
    USE_THIS_COST: 'Usar este costo',
    COST_READONLY_FROM_PURCHASES:
      'El costo se actualiza automáticamente desde las compras',
    SELLING_PRICE: 'Precio de Venta',
    VARIANT_AXIS_NAME_PLACEHOLDER: 'Nombre de variante (ej. Talla)',
    VARIANT_AXIS_VALUE_PLACEHOLDER: 'Valor (ej. M)',
    VARIANT_ADD_AXIS: 'Agregar variante',
    VARIANT_ADD_VALUE: 'Agregar valor',
    VARIANT_ADD_IMAGE: 'Agregar imagen',
    VARIANT_HELP:
      'Las variantes convierten este producto en una plantilla: define ejes (ej. Talla, Color) y cada combinación se crea como un producto hijo con su propio precio, costo, stock y SKU. El producto base no se vende directamente — al facturar eliges la combinación. Ideal para ropa y calzado donde no quieres registrar el mismo artículo una vez por talla/color.',
    VARIANT_PRESENTATION_CONFLICT:
      'Las variantes no están disponibles cuando el producto tiene presentaciones. Elimina las presentaciones para usar variantes.',
    VARIANT_RECIPE_CONFLICT:
      'Desactiva la receta para usar variantes: un producto padre agrupa a sus variantes y no se compone de ingredientes.',
    VARIANT_HAS_PERSISTED:
      'Este producto ya tiene variantes guardadas. Quítalas con la X (en el eje o en cada valor) antes de desactivar las variantes.',
    VARIANT_DELETE_CONFIRM_TITLE: 'Se eliminarán variantes',
    VARIANT_DELETE_CONFIRM_BODY:
      'Al guardar se eliminarán {{count}} variante(s) de forma permanente. Las facturas y movimientos ya emitidos conservan el nombre y SKU de la variante, pero el producto dejará de existir. ¿Continuar?',
    VARIANT_COMBINATIONS: 'Variantes ({{count}})',
    VARIANT_ACTIVE: 'Activo',
    VARIANT_INITIAL_STOCK: 'Stock inicial',
    VARIANT_HAS: 'Este producto tiene variantes (tallas, colores)',
    VARIANT_HAS_HINT:
      'Cada combinación es un producto con su propio precio, costo y stock. El producto base no se vende directamente.',
    VARIANT_SECTION_TITLE: 'Variantes',
    VARIANT_SELECT_TITLE: 'Seleccionar variante',
    VARIANT_SELECT_SUBTITLE: 'Elige una combinación para agregar',
    VARIANT_COLUMN: 'Variante',
    VARIANT_NONE_AVAILABLE: 'No hay variantes disponibles',
    VARIANT_OUT_OF_STOCK: 'Sin stock',
    VARIANT_STOCK_LABEL: 'Stock',
    SELLING_PRICE_WITH_TAX: 'Precio de Venta con Impuesto',
    USE_AUTO_SELLING_PRICE: 'Usar precio automático',
    USE_AUTO_SELLING_PRICE_PERCENTAGE_HELP:
      'Calcula el precio porcentaje sobre el costo de {{percentage}}%',
    CONFIGURE_MARKUP_PERCENTAGE: 'Configurar porcentaje de marcado',
    INVOICE_DESCRIPTION: 'Descripción para Factura',
    ADD_MODIFIERS: 'Agregar modificadores',
    ADD_IMAGES: 'Agregar imágenes',
    SELECT_MODIFIERS_TITLE: 'Seleccione los modificadores',
    SELECT_MODIFIERS_DESCRIPTION:
      'Elija los modificadores disponibles para este producto.',
    WAREHOUSE: 'Almacén',
    WAREHOUSE_EMPTY_HINT: 'Déjelo vacío para asignar al almacén principal',
    BRAND: 'Marca',
    WEIGHT: 'Peso',
    DIMENSIONS: 'Dimensiones',
    COLOR: 'Color',
    SIZE: 'Tamaño',
    LOCATION: 'Ubicación',
    ADDITIONAL_DETAILS: 'Detalles adicionales',
    PROVIDER_NAME: 'Proveedor',
    NO_PROVIDER_YET: '(Sin proveedor asignado)',
    STOCK_RESERVED: 'Stock Reservado',
    STOCK_AVAILABLE: 'Disponible',
    DEACTIVATE: 'Desactivar',
    ACTIVATE: 'Activar',
    MODIFIER_PLACEHOLDER: 'Tamaño, Sabor, etc.',
    ERROR_LOADING_PRODUCTS: 'Error al cargar los productos',
    TAX: 'Impuesto',
    VIEW_MORE: 'Ver más',
    VALIDATE_AND_IMPORT: 'Validar e Importar',
    PROVIDER: 'Proveedor',
    SAVE_AND_CREATE_ANOTHER: 'Guardar y Crear nuevo',
    SAVE_AND_CREATE_ANOTHER_SUCCESS:
      'Producto creado. Puedes registrar otro a continuación.',
    // Price Tiers
    PRICE_TIERS: 'Niveles de Precio',
    PRICE_TIERS_DESCRIPTION:
      'Configura precios diferentes para distintos tipos de clientes',
    PRICE_TIERS_HELP_TITLE: 'Cómo funcionan los niveles de precio',
    PRICE_TIERS_HELP_BODY:
      'El precio del nivel solo se aplica cuando el cliente tiene asignado ese mismo nivel en su perfil. Si el cliente no tiene niveles asignados o ninguno coincide, se usa el precio base del producto.',
    PRICE_TIERS_HELP_STEPS_TITLE: 'Para activarlo:',
    PRICE_TIERS_HELP_STEP_1:
      'Crea el nivel aquí en el producto (ej: Mayorista, VIP) con el precio que deseas.',
    PRICE_TIERS_HELP_STEP_2:
      'Abre el cliente en el módulo de Clientes y asígnale el mismo nivel en la sección Niveles de Precio.',
    PRICE_TIERS_HELP_STEP_3:
      'Al facturar a ese cliente, el precio del nivel se aplicará automáticamente; podrás verlo y cambiarlo desde el selector en cada línea.',
    ADD_PRICE_TIER: 'Agregar Nivel de Precio',
    TIER_NAME: 'Nombre del Nivel',
    TIER_PRICE: 'Precio',
    TIER_NAME_PLACEHOLDER: 'Ej: Mayorista, Distribuidor, VIP',
    REMOVE_TIER: 'Eliminar nivel',
    MIN_QUANTITY: 'Cant. mín.',
    MIN_QUANTITY_PLACEHOLDER: 'Ej: 10',
    MIN_QUANTITY_DESCRIPTION:
      'Cantidad mínima de unidades que el cliente debe comprar para obtener este precio especial.',
    NO_PRICE_TIERS: 'Sin niveles de precio configurados',
    ALL_TIERS_ADDED: 'Todos los niveles han sido agregados',
    SELECT_PRICE_TIER: 'Seleccionar nivel de precio',
    SELECT_TIER: 'Seleccionar nivel',
    SELECT_TIER_FOR_PRODUCT: 'Selecciona un nivel de precio para {{product}}',
    MIN_QUANTITY_REQUIRED: 'La cantidad mínima requerida es {{min}}',
    REGULAR_PRICE: 'Precio regular',
    CONFIGURE_TIERS: 'Configurar Niveles',
    MANAGE_PRICE_TIERS: 'Gestionar Niveles de Precio',
    MANAGE_PRICE_TIERS_DESCRIPTION:
      'Selecciona los niveles de precio para este producto y define sus precios. Puedes crear nuevos niveles si es necesario.',
    AVAILABLE_TIERS: 'Niveles Disponibles',
    NO_SYSTEM_TIERS:
      'No hay niveles de precio configurados en el sistema. Crea el primer nivel para comenzar.',
    CREATE_FIRST_TIER: 'Crear Primer Nivel',
    CREATE_NEW_TIER: 'Crear Nuevo Nivel',
    TIER_DISPLAY_NAME: 'Nombre del Nivel',
    TIER_DISPLAY_NAME_HINT:
      'Ejemplo: Mayorista, Distribuidor, VIP. Este nombre se mostrará en la interfaz.',
    TIER_CODE: 'Código del Nivel',
    TIER_CODE_HINT:
      'Código único sin espacios. Ejemplo: WHOLESALE, DISTRIBUTOR, VIP',
    EDIT_TIER: 'Editar Nivel',
    TIER_UPDATED: 'Nivel actualizado correctamente',
    SELECTED_TIERS_COUNT: '{{count}} nivel(es) seleccionado(s)',
    CLICK_CONFIGURE_TO_ADD:
      'Haz clic en "Configurar Niveles" para agregar precios especiales',
    DELETE_TIER_CONFIRMATION:
      '¿Estás seguro de que deseas eliminar el nivel de precio "{{tierName}}"? Esta acción no se puede deshacer y afectará a todos los productos y clientes que utilicen este nivel de precio.',
    DELETE_TIER_CONFIRMATION_NOTE:
      'Nota: Si deseas eliminar este nivel de precio solo de un producto específico, puedes hacerlo desde la sección "Niveles de Precio" en el formulario del producto.',
    // Modifier Groups
    MODIFIER_GROUPS: 'Grupos de Modificadores',
    MODIFIER_GROUPS_DESCRIPTION:
      'Configura opciones adicionales que modifican el precio del producto',
    ADD_MODIFIER_GROUP: 'Agregar Grupo',
    REUSE_MODIFIER_BUTTON: 'Reutilizar modificador',
    REUSE_MODIFIER_TITLE: 'Reutilizar un modificador existente',
    REUSE_MODIFIER_DESCRIPTION:
      'Selecciona un modificador registrado en otro producto para copiarlo aquí. Los cambios que hagas después son independientes de la versión original.',
    REUSE_MODIFIER_SEARCH_PLACEHOLDER:
      'Buscar por nombre de grupo o de opción…',
    REUSE_MODIFIER_EMPTY:
      'No hay modificadores para reutilizar. Crea uno desde otro producto primero.',
    REUSE_MODIFIER_MODE_TITLE: '¿Cómo quieres reutilizarlo?',
    REUSE_MODIFIER_MODE_SAME_LABEL: 'Copiar tal cual',
    REUSE_MODIFIER_MODE_SAME_DESC:
      'Copia el grupo con los mismos nombres, precios y reglas.',
    REUSE_MODIFIER_MODE_CUSTOMIZE_LABEL: 'Copiar y personalizar',
    REUSE_MODIFIER_MODE_CUSTOMIZE_DESC:
      'Copia la estructura y abre el editor para que ajustes precios, nombres o reglas antes de guardar.',
    REUSE_MODIFIER_COPY_SAME_CTA: 'Copiar tal cual',
    REUSE_MODIFIER_CUSTOMIZE_CTA: 'Personalizar',
    MANAGE_MODIFIER_GROUPS: 'Gestionar Modificadores',
    MANAGE_MODIFIER_GROUPS_DESCRIPTION:
      'Configura grupos de modificadores con opciones que pueden agregar un precio adicional al producto.',
    NO_MODIFIER_GROUPS: 'Sin modificadores configurados',
    GROUP_NAME: 'Nombre del Grupo',
    GROUP_NAME_PLACEHOLDER: 'Ej: Tamaño, Sabor, Extras',
    IS_REQUIRED: 'Obligatorio',
    IS_REQUIRED_DESCRIPTION: 'El cliente debe seleccionar al menos una opción',
    SELECTION_TYPE: 'Tipo de selección',
    SELECTION_TYPE_SINGLE: 'Selección única',
    SELECTION_TYPE_MULTIPLE: 'Selección múltiple',
    SELECTION_TYPE_SINGLE_DESCRIPTION:
      'El cliente puede elegir solo una opción (radio buttons)',
    SELECTION_TYPE_MULTIPLE_DESCRIPTION:
      'El cliente puede elegir varias opciones (checkboxes)',
    MIN_SELECTIONS: 'Mín. selecciones',
    MAX_SELECTIONS: 'Máx. selecciones',
    MODIFIER_OPTIONS_LABEL: 'Opciones',
    ADD_OPTION: 'Agregar Opción',
    OPTION_NAME: 'Nombre',
    OPTION_NAME_PLACEHOLDER: 'Ej: Grande, Mediano',
    OPTION_PRICE_DELTA: 'Precio adicional',
    OPTION_PRICE_DELTA_PLACEHOLDER: '0.00',
    OPTION_IS_DEFAULT: 'Por defecto',
    REMOVE_OPTION: 'Eliminar opción',
    REMOVE_GROUP: 'Eliminar grupo',
    EDIT_GROUP: 'Editar grupo',
    CONFIRM_DELETE_GROUP: '¿Eliminar este grupo de modificadores?',
    CONFIRM_DELETE_GROUP_DESCRIPTION:
      'Esta acción eliminará el grupo y todas sus opciones. No se puede deshacer.',
    OPTIONS_COUNT: '{{count}} opción(es)',
    NO_OPTIONS: 'Sin opciones',
    CONFIGURE_MODIFIERS: 'Configurar Modificadores',
    SKU_BRIEF: 'Ref',
    VALIDATION: {
      NAME_MIN: 'El nombre debe tener al menos 2 caracteres',
      NAME_MAX: 'El nombre no puede exceder 100 caracteres',
      SKU_MIN: 'La referencia debe tener al menos 2 caracteres',
      SKU_MAX: 'La referencia no puede exceder 100 caracteres',
      BARCODE_MIN: 'El código de barras debe tener al menos 2 caracteres',
      BARCODE_MAX: 'El código de barras no puede exceder 100 caracteres',
      DESCRIPTION_MIN: 'La descripción debe tener al menos 2 caracteres',
      DESCRIPTION_MAX: 'La descripción no puede exceder 500 caracteres',
      INVOICE_DESCRIPTION_MIN:
        'La descripción de factura debe tener al menos 2 caracteres',
      INVOICE_DESCRIPTION_MAX:
        'La descripción de factura no puede exceder 500 caracteres',
      SELLING_PRICE_REQUIRED: 'El precio de venta es obligatorio',
      COST_REQUIRED: 'El costo es obligatorio',
      STOCK_REQUIRED: 'El stock es obligatorio',
      CATEGORY_REQUIRED: 'La categoría es obligatoria',
      WAREHOUSE_REQUIRED: 'El almacén es obligatorio',
      NO_WAREHOUSE_FOR_BRANCH:
        'Esta sucursal no tiene un almacén. Crea un almacén para esta sucursal antes de agregar productos.',
      FORM_HAS_ERRORS:
        'No se pudo guardar: revisa los campos obligatorios marcados en el formulario.',
      PRESENTATION_UNIT_REQUIRED:
        'Cada presentación necesita una unidad y un factor de conversión. Revisa la sección "Presentaciones del producto".',
      TIER_NAME_REQUIRED: 'El nombre del nivel de precio es obligatorio',
      TIER_NAME_MAX: 'El nombre del nivel no puede exceder 50 caracteres',
      TIER_PRICE_REQUIRED: 'El precio del nivel es obligatorio',
      TIER_PRICE_INVALID:
        'El precio debe ser un número válido mayor o igual a 0',
      PRICE_BELOW_COST_WARNING:
        'Este precio es menor al costo y podría generar pérdidas',
      MIN_STOCK_LEVEL_INVALID:
        'El stock mínimo debe ser un número válido mayor o igual a 0',
      MAX_STOCK_LEVEL_INVALID:
        'El stock máximo debe ser un número válido mayor o igual a 0',
      REORDER_POINT_INVALID:
        'El punto de reorden debe ser un número válido mayor o igual a 0',
    },
    CATEGORIES: {
      FOOD: 'Comida',
      DRINKS: 'Bebidas',
      DESSERTS: 'Postres',
      SNACKS: 'Snacks',
    },
    SELECT_CATEGORY: 'Seleccionar categoría',
    QUICK_TAB_GENERAL: 'General',
    NO_MODIFIERS: 'Sin modificadores',
    // Bulk Import
    BULK_IMPORT: 'Importación Masiva',
    BULK_IMPORT_DESCRIPTION:
      'Importa productos desde diferentes plataformas o archivos CSV o Excel personalizados',
    SELECT_PLATFORM: 'Seleccionar Plataforma',
    SELECT_PLATFORM_PLACEHOLDER: 'Elige una plataforma',
    PLATFORM_DESCRIPTION:
      'Selecciona la plataforma desde la que deseas importar productos. Cada plataforma tiene un formato de mapeo predefinido.',
    DOWNLOAD_TEMPLATE: 'Descargar Plantilla',
    DOWNLOAD_TEMPLATE_DESCRIPTION:
      'Descarga una plantilla con las columnas requeridas para importar productos.',
    VIEW_RECORD_DETAIL: 'Ver detalle del registro',
    RECORD_DETAIL: 'Detalle del Registro',
    CUSTOM_CSV: 'Personalizado',
    DRAG_DROP_FILE: 'Arrastra y suelta tu plantilla aquí',
    OR: 'o',
    BROWSE_FILES: 'Explorar archivos',
    SUPPORTED_FORMAT: 'Formato soportado: CSV, Excel',
    INVALID_FILE_TYPE:
      'Tipo de archivo inválido. Por favor, sube un archivo CSV o Excel.',
    CSV_PARSE_ERROR: 'Error al leer el archivo CSV. Verifica el formato.',
    EMPTY_FILE: 'El archivo está vacío. Por favor, sube un archivo con datos.',
    CSV_REQUIREMENTS: 'Requisitos del archivo CSV o Excel',
    CSV_REQUIREMENT_1: 'El archivo debe tener encabezados en la primera fila',
    CSV_REQUIREMENT_2: 'Usa una columna por campo y una fila por producto',
    CSV_REQUIREMENT_3: 'Asegúrate de que el archivo esté codificado en UTF-8',
    PARENT_SKU: 'SKU del Padre (variantes)',
    VARIANT_AXES: 'Ejes de Variante',
    VARIANT_VALUES: 'Valores de Variante',
    BULK_IMPORT_HELP_TITLE: 'Cómo incluir variantes en el archivo',
    BULK_IMPORT_HELP_NORMAL:
      'Producto normal: llena Nombre, SKU, Precio de Venta y los demás campos. Deja SKU del Padre, Ejes de Variante y Valores de Variante vacíos. Ej: Gorra, GORRA, 300, stock 20.',
    BULK_IMPORT_HELP_VARIANT_PARENT:
      'Fila PADRE (agrupa, no se vende): SKU = CAMISA, Ejes de Variante = "Talla=S,M,L|Color=Rojo,Azul". Deja Precio y Stock vacíos.',
    BULK_IMPORT_HELP_VARIANT_CHILD:
      'Fila HIJA (una por combinación): SKU del Padre = CAMISA, Valores de Variante = "Talla=M;Color=Rojo", con su propio SKU, Precio y Stock. Ej: CAMISA-M-ROJO, 500, stock 10.',
    FIELD_MAPPING_DESCRIPTION:
      'Mapea los campos de tu archivo con los campos del sistema. Los campos marcados como requeridos deben ser mapeados.',
    TARGET_FIELD: 'Campo del sistema',
    SOURCE_FIELD: 'Campo de tu archivo',
    SELECT_SOURCE_FIELD: 'Seleccionar campo',
    SKIP_FIELD: 'Omitir este campo',
    MAP_REQUIRED_FIELDS:
      'Por favor, mapea todos los campos requeridos antes de continuar',
    PREVIEW_IMPORT: 'Vista Previa',
    PREVIEW_DESCRIPTION:
      'Vista previa de {{count}} productos que serán importados',
    SHOWING_PREVIEW_ROWS: 'Mostrando las primeras {{count}} de {{total}} filas',
    IMPORTING: 'Importando...',
    IMPORT_PRODUCTS: 'Importar Productos',
    IMPORT_SUCCESS: '{{count}} productos importados exitosamente',
    IMPORT_FAILED: '{{count}} productos fallaron al importar',
    REQUIRED: 'Requerido',
    // Validation Step
    ITEMS_TO_CREATE: 'Registros a Crear',
    ITEMS_TO_UPDATE: 'Registros a Actualizar',
    NEW_CATEGORIES: 'Nuevas Categorías',
    NEW_PROVIDERS: 'Nuevos Proveedores',
    NEW_WAREHOUSES: 'Nuevos Almacenes',
    UPDATING_CATEGORIES: 'Categorías a Actualizar',
    UPDATING_PROVIDERS: 'Proveedores a Actualizar',
    UPDATING_WAREHOUSES: 'Almacenes a Actualizar',
    ITEMS_WITH_ERRORS: 'Con Errores',
    NEW_CATEGORIES_WILL_BE_CREATED: 'Las siguientes categorías serán creadas',
    NEW_PROVIDERS_WILL_BE_CREATED: 'Los siguientes proveedores serán creados',
    NEW_WAREHOUSES_WILL_BE_CREATED: 'Los siguientes almacenes serán creados:',
    UPDATING_CATEGORIES_WILL_BE_UPDATED:
      'Las siguientes categorías serán actualizadas',
    UPDATING_PROVIDERS_WILL_BE_UPDATED:
      'Los siguientes proveedores serán actualizados',
    UPDATING_WAREHOUSES_WILL_BE_UPDATED:
      'Los siguientes almacenes serán actualizados',
    NEW_RESOURCES_TO_CREATE: 'Nuevos Recursos a Crear',
    UPDATING_RESOURCES_TO_UPDATE: 'Recursos a Actualizar',
    ACTION: 'Acción',
    ERRORS: 'errores',
    WARNINGS: 'advertencias',
    ERRORS_LABEL: 'Errores:',
    WARNINGS_LABEL: 'Advertencias:',
    ITEMS_SELECTED_FOR_IMPORT:
      'de {{total}} registros seleccionados para importar',
    ACTION_CREATE: 'crear',
    ACTION_UPDATE: 'actualizar',
    PROCESSING: 'Procesando...',
    VALIDATING: 'Validando...',
    VALIDATING_IMPORT: 'Validando Importación',
    VALIDATING_ITEMS: 'Validando {{count}} registros',
    PLEASE_WAIT: 'Por favor espere mientras validamos los datos...',
    CATEGORY_DUPLICATE_ERROR:
      'No puedes tener dos categorías con el mismo nombre. La categoría "{{categories}}" ya existe.',
    CATEGORY_EXISTS_ERROR:
      'No puedes renombrar una categoría a "{{category}}" porque ya existe una categoría con ese nombre.',
    PROVIDER_DUPLICATE_ERROR:
      'No puedes tener dos proveedores con el mismo nombre. El proveedor "{{providers}}" ya existe.',
    PROVIDER_EXISTS_ERROR:
      'No puedes renombrar un proveedor a "{{provider}}" porque ya existe un proveedor con ese nombre.',
    WAREHOUSE_DUPLICATE_ERROR:
      'No puedes tener dos almacenes con el mismo nombre. El almacén "{{warehouses}}" ya existe.',
    WAREHOUSE_EXISTS_ERROR:
      'No puedes renombrar un almacén a "{{warehouse}}" porque ya existe un almacén con ese nombre.',
    // Bulk Import Warnings
    WARNING_CATEGORY_WILL_BE_CREATED:
      'Se creará la nueva categoría: {{category}}',
    WARNING_CATEGORY_WILL_BE_UPDATED:
      'Se actualizará la categoría existente: {{category}}',
    WARNING_PROVIDER_WILL_BE_CREATED:
      'Se creará el nuevo proveedor: {{provider}}',
    WARNING_PROVIDER_WILL_BE_UPDATED:
      'Se actualizará el proveedor existente: {{provider}}',
    WARNING_WAREHOUSE_WILL_BE_CREATED:
      'Se creará el nuevo almacén: {{warehouse}}',
    WARNING_WAREHOUSE_WILL_BE_UPDATED:
      'Se actualizará el almacén existente: {{warehouse}}',
    WARNING_NO_WAREHOUSE_SPECIFIED:
      'No se especificó almacén, se usará el almacén predeterminado: {{warehouse}}',
    WARNING_NO_SKU_PROVIDED:
      'No se proporcionó SKU, se generará automáticamente',
    // Success Screen
    IMPORT_IN_PROGRESS: 'Importación en Proceso',
    IMPORT_IN_PROGRESS_TITLE: '¡Importación Iniciada!',
    IMPORT_IN_PROGRESS_MESSAGE:
      'Tu importación masiva ha sido procesada exitosamente y se está ejecutando en segundo plano.',
    IMPORT_BACKGROUND_INFO:
      'Un indicador del progreso de la importación masiva se mostrará en la interfaz hasta que se complete el proceso.',
    WILL_NOTIFY_WHEN_COMPLETE:
      'Te notificaremos cuando la importación esté completa.',
    IMPORT_COMPLETED_SUCCESSFULLY:
      'La importación se ha completado exitosamente.',
    IMPORT_SUMMARY: '{{itemsImported}} {{itemLabel}}{{failedInfo}}',
    IMPORTED_ITEM_SINGULAR: 'producto importado',
    IMPORTED_ITEM_PLURAL: 'productos importados',
    IMPORT_FAILED_INFO: '({{count}} fallidos)',
    PROCESS_RESUMED: 'Proceso reanudado',
    PROCESS_RESUMED_DESCRIPTION:
      'Proceso reanudado automáticamente después de detectar inactividad.',
    RESUME_FAILED: 'No se pudo reanudar el proceso',
    CANCEL_FAILED: 'No se pudo cancelar el proceso',
    // Recovery
    CANCEL_IMPORT_CONFIRMATION:
      '¿Estás seguro de que deseas cancelar esta importación? Todos los productos y categorías creados durante esta importación serán eliminados permanentemente.',
    RETRY_IMPORT: 'Reintentar importación',
    RESUME_IMPORT: 'Reanudar importación',
    PROCESS_STALE: 'Proceso detenido',
    PROCESS_STALE_DESC:
      'Este proceso parece estar inactivo. Puedes reintentarlo.',
    PROCESS_STUCK_TITLE: 'Proceso detenido',
    PROCESS_STUCK_MESSAGE:
      'La importación se ha detenido. Haz clic en el botón para continuar desde donde se detuvo.',
    RESUME_TO_CONTINUE: 'Presiona el botón para continuar',
    CHECKING_STATUS: 'Verificando estado...',
    RECOVERING_PROCESS: 'Recuperando proceso...',
    PROCESS_RECOVERED: 'Proceso recuperado exitosamente',
    RECOVERY_FAILED: 'No se pudo recuperar el proceso',
    PROCESSES_REMOVED: 'Procesos eliminados',
    DUPLICATE_PROCESSES_TITLE: 'Procesos duplicados detectados',
    DUPLICATE_PROCESSES_MESSAGE:
      'Se encontraron más de 2 procesos del mismo tipo. ¿Desea continuar con todos o eliminar los duplicados?',
    CONTINUE_WITH_ALL: 'Continuar con todos',
    REMOVE_DUPLICATES: 'Eliminar duplicados',
    BULK_IMPORT_NOT_FOUND:
      'La importación masiva no existe o fue eliminada previamente',
    BULK_IMPORT_CORRUPTED:
      'Los datos de la importación están corruptos. Por favor, cancele este proceso e inicie una nueva importación.',
    // Status
    STATUS_WITH_ERRORS: 'Con errores',
    STATUS_WITH_WARNINGS: 'Con advertencias',
    STATUS_READY: 'Listo',
    REMOVE_ITEM_TITLE: '¿Remover registro?',
    REMOVE_ITEM_MESSAGE:
      '¿Estás seguro de que deseas remover este registro de la importación?',
    ITEMS: 'registros',
    CATEGORIES_LOWERCASE: 'categorías',
    PROVIDERS_LOWERCASE: 'proveedores',
    WAREHOUSES_LOWERCASE: 'almacenes',
    LOAD_MORE: 'Cargar más',
    SHOWING_ITEMS_OF: 'Mostrando {{showing}} de {{total}} registros',
    LOAD_ALL: 'Cargar todos',
    // Validation Errors
    NAME_REQUIRED: 'El nombre es requerido',
    SELLING_PRICE_REQUIRED: 'El precio de venta es requerido',
    SELLING_PRICE_NOT_PROVIDED: 'Precio de venta no proporcionado',
    COST_PRICE_NOT_PROVIDED: 'Precio de costo no proporcionado',
    SKU_REQUIRED: 'La referencia/SKU es requerida',
    CATEGORY_REQUIRED: 'La categoría es requerida',
    INVALID_PRICE_FORMAT: 'Formato de precio inválido',
    INVALID_STOCK_FORMAT: 'Formato de cantidad inválido',
    INVALID_TAX_VALUE:
      'Impuesto no válido — usa 18, 16, No aplica o Exento (vacío = ITBIS 18%)',
    PRICE_MUST_BE_POSITIVE: 'El precio debe ser mayor que 0',
    PRICE_MUST_BE_NON_NEGATIVE: 'El precio debe ser 0 o mayor',
    STOCK_MUST_BE_NON_NEGATIVE: 'La cantidad debe ser 0 o mayor',
    MORE_ERRORS: '+{{count}} error(es) más',
    NO_ERRORS: 'Sin errores',
    // Confirmation
    CONFIRM_IMPORT_TITLE: 'Confirmar Importación',
    CONFIRM_IMPORT_DESCRIPTION:
      '¿Estás seguro de que deseas procesar {{count}} productos?',
    TOTAL_PRODUCTS_TO_PROCESS: 'Total de Productos a Procesar',
    PRODUCTS_WITH_ERRORS_EXCLUDED:
      'Los productos con errores no serán procesados',
    KDS_NODE_LABEL: 'Flujo KDS',
    KDS_NODE_INHERITED_FROM_CATEGORY:
      'Este flujo se hereda de la categoría seleccionada. Cambia el flujo desde la categoría si necesitas actualizarlo.',
    KDS_PREP_TIME_INHERIT_HELP:
      'Si lo dejas vacío se usará el tiempo predeterminado del flujo.',
  },
  EDIT_PRODUCT: {
    TITLE: 'Editar Producto',
    SUBTITLE: 'Añade o actualiza el detalle del producto.',
    PRODUCT_INFO: 'Información del Producto',
    PRODUCT_IMAGES: 'Imágenes del Producto',
    PRODUCT_CATEGORY: 'Categoría del Producto',
    STOCK: 'Cantidad',
    WAREHOUSE: 'Almacén',
    LOCATION: 'Ubicación',
    ADD_IMAGE: 'Agregar Imagen',
    IMAGES: 'Imágenes',
    MAX_IMAGES_REACHED: 'Se ha alcanzado el número máximo de imágenes',
    MODIFIERS: 'Modificadores',
    MODIFIER_NAME: 'Nombre del Modificador',
    MODIFIER: 'Modificador',
    MODIFIER_OPTIONS: 'Opciones del Modificador (separadas por comas)',
    ADD_ANOTHER_MODIFIER: 'Agregar otro modificador',
    MODIFIER_OPTION: 'Opción',
    ADD_STOCK_MOVEMENT: 'Agregar Movimiento de Stock',
    STOCK_MOVEMENTS: 'Movimientos de Stock',
    DOCUMENT_HISTORY: 'Historial',
    DOCUMENT_HISTORY_MODAL_TITLE:
      'Historial de transacciones relacionadas al producto',
    DOCUMENT_HISTORY_MODAL_SUBTITLE:
      'Facturas y documentos donde este producto ha sido registrado.',
    NO_DOCUMENTS: 'No se encontraron documentos para este producto.',
    TAB_STOCK_MOVEMENTS: 'Movimientos de Stock',
    TAB_INVOICES: 'Facturas',
    TAB_QUOTES: 'Cotizaciones',
    TAB_CREDIT_NOTES: 'Notas de Crédito',
    TAB_PURCHASE_ORDERS: 'Órdenes de Compra',
    TAB_DEBIT_NOTES: 'Notas de Débito',
    NO_STOCK_MOVEMENTS: 'No hay movimientos de stock para este producto.',
    NO_PURCHASE_ORDERS: 'No hay órdenes de compra para este producto.',
    NO_DEBIT_NOTES: 'No hay notas de débito para este producto.',
    STOCK_MOVEMENT_MODAL: {
      TITLE: 'Agregar Movimiento de Stock',
      SUBTITLE: 'Registra un nuevo movimiento de inventario para este producto',
      QUANTITY: 'Cantidad',
      QUANTITY_PLACEHOLDER: 'Ingrese la cantidad',
      MOVEMENT_TYPE: 'Tipo de Movimiento',
      MOVEMENT_TYPE_PLACEHOLDER: 'Seleccione el tipo',
      REASON: 'Motivo',
      REASON_PLACEHOLDER: 'Describe el motivo del movimiento',
      INCREASE_STOCK: '+ Aumentar Stock',
      DECREASE_STOCK: '- Reducir Stock',
      SAVING: 'Guardando...',
      QUANTITY_ERROR: 'La cantidad debe ser mayor a 0',
      REASON_ERROR: 'El motivo es requerido',
      MOVEMENT_TYPES: {
        ADJUSTMENT: 'Ajuste de Inventario',
        PURCHASE: 'Compra',
        DAMAGED: 'Producto Dañado',
        LOST: 'Producto Perdido',
        TRANSFER: 'Transferencia',
        INITIAL: 'Stock Inicial',
        SALE: 'Venta',
        RETURN: 'Devolución',
        RESERVATION: 'Reserva',
        RELEASE: 'Liberación',
        CONDUCE: 'Conduce',
      },
    },
  },
  CATEGORIES: {
    TITLE: 'Categorías',
    SUBTITLE: 'Administre las categorías de productos.',
    ADD_CATEGORY: 'Agregar Categoría',
    NO_CATEGORIES: 'No hay categorías disponibles',
    SEARCH_PLACEHOLDER: 'Buscar categoría...',
    NAME: 'Nombre de categoría',
    DESCRIPTION: 'Descripción',
    PRODUCTS_COUNT: 'Cantidad de productos',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    NO_CATEGORIES_FOUND: 'No se encontraron categorías',
    CREATE_EDIT_TITLE: 'Crear/Editar Categoría',
    CREATE_EDIT_DESCRIPTION:
      'Complete el formulario para crear o editar una categoría de productos.',
    VIEW_PRODUCTS: 'Ver Productos',
    NEW_CATEGORY: 'Nueva Categoría',
    ROOT_CATEGORY: 'Categoría Raíz',
    CREATE_NEW_CATEGORY: 'Crear categoría',
    CREATE_CATEGORY_QUESTION:
      '¿Desea crear la categoría "{{name}}"? Presione Enter para confirmar.',
    CONFIRM_CREATE_CATEGORY: 'Confirmar creación de categoría',
    CONFIRM_CREATE_CATEGORY_DESCRIPTION:
      '¿Está seguro de que desea crear la categoría "{{name}}"?',
    CATEGORY_CREATED: 'Categoría creada exitosamente',
    KDS_STATION_LABEL: 'Estación KDS',
    KDS_STATION_PLACEHOLDER: 'Seleccionar estación',
    KDS_NODE_LABEL: 'Flujo KDS',
    KDS_NODE_PLACEHOLDER: 'Seleccionar flujo',
    KDS_NODE_HELP:
      'Las órdenes de los productos de esta categoría aparecerán en el flujo seleccionado.',
    KDS_NODE_NONE: 'Sin flujo asignado',
    KDS_NODE_EMPTY:
      'Aún no hay flujos KDS configurados. Crea uno desde Inventario → KDS.',
    STATION_KITCHEN: 'Cocina',
    STATION_BAR: 'Bar',
    STATION_WAREHOUSE: 'Almacén',
    SHOW_IN_POS: 'Mostrar en POS',
    SHOW_IN_POS_HELP:
      'Si lo apagas, esta categoría no aparece en el teclado del POS, pero sigue visible en inventario.',
    SHOW_IN_RESTAURANT: 'Mostrar en Restaurante',
    SHOW_IN_RESTAURANT_HELP:
      'Si lo apagas, esta categoría no aparece en el panel de servicio del restaurante, pero sigue visible en inventario.',
    BRANCH_VISIBILITY_TITLE: 'Visibilidad por sucursal',
    BRANCH_VISIBILITY_HELP:
      'Marca las sucursales donde esta categoría debe aparecer. La sede principal siempre la ve.',
    BRANCH_VISIBILITY_EMPTY:
      'Aún no hay sucursales secundarias. Al crearlas, podrás gestionar la visibilidad desde aquí.',
    HIDDEN_IN_N_BRANCHES: 'Oculta en {{count}} sucursal(es)',
    VALIDATION_NAME_MIN: 'El nombre debe tener al menos {{min}} caracteres.',
    VALIDATION_NAME_MAX: 'El nombre no puede exceder {{max}} caracteres.',
    VALIDATION_DESCRIPTION_MIN:
      'La descripción debe tener al menos {{min}} caracteres.',
    VALIDATION_DESCRIPTION_MAX:
      'La descripción no puede exceder {{max}} caracteres.',
    SELECT_PARENT_CATEGORY: 'Seleccionar categoría padre',
    PARENT_CATEGORY: 'Categoría padre',
    SHARED_INVENTORY_BANNER: 'Inventario compartido',
    SHARED_INVENTORY_BANNER_DESCRIPTION:
      'Las categorías son gestionadas desde la sede principal. Cambie a la sede principal para crear o editar categorías.',
  },
  WAREHOUSES: {
    TYPE: 'Tipo',
    CODE: 'Código',
    DEFAULT: '¿Es Predeterminado?',
    TITLE: 'Almacenes',
    SUBTITLE: 'Administre los almacenes del sistema.',
    ADD_WAREHOUSE: 'Nuevo Almacén',
    ALL_LOCATIONS: 'Todas las ubicaciones',
    NO_LOCATIONS: 'No hay ubicaciones disponibles',
    NO_WAREHOUSES: 'No hay almacenes disponibles',
    NAME: 'Nombre',
    STOCK: 'Cantidad',
    LOCATION: 'Ubicación',
    DESCRIPTION: 'Descripción',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    NO_WAREHOUSES_FOUND: 'No se encontraron almacenes',
    SEARCH_WAREHOUSES: 'Buscar almacenes...',
    ADD_WAREHOUSE_DESCRIPTION:
      'Complete el formulario para crear o editar un almacén.',
    // Form fields
    WAREHOUSE_NAME: 'Nombre del Almacén',
    WAREHOUSE_CODE: 'Código del Almacén',
    EMAIL: 'Correo Electrónico',
    PHONE: 'Teléfono',
    ADDRESS_INFORMATION: 'Información de Dirección',
    ADDRESS_LINE_1: 'Dirección Línea 1',
    ADDRESS_LINE_2: 'Dirección Línea 2',
    CITY: 'Ciudad',
    STATE_PROVINCE: 'Estado/Provincia',
    POSTAL_CODE: 'Código Postal',
    COUNTRY: 'País',
    WAREHOUSE_DETAILS: 'Detalles del Almacén',
    TOTAL_AREA: 'Área Total',
    AVAILABLE_AREA: 'Área Disponible',
    CAPACITY: 'Capacidad',
    NUMBER_OF_ZONES: 'Número de Zonas',
    WAREHOUSE_TYPE: 'Tipo de Almacén',
    OPERATING_HOURS: 'Horario de Operación',
    OPERATIONS_SETTINGS: 'Configuración de Operaciones',
    ALLOWS_RECEIVING: 'Permite Recepción',
    ALLOWS_SHIPPING: 'Permite Envío',
    ALLOWS_PICKUP: 'Permite Recolección',
    ENVIRONMENTAL_CONTROLS: 'Controles Ambientales',
    TEMPERATURE_CONTROLLED: 'Control de Temperatura',
    MINIMUM_TEMPERATURE: 'Temperatura Mínima',
    MAXIMUM_TEMPERATURE: 'Temperatura Máxima',
    HUMIDITY_CONTROLLED: 'Control de Humedad',
    MINIMUM_HUMIDITY: 'Humedad Mínima',
    MAXIMUM_HUMIDITY: 'Humedad Máxima',
    STATUS: 'Estado',
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    STATUS_ACTIVE: 'Activo',
    STATUS_INACTIVE: 'Inactivo',
    STATUS_MAINTENANCE: 'Mantenimiento',
    UPDATE_WAREHOUSE: 'Actualizar Almacén',
    CREATE_WAREHOUSE: 'Crear Almacén',
    SAVING: 'Guardando...',
    // Warehouse types
    DISTRIBUTION: 'Distribución',
    STORAGE: 'Almacenamiento',
    FULFILLMENT: 'Fulfillment',
    COLD_STORAGE: 'Almacén Frío',
    HAZMAT: 'Materiales Peligrosos',
    SELECT_TYPE: 'Seleccionar tipo',
    SELECT_STATUS: 'Seleccionar estado',
  },
  MODIFIERS: {
    TITLE: 'Modificadores',
    SUBTITLE: 'Administre los modificadores de productos.',
    ADD_MODIFIER: 'Nuevo Modificador',
    SEARCH_MODIFIERS: 'Buscar modificadores...',
    NAME: 'Nombre',
    VALUES: 'Valores',
    PRICE_DELTA: 'Precio Adicional',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    DESCRIPTION: 'Descripción',
    NO_MODIFIERS_FOUND: 'No se encontraron modificadores',
    ADD_MODIFIER_DESCRIPTION:
      'Complete el formulario para crear o editar un modificador de producto.',
    SELECT_MODIFIER: 'Selección',
    VALUES_COUNT: 'Cantidad de Valores',
    NO_VALUES: 'Sin valores',
    VALUE: 'Valor',
    SEARCH_PLACEHOLDER: 'Buscar modificadores...',
    EMPTY_STATE_TITLE: 'No hay modificadores',
    EMPTY_STATE_DESCRIPTION: 'Comience creando su primer modificador.',
    ADD_MODIFIER_VALUE: 'Agregar Valor',
  },
  STOCK_MOVEMENTS: {
    TITLE: 'Movimientos de Inventario',
    SUBTITLE: 'Visualice el historial de movimientos de inventario',
    FILTER_BY_DATE: 'Filtrar por fecha',
    PRODUCT_ID: 'ID de Producto',
    START_DATE: 'Fecha Inicial',
    END_DATE: 'Fecha Final',
    APPLY_FILTERS: 'Aplicar Filtros',
    CLEAR_FILTERS: 'Limpiar Filtros',
    NO_MOVEMENTS: 'No hay movimientos de inventario',
    TYPE: 'Tipo',
    QUANTITY: 'Cantidad',
    PREVIOUS_STOCK: 'Stock Anterior',
    PRODUCT_SKU: 'SKU/Referencia',
    REFERENCE: 'Referencia',
    DATE: 'Fecha',
    LOADING: 'Cargando movimientos...',
    FILTER_RESULTS: 'Filtrar Resultados',
    SEARCH_BY_SKU_OR_NAME: 'Buscar por SKU o Nombre...',
    PRODUCT_NAME: 'Nombre del Producto',
    ADD_ADJUSTMENT: 'Ajustar Inventario',
    USER: 'Usuario',
    ALL_TYPES: 'Todos los tipos',
    TYPE_LABELS: {
      SALE: 'Venta',
      RETURN: 'Devolución',
      PURCHASE: 'Compra',
      DAMAGED: 'Dañado',
      LOST: 'Perdido',
      MERMA: 'Merma',
      ADJUSTMENT: 'Ajuste',
      TRANSFER: 'Transferencia',
      INITIAL: 'Inicial',
      CONDUCE: 'Conduce',
      RESERVATION: 'Reserva',
      RELEASE: 'Liberación',
    },
    BULK_MODAL: {
      TITLE: 'Ajuste Masivo de Inventario',
      SUBTITLE:
        'Agregue múltiples productos para ajustar sus cantidades en inventario',
      SELECT_PRODUCT: 'Seleccionar Producto',
      SELECT_PRODUCT_PLACEHOLDER: 'Buscar producto por nombre o SKU...',
      PRODUCT_AUTO_ADD_HINT:
        'Seleccione un producto, configure los detalles y agregue a la lista',
      SELECTED_PRODUCT: 'Producto Seleccionado',
      NO_PRODUCT_SELECTED: 'Seleccione un producto para configurar el ajuste',
      RECIPE_WARNING:
        'Este producto es una receta. El ajuste solo modificará el saldo de este producto — los ingredientes NO se descontarán automáticamente. Si necesitas corregir el stock de los ingredientes, edítalos individualmente.',
      ADD_TO_LIST: 'Agregar a la Lista',
      CLEAR_SELECTION: 'Limpiar Selección',
      INCREASE_STOCK: '+ Aumentar Stock',
      DECREASE_STOCK: '- Reducir Stock',
      MODE_DELTA: 'Ajustar por diferencia',
      MODE_ABSOLUTE: 'Establecer cantidad total',
      ACTION_TITLE: '¿Qué quieres hacer?',
      ACTION_INCREASE_TITLE: 'Aumentar',
      ACTION_INCREASE_HINT: 'Sumar unidades al stock actual',
      ACTION_DECREASE_TITLE: 'Reducir',
      ACTION_DECREASE_HINT: 'Restar unidades del stock actual',
      ACTION_SET_TITLE: 'Establecer',
      ACTION_SET_HINT: 'Fijar la cantidad total en inventario',
      ADD_TO_LIST_INCREASE: 'Agregar +{{qty}} al ajuste',
      ADD_TO_LIST_DECREASE: 'Agregar -{{qty}} al ajuste',
      ADD_TO_LIST_SET: 'Fijar en {{target}} (Δ {{delta}}) al ajuste',
      ADD_TO_LIST_DISABLED: 'Completa los datos para continuar',
      CURRENT_STOCK_LABEL: 'Stock actual',
      ABSOLUTE_QUANTITY_LABEL: 'Cantidad real en inventario',
      ABSOLUTE_QUANTITY_PLACEHOLDER: 'Ej: 12',
      ABSOLUTE_DELTA_PREVIEW:
        'Se registrará un ajuste de {{delta}} para llevar el stock a {{target}}.',
      ABSOLUTE_NO_CHANGE_HINT:
        'La cantidad ingresada coincide con el stock actual — no se creará movimiento.',
      ADDED_ITEMS: 'Productos Agregados',
      NO_ITEMS_ADDED: 'No hay productos agregados',
      REMOVE_ITEM: 'Eliminar',
      SUBMIT_ALL: 'Guardar Todos los Ajustes',
      SUBMITTING: 'Guardando ajustes...',
      SUCCESS_MESSAGE: 'Ajustes de inventario guardados exitosamente',
      CONFIRM_TITLE: 'Confirmar Ajustes de Inventario',
      CONFIRM_DESCRIPTION:
        '¿Está seguro que desea guardar {{count}} ajuste(s) de inventario? Esta acción modificará el stock de los productos seleccionados.',
      VALIDATION: {
        PRODUCT_REQUIRED: 'El producto es requerido',
        QUANTITY_MIN: 'La cantidad debe ser mayor a 0',
        REASON_REQUIRED: 'El motivo es requerido',
        MIN_ONE_PRODUCT: 'Debes agregar al menos un producto',
      },
    },
  },
  STOCK_COUNTING: {
    TAB_MOVEMENTS: 'Movimientos',
    TAB_COUNTING: 'Conteos',
    ADD_COUNTING: 'Nuevo Conteo',
    NO_COUNTINGS: 'No hay conteos registrados',
    FILTER_RESULTS: 'Filtrar Conteos',
    CLEAR_FILTERS: 'Limpiar Filtros',
    SEARCH_PLACEHOLDER: 'Buscar por nombre...',
    NAME: 'Nombre',
    WAREHOUSE: 'Almacén',
    START_DATE: 'Fecha de Inicio',
    END_DATE: 'Fecha de Finalización',
    STATUS: 'Estado',
    STATUS_IN_PROGRESS: 'En Progreso',
    STATUS_COMPLETED: 'Completado',
    DETAIL_TITLE: 'Detalle del Conteo',
    PRODUCT_NAME: 'Producto',
    PRODUCT_SKU: 'SKU / Referencia',
    PRODUCT_BARCODE: 'Código de Barras',
    LOCATION: 'Ubicación Actual',
    PREVIOUS_LOCATION: 'Ubicación Anterior',
    SYSTEM_STOCK: 'Stock del Sistema',
    COUNTED_QUANTITY: 'Cantidad Contada',
    DISCREPANCY: 'Discrepancia',
    COMPLETE_COUNTING: 'Completar Conteo',
    COMPLETING: 'Completando...',
    FILTER_PRODUCT: 'Buscar producto, SKU, código de barras...',
    ADD_PRODUCT: 'Agregar Producto',
    PRODUCT_CATALOG: 'Catálogo de Productos',
    COUNTING_ITEMS: 'Productos del Conteo',
    ITEM_ADDED_SUCCESS: 'Producto agregado al conteo',
    ITEM_UPDATED_SUCCESS: 'Cantidad actualizada correctamente',
    ITEM_DELETED_SUCCESS: 'Producto eliminado del conteo',
    COUNTING_CREATED_SUCCESS: 'Conteo creado correctamente',
    COUNTING_COMPLETED_SUCCESS:
      'Conteo completado. El inventario ha sido actualizado.',
    NO_ITEMS: 'No hay productos en este conteo',
    CONFIRM_COMPLETE_TITLE: 'Confirmar Completar Conteo',
    CONFIRM_COMPLETE_DESCRIPTION:
      '¿Desea completar el conteo? Se actualizará el inventario basado en las cantidades contadas. Esta acción no se puede deshacer.',
    CREATE_TITLE: 'Crear Nuevo Conteo',
    CREATE_SUBTITLE: 'Configure el conteo físico de inventario',
    COUNTING_NAME: 'Nombre del Conteo',
    COUNTING_NAME_PLACEHOLDER: 'Ej: Conteo de Categoría X',
    SELECT_WAREHOUSE: 'Almacén',
    SELECT_WAREHOUSE_PLACEHOLDER: 'Seleccione un almacén (opcional)',
    NOTES_LABEL: 'Notas',
    NOTES_PLACEHOLDER: 'Notas adicionales...',
    SAVE: 'Crear Conteo',
    SAVING: 'Creando...',
    NOTES: 'Notas',
    BARCODE_SCAN_PLACEHOLDER: 'Escanear código de barras o SKU...',
    BARCODE_NOT_FOUND: 'Producto no encontrado',
    BARCODE_ALREADY_ADDED: 'El producto ya está en el conteo',
    COMPLETE_MISSING_QUANTITIES:
      'Todos los productos deben tener una cantidad contada antes de completar el conteo.',
    VALIDATION: {
      NAME_REQUIRED: 'El nombre del conteo es requerido',
    },
  },
  KDS: {
    TITLE: 'Display de Ordenes (KDS)',
    SUBTITLE: 'Sistema de visualización de órdenes',
    TODO: 'Por Preparar',
    PREPARING: 'Preparando',
    READY: 'Listo',
    NO_ORDERS: 'No hay órdenes',
    LOADING: 'Cargando órdenes...',
    START_PREPARING: 'Iniciar Preparación',
    MARK_READY: 'Marcar Listo',
    COMPLETE: 'Completar',
    ORDER_NUMBER: 'Número de Orden',
    TABLE_NUMBER: 'Número de Mesa',
    PRODUCT_NAME: 'Nombre del Producto',
    QUANTITY: 'Cantidad',
    NOTES: 'Notas',
    CREATED_AT: 'Creado',
    MOVE_NEXT: 'Siguiente',
    MOVE_BACK: 'Regresar',
    CANCELLED: 'Cancelado',
    CANCEL_ORDER: 'Cancelar Orden',
    OVERDUE: 'Atrasado',
    WARNING: 'Advertencia',
    VIEW_DETAILS: 'Ver detalles',
    TABLE: 'Mesa',
    ELAPSED: 'Tiempo transcurrido',
    ITEMS: 'Ítems',
    PREP_TIME: 'Tiempo de preparación',
    VENDOR: 'Vendedor',
    DELIVERY_DATE: 'Fecha de entrega',
    DISPATCHED_BY: 'Enviada por',
    COLUMN_NAME: 'Nombre de Columna',
    COLUMN_STATE_LABEL: 'Estado',
    COLUMN_STATE_PLACEHOLDER: 'Seleccionar estado',
    STATE_ORDERED: 'Ordenado',
    STATE_PREPARING: 'Preparando',
    STATE_READY: 'Listo',
    STATE_TODO: 'Por preparar',
    STATE_SEARCHING: 'Buscando',
    STATE_DONE: 'Hecho',
    ADD_COLUMN: 'Agregar Columna',
    EDIT_COLUMN: 'Editar Columna',
    DELETE_COLUMN: 'Eliminar Columna',
    DELETE_COLUMN_BLOCKED_BOOKEND:
      'No se puede eliminar la primera ni la última columna del flujo.',
    CONFIGURATION: 'Configuración',
    OVERDUE_TIME: 'Tiempo de atraso (min)',
    WARNING_TIME: 'Tiempo de advertencia (min)',
    ADD_COLUMN_DESCRIPTION: 'Añade una nueva columna al tablero de órdenes',
    EDIT_COLUMN_DESCRIPTION: 'Modifica la configuración de esta columna',
    COLUMN_NAME_PLACEHOLDER: 'Ej: En preparación',
    COLUMN_COLOR: 'Color de la columna',
    COLUMN_OPTIONS: 'Opciones de la columna',
    ALLOW_CANCEL: 'Permitir cancelar órdenes',
    ALLOW_BACK: 'Permitir regresar órdenes',
    DELETE_COLUMN_TITLE: 'Eliminar Columna',
    DELETE_COLUMN_CONFIRM:
      '¿Estás seguro de que deseas eliminar esta columna? Las órdenes en esta columna serán movidas a la primera columna.',
    MOVE_COLUMN_LEFT: 'Mover columna a la izquierda',
    MOVE_COLUMN_RIGHT: 'Mover columna a la derecha',
    STATION_FILTER_LABEL: 'Filtrar por estación',
    STATION_ALL: 'Todas',
    STATION_KITCHEN: 'Cocina',
    STATION_BAR: 'Bar',
    STATION_WAREHOUSE: 'Almacén',
    NOT_ENABLED_TITLE: 'KDS no está habilitado',
    NOT_ENABLED_DESCRIPTION:
      'El sistema de pantalla de ordenes (KDS) no está habilitado para este negocio. Contacta a un administrador para activarlo desde la configuración del negocio.',
    NOT_ENABLED_ADMIN_DESCRIPTION:
      'El sistema de pantalla de ordenes (KDS) no está habilitado. Actívalo desde la configuración del negocio para comenzar a gestionar las órdenes en tiempo real.',
    GO_TO_SETTINGS: 'Ir a configuración',
    HISTORY: 'Historial',
    HISTORY_TITLE: 'Historial de órdenes KDS',
    HISTORY_SUBTITLE:
      'Órdenes completadas o canceladas dentro del rango seleccionado',
    HISTORY_FROM: 'Desde',
    HISTORY_TO: 'Hasta',
    HISTORY_STATUS: 'Estado',
    HISTORY_STATUS_ALL: 'Todos',
    HISTORY_STATUS_COMPLETED: 'Completadas',
    HISTORY_STATUS_CANCELLED: 'Canceladas',
    HISTORY_ENVIRONMENT: 'Ambiente',
    HISTORY_ENVIRONMENT_ALL: 'Todos los ambientes',
    HISTORY_EMPTY: 'No hay órdenes para el rango seleccionado',
    HISTORY_ORDER: 'Orden',
    HISTORY_ITEMS: 'Productos',
    HISTORY_CREATED: 'Creada',
    HISTORY_UPDATED: 'Actualizada',
    HISTORY_SOMMELIER: 'Mesero',
    HISTORY_NOTES: 'Notas',
    HISTORY_ACTIONS: 'Acciones',
    HISTORY_REOPEN: 'Reabrir',
    HISTORY_REOPEN_TOOLTIP: 'Devolver la orden a la primera columna',
    HISTORY_TIMELINE: 'Línea de tiempo',
    HISTORY_TIMELINE_TOOLTIP:
      'Ver el recorrido de la factura por cada nodo del flujo',
    HISTORY_TIMELINE_TITLE: 'Línea de tiempo — Factura {{number}}',
    HISTORY_TIMELINE_EMPTY: 'Sin eventos para esta factura.',
    HISTORY_TIMELINE_TOTAL: 'Total',
    HISTORY_TIMELINE_NODE: 'Nodo',
    HISTORY_TIMELINE_ENTERED: 'Inicio',
    HISTORY_TIMELINE_COMPLETED: 'Fin',
    HISTORY_TIMELINE_DURATION: 'Duración',
    HISTORY_PREP_TIME: 'Tiempo estimado',
    HISTORY_PREP_TIME_TOOLTIP:
      'Tiempo de preparación configurado en el producto al momento de crear la orden',
    PREP_TIME_MINUTES_SHORT: 'min',
    ITEM_COLOR: 'Color',
    ITEM_LOCATION: 'Tramo',
    ITEM_BRAND: 'Marca',
    ITEM_SIZE: 'Talla',
    ITEM_DIMENSIONS: 'Dim.',
    ITEM_WEIGHT: 'Peso',
    ITEM_SERIALS: 'Seriales',
    CANCEL_DIALOG_TITLE: 'Cancelar orden',
    CANCEL_DIALOG_DESCRIPTION:
      'Selecciona el motivo de cancelación. Esta acción enviará la orden al historial.',
    CANCEL_DIALOG_REASON_LABEL: 'Motivo',
    CANCEL_DIALOG_CUSTOM_LABEL: 'Detalle adicional',
    CANCEL_DIALOG_CUSTOM_PLACEHOLDER: 'Escribe el motivo...',
    CANCEL_DIALOG_CONFIRM: 'Confirmar cancelación',
    CANCEL_REASON_CLIENTE_CANCELO: 'Cliente canceló',
    CANCEL_REASON_MAL_PREPARADA: 'Orden mal preparada',
    CANCEL_REASON_ORDEN_ERRONEA: 'Orden errónea',
    CANCEL_REASON_SIN_STOCK: 'Producto agotado',
    CANCEL_REASON_CAMBIO_PEDIDO: 'Cliente cambió de pedido',
    CANCEL_REASON_MESA_CERRADA: 'Mesa cerrada antes de servir',
    CANCEL_REASON_TIEMPO_EXCEDIDO: 'Tiempo de espera excedido',
    CANCEL_REASON_DUPLICADA: 'Orden duplicada',
    CANCEL_REASON_OTRO: 'Otro motivo',
    HISTORY_REASON: 'Motivo',
    TAB_BOARD: 'Tablero',
    TAB_HISTORY: 'Historial',
    TAB_WORKFLOW: 'Workflow',
    TAB_ACCESS: 'Accesos',
    NODES_LABEL: 'Nodos KDS',
    NODES_MANAGE: 'Administrar',
    NODES_EMPTY_HINT: 'Aún no hay nodos KDS configurados.',
    SELECT_NODE_HINT: 'Selecciona un nodo a la izquierda para ver su tablero.',
    ACCESS_USERS: 'Usuarios',
    ACCESS_PER_NODE: 'Accesos por nodo KDS',
    ACCESS_ASSIGNED_COUNT: '{{count}} asignados',
    ACCESS_NONE: 'Sin acceso',
    ACCESS_READER: 'Lector',
    ACCESS_OPERATOR: 'Operador',
    ACCESS_SUPERVISOR: 'Supervisor',
    ACCESS_NO_USERS: 'No hay usuarios en este negocio.',
    ACCESS_NO_NODES:
      'Crea al menos un nodo en la pestaña Workflow antes de asignar accesos.',
    ACCESS_SAVED: 'Accesos guardados',
    ACCESS_SAVE: 'Guardar',
    SUBNODE: 'Subnodo',
    ONBOARD_TITLE: 'Configura tu primer workflow KDS',
    ONBOARD_BODY_RESTAURANT:
      'Un workflow describe el flujo de preparación: cocina → grill → empaque. Crea aquí los nodos que necesites; cada salón que añadas en Restaurantes también se materializa como un nodo automáticamente, pero no es obligatorio para empezar.',
    ONBOARD_BODY_GENERIC:
      'Un workflow describe el flujo de preparación: cocina → grill → empaque. Los pedidos avanzan automáticamente entre nodos cuando se marcan como listos. KDS funciona con cualquier pedido — desde POS, factura o el salón de restaurante.',
    ONBOARD_CTA_RESTAURANT: 'Crear primer nodo',
    ONBOARD_CTA_GENERIC: 'Crear primer nodo',
    ONBOARD_HOW_IT_WORKS: 'Cómo funciona',
    ONBOARD_STEP_NODES:
      '1. Crea los nodos del flujo (cocina, bar, grill, empaque…).',
    ONBOARD_STEP_ROUTE:
      '2. Asocia productos o categorías al nodo donde se preparan. POS, factura o salón los enrutan automáticamente.',
    ONBOARD_STEP_ACCESS:
      '3. Asigna accesos para que cada usuario solo vea su tablero.',
    NODE_DELETE: 'Eliminar',
    NODE_DELETE_TITLE: '¿Eliminar nodo KDS?',
    NODE_DELETE_DESCRIPTION:
      'El nodo se eliminará lógicamente. No podrás eliminarlo si tiene órdenes activas pendientes.',
    NODE_DELETED: 'Nodo eliminado',
    NODE_EDIT: 'Editar',
    ACCESS_DENIED_TITLE: 'Sin acceso a KDS',
    ACCESS_DENIED_DESCRIPTION:
      'Aún no tienes acceso a ninguna pantalla KDS. Pídele al administrador que te asigne acceso desde KDS → Accesos.',
    STRATEGY_HELP_TITLE: 'Cómo funcionan las estrategias',
    STRATEGY_HELP_ALL:
      '**Todos los hijos**: el nodo padre se marca como completado solo cuando TODOS sus subnodos están listos. Usa esta opción cuando un pedido necesite pasar por varias estaciones en paralelo (ej. cocina + bar) antes de salir.',
    STRATEGY_HELP_SEQUENTIAL:
      '**Secuencial**: el padre avanza un hijo a la vez, en orden. Cada subnodo solo recibe pedidos cuando el anterior terminó. Útil para flujos lineales (ej. masa → horno → empaque).',
    STRATEGY_HELP_PARALLEL:
      '**Paralelo**: el padre se marca como completado en cuanto CUALQUIER subnodo termina. Útil cuando varias estaciones pueden cubrir el mismo paso (ej. dos cocineros, primero en terminar gana).',
    STRATEGY_LABEL_ALL: 'Todos los hijos',
    STRATEGY_LABEL_SEQUENTIAL: 'Secuencial',
    STRATEGY_LABEL_PARALLEL: 'Paralelo',
    NODE_MANAGER_TITLE: 'Administrar nodos KDS',
    NODE_MANAGER_SECTION: 'Nodos',
    NODE_MANAGER_NEW: 'Nuevo',
    NODE_MANAGER_EMPTY: 'Sin nodos aún.',
    NODE_MANAGER_ROOM_ARIA: 'Salón',
    NODE_MANAGER_FIELD_NAME: 'Nombre',
    NODE_MANAGER_FIELD_NAME_PLACEHOLDER: 'Cocina / Grill / Empaque',
    NODE_MANAGER_FIELD_PARENT: 'Padre',
    NODE_MANAGER_FIELD_PARENT_NONE: '— Sin padre —',
    NODE_MANAGER_FIELD_NEXT: 'Nodo al finalizar este',
    NODE_MANAGER_FIELD_NEXT_NONE: '— Ninguno —',
    NODE_MANAGER_FIELD_STRATEGY: 'Estrategia de finalización',
    NODE_MANAGER_FIELD_ENTRY: 'Nodo de entrada',
    NODE_MANAGER_FIELD_ACTIVE: 'Activo',
    NODE_MANAGER_SAVED_TOAST: 'Nodo guardado',
    NODE_MANAGER_DELETED_TOAST: 'Nodo eliminado',
    NODE_MANAGER_CONFIRM_DELETE_TITLE: '¿Eliminar nodo KDS?',
    NODE_MANAGER_CONFIRM_DELETE_BODY:
      'El nodo se eliminará lógicamente. Las órdenes existentes no se moverán automáticamente.',
    NODE_MANAGER_ENTRY_NO_PARENT_HINT:
      'Los nodos de entrada no pueden tener padre. Desactiva "Nodo de entrada" para asignar uno.',
    REPORTS_TITLE: 'Reportes KDS',
    REPORTS_SUBTITLE:
      'Métricas por nodo, desempeño por operador y línea de tiempo por factura.',
    REPORTS_OPEN_PAGE: 'Abrir reportes',
    REPORTS_NODE_PICKER: 'Nodo',
    REPORTS_NODE_PICKER_PLACEHOLDER: 'Selecciona un nodo',
    REPORTS_NODE_PICKER_ALL: 'Todos los nodos',
    REPORTS_TRANSITIONS_TITLE: 'Tiempos por columna',
    REPORTS_TRANSITIONS_EMPTY:
      'Sin transiciones registradas para esta comanda.',
    REPORTS_TRANSITIONS_COL_STAGE: 'Etapa',
    REPORTS_TRANSITIONS_COL_ENTERED: 'Entrada',
    REPORTS_TRANSITIONS_COL_LEFT: 'Salida',
    REPORTS_TRANSITIONS_COL_DURATION: 'Duración',
    REPORTS_TRANSITIONS_TOTAL: 'Total',
    REPORTS_TRANSITIONS_ACTION: 'Ver tiempos',
    COLUMN_TODO: 'Por hacer',
    COLUMN_PREPARING: 'Preparando',
    COLUMN_READY: 'Listo',
    COLUMN_DELIVERED: 'Entregado',
    COLUMN_ON_HOLD: 'En espera',
    REPORTS_METRICS_TITLE: 'Métricas del nodo',
    REPORTS_METRIC_ORDERS: 'Órdenes procesadas',
    REPORTS_METRIC_AVG_DURATION: 'Duración promedio',
    REPORTS_METRIC_REOPENS: 'Reaperturas',
    REPORTS_METRIC_COMPLETED: 'Completadas',
    REPORTS_METRIC_CANCELLED: 'Canceladas',
    REPORTS_METRIC_COMPLETION_RATE: 'Tasa de completación',
    REPORTS_OPERATORS_TITLE: 'Desempeño por operador',
    REPORTS_OPERATOR_USER: 'Usuario',
    REPORTS_OPERATOR_ORDERS: 'Órdenes atendidas',
    REPORTS_OPERATOR_AVG_HANDLE: 'Tiempo promedio',
    REPORTS_OPERATOR_SLA: 'Excesos SLA',
    REPORTS_OPERATOR_ANON: 'Sin asignar',
    REPORTS_OPERATORS_EMPTY: 'Aún no hay actividad en este nodo.',
    REPORTS_OPERATORS_SEARCH_PLACEHOLDER: 'Buscar operador',
    REPORTS_FILTER_FROM: 'Desde',
    REPORTS_FILTER_TO: 'Hasta',
    REPORTS_FILTER_OPERATOR: 'Operador',
    REPORTS_FILTER_OPERATOR_ALL: 'Todos los operadores',
    REPORTS_FILTER_PRESET_TODAY: 'Hoy',
    REPORTS_FILTER_PRESET_7D: 'Últimos 7 días',
    REPORTS_FILTER_PRESET_30D: 'Últimos 30 días',
    REPORTS_PAGINATION_PREV: 'Anterior',
    REPORTS_PAGINATION_NEXT: 'Siguiente',
    REPORTS_PAGINATION_PAGE_OF: 'Página {{page}} de {{total}}',
    REPORTS_PAGINATION_SHOWING:
      'Mostrando {{from}}–{{to}} de {{total}} registros',
    REPORTS_LIFECYCLE_SEARCH_BUTTON: 'Buscar documento',
    REPORTS_LIFECYCLE_CHANGE_INVOICE: 'Cambiar documento',
    REPORTS_LIFECYCLE_DIALOG_TITLE: 'Buscar documento',
    REPORTS_LIFECYCLE_DIALOG_SEARCH_PLACEHOLDER: 'Buscar por número o cliente',
    REPORTS_LIFECYCLE_DIALOG_EMPTY: 'No se encontraron documentos.',
    REPORTS_LIFECYCLE_DIALOG_INVOICE_NUMBER: 'Número',
    REPORTS_LIFECYCLE_DIALOG_CUSTOMER: 'Cliente',
    REPORTS_LIFECYCLE_DIALOG_DATE: 'Fecha',
    REPORTS_LIFECYCLE_DIALOG_TOTAL: 'Total',
    REPORTS_LIFECYCLE_DIALOG_TAB_INVOICES: 'Facturas',
    REPORTS_LIFECYCLE_DIALOG_TAB_ORDERS: 'Órdenes de venta',
    REPORTS_LIFECYCLE_DIALOG_ORDER_NUMBER: 'Orden',
    REPORTS_LIFECYCLE_DIALOG_ORDER_TYPE: 'Tipo',
    REPORTS_LIFECYCLE_DIALOG_ORDER_STATE: 'Estado',
    REPORTS_LIFECYCLE_SELECTED_LABEL: 'Documento seleccionado',
    REPORTS_LIFECYCLE_TITLE: 'Línea de tiempo por documento',
    REPORTS_CLOSED_TITLE: 'Comandas',
    REPORTS_CLOSED_EMPTY: 'No hay comandas en este rango.',
    REPORTS_CLOSED_COL_TICKET: 'Orden',
    REPORTS_CLOSED_COL_COMMANDA: 'Comanda',
    REPORTS_CLOSED_COL_NODE: 'Nodo',
    REPORTS_CLOSED_COL_CLOSED_AT: 'Cerrada',
    REPORTS_CLOSED_COL_DURATION: 'Duración',
    REPORTS_CLOSED_COL_OPERATOR: 'Operador',
    REPORTS_CLOSED_ROW_VIEW: 'Ver línea de tiempo',
    REPORTS_LIFECYCLE_INVOICE_LABEL: 'ID del documento',
    REPORTS_LIFECYCLE_INVOICE_PLACEHOLDER:
      'Selecciona un documento con Buscar documento',
    REPORTS_LIFECYCLE_NODE_COL: 'Nodo',
    REPORTS_LIFECYCLE_ENTERED_COL: 'Inicio',
    REPORTS_LIFECYCLE_COMPLETED_COL: 'Fin',
    REPORTS_LIFECYCLE_DURATION_COL: 'Duración',
    REPORTS_LIFECYCLE_TOTAL: 'Total',
    REPORTS_LIFECYCLE_EMPTY: 'Sin eventos para esta factura.',
    REPORTS_LIFECYCLE_LOAD: 'Buscar línea de tiempo',
    REPORTS_EXPORT_CSV: 'Exportar CSV',
    REPORTS_EXPORT_PDF: 'Exportar PDF',
    REPORTS_PAYWALL_PITCH:
      'Los reportes KDS forman parte del paquete de reportes avanzados.',
    REPORTS_PAYWALL_FEATURE_METRICS:
      'Métricas por nodo (volumen, duración, reaperturas)',
    REPORTS_PAYWALL_FEATURE_OPERATORS: 'Desempeño por operador con excesos SLA',
    REPORTS_PAYWALL_FEATURE_LIFECYCLE:
      'Línea de tiempo por factura paso a paso',
    REPORTS_PAYWALL_FEATURE_EXPORT: 'Exportación a CSV y PDF',
    REPORTS_NODES_EMPTY:
      'Aún no hay flujos KDS configurados. Crea uno desde el tablero principal.',
    WORKFLOWS_TAB: 'Flujos de trabajo',
    WORKFLOWS_TAB_SUBTITLE:
      'Define qué eventos generan trabajo en el KDS y cómo cada nodo reacciona a ellos.',
    WORKFLOW_ADD: 'Agregar flujo',
    WORKFLOW_EDIT: 'Editar flujo',
    WORKFLOW_DELETE: 'Eliminar flujo',
    WORKFLOW_LABEL: 'Nombre',
    WORKFLOW_CODE: 'Código',
    WORKFLOW_ACTIVE: 'Activo',
    WORKFLOW_EMPTY: 'Aún no hay flujos configurados.',
    WORKFLOW_STATES_TITLE: 'Estados',
    STATE_ADD: 'Agregar estado',
    STATE_LABEL: 'Nombre',
    STATE_CODE: 'Código',
    STATE_SORT: 'Orden',
    STATE_DELETE: 'Eliminar',
    STATE_SAVE: 'Guardar estados',
    SUBSCRIPTIONS_TAB: 'Suscripciones de nodos',
    SUBSCRIPTION_ADD: 'Agregar suscripción',
    SUBSCRIPTION_NODE: 'Nodo',
    SUBSCRIPTION_WORKFLOW: 'Flujo',
    SUBSCRIPTION_PRIORITY: 'Prioridad',
    SUBSCRIPTION_ALLOW_UPDATES: 'Permitir actualizaciones',
    SUBSCRIPTION_COLOR: 'Color',
    SUBSCRIPTION_ASSIGNEE: 'Asignado por defecto',
    SUBSCRIPTION_STATE_ACTIONS: 'Acciones por estado',
    SUBSCRIPTION_ACTION_CREATE: 'Crear ticket',
    SUBSCRIPTION_ACTION_UPDATE: 'Actualizar ticket',
    SUBSCRIPTION_ACTION_CLOSE: 'Cerrar ticket',
    SUBSCRIPTION_ACTION_CANCEL: 'Cancelar ticket',
    SUBSCRIPTION_ACTION_IGNORE: 'Ignorar',
    SUBSCRIPTION_EMPTY: 'Aún no hay suscripciones configuradas.',
    WORKFLOW_TOAST_CREATED: 'Flujo creado.',
    WORKFLOW_TOAST_UPDATED: 'Flujo actualizado.',
    WORKFLOW_TOAST_DELETED: 'Flujo eliminado.',
    SUBSCRIPTION_TOAST_SAVED: 'Suscripción guardada.',
    SUBSCRIPTION_TOAST_DELETED: 'Suscripción eliminada.',
  },
  RENTALS: {
    TITLE: 'Alquileres',
    SUBTITLE:
      'Gestiona pedidos, reservas, calendario y recogido de tus alquileres.',
    NEW: 'Nuevo alquiler',
    EMPTY_TITLE: 'Sin alquileres',
    EMPTY_BODY: 'Crea tu primer pedido de alquiler para verlo aquí.',
    COL_RENTAL_NUMBER: 'Número',
    COL_CUSTOMER: 'Cliente',
    COL_STATE: 'Estado',
    COL_START_DATE: 'Desde',
    COL_END_DATE: 'Hasta',
    COL_TOTAL: 'Total',
    COL_FIANZA: 'Fianza',
    FIANZA_REFUNDABLE_LABEL: 'Fianza (reembolsable)',
    COL_BADGES: 'Pago',
    COL_ACTIONS: 'Acciones',
    STATE_DRAFT: 'Borrador',
    STATE_RESERVED: 'Reservado',
    STATE_CONFIRMED: 'Confirmado',
    STATE_ABONO: 'Abono',
    ABONO_SUMMARY_LABEL: 'Abono mínimo requerido',
    ABONO_PENDING_LABEL: 'Abono pendiente',
    ADD_ITEMS_TITLE: 'Agregar artículos',
    ADD_ITEMS_REOPEN_WARNING:
      'Al guardar, el pedido volverá a Confirmado para que puedas enviarlo a producción y marcarlo listo nuevamente.',
    ACTION_ADD_ITEMS: 'Agregar artículos',
    COBROS_LABEL: 'Cobros',
    PAYMENT_RETAINED: 'Retenido',
    PAYMENT_REFUNDED: 'Reembolsado',
    CANCEL_NO_REFUND_WARNING:
      'Al cancelar, el monto cobrado ({{amount}}) se perderá: la política del negocio no reembolsa los pagos al cancelar. El cliente no recibirá el abono de vuelta y esta acción no se puede deshacer.',
    ABONO_PENDING_TITLE: 'Falta cobrar el abono',
    ABONO_PENDING_BODY:
      'Este pedido requiere un abono mínimo de {{required}} antes de reservar o confirmar. Cobrado: {{paid}}.',
    STATE_IN_PRODUCTION: 'En producción',
    STATE_READY: 'Listo',
    STATE_DELIVERED: 'Entregado',
    STATE_RETURNED: 'Devuelto',
    STATE_PENDING_DAMAGE_CHARGE: 'Pendiente cobro por daños',
    DAMAGE_INVOICE_LABEL: 'Factura por daños',
    MISSING_PANEL_TITLE: 'Artículos no devueltos',
    MISSING_PANEL_HELP:
      'Estos artículos no fueron devueltos y generan el cargo pendiente. Si el cliente los devuelve, márcalos para reingresarlos al inventario y recalcular el cargo.',
    MISSING_ITEM_CHARGE_HINT: 'Valor del artículo: {{amount}}',
    MISSING_ITEM_RETURN_BTN: 'Devolver',
    MISSING_RETURNED_TOAST: 'Artículo devuelto y reingresado al inventario.',
    STATE_COMPLETED: 'Completado',
    STATE_LATE_COMPLETED: 'Completado tardío',
    STATE_CANCELLED: 'Cancelado',
    BADGE_HAS_FORFEIT: 'Fianza no devuelta',
    BADGE_HAS_DAMAGES: 'Daños',
    BADGE_HAS_LOST_ITEMS: 'Ítems no devueltos',
    BADGE_PENDING_PAYMENT: 'Pago pendiente',
    BADGE_PAID: 'Pagado',
    TAB_RESERVATIONS: 'Reservas',
    TAB_CALENDAR: 'Calendario',
    TAB_RECOGIDO: 'Recogido',
    RESERVATIONS_TITLE: 'Reservas',
    RESERVATIONS_NUMBER: 'Número',
    RESERVATIONS_EXPIRES: 'Expira',
    RESERVATION_NEW: 'Nueva reservación',
    CALENDAR_TITLE: 'Calendario',
    CALENDAR_WINDOW: 'Ventana',
    CALENDAR_TODAY: 'Hoy',
    CALENDAR_ENTRY_KIND: 'Tipo',
    CALENDAR_ENTRY_NUMBER: 'Número',
    CALENDAR_KIND_RENTAL: 'Pedido',
    CALENDAR_KIND_RESERVATION: 'Reserva',
    RECOGIDO_TITLE: 'Recogido',
    RECOGIDO_SUBTITLE: 'Pedidos listos para entregar al cliente.',
    RECOGIDO_TOKEN_EXPIRES: 'Token expira',
    RECOGIDO_EMPTY: 'Sin pedidos listos para recoger.',
    RECOGIDO_VALIDATE_TITLE: 'Validar token de recogido',
    RECOGIDO_VALIDATE_HELP:
      'Escanea el código QR del cliente o ingresa el token para validarlo y entregar el pedido.',
    RECOGIDO_VALIDATE_PLACEHOLDER: 'Escanea o ingresa el token',
    RECOGIDO_VALIDATE_BTN: 'Validar',
    RECOGIDO_MARK_COLLECTED: 'Marcar recogido',
    RECOGIDO_COLLECTED_TOAST:
      'Pedido entregado. El cliente puede retirar los artículos.',
    RECOGIDO_RESEND_EMAIL: 'Reenviar correo',
    RECOGIDO_RESEND_EMAIL_TOAST: 'Correo de recogido reenviado al cliente.',
    RECOGIDO_SHARE_WHATSAPP: 'WhatsApp',
    PICKUP_SHARE_TITLE: 'Compartir código de recogido',
    PICKUP_SHARE_DESC:
      'Envía el código QR de recogido al cliente por correo o WhatsApp.',
    PICKUP_SHARE_QR_HINT:
      'El cliente presenta este código en el mostrador para recoger.',
    PICKUP_SHARE_QR_ERROR:
      'No se pudo cargar el código QR. Verifica tu conexión e inténtalo de nuevo.',
    RECOGIDO_WHATSAPP_OPENED_TOAST:
      'Abriendo WhatsApp con el token de recogido.',
    CONTACT_MODAL_TITLE_EMAIL: 'Agregar correo del cliente',
    CONTACT_MODAL_TITLE_WHATSAPP: 'Agregar teléfono del cliente',
    CONTACT_MODAL_DESC_EMAIL:
      'El cliente no tiene un correo registrado. Agrégalo para reenviar el token de recogido.',
    CONTACT_MODAL_DESC_WHATSAPP:
      'El cliente no tiene un teléfono registrado. Agrégalo para compartir el token por WhatsApp.',
    CONTACT_MODAL_EMAIL_LABEL: 'Correo electrónico',
    CONTACT_MODAL_PHONE_LABEL: 'Teléfono',
    CONTACT_MODAL_INVALID_EMAIL: 'Correo electrónico inválido.',
    CONTACT_MODAL_NO_CUSTOMER:
      'Este pedido no tiene un cliente registrado para actualizar.',
    TILE_IN_PRODUCTION: 'Artículos en producción',
    TILE_READY: 'Artículos listos para recoger',
    TILE_RENTED: 'Artículos alquilados',
    TILE_PENDING_DAMAGE: 'Artículos pendientes de cobro por daños',
    TILE_ITEMS_BTN: 'Ver artículos',
    TILE_ITEMS_DESC: 'Artículos de los pedidos en este estado.',
    TILE_ITEMS_EMPTY: 'No hay artículos en este estado.',
    TILE_ITEMS_QTY: 'Cant: {{qty}}',
    CLIENT_REQUIRED_TITLE: 'Selecciona un cliente específico',
    CLIENT_REQUIRED_DESC:
      'Para enviar el código de recogido por correo o WhatsApp, el pedido necesita un cliente con correo y teléfono. Agrega el contacto al cliente actual o selecciona otro.',
    CLIENT_REQUIRED_NOT_GENERIC:
      'No se puede usar el cliente genérico. Selecciona un cliente registrado.',
    CLIENT_REQUIRED_NEED_CONTACT:
      'El cliente seleccionado necesita correo y teléfono. Complétalos para continuar.',
    PICKUP_COLLECT_TITLE: 'Validar recogido',
    PICKUP_OPEN_BTN: 'Validar recogido',
    PICKUP_SCAN_BTN: 'Escanear QR',
    PICKUP_SCAN_STOP: 'Detener cámara',
    PICKUP_TOKEN_MISMATCH: 'El código no corresponde a este pedido.',
    PICKUP_CAMERA_ERROR: 'No se pudo acceder a la cámara.',
    ACTION_MARK_RETURNED: 'Marcar devuelto',
    RETURN_TITLE: 'Procesar devolución',
    RETURN_HELP:
      'Marca cada artículo como devuelto o no devuelto. Un artículo no devuelto cancela la fianza; una devolución fuera de plazo puede perder la fianza según la configuración.',
    RETURN_CONFIRM: 'Confirmar devolución',
    RETURN_DONE_TOAST: 'Devolución procesada.',
    RETURN_ITEM_NOT_RETURNED: 'No devuelto',
    RETURN_ITEM_DAMAGE: 'Monto por daños',
    RETURN_ITEM_CHARGE: 'Se cobrará {{amount}} por no devolverlo.',
    RETURN_CONDITION_LABEL: 'Condición',
    RETURN_CONDITION_GOOD: 'Buen estado',
    RETURN_CONDITION_DAMAGED: 'Dañado',
    RETURN_CONDITION_LOST: 'Perdido / No devuelto',
    RETURN_CHARGE_PREVIEW: 'Cargo por daños estimado',
    ADJUST_CHARGE_BTN: 'Ajustar cargo',
    ADJUST_CHARGE_TITLE: 'Ajustar cargo por daños',
    ADJUST_CHARGE_HELP:
      'Reduce o anula el cargo pendiente por daños o artículos no devueltos. Poner 0 anula el cargo y completa el pedido.',
    ADJUST_CHARGE_CURRENT: 'Cargo actual: {{amount}}',
    ADJUST_CHARGE_AMOUNT_LABEL: 'Nuevo cargo',
    ADJUST_CHARGE_REASON_LABEL: 'Motivo',
    ADJUST_CHARGE_WAIVE_BTN: 'Anular cargo (0)',
    ADJUST_CHARGE_CONFIRM: 'Guardar ajuste',
    ADJUST_CHARGE_TOAST: 'Cargo por daños actualizado.',
    WAIVE_CHARGE_TOAST: 'Cargo por daños anulado.',
    RETURN_ITEM_CANCELS_DEPOSIT: 'Cancela la fianza del pedido.',
    RETURN_OVERRIDE_LATE: 'Devolver fianza aunque sea tarde',
    RETURN_OVERRIDE_LATE_HELP:
      'Permite devolver la fianza aun cuando la devolución es fuera de plazo.',
    REFUND_DEPOSIT_BUTTON: 'Devolver fianza',
    REFUND_DEPOSIT_TITLE: 'Devolver fianza',
    REFUND_PERCENT_LABEL: 'Porcentaje a devolver',
    REFUND_PREVIEW_LABEL: 'Fianza a devolver',
    REFUNDABLE_BALANCE_LABEL: 'Saldo de fianza disponible',
    REFUND_REASON_LABEL: 'Motivo (opcional)',
    REFUND_DONE_TOAST: 'Fianza devuelta.',
    DEPOSIT_REFUNDED_LABEL: 'Fianza devuelta',
    TIMELINE_TITLE: 'Estado del pedido',
    TIMELINE_CURRENT: 'Estado actual',
    RECOGIDO_NOT_READY: 'Este pedido no está en estado Listo para recoger.',
    RECOGIDO_RESOLVED_READY: 'Token válido — listo para recoger.',
    FORM_SECTION_CUSTOMER: 'Cliente',
    FORM_SECTION_PERIOD: 'Período de alquiler',
    FORM_SECTION_ITEMS: 'Artículos',
    FORM_CUSTOMER_NAME: 'Nombre',
    FORM_CUSTOMER_RNC: 'RNC / Cédula',
    FORM_CUSTOMER_PHONE: 'Teléfono',
    FORM_CUSTOMER_EMAIL: 'Email',
    FORM_PRODUCT_ID: 'Product ID (uuid)',
    FORM_PRODUCT_NAME: 'Nombre',
    FORM_QUANTITY: 'Cantidad',
    FORM_UNIT_PRICE: 'Precio unitario',
    FORM_SUBTOTAL: 'Subtotal',
    FORM_ADD_ITEM: '+ Agregar ítem',
    FORM_TOTAL: 'Total',
    FORM_PRODUCT: 'Producto',
    FORM_SEARCH_PRODUCT: 'Buscar producto',
    FORM_SEARCH_PRODUCT_PLACEHOLDER: 'Escribe para buscar…',
    FORM_ITEMS_EMPTY: 'Aún no se han agregado ítems. Usa el buscador arriba.',
    FORM_CUSTOMER_PICKER: 'Cliente',
    FORM_CUSTOMER_PICKER_PLACEHOLDER: 'Seleccionar cliente',
    FORM_CUSTOMER_PICKER_SEARCH: 'Buscar cliente…',
    SUMMARY_TITLE: 'Resumen',
    SUMMARY_ITEMS: 'Ítems',
    SUMMARY_DAYS: 'Días',
    SUMMARY_SUBTOTAL: 'Subtotal',
    SUMMARY_TOTAL: 'Total',
    REMAINING_LABEL: 'Pendiente: {{amount}}',
    TOTAL_DUE_LABEL: 'Total a pagar',
    PERIOD_MIN_HINT: 'Mínimo: {{days}} día(s) por configuración de negocio.',
    PAYMENT_DIALOG_TITLE: 'Recibir pago',
    PAYMENT_KIND_LABEL: 'Tipo',
    PAYMENT_KIND_FIANZA: 'Fianza',
    PAYMENT_KIND_BALANCE: 'Balance',
    PAYMENT_AMOUNT_LABEL: 'Monto',
    PAYMENT_METHOD_LABEL: 'Método de pago',
    PAYMENT_REFERENCE_LABEL: 'Referencia',
    PAYMENT_SUBMIT: 'Registrar pago',
    PAYMENT_SUCCESS: 'Pago registrado',
    PAYMENT_ACTION_LABEL: 'Cobrar',
    PAYMENT_STATUS_TITLE: 'Estado de pagos',
    PAYMENT_STATUS_FIANZA: 'Fianza',
    PAYMENT_STATUS_BALANCE: 'Balance',
    PAYMENT_STATUS_PAID: 'Pagado',
    PAYMENT_STATUS_PENDING: 'Pendiente',
    ACTION_CONFIRM: 'Confirmar',
    ACTION_SEND_TO_PRODUCTION: 'Enviar a producción',
    ACTION_MARK_READY: 'Marcar listo',
    ACTION_MARK_DELIVERED: 'Marcar entregado',
    ACTION_PROCESS_RETURN: 'Procesar devolución',
    ACTION_FINALIZE: 'Finalizar (emitir factura)',
    ACTION_CANCEL: 'Cancelar Pedido',
    ACTIONS_TITLE: 'Acciones',
    CUSTOMER_INFO_TITLE: 'Cliente',
    CONFIRM_TITLE_CONFIRM: '¿Confirmar Pedido?',
    CONFIRM_TITLE_SEND_TO_PRODUCTION: '¿Enviar a producción?',
    TOAST_SENT_TO_PRODUCTION: 'Pedido enviado a producción.',
    TOAST_MARKED_READY:
      'Pedido marcado como listo. Se envió el correo con el token QR al cliente.',
    TOAST_MARKED_DELIVERED: 'Pedido entregado al cliente.',
    CONFIRM_TITLE_MARK_READY: '¿Marcar como listo?',
    CONFIRM_TITLE_MARK_DELIVERED: '¿Marcar como entregado?',
    CONFIRM_TITLE_PROCESS_RETURN: '¿Procesar devolución?',
    CONFIRM_TITLE_FINALIZE: '¿Finalizar y emitir factura?',
    CONFIRM_TITLE_CANCEL: '¿Cancelar Pedido?',
    CONFIRM_DESC_CONFIRM:
      'El Pedido pasará al estado confirmado. Se reservará el stock de los ítems y quedará listo para producción.',
    CONFIRM_DESC_SEND_TO_PRODUCTION:
      'El Pedido entrará a producción. A partir de este punto los ítems quedan bloqueados salvo permiso especial.',
    CONFIRM_DESC_MARK_READY:
      'El Pedido pasará a listo y se enviará el correo con el token QR al cliente para el recogido.',
    CONFIRM_DESC_MARK_DELIVERED:
      'El Pedido pasará a entregado al cliente. Requiere que el balance esté pagado por completo.',
    CONFIRM_DESC_PROCESS_RETURN:
      'Iniciarás el flujo de devolución. Podrás inspeccionar los ítems y decidir el reembolso o retención de la fianza.',
    CONFIRM_DESC_FINALIZE:
      'Se emitirá exactamente una factura fiscal a partir del Pedido. La operación es idempotente.',
    CONFIRM_DESC_CANCEL:
      'El Pedido será cancelado. Solo permitido en borrador / reservado / confirmado.',
    EDIT_ITEMS_LABEL: 'Editar Pedido',
    EDIT_ITEMS_DIALOG_TITLE: 'Editar Pedido',
    EDIT_ITEMS_SAVE: 'Guardar cambios',
    EDIT_ITEMS_LOCKED_HINT:
      'El Pedido ya entró en producción. Solo usuarios con permiso pueden modificarlo.',
    EDIT_CANCEL: 'Cancelar edición',
    SAVE_CONFIRM_TITLE: '¿Guardar Pedido?',
    SAVE_CONFIRM_DESC: 'Se creará el Pedido en estado borrador.',
    SAVE_CONFIRM_DESC_FIANZA:
      'Se creará el Pedido y, al terminar, se abrirá el diálogo para cobrar la fianza (configurada como obligatoria).',
    SAVE_CONFIRM_DESC_AND_NEW:
      'Se creará el Pedido y el formulario se limpiará para registrar otro alquiler.',
    SAVE_CONFIRM_DESC_AND_COLLECT:
      'Se creará el Pedido y, al terminar, se abrirá el diálogo para cobrar.',
    SAVE_CONFIRM_DESC_EDIT: 'Se guardarán los cambios del Pedido.',
    SAVE_AND_NEW: 'Guardar y crear nuevo',
    SAVE_AND_COLLECT: 'Guardar y cobrar',
    VIEW_INVOICE: 'Ver factura',
  },
  SALES_ORDERS: {
    TITLE: 'Órdenes de venta',
    SUBTITLE:
      'Gestiona órdenes operativas — desde comandas de restaurante hasta pedidos con adelanto y entrega programada.',
    NEW: 'Nueva orden',
    EMPTY_TITLE: 'Aún no hay órdenes de venta',
    EMPTY_BODY:
      'Crea la primera orden desde una cotización, desde el POS o directamente aquí.',
    COL_ORDER_NUMBER: 'Número',
    COL_TYPE: 'Tipo',
    COL_CUSTOMER: 'Cliente',
    COL_STATE: 'Estado',
    COL_DELIVERY_DATE: 'Fecha de entrega',
    COL_DEPOSIT: 'Adelanto',
    COL_TOTAL: 'Total',
    COL_KDS: 'KDS',
    COL_ACTIONS: 'Acciones',
    KDS_OPEN_DRAWER: 'Ver comandas',
    KDS_DRAWER_TITLE: 'Comandas KDS',
    KDS_DRAWER_EMPTY: 'Aún no hay comandas enviadas.',
    KDS_DRAWER_COLUMN: 'Columna',
    KDS_DRAWER_FLOW: 'Flujo',
    KDS_STATUS_ACTIVE: 'En proceso',
    KDS_STATUS_COMPLETED: 'Lista',
    KDS_STATUS_CANCELLED: 'Anulada',
    KDS_STATUS_ON_HOLD: 'En espera',
    FILTER_STATE: 'Estado',
    FILTER_TYPE: 'Tipo',
    FILTER_CASHIER_QUEUE: 'Cola de cajero (borradores)',
    FILTER_ALL: 'Todos',
    FILTER_DATE_FROM: 'Desde',
    FILTER_DATE_TO: 'Hasta',
    ACTION_CONFIRM: 'Confirmar',
    ACTION_MARK_IN_PRODUCTION: 'Iniciar producción',
    ACTION_MARK_READY: 'Marcar lista',
    ACTION_MARK_DELIVERED: 'Entregar',
    ACTION_MARK_DEPOSIT: 'Recibir adelanto',
    ACTION_CANCEL: 'Cancelar',
    ACTION_DELETE: 'Eliminar',
    ACTION_VIEW_DETAIL: 'Ver detalle',
    ACTION_SEND_TO_PRODUCTION: 'Enviar a producción',
    ACTION_PRINT_ORDER: 'Imprimir orden',
    PRINT_ORDER_LABEL: 'Orden',
    PRINT_ORDER_WATERMARK: 'ORDEN # {{orderNumber}}',
    PRINT_ORDER_HEADER: 'Orden #{{orderNumber}}',
    COPY_DOCUMENT_LINK: 'Copiar enlace',
    WHATSAPP_SHARE_TITLE: 'Enlace del documento',
    WHATSAPP_SHARE_BODY:
      'Copia el enlace del documento para compartirlo con el cliente por WhatsApp, correo o cualquier otro canal.',
    WHATSAPP_SHARE_COPIED: 'Enlace copiado al portapapeles.',
    WHATSAPP_SHARE_OPEN: 'Abrir enlace',
    ACTION_EMIT_DEPOSIT_INVOICE: 'Emitir factura de abono',
    ACTION_EMIT_FINAL_INVOICE: 'Emitir factura final',
    ACTION_CONVERT_TO_INVOICE_AND_PAY: 'Convertir a Factura y pagar',
    DEPOSIT_CONFIGURED_TITLE: 'Adelanto (configurado por el negocio)',
    DETAIL_TOTALS_DEPOSIT: 'Adelanto',
    DETAIL_TOTALS_BALANCE: 'Balance',
    RELATED_DOCS_TITLE: 'Documentos relacionados',
    RELATED_QUOTE: 'Cotización',
    RELATED_DEPOSIT_INVOICE: 'Factura de abono',
    RELATED_FINAL_INVOICE: 'Factura final',
    DEPOSIT_DIALOG_TITLE: 'Recibir adelanto',
    DEPOSIT_DIALOG_FLEXIBLE_HINT:
      'Ingresa el monto que el cliente entregó como adelanto.',
    DEPOSIT_DIALOG_FIXED_HINT:
      'Adelanto configurado: {{amount}}. Ajusta si es necesario.',
    DEPOSIT_DIALOG_AMOUNT_LABEL: 'Monto',
    DEPOSIT_DIALOG_RECOMMENDED_LABEL: 'Recomendado:',
    DEPOSIT_DIALOG_TOTAL_LABEL: 'Total',
    DEPOSIT_DIALOG_METHOD_LABEL: 'Método de pago',
    DEPOSIT_DIALOG_METHOD_PLACEHOLDER: 'Selecciona el método',
    DEPOSIT_DIALOG_METHOD_EMPTY:
      'No hay tipos de pago activos. Configúralos en Configuración → Negocio → Tipos de pago.',
    DEPOSIT_FLEXIBLE_HINT: 'Adelanto libre',
    DEPOSIT_RECEIVED_AMOUNT: 'Adelanto recibido: {{amount}}',
    DEPOSIT_RULE_SKIPPED_HINT:
      'La regla del tipo pide un abono de {{configured}} pero el total de la orden es {{total}}. La regla se omite: acepta cualquier monto positivo hasta el total.',
    TYPE_RESTAURANT: 'Restaurante',
    TYPE_RETAIL: 'Mostrador',
    TYPE_CUSTOM: 'Pedido',
    TYPE_ONLINE: 'Tienda online',
    TYPE_MARKETPLACE: 'Marketplace',
    STATE_DRAFT: 'Borrador',
    STATE_PENDING_CONFIRMATION: 'Pendiente confirmación',
    STATE_PENDING_DEPOSIT: 'Pendiente adelanto',
    STATE_CONFIRMED: 'Confirmada',
    STATE_IN_PRODUCTION: 'En producción',
    STATE_READY: 'Lista',
    STATE_DELIVERED: 'Entregada',
    STATE_CANCELLED: 'Cancelada',
    DEPOSIT_REQUIRED_PAID: 'Adelanto recibida',
    DEPOSIT_REQUIRED_PENDING: 'Adelanto pendiente',
    DEPOSIT_NOT_REQUIRED: 'Sin adelanto',
    TOAST_TRANSITION_OK: 'Estado actualizado.',
    TOAST_DEPOSIT_OK: 'Adelanto registrada.',
    TOAST_NOT_FOUND: 'La orden no existe o fue eliminada.',
    TOAST_CANCEL_OK: 'Orden cancelada.',
    TOAST_DELETE_OK: 'Orden eliminada.',
    CONFIRM_CANCEL_TITLE: '¿Cancelar orden?',
    CONFIRM_CANCEL_BODY:
      'La orden pasará al estado cancelada y no podrá volver a modificarse.',
    CONFIRM_SEND_TO_PRODUCTION_TITLE: '¿Enviar a producción?',
    CONFIRM_SEND_TO_PRODUCTION_BODY:
      'La comanda se enviará a la cocina/producción y la orden quedará confirmada. Podrás seguir agregando o editando ítems solo si la comanda no ha iniciado, pero las modificaciones actualizarán la comanda ya emitida.',
    DEPOSIT_BEFORE_PROD_TITLE: 'Adelanto pendiente',
    DEPOSIT_BEFORE_PROD_BODY:
      'Esta orden tiene un adelanto configurado ({{amount}}). El cliente debe pagarlo antes de enviar la orden a producción.',
    SEND_AFTER_DEPOSIT_TITLE: 'Adelanto registrado',
    SEND_AFTER_DEPOSIT_BODY:
      'El abono quedó registrado. ¿Deseas enviar la orden a producción ahora?',
    ADVISE_VENDEDOR_AFTER_DEPOSIT_TITLE: 'Adelanto registrado',
    ADVISE_VENDEDOR_AFTER_DEPOSIT_BODY:
      'El abono quedó registrado y el recibo fue impreso. Avisa al vendedor para que envíe la orden a producción.',
    DEPOSIT_BEFORE_PROD_BODY_FLEXIBLE:
      'Esta orden requiere un adelanto. El cliente debe pagarlo antes de enviar la orden a producción.',
    DEPOSIT_BEFORE_PROD_CASHIER_TITLE: 'Falta cobrar el adelanto',
    DEPOSIT_BEFORE_PROD_CASHIER_BODY:
      'Antes de enviar esta orden a producción, el cajero debe cobrar el abono o adelanto desde Pagos.',
    CONVERT_CONFIRM_TITLE: '¿Convertir orden a factura?',
    CONVERT_CONFIRM_BODY:
      'Se abrirá la pantalla de factura con los ítems de la orden ya cargados. Si el cliente pagó un abono, se aplicará como descuento y verás el restante a cobrar.',
    CONVERT_CONFIRM_OK: 'Continuar',
    CREATE_ORDER_CONFIRM_TITLE: '¿Crear la orden?',
    CREATE_ORDER_CONFIRM_BODY:
      'Se creará la orden con los ítems y datos ingresados. Podrás seguir editándola hasta que sea enviada a producción o facturada.',
    CREATE_ORDER_CONFIRM_OK: 'Crear orden',
    CONFIRM_ADVANCE_TITLE: '¿Marcar como en producción?',
    CONFIRM_ADVANCE_BODY:
      'La orden pasará al estado "En producción" y la comanda quedará marcada como en preparación en el KDS.',
    CONFIRM_DELETE_TITLE: '¿Eliminar borrador?',
    CONFIRM_DELETE_BODY:
      'Sólo los borradores pueden eliminarse. Esta acción no se puede deshacer.',
    DETAIL_HEADER: 'Encabezado',
    DETAIL_ITEMS: 'Líneas',
    DETAIL_TIMELINE: 'Línea de tiempo',
    DETAIL_BACK: 'Volver',
    DETAIL_NOTES: 'Notas',
    DETAIL_TOTALS_SUBTOTAL: 'Subtotal',
    DETAIL_TOTALS_TAX: 'ITBIS',
    DETAIL_TOTALS_DISCOUNT: 'Descuento',
    DETAIL_TOTALS_TOTAL: 'Total',
    ITEM_COL_PRODUCT: 'Producto',
    ITEM_COL_QTY: 'Cantidad',
    ITEM_COL_PRICE: 'Precio',
    ITEM_COL_TAX: 'ITBIS',
    ITEM_COL_TOTAL: 'Total',
    TIMELINE_EMPTY: 'Aún no hay eventos.',
    TAB_DETAILS: 'Detalles',
    TAB_TIMELINE: 'Línea de tiempo',
    DEPOSIT_HINT_TITLE: 'Este tipo de orden requiere adelanto',
    DEPOSIT_HINT_FIXED: 'Se solicitará un adelanto fijo de {{amount}}.',
    DEPOSIT_HINT_PERCENT:
      'Se solicitará un adelanto de {{percent}}% del total.',
    DEPOSIT_HINT_FLEXIBLE:
      'El monto del adelanto lo define el cliente al momento de procesar la orden.',
    EVENT_ORDER_CREATED: 'Orden creada',
    EVENT_ORDER_UPDATED: 'Orden actualizada',
    EVENT_ORDER_ITEM_ADDED: 'Línea agregada',
    EVENT_ORDER_DEPOSIT_RECEIVED: 'Adelanto recibido',
    EVENT_ORDER_DEPOSIT_INVOICE_EMITTED: 'Factura de adelanto emitida',
    EVENT_ORDER_FINAL_INVOICE_EMITTED: 'Factura final emitida',
    EVENT_ORDER_PAYMENT_SETTLED: 'Orden pagada por completo',
    EVENT_ORDER_PAYMENT_SETTLED_INVOICE: 'Factura {{invoiceNumber}}',
    EVENT_ORDER_INVOICE_CREATED: 'Convertida a factura',
    EVENT_ORDER_INVOICE_CREATED_LINE: 'Factura {{invoiceNumber}}',
    EVENT_ORDER_ALL_COMMANDAS_COMPLETED: 'Todas las commandas completadas',
    HEADER_INVOICE_LINK: 'Factura',
    HEADER_PAID_BADGE: 'Pagada',
    EVENT_ORDER_STATE_TRANSITION: 'Cambio de estado',
    EVENT_ORDER_BACKFILL: 'Migración desde comanda de restaurante',
    CREATE_TITLE: 'Nueva orden',
    CREATE_SUBTITLE:
      'Registra una nueva orden de venta. Podrás agregar líneas y detalles en la pantalla de la orden.',
    CREATE_SUBMIT: 'Crear orden',
    FIELD_TYPE: 'Tipo',
    FIELD_CUSTOMER: 'Cliente',
    FIELD_NOTES: 'Notas',
    FIELD_DELIVERY_DATE: 'Fecha de entrega',
    FIELD_DEPOSIT_REQUIRED: 'Requiere adelanto',
    FIELD_DEPOSIT_TYPE: 'Tipo de adelanto',
    FIELD_DEPOSIT_VALUE: 'Valor de adelanto',
    FIELD_PRINT_RESPONSIBILITY: 'Responsable de imprimir / enviar a producción',
    PRINT_RESPONSIBILITY_SELLER:
      'Vendedor (imprime y envía a producción de una vez)',
    PRINT_RESPONSIBILITY_CASHIER:
      'Cajero (vendedor guarda el borrador; cajero cobra el adelanto e imprime)',
    RESTAURANT_READ_ONLY_TITLE: 'Orden de restaurante — solo lectura',
    RESTAURANT_READ_ONLY_BODY:
      'Esta orden se administra desde la sección del servicio de mesa. Productos, comandas y estados se controlan allí.',
    RESTAURANT_OPEN_SERVICE: 'Abrir servicio',
    HEADER_TABLE: 'Mesa',
    PRINT_RESPONSIBILITY_RESTAURANT_HINT:
      'En órdenes de restaurante la comanda se envía a KDS por cada ítem agregado desde el apartado del servicio de mesa. El cobro y la impresión de la factura al cerrar la mesa se rigen por la configuración de facturación del negocio.',
    DEPOSIT_TYPE_FIXED: 'Monto fijo',
    DEPOSIT_TYPE_PERCENTAGE: 'Porcentaje',
    TOAST_CREATE_OK: 'Orden creada.',
    SETTINGS_TITLE: 'Configuración de órdenes de venta',
    SETTINGS_SUBTITLE:
      'Elige el tipo de orden y define su adelanto (pago anticipado). Cada tipo se guarda por separado — un restaurante puede exigir abono mientras que el mostrador lo deja opcional.',
    SETTINGS_ORDER_TYPE: 'Tipo de orden',
    SETTINGS_SAVED: 'Guardado.',
    SETTINGS_ADD_TYPE: 'Agregar tipo',
    SETTINGS_REMOVE_TYPE: 'Quitar tipo',
    SETTINGS_EDITING_HINT:
      'Editando <strong>{{typeLabel}}</strong> — los cambios se guardan sólo para este tipo.',
    SETTINGS_DEPOSIT_REQUIRED_HELP:
      'Cuando está activo, la orden pide un adelanto antes de avanzar al estado Confirmada.',
  },
  KDS_SETTINGS: {
    TAB_BOARD: 'Tablero',
    TAB_SETTINGS: 'Configuración',
    SUBNAV_OVERVIEW: 'Resumen',
    SUBNAV_NODES: 'Nodos',
    SUBNAV_AUTOMATIONS: 'Automatizaciones',
    SUBNAV_USERS: 'Usuarios',
    OVERVIEW_TITLE: 'Resumen',
    OVERVIEW_COUNT_NODES: 'Nodos',
    OVERVIEW_COUNT_AUTOMATIONS: 'Automatizaciones',
    OVERVIEW_COUNT_USERS: 'Usuarios asignados',
    OVERVIEW_HEALTH_TITLE: 'Estado',
    OVERVIEW_NODE_HEALTHY: '{{name}} — {{count}} automatización(es) activa(s)',
    OVERVIEW_NODE_NO_AUTOMATIONS: '{{name}} sin automatizaciones',
    OVERVIEW_NODE_ALL_DISABLED: '{{name}} con automatizaciones desactivadas',
    OVERVIEW_DISABLED_COUNT: '{{count}} automatización(es) desactivada(s)',
    OVERVIEW_ALL_GOOD: 'Todo listo. Tus nodos están recibiendo trabajo.',
    OVERVIEW_CTA_NODES: 'Ir a Nodos',
    OVERVIEW_CTA_AUTOMATIONS: 'Ir a Automatizaciones',
    AUTOMATIONS_TITLE: 'Automatizaciones',
    AUTOMATIONS_SUBTITLE:
      'Decide qué eventos del negocio crean, actualizan o cierran tickets en cada nodo del KDS.',
    AUTOMATIONS_EMPTY_TITLE: 'Aún no tienes automatizaciones',
    AUTOMATIONS_EMPTY_BODY:
      'Una automatización conecta un evento de tu negocio (una factura, una venta, una orden) con una acción en un nodo del KDS.',
    AUTOMATIONS_EMPTY_EXAMPLE:
      'Ejemplo: cuando una factura se confirma → crear ticket en Cocina.',
    AUTOMATIONS_ADD: 'Nueva automatización',
    AUTOMATIONS_NO_NODES_TITLE: 'Primero crea un nodo',
    AUTOMATIONS_NO_NODES_BODY:
      'Un nodo es una pantalla o estación (Cocina, Bar, Almacén). Los tickets llegan a los nodos que configures.',
    AUTOMATIONS_NO_NODES_CTA: 'Crear mi primer nodo',
    AUTOMATION_WHEN: 'CUANDO',
    AUTOMATION_BECOMES: 'cambia a',
    AUTOMATION_THEN: 'ENTONCES',
    AUTOMATION_IN: 'EN',
    AUTOMATION_ACTIVE: 'Activa',
    AUTOMATION_INACTIVE: 'Inactiva',
    AUTOMATION_DELETE: 'Eliminar',
    AUTOMATION_SAVE: 'Guardar',
    AUTOMATION_ACTION_CREATE: 'Crear ticket',
    AUTOMATION_ACTION_UPDATE: 'Actualizar ticket',
    AUTOMATION_ACTION_CLOSE: 'Cerrar ticket',
    AUTOMATION_ACTION_CANCEL: 'Cancelar ticket',
    AUTOMATION_ACTION_IGNORE: 'Ignorar',
    AUTOMATION_STATE_HINT:
      'El flujo {{workflow}} tiene {{count}} estado(s) disponible(s) para el KDS. Si necesitas más, agrégalos desde el catálogo del flujo (backend).',
    NODE_FROM_FLOOR: 'Salón',
    NODE_FROM_FLOOR_HINT:
      'Los nodos con el ícono 👨‍🍳 son salones del plano de piso — las comandas creadas en sus mesas llegan aquí automáticamente.',
    TOAST_CREATED: 'Automatización creada.',
    TOAST_UPDATED: 'Automatización guardada.',
    TOAST_DELETED: 'Automatización eliminada.',
    PAYWALL_SUBTITLE: 'Activa el módulo para configurar tu KDS.',
    PAYWALL_PITCH:
      'El módulo KDS no está activo para tu negocio. Actívalo para enrutar tickets a cocina, bar o almacén automáticamente.',
    PAYWALL_FEATURE_TICKETS:
      'Enruta tickets a la estación correcta cuando cierras una venta o confirmas una factura',
    PAYWALL_FEATURE_SCREENS:
      'Pantallas dedicadas para cocina, bar, almacén y producción',
    PAYWALL_FEATURE_PICKING:
      'Flujo de picking para almacén y producción con estado por ítem',
    PAYWALL_FEATURE_HISTORY:
      'Historial + tiempos por estación para medir tu operación',
    WIZARD_TITLE: 'Configura tu KDS en 2 pasos',
    WIZARD_INTRO:
      'Vamos a preparar tu KDS. Escoge una estación y decide cuándo aparecen los tickets.',
    WIZARD_STEP_LABEL: 'Paso {{current}} de {{total}}',
    WIZARD_STEP1_TITLE: 'Elige tu primera estación',
    WIZARD_STEP1_BODY:
      'Una estación es una pantalla o zona (Cocina, Bar, Almacén). Los tickets llegan aquí.',
    WIZARD_STEP1_PRESET_KITCHEN: 'Cocina',
    WIZARD_STEP1_PRESET_BAR: 'Bar',
    WIZARD_STEP1_PRESET_WAREHOUSE: 'Almacén',
    WIZARD_STEP1_PRESET_CUSTOM: 'Otro nombre',
    WIZARD_STEP1_CUSTOM_LABEL: 'Nombre de la estación',
    WIZARD_STEP2_TITLE: '¿Cuándo deben aparecer los tickets?',
    WIZARD_STEP2_BODY:
      'Marca los eventos del negocio que enviarán tickets a esta estación. Puedes cambiar esto cuando quieras.',
    WIZARD_STEP2_PRESET_INVOICE: 'Cuando confirmo una factura, crear ticket',
    WIZARD_STEP2_PRESET_POS: 'Cuando cierro una venta POS, crear ticket',
    WIZARD_STEP2_PRESET_ORDER:
      'Cuando confirmo una orden, crear ticket + seguir el ciclo completo',
    WIZARD_NEXT: 'Continuar',
    WIZARD_BACK: 'Atrás',
    WIZARD_FINISH: 'Listo',
    WIZARD_TOAST_DONE: 'Tu KDS quedó configurado.',
    WIZARD_CREATING: 'Configurando…',
    USERS_EMPTY_TITLE: 'Aún no tienes usuarios asignables',
    USERS_EMPTY_BODY:
      'Los administradores ya tienen acceso completo al KDS. Para asignar cajeros, meseros o supervisores a nodos específicos, primero créalos en el módulo de usuarios.',
    USERS_EMPTY_CTA: 'Crear usuario',
    USERS_NO_NODES_TITLE: 'Primero crea un nodo',
    USERS_NO_NODES_BODY:
      'Los usuarios se asignan a nodos. Crea al menos uno para continuar.',
    USERS_NO_NODES_CTA: 'Crear mi primer nodo',
  },
  ROLES: {
    super_admin: 'Super Administrador',
    admin: 'Administrador',
    manager: 'Gerente',
    cashier: 'Cajero',
    guest: 'Invitado',
    vendor: 'Vendedor',
    administrator: 'Administrador',
    sommelier: 'Mesero',
  },
  USERS: {
    TITLE: 'Usuarios',
    SUBTITLE: 'Administre los usuarios del sistema.',
    ADD_USER: 'Nuevo Usuario',
    SEARCH_PLACEHOLDER: 'Buscar usuarios...',
    NAME: 'Nombre',
    EMAIL: 'Correo electrónico',
    ROLE: 'Rol',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    NO_USERS: 'No hay usuarios disponibles',
    ADD_USER_DESCRIPTION:
      'Complete el formulario para crear un nuevo usuario en el sistema.',
    LEAVE_BLANK_PASSWORD_TO_KEEP:
      'Deje en blanco la contraseña para mantener la actual',
    // Additional fields for comprehensive user management
    STATUS: 'Estado',
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    VERIFIED: 'Verificado',
    RESTORE: 'Restaurar',
    UNVERIFIED: 'No verificado',
    NO_ROLE: 'Sin rol asignado',
    ACTIVATE: 'Activar',
    DEACTIVATE: 'Desactivar',
    DELETE_CONFIRMATION: '¿Está seguro de eliminar al usuario {{name}}?',
    ERROR_LOADING: 'Error al cargar usuarios',
    ALL_STATUSES: 'Todos los estados',
    EXPORT: 'Exportar',
    NO_USERS_FOUND: 'No se encontraron usuarios',
    USERNAME: 'Nombre de usuario',
    FIRST_NAME: 'Nombre',
    LAST_NAME: 'Apellido',
    PASSWORD: 'contraseña',
    CONFIRM_PASSWORD: 'Confirmar contraseña',
    CREATE_USER: 'Crear Usuario',
    UPDATE_USER: 'Actualizar Usuario',
    USER_CREATED: 'Usuario creado exitosamente',
    USER_UPDATED: 'Usuario actualizado exitosamente',
    USER_DELETED: 'Usuario eliminado exitosamente',
    USER_ACTIVATED: 'Usuario activado exitosamente',
    USER_DEACTIVATED: 'Usuario desactivado exitosamente',
    BUSINESS: 'Empresa',
    EMAIL_VERIFIED: 'Email verificado',
    SELECT_ROLE_PLACEHOLDER: 'Seleccione un rol',
    VALIDATION: {
      BUSINESS_REQUIRED: 'La empresa es obligatoria',
      ROLE_REQUIRED: 'El rol es obligatorio',
      FIRST_NAME_REQUIRED: 'El nombre es obligatorio',
      LAST_NAME_REQUIRED: 'El apellido es obligatorio',
      USERNAME_MIN_LENGTH:
        'El nombre de usuario debe tener al menos 3 caracteres',
      EMAIL_INVALID: 'Por favor ingrese un email válido',
      PASSWORD_REQUIRED: 'La contraseña es obligatoria para nuevos usuarios',
      PASSWORD_MIN_LENGTH: 'La contraseña debe tener al menos 6 caracteres',
      PASSWORDS_DONT_MATCH: 'Las contraseñas no coinciden',
      BRANCH_ACCESS_REQUIRED:
        'Asigna al menos una sucursal con un rol, o desmarca el acceso a varias sucursales.',
    },
    UPDATE_USER_DESCRIPTION: 'Actualiza la información del usuario',
    DEACTIVATED_ERROR: 'Error al desactivar usuario',
    DEACTIVATED_SUCCESS: 'Usuario desactivado exitosamente',
    ACTIVATED_SUCCESS: 'Usuario activado exitosamente',
    ACTIVATED_ERROR: 'Error al activar usuario',
    ROLE_ASSIGNMENT_HELPER_TEXT:
      'Selecciona el rol que define los permisos de este usuario dentro del sistema.',
    MANAGE_ROLES_LINK:
      '¿No ves el rol que necesitas? Administra los roles aquí.',
    MULTI_BRANCH_ACCESS: 'Acceso a varias sucursales',
    MULTI_BRANCH_ACCESS_HELP:
      'Permite que este usuario opere en las sucursales que elijas abajo, cada una con su propio rol. Solo podrá ver y cambiar a esas sucursales. Los administradores acceden a todas sin asignarlas.',
    BRANCH_ROLES_TITLE: 'Sucursales y rol en cada una',
    BRANCH_ROLES_HELP:
      'Elige con qué rol opera en cada sucursal. Las que dejes en "Sin acceso" no aparecerán en su selector de sucursal.',
    NO_BRANCH_ACCESS: 'Sin acceso',
    APPLY_ROLE_TO_ALL: 'Aplicar a todas',
    APPLY_ROLE_TO_ALL_HELP:
      'Asigna este rol en todas las sucursales a las que el usuario tiene acceso',
    APPLY_ROLE_TO_ALL_DONE:
      'Rol aplicado a {{count}} sucursal(es). Guarda para confirmar.',
    UNLOCK_ACCOUNT: 'Desbloquear Cuenta',
    UNLOCK_ACCOUNT_CONFIRMATION: 'Desbloquear Cuenta',
    UNLOCK_ACCOUNT_CONFIRMATION_DESCRIPTION:
      '¿Está seguro de que desea desbloquear {{username}}? Esto borrará sus intentos fallidos de inicio de sesión y les permitirá iniciar sesión nuevamente.',
    ACCOUNT_LOCKED: 'Bloqueado',
    PIN_SECTION_TITLE: 'PIN del usuario',
    PIN_SECTION_DESCRIPTION:
      'Configura el PIN de autenticación (inicio de sesión rápido) y el PIN de autorización (aprobaciones).',
    PIN_LOGIN_TITLE: 'PIN de autenticación',
    PIN_LOGIN_DESCRIPTION:
      'Se usa para iniciar sesión sin teclear la contraseña en terminales compartidos.',
    PIN_APPROVAL_TITLE: 'PIN de autorización',
    PIN_APPROVAL_DESCRIPTION:
      'Se usa para aprobar acciones que requieren un manager (descuentos, anulaciones, apertura de cuentas, etc.).',
    PIN_BADGE_CONFIGURED: 'Configurado',
    PIN_BADGE_NOT_CONFIGURED: 'Sin configurar',
    PIN_NEW_LABEL: 'PIN (4 dígitos)',
    PIN_SET: 'Establecer',
    PIN_REPLACE: 'Reemplazar',
    PIN_REMOVE: 'Eliminar',
    PIN_VALUE_MISMATCH: 'Los PINs no coinciden',
    PIN_LOGIN_SAVED: 'PIN de inicio de sesión guardado',
    PIN_LOGIN_REMOVED: 'PIN de inicio de sesión eliminado',
    PIN_APPROVAL_SAVED: 'PIN de autorización guardado',
    PIN_APPROVAL_REMOVED: 'PIN de autorización eliminado',
    PIN_SAVE_ERROR: 'No pudimos guardar el PIN. Intenta de nuevo.',
    CARD_TITLE: 'Tarjeta de acceso',
    CARD_DESCRIPTION_ADMIN:
      'Asigna una tarjeta RFID/NFC a este usuario para iniciar sesión, identificarse en mesas y aprobar acciones sin teclear el PIN.',
    CARD_DESCRIPTION_SELF:
      'Asocia tu tarjeta personal para iniciar sesión y autorizar acciones con un solo escaneo.',
    CARD_DESKTOP_ONLY_HINT:
      'Esta acción requiere la aplicación de escritorio NeoPOS para leer la tarjeta. Descárgala desde el botón "Descargar app" en la sección de POS o Restaurante.',
    CARD_BADGE_CONFIGURED: 'Asignada · ••{{last4}}',
    CARD_BADGE_NOT_CONFIGURED: 'Sin asignar',
    CARD_ASSIGN: 'Asignar tarjeta',
    CARD_REPLACE: 'Reemplazar tarjeta',
    CARD_REMOVE: 'Quitar tarjeta',
    CARD_SCAN_TITLE: 'Escanea la tarjeta',
    CARD_SCAN_DESCRIPTION:
      'Acerca la tarjeta al lector ahora. El UID se completará automáticamente.',
    CARD_SCAN_LABEL: 'UID de la tarjeta',
    CARD_SCAN_PLACEHOLDER: 'Esperando lectura…',
    CARD_SAVED: 'Tarjeta asignada',
    CARD_REMOVED: 'Tarjeta eliminada',
    CARD_SAVE_ERROR: 'No pudimos guardar la tarjeta. Intenta de nuevo.',
  },
  PRINT_PREFERENCE: {
    CHANGE_PRINTER: 'Impresora: {{size}} · Cambiar',
    TOGGLE_HINT: 'Alternar entre Térmica y A4',
  },
  PRICE_TIERS: {
    SELECT_PLACEHOLDER: 'Nivel de precio',
    USE_BASE_PRICE: 'Usar precio base',
    NAMES: {
      WHOLESALE: 'Mayorista',
      RETAIL: 'Minorista',
      VIP: 'VIP',
      LOYAL: 'Cliente leal',
      REGULAR: 'Regular',
      PREFERRED: 'Preferente',
      CORPORATE: 'Corporativo',
      RESELLER: 'Revendedor',
      DISTRIBUTOR: 'Distribuidor',
      MEMBER: 'Miembro',
      BULK: 'Por mayor',
    },
  },
  RESTAURANT: {
    /** Watermark on the open-table pre-bill (Vista previa / print before
     *  Cerrar mesa) so it can't be mistaken for the final paid invoice. */
    PRE_BILL_WATERMARK: 'PRE-CUENTA',
    TAB_ENVIRONMENTS: 'Ambientes',
    TAB_ELEMENTS: 'Agregar Mesas y Decoración',
    TAB_PROPERTIES: 'Propiedades',
    SIDEBAR_EXPAND: 'Expandir panel',
    SIDEBAR_COLLAPSE: 'Contraer panel',
    SAVED_HINT: 'Todos los cambios guardados',
    COLOR: 'Color',
    CANVAS_COLOR: 'Color del lienzo',
    SIZE: 'Tamaño',
    SIZE_INCREASE: 'Ampliar',
    SIZE_DECREASE: 'Reducir',
    RESET_LAYOUT_SHORT: 'Vaciar',
    DISCARD_CHANGES_SHORT: 'Descartar',
    SAVE_LAYOUT_SHORT: 'Guardar',
    TAB_CUSTOMER_OR_NAME_HINT:
      'Selecciona un cliente registrado para habilitar crédito y tarifas por tier, o escribe solo un nombre para una cuenta rápida.',
    TAB_ASSIGN_NAME_CTA: 'Asignar nombre o cliente',
    RESERVE_TABLE_CTA: 'Reservar mesa',
    PAYMENT_ACCOUNT_NOT_READY:
      'La cuenta aún no está lista para facturar. Recarga la mesa e intenta de nuevo.',
    PAYMENT_CUSTOMER_REQUIRED:
      'Asigna un cliente a la cuenta antes de crear la factura.',
    PAYMENT_NO_ITEMS_TO_INVOICE:
      'No hay ítems para facturar. Agrega al menos un ítem a la cuenta.',
    OPEN_TABLE_PIN_PROMPT:
      'Ingresa tu PIN personal para identificarte como el mesero que abre esta mesa.',
    LOCKED_TO_ANOTHER_SOMMELIER:
      'Esta mesa está siendo atendida por {{name}}. Solo el meser@ asignado o un administrador puede modificarla.',
    TABLE_ALREADY_OPEN_TOAST:
      'El mesero {{name}} ya abrió esta mesa, valida con él o ella si puedes utilizar esta. El tablero se acaba de actualizar.',
    TABLE_ALREADY_OPEN_TOAST_ANONYMOUS:
      'Otro mesero ya abrió esta mesa. Valida con él o ella si puedes utilizarla. El tablero se acaba de actualizar.',
    TABLE_ALREADY_OPEN_TOAST_SELF:
      'Tú ya abriste esta mesa antes. Ábrela para continuar donde la dejaste.',
    TABLE_ORPHAN_OPEN_TITLE: 'Mesa sin cuenta activa',
    TABLE_ORPHAN_OPEN_DESC:
      'La mesa estaba marcada como abierta pero no tiene una cuenta asociada. Vuelve a abrirla para continuar.',
    IDENTIFY_OPERATOR: 'Identifica al mesero',
    IDENTIFY_OPERATOR_DESCRIPTION:
      'Selecciona el mesero y digita su PIN personal para continuar.',
    OPERATOR: 'Mesero',
    SELECT_OPERATOR: 'Selecciona un mesero',
    TABLE_MANAGED_BY:
      'Esta mesa está siendo atendida por {{name}}. Solo el mesero asignado o un administrador puede modificarla.',
    OPERATOR_PIN_INVALID: 'PIN inválido para el mesero seleccionado.',
    OPERATOR_NEEDS_PIN_SETUP:
      'Este mesero aún no tiene PIN. Configúralo ahora para continuar.',
    NO_DRAFT_ITEMS:
      'Aún no has agregado items. Selecciona productos del catálogo.',
    NO_SOMMELIERS_HELP:
      'No hay meseros con permisos de servicio en esta sucursal. Contacta al administrador para asignar el rol de mesero a un usuario.',
    NO_FLOOR_PLAN_EDIT_PERMISSION:
      'No tienes permiso para modificar los salones.',
    DRAFT_CONFLICT_COPY_HINT:
      'Copia los ítems al portapapeles para pegarlos en la cuenta correcta o elige otra mesa.',
    DRAFT_ITEMS_COPIED: 'Ítems copiados al portapapeles.',
    DRAFT_LOCK_READONLY_HINT:
      'Otro mesero está armando la cuenta en esta mesa. Puedes revisarla pero no modificarla hasta que la libere.',
    DRAFT_LOCK_BADGE: 'En uso',
    FORCE_RELEASE_TABLE: 'Liberar mesa',
    CONFIRM_OPEN_ACCOUNT_TITLE: '¿Abrir cuenta?',
    CONFIRM_OPEN_ACCOUNT_DESC:
      'Se creará la cuenta de la mesa con los artículos agregados y se enviará a cocina si corresponde. Esta acción no se puede deshacer.',
    DRAFT_CTA: 'Confirmar y abrir cuenta',
    DRAFT_EMPTY:
      'Selecciona artículos del catálogo. La cuenta se crea al confirmar.',
    TRANSFER_ITEM_MENU_MOVE: 'Mover a otra cuenta',
    TRANSFER_ITEM_MENU_DUPLICATE: 'Duplicar en otra cuenta',
    TRANSFER_ITEM_MENU_SPLIT: 'Dividir cantidad',
    TRANSFER_ITEM_MODAL_TITLE: 'Transferir artículo',
    TRANSFER_ITEM_SELECT_DESTINATION: 'Cuenta destino',
    TRANSFER_ITEM_NEW_TAB_NAME: 'Nombre de la nueva cuenta (opcional)',
    TRANSFER_ITEM_SPLIT_QTY_LABEL: 'Cantidad a mover',
    TRANSFER_ITEM_SUCCESS_MOVE: 'Artículo movido.',
    TRANSFER_ITEM_SUCCESS_DUPLICATE: 'Artículo duplicado.',
    TRANSFER_ITEM_SUCCESS_SPLIT: 'Artículo dividido.',
    TITLE: 'Restaurante',
    FLOOR_PLAN: 'Plano del Salón',
    SERVICE: 'Servicio',
    ENVIRONMENTS: 'Administrar Salones',
    ENVIRONMENT: 'Salón',
    CREATE_ENVIRONMENT: 'Crear Salón',
    ENV_KDS_SECTION_TITLE: 'KDS y comanda',
    ENV_KDS_MATERIALIZE_LABEL: 'Crear como flujo de trabajo en KDS',
    ENV_KDS_MATERIALIZE_HELP:
      'Si lo activas, este salón se materializa como un nodo de entrada en el KDS para recibir comandas de productos sin categoría/producto KDS asignado.',
    ENV_KDS_NODE_LABEL: 'Nodo KDS por defecto del salón',
    ENV_KDS_NODE_PLACEHOLDER: 'Sin nodo específico',
    ENV_KDS_NODE_NONE_BADGE: 'Sin flujo KDS',
    ENV_KDS_PRECEDENCE_TITLE: '¿A dónde va la comanda?',
    ENV_KDS_PRECEDENCE_BODY:
      'Orden de prioridad: 1) Nodo KDS de la categoría, 2) Nodo KDS del producto, 3) Nodo KDS del salón. Si ningún nivel define un nodo, se usa el flujo por defecto.',
    ENV_KDS_PRINT_FALLBACK:
      'Si el KDS no está activo, la comanda se imprime usando la impresora configurada para este salón.',
    ENV_KDS_CONFIGURE_CTA: 'Configurar KDS',
    ENV_KDS_NODE_CREATE_INLINE: 'Crear nodo KDS',
    ENV_KDS_NODE_CREATE_TITLE: 'Nuevo nodo KDS',
    ENV_KDS_NODE_CREATE_ENTRY_HINT:
      'Se crea como nodo de entrada activo y queda listo para asignarse al salón. Puedes reorganizar la jerarquía luego desde Administrar nodos KDS.',
    ENV_KDS_NODE_CREATE_PERM_HINT:
      'Necesitas el permiso "Administrar nodos KDS" para crear nodos desde aquí.',
    ENV_KDS_NODE_CREATE_CAPACITY_HINT:
      'Alcanzaste el límite de pantallas KDS de tu plan. Aumenta la capacidad antes de crear un nuevo nodo.',
    ENV_KDS_NODE_CREATE_ADDON_HINT:
      'El módulo KDS no está incluido en tu plan actual. Adquiere el addon KDS para crear nodos desde aquí.',
    ENV_KDS_NODE_INCREASE_CAPACITY_CTA: 'Aumentar capacidad de Pantallas KDS',
    EDIT_ENVIRONMENT: 'Editar Salón',
    DELETE_ENVIRONMENT: 'Eliminar Salón',
    ENVIRONMENT_NAME: 'Nombre del Salón',
    ENVIRONMENT_DESCRIPTION: 'Descripción',
    CANVAS_WIDTH: 'Ancho del Lienzo',
    CANVAS_HEIGHT: 'Alto del Lienzo',
    GRID_SIZE: 'Tamaño de Cuadrícula',
    ADD_TABLE: 'Agregar Mesa',
    ADD_DECORATION: 'Agregar Decoración',
    TABLE: 'Mesa',
    TABLE_LABEL: 'Etiqueta',
    SHAPE: 'Forma',
    SEATS: 'Plazas',
    ROTATION: 'Rotación',
    SHAPE_ROUND: 'Redonda',
    SHAPE_SQUARE_2: 'Cuadrada (2)',
    SHAPE_SQUARE_4: 'Cuadrada (4)',
    SHAPE_SQUARE_6: 'Cuadrada (6)',
    SHAPE_SQUARE_8: 'Cuadrada (8)',
    SHAPE_RECT_LONG: 'Rectangular larga',
    SHAPE_BAR_STOOL: 'Banco de bar',
    DECOR_TREE: 'Árbol',
    DECOR_PLANT: 'Planta',
    DECOR_WALL: 'Pared',
    DECOR_DOOR: 'Puerta',
    DECOR_DOOR_SWING: 'Puerta abrir',
    DECOR_DOOR_SLIDING: 'Puerta corredera',
    DECOR_DOOR_BIFOLD: 'Puerta articulada',
    DECOR_DOOR_ACCORDION: 'Puerta sanfonada',
    DOOR_VARIANT: 'Tipo de puerta',
    DECOR_WINDOW: 'Ventana',
    DECOR_BAR: 'Barra',
    DECOR_STAGE: 'Escenario',
    DECOR_OTHER: 'Otro',
    STATUS_AVAILABLE: 'Disponible',
    STATUS_OPEN: 'Abierta',
    STATUS_RESERVED: 'Reservada',
    STATUS_BLOCKED: 'Bloqueada',
    STATUS_CLEANING: 'Limpieza',
    OPEN_TABLE: 'Abrir Mesa',
    CLOSE_TABLE: 'Cerrar Mesa',
    CANCEL_EMPTY_ACCOUNT: 'Cancelar cuenta',
    CONFIRM_CANCEL_EMPTY_ACCOUNT:
      '¿Está seguro de que desea cancelar esta cuenta vacía? La mesa quedará libre.',
    CLOSE_TABLE_PERMISSION_HINT:
      'Necesitas el permiso "Cerrar mesa" para realizar esta acción.',
    ACCOUNT_CLOSED_TOAST: 'Esta cuenta ya fue cerrada. Se actualizará la mesa.',
    SIBLING_ADDED_TITLE: 'Otro mesero modificó la cuenta',
    SIBLING_ADDED_INTRO:
      'Se agregaron los siguientes ítems a esta mesa mientras cobrabas:',
    SIBLING_ADDED_NEW_TOTAL: '¿Deseas continuar? El total ahora es {{total}}.',
    PAYMENT_IN_PROGRESS_TOAST:
      'Se está procesando el pago y cerrando la mesa {{mesa}}. Si necesitas modificar algo, comunícate con el cajero.',
    PAYMENT_IN_PROGRESS_GENERIC:
      'Se está procesando el pago y cerrando esta mesa. Si necesitas modificar algo, comunícate con el cajero.',
    PAYMENT_LOCK_IDLE_WARNING:
      'La mesa se liberó por inactividad; otro terminal pudo haberla modificado. Revisa el total antes de cobrar.',
    PAYMENT_LOCK_BLOCKED_TITLE: 'Mesa en cobro',
    PAYMENT_LOCK_BLOCKED_COUNTDOWN:
      'Otro cajero está cobrando esta mesa. Se desbloqueará en {{seconds}}s salvo que cierre la cuenta.',
    PAYMENT_REVIEW_TOTAL_BEFORE_CONTINUE:
      'Revise el total antes de continuar, puede que otra persona haya modificado esta cuenta.',
    FISCAL_RECEIPT_HELP_TITLE: '¿Cómo habilitar el comprobante fiscal?',
    FISCAL_RECEIPT_HELP_BODY:
      'El comprobante fiscal (B01/E31) permite al cliente reclamar crédito fiscal del ITBIS. Requiere RNC del negocio, un ITBIS aplicable y secuencias NCF configuradas.',
    FISCAL_RECEIPT_HELP_STEPS_TITLE: 'Pasos para habilitarlo:',
    FISCAL_RECEIPT_HELP_STEP_1:
      'Registra el RNC del negocio en Configuración → Negocio.',
    FISCAL_RECEIPT_HELP_STEP_2:
      'Agrega al menos un impuesto de 16% o 18% (ITBIS) a esta cuenta.',
    FISCAL_RECEIPT_HELP_STEP_3:
      'Configura una secuencia NCF tipo B o E en Configuración → NCF.',
    HEAD_COUNT: 'Comensales',
    SOMMELIER: 'Mesero',
    NO_SOMMELIER: 'Sin mesero',
    TABS: 'Cuentas',
    TAB_NAME: 'Nombre de cuenta',
    TAB_NAME_REQUIRED: 'El nombre no puede estar vacío',
    ADD_TAB: 'Agregar cuenta',
    REMOVE_TAB: 'Quitar cuenta',
    SEAT_NAME: 'Puesto {{seat}}',
    ADD_SEAT: 'Agregar puesto',
    EXTRA_SEAT_HINT: 'Puesto extra (excede la capacidad de la mesa)',
    GUESTS_AT_TABLE: 'Comensales: {{count}} de {{capacity}}',
    FISCAL_RECEIPT_QUESTION: '¿Comprobante fiscal?',
    FISCAL_RECEIPT_HINT: 'Solicita RNC para emitir crédito fiscal',
    COMMANDA_PRINT: 'Imprimir comanda',
    COMMANDA_PRINT_ALL: 'Imprimir todas',
    COMMANDA_PRINTED: 'Comanda ya impresa',
    COMMANDA_REPRINT_BLOCKED:
      'Comanda ya impresa. Solo un administrador puede reimprimir.',
    COMMANDA_REPRINT_APPROVAL_TITLE: 'Aprobación para reimprimir comanda',
    COMMANDA_TITLE: 'COMANDA',
    COMMANDA_CLIENT: 'Cliente',
    COMMANDA_SOMMELIER: 'Mesero',
    COMMANDA_TABLE: 'Mesa',
    COMMANDA_PRODUCT: 'Producto',
    COMMANDA_QUANTITY: 'Cantidad',
    COMMANDA_NOTE: 'Nota',
    COMMANDA_TIME: 'Hora',
    COMMANDA_DATE: 'Fecha',
    PRINTER_SETTINGS: 'Impresoras del ambiente',
    PRINTER_INLINE_LABEL: 'Impresoras',
    PRINTER_INLINE_COMMANDA: 'Comanda',
    PRINTER_INLINE_INVOICE: 'Factura',
    PRINTER_INLINE_OS_DEFAULT: 'Predeterminada del sistema',
    PRINTER_INLINE_OVERRIDE_HINT:
      'Solo aplica a esta sesión; no cambia la configuración guardada del salón.',
    PRINTER_INLINE_RESET: 'Restablecer',
    PRINTER_OS_DEFAULT: 'Impresora predeterminada del sistema',
    PRINTER_DESKTOP_ONLY_HINT:
      'La lista de impresoras solo se puede leer desde la app de escritorio NeoGeniux POS.',
    PRINTER_SAVED: 'Configuración de impresoras guardada',
    PRINTER_TAURI_DEVICE: 'Impresora neoprint',
    PRINTER_SILENT_FALLBACK:
      'No se pudo imprimir en la impresora configurada; usando impresora del sistema como respaldo.',
    RENAME_TAB: 'Renombrar cuenta',
    CONFIRM_DELETE_TAB:
      '¿Eliminar esta cuenta? Esta acción no se puede deshacer.',
    TAB_DELETE_BLOCKED_NON_ADMIN:
      'Solo un administrador puede eliminar una cuenta con ítems.',
    LINK_CUSTOMER: 'Vincular cliente',
    ADD_ITEM: 'Agregar ítem',
    ITEM_NOTE: 'Nota',
    VOID_ITEM: 'Anular ítem',
    EDIT_ITEM_TOOLTIP: 'Editar nota o modificadores',
    EDIT_ITEM_LOCKED_PREPARING:
      'La orden ya está en preparación y no puede modificarse.',
    ITEM_STATUS_PENDING: 'Pendiente',
    ITEM_STATUS_SENT: 'Enviado a cocina',
    ITEM_STATUS_PREPARING: 'Preparando',
    ITEM_STATUS_READY: 'Listo para servir',
    ITEM_STATUS_DELIVERED: 'Servida',
    ITEM_STATUS_VOID: 'Anulado',
    CONFIRM_DELETE_ENVIRONMENT:
      '¿Está seguro de que desea eliminar este ambiente? Las mesas y decoraciones también serán eliminadas. ⚠️ No elimine este salón si aún tiene cuentas abiertas — ciérrelas primero para evitar pérdida de información.',
    CANNOT_DELETE_ENVIRONMENT_OPEN_ACCOUNTS:
      'No se puede eliminar este salón porque tiene cuentas abiertas. Cierre todas las cuentas antes de continuar.',
    CANNOT_VOID_SERVED_ITEM:
      'No se puede anular un producto ya servido. Pídale al cliente y registre una devolución si es necesario.',
    CONFIRM_DELETE_TABLE: '¿Está seguro de que desea eliminar esta mesa?',
    CONFIRM_CLOSE_TABLE: '¿Está seguro de que desea cerrar esta mesa?',
    SAVE_LAYOUT: 'Guardar Plano',
    LAYOUT_SAVED: 'Disposición guardada',
    NO_ENVIRONMENTS_YET: 'Aún no hay ambientes. Cree uno para comenzar.',
    NO_ENVIRONMENTS_EMPTY_TITLE: 'Aún no hay salones configurados',
    NO_ENVIRONMENTS_EMPTY_DESCRIPTION:
      'Los salones representan las áreas físicas de tu restaurante (Salón Principal, Terraza, Barra). Antes de poder tomar pedidos por mesa, debes crear al menos un salón con su distribución de mesas.',
    NO_ENVIRONMENTS_EMPTY_STEP1:
      '1. Ve a Restaurante → Diseño de salón y haz clic en "Crear Salón".',
    NO_ENVIRONMENTS_EMPTY_STEP2:
      '2. Asigna un nombre, define la impresora para comandas/facturas y, si usas KDS, elige el nodo de cocina que recibirá las comandas.',
    NO_ENVIRONMENTS_EMPTY_STEP3:
      '3. Dibuja las mesas en el lienzo y guarda. Luego vuelve a esta pantalla para empezar a abrir cuentas.',
    NO_ENVIRONMENTS_EMPTY_CTA: 'Ir a Diseño de salón',
    NO_ENVIRONMENT_SELECTED:
      'Seleccione un ambiente para comenzar a configurar.',
    NO_ENVIRONMENT_SELECTED_TITLE: 'Configura la distribución de tus mesas',
    NO_ENVIRONMENT_SELECTED_DESCRIPTION:
      'Selecciona un salón del panel izquierdo para empezar a colocar mesas, asignar impresoras y configurar el flujo KDS. Si todavía no tienes salones, crea uno con el botón "+ Crear Salón".',
    NO_ENVIRONMENT_SELECTED_STEP1:
      '1. Crea o selecciona un salón en el panel izquierdo (Salón Principal, Terraza, Barra…).',
    NO_ENVIRONMENT_SELECTED_STEP2:
      '2. Configura la impresora de comandas y facturas con el ícono 🖨️ junto al nombre del salón.',
    NO_ENVIRONMENT_SELECTED_STEP3:
      '3. Si usas KDS, edita el salón (✏️) y elige el nodo de cocina por defecto y si debe materializarse como flujo de trabajo.',
    NO_ENVIRONMENT_SELECTED_STEP4:
      '4. Arrastra mesas y decoraciones en el lienzo, ajusta la cantidad de comensales y guarda. Tus cajeros podrán abrir cuentas inmediatamente.',
    SELECT_ENVIRONMENT: 'Seleccionar ambiente',
    TABLE_OCCUPIED: 'Ocupada',
    TABLE_AVAILABLE: 'Disponible',
    OPENED_BY: 'Abierta por',
    OPENED_AT: 'Abierta el',
    TIME_OPEN: 'Tiempo abierta',
    TIME_OPEN_HM: '{{h}}h {{m}}m',
    TIME_OPEN_M: '{{m}}m',
    TILE_LONGEST_OPEN: 'Mayor tiempo',
    TILE_OCCUPANCY: 'Ocupación',
    TILE_LIVE_SALES: 'Venta abierta',
    TILE_BUSIEST_SERVER: 'Mesero con más mesas',
    TILE_NO_SERVICE: 'Sin servicio activo',
    PALETTE: 'Paleta',
    INSPECTOR: 'Propiedades',
    NO_SELECTION: 'Seleccione un elemento para editar sus propiedades.',
    DELETE: 'Eliminar',
    DUPLICATE: 'Duplicar',
    PREP_TIME_MINUTES: 'Tiempo de preparación (min)',
    WIDTH: 'Ancho',
    HEIGHT: 'Alto',
    ROTATE_90: 'Rotar 90°',
    SWAP_ORIENTATION: 'Cambiar orientación',
    UNSAVED_CHANGES: 'Cambios sin guardar',
    UNSAVED_CHANGES_LEAVE_CONFIRM:
      'Hay cambios sin guardar. ¿Está seguro de que desea salir? Se perderán los cambios.',
    UNSAVED_CHANGES_PREVIEW:
      'Vista previa con cambios sin guardar del Plano del Salón. Guarde los cambios para aplicarlos.',
    CONFIRM_DELETE_ELEMENT: '¿Está seguro de que desea eliminar este elemento?',
    SELECTED: 'seleccionados',
    RESET_LAYOUT: 'Vaciar lienzo',
    CONFIRM_RESET_LAYOUT:
      '¿Está seguro de que desea eliminar TODOS los elementos del lienzo? Esta acción se aplicará al guardar. ⚠️ No vacíe este salón si aún tiene cuentas abiertas — ciérrelas primero para evitar pérdida de información.',
    DISCARD_CHANGES: 'Descartar cambios',
    PRINT_PREVIEW: 'Imprimir borrador',
  },
  PROFILE: {
    TITLE: 'Mi Perfil',
    SUBTITLE: 'Administra tu información personal y preferencias',
    PERSONAL_INFO: 'Información Personal',
    WORK_INFO: 'Información Laboral',
    EDIT_PROFILE: 'Editar Perfil',
    SAVE_CHANGES: 'Guardar Cambios',
    CANCEL: 'Cancelar',
    FIRST_NAME: 'Nombre',
    LAST_NAME: 'Apellido',
    EMAIL: 'Correo electrónico',
    PHONE: 'Teléfono',
    ROLE: 'Rol',
    DEPARTMENT: 'Departamento',
    LOCATION: 'Ubicación',
    BIO: 'Biografía',
    JOINED: 'Miembro desde',
    CHANGE_PHOTO: 'Cambiar Foto',
    UPLOAD_PHOTO: 'Subir Foto',
    REMOVE_PHOTO: 'Eliminar Foto',
    PROFILE_UPDATED: 'Perfil actualizado exitosamente',
    PROFILE_UPDATE_ERROR: 'Error al actualizar el perfil',
    PHOTO_UPDATED: 'Foto actualizada exitosamente',
    PHOTO_REMOVED: 'Foto eliminada exitosamente',
    PHOTO_UPDATE_ERROR: 'Error al actualizar la foto',
    VALIDATION: {
      FIRST_NAME_REQUIRED: 'El nombre es requerido',
      LAST_NAME_REQUIRED: 'El apellido es requerido',
      PHONE_INVALID: 'El teléfono debe tener un formato válido',
      BIO_MAX_LENGTH: 'La biografía no puede exceder 500 caracteres',
      CURRENT_PASSWORD_REQUIRED:
        'La contraseña actual es requerida para cambiar la contraseña',
    },
  },
  SECURITY: {
    TITLE: 'Seguridad',
    SUBTITLE: 'Configura tu PIN de acceso rápido',
    PIN_SECTION_TITLE: 'PIN de Acceso Rápido',
    PIN_SECTION_DESCRIPTION:
      'Usa un PIN de 4 dígitos para iniciar sesión rápidamente sin escribir tu contraseña completa.',
    PIN_CONFIGURED: 'PIN configurado',
    PIN_NOT_CONFIGURED: 'PIN no configurado',
    SETUP_PIN: 'Configurar PIN',
    CHANGE_PIN: 'Cambiar PIN',
    REMOVE_PIN: 'Eliminar PIN',
    PIN_INPUT_LABEL: 'PIN',
    PIN_CONFIRM_LABEL: 'Confirmar PIN',
    PIN_PLACEHOLDER: 'Ingresa tu PIN',
    PIN_SETUP_SUCCESS: 'PIN configurado exitosamente',
    PIN_REMOVE_SUCCESS: 'PIN eliminado exitosamente',
    PIN_MISMATCH: 'Los PINs no coinciden',
    PIN_INVALID_FORMAT: 'El PIN debe tener exactamente 4 dígitos',
    CONFIRM_REMOVE_PIN: 'Eliminar PIN',
    CONFIRM_REMOVE_PIN_DESCRIPTION:
      'Ingresa tu contraseña actual para confirmar la eliminación del PIN.',
    CURRENT_PASSWORD_LABEL: 'contraseña actual',
    USE_PASSWORD: 'Usar contraseña',
    USE_PIN: 'Usar PIN',
    FORGOT_PIN: '¿Olvidaste tu PIN?',
    PIN_LOGIN_TITLE: 'Acceso Rápido',
    PIN_LOGIN_DESCRIPTION: 'Ingresa tu PIN para continuar',
    INVALID_PIN: 'PIN incorrecto',
    LOCK_SCREEN_TITLE: 'Sesión Bloqueada',
    LOCK_SCREEN_DESCRIPTION: 'Ingresa tu PIN para desbloquear',
    LOCK_SCREEN_PASSWORD_DESCRIPTION: 'Ingresa tu contraseña para desbloquear',
    LOCK_SCREEN_CARD_DESCRIPTION:
      'Acerca tu tarjeta al lector para desbloquear',
    INVALID_PASSWORD: 'contraseña incorrecta',
    INVALID_CARD: 'Tarjeta no reconocida',
    USE_CARD: 'Usar tarjeta',
    CARD_PLACEHOLDER: 'Esperando lectura de tarjeta…',
    LOCK_SESSION: 'Bloquear sesión',
    UNLOCK: 'Desbloquear',
    LOGOUT: 'Cerrar sesión',
  },
  BULK_ENTITY_IMPORT: {
    TITLE_CUSTOMERS: 'Importación Masiva de Clientes',
    TITLE_PROVIDERS: 'Importación Masiva de Proveedores',
    BUTTON_LABEL: 'Importación Masiva',
    DESCRIPTION:
      'Sube un archivo CSV o Excel para crear registros en lote. Puedes incluir un saldo de apertura por registro.',
    DOWNLOAD_TEMPLATE: 'Descargar Plantilla',
    DRAG_DROP_FILE: 'Arrastra y suelta tu archivo aquí',
    OR: 'o',
    BROWSE_FILES: 'Buscar archivos',
    SUPPORTED_FORMAT: 'Formato soportado: CSV, Excel',
    FILE_REQUIREMENTS: 'Requisitos del archivo CSV o Excel',
    FILE_REQUIREMENT_1: 'El archivo debe tener encabezados en la primera fila',
    FILE_REQUIREMENT_2: 'Usa una columna por campo y una fila por registro',
    FILE_REQUIREMENT_3:
      'La columna Nombre es obligatoria; la Referencia es obligatoria cuando hay saldo de apertura',
    INVALID_FILE_TYPE:
      'Tipo de archivo inválido. Por favor, sube un archivo CSV o Excel.',
    EMPTY_FILE: 'El archivo está vacío. Por favor, sube un archivo con datos.',
    PARSE_ERROR: 'Error al leer el archivo. Verifica el formato.',
    FIELD_MAPPING_DESCRIPTION:
      'Asocia cada columna de tu archivo con el campo correspondiente.',
    SELECT_SOURCE_FIELD: 'Selecciona la columna de origen',
    SKIP_FIELD: 'Omitir este campo',
    MAP_REQUIRED_FIELDS: 'Asocia todos los campos obligatorios para continuar.',
    VALIDATING: 'Validando…',
    VALIDATION_FAILED:
      'La validación falló. Revisa el archivo e inténtalo de nuevo.',
    ROWS_TO_CREATE: 'Registros a crear',
    ROWS_SKIPPED: '{{count}} fila(s) omitida(s) (ya existen o con errores)',
    VALIDATE_AND_IMPORT: 'Validar e Importar',
    PROCESSING: 'Procesando…',
    IMPORT_SUCCESS: 'Se importaron {{count}} registro(s) correctamente.',
    FIELD_NAME: 'Nombre',
    FIELD_TAX_ID: 'RNC / Cédula',
    FIELD_TAX_ID_TYPE: 'Tipo de Identificación',
    FIELD_EMAIL: 'Correo Electrónico',
    FIELD_PHONE: 'Teléfono',
    FIELD_ADDRESS: 'Dirección',
    FIELD_CREDIT_LIMIT: 'Límite de Crédito',
    FIELD_PAYMENT_TERMS_DAYS: 'Términos de Pago (días)',
    FIELD_TAX_EXEMPT: 'Exento de Impuesto',
    FIELD_OPENING_BALANCE: 'Saldo Pendiente de Apertura',
    FIELD_OPENING_BALANCE_PAYABLE: 'Saldo por Pagar de Apertura',
    FIELD_OPENING_BALANCE_REFERENCE: 'Referencia (Saldo Apertura)',
    FIELD_OPENING_BALANCE_DATE: 'Fecha del Saldo (Apertura)',
    ERR_ROW_DUPLICATE: 'El registro ya existe y fue omitido.',
    ERR_OPENING_BALANCE_REFERENCE_REQUIRED:
      'La referencia es obligatoria cuando se indica un saldo de apertura.',
    ERR_NAME_REQUIRED: 'El nombre es obligatorio.',
    ERR_OPENING_BALANCE_NEGATIVE: 'El saldo de apertura no puede ser negativo.',
    ERR_OPENING_BALANCE_DATE_REQUIRED: 'Indica la fecha del saldo de apertura.',
    ERR_OPENING_BALANCE_DATE_INVALID: 'La fecha no es válida (AAAA-MM-DD).',
    ERR_OPENING_BALANCE_DATE_FUTURE: 'La fecha del saldo no puede ser futura.',
    VALIDATION_TITLE: 'Revisa y corrige los registros',
    VALIDATION_SUBTITLE:
      'Edita cualquier celda para corregir errores antes de importar. Las filas con errores o duplicadas no se importarán.',
    COL_STATUS: 'Estado',
    STATUS_OK: 'Listo',
    STATUS_DUPLICATE: 'Ya existe',
    STATUS_ERROR: 'Revisar',
    ROWS_READY: '{{count}} registro(s) listo(s) para importar',
    ADD_ROW: 'Agregar registro',
    LOAD_MORE: 'Cargar más ({{count}} restantes)',
    IMPORT_STARTED_TITLE: '¡Importación Iniciada!',
    IMPORT_STARTED_MESSAGE:
      'Se están importando {{count}} registro(s) en segundo plano.',
    IMPORT_BACKGROUND_INFO:
      'Un indicador del progreso se mostrará en la interfaz hasta que se complete el proceso.',
    WILL_NOTIFY_WHEN_COMPLETE:
      'Te notificaremos cuando la importación esté completa.',
  },
  CUSTOMERS: {
    TITLE: 'Cliente',
    SUBTITLE: 'Administre la información de sus clientes.',
    ADD_CUSTOMER: 'Nuevo Cliente',
    ADD_CUSTOMER_DESCRIPTION:
      'Crea un nuevo cliente completando el formulario a continuación',
    EDIT_CUSTOMER: 'Editar Cliente',
    EDIT_CUSTOMER_DESCRIPTION: 'Edita la información y detalles del cliente',
    BULK_IMPORT: 'Importación Masiva de Clientes',
    SEARCH_PLACEHOLDER: 'Buscar clientes por nombre, email, teléfono...',
    NAME: 'Nombre',
    EMAIL: 'Correo electrónico',
    PHONE: 'Teléfono',
    COUNTRY: 'País',
    TAX_ID: 'ID Fiscal',
    USE_DGII_NAME: 'Usar nombre de DGII',
    TYPE: 'Tipo',
    STATUS: 'Estado',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    ACTIVATE: 'Activar',
    DEACTIVATE: 'Desactivar',
    RESTORE: 'Restaurar',
    NO_CUSTOMERS: 'No hay clientes disponibles',
    NO_NAME: 'Sin nombre',
    WALK_IN_CUSTOMER: 'Cliente Mostrador',
    UNREGISTERED_CUSTOMER: 'Cliente sin registrar',
    UNREGISTERED_BADGE: 'Sin registrar',
    USE_WITHOUT_CREATING: 'Sin registrar',
    USE_AND_SKIP_REGISTER: 'Usar y no registrar',
    REGISTER_UNREGISTERED_BUTTON: 'Sin registrar · registrar',
    REGISTER_UNREGISTERED_TOOLTIP:
      'Convertir este cliente sin registrar en una ficha de cliente.',
    REGISTER_UNREGISTERED_TITLE: 'Registrar cliente',
    REGISTER_UNREGISTERED_DESCRIPTION:
      '¿Deseas registrar a "{{name}}" como cliente? Se creará una ficha nueva y se usará para esta y futuras transacciones.',
    UNREGISTERED_DIALOG_DESCRIPTION:
      'Captura el nombre del cliente sin crear un registro. Se usará en la factura, cotización, orden de venta o venta POS y no se creará ficha de cliente.',
    UNREGISTERED_NAME_PLACEHOLDER: 'Ej. Juan Pérez',
    TYPE_INDIVIDUAL: 'Persona',
    TYPE_COMPANY: 'Empresa',
    TYPE_ANONYMOUS: 'Anónimo',
    ALL_TYPES: 'Todos los tipos',
    DELETE_TITLE: 'Eliminar Cliente',
    DELETE_DESCRIPTION:
      '¿Está seguro de eliminar este cliente? Esta acción no se puede deshacer.',
    CREATE_CONFIRM: '¿Desea crear el cliente "{{name}}"?',
    DELETE_CONFIRM: 'Eliminar',
    DELETE_CANCEL: 'Cancelar',
    CREATE_CUSTOMER: 'Crear Cliente',
    UPDATE_CUSTOMER: 'Actualizar Cliente',
    CUSTOMER_CREATED: 'Cliente creado exitosamente',
    CUSTOMER_UPDATED: 'Cliente actualizado exitosamente',
    CUSTOMER_DELETED: 'Cliente eliminado exitosamente',
    BUSINESS_NAME: 'Nombre de la Empresa',
    FIRST_NAME: 'Nombre',
    LAST_NAME: 'Apellido',
    TAX_ID_TYPE: 'Tipo de ID Fiscal',
    SELECT_TAX_ID_TYPE: 'Seleccionar tipo de ID fiscal',
    NO_TAX_ID_TYPE: 'Sin tipo de ID fiscal',
    STATE_REGION: 'Estado/Región',
    CITY: 'Ciudad',
    POSTAL_CODE: 'Código Postal',
    ADDRESS: 'Dirección',
    TAX_REGIME: 'Régimen Fiscal',
    TAX_EXEMPT: 'Exento de Impuestos',
    FIXED_TAX_RATE: 'Tasa Fija de Impuesto',
    LOCAL_TAX_CODE: 'Código Fiscal Local',
    FISCAL_RESIDENCY: 'Residencia Fiscal',
    PREFERRED_PAYMENT_METHOD: 'Método de Pago Preferido',
    PREFERRED_CURRENCY: 'Moneda Preferida',
    CREDIT_LIMIT: 'Límite de Crédito',
    CREDIT_BALANCE: 'Saldo de Crédito',
    CREDIT_DETAILS: 'Detalles de Crédito',
    OUTSTANDING_BALANCE: 'Saldo Pendiente',
    VIEW_PENDING_INVOICES: 'Ver facturas pendientes',
    WITH_OUTSTANDING_ONLY: 'Solo con saldo pendiente',
    PENDING_INVOICES: 'Facturas Pendientes',
    PAYMENTS_MADE: 'Pagos Realizados',
    AVAILABLE_CREDIT: 'Crédito Disponible',
    PRINT_LIST: 'Imprimir lista',
    PRINTED_AT: 'Fecha de impresión',
    PRINT_NOTHING_TO_EXPORT: 'No hay información para exportar en esta vista.',
    DEFAULT_PRICE_TIER: 'Nivel de Precio Predeterminado',
    DEFAULT_PRICE_TIER_DESCRIPTION:
      'Nivel de precio que se aplicará automáticamente para este cliente',
    PRICE_TIERS: 'Niveles de Precio',
    PRICE_TIERS_DESCRIPTION:
      'Selecciona los niveles de precio que aplican a este cliente',
    NO_PRICE_TIERS_AVAILABLE:
      'No hay niveles de precio configurados. Ve a Configuración > Niveles de Precio para crear.',
    SELECT_PRICE_TIER: 'Seleccionar nivel de precio',
    NO_TIER_DEFAULT_PRICE:
      'Si no se selecciona ningún nivel, se usará el precio de venta estándar',
    NOTES: 'Notas',
    BASIC_INFO: 'Información Básica',
    ADDRESS_INFO: 'Información de Dirección',
    TAX_INFO: 'Información Fiscal',
    PAYMENT_INFO: 'Información de Pago y Crédito',
    SELECTED_CLIENT: 'Cliente Seleccionado',
    CONFIRM_CREATE_TITLE: '¿Crear nuevo cliente?',
    CONFIRM_CREATE_DESCRIPTION:
      'Se creará un nuevo cliente con el nombre "{{name}}". ¿Desea continuar?',
    VALIDATION: {
      TYPE_REQUIRED: 'El tipo de cliente es obligatorio',
      INDIVIDUAL_NAME_REQUIRED:
        'El nombre y apellido son obligatorios para personas',
      COMPANY_NAME_REQUIRED: 'El nombre de la empresa es obligatorio',
      EMAIL_INVALID: 'Por favor ingrese un email válido',
    },
  },
  SHIFTS: {
    TITLE: 'Turnos y Cuadre de Caja',
    SUBTITLE: 'Administre los turnos y cuadre de caja.',
    ADD_SHIFT: 'Nuevo Turno',
    SEARCH_PLACEHOLDER: 'Buscar turnos...',
    NAME: 'Nombre',
    CASHIER: 'Cajero',
    START: 'Inicio',
    END: 'Fin',
    STATUS: 'Estado',
    INITIAL_AMOUNT: 'Monto Inicial',
    FINAL_AMOUNT: 'Monto Final',
    VOUCHER_AMOUNT: 'Total Vouchers / Tarjetas',
    VOUCHER_AMOUNT_DESC:
      'Suma de los vouchers de tarjeta o transferencia recibidos durante el turno',
    USD_RECEIVED: 'USD Recibidos',
    EUR_RECEIVED: 'EUR Recibidos',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    NO_SHIFTS: 'No hay turnos disponibles',
    NO_SHIFTS_FOUND: 'No se encontraron turnos',
    OPEN: 'Abierto',
    CLOSED: 'Cerrado',
    CLOSE: 'Cerrar Turno de Caja',
    DURATION: 'Duración',
    OPEN_SHIFT: 'Abrir Turno de Caja',
    OPEN_NEW_SHIFT: 'Complete el formulario para abrir un nuevo turno de caja.',
    CLOSE_SHIFT: 'Cerrar Turno de Caja',
    CLOSE_CURRENT_SHIFT: 'Cierre el turno de {{name}} en {{location}}',
    CONFIRM_CLOSE_TITLE: 'Confirmar cierre del turno',
    CONFIRM_CLOSE_DESCRIPTION:
      'Revisa el resumen antes de cerrar. Esta acción no se puede deshacer sin intervención de administrador.',
    NOTES: 'Notas',
    NOTES_PLACEHOLDER: 'Ingrese notas para este turno...',
    CLOSE_NOTES_PLACEHOLDER: 'Ingrese notas de cierre del turno...',
    START_TIME: 'Hora de Inicio',
    SELECT_USER: 'Seleccione un usuario',
    INITIAL_AMOUNT_DESC:
      'Ingrese el monto inicial de dinero en efectivo en la caja',
    FINAL_AMOUNT_DESC: 'Ingrese el monto final contado en la caja al cerrar',
    OPENING: 'Abriendo...',
    CLOSING: 'Cerrando...',
    OPENING_BREAKDOWN_TITLE: 'Desglose de apertura',
    OPENING_BREAKDOWN_DESCRIPTION:
      'Cuenta el efectivo con el que abres la caja. El total se aplica al Monto Inicial.',
    OPENING_BREAKDOWN_APPLY: 'Aplicar',
    CUADRE_LAYOUT_LABEL: 'Formato del cierre impreso',
    CUADRE_LAYOUT_DESCRIPTION:
      'Cambia cómo se muestran las ventas en el Cuadre de Caja impreso. "Lista de facturas" imprime una fila por factura (por defecto). "Productos agrupados por categoría" agrupa los ítems por categoría con un total de Cantidad y Total al pie.',
    CUADRE_LAYOUT_INVOICES: 'Lista de facturas (por defecto)',
    CUADRE_LAYOUT_PRODUCTS: 'Productos agrupados por categoría',
    CUADRE_PRODUCTS_QUANTITY: 'Cantidad',
    CUADRE_PRODUCTS_TOTAL: 'Total',
    CUADRE_PRODUCTS_UNCATEGORIZED: 'Sin categoría',
    CUADRE_PRODUCTS_TITLE: 'Productos Vendidos',
    VIEW_TRANSACTIONS: 'Ver Transacciones',
    SHIFT_TRANSACTIONS: 'Transacciones del Turno',
    SHIFT_PERIOD: 'Período del Turno',
    TOTAL_TRANSACTIONS: 'Total de Transacciones',
    EXPECTED_END_AMOUNT: 'Monto Final Esperado',
    TRANSACTIONS_LIST: 'Lista de Transacciones',
    NO_TRANSACTIONS: 'No hay transacciones para este turno',
    MESSAGES: {
      SHIFT_CREATED: 'Turno creado exitosamente',
      SHIFT_CLOSED: 'Turno cerrado exitosamente',
    },
    VALIDATION: {
      INITIAL_AMOUNT_POSITIVE: 'El monto inicial debe ser positivo',
      END_AMOUNT_POSITIVE: 'El monto final debe ser positivo',
      CLOSING_GREATER:
        'El monto final es mayor que las ventas totales por {{diff}}. Verifique el depósito o fondos adicionales.',
      CLOSING_LESS:
        'El monto final es menor que las ventas totales por {{diff}}. Por favor verifique y corrija antes de cerrar.',
    },
    RECONCILIATION: {
      TITLE: 'Cuadre de Caja',
      CASH_MOVEMENT_TITLE: 'Movimiento de Efectivo',
      GENERAL_TITLE: 'Cuadre general',
      GENERAL_HELP:
        'Cuadre general agrupa el cierre de todos los cajeros de la sucursal en la fecha seleccionada. El Cuadre por turno se hace al cerrar cada turno de forma individual.',
      DENOMINATION_TABLE: 'Desglose de Efectivo',
      DENOMINATION: 'Denominación',
      QUANTITY: 'Cantidad',
      BILLS: 'Billetes',
      COINS: 'Monedas',
      TOTAL_CASH: 'Efectivo contado (cierre)',
      PAYMENT_INSTRUMENTS: 'Instrum. Pago',
      TOTAL_DRAWER: 'Total de Gaveta',
      SALES_CASH: 'Efectivo cobrado',
      SALES_CREDIT: 'Ventas Crédito',
      TOTAL_SALES: 'Ventas brutas',
      GROSS_SALES: 'Ventas brutas',
      NET_SALES: 'Ventas netas',
      SERVICE_CHARGE: 'Propina Legal (personal)',
      TAXES: 'Impuestos (ITBIS)',
      SALES_NET: 'Ventas netas',
      OUTSTANDING: 'Pendiente de cobro',
      SHORTAGE: 'FALTANTE',
      SURPLUS: 'SOBRANTE',
      RECONCILED_OK: 'CUADRE OK',
      CASH_COLLECTED: 'Efectivo cobrado',
      TOTAL_COLLECTED: 'Total ventas',
      EXPECTED_CASH: 'Efectivo',
      PHYSICAL_CASH: 'Efectivo contado (cierre)',
      INVOICED_TOTAL: 'Facturado',
      COLLECTED_TOTAL: 'Cobrado',
      OUTSTANDING_TOTAL: 'Pendiente',
      PAYMENT_TERM_CONTADO: 'Contado',
      PAYMENT_TERM_CREDIT: 'Crédito',
      INVOICES_PROCESSED_TITLE: 'Facturas Procesadas',
      INVOICE_COL_ID: 'Factura',
      INVOICE_COL_TERM: 'Término',
      INVOICE_COL_CREDIT_NOTE: 'N/C',
      TIPS: 'Propinas (personal)',
      TIPS_INPUT_LABEL: 'Propinas incluidas en la gaveta',
      TIPS_INPUT_HINT:
        'Cuenta las propinas dentro del efectivo. Se restan del conteo porque son del personal, no del negocio.',
      BUSINESS_CASH: 'Efectivo del Negocio',
      CREDIT_NOTES_APPLIED: 'N/C Aplicadas',
      PAYMENTS_RECEIVED: 'Pagos Recibidos Ventas',
      VOUCHERS_IN: 'Vales Ingreso',
      VOUCHERS_OUT: 'Vales Egreso',
      SYSTEM_CLOSE: 'Efectivo',
      DIFFERENCE: 'Diferencia',
      INITIAL_FUND: 'Apertura de caja',
      DOCUMENT_COUNTS: 'Número de Transacciones',
      INVOICES_COUNT: 'Facturas',
      CREDIT_NOTES_COUNT: 'Notas de Créd.',
      PAYMENTS_COUNT: 'Pagos',
      TOTALS: 'Totales',
      DAILY_SUMMARY: 'Resumen Diario',
      DAILY_SUMMARY_DESCRIPTION:
        'Resumen consolidado de todos los turnos del día',
      PER_CASHIER: 'Por Cajero',
      COMBINED_TOTAL: 'Total Combinado',
      SELECT_DATE: 'Seleccionar Fecha',
      TRANSACTION_DETAIL: 'Detalle de Transacciones',
      TRANSACTION_COUNT: '{{count}} transacciones',
      VIEW_DETAILS: 'Ver Detalle',
      PRINT_RECONCILIATION: 'Imprimir Cuadre',
      PRINT_CUADRE_TITLE: 'Imprimir Cuadre de Caja',
      PRINT_CUADRE_DESCRIPTION:
        'Selecciona el tamaño de papel. Tu preferencia se guardará para el próximo cierre.',
      PRINT_CUADRE_ACTION: 'Imprimir',
      PRINT_CUADRE_THERMAL: 'Térmico 80 mm',
      PRINT_CUADRE_THERMAL_HINT: 'Impresora de tickets del punto de venta.',
      PRINT_CUADRE_A4: 'A4',
      PRINT_CUADRE_A4_HINT: 'Impresora de escritorio para archivo.',
      OPENED_AT: 'Fecha y hora apertura',
      CLOSED_AT: 'Fecha y hora cierre',
      OPENING_CASH_TITLE: 'Efectivo en Caja (Apertura)',
      FOREIGN_CURRENCY_RECEIVED_CLOSE: 'Moneda Extranjera Recibida (Cierre)',
    },
    TABS: {
      SHIFTS: 'Turnos',
      CUADRE_GENERAL: 'Cuadre general',
    },
  },
  CASH_VOUCHERS: {
    TITLE: 'Vales de Caja',
    CREATE: 'Crear Vale',
    VOUCHER_IN: 'Vale de Ingreso',
    VOUCHER_OUT: 'Vale de Egreso',
    AMOUNT: 'Monto',
    DESCRIPTION: 'Descripción',
    TYPE: 'Tipo',
    CREATED_SUCCESS: 'Vale creado exitosamente',
    DELETED_SUCCESS: 'Vale eliminado exitosamente',
    NO_VOUCHERS: 'No hay vales registrados',
    DELETE_CONFIRM: '¿Está seguro de eliminar este vale?',
  },
  PROVIDERS: {
    TITLE: 'Proveedor',
    SUBTITLE: 'Administre la información de sus proveedores.',
    ADD_PROVIDER: 'Nuevo Proveedor',
    CREATE_PROVIDER: 'Crear Proveedor',
    PICKER_TITLE: 'Información del Proveedor',
    UNREGISTERED_PROVIDER: 'Proveedor sin registrar',
    REGISTER_UNREGISTERED_BUTTON: 'Registrar',
    REGISTER_UNREGISTERED_TOOLTIP: 'Registrar este proveedor',
    REGISTER_UNREGISTERED_TITLE: '¿Registrar proveedor?',
    REGISTER_UNREGISTERED_DESCRIPTION:
      '¿Deseas registrar a {{name}} como proveedor?',
    EDIT_PROVIDER: 'Editar Proveedor',
    SEARCH_PLACEHOLDER: 'Buscar proveedores por nombre, email, teléfono...',
    NAME: 'Nombre',
    EMAIL: 'Correo electrónico',
    PHONE: 'Teléfono',
    WEBSITE: 'Sitio Web',
    COUNTRY: 'País',
    STATE_REGION: 'Estado/Región',
    CITY: 'Ciudad',
    ADDRESS: 'Dirección',
    FISCAL_ID: 'RNC/Cédula',
    TAX_ID_TYPE: 'Tipo de Identificación Fiscal',
    PAYMENT_TERMS_DAYS: 'Días de Crédito',
    CREDIT_LIMIT: 'Límite de Crédito',
    CREDIT_BALANCE: 'Balance de Crédito',
    WITH_OUTSTANDING_ONLY: 'Solo con saldo pendiente',
    INTERNAL_CODE: 'Código Interno',
    TYPE: 'Tipo',
    STATUS: 'Estado',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    ACTIVATE: 'Activar',
    DEACTIVATE: 'Desactivar',
    RESTORE: 'Restaurar',
    NO_PROVIDERS: 'No hay proveedores disponibles',
    NO_NAME: 'Sin nombre',
    TYPE_INDIVIDUAL: 'Persona',
    TYPE_COMPANY: 'Empresa',
    ALL_TYPES: 'Todos los tipos',
    DELETE_TITLE: 'Eliminar Proveedor',
    DELETE_DESCRIPTION:
      '¿Está seguro de eliminar este proveedor? Esta acción no se puede deshacer.',
    DELETE_CONFIRM: 'Eliminar',
    DELETE_CANCEL: 'Cancelar',
    CONFIRM_CREATE_TITLE: '¿Crear nuevo proveedor?',
    CONFIRM_CREATE_DESCRIPTION:
      '¿Desea crear un nuevo proveedor con el nombre "{{name}}"?, Podras añadir más información de este proveedor desde la seccion de proveedores.',
  },
  CONFIGURATION: {
    TITLE: 'Configuración',
    SUBTITLE: 'Administre la configuración del sistema.',
    COMPANY: 'Empresa',
    POS: 'Punto de Venta',
    BOM_TERMINOLOGY_LABEL: 'Terminología de composición',
    BOM_TERMINOLOGY_HELP:
      'Vocabulario que la app usa para productos compuestos (recetas, kits, ensamblajes). Solo cambia las etiquetas visibles.',
    BOM_TERMINOLOGY_RECIPE: 'Receta / Ingredientes (restaurantes)',
    BOM_TERMINOLOGY_KIT: 'Kit / Componentes (retail, tecnología)',
    BOM_TERMINOLOGY_BOM: 'BOM / Materiales (manufactura)',
    BOM_TERMINOLOGY_BUNDLE: 'Bundle / Contenido (suscripción, regalos)',
    BUSINESS_SETTINGS: {
      TITLE: 'Configuración del Negocio',
      SUBTITLE:
        'Administre las reglas y configuraciones operacionales de su negocio',
      TAB_GENERAL: 'General',
      TAB_VENTAS: 'Ventas / Facturación',
      TAB_INVENTARIO: 'Inventario',
      TAB_CONTABILIDAD: 'Contabilidad',
      TAB_KDS: 'KDS / Restaurante',
      TAB_ECF: 'e-CF (DGII)',
      TAB_ENVIOS: 'Envíos',
      TAB_RESTAURANT: 'Restaurante',
      TAB_TURNOS: 'Turnos y Cuadre de Caja',
      TAB_ALQUILERES: 'Alquileres',
      RENTALS_FIANZA_TITLE: 'Fianza',
      RENTALS_FIANZA_HELP:
        'Configura cómo se calcula la fianza del alquiler. Se retiene como pasivo y se devuelve o descuenta al inspeccionar la devolución.',
      RENTALS_ABONO_TITLE: 'Abono',
      RENTALS_ABONO_HELP:
        'Define el abono mínimo (pago inicial) que debe cobrarse antes de reservar o confirmar un pedido.',
      RENTALS_PAYMENTS_TITLE: 'Cobro y pagos',
      RENTALS_PAYMENTS_HELP:
        'Reglas de cobro del alquiler: términos de pago, entrega condicionada al pago y reembolso al cancelar.',
      RENTALS_FLOW_TITLE: 'Flujo del pedido',
      RENTALS_FLOW_HELP:
        'Controla las etapas por las que pasa el pedido y cuándo se reserva el inventario.',
      RENTALS_RULES_TITLE: 'Reglas de negocio',
      RENTALS_RULES_HELP:
        'Reglas de cargos y reembolsos del alquiler: artículos no devueltos y reembolso al cancelar.',
      RENTALS_FIANZA_TYPE_LABEL: 'Tipo de fianza',
      RENTALS_FIANZA_TYPE_FIXED: 'Monto fijo',
      RENTALS_FIANZA_TYPE_PERCENTAGE_TOTAL: 'Porcentaje del total',
      RENTALS_FIANZA_TYPE_PERCENTAGE_PER_PRODUCT: 'Porcentaje por producto',
      RENTALS_FIANZA_VALUE_LABEL: 'Valor (monto o %)',
      RENTALS_FIANZA_REQUIRED_LABEL: 'Bloquear entrega sin cobro completo',
      RENTALS_FIANZA_REQUIRED_HELP:
        'Cuando está activo, el sistema no permite marcar el Pedido como entregado hasta que el saldo del alquiler esté completamente cobrado (backend ya aplica esta regla; este ajuste sirve de recordatorio para el operador).',
      RENTALS_FIANZA_ENABLED_LABEL: 'Habilitar fianza (depósito reembolsable)',
      RENTALS_FIANZA_ENABLED_HELP:
        'Cuando está activo, los alquileres retienen una fianza (depósito) reembolsable según el tipo y valor configurados; se devuelve al cliente al finalizar el alquiler, menos daños o cargos pendientes. Cuando está desactivado, los alquileres no llevan fianza.',
      RENTALS_ABONO_REQUIRE_LABEL: 'Requerir abono mínimo',
      RENTALS_ABONO_REQUIRE_HELP:
        'Cuando está activo, el pedido no se puede reservar ni confirmar hasta cobrar el abono mínimo. El abono permite pagos parciales.',
      RENTALS_ABONO_TYPE_LABEL: 'Tipo de abono',
      RENTALS_ABONO_TYPE_FIXED: 'Monto fijo',
      RENTALS_ABONO_TYPE_PERCENTAGE: 'Porcentaje del total',
      RENTALS_ABONO_VALUE_LABEL: 'Valor (monto o %)',
      RENTALS_PAYMENT_TERMS_ALLOWED_LABEL: 'Términos de pago permitidos',
      RENTALS_PAYMENT_TERMS_ALLOWED_HELP:
        'Contado: cada Cobrar debe pagar el saldo completo. Abono: se permite pago parcial y el Pedido queda pendiente. Ambos: el operador elige por Pedido.',
      RENTALS_PAYMENT_TERMS_CONTADO_ONLY: 'Solo Contado',
      RENTALS_PAYMENT_TERMS_ABONO_ONLY: 'Solo Abono',
      RENTALS_PAYMENT_TERMS_BOTH: 'Ambos',
      RENTALS_PRODUCTION_FLOW_LABEL: 'Flujo de producción',
      RENTALS_PRODUCTION_FLOW_HELP:
        'Activa el paso "Enviar a producción → Listo" antes de entregar. Desactivado, el operador marca el Pedido como entregado directamente después de confirmarlo.',
      RENTALS_RESERVATION_TRIGGER_LABEL: 'Reservar inventario',
      RENTALS_RESERVATION_TRIGGER_HELP:
        'Define cuándo un pedido aparta (reserva) el inventario de sus artículos para que no se vendan a otro cliente.',
      RENTALS_RESERVATION_TRIGGER_ON_CREATE: 'Al crear el pedido',
      RENTALS_RESERVATION_TRIGGER_ON_CONFIRMED: 'Al confirmar el pedido',
      RENTALS_RESERVATION_TRIGGER_ON_PAID: 'Al pagar por completo',
      RENTALS_DEPOSIT_RETURN_POLICY_LABEL: 'Devolución de la fianza',
      RENTALS_DEPOSIT_RETURN_POLICY_HELP:
        'Define si la fianza se devuelve solo cuando el pedido se devuelve a tiempo o también cuando se devuelve fuera de plazo.',
      RENTALS_DEPOSIT_RETURN_POLICY_ON_TIME: 'Devolver fianza a tiempo',
      RENTALS_DEPOSIT_RETURN_POLICY_OVERDUE: 'Devolver fianza fuera de plazo',
      RENTALS_CHARGE_MISSING_ITEMS_LABEL: 'Cobrar artículos no devueltos',
      RENTALS_CHARGE_MISSING_ITEMS_HELP:
        'Si está activo, cuando un artículo no se devuelve se cobra un porcentaje de su valor además de retener la fianza.',
      RENTALS_MISSING_ITEM_CHARGE_PERCENT_LABEL:
        'Porcentaje a cobrar por artículo no devuelto',
      RENTALS_MISSING_ITEM_CHARGE_PERCENT_HELP:
        'Porcentaje del valor del artículo no devuelto que se cobra al cliente (100% = valor completo).',
      RENTALS_REFUND_ON_CANCEL_LABEL: 'Reembolsar pagos al cancelar',
      RENTALS_REFUND_ON_CANCEL_HELP:
        'Si está activo, al cancelar un alquiler con pagos cobrados se reembolsa el dinero; si está inactivo, el dinero cobrado permanece en el pedido cancelado.',
      RENTALS_PAYMENT_TERMS_FIELD_LABEL: 'Tipo de pago',
      RENTALS_PAYMENT_TERMS_CONTADO: 'Contado',
      RENTALS_PAYMENT_TERMS_ABONO: 'Abono',
      RENTALS_RATE_TITLE: 'Tarifa de alquiler',
      RENTALS_RATE_HELP:
        'Regla por defecto para calcular el subtotal del alquiler. Cada producto puede sobreescribir esta configuración.',
      RENTALS_RATE_TYPE_LABEL: 'Tipo de tarifa',
      RENTALS_RATE_TYPE_FIXED_PRICE: 'Precio fijo',
      RENTALS_RATE_TYPE_PER_DAY_PERCENTAGE: 'Porcentaje por día',
      RENTALS_RATE_TYPE_PER_DAY_FIXED: 'Monto fijo por día',
      RENTALS_RATE_VALUE_LABEL: 'Valor (monto o %)',
      RENTALS_PERIOD_TITLE: 'Período de alquiler',
      RENTALS_PERIOD_HELP:
        'Período mínimo permitido para un Pedido o reservación de alquiler. El formulario predefine la fecha de fin al mínimo y bloquea selecciones más cortas.',
      RENTALS_PERIOD_MIN_DAYS_LABEL: 'Días mínimos',
      RENTALS_RESERVATION_TITLE: 'Reservaciones',
      RENTALS_RESERVATION_HELP:
        'Horas antes de que una reservación confirmada expire y libere el stock.',
      RENTALS_RESERVATION_EXPIRY_LABEL: 'Horas de expiración',
      RENTALS_PICKUP_TITLE: 'Recogido',
      RENTALS_PICKUP_HELP:
        'Cuando un Pedido está listo, el sistema puede enviar un correo con el token QR para que el cliente lo presente al recoger.',
      RENTALS_PICKUP_EMAIL_LABEL: 'Enviar correo de recogido',
      RENTALS_PICKUP_EMAIL_HELP:
        'Envía el correo Listo-para-recoger con el enlace QR cuando el Pedido pasa a listo.',
      RENTALS_PICKUP_TTL_LABEL: 'Vigencia del token (horas)',
      RESTAURANT_OPERATOR_VERIFICATION_TITLE:
        'Verificación del mesero en el salón',
      RESTAURANT_OPERATOR_VERIFICATION_LABEL: 'Modo de verificación',
      RESTAURANT_OPERATOR_VERIFICATION_HELP:
        'Controla cuándo el sistema pide identificar al mesero con PIN o tarjeta al operar en el salón.',
      RESTAURANT_OPERATOR_VERIFICATION_OPT_OFF:
        'Desactivado — nunca pide verificación en el salón.',
      RESTAURANT_OPERATOR_VERIFICATION_OPT_ON_ACCOUNT_OPEN:
        'Al abrir una mesa — solo se pide al abrir la cuenta; agregar artículos después no vuelve a pedir.',
      RESTAURANT_OPERATOR_VERIFICATION_OPT_ON_OPERATOR_CHANGE:
        'Al cambiar de mesero — se pide al abrir y cada vez que un mesero diferente al que abrió la cuenta agrega artículos.',
      INVOICING_TITLE: 'Facturación',
      INVOICING_DESCRIPTION:
        'Configura las opciones de facturación de tu negocio: facturas a crédito y facturación libre (sin catálogo de productos).',
      EDIT: 'Editar',
      LOADING: 'Cargando configuración...',
      SEARCH_EMPTY_TITLE: 'Sin resultados',
      SEARCH_EMPTY_BODY:
        'Ningún ajuste de esta pestaña coincide con tu búsqueda.',
      SUCCESS_MESSAGE: 'Configuración del negocio actualizada correctamente',
      ERROR_MESSAGE: 'No se pudo actualizar la configuración',
      NO_CHANGES_TO_SAVE: 'No hay cambios para guardar.',
      INVALID_FORM: 'Formulario inválido',
      INVALID_FIELD_FALLBACK: 'inválido',
      // Credit Notes & Refunds Configuration
      CREDIT_NOTES_TITLE: 'Notas de Crédito y Reembolsos',
      CREDIT_NOTES_DESCRIPTION:
        'Configure cómo se manejan las notas de crédito y reembolsos en su negocio',
      CREDIT_NOTE_HANDLING: 'Manejo de Notas de Crédito',
      CREDIT_NOTE_HANDLING_HELP:
        'Determina si las notas de crédito se acumulan como balance del cliente o se procesan como reembolsos directos',
      HANDLING_CUSTOMER_BALANCE: 'Balance del Cliente',
      HANDLING_REFUND: 'Reembolso Directo',
      REFUND_WINDOW_DAYS: 'Período de Devolución',
      REFUND_WINDOW_DAYS_HELP:
        'Número de días después de la compra en los que no se requiere aprobación para devoluciones',
      REQUIRE_APPROVAL_OUTSIDE_WINDOW: 'Requerir Aprobación Fuera del Período',
      REQUIRE_APPROVAL_OUTSIDE_WINDOW_HELP:
        'Requiere aprobación de un gerente para devoluciones fuera del período de devolución',
      APPROVAL_THRESHOLD_AMOUNT: 'Monto Límite para Aprobación',
      APPROVAL_THRESHOLD_AMOUNT_HELP:
        'Las notas de crédito por encima de este monto siempre requerirán aprobación (dejar en 0 para desactivar)',
      CREDIT_EXPIRATION_ENABLED: 'Habilitar Expiración de Créditos',
      CREDIT_EXPIRATION_ENABLED_HELP:
        'Los créditos de clientes expirarán después del período configurado',
      CREDIT_EXPIRATION_DAYS: 'Días para Expiración de Créditos',
      CREDIT_EXPIRATION_DAYS_HELP:
        'Número de días después de los cuales los créditos de clientes expiran automáticamente',
      // Credit Invoices Configuration
      CREDIT_INVOICES_TITLE: 'Facturas a Crédito',
      CREDIT_INVOICES_DESCRIPTION:
        'Permite generar facturas a crédito y asignar límites de crédito a clientes',
      CREDIT_INVOICES_ENABLED: 'Habilitar Facturas a Crédito',
      CREDIT_INVOICES_ENABLED_HELP:
        'Al habilitarlo, aparecerán los términos de pago a crédito (15, 30, 60, 90 días) y podrás configurar límites de crédito por cliente',
      CREDIT_INVOICES_DISABLED_ADMIN:
        'Las facturas a crédito están deshabilitadas. Puedes habilitarlas en Configuración → Ajustes del Negocio.',
      CREDIT_INVOICES_DISABLED_USER:
        'Las facturas a crédito no están habilitadas en este negocio. Contacta al administrador para habilitarlas.',
      // Invoice Workflow Configuration
      INVOICE_WORKFLOW_TITLE: 'Flujo de Facturación',
      INVOICE_WORKFLOW_DESCRIPTION:
        'Define cómo se crean y procesan los pagos de las facturas en el negocio',
      INVOICE_WORKFLOW_TYPE: 'Tipo de Flujo',
      INVOICE_WORKFLOW_TYPE_HELP:
        'Determina si el vendedor cobra y imprime, o si el cajero procesa el pago',
      INVOICE_WORKFLOW_SELLER_PRINTS: 'Vendedor Imprime',
      INVOICE_WORKFLOW_SELLER_PRINTS_HELP:
        'El vendedor cobra el dinero e imprime la factura (un solo empleado)',
      INVOICE_WORKFLOW_CASHIER_PRINTS: 'Cajero Imprime',
      INVOICE_WORKFLOW_CASHIER_PRINTS_HELP:
        'El vendedor crea la factura, el cajero procesa el pago e imprime (dos empleados)',
      INVOICE_WORKFLOW_SELLER_PENDING: 'Vendedor Imprime — Pendiente',
      INVOICE_WORKFLOW_SELLER_PENDING_HELP:
        'El vendedor imprime la factura, pero la factura queda con estado Pendiente de Pago; caja completa el cobro después',
      // Payment modal mode (Phase 4)
      PAYMENT_MODAL_MODE_TITLE: 'Experiencia del modal de pago',
      PAYMENT_MODAL_MODE_DESCRIPTION:
        'Define cómo se muestra el modal de pago en cada sucursal.',
      PAYMENT_MODAL_MODE_LABEL: 'Modo',
      PAYMENT_MODAL_MODE_HELP:
        'Detallado muestra todos los pasos y opciones. Rápido es pantalla completa con 2 toques (efectivo + factura preseleccionados) ideal para mostradores de alto volumen. Las validaciones fiscales (RNC, NCF, impuestos) son idénticas en ambos modos.',
      PAYMENT_MODAL_MODE_DETAILED: 'Detallado (estándar)',
      PAYMENT_MODAL_MODE_FAST: 'Rápido (alta rotación)',
      DEFAULT_PRINT_PAPER_SIZE_TITLE: 'Tamaño de impresión por documento',
      DEFAULT_PRINT_PAPER_SIZE_DESCRIPTION:
        'Define el tamaño por defecto de cada tipo de documento. Si dejas «Sin preferencia», al imprimir se inicia en 80mm (térmica) y se recuerda la última selección del operador. No aplica a comandas.',
      DEFAULT_PRINT_PAPER_SIZE_LABEL: 'Tamaño por defecto',
      DEFAULT_PRINT_PAPER_SIZE_HELP:
        'Elige entre 80mm (impresora térmica) o A4 (impresora de escritorio). Aplica solo a facturas, pagos y cotizaciones — nunca a comandas.',
      DEFAULT_PRINT_PAPER_SIZE_NONE: 'Sin preferencia (preguntar)',
      DEFAULT_PRINT_PAPER_SIZE_THERMAL: 'Térmica 80mm',
      DEFAULT_PRINT_PAPER_SIZE_A4: 'A4 (Carta 8 1/2 x 11)',
      PRINT_PAPER_SIZE_INVOICE: 'Factura',
      PRINT_PAPER_SIZE_POS: 'Punto de venta (POS)',
      PRINT_PAPER_SIZE_RESTAURANT: 'Restaurante (cierre de mesa)',
      PRINT_PAPER_SIZE_QUOTE: 'Cotización',
      PRINT_PAPER_SIZE_PAYMENT: 'Recibo de pago',
      PRINT_PAPER_SIZE_SALES_ORDER: 'Orden de venta',
      PRINT_PAPER_SIZE_PURCHASE_ORDER: 'Orden de compra',
      PRINT_PAPER_SIZE_RENTAL: 'Alquileres',
      SHOW_DIFFERENCE_TO_CASHIER_LABEL: 'Mostrar diferencia al cajero',
      SHOW_DIFFERENCE_TO_CASHIER_DESC:
        'Cuando está activo, el cajero ve la Diferencia y el Efectivo en el Cuadre al cerrar el turno. Cuando está inactivo se ocultan (conteo a ciegas). Los administradores siempre los ven, y la Diferencia siempre se imprime en el cuadre.',
      PRINT_BEHAVIOR_TITLE: 'Comportamiento de impresión y envío',
      PRINT_BEHAVIOR_COL_SETTING: 'Configuración',
      PRINT_BEHAVIOR_DOCUMENTS_TITLE: 'Documentos',
      PRINT_BEHAVIOR_DOCUMENTS_DESCRIPTION:
        'Cada documento tiene sus propios ajustes. Sin configurar, usa los de Factura.',
      PRINT_DOC_QUOTE: 'Cotización',
      PRINT_DOC_CONDUCE: 'Conduce',
      PRINT_DOC_SALES_ORDER: 'Orden de venta',
      PRINT_DOC_PURCHASE_ORDER: 'Orden de compra',
      PRINT_DOC_RENTAL: 'Alquileres',
      PRINT_DOC_PAYMENT: 'Pago',
      PRINT_DOC_SUPPLIER_INVOICE: 'Factura de proveedor',
      PRINT_BEHAVIOR_COL_VALUE: 'Activo / impresora',
      PRINT_BEHAVIOR_DESCRIPTION:
        'Controla, por cada surface (POS, Factura, Restaurante), si se imprime automáticamente, se permite reimprimir, compartir por WhatsApp, enviar por correo o imprimir la comanda de cocina.',
      PRINT_BEHAVIOR_POS_TITLE: 'Punto de Venta (POS)',
      PRINT_BEHAVIOR_INVOICE_TITLE: 'Facturación (Escritorio)',
      PRINT_BEHAVIOR_INVOICE_SCOPE:
        'Aplica a facturas, cotizaciones, conduces y órdenes de venta impresas desde el listado o su detalle. Imprimir automáticamente y la comanda solo aplican al crear facturas.',
      PRINT_BEHAVIOR_RESTAURANT_TITLE: 'Restaurante',
      PRINT_BEHAVIOR_AUTO_PRINT: 'Imprimir automáticamente',
      PRINT_BEHAVIOR_AUTO_PRINT_HELP:
        'Después de completar el pago se imprime la factura sin pasos adicionales.',
      PRINT_BEHAVIOR_REPRINT: 'Permitir reimprimir',
      PRINT_BEHAVIOR_REPRINT_HELP:
        'El modal de pago no se cierra al terminar. Cualquier usuario con permiso puede volver a imprimir.',
      PRINT_BEHAVIOR_ALLOW_SHARING: 'Permitir compartir por WhatsApp',
      PRINT_BEHAVIOR_ALLOW_SHARING_HELP:
        'Habilita el campo de teléfono para enviar el enlace del documento por WhatsApp.',
      PRINT_BEHAVIOR_ALLOW_EMAILING: 'Permitir envío por correo',
      PRINT_BEHAVIOR_ALLOW_EMAILING_HELP:
        'Habilita el campo de correo para enviar el documento como PDF.',
      PRINT_BEHAVIOR_PRINT_COMMANDA: 'Imprimir comanda al procesar',
      PRINT_BEHAVIOR_PRINT_COMMANDA_HELP:
        'Al confirmar los productos se dispara la impresión de la comanda para cocina.',
      ASK_PAPER_SIZE_TITLE: 'Tamaño de impresión',
      ASK_PAPER_SIZE_DESCRIPTION:
        'Elige el tamaño de papel para imprimir esta factura. Guardaremos tu preferencia en este dispositivo — no volveremos a preguntar. Puedes cambiarla luego en Configuración → Negocio.',
      // Delivery Settings
      DELIVERY_SETTINGS_TITLE: 'Configuración de Delivery',
      DELIVERY_SETTINGS_DESCRIPTION:
        'Habilite o deshabilite el delivery en las facturas',
      DELIVERIES_ENABLED: 'Habilitar delivery',
      DELIVERIES_ENABLED_HELP:
        'Permite seleccionar método, repartidor y tarifa en las facturas',
      DELIVERY_AMOUNT_ACCOUNTED: 'Contabilizar el monto del delivery',
      DELIVERY_AMOUNT_ACCOUNTED_HELP:
        'Activo: el cobro del delivery se suma al total de la factura y se registra como ingreso. Desactivado: el monto se muestra solo como referencia en la factura (no suma al total) y se registra contra la cuenta seleccionada — útil cuando el delivery lo opera un tercero y el negocio solo pasa el cobro.',
      DELIVERY_ACCOUNT: 'Cuenta contable para delivery (tercero)',
      DELIVERY_ACCOUNT_HELP:
        'Cuenta del catálogo donde se registrará el dinero recibido por delivery mientras no sea liquidado al proveedor externo. Normalmente una cuenta de pasivo / clearing.',
      DELIVERY_TAX_MODE_LABEL: 'Impuesto al delivery',
      DELIVERY_TAX_MODE_HELP:
        'Define si el cargo de delivery lleva impuesto. Automático: se cobra impuesto solo cuando todos los productos de la venta son gravados (regla DGII CA3251). Siempre: cobra impuesto al delivery sin importar los productos. Nunca: el delivery nunca lleva impuesto.',
      DELIVERY_TAX_MODE_AUTO: 'Automático (según los productos)',
      DELIVERY_TAX_MODE_ALWAYS: 'Siempre cobrar impuesto',
      DELIVERY_TAX_MODE_NEVER: 'Nunca cobrar impuesto',
      DELIVERY_TAX_LABEL: 'Tipo de impuesto del delivery',
      DELIVERY_TAX_HELP:
        'Impuesto del catálogo que se cobrará al delivery. Requerido cuando el modo es "Siempre cobrar impuesto".',
      // Dashboard Settings
      DASHBOARD_SETTINGS_TITLE: 'Configuración del Dashboard',
      DASHBOARD_SETTINGS_DESCRIPTION:
        'Configure las metas y objetivos que se muestran en el panel de control',
      MONTHLY_SALES_GOAL: 'Meta de Ventas Mensual',
      MONTHLY_SALES_GOAL_HELP:
        'El objetivo de ventas mensuales que se usará para calcular las metas diarias y semanales en el dashboard',
      // WhatsApp Settings
      WHATSAPP_SETTINGS_TITLE: 'Configuración de WhatsApp',
      WHATSAPP_SETTINGS_DESCRIPTION:
        'Configure el mensaje que se envía cuando comparte facturas por WhatsApp',
      WHATSAPP_MESSAGE_TEMPLATE: 'Plantilla de Mensaje de Factura',
      WHATSAPP_MESSAGE_TEMPLATE_HELP:
        'Use {customerName}, {businessName}, {total}, {invoiceNumber} e {invoiceLink} como placeholders',
      WHATSAPP_QUOTE_MESSAGE_TEMPLATE: 'Plantilla de Mensaje de Cotización',
      WHATSAPP_QUOTE_MESSAGE_TEMPLATE_HELP:
        'Use {customerName}, {businessName}, {total}, {invoiceNumber} e {invoiceLink} como placeholders',
      QUOTE_VALIDITY_DAYS: 'Días de Validez de Cotización',
      QUOTE_VALIDITY_DAYS_HELP:
        'Número de días que una cotización es válida desde su creación. Use 0 para cotizaciones sin vencimiento.',
      // Inventory Alerts Settings
      INVENTORY_ALERTS_TITLE: 'Alertas de Inventario',
      INVENTORY_ALERTS_DESCRIPTION:
        'Configura el periodo de días sin ventas para alertar sobre productos sin movimiento',
      NO_SALES_PERIOD_DAYS: 'Días sin ventas',
      NO_SALES_PERIOD_DAYS_HELP:
        'Número de días sin ventas antes de que un producto sea marcado como sin movimiento en el dashboard',
      // Cost Management Settings
      COST_MANAGEMENT_TITLE: 'Gestión de Costos',
      COST_MANAGEMENT_DESCRIPTION:
        'Define cómo se determina y controla el costo de los productos.',
      COST_FROM_PURCHASES_MODE_LABEL: 'Origen del costo',
      COST_FROM_PURCHASES_MODE_HELP:
        'Con "Última compra", el costo del producto se actualiza automáticamente al recibir una orden de compra y solo usuarios autorizados pueden editarlo manualmente.',
      COST_MODE_MANUAL: 'Manual',
      COST_MODE_LAST_PURCHASE: 'Última compra',
      PRODUCT_COST_REQUIRED_LABEL: 'Costo obligatorio',
      PRODUCT_COST_REQUIRED_HELP:
        'Cuando está activo, los productos no pueden guardarse sin costo (0 es válido para costo cero real).',
      // ECF Submission Mode
      ECF_SUBMISSION_MODE_TITLE: 'Facturación Electrónica (e-CF)',
      ECF_SUBMISSION_MODE_DESCRIPTION:
        'Configura cómo se envían los comprobantes fiscales electrónicos (e-CF) a la DGII.',
      ECF_SUBMISSION_MODE: 'Modo de envío',
      ECF_SUBMISSION_MODE_HELP:
        'En línea: cada factura se envía a la DGII al momento de crearla. Por lotes: las facturas se acumulan y se envían en un proceso masivo.',
      ECF_SUBMISSION_MODE_ONLINE: 'En línea (inmediato)',
      ECF_SUBMISSION_MODE_BATCH: 'Por lotes (batch)',
      // ECF Certificate Management
      ECF_CERTIFICATES_TITLE: 'Certificados Digitales DGII',
      ECF_CERTIFICATES_DESCRIPTION:
        'Certificados P12/PFX registrados para la facturación electrónica. Puedes gestionar los certificados desde la configuración de e-CF.',
      ECF_CERTIFICATES_EMPTY: 'No hay certificados registrados.',
      ECF_CERTIFICATES_VALID_TO: 'Válido hasta',
      ECF_CERTIFICATES_DELETE_CONFIRM:
        '¿Estás seguro de que deseas eliminar este certificado? Esta acción no se puede deshacer.',
      // Session Lock Settings
      TOUCH_KEYBOARD_TITLE: 'Teclado en pantalla (táctil)',
      TOUCH_KEYBOARD_DESCRIPTION:
        'Muestra un teclado en pantalla al enfocar un campo en las superficies táctiles seleccionadas. Pensado para tablets. Cada operador puede activarlo o desactivarlo en su propio dispositivo.',
      TOUCH_KEYBOARD_ENABLE_LABEL: 'Habilitar teclado en pantalla',
      TOUCH_KEYBOARD_ENABLE_HELP:
        'Interruptor maestro del negocio. Apagado = se usa el teclado del sistema operativo en todas las superficies.',
      TOUCH_KEYBOARD_SURFACES_LABEL: 'Superficies con teclado en pantalla',
      TOUCH_KEYBOARD_SURFACES_HELP:
        'Elige en qué superficies aparece. Vacío = Todas.',
      DISCOUNT_MODE_TITLE: 'Descuentos por superficie',
      DISCOUNT_MODE_DESCRIPTION:
        'Elige si cada superficie de venta aplica el descuento como porcentaje (%) o como monto fijo (RD$). Aplica al descuento general y al descuento por línea.',
      DISCOUNT_MODE_PERCENTAGE: 'Porcentaje (%)',
      DISCOUNT_MODE_FIXED: 'Monto fijo (RD$)',
      DISCOUNT_SURFACE_INVOICE: 'Factura',
      DISCOUNT_SURFACE_QUOTE: 'Cotización',
      DISCOUNT_SURFACE_CONDUCE: 'Conduce',
      DISCOUNT_SURFACE_SALES_ORDER: 'Orden de venta',
      DISCOUNT_SURFACE_POS: 'Punto de venta',
      DISCOUNT_SURFACE_RESTAURANT_TAB: 'Restaurante (cuenta de mesa)',
      PRE_FACTURA_TITLE: 'Pre-Factura por superficie',
      PRE_FACTURA_DESCRIPTION:
        'Controla en qué superficies de venta se permite la Pre-Factura (documento sin valor fiscal, sin NCF). Donde esté deshabilitada, el operador deberá emitir un comprobante fiscal (B/E).',
      POS_SUPERVISED_MODE_LABEL: 'Modo supervisado en POS',
      POS_SUPERVISED_MODE_HELP:
        'Cuando está activo en esta sucursal: se oculta el botón Guardar y se requiere la aprobación de un gerente/administrador para quitar un artículo del carrito, cancelar la factura o salir/recargar la pantalla con artículos en el carrito. Cada remoción aprobada se registra y aparece en el cierre de caja.',
      PRE_FACTURA_ENABLE_LABEL: 'Permitir Pre-Factura',
      PRE_FACTURA_ENABLE_HELP:
        'Interruptor maestro. Apagado = Pre-Factura deshabilitada en todas las superficies; solo se emiten comprobantes fiscales.',
      PRE_FACTURA_SURFACES_LABEL: 'Superficies con Pre-Factura permitida',
      PRE_FACTURA_SURFACES_HELP:
        'Marca dónde se permite la Pre-Factura. Vacío = Todas. Desmarca una superficie para forzar comprobante fiscal (el tipo por defecto se elige según el RNC del cliente).',
      PRE_FACTURA_REQUIRES_NCF_WARNING:
        'Para deshabilitar la Pre-Factura necesitas al menos una secuencia de comprobante fiscal (B o E) activa, o no se podrá cobrar en esas superficies.',
      PRE_FACTURA_CONFIGURE_NCF_LINK: 'Configurar comprobantes (NCF)',
      COMMISSION_SECTION_LABEL: 'Etiqueta de la sección de vendedores',
      COMMISSION_SECTION_LABEL_HELP:
        'Nombre que verá el cajero sobre la selección de vendedor al cobrar. Vacío = "Comisión Vendedores".',
      SESSION_LOCK_TITLE: 'Bloqueo de Sesión',
      SESSION_LOCK_DESCRIPTION:
        'Configura el tiempo de inactividad antes de que la sesión se bloquee automáticamente. El usuario deberá ingresar su PIN o contraseña para continuar.',
      SESSION_LOCK_TIMEOUT_WEB: 'Tiempo de bloqueo (Web)',
      SESSION_LOCK_TIMEOUT_WEB_HELP:
        'Minutos de inactividad antes de bloquear la sesión en el navegador (neocrm).',
      SESSION_LOCK_TIMEOUT_MOBILE: 'Tiempo de bloqueo (Móvil)',
      SESSION_LOCK_TIMEOUT_MOBILE_HELP:
        'Minutos en segundo plano antes de bloquear la sesión en la app móvil.',
      MAX_LOGIN_ATTEMPTS: 'Máximo de intentos de inicio de sesión',
      MAX_LOGIN_ATTEMPTS_HELP:
        'Cantidad máxima de intentos fallidos (PIN + contraseña combinados) antes de cerrar todas las sesiones del usuario.',
      // ECF Environment
      ECF_ENVIRONMENT_TITLE: 'Entorno e-CF',
      ECF_ENVIRONMENT_DESCRIPTION:
        'Configura el entorno de la DGII para la facturación electrónica.',
      ECF_ENVIRONMENT_LABEL: 'Entorno DGII',
      ECF_ENVIRONMENT_HELP:
        'Selecciona el entorno de la DGII. Pruebas para desarrollo, Certificación para validación, y Producción para envíos reales.',
      ECF_ENVIRONMENT_TESTECF: 'Pruebas (TesteCF)',
      ECF_ENVIRONMENT_CERTECF: 'Certificación (CerteCF)',
      ECF_ENVIRONMENT_ECF: 'Producción (eCF)',
      ECF_ENVIRONMENT_NO_CERT_TAG: 'sin certificado',
      ECF_ENVIRONMENT_REQUIRES_CERT_HINT:
        'Este ambiente no tiene un certificado DGII vigente. Súbelo en Certificados DGII antes de cambiar a este ambiente.',
      ECF_ENVIRONMENT_GO_TO_CERTIFICATES: 'Ir a Certificados DGII',
      ECF_ENVIRONMENT_ONBOARDING_LOCKED:
        'Durante el onboarding de e-CF el ambiente queda fijado en Certificación (CerteCF). Finaliza el onboarding para poder cambiar a Pruebas o Producción.',
      ECF_ENVIRONMENT_ONBOARDING_LINK: 'Ir al onboarding e-CF',
      ECF_ACTIVE_TITLE: 'Facturación electrónica activa',
      ECF_ACTIVE_DESCRIPTION:
        'Controla si el negocio envía comprobantes electrónicos a la DGII. Desactivar suspende inmediatamente el flujo eCF: las nuevas facturas se emitirán solo como NCF impresos.',
      ECF_ACTIVE_LABEL: 'e-CF activo',
      ECF_ACTIVE_HELP:
        'Antes de reactivar, verifica que el certificado DGII sigue vigente y el onboarding se completó.',
      ECF_ACTIVE_REQUIRES_CERT:
        'No hay un certificado DGII vigente. Debes completar el asistente de Facturación Electrónica antes de activar el e-CF.',
      ECF_ACTIVE_GO_TO_ONBOARDING: 'Ir al asistente de Facturación Electrónica',
      // Validation Messages
      VALIDATION: {
        MARKUP_MIN: 'El porcentaje de margen debe ser al menos 0%',
        MARKUP_MAX: 'El porcentaje de margen no puede exceder 100%',
        KDS_WARNING_MIN: 'El tiempo de advertencia debe ser al menos 1 minuto',
        KDS_WARNING_MAX: 'El tiempo de advertencia no puede exceder 60 minutos',
        KDS_OVERDUE_MIN: 'El tiempo de vencimiento debe ser al menos 1 minuto',
        KDS_OVERDUE_MAX:
          'El tiempo de vencimiento no puede exceder 120 minutos',
        REFUND_WINDOW_MIN: 'El período de devolución debe ser al menos 0 días',
        REFUND_WINDOW_MAX: 'El período de devolución no puede exceder 365 días',
        APPROVAL_THRESHOLD_MIN: 'El monto límite debe ser al menos 0',
        CREDIT_EXPIRATION_MIN:
          'El período de expiración debe ser al menos 1 día',
        CREDIT_EXPIRATION_MAX:
          'El período de expiración no puede exceder 3650 días',
        INVALID_WORKFLOW_TYPE: 'Debe seleccionar un tipo de flujo válido',
        INVALID_CREDIT_HANDLING:
          'Debe seleccionar un manejo de nota de crédito válido',
        REQUIRED_NUMBER: 'Este campo requiere un número válido',
        MONTHLY_SALES_GOAL_MIN: 'La meta de ventas mensual debe ser al menos 0',
      },
    },
    MODULES: {
      COMPANY: {
        TITLE: 'Empresa',
        DESCRIPTION: 'Información general de tu empresa',
        ITEMS: ['Nombre y dirección', 'Información fiscal', 'Logo', 'Contacto'],
      },
      BUSINESS_SETTINGS: {
        TITLE: 'Configuración del Negocio',
        DESCRIPTION: 'Reglas operacionales y configuración empresarial',
        ITEMS: [
          'Cálculo automático de precios',
          'Gestión de órdenes de compra',
          'Reglas de inventario',
          'Configuración de precios',
        ],
      },
      TAXES: {
        TITLE: 'Impuestos',
        DESCRIPTION: 'Configuración de tasas e impuestos',
        ITEMS: [
          'ITBIS / IVA',
          'Impuestos adicionales',
          'Exenciones',
          'Zonas fiscales',
        ],
      },
      INVOICES: {
        TITLE: 'Plantilla de Factura',
        DESCRIPTION: 'Configuración de facturas y recibos',
        ITEMS: [
          'Formato de factura',
          'Numeración',
          'Términos y condiciones',
          'Notas predeterminadas',
        ],
      },
      POS: {
        TITLE: 'Punto de Venta',
        DESCRIPTION: 'Configuración del punto de venta',
        ITEMS: [
          'Impresora de recibos',
          'Diseño de ticket',
          'Métodos de pago',
          'Comportamiento del POS',
        ],
      },
      DELIVERY: {
        TITLE: 'Delivery',
        DESCRIPTION: 'Configura métodos, tarifas y repartidores',
        ITEMS: [
          'Métodos de delivery',
          'Tarifas base',
          'Repartidores',
          'Asignación en facturas',
        ],
      },
      USERS: {
        TITLE: 'Usuarios y Roles',
        DESCRIPTION: 'Gestión de permisos y accesos',
        ITEMS: [
          'Usuarios del sistema',
          'Roles y permisos',
          'Seguridad',
          'Historial de accesos',
        ],
      },
      DOCUMENTS: {
        TITLE: 'Documentos',
        DESCRIPTION: 'Plantillas y formatos de documentos',
        ITEMS: [
          'Plantillas de factura',
          'Cotizaciones',
          'Órdenes de compra',
          'Reportes personalizados',
        ],
      },
      SHIFTS: {
        TITLE: 'Turnos y Cuadre de Caja',
        DESCRIPTION: 'Gestión de turnos y cuadre de caja',
        ITEMS: [
          'Abrir y cerrar turnos',
          'Historial de turnos',
          'Movimientos de efectivo',
          'Reportes de caja',
        ],
      },
      ROLE_PERMISSIONS: {
        TITLE: 'Permisos de Roles',
        DESCRIPTION: 'Configurar permisos para roles de usuario',
        ITEMS: [
          'Asignación de permisos',
          'Gestión de roles',
          'Control de acceso',
          'Plantillas de permisos',
        ],
        PAGE: {
          TITLE: 'Permisos de Roles',
          SUBTITLE: 'Administra los permisos asignados a cada rol del sistema',
          EDITING_FOR_BRANCH: 'Editando permisos para la sucursal: {{branch}}.',
          BRANCH_SCOPE_NOTE:
            'Los cambios aplican solo a esta sucursal; otras sucursales no se ven afectadas.',
          SELECT_ROLE: 'Seleccionar Rol',
          SELECT_ROLE_PLACEHOLDER: 'Elige un rol para gestionar permisos',
          PERMISSIONS_SELECTED: '{{count}} permisos seleccionados',
          UNSAVED_CHANGES: 'Cambios sin guardar',
          NO_PERMISSIONS_AVAILABLE: 'No hay permisos disponibles',
          NO_ROLE_SELECTED: 'Ningún Rol Seleccionado',
          NO_ROLE_SELECTED_MESSAGE:
            'Selecciona un rol del menú desplegable para gestionar sus permisos',
          PERMISSIONS: 'Permisos',
          ADD_PERMISSION: 'Agregar Permiso',
          ADD_ROLE: 'Crear Rol',
          EDIT_ROLE: 'Editar Rol',
          DELETE_ROLE: 'Eliminar Rol',
          EDIT_PERMISSION: 'Editar Permiso',
          DELETE_PERMISSION_TITLE: 'Eliminar Permiso',
          DELETE_PERMISSION_DESCRIPTION:
            'Estás a punto de eliminar el permiso "{{name}}". Esta acción no se puede deshacer.',
          ADD_PERMISSION_DESCRIPTION:
            'Completa el formulario para crear un nuevo permiso en el sistema.',
          EDIT_PERMISSION_DESCRIPTION:
            'Modifica los detalles del permiso seleccionado.',
          PERMISSION_CODE: 'Código del Permiso',
          PERMISSION_NAME: 'Nombre del Permiso',
          DESCRIPTION: 'Descripción',
          CATEGORY: 'Categoría',
          MODULE: 'Módulo',
          SELECT_CATEGORY: 'Seleccionar categoría',
          SORT_ORDER: 'Orden',
          IS_SYSTEM: 'Permiso del Sistema',
          CODE_PATTERN_ERROR:
            'El código debe contener solo letras minúsculas, números, puntos y guiones bajos',
          CODE_HELP_TEXT:
            'Formato: module.action (ejemplo: users.create, inventory.view)',
          NAME_PLACEHOLDER: 'Ej: Crear Usuarios',
          DESCRIPTION_PLACEHOLDER: 'Breve descripción del permiso y su alcance',
          CREATE_ROLE_TITLE: 'Crear Nuevo Rol',
          CREATE_ROLE_DESCRIPTION: 'Crea un nuevo rol y asigna permisos a él',
          EDIT_ROLE_TITLE: 'Editar Rol',
          EDIT_ROLE_DESCRIPTION:
            'Modifica el nombre y la información de visualización del rol',
          DELETE_ROLE_TITLE: 'Eliminar Rol',
          DELETE_ROLE_CONFIRM_MESSAGE:
            '¿Estás seguro de que deseas eliminar el rol "{{roleName}}"? Esta acción no se puede deshacer.',
          DELETE_ROLE_CASCADE_INFO:
            'Los permisos asignados a este rol se eliminarán automáticamente.',
          DELETE_ROLE_ERROR_USERS_ASSIGNED:
            'No se puede eliminar el rol. {{count}} usuario(s) están asignados a este rol: {{usernames}}. Por favor, reasigna estos usuarios a otro rol primero.',
          DELETE_ROLE_WARNING_TITLE: 'No se puede eliminar el rol',
          DELETE_ROLE_WARNING_MESSAGE:
            'El rol "{{roleName}}" tiene {{count}} usuario(s) asignado(s). Debes reasignar estos usuarios a otro rol antes de poder eliminarlo.',
          USERS_WITH_ROLE: '{{count}} usuario(s) con este rol:',
          REASSIGN_USERS_FIRST:
            'Por favor, reasigna estos usuarios a otro rol antes de continuar.',
          CHECKING_USERS: 'Verificando usuarios asignados...',
          ROLE_NAME_LABEL: 'Nombre del Rol (Interno)',
          ROLE_NAME_PLACEHOLDER: 'ej: gerente_almacen',
          ROLE_NAME_DESCRIPTION: 'Usa solo letras minúsculas y guiones bajos',
          ROLE_NAME_PATTERN_ERROR:
            'El nombre del rol debe contener solo letras minúsculas y guiones bajos',
          DISPLAY_NAME_LABEL: 'Nombre para Mostrar',
          DISPLAY_NAME_PLACEHOLDER: 'ej: Gerente de Almacén',
          DISPLAY_NAME_DESCRIPTION: 'El nombre que se muestra a los usuarios',
          SEARCH_PERMISSIONS_PLACEHOLDER:
            'Buscar módulo, permiso o descripción…',
          SEARCH_PERMISSIONS_EMPTY: 'Ningún permiso coincide con la búsqueda.',
        },
      },
      BRANCHES: {
        TITLE: 'Sucursales',
        DESCRIPTION: 'Gestión de sucursales del negocio',
        ITEMS: [
          'Crear y editar sucursales',
          'Asignar almacenes',
          'Configurar horarios',
          'Información de contacto',
        ],
      },
      APPROVALS: {
        TITLE: 'Aprobaciones',
        DESCRIPTION: 'Gestión de solicitudes de aprobación',
        ITEMS: [
          'Solicitudes pendientes',
          'Historial de aprobaciones',
          'Descuentos y créditos',
          'Notificaciones',
        ],
      },
      SYNCING: {
        TITLE: 'Sincronización',
        DESCRIPTION:
          'Reconciliar registros con conflictos de sincronización offline',
        ITEMS: [
          'Conflictos por cliente',
          'Conflictos por proveedor',
          'Estrategias: conservar local, servidor o duplicar',
          'Estado de sincronización',
        ],
      },
      ECF_BATCH: {
        TITLE: 'ECF por Lotes',
        DESCRIPTION: 'Procesar facturas electrónicas pendientes en lote',
        ITEMS: [
          'Estado de facturas pendientes',
          'Procesamiento masivo',
          'Reintentar facturas fallidas',
          'Detalle de errores DGII',
        ],
      },
      ECF_CERTIFICATES: {
        TITLE: 'Certificados Digitales',
        DESCRIPTION:
          'Gestión de certificados P12/PFX para facturación electrónica DGII',
        ITEMS: [
          'Certificados por ambiente',
          'Estado y vigencia',
          'Subir nuevo certificado',
          'Eliminar certificados',
        ],
      },
      NCF: {
        TITLE: 'NCF',
        DESCRIPTION: 'Configuración de Números de Comprobante Fiscal',
        ITEMS: [
          'Secuencias de NCF',
          'Tipos de comprobantes',
          'Rangos autorizados',
          'Configuración DGII',
        ],
        PAGE: {
          TITLE: 'Secuencias NCF Activas',
          LOADING: 'Cargando secuencias NCF...',
          CREATE_BUTTON: 'Nueva Secuencia',
          EMPTY_STATE: {
            TITLE: 'No hay secuencias NCF',
            DESCRIPTION: 'Comienza creando tu primera secuencia de NCF',
            BUTTON: 'Crear Primera Secuencia',
          },
          STATUS: {
            AVAILABLE: 'Disponible',
            USED: 'Usado',
            EXPIRED: 'Expirado',
            CANCELLED: 'Cancelado',
          },
          TYPES: {
            FISCAL_CREDIT: 'Crédito Fiscal',
            CONSUMER: 'Consumidor Final',
            SPECIAL: 'Régimen Especial',
            UNIQUE_INCOME: 'Ingresos Únicos',
            MINOR_EXPENSES: 'Gastos Menores',
            SPECIAL_REGIME: 'Régimen Especial',
            GOVERNMENTAL: 'Gubernamental',
          },
          LABELS: {
            SERIE: 'Serie',
            PREFIX: 'Prefijo',
            SEQUENCE: 'Secuencia',
            EXPIRES: 'Expira',
            AUTHORIZATION: 'Auth',
            USED_ON: 'Usado el',
            INVOICE: 'en factura',
          },
        },
        MODAL: {
          TITLE: 'Nueva Secuencia NCF',
          EDIT_TITLE: 'Editar Secuencia NCF',
          EXAMPLE: 'Ejemplo:',
          FIELDS: {
            NCF_TYPE: 'Tipo de NCF',
            NCF_TYPE_PLACEHOLDER: 'Selecciona el tipo de NCF',
            NCF_INFO: 'Información del Comprobante',
            NCF_SELECT_DESCRIPTION:
              'Selecciona un tipo de NCF para ver su descripción',
            SERIE_AUTO_DESCRIPTION:
              'Se determina automáticamente según el tipo de NCF',
            NCF_PREVIEW_TITLE: 'Vista Previa del NCF',
            NCF_PREVIEW_DESCRIPTION: 'Así se verá el número generado:',
            NCF_PREVIEW_CURRENT: 'Secuencia actual',
            NCF_PREVIEW_FINAL: 'Última secuencia',
            PREFIX: 'Prefijo',
            PREFIX_PLACEHOLDER: 'Ej: 01',
            SEQUENCE: 'Número de Secuencia Inicial',
            SEQUENCE_PLACEHOLDER: '1',
            FINAL_SEQUENCE: 'Número de Secuencia Final',
            FINAL_SEQUENCE_PLACEHOLDER: '100',
            ACTUAL_SEQUENCE: 'Secuencia Actual',
            ACTUAL_SEQUENCE_DESCRIPTION:
              'Opcional. Si no se especifica, se usará el valor de la secuencia inicial.',
            ACTUAL_SEQUENCE_PLACEHOLDER: '1',
            IS_ELECTRONIC: 'Es Electrónico',
            SERIE: 'Serie',
            SERIE_PLACEHOLDER: 'B',
            EXPIRATION_DATE: 'Fecha de Expiración',
            REGISTRY_DATE: 'Fecha de Registro',
            BRANCH_SCOPE: 'Sucursal (opcional)',
            BRANCH_SCOPE_ALL: 'Todas las sucursales (rango general)',
            BRANCH_SCOPE_HINT:
              'Deja "Todas" para un rango general compartido por todas las sucursales, o asigna un rango exclusivo a una sucursal. Los rangos por sucursal no pueden solaparse.',
            NOTES: 'Notas (Opcional)',
            NOTES_PLACEHOLDER:
              'Información adicional sobre esta secuencia NCF...',
          },
          VALIDATION: {
            PREFIX_MIN: 'El prefijo debe tener 3 caracteres',
            PREFIX_MAX: 'El prefijo debe tener 3 caracteres',
            PREFIX_REQUIRED: 'El prefijo es requerido',
            SEQUENCE_MIN: 'La secuencia debe ser mayor a 0',
            FINAL_SEQUENCE_MIN: 'La secuencia final debe ser mayor a 0',
            FINAL_SEQUENCE_GREATER:
              'La secuencia final debe ser mayor que la inicial',
            ACTUAL_SEQUENCE_MIN: 'La secuencia actual debe ser mayor a 0',
            ACTUAL_SEQUENCE_RANGE:
              'La secuencia actual debe estar entre la secuencia inicial y final',
            SERIE_LENGTH: 'La serie debe ser exactamente 1 carácter',
            SERIE_FORMAT: 'La serie debe ser una letra mayúscula',
            ELECTRONIC_MISMATCH:
              'El tipo de comprobante no coincide con la configuración electrónica',
            SERIE_NCF_MISMATCH:
              'La serie debe coincidir con el tipo de NCF (B para físico, E para electrónico)',
            EXPIRATION_REQUIRED: 'La fecha de expiración es requerida',
            REGISTRY_DATE_REQUIRED: 'La fecha de registro es requerida',
            ALL_FIELDS_REQUIRED:
              'Todos los campos son requeridos cuando NCF está habilitado',
          },
          TYPE_OPTIONS: {
            B01: 'B01 - Factura de Crédito Fiscal',
            B02: 'B02 - Factura de Consumo',
            B03: 'B03 - Nota de Débito',
            B04: 'B04 - Nota de Crédito',
            B11: 'B11 - Comprobante de Compras',
            B12: 'B12 - Registro Único de Ingresos',
            B14: 'B14 - Comprobante para Regímenes Especiales',
            B15: 'B15 - Comprobante Gubernamental',
            B16: 'B16 - Comprobante para Exportaciones',
            E31: 'E31 - Factura de Crédito Fiscal Electrónica',
            E32: 'E32 - Factura de Consumo Electrónica',
            E33: 'E33 - Nota de Débito Electrónica',
            E34: 'E34 - Nota de Crédito Electrónica',
            E41: 'E41 - Comprobante de Compras Electrónico',
            E42: 'E42 - Registro Único de Ingresos Electrónico',
            E43: 'E43 - Comprobante para Gastos Menores Electrónico',
            E44: 'E44 - Comprobante de Regímenes Especiales Electrónico',
            E45: 'E45 - Comprobante Gubernamental Electrónico',
            E46: 'E46 - Comprobante para Exportaciones Electrónico',
            E47: 'E47 - Comprobante para Pagos al Exterior Electrónico',
          },
          TYPE_DESCRIPTIONS: {
            B01: 'Factura de Crédito Fiscal - Para registrar transacciones de compra y venta de bienes y/o servicios entre contribuyentes.',
            B02: 'Factura de Consumo - Para transferencia de bienes, entrega en uso o prestación de servicios a consumidores finales.',
            B03: 'Nota de Débito - Para ajustes que aumentan el valor a pagar por la factura (intereses, fletes, etc.).',
            B04: 'Nota de Crédito - Para ajustes que disminuyen el valor a pagar (devoluciones, descuentos, errores).',
            B11: 'Comprobante de Compras - Para compras de bienes o servicios de personas no registradas como contribuyentes.',
            B12: 'Registro Único de Ingresos - Resumen diario de transacciones a consumidores finales (productos exentos de ITBIS).',
            B14: 'Comprobante para Regímenes Especiales - Para facturar ventas exentas del ITBIS o ISC a personas acogidas a regímenes especiales de tributación.',
            B15: 'Comprobante Gubernamental - Para facturar ventas al Gobierno Central, instituciones descentralizadas, autónomas y de seguridad social.',
            B16: 'Comprobante para Exportaciones - Para reportar ventas de bienes fuera del territorio nacional.',
            E31: 'Factura de Crédito Fiscal Electrónica - Versión electrónica con firma digital para transacciones entre contribuyentes.',
            E32: 'Factura de Consumo Electrónica - Versión electrónica para consumidores finales.',
            E33: 'Nota de Débito Electrónica - Versión electrónica para ajustes que aumentan el valor.',
            E34: 'Nota de Crédito Electrónica - Versión electrónica para ajustes que disminuyen el valor.',
            E41: 'Comprobante de Compras Electrónico - Versión electrónica para proveedores no contribuyentes.',
            E42: 'Registro Único de Ingresos Electrónico - Versión electrónica del resumen diario.',
            E43: 'Comprobante para Gastos Menores Electrónico - Versión electrónica para gastos menores.',
            E44: 'Comprobante de Regímenes Especiales Electrónico - Versión electrónica para regímenes especiales.',
            E45: 'Comprobante Gubernamental Electrónico - Versión electrónica para gobierno.',
            E46: 'Comprobante para Exportaciones Electrónico - Versión electrónica para exportaciones.',
            E47: 'Comprobante para Pagos al Exterior Electrónico - Versión electrónica para pagos internacionales.',
          },
        },
        ACTIONS: {
          EDIT: 'Editar',
          DUPLICATE: 'Duplicar',
          DELETE: 'Eliminar',
          SPLIT: 'Dividir por sucursal',
          VIEW_SPLIT: 'Ver división por sucursal',
        },
        SPLIT: {
          TITLE: 'Dividir rango por sucursal',
          DESCRIPTION:
            'Divide el rango {{serie}}{{prefix}} ({{start}}–{{end}}) en tramos por sucursal. Los tramos deben cubrir todo el rango sin huecos ni solapamientos.',
          FROM: 'Desde',
          TO: 'Hasta',
          QTY: 'Cantidad',
          QTY_HINT:
            'Escribe cuántos NCF asignar; el resto se reparte entre las sucursales de abajo.',
          ADD_SLICE: 'Agregar tramo',
          REMOVE: 'Quitar tramo',
          SUBMIT: 'Dividir',
          TRUNCATED: 'Recortado al fin del rango',
          NO_DATA: '—',
          NO_TAIL:
            'Este rango no tiene suficientes números sin emitir para dividir en dos o más tramos.',
          ALREADY_DIVIDED:
            'Este rango ya está dividido entre las sucursales. Mira la división actual abajo o usa “Ver división por sucursal”.',
          CURRENT_DIVISION: 'División actual por sucursal',
          UNASSIGNED_BANNER:
            'Estas sucursales quedaron sin asignación: {{branches}}. Quítalas o reduce las cantidades de arriba.',
          VALIDATION: {
            MIN_TWO: 'Define al menos dos tramos.',
            DISTINCT_BRANCH:
              'Cada tramo debe asignarse a una sucursal distinta.',
            BAD_RANGE:
              'El fin de cada tramo debe ser mayor o igual a su inicio.',
            WITHIN_SPAN:
              'Los tramos deben ser contiguos y quedar dentro del rango.',
          },
        },
        SPLIT_OVERVIEW: {
          TITLE: 'División por sucursal',
          DESCRIPTION:
            'Distribución del rango {{serie}}{{prefix}} entre sucursales y su consumo.',
          BRANCH: 'Sucursal',
          RANGE: 'Rango',
          USAGE: 'Consumo',
          ISSUED_ORIGIN: 'Emitidos (origen)',
          ALL_BRANCHES: 'Todas las sucursales',
          RESPLIT: 'Redividir cola',
          REASSIGN: 'Reasignar sucursal',
          FULLY_ISSUED: 'Agotado',
          EMPTY: 'Este tipo no tiene tramos por sucursal.',
        },
        LABELS: {
          SEQUENCE_PROGRESS: 'Progreso de Secuencia',
          CURRENT_NCF: 'NCF Actual',
          EXPIRES: 'Vence',
          REGISTERED: 'Registrado',
          NOTES: 'Notas',
          USAGE_INFORMATION: 'Información de Uso',
        },
        TOOLTIPS: {
          CURRENT_NCF: 'Próximo número de NCF a ser asignado',
          EXPIRES: 'Fecha de vencimiento de la secuencia NCF',
          REGISTERED: 'Fecha en que se registró la secuencia',
        },
        ERROR: {
          LOADING_TITLE: 'Error cargando secuencias NCF',
          LOADING_DESCRIPTION:
            'Hubo un error al cargar las secuencias NCF. Por favor, inténtalo de nuevo más tarde.',
        },
        DELETE: {
          CONFIRM_TITLE: 'Confirmar Eliminación',
          CONFIRM_DESCRIPTION:
            '¿Estás seguro de que deseas eliminar la secuencia NCF',
          CONFIRM_WARNING: '? Esta acción no se puede deshacer.',
          USAGE_WARNING:
            'Atención: Esta secuencia ya tiene NCF utilizados. Se recomienda no eliminar secuencias con uso activo.',
          CANCEL: 'Cancelar',
          DELETE_BUTTON: 'Eliminar',
          DELETING: 'Eliminando...',
          BULK_CONFIRM_TITLE: 'Confirmar eliminación múltiple',
          BULK_CONFIRM_DESCRIPTION:
            '¿Estás seguro de que deseas eliminar {{count}} secuencia(s) NCF seleccionada(s)? Esta acción no se puede deshacer.',
          SELECTED_COUNT: '{{count}} seleccionada(s)',
          CLEAR_SELECTION: 'Cancelar selección',
          SELECT_ROW: 'Seleccionar secuencia',
          SELECT_ALL: 'Seleccionar todo',
          BULK_SKIPPED_TITLE: 'Algunas secuencias no se eliminaron',
          BULK_SKIPPED_DESCRIPTION:
            '{{count}} secuencia(s) no se pudieron eliminar porque ya tienen NCF utilizados.',
        },
        DEBUG: {
          CREATE_MUTATION_STATUS: 'Estado de Creación:',
          LOADING: 'Cargando...',
          ERROR: 'Error:',
        },
        TEXT: {
          USED_PERCENTAGE: '% usado',
          REMAINING: 'restantes',
          USED_ON: 'Usado el',
          FOR_INVOICE: 'para Factura',
        },
        IMPORT: {
          BUTTON: 'Importar de DGII',
          TITLE: 'Importar Comprobantes DGII',
          DESCRIPTION:
            'Sube el archivo ComprobantesSolicitud que descargaste de la Oficina Virtual de la DGII. Aceptamos los tres formatos que emite DGII (CSV, XML y XLS).',
          UPLOAD_HINT: 'Arrastra el archivo aquí o haz clic para seleccionarlo',
          ACCEPTED_FORMATS: 'Formatos: CSV · XML · XLS',
          UPLOAD_ERROR:
            'No pudimos leer el archivo. Verifica que sea el ComprobantesSolicitud original de DGII.',
          PREVIEW_TITLE: 'Revisar filas antes de importar',
          COL_TYPE: 'Tipo',
          COL_PREFIX: 'Prefijo',
          COL_RANGE: 'Rango',
          COL_QTY: 'Cantidad',
          COL_VIGENCIA: 'Vigencia',
          COL_AUTH: 'Autorización',
          COL_STATUS: 'Estado',
          COL_ACTION: 'Acción',
          STATUS_NEW: 'Nuevo',
          STATUS_DUPLICATE: 'Duplicado',
          STATUS_COVERED: 'Ya cubierto',
          STATUS_EXTENDS: 'Extiende existente',
          STATUS_CONFLICT: 'Conflicto',
          STATUS_EXPIRED: 'Vencido',
          EXPLAIN_DUPLICATE:
            'Ya importaste esta autorización DGII ({{auth}}). Se omite para evitar duplicados.',
          EXPLAIN_COVERED:
            'Este rango ya está cubierto por la secuencia existente {{range}}. No hay nada nuevo que crear.',
          EXPLAIN_EXTENDS:
            'La secuencia existente {{range}} continúa con este rango. Elige "Extender" para agregar los nuevos números o "Omitir" para dejarla como está.',
          EXPLAIN_CONFLICT:
            'Este rango solapa la secuencia existente {{range}}. Revisa qué rango es correcto antes de importar; puedes extender la existente o omitir esta fila.',
          EXPLAIN_EXPIRED:
            'La vigencia venció el {{date}}. DGII no aceptará estos números; contacta a DGII para renovar antes de usarlos.',
          ACTION_SKIP: 'Omitir',
          ACTION_CREATE: 'Crear',
          ACTION_EXTEND: 'Extender',
          BUSINESS_WIDE_HINT:
            'Las secuencias NCF son a nivel de negocio (compartidas por todas las sucursales).',
          COMMIT_BUTTON: 'Importar',
          COMMIT_PENDING: 'Importando…',
          COMPLETE_TITLE: 'Importación completa',
          SUMMARY_CREATED: 'Creados: {{count}}',
          SUMMARY_SKIPPED: 'Omitidos: {{count}}',
          SUMMARY_ERRORS: 'Errores: {{count}}',
          CLOSE_BUTTON: 'Cerrar',
          BACK_BUTTON: 'Volver',
        },
      },
    },
  },
  POS_TAB: {
    TITLE: 'Configuración del Punto de Venta',
    SUBTITLE:
      'Personaliza el comportamiento del sistema POS según tus necesidades.',
    PRINT_RECEIPT_AUTO: 'Imprimir recibo automáticamente',
    PRINT_RECEIPT_AUTO_DESCRIPTION:
      'Imprime el recibo automáticamente después de cada venta.',
    MAX_DISCOUNT_PERCENT: 'Descuento máximo (%)',
    ALLOW_REFUNDS: 'Permitir reembolsos',
    ALLOW_REFUNDS_DESCRIPTION:
      'Permite reembolsar una factura luego de su pago.',
    TAX_ID_LABEL: 'Tasa de ITBIS (%)',
    ENABLE_DISCOUNTS: 'Permitir descuentos',
    ENABLE_DISCOUNTS_DESCRIPTION: 'Permite aplicar descuentos a las facturas.',
    ENABLE_TAXES: 'Permitir cobro de impuestos',
    ENABLE_TAXES_DESCRIPTION: 'Permite aplicar impuestos a las facturas.',
    INVOICE_DESIGN: 'Diseño de Factura',
    INVOICE_DESIGN_SUBTITLE:
      'Personaliza el diseño y contenido de las facturas impresas',
    CONFIGURE_DESIGN: 'Configurar Diseño',
  },
  SYSTEM_TAB: {
    TITLE: 'Configuración del Sistema',
    SUBTITLE:
      'Personaliza el comportamiento del sistema según tus necesidades.',
  },
  SYSTEM_CONFIG: {
    LOADING: 'Cargando configuración del sistema...',
    GENERAL_SETTINGS: 'Configuración General',
    SECURITY_SETTINGS: 'Configuración de Seguridad',
    BACKUP_SETTINGS: 'Configuración de Respaldos',
    PERFORMANCE_SETTINGS: 'Configuración de Rendimiento',
    MAINTENANCE_SETTINGS: 'Configuración de Mantenimiento',
    MAINTENANCE_MODE: 'Modo de Mantenimiento',
    API_CONFIGURATION: 'Configuración de API',
    GENERAL_DESCRIPTION: 'Configuración básica del sistema',
    SECURITY_DESCRIPTION: 'Políticas de seguridad y autenticación',
    BACKUP_DESCRIPTION: 'Configuración de copias de seguridad',
    PERFORMANCE_DESCRIPTION: 'Optimización y cache del sistema',
    MAINTENANCE_DESCRIPTION: 'Deshabilitar el acceso al sistema temporalmente',
    API_DESCRIPTION: 'Control de acceso y límites de la API',
    CACHING_DESCRIPTION: 'Mejora el rendimiento almacenando datos en memoria',
    TWO_FACTOR_DESCRIPTION: 'Requerir verificación adicional al iniciar sesión',
    MAINTENANCE_MESSAGE_LABEL: 'Mensaje de Mantenimiento',
    MAINTENANCE_MESSAGE_PLACEHOLDER:
      'El sistema está en mantenimiento. Volveremos pronto.',
    LOADING_CONFIG: 'Cargando configuración del sistema...',
    FIELDS: {
      SYSTEM_NAME: 'Nombre del Sistema',
      SYSTEM_URL: 'URL del Sistema',
      SYSTEM_URL_PLACEHOLDER: 'https://miempresa.com',
      TIMEZONE: 'Zona Horaria',
      LANGUAGE: 'Idioma',
      CURRENCY: 'Moneda',
      DATE_FORMAT: 'Formato de Fecha',
      TIME_FORMAT: 'Formato de Hora',
      ENABLE_TWO_FACTOR: 'Habilitar autenticación de dos factores',
      PASSWORD_MIN_LENGTH: 'Longitud mínima de contraseña',
      REQUIRE_UPPERCASE: 'Requerir mayúsculas en contraseñas',
      REQUIRE_NUMBERS: 'Requerir números en contraseñas',
      REQUIRE_SPECIAL_CHARS: 'Requerir caracteres especiales en contraseñas',
      SESSION_TIMEOUT: 'Tiempo de sesión (minutos)',
      MAX_LOGIN_ATTEMPTS: 'Intentos máximos de inicio',
      LOCKOUT_DURATION: 'Duración del bloqueo (minutos)',
      ENABLE_AUTOMATIC_BACKUPS: 'Habilitar respaldos automáticos',
      BACKUP_FREQUENCY: 'Frecuencia',
      BACKUP_TIME: 'Hora del Respaldo',
      BACKUP_RETENTION: 'Retención (días)',
      BACKUP_LOCATION: 'Ubicación del Respaldo',
      ENABLE_CACHING: 'Habilitar caché del sistema',
      CACHE_EXPIRATION: 'Expiración del caché (minutos)',
      ENABLE_COMPRESSION: 'Habilitar compresión de datos',
      ENABLE_CDN: 'Habilitar CDN',
      CDN_URL: 'URL del CDN',
      MAINTENANCE_MODE: 'Modo de mantenimiento',
      MAINTENANCE_MESSAGE: 'Mensaje de mantenimiento',
      ENABLE_DEBUG: 'Habilitar modo de depuración',
      ENABLE_API_ACCESS: 'Habilitar acceso a API',
      API_RATE_LIMIT: 'Límite de velocidad de API (por minuto)',
      COMPRESSION: 'Compresión de Datos',
      COMPRESSION_DESCRIPTION: 'Reduce el tamaño de las respuestas HTTP',
      USE_CDN: 'Usar CDN',
      USE_CDN_DESCRIPTION: 'Distribuir contenido estático mediante CDN',
      CDN_URL_LABEL: 'URL del CDN',
      CDN_URL_PLACEHOLDER: 'https://cdn.miempresa.com',
      API_LOGGING: 'Registro de API',
      API_LOGGING_DESCRIPTION: 'Guardar logs de todas las solicitudes de API',
      API_RATE_LIMIT_LABEL: 'Límite de solicitudes (por minuto)',
    },
    OPTIONS: {
      LANGUAGE: {
        ES: 'Español',
        EN: 'Inglés',
      },
      CURRENCY: {
        DOP: 'Peso Dominicano (DOP)',
        USD: 'Dólar Americano (USD)',
        EUR: 'Euro (EUR)',
      },
      DATE_FORMAT: {
        DD_MM_YYYY: 'DD/MM/YYYY',
        MM_DD_YYYY: 'MM/DD/YYYY',
        YYYY_MM_DD: 'YYYY-MM-DD',
      },
      TIME_FORMAT: {
        '12h': '12 horas (AM/PM)',
        '24h': '24 horas',
      },
      BACKUP_FREQUENCY: {
        DAILY: 'Diario',
        WEEKLY: 'Semanal',
        MONTHLY: 'Mensual',
      },
    },
  },
  CASHIER_SHIFT: {
    TITLE: 'Turno de Caja',
    SUBTITLE: 'Gestiona tu turno de cajero y realiza el cierre de caja',
    REPRINT_LAST_CUADRE: 'Imprimir mi último cuadre',
    NO_ACTIVE_SHIFT: 'No hay turno activo',
    NO_ACTIVE_SHIFT_DESCRIPTION:
      'Debes abrir un turno para comenzar a registrar transacciones',
    OPEN_SHIFT: 'Abrir Turno',
    CURRENT_SHIFT: 'Turno Actual',
    ACTIVE: 'Activo',
    CASHIER: 'Cajero',
    START: 'Inicio',
    DURATION: 'Duración',
    CLOSE_SHIFT: 'Cerrar Turno de Caja',
    INITIAL_BALANCE: 'Balance Inicial',
    CURRENT_BALANCE: 'Balance Actual',
    EXPECTED_BALANCE: 'Balance Esperado',
    TRANSACTION_SUMMARY: 'Resumen de Transacciones',
    SALES: 'Ventas',
    REFUNDS: 'Devoluciones',
    EXPENSES: 'Gastos',
    DEPOSITS: 'Depósitos',
    TRANSACTIONS: 'transacciones',
    RECENT_TRANSACTIONS: 'Transacciones Recientes',
    NO_DESCRIPTION: 'Sin descripción',
    RECEIPT: 'Recibo',
    CLOSE_SHIFT_MODAL_TITLE: 'Cerrar Turno de Caja',
    CLOSE_SHIFT_MODAL_SUBTITLE:
      'Revisa la información y confirma el cierre del turno de caja',
    SHIFT_START: 'Inicio del turno de caja',
    CASH_COUNT_LABEL: 'Conteo de caja (efectivo real)',
    CASH_COUNT_PLACEHOLDER: 'Ingrese el monto contado',
    BALANCED: 'Caja cuadrada',
    BALANCED_DESCRIPTION:
      'El efectivo contado coincide con el balance esperado',
    DIFFERENCE: 'Diferencia en caja',
    DIFFERENCE_DESCRIPTION: 'Diferencia',
    SURPLUS: 'sobrante',
    SHORTAGE: 'faltante',
    CANCEL: 'Cancelar',
    CONFIRM_CLOSE: 'Confirmar Cierre',
    PAYMENTS_RECEIVED: 'Pagos Recibidos',
    NO_TRANSACTIONS_YET: 'No hay transacciones registradas en este turno',
    PAYMENT_FOR_INVOICE: 'Pago de factura',
  },
  BUSINESS: {
    TITLE: 'Información de la Empresa',
    DESCRIPTION: 'Configura la información básica de tu empresa',
    COMPANY: {
      EDIT: 'Editar',
      SAVE: 'Guardar',
      CANCEL: 'Cancelar',
      NAME_LABEL: 'Nombre de la Empresa',
      RNC_LABEL: 'RNC/Cédula',
      PHONE_LABEL: 'Teléfono',
      EMAIL_LABEL: 'Email',
      WEBSITE_LABEL: 'Sitio Web',
      CURRENCY_LABEL: 'Moneda',
      ADDRESS_LABEL: 'Dirección',
    },
    FORM: {
      SECTION_LOGO: 'Logo de la Empresa',
      SECTION_GENERAL: 'Información General',
      SECTION_CONTACT: 'Información de Contacto',
      SECTION_ADDRESS: 'Información de Dirección',
      LOGO_DESCRIPTION:
        'Este logo se usará en las facturas, recibos y como ícono del negocio',
      NAME_LABEL: 'Nombre de la Empresa',
      LEGAL_NAME_LABEL: 'Nombre Legal',
      TAX_ID_LABEL: 'RNC/Cédula',
      BUSINESS_TYPE_LABEL: 'Tipo de Negocio',
      WEBSITE_LABEL: 'Sitio Web',
      PLAN_LABEL: 'Plan',
      PHONE_LABEL: 'Teléfono',
      EMAIL_LABEL: 'Email',
      ADDRESS_LINE1_LABEL: 'Dirección Línea 1',
      ADDRESS_LINE2_LABEL: 'Dirección Línea 2 (Opcional)',
      CITY_LABEL: 'Ciudad',
      STATE_LABEL: 'Estado/Provincia',
      COUNTRY_LABEL: 'País',
      CURRENCY_LABEL: 'Moneda',
      CURRENCY_HELPER:
        'Código ISO de 3 letras de la moneda (ej: USD, EUR, DOP) que se mostrará en facturas, estados de cuenta y reportes',
      TIME_ZONE_LABEL: 'Zona horaria',
      TIME_FORMAT_LABEL: 'Formato de hora',
      BUSINESS_TYPE_RESTAURANT: 'Restaurante',
      BUSINESS_TYPE_RETAIL: 'Retail',
      BUSINESS_TYPE_SERVICES: 'Servicios',
      BUSINESS_TYPE_OTHER: 'Otro',
      VALIDATIONS: {
        INVALID_URL: 'URL inválida',
        INVALID_EMAIL: 'Correo electrónico inválido',
      },
      ERROR_NO_BUSINESS_ID: 'No se pudo identificar el negocio',
      ERROR_UPDATING_BUSINESS: 'No se pudo actualizar el negocio',
      TAX_ID_VALIDATING: 'Validando RNC con DGII…',
      TAX_ID_INVALID_TITLE: 'RNC no encontrado en DGII',
      TAX_ID_INVALID_DESCRIPTION:
        'No pudimos validar este RNC contra el registro de DGII. Verifica que esté escrito correctamente. Si estás seguro de que es válido, puedes usarlo de todos modos.',
      TAX_ID_FIX: 'Corregir',
      TAX_ID_USE_ANYWAY: 'Usar de todos modos',
      TAX_ID_USE_ANYWAY_HINT:
        'Puedes guardar de todos modos si estás seguro de que el RNC es válido.',
      CREDIT_TERMS_CONFIGURE_LINK: 'Configurar términos de factura',
      SECTION_CREDIT_TERMS: 'Términos de Factura',
      SECTION_CREDIT_TERMS_DESCRIPTION:
        'Define los plazos de crédito que tu negocio ofrece y los días de cada uno.',
      CREDIT_TERM_ADD: 'Agregar término',
      CREDIT_TERM_NAME_LABEL: 'Nombre',
      CREDIT_TERM_NAME_PLACEHOLDER: 'Ej: Crédito 45 días',
      CREDIT_TERM_DAYS_LABEL: 'Días',
      CREDIT_TERM_DAYS_HELP:
        'Días que se suman a la fecha de la factura para calcular el vencimiento.',
      CREDIT_TERM_DAYS_FIXED:
        'Este término se cobra de inmediato, no admite días.',
      CREDIT_TERM_ACTIVE: 'Disponible al facturar',
      CREDIT_TERM_NO_TERMS: 'No hay términos configurados',
      CREDIT_TERM_SAVED: 'Término guardado.',
      CREDIT_TERM_DELETED: 'Término eliminado.',
      CREDIT_TERM_DELETE_CONFIRM:
        '¿Eliminar este término? Las facturas ya emitidas conservan el plazo con el que se emitieron.',
      CREDIT_TERM_NAME_REQUIRED: 'Escribe un nombre para el término.',
      CREDIT_TERM_DAYS_REQUIRED: 'Indica cuántos días de crédito otorga.',
      CREDIT_TERM_BUILTIN_HINT:
        'Término estándar. Puedes renombrarlo y ajustar los días para tu negocio.',
      SECTION_BANKS: 'Bancos para Transferencias',
      SECTION_BANKS_DESCRIPTION:
        'Configura los bancos disponibles para pagos por transferencia',
      SECTION_PAYMENT_TYPES: 'Tipos de Pago',
      SECTION_PAYMENT_TYPES_DESCRIPTION:
        'Configura los medios de pago que acepta tu negocio (efectivo, tarjetas, transferencia, etc.)',
      ADD_BANK: 'Agregar Banco',
      BANK_NAME_LABEL: 'Nombre del Banco',
      BANK_NAME_PLACEHOLDER: 'Ej: Banco Popular',
      NO_BANKS: 'No hay bancos configurados',
      DELETE_BANK_CONFIRMATION:
        '¿Estás seguro de que deseas eliminar este banco?',
      EDIT_BANK: 'Editar Banco',
    },
    QUICK_LINKS: {
      LOGO_TITLE: 'Logo y Marca',
      LOGO_DESCRIPTION: 'Configura el logo y la identidad visual de tu empresa',
      TAX_TITLE: 'Información Fiscal',
      TAX_DESCRIPTION: 'Gestiona la información fiscal y tributaria',
      CONTACT_TITLE: 'Contacto',
      CONTACT_DESCRIPTION: 'Administra los datos de contacto y comunicación',
    },
  },
  ECF_BATCH: {
    TITLE: 'Procesamiento de e-CF por Lotes',
    SUBTITLE:
      'Procesa las facturas electrónicas pendientes de envío a la DGII en lote.',
    STATUS_CARD_TITLE: 'Estado de Facturas Electrónicas',
    ECF_DISABLED_BANNER_TITLE: 'Facturación electrónica desactivada',
    ECF_DISABLED_BANNER_DESCRIPTION:
      'El envío automático (cron) está pausado. Las facturas pendientes NO se enviarán a la DGII hasta que reactives el flag "e-CF activo" en la configuración del negocio.',
    ECF_DISABLED_BANNER_CTA: 'Ir a Configuración del negocio',
    PENDING: 'Pendientes',
    PROCESSING: 'En proceso',
    COMPLETED: 'Completadas',
    FAILED: 'Fallidas',
    TOTAL: 'Total',
    START_PROCESSING: 'Iniciar procesamiento',
    PROCESSING_IN_PROGRESS: 'Procesando facturas...',
    NO_PENDING_INVOICES: 'No hay facturas pendientes de procesamiento.',
    RESULTS_TITLE: 'Resultados del procesamiento',
    INVOICE_ID: 'Factura',
    ENCF: 'e-NCF',
    STATUS: 'Estado',
    ERROR: 'Error',
    ACTIONS: 'Acciones',
    RETRY: 'Reintentar',
    POLL_STATUS: 'Consultar estado',
    POLL_STATUS_SUCCESS: 'Estado actualizado',
    POLL_STATUS_ERROR: 'No se pudo consultar el estado en DGII',
    PROCESS_ONE: 'Procesar',
    PROCESS_ONE_SUCCESS: 'e-CF procesado',
    PROCESS_ONE_ERROR: 'Error al procesar el e-CF',
    RETRY_DELIVERIES_TITLE: 'Reintentar entregas al receptor',
    RETRY_DELIVERIES_DESCRIPTION:
      'Reenvía los e-CF aceptados cuya entrega al receptor falló previamente (hasta 3 intentos).',
    RETRY_DELIVERIES_ACTION: 'Reintentar entregas',
    RETRY_DELIVERIES_SUCCESS: 'Reintentos completados',
    RETRY_DELIVERIES_ERROR: 'Error al reintentar las entregas',
    DELIVERIES_ATTEMPTED: 'intentos',
    DELIVERIES_DELIVERED: 'entregados',
    DELIVERIES_ERRORS: 'errores',
    SUCCESS_TOAST: 'Procesamiento completado',
    ERROR_TOAST: 'Error al procesar el lote',
    RETRY_SUCCESS: 'Factura reenviada exitosamente',
    RETRY_ERROR: 'Error al reenviar la factura',
    RETRY_ALL_FAILED: 'Reintentar todos los fallidos',
    RETRY_ALL_FAILED_CONFIRM_TITLE: 'Reintentar todos los e-CF fallidos',
    RETRY_ALL_FAILED_CONFIRM_DESCRIPTION:
      'Se marcarán hasta {{count}} comprobante(s) fallido(s) para reintento. El sistema los reenviará a la DGII automáticamente. Verifica en el portal de la DGII que estos comprobantes no fueron aceptados antes de continuar, para evitar duplicidad fiscal.',
    RETRY_ALL_FAILED_SUCCESS: 'Reintento programado',
    RETRY_ALL_FAILED_SUCCESS_DETAIL:
      '{{count}} comprobante(s) marcado(s) para reintento.',
    RETRY_ALL_FAILED_ERROR: 'Error al reintentar los comprobantes fallidos',
    STATUS_ACCEPTED: 'Aceptado',
    STATUS_REJECTED: 'Rechazado',
    STATUS_IN_PROCESS: 'En proceso',
    STATUS_ACCEPTED_CONDITIONAL: 'Aceptado condicional',
    STATUS_ERROR: 'Error',
    STATUS_PENDING: 'Pendiente',
    INVOICES_PROCESSED: 'Facturas procesadas',
    INVOICES_SUCCEEDED: 'Exitosas',
    INVOICES_FAILED: 'Fallidas',
    INVOICES_SKIPPED: 'Omitidas',
    EMPTY_RESULTS: 'No hay resultados para mostrar.',
    REFRESH_STATUS: 'Actualizar estado',
    PENDING_INVOICES_TITLE: 'Facturas pendientes de envío',
    PENDING_INVOICES_SUBTITLE:
      'Facturas firmadas pendientes de envío a la DGII. Puede ver el XML y corregir errores antes de procesar.',
    PENDING_EMPTY_TITLE: 'Sin facturas pendientes de envío',
    PENDING_EMPTY_DESCRIPTION:
      'Todas las facturas electrónicas ya fueron enviadas a la DGII. Cuando emitas una nueva aparecerá aquí para procesar.',
    RETRY_DELIVERIES_EMPTY_TITLE: 'Nada por reintentar por ahora',
    RETRY_DELIVERIES_EMPTY_DESCRIPTION:
      'Solo se reintentan las entregas al receptor que fallaron después de que la DGII aprobó el e-CF. Si no hay entregas fallidas, la acción no hará nada.',
    INVOICE_NUMBER: 'No. Factura',
    CUSTOMER: 'Cliente',
    INVOICE_TYPE: 'Tipo',
    INVOICE_DATE: 'Fecha',
    INVOICE_TOTAL: 'Total',
    RETRY_COUNT: 'Reintentos',
    VIEW_XML: 'Ver XML',
    HIDE_XML: 'Ocultar XML',
    ERROR_EXPAND: 'Ver más',
    ERROR_HIDE: 'Ocultar',
    ERROR_NO_DETAIL: 'Error sin detalle',
    REASSIGN_NCF: 'Reasignar NCF',
    REASSIGN_NCF_SUCCESS: 'NCF reasignado exitosamente',
    REASSIGN_NCF_ERROR: 'Error al reasignar NCF',
    REPAIR_DATE: 'Ajustar fecha',
    REPAIR_DATE_TITLE: 'Ajustar fecha del e-CF',
    REPAIR_DATE_DESCRIPTION:
      'Corrige el error DGII [145] modificando la fecha de la factura dentro del rango válido del NCF. Solo se actualiza la fecha; los ítems e ITBIS no cambian.',
    REPAIR_DATE_CURRENT_INVOICE_DATE: 'Fecha actual',
    REPAIR_DATE_CURRENT_DUE_DATE: 'Vencimiento actual',
    REPAIR_DATE_NEW_DATE: 'Nueva fecha de emisión',
    REPAIR_DATE_PROJECTED_DUE_DATE: 'Nuevo vencimiento (proyectado)',
    REPAIR_DATE_NCF_EXPIRATION: 'Vencimiento del NCF',
    REPAIR_DATE_MIN_CUTOFF: 'Fecha mínima (DGII 30 días)',
    REPAIR_DATE_MAX_CUTOFF: 'Fecha máxima (NCF - 1 día)',
    REPAIR_DATE_PREVIEW: 'Vista previa',
    REPAIR_DATE_APPLY: 'Aplicar y reintentar',
    REPAIR_DATE_SUCCESS:
      'Fecha actualizada. El próximo ciclo del cron reenviará el e-CF con la nueva fecha.',
    REPAIR_DATE_ERROR: 'Error al ajustar la fecha del e-CF',
    REPAIR_DATE_WHATSAPP_HINT:
      'El enlace de WhatsApp y la vista pública mostrarán la fecha actualizada automáticamente al recargar.',
    XML_INVOICE_VIEW: 'Ver factura',
    XML_INVOICE_VIEW_TITLE: 'Factura reconstruida desde XML',
    XML_INVOICE_VIEW_DESCRIPTION:
      'Vista previa generada a partir del XML firmado enviado a la DGII.',
    XML_INVOICE_VIEW_PARSE_ERROR:
      'No se pudo parsear el XML. Verifica que el comprobante haya sido firmado.',
    XML_INVOICE_VIEW_ISSUER: 'Emisor',
    XML_INVOICE_VIEW_BUYER: 'Comprador',
    XML_INVOICE_VIEW_DUE_DATE: 'Fecha límite de pago',
    XML_INVOICE_VIEW_SIGNED_BADGE: 'XML firmado digitalmente',
    REPAIR_BUTTON: 'Reparar',
    REPAIR_PANEL_TITLE: 'Reparar factura',
    REPAIR_PANEL_DESCRIPTION:
      'Cambia los campos que necesites y confirma. La factura se limpia y queda en pending_retry para el próximo ciclo del cron.',
    REPAIR_FIELD_DATE: 'Fecha de emisión',
    REPAIR_FIELD_DATE_ORIGINAL: 'Original (XML):',
    REPAIR_FIELD_TOTALS: 'Reparación de totales',
    REPAIR_FIELD_TOTALS_WILL_ADJUST: 'Ajustará ITBIS1 → {{amount}}',
    REPAIR_FIELD_TOTALS_OK: 'Totales OK — no se ajustarán',
    REPAIR_FIELD_TOTALS_HINT:
      'Snapea invoice_item.tax_rate al canonical del bucket.',
    REPAIR_INVALID_DATE: 'Fecha inválida',
    REPAIR_CONFIRM_BUTTON: 'Confirmar reparación',
    REPAIR_BACK_BUTTON: 'Volver',
    REPAIR_ALERT_TITLE: 'Confirmar reparación de factura',
    REPAIR_ALERT_OK: 'Sí, reparar',
    REPAIR_ALERT_CANCEL: 'No',
    REPAIR_ALERT_DESC_DATE_AND_TOTALS:
      'Se cambiará la fecha a {{date}} y se ajustarán los totales. La factura entrará en pending_retry.',
    REPAIR_ALERT_DESC_DATE_ONLY:
      'Se cambiará la fecha a {{date}}. La factura entrará en pending_retry.',
    REPAIR_ALERT_DESC_TOTALS_ONLY:
      'Se ajustarán los totales al canonical rate. La factura entrará en pending_retry.',
    REPAIR_SUCCESS_TITLE: 'Factura reparada',
    REPAIR_SUCCESS_TOTALS:
      '{{lines}} línea(s) corregida(s). Nuevo total: {{total}}.',
    REPAIR_SUCCESS_DATE:
      'Fecha actualizada a {{date}}. La factura queda en pending_retry.',
    REPAIR_ERROR_TITLE: 'Error al reparar',
    REPAIR_ALERT_INTRO: 'Se aplicarán los siguientes cambios:',
    REPAIR_ALERT_ROW_DATE: 'Fecha de emisión',
    REPAIR_ALERT_ROW_ITBIS1: 'ITBIS1',
    REPAIR_ALERT_OUTRO: 'La factura quedará en pending_retry.',
    REPAIR_ACCEPTED_BANNER_TITLE: 'Factura ya aceptada por DGII',
    REPAIR_ACCEPTED_BANNER_DESCRIPTION:
      'La reparación actualizará solo los registros internos (líneas, impuestos, totales y contabilidad). El e-CF seguirá con el mismo securityCode y ecfStatus. Para corregir el registro fiscal ante DGII emite una nota de crédito.',
    REPAIR_ALERT_OUTRO_INTERNAL_ONLY:
      'DGII ya aceptó la factura: se ajustan solo los registros internos y contables. El e-CF firmado no cambia.',
    REPAIR_SUCCESS_INTERNAL_ONLY:
      '{{lines}} línea(s) corregida(s). Nuevo total: {{total}}. e-CF no se re-firma (DGII ya lo aceptó).',
    NO_XML_AVAILABLE: 'XML no disponible (pendiente de firma)',
    ERROR_DETAILS: 'Error DGII',
    STATUS_SIGNED: 'Firmado',
    STATUS_PENDING_RETRY: 'Pendiente reintento',
    STATUS_FAILED: 'Fallido',
    DETAIL_TITLE_PENDING: 'Facturas Pendientes',
    DETAIL_TITLE_PROCESSING: 'Facturas En Proceso',
    DETAIL_TITLE_COMPLETED: 'Facturas Completadas',
    DETAIL_TITLE_FAILED: 'Facturas Fallidas',
    DETAIL_TITLE_ALL: 'Todas las Facturas',
    FILTER_DATE_FROM: 'Desde',
    FILTER_DATE_TO: 'Hasta',
    FILTER_CLEAR: 'Limpiar filtros',
    FILTER_ECF_TYPE: 'Tipo e-CF',
    FILTER_ECF_TYPE_ALL: 'Todos los tipos',
    NO_INVOICES_FOUND: 'No se encontraron facturas',
    BACK_TO_BATCH: 'Volver a e-CF por Lotes',
  },
  CONFIGURATION_POS: {
    TITLE: 'Configuración del Diseño de Factura',
    SUBTITLE: 'Personaliza todos los aspectos del diseño de tus facturas.',
    SAVE_ALL: 'Guardar Todo',
    TEMPLATE_CONFIGURATION: 'Configuración de Plantilla',
    CUSTOMIZE_INVOICE_REALTIME: 'Personaliza tu factura en tiempo real',
    CHANGES_REFLECT_AUTOMATICALLY: 'Los cambios se reflejan automáticamente',
    LOADING_CONFIGURATION: 'Cargando configuración...',
    HEADER: 'Encabezado',
    PRODUCTS: 'Productos',
    TOTALS: 'Totales',
    FOOTER: 'Pie de Página',
    PRINT: 'Impresión',
    PRINT_SECTION_DESCRIPTION:
      'Ajustes específicos para impresoras: márgenes en milímetros, fuente y etiqueta de borrador.',
    PRINT_MARGINS: 'Márgenes personalizados (mm)',
    PRINT_MARGINS_HELP:
      'Cuando un valor está vacío se usa el preset (estrecho/normal/ancho). Cuando llenas alguno, se aplica milímetro por milímetro y se ignora el preset.',
    PRINT_MARGIN_TOP: 'Superior',
    PRINT_MARGIN_RIGHT: 'Derecho',
    PRINT_MARGIN_BOTTOM: 'Inferior',
    PRINT_MARGIN_LEFT: 'Izquierdo',
    PRINT_MARGIN_RESET: 'Volver al preset',
    PRINT_FONT_FAMILY: 'Tipo de letra',
    PRINT_FONT_FAMILY_HELP:
      'Selecciona la familia de fuentes para todo el recibo. Si la fuente no está instalada en la impresora se usa sans-serif.',
    PRINT_FONT_SIZE: 'Tamaño de letra (px)',
    PRINT_FONT_SIZE_HELP:
      'Tamaño numérico en px (igual que Word: 8, 9, 10, 11, 12, 14…). Vacío usa el preset Pequeño/Mediano/Grande.',
    PRINT_FONT_SIZE_SMALL: 'Pequeño',
    PRINT_FONT_SIZE_MEDIUM: 'Mediano',
    PRINT_FONT_SIZE_LARGE: 'Grande',
    CURRENCY_FORMAT: 'Formato de moneda',
    CURRENCY_SYMBOL_SHORT: 'Símbolo de moneda corto ($)',
    CURRENCY_SYMBOL_SHORT_HELP:
      'Muestra $ en lugar de RD$ / US$ en los montos.',
    PRINT_DEFAULT_PRINTER: 'Impresora predeterminada',
    PRINT_DEFAULT_PRINTER_HELP:
      'Impresora que se usará para esta plantilla cuando se imprima automáticamente. Vacío = usar impresora predeterminada del sistema.',
    LOGO_COMPANY: 'Logo de la Empresa',
    SHOW_LOGO: 'Mostrar Logo',
    COMPANY_NAME: 'Nombre de la Empresa',
    COMPANY_ADDRESS: 'Dirección de la Empresa',
    COMPANY_PHONE: 'Teléfono de la Empresa',
    COMPANY_EMAIL: 'Correo Electrónico de la Empresa',
    COMPANY_WEBSITE: 'Sitio Web de la Empresa',
    COMPANY_TAXID: 'RNC de la Empresa',
    SHOW_NCF: 'Mostrar NCF',
    SHOW_CUSTOMER_RNC: 'Mostrar RNC del Cliente',
    UPLOAD_LOGO: 'Subir Logo',
    LOGO_POSITION: 'Posición del Logo',
    LOGO_HEIGHT: 'Altura del Logo',
    LOGO_POSITION_LEFT: 'Izquierda',
    LOGO_POSITION_CENTER: 'Centro',
    LOGO_POSITION_RIGHT: 'Derecha',
    COMPANY_INFORMATION: 'Información de la Empresa',
    COMPANY_INFO_ALIGN: 'Alineación de Información',
    COMPANY_INFO_HEIGHT: 'Altura de la Información de la Empresa',
    LEFT: 'Izquierda',
    CENTER: 'Centro',
    RIGHT: 'Derecha',
    PRODUCT_COLUMN: 'Columnas de productos',
    PRODUCT_RECOMMENDED_THERMAL: 'Usar configuración recomendada para 80 mm',
    PRODUCT_CODE: 'Código',
    PRODUCT_NAME: 'Nombre',
    PRODUCT_PRICE: 'Precio',
    PRODUCT_DESCRIPTION: 'Descripción',
    PRODUCT_MODIFIERS: 'Modificadores',
    PRODUCT_TAX: 'Impuesto',
    PRODUCT_DISCOUNT: 'Descuento',
    PRODUCT_SUBTOTAL: 'Subtotal',
    QUANTITY: 'Cantidad',
    TOTAL: 'Total',
    SUBTOTAL: 'Subtotal',
    DISCOUNT: 'Descuento',
    ITBIS: 'ITBIS',
    NOTES: 'Notas del pie de página',
    TERMS_AND_CONDITIONS: 'Términos y Condiciones',

    // New translation keys
    LOGO_SECTION_DESCRIPTION: 'Configura la imagen y posición del logo',
    COMPANY_INFO_SECTION: 'Información de la Empresa',
    COMPANY_INFO_SECTION_DESCRIPTION:
      'Configura qué datos de la empresa mostrar',
    STYLING_SECTION: 'Estilo',
    STYLING_SECTION_DESCRIPTION: 'Personaliza los colores del encabezado',
    HEADER_COLOR: 'Color del Encabezado',
    HEADER_TEXT_COLOR: 'Color del Texto del Encabezado',
    PRODUCTS_SECTION_DESCRIPTION:
      'Selecciona qué columnas mostrar en la tabla de productos',
    TOTALS_SECTION_DESCRIPTION:
      'Configura qué totales mostrar y la información fiscal',
    TAX_NAME: 'Nombre del impuesto',
    TAX_RATE: 'Tasa (%)',
    QUANTITY_SUMMARY: 'Resumen de cantidades',
    TOTAL_ITEMS: 'Total de artículos',
    UNITS: 'unidades',
    FOOTER_SECTION_DESCRIPTION:
      'Añade notas personalizadas y términos y condiciones al pie de página',
    NOTES_PLACEHOLDER: 'Agrega una nota personalizada para tus clientes',
    TERMS_PLACEHOLDER: 'Define los términos y condiciones de venta',
    SIGNATURES: 'Firmas',
    SIGNATURES_DESCRIPTION:
      'Imprime una línea para firmar al pie del documento. Elige en qué documentos aparece cada línea.',
    SIGNATURE_RECEIVED: 'Recibido',
    SIGNATURE_DISPATCHED: 'Despachado',
    SIGNATURE_LABEL: 'Etiqueta',
    SIGNATURE_SHOW_ON: 'Mostrar en',
    SIGNATURE_NO_DOCUMENTS:
      'Selecciona al menos un documento para que la línea se imprima.',
    SIGNATURE_DOC_INVOICE: 'Factura',
    SIGNATURE_DOC_QUOTE: 'Cotización',
    SIGNATURE_DOC_CONDUCE: 'Conduce',
    SIGNATURE_DOC_PURCHASE_ORDER: 'Orden de compra',
    SIGNATURE_DOC_SUPPLIER_INVOICE: 'Factura de proveedor',
    SAVING: 'Guardando...',
    SAVE: 'Guardar',
    PREVIEW: 'Vista Previa',
    REAL_TIME_UPDATE: 'Actualización en tiempo real',
    TOO_MANY_COLUMNS_WARNING:
      'Demasiadas columnas activas para el tamaño de papel seleccionado. El contenido podría no caber correctamente al imprimir.',
    PRINT_PREVIEW: 'Imprimir Vista Previa',
    PRINTING: 'Imprimiendo...',
    LOGO_URL_PLACEHOLDER: 'https://ejemplo.com/logo.png',
    SELECT_POSITION_PLACEHOLDER: 'Seleccionar posición',
    TAX_NAME_PLACEHOLDER: 'ITBIS',
    TAX_RATE_PLACEHOLDER: '18',
    DEFAULT_TAX_NAME: 'ITBIS',
    DEFAULT_TAX_RATE: '18',
    DEFAULT_FOOTER_NOTES: 'Gracias por su preferencia',
    DEFAULT_FOOTER_TERMS: 'Los términos y condiciones se aplican',
    DEFAULT_TEMPLATE_NAME: 'Plantilla Principal',
    LOGO_UPLOADER_UPLOADING: 'Subiendo...',
    LOGO_UPLOADER_DROP_HERE: 'Suelta aquí',
    LOGO_UPLOADER_CHANGE_LOGO: 'Cambiar logo',
    LOGO_UPLOADER_UPLOAD_LOGO: 'Subir logo',
    LOGO_UPLOADER_CLICK_OR_DRAG: 'Haz clic o arrastra una imagen',
    SELECT_PREVIEW_STYLE: 'Seleccionar estilo',
    PREVIEW_A4: 'A4 (Carta 8 1/2 x 11)',
    PREVIEW_LETTER: 'Carta',
    PREVIEW_THERMAL: 'Impresora 80 MM',

    // Auto-Pricing Settings
    AUTO_PRICING: 'Precio Automático',
    AUTO_PRICING_DESCRIPTION:
      'Configuración del cálculo automático de precios de venta',
    AUTO_CALCULATE_SELLING_PRICE: 'Calcular precio de venta automáticamente',
    AUTO_CALCULATE_SELLING_PRICE_PERCENTAGE_HELP:
      'Calcula el precio de venta basándose en el promedio de las últimas órdenes de compra',
    PURCHASE_ORDERS_TO_CONSIDER: 'Órdenes de compra a considerar',
    PURCHASE_ORDERS_TO_CONSIDER_HELP:
      'Número de órdenes de compra recientes para calcular el promedio',
    DEFAULT_MARKUP_PERCENTAGE: 'Porcentaje de Ganancia Predeterminado',
    DEFAULT_MARKUP_PERCENTAGE_HELP:
      'Porcentaje que se aplicará por defecto al precio de costo para calcular el precio de venta (ej: 30% sobre 200 = 260)',
    ALLOW_NEGATIVE_STOCK: 'Permitir Stock Negativo',
    ALLOW_NEGATIVE_STOCK_HELP:
      'Permite crear facturas aunque el producto tenga stock en 0. Si está desactivado, los productos sin stock no podrán seleccionarse en POS ni facturas.',
    INVENTORY_RULES: 'Reglas de Inventario',
    INVENTORY_RULES_DESCRIPTION:
      'Configure las reglas para la gestión y control de inventario',
    KDS_SETTINGS: 'Configuración de Ordenes (KDS)',
    KDS_SETTINGS_DESCRIPTION:
      'Configure los tiempos de advertencia y atraso para las órdenes en el sistema de visualización de cocina',
    KDS_ENABLED: 'Habilitar KDS',
    KDS_ENABLED_HELP:
      'Activa el Sistema de Visualización de Ordenes (KDS) para gestionar órdenes en preparación',
    KDS_WARNING_TIME: 'Tiempo de Advertencia (minutos)',
    KDS_WARNING_TIME_HELP:
      'Tiempo en minutos después del cual una orden mostrará una advertencia visual (color amarillo)',
    KDS_OVERDUE_TIME: 'Tiempo de Atraso (minutos)',
    KDS_OVERDUE_TIME_HELP:
      'Tiempo en minutos después del cual una orden se marcará como atrasada (color rojo con animación)',
    CONFIG_SAVED_TITLE: 'Configuración guardada',
    CONFIG_SAVED_HEADER:
      'La configuración del encabezado se guardó correctamente',
    CONFIG_SAVED_PRODUCTS:
      'La configuración de productos se guardó correctamente',
    CONFIG_SAVED_TOTALS: 'La configuración de totales se guardó correctamente',
    CONFIG_ERROR_TITLE: 'Error al guardar',
    CONFIG_ERROR_HEADER: 'No se pudo guardar la configuración del encabezado',
    CONFIG_ERROR_PRODUCTS: 'No se pudo guardar la configuración de productos',
    CONFIG_ERROR_TOTALS: 'No se pudo guardar la configuración de totales',
    CONFIG_NO_CHANGES_TITLE: 'Sin cambios',
    CONFIG_NO_CHANGES_DESCRIPTION:
      'No se detectaron cambios en la configuración',
    SHOW_TAXES: 'Mostrar impuestos',
    CONFIGURED_TAXES: 'Impuestos configurados',
    DEFAULT_TAXES_NOTE: 'Se aplicarán por defecto al crear facturas',
    // Sample data for invoice preview
    SAMPLE_CUSTOMER: 'Juan Pérez',
    SAMPLE_INVOICE_NUMBER: '001-001-00000001',
    SAMPLE_CASHIER: 'María García',
    SAMPLE_NCF: 'B02000000001',
    SAMPLE_CUSTOMER_RNC: '130203245',
    SAMPLE_PRODUCT_1_CODE: '001',
    SAMPLE_PRODUCT_1_NAME: 'Producto Ejemplo',
    SAMPLE_PRODUCT_1_DESCRIPTION: 'Descripción Ejemplo',
    SAMPLE_PRODUCT_1_MODIFIERS: 'Tamaño: Grande | Color: Rojo',
    SAMPLE_PRODUCT_2_CODE: '002',
    SAMPLE_PRODUCT_2_NAME: 'Otro Producto',
    SAMPLE_PRODUCT_2_DESCRIPTION: 'Descripción Otro Producto',
    // Short column headers for Thermal/Zebra format
    THERMAL_REF: 'Ref.',
    THERMAL_DESC: 'Desc.',
    THERMAL_QTY: 'Cant.',
    THERMAL_TOTAL: 'Total',
    REPLICATE_TO_ALL_BRANCHES: 'Replicar a sucursales',
  },
  REPORTS: {
    TITLE: 'Reportes',
    SUBTITLE: 'Analice el rendimiento de su negocio',
    SELECT_PERIOD: 'Seleccionar período',
    SELECT_BRANCH: 'Seleccionar sucursal',
    SELECT_WAREHOUSE: 'Seleccionar almacén',
    SELECT_USER: 'Seleccionar usuario',
    TODAY: 'Hoy',
    YESTERDAY: 'Ayer',
    LAST_7_DAYS: 'Últimos 7 días',
    LAST_30_DAYS: 'Últimos 30 días',
    THIS_MONTH: 'Este mes',
    LAST_MONTH: 'Mes pasado',
    CUSTOM_RANGE: 'Rango personalizado',
    ALL_BRANCHES: 'Todas las sucursales',
    ALL_WAREHOUSES: 'Todos los almacenes',
    ALL_USERS: 'Todos los usuarios',

    // Sales Report
    SALES_REPORT: 'Reporte de Ventas',
    SALES_REPORT_SUBTITLE: 'Análisis detallado de las ventas',
    TOTAL_SALES: 'Ventas Totales',
    TOTAL_TRANSACTIONS: 'Total de Transacciones',
    AVERAGE_TICKET: 'Ticket Promedio',
    SALES_BY_HOUR: 'Ventas por Hora',
    SALES_BY_CATEGORY: 'Ventas por Categoría',
    SALES_BY_PRODUCT: 'Ventas por Producto',
    SALES_BY_PAYMENT_METHOD: 'Ventas por Método de Pago',
    TOP_SELLING_PRODUCTS: 'Productos Más Vendidos',
    PAYMENT_METHOD: 'Método de Pago',
    AMOUNT: 'Monto',
    CASH: 'Efectivo',
    CARD: 'Tarjeta',
    TRANSFER: 'Transferencia',
    VARIOUS: 'Varios',
    CREDIT_CARD: 'Tarjeta de Crédito',
    DEBIT_CARD: 'Tarjeta de Débito',
    CHECK: 'Cheque',
    PAYPAL: 'PayPal',
    ZELLE: 'Zelle',
    CRYPTO: 'Criptomoneda',
    OTHER: 'Otro',

    // Inventory Report
    INVENTORY_REPORT: 'Reporte de Inventario',
    INVENTORY_REPORT_SUBTITLE: 'Estado actual del inventario',
    TOTAL_PRODUCTS: 'Total de Productos',
    LOW_STOCK_ITEMS: 'Productos con Stock Bajo',
    NO_SALES_LAST_30_DAYS: 'Sin Ventas (30 días)',
    OUT_OF_STOCK: 'Sin Stock',
    INVENTORY_VALUE: 'Valor del Inventario',
    STOCK_LEVEL: 'Nivel de Stock',
    REORDER_POINT: 'Punto de Reorden',
    STOCK_MOVEMENTS: 'Movimientos de Stock',
    RECENT_MOVEMENTS: 'Según el rango de fechas seleccionado',
    STOCK_IN: 'Entradas',
    STOCK_OUT: 'Salidas',
    CURRENT_STOCK: 'Stock Actual',
    UNIT: 'Unidad',
    MINIMUM_STOCK: 'Stock Mínimo',
    PRODUCT_NAME: 'Nombre del Producto',

    // User Activity Report
    USER_ACTIVITY: 'Actividad de Usuario',
    USER_ACTIVITY_SUBTITLE: 'Registro de actividades del sistema',
    ACTIVE_USERS: 'Usuarios Activos',
    TOTAL_ACTIONS: 'Total de Acciones',
    LAST_LOGIN: 'Último Acceso',
    USER_NAME: 'Usuario',
    ACTION: 'Acción',
    TIMESTAMP: 'Fecha y Hora',
    DETAILS: 'Detalles',
    LOGIN: 'Inicio de Sesión',
    LOGOUT: 'Cierre de Sesión',
    CREATE: 'Crear',
    UPDATE: 'Actualizar',
    DELETE: 'Eliminar',
    VIEW: 'Ver',
    MOST_ACTIVE_USERS: 'Usuarios Más Activos',
    ACTIONS_BY_TYPE: 'Acciones por Tipo',

    // Financial Summary
    FINANCIAL_SUMMARY: 'Resumen Financiero',
    FINANCIAL_SUMMARY_SUBTITLE: 'Estado financiero del negocio',
    TOTAL_REVENUE: 'Ingresos Totales',
    TOTAL_EXPENSES: 'Gastos Totales',
    NET_PROFIT: 'Ganancia Neta',
    PROFIT_MARGIN: 'Margen de Ganancia',
    REVENUE_BREAKDOWN_PAYMENTS: '{{count}} pago(s): {{amount}}',
    REVENUE_BREAKDOWN_REFUNDS: '{{count}} reembolso(s): -{{amount}}',
    REVENUE_BREAKDOWN_CREDIT_NOTES: '{{count}} nota(s) de crédito: -{{amount}}',
    EXPENSES_BREAKDOWN_PURCHASE_ORDERS: '{{count}} orden(es) de compra',
    PROFIT_BREAKDOWN_INVOICES: '{{count}} factura(s) pagada(s)',
    PROFIT_MARGIN_BASED_ON: 'Basado en {{revenue}} de ingresos',
    REVENUE_VS_EXPENSES: 'Ingresos vs Gastos',
    PROFIT_TREND: 'Tendencia de Ganancias',
    EXPENSES_BY_CATEGORY: 'Gastos por Categoría',
    REVENUE_SOURCES: 'Fuentes de Ingresos',
    OPERATING_EXPENSES: 'Gastos Operativos',
    COST_OF_GOODS: 'Costo de Mercancía',

    // Daily Summary
    DAILY_SUMMARY: 'Resumen Diario',
    DAILY_SUMMARY_SUBTITLE: 'Resumen de operaciones del día',
    TODAYS_SALES: 'Ventas de Hoy',
    TRANSACTIONS_TODAY: 'Transacciones de Hoy',
    CUSTOMERS_SERVED: 'Clientes Atendidos',
    HOURLY_SALES: 'Ventas por Hora',
    PEAK_HOURS: 'Horas Pico',
    SHIFT_SUMMARY: 'Resumen de Turnos',
    CASHIER_PERFORMANCE: 'Rendimiento de Cajeros',
    OPENED_AT: 'Abierto a las',
    CLOSED_AT: 'Cerrado a las',
    SHIFT_SALES: 'Ventas del Turno',

    // Receipts
    RECEIPTS: 'Recibos',
    RECEIPTS_SUBTITLE: 'Historial de recibos y facturas',
    RECEIPT_NUMBER: 'Número de Recibo',
    RECEIPT_DATE: 'Fecha',
    CUSTOMER_NAME: 'Cliente',
    STATUS: 'Estado',
    PRINT: 'Imprimir',
    EMAIL: 'Enviar Email',
    SEARCH_RECEIPTS: 'Buscar recibos...',
    PAID: 'Pagado',
    PENDING: 'Pendiente',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Reembolsado',
    VIEW_RECEIPT: 'Ver Recibo',
    DOWNLOAD_PDF: 'Descargar PDF',
    SEND_EMAIL: 'Enviar por Email',
    RECEIPT_DETAILS: 'Detalles del Recibo',

    // Additional descriptions
    SALES_REPORT_DESCRIPTION: 'Análisis completo de ventas y rendimiento',
    INVENTORY_REPORT_DESCRIPTION:
      'Control de stock y movimientos de inventario',
    USER_ACTIVITY_DESCRIPTION:
      'Seguimiento de actividades y acciones de usuarios',
    FINANCIAL_SUMMARY_DESCRIPTION: 'Vista general del estado financiero',
    DAILY_SUMMARY_DESCRIPTION: 'Resumen de operaciones y métricas diarias',
    RECEIPTS_DESCRIPTION: 'Gestión y búsqueda de recibos',

    // Additional sales keys
    TRANSACTIONS: 'Transacciones',
    CANCELLED_BADGE_LABEL: 'anuladas',
    CANCELLED_INVOICES_HINT:
      'Pagos vinculados a facturas anuladas — excluidas del total mostrado',
    CREDIT_NOTED_BADGE_LABEL: 'con NC',
    CREDIT_NOTED_INVOICES_HINT:
      'Pagos vinculados a facturas con notas de crédito emitidas — excluidas del total mostrado',
    PAYMENT_TYPE_DETAIL_BREADCRUMB_ROOT: 'Métodos de pago',
    INVOICE_STATUS_CANCELLED: 'Anulada',
    INVOICE_STATUS_CREDIT_NOTED: 'Con Nota de Crédito',
    BEST_SELLERS: 'Más vendidos',
    SALES_TREND: 'Tendencia de ventas',
    TOP_PRODUCTS: 'Productos destacados',
    PEAK_HOUR: 'Hora pico',
    ITEMS_SOLD: 'Artículos vendidos',
    PROPINA_COMMISSION: 'Comisión Propina (10%)',
    PROPINA_REPORT: 'Propina 10%',
    PROPINA_REPORT_DESCRIPTION:
      'Propina Legal recaudada, comisión por pagar y las ventas que la generan',
    PROPINA_COLLECTED: 'Propina recaudada',
    PROPINA_COLLECTED_SUB: '{{count}} factura(s) · 10% sobre base {{base}}',
    PROPINA_COMMISSION_SUB: 'a pagar a vendedores',
    PROPINA_SALES: 'Ventas con propina',
    PROPINA_SALES_SUB:
      'Base {{base}} + ITBIS {{tax}} + propina {{propina}} · {{percent}}% de las ventas del período',
    PROPINA_ITEMS_SUB: 'ticket promedio {{amount}}',
    PROPINA_SCOPE_CAPTION:
      'Período {{start}} – {{end}} · solo facturas con Propina Legal · todas las del rango, no solo esta página.',
    PROPINA_SCOPE_CAPTION_ALL:
      'Todo el período · solo facturas con Propina Legal · todas las del rango, no solo esta página.',
    PROPINA_EMPTY: 'Ninguna factura del período lleva Propina Legal.',
    STATS_SCOPE_CAPTION:
      'Totales del período · {{start}} – {{end}} · todas las facturas del rango, no solo esta página.',
    STATS_SCOPE_CAPTION_ALL:
      'Totales de todo el período · todas las facturas del rango, no solo esta página.',
    GENERATED_ON: 'Generado el',
    CARD_PAYMENTS: 'Pagos con tarjeta',
    RETURN_RATE: 'Tasa de retorno',

    // Inventory keys
    TOTAL_ITEMS: 'Total de artículos',
    MIN_STOCK: 'Stock mínimo',
    VALUE: 'Valor',
    STOCK_OK: 'Stock OK',
    LOW_STOCK: 'Stock bajo',
    CRITICAL: 'Bajo',
    OVERSTOCK: 'Sobre stock',
    INVENTORY_STATUS: 'Estado del inventario',
    CURRENT_CRITICAL_STOCK_LEVELS: 'Niveles de stock bajos actuales',
    CATEGORIES_DISTRIBUTION: 'Distribución por categorías',
    INVENTORY_BY_CATEGORY: 'Inventario por categoría',
    ALL_CATEGORIES: 'Todas las categorías',
    CATEGORIES_SELECTED: '{{count}} categoría(s)',
    SHOW_STOCK_MOVEMENTS: 'Mostrar movimientos de stock',

    // User activity keys
    RECENT_ACTIVITY: 'Actividad reciente',
    LAST_ACTIONS: 'Últimas acciones',
    ACTIVITY_CUSTOMER_CREATED: 'Cliente creado',
    ACTIVITY_CUSTOMER_UPDATED: 'Cliente actualizado',
    ACTIVITY_CUSTOMER_DELETED: 'Cliente eliminado',
    ACTIVITY_SHIFT_CREATED: 'Turno creado',
    ACTIVITY_SHIFT_UPDATED: 'Turno actualizado',
    ACTIVITY_SHIFT_DELETED: 'Turno eliminado',

    // Activity verb participles (gender variants)
    ACTIVITY_PAST_CREATED_M: 'creado',
    ACTIVITY_PAST_CREATED_F: 'creada',
    ACTIVITY_PAST_UPDATED_M: 'actualizado',
    ACTIVITY_PAST_UPDATED_F: 'actualizada',
    ACTIVITY_PAST_DELETED_M: 'eliminado',
    ACTIVITY_PAST_DELETED_F: 'eliminada',

    // Activity module singular names (for action labels)
    ACTIVITY_MOD_APPROVAL: 'Aprobación',
    ACTIVITY_MOD_BUSINESS: 'Negocio',
    ACTIVITY_MOD_CREDIT_NOTE: 'Nota de crédito',
    ACTIVITY_MOD_CUSTOMER: 'Cliente',
    ACTIVITY_MOD_DELIVERY_DRIVER: 'Conductor',
    ACTIVITY_MOD_DELIVERY_METHOD: 'Método de entrega',
    ACTIVITY_MOD_IMAGE: 'Imagen',
    ACTIVITY_MOD_INVENTORY: 'Inventario',
    ACTIVITY_MOD_INVOICE: 'Factura',
    ACTIVITY_MOD_CONDUCE: 'Conduce',
    ACTIVITY_MOD_MENU: 'Menú',
    ACTIVITY_MOD_NCF: 'NCF',
    ACTIVITY_MOD_NOTIFICATION: 'Notificación',
    ACTIVITY_MOD_PAYMENT: 'Pago',
    ACTIVITY_MOD_PERMISSION: 'Permiso',
    ACTIVITY_MOD_PLAN_FEATURE: 'Característica del plan',
    ACTIVITY_MOD_STOCK_MOVEMENT: 'Movimiento de inventario',
    ACTIVITY_MOD_PROMOTION: 'Promoción',
    ACTIVITY_MOD_PROVIDER: 'Proveedor',
    ACTIVITY_MOD_PURCHASE_ORDER: 'Orden de compra',
    ACTIVITY_MOD_REFUND: 'Reembolso',
    ACTIVITY_MOD_REPORT: 'Reporte',
    ACTIVITY_MOD_ROLE: 'Rol',
    ACTIVITY_MOD_SETTING: 'Configuración',
    ACTIVITY_MOD_SETTINGS_INVOICE_TEMPLATE: 'Plantilla de factura',
    ACTIVITY_MOD_SETTINGS_BUSINESS: 'Configuración del negocio',
    ACTIVITY_MOD_SETTINGS_NOTIFICATION: 'Configuración de notificaciones',
    ACTIVITY_MOD_SETTINGS_EMAIL: 'Configuración de email',
    ACTIVITY_MOD_SETTINGS_GENERAL: 'Configuración general',
    ACTIVITY_MOD_SHIFT: 'Turno',
    ACTIVITY_MOD_SUBSCRIPTION: 'Suscripción',
    ACTIVITY_MOD_SYSTEM_OPTION: 'Opción del sistema',
    ACTIVITY_MOD_TAX: 'Impuesto',
    ACTIVITY_MOD_USER: 'Usuario',
    ACTIVITY_MOD_SYNC: 'Sincronización',
    ACTIVITY_MOD_RESTAURANT_ENVIRONMENT: 'Salón',
    ACTIVITY_MOD_RESTAURANT_TABLE: 'Mesa',
    ACTIVITY_MOD_RESTAURANT_DECORATION: 'Decoración',
    ACTIVITY_MOD_RESTAURANT_OPEN_ACCOUNT: 'Cuenta abierta',
    ACTIVITY_MOD_RESTAURANT_OPEN_TAB: 'Comanda',
    ACTIVITY_MOD_RESTAURANT_TAB_ITEM: 'Producto de comanda',
    ACTIVITY_MODS_RESTAURANT_ENVIRONMENTS: 'Salones',
    ACTIVITY_MODS_RESTAURANT_TABLES: 'Mesas',
    ACTIVITY_MODS_RESTAURANT_DECORATIONS: 'Decoraciones',
    ACTIVITY_MODS_RESTAURANT_OPEN_ACCOUNTS: 'Cuentas abiertas',
    ACTIVITY_MODS_RESTAURANT_OPEN_TABS: 'Comandas',
    ACTIVITY_MODS_RESTAURANT_TAB_ITEMS: 'Productos de comanda',

    // Activity module plural names (for module badge)
    ACTIVITY_MODS_APPROVALS: 'Aprobaciones',
    ACTIVITY_MODS_BUSINESSES: 'Negocios',
    ACTIVITY_MODS_CREDIT_NOTES: 'Notas de crédito',
    ACTIVITY_MODS_CUSTOMERS: 'Clientes',
    ACTIVITY_MODS_DELIVERY_DRIVERS: 'Conductores',
    ACTIVITY_MODS_DELIVERY_METHODS: 'Métodos de entrega',
    ACTIVITY_MODS_IMAGES: 'Imágenes',
    ACTIVITY_MODS_INVENTORY: 'Inventario',
    ACTIVITY_MODS_INVOICES: 'Facturas',
    ACTIVITY_MODS_CONDUCES: 'Conduces',
    ACTIVITY_MODS_MENUS: 'Menús',
    ACTIVITY_MODS_NCFS: 'NCF',
    ACTIVITY_MODS_NOTIFICATIONS: 'Notificaciones',
    ACTIVITY_MODS_PAYMENTS: 'Pagos',
    ACTIVITY_MODS_PERMISSIONS: 'Permisos',
    ACTIVITY_MODS_PLAN_FEATURES: 'Características del plan',
    ACTIVITY_MODS_STOCK_MOVEMENTS: 'Movimientos de inventario',
    ACTIVITY_MODS_PROMOTIONS: 'Promociones',
    ACTIVITY_MODS_PROVIDERS: 'Proveedores',
    ACTIVITY_MODS_PURCHASE_ORDERS: 'Órdenes de compra',
    ACTIVITY_MODS_REFUNDS: 'Reembolsos',
    ACTIVITY_MODS_REPORTS: 'Reportes',
    ACTIVITY_MODS_ROLES: 'Roles',
    ACTIVITY_MODS_SETTINGS: 'Configuración',
    ACTIVITY_MODS_SETTINGS_INVOICE_TEMPLATE: 'Plantilla de factura',
    ACTIVITY_MODS_SETTINGS_BUSINESS: 'Configuración del negocio',
    ACTIVITY_MODS_SETTINGS_NOTIFICATION: 'Configuración de notificaciones',
    ACTIVITY_MODS_SETTINGS_EMAIL: 'Configuración de email',
    ACTIVITY_MODS_SETTINGS_GENERAL: 'Configuración general',
    ACTIVITY_MODS_SHIFTS: 'Turnos y Cuadres de Caja',
    ACTIVITY_MODS_SUBSCRIPTIONS: 'Suscripciones',
    ACTIVITY_MODS_SYSTEM_OPTIONS: 'Opciones del sistema',
    ACTIVITY_MODS_TAXES: 'Impuestos',
    ACTIVITY_MODS_USERS: 'Usuarios',
    ACTIVITY_MODS_SYNCS: 'Sincronizaciones',
    ACTIVITY_MOD_USER_PREFERENCE: 'Preferencia de Usuario',
    ACTIVITY_MODS_USER_PREFERENCES: 'Preferencias de Usuario',
    ACTIVITY_MOD_CONTRACT: 'Contrato',
    ACTIVITY_MODS_CONTRACTS: 'Contratos',
    ACTIVITY_MOD_ECF: 'e-CF',
    ACTIVITY_MODS_ECF: 'e-CF',
    ACTIVITY_MOD_ACCOUNTING: 'Registro contable',
    ACTIVITY_MODS_ACCOUNTING: 'Contabilidad',
    ACTIVITY_MODS_AUTH: 'Autenticación',
    ACTIVITY_MOD_SYSTEM: 'Sistema',
    ACTIVITY_MODS_SYSTEM: 'Sistema',
    ACTIVITY_MOD_FINANCE: 'Movimiento financiero',
    ACTIVITY_MODS_FINANCE: 'Finanzas',
    ACTIVITY_MOD_BILLING: 'Facturación',
    ACTIVITY_MODS_BILLING: 'Facturación',
    ACTIVITY_MOD_KDS: 'KDS',
    ACTIVITY_MODS_KDS: 'KDS',
    ACTIVITY_MOD_RESTAURANT: 'Restaurante',
    ACTIVITY_MODS_RESTAURANT: 'Restaurante',
    ACTIVITY_MOD_SALES_ORDER: 'Orden de venta',
    ACTIVITY_MODS_SALES_ORDERS: 'Órdenes de venta',
    ACTIVITY_MOD_PROCUREMENT: 'Compra',
    ACTIVITY_MODS_PROCUREMENT: 'Compras',
    ACTIVITY_MOD_SUPPLIER_PAYMENT: 'Pago a proveedor',
    ACTIVITY_MODS_SUPPLIER_PAYMENTS: 'Pagos a proveedores',
    ACTIVITY_MOD_SUPPLIER_TAX_DOCUMENT: 'Comprobante de proveedor',
    ACTIVITY_MODS_SUPPLIER_TAX_DOCUMENTS: 'Comprobantes de proveedores',
    ACTIVITY_MOD_CASH_VOUCHER: 'Vale de caja',
    ACTIVITY_MODS_CASH_VOUCHERS: 'Vales de caja',
    ACTIVITY_MOD_COUPON: 'Cupón',
    ACTIVITY_MODS_COUPONS: 'Cupones',
    ACTIVITY_MOD_DEVICE: 'Dispositivo',
    ACTIVITY_MODS_DEVICES: 'Dispositivos',
    ACTIVITY_MOD_FOLLOW_UP_REMINDER: 'Recordatorio de seguimiento',
    ACTIVITY_MODS_FOLLOW_UP_REMINDERS: 'Recordatorios de seguimiento',
    ACTIVITY_MOD_SUBSCRIPTION_CHANGE: 'Cambio de suscripción',
    ACTIVITY_MODS_SUBSCRIPTION_CHANGES: 'Cambios de suscripción',
    ACTIVITY_MOD_STOCK_COUNT: 'Conteo de inventario',
    ACTIVITY_MODS_STOCK_COUNTING: 'Conteos de inventario',
    ACTIVITY_MOD_SECURITY: 'Evento de seguridad',
    ACTIVITY_MODS_SECURITY: 'Seguridad',
    ACTIVITY_TAB_RECENT: 'Actividad reciente',
    ACTIVITY_INVENTORY_RESET: 'Restableció el inventario',
    ACTIVITY_PRINT_PREFERENCES_UPDATED: 'Actualizó preferencias de impresión',
    ACTIVITY_PRINT_PREFERENCES_RESET: 'Restableció preferencias de impresión',
    ACTIVITY_INVOICE_WRITE_OFF: 'Aplicó ajuste de saldo a factura',
    ACTIVITY_INVOICE_RECONCILE_ROUNDING:
      'Reconcilió redondeo de facturas pendientes',
    ACTIVITY_PURCHASE_EXPENSE_ACCOUNT_MAP:
      'Actualizó las cuentas contables por tipo de compra (DGII 606)',
    ACTIVITY_CONDUCE_CREATE: 'Creó un conduce',
    ACTIVITY_CONDUCE_UPDATE: 'Actualizó un conduce',
    ACTIVITY_CONDUCE_RETURN: 'Devolvió ítems de un conduce',
    ACTIVITY_RENTAL_DAMAGE_ADJUST: 'Ajustó cargo por daños',
    ACTIVITY_INVOICE_RETURN_TO_SELLER: 'Devolvió factura al vendedor',
    ACTIVITY_INVOICE_FINALIZE: 'Cobró factura pendiente',
    ACTIVITY_SUPPLIER_DOC_VOID: 'Anuló documento de proveedor',
    ACTIVITY_SUPPLIER_PAYMENT_VOID: 'Anuló pago a proveedor',
    ACTIVITY_EXPENSE_VOID: 'Anuló gasto',
    ACTIVITY_SUPPLIER_CREDIT_NOTE_PROCESS:
      'Procesó nota de crédito de proveedor',

    ACTIVITY_CHANGES_TITLE: 'Detalle del cambio',
    ACTIVITY_MODIFIED_FIELDS: 'Campos modificados',
    ACTIVITY_NO_DETAILS: 'No hay detalles disponibles para esta actividad.',

    TOP_ACTIVE_USERS: 'Usuarios más activos',
    ACTIONS: 'Acciones',
    ACTIVITY_BY_USER: 'Actividad por usuario',
    ACTIVITY_BY_MODULE: 'Actividad por módulo',
    MODULE: 'Módulo',
    MODULE_USAGE: 'Uso de módulos',
    SESSIONS_TODAY: 'Sesiones hoy',
    AVG_SESSION_TIME: 'Tiempo promedio de sesión',

    // Financial keys
    LAST_6_MONTHS: 'Últimos 6 meses',
    BY_CATEGORY: 'Por categoría',
    SALARIES: 'Salarios',
    RENT_UTILITIES: 'Alquiler y servicios',
    MARKETING: 'Marketing',
    OTHER_EXPENSES: 'Otros gastos',
    CASH_FLOW: 'Flujo de caja',
    INFLOW_OUTFLOW: 'Entradas y salidas',
    CASH_INFLOW: 'Dinero entrante',
    CASH_OUTFLOW: 'Salida de efectivo',
    NET_CASH_FLOW: 'Flujo de caja neto',
    TAX_COLLECTED_ON_INVOICES: 'Impuestos percibidos en Facturas',
    TAX_COLLECTED_SUBTITLE: 'ITBIS retenido en facturas del período',
    DETAIL_BREADCRUMB_ROOT: 'Reporte financiero',
    DETAIL_TITLE_GROSS_REVENUE: 'Detalle — Ingresos Brutos',
    DETAIL_TITLE_TOTAL_REVENUE: 'Detalle — Ingresos Totales',
    DETAIL_TITLE_EXPENSES: 'Detalle — Gastos Totales',
    DETAIL_TITLE_NET_PROFIT: 'Detalle — Utilidad Neta',
    DETAIL_TITLE_PROFIT_MARGIN: 'Detalle — Margen de Ganancia',
    DETAIL_TITLE_CREDIT_NOTE_RATE: 'Detalle — Notas de Crédito',
    DETAIL_TITLE_TAX_COLLECTED: 'Detalle — Impuestos Percibidos',
    DETAIL_COL_DATE: 'Fecha',
    DETAIL_COL_DOCUMENT: 'Documento',
    DETAIL_COL_CUSTOMER: 'Cliente',
    DETAIL_COL_METHOD: 'Método',
    DETAIL_COL_DESCRIPTION: 'Descripción',
    DETAIL_COL_AMOUNT: 'Monto',
    DETAIL_COL_TAX: 'Impuesto',
    DETAIL_COL_TOTAL: 'Total',
    DETAIL_COL_PROFIT: 'Utilidad',
    DETAIL_COL_MARGIN: 'Margen',
    DETAIL_EXPORT_MENU: 'Exportar',
    DETAIL_EXPORT_PDF: 'PDF (horizontal)',
    DETAIL_EXPORT_EXCEL: 'Excel (.xlsx)',
    DETAIL_EXPORT_CSV: 'CSV',
    DETAIL_EXPORT_TXT: 'Texto (.txt)',
    DETAIL_SUMMARY_COUNT: 'Registros',
    DETAIL_SUMMARY_AMOUNT: 'Total',
    DETAIL_SUMMARY_TAX: 'Impuesto',
    DETAIL_SUMMARY_PROFIT: 'Utilidad',
    DETAIL_SEARCH_PLACEHOLDER: 'Buscar por documento, cliente, descripción…',
    DETAIL_EMPTY: 'No hay registros para este rango',
    DETAIL_DOWNLOAD_ERROR: 'No se pudo generar el archivo. Intenta nuevamente.',
    PROFIT_MARGIN_TREND: 'Tendencia de margen de ganancia',
    MONTHLY_PERFORMANCE: 'Rendimiento mensual',

    // Daily summary keys
    DAILY_SALES: 'Ventas diarias',
    AVG_TICKET: 'Ticket promedio',
    HOURLY_PERFORMANCE: 'Rendimiento por hora',
    TOP_SELLERS_TODAY: 'Más vendidos hoy',
    BEST_SELLING_PRODUCTS: 'Productos más vendidos',

    // Receipt keys
    TOTAL_RECEIPTS: 'Total de recibos',
    COMPLETED: 'Completados',
    ALL_RECEIPTS: 'Todos los recibos',
    DATE_TIME: 'Fecha y hora',
    CUSTOMER: 'Cliente',
    CASHIER: 'Cajero',
    SELLER: 'Vendedor',
    SEARCH_BY_NUMBER_CUSTOMER: 'Buscar por número o cliente...',
    SHOWING: 'Mostrando',
    OF: 'de',
    PAYMENT_METHODS: 'Métodos de pago',
    EXPENSE_BREAKDOWN: 'Desglose de gastos',

    // Common
    NO_DATA: 'No hay datos disponibles para el período seleccionado',
    LOADING_REPORT: 'Cargando reporte...',
    EXPORT_SUCCESS: 'Reporte exportado exitosamente',
    EXPORT_ERROR: 'Error al exportar el reporte',
    FILTER_RESULTS: 'Filtrar resultados',
    CLEAR_FILTERS: 'Limpiar filtros',
    VIEW_MODE: 'Vista',
    SELECT_VIEW_MODE: 'Selecciona la vista',
    VIEW_MODE_DETAIL: 'Detalle de ventas',
    VIEW_MODE_BY_CATEGORY: 'Ventas por categoría',
    VIEW_MODE_BY_PRODUCT: 'Ventas por producto',
    BREAKDOWN_CATEGORY_TITLE:
      'Ventas por categoría (ordenadas de mayor a menor)',
    BREAKDOWN_PRODUCT_TITLE: 'Ventas por producto (ordenadas de mayor a menor)',
    BREAKDOWN_RANK: '#',
    BREAKDOWN_NAME: 'Nombre',
    BREAKDOWN_QUANTITY: 'Unidades',
    BREAKDOWN_REVENUE: 'Subtotal',
    BREAKDOWN_TAX: 'ITBIS',
    BREAKDOWN_TOTAL: 'Total',
    BREAKDOWN_TRANSACTIONS: 'Transacciones',
    BREAKDOWN_EMPTY: 'No hay ventas en el rango seleccionado.',
    COMPARISON: 'Comparación',
    VS_PREVIOUS_PERIOD: 'vs período anterior',
    INCREASE: 'Incremento',
    DECREASE: 'Disminución',

    // Sales List
    DATE: 'Fecha',
    TIME: 'Hora',
    INVOICE: 'Factura',
    USER: 'Usuario',
    ITEMS: 'Artículos',
    SUBTOTAL: 'Subtotal',
    TAX: 'Impuesto',
    DISCOUNT: 'Descuento',
    TOTAL: 'Total',
    TOTAL_PAGE: 'Total página',
    TOTAL_PERIOD: 'Total del período',
    DATE_RANGE: 'Rango de fechas',
    THIS_WEEK: 'Esta semana',
    LAST_WEEK: 'Semana pasada',
    CUSTOM: 'Personalizado',
    TIME_PERIOD: 'Período de tiempo',
    ALL_DAY: 'Todo el día',
    MORNING: 'Mañana',
    AFTERNOON: 'Tarde',
    EVENING: 'Noche',
    NIGHT: 'Madrugada',
    CUSTOM_TIME: 'Hora personalizada',
    CONFIGURE_COLUMNS: 'Configurar columnas',
    NO_SALES_FOUND: 'No se encontraron ventas',
    SALES: 'ventas',
    SHOW_ALL: 'Mostrar todo',
    HIDE_ALL: 'Ocultar todo',

    // Payment Type Report
    PAYMENT_TYPE_REPORT: 'Reporte de Tipos de Pago',
    PAYMENT_TYPE_REPORT_DESCRIPTION:
      'Análisis detallado de métodos de pago utilizados',
    PAYMENT_DISTRIBUTION: 'Distribución de Pagos',
    PAYMENT_DETAILS: 'Detalles de Pagos',
    PAYMENT_DETAILS_DESCRIPTION:
      'Listado de transacciones del método seleccionado',
    NO_PAYMENTS: 'No hay pagos registrados',
    INVOICE_NUMBER: 'Número de Factura',
    REFERENCE: 'Referencia',
    // User Activity Missing Keys
    NO_ACTIVITY_DATA: 'No hay actividad registrada',
    NO_USER_DATA: 'No hay datos de usuarios',
    NO_MODULE_DATA: 'No hay datos de módulos',
    NO_DATA_TO_EXPORT: 'No hay datos para exportar',
    // Chart visualization
    CHART_VIEW: 'Vista de Gráfico',
    BAR_CHART: 'Gráfico de Barras',
    PIE_CHART: 'Gráfico Circular',
    PROGRESS_BARS: 'Barras de Progreso',
    REVENUE_BY_PAYMENT_METHOD: 'Ingresos por Método de Pago',
    TOP_PERFORMING_PRODUCTS: 'Productos Más Rentables',
    REVENUE: 'Ingresos',
    EXPENSES: 'Gastos',
    PROFIT: 'Beneficio',
    NO_FINANCIAL_DATA: 'No hay datos financieros disponibles',
    GROSS_REVENUE: 'Ingresos Brutos',
    NET_REVENUE: 'Ingresos Netos',
    CREDIT_NOTE_RATE: 'Tasa de Notas de Crédito',
    CREDIT_NOTE_RATE_SUBTITLE:
      'Porcentaje de ingresos brutos devueltos como notas de crédito',
    CREDIT_NOTE_REFERENCES: 'Notas de Crédito del Período',
    CREDIT_NOTE_REFERENCES_SUBTITLE:
      'Notas de crédito aplicadas en este período',
    NO_CREDIT_NOTES: 'Sin notas de crédito en este período',
    ORIGINAL_INVOICE_SHORT: 'Factura Original',
    ACCOUNTS_RECEIVABLE: 'Cuentas por Cobrar',
    ACCOUNTS_RECEIVABLE_SUBTITLE:
      'Análisis de antigüedad de facturas pendientes',
    AR_CURRENT: 'Vigente',
    AR_1_30: '1-30 días',
    AR_31_60: '31-60 días',
    AR_61_90: '61-90 días',
    AR_90_PLUS: '90+ días',
    AR_TOTAL_OUTSTANDING: 'Total Pendiente',
    AR_DAYS_OVERDUE: 'Días vencido',
    AR_BALANCE: 'Saldo',
    AR_NO_OUTSTANDING: 'No hay facturas pendientes de cobro',
    CASH_FLOW_REFUNDS: 'Reembolsos',
    CASH_FLOW_CREDIT_NOTES: 'Notas de Crédito',
    SHIPPING: 'Envío',
    TAXES: 'Impuestos',
    PERIOD: 'Período',
    GROUP_BY: 'Agrupar por',
    DAY: 'Día',
    WEEK: 'Semana',
    MONTH: 'Mes',
    YEAR: 'Año',
    QUANTITY_SOLD: 'Cantidad Vendida',
    DGII_TITLE: 'Reportes DGII',
    DGII_SUBTITLE:
      'Genera y descarga los formatos 606, 607 y 608 listos para subir al portal de la DGII.',
    DGII_TAB_606: 'Formato 606 — Compras',
    DGII_TAB_607: 'Formato 607 — Ventas',
    DGII_TAB_608: 'Formato 608 — Anulados',
    DGII_TAB_609: 'Formato 609 — Pagos al Exterior',
    DGII_TAB_IT1: 'IT-1 — Resumen ITBIS',
    DGII_606_DESCRIPTION:
      'Compras a proveedores con NCF para el período seleccionado.',
    DGII_607_DESCRIPTION:
      'Ventas con NCF emitidas a clientes en el período seleccionado.',
    DGII_608_DESCRIPTION: 'NCFs anulados durante el período seleccionado.',
    DGII_609_DESCRIPTION:
      'Pagos a beneficiarios no residentes (servicios, regalías, intereses) con retención ISR — período seleccionado.',
    DGII_IT1_DESCRIPTION:
      'Resumen mensual del ITBIS (Formulario IT-1). Aggrega 606 + 607 para conciliar 1:1 con los reportes detalle.',
    DGII_IT1_CONCEPT: 'Concepto',
    DGII_IT1_AMOUNT: 'Monto (RD$)',
    DGII_IT1_ITBIS_TO_PAY: 'ITBIS a pagar',
    DGII_IT1_BALANCE_IN_FAVOR: 'Saldo a favor',
    DGII_BENEFICIARY: 'Beneficiario',
    DGII_COUNTRY: 'País',
    DGII_RETENTION_TYPE: 'Tipo de retención',
    DGII_AMOUNT_PAID: 'Monto pagado',
    DGII_RETENTION_RATE: 'Tasa retención',
    DGII_TOTAL_PAID: 'Total pagado',
    DGII_TOTAL_RETAINED: 'Total retenido',
    DGII_DOWNLOAD_TXT: 'Descargar TXT',
    DGII_DOWNLOAD_STARTED: 'Descarga iniciada.',
    DGII_DOWNLOAD_FAILED: 'No se pudo generar el archivo. Intenta nuevamente.',
    DGII_EMPTY_STATE: 'No hay comprobantes para este período.',
    DGII_TOTAL_ROWS: 'Registros',
    DGII_TOTAL_INVOICED: 'Monto facturado',
    DGII_TOTAL_ITBIS: 'ITBIS facturado',
    DGII_NCF: 'NCF',
    DGII_RNC: 'RNC / Cédula',
    DGII_PROVIDER: 'Proveedor',
    DGII_CUSTOMER: 'Cliente',
    DGII_INVOICE_DATE: 'Fecha comprobante',
    DGII_PAYMENT_DATE: 'Fecha pago',
    DGII_ID_TYPE: 'Tipo ID',
    DGII_ANNULMENT_TYPE: 'Tipo anulación',
    DGII_INCOME_TYPE: 'Tipo ingreso',
    DGII_GOODS_SERVICES: 'Tipo bienes/servicios',
    DGII_PAYMENT_FORM: 'Forma de pago',
    DGII_PERIOD_LABEL: 'Período',
    DGII_NOT_AVAILABLE: 'Reportes DGII no disponibles',
    DGII_NOT_AVAILABLE_DESCRIPTION:
      'Activa el complemento "Reportes DGII" en tu plan para generar los formatos 606, 607 y 608.',
    DGII_RNC_PROVIDER: 'RNC / Cédula Proveedor',
    DGII_RNC_CUSTOMER: 'RNC / Cédula Cliente',
    DGII_NCF_MODIFIED: 'NCF Modificado',
    DGII_WITHHOLDING_DATE: 'Fecha Retención',
    DGII_ITBIS_RETENIDO: 'ITBIS Retenido',
    DGII_ISR_RETENIDO: 'ISR Retenido',
    DGII_FEATURE_606: 'Formato 606 — Compras a proveedores con NCF',
    DGII_FEATURE_607: 'Formato 607 — Ventas con NCF emitidas a clientes',
    DGII_FEATURE_608: 'Formato 608 — NCFs anulados',
    DGII_FEATURE_TXT: 'Descarga TXT pipe-separado listo para subir a la DGII',
    DGII_FEATURE_FILTERS: 'Filtro por año y mes con totales facturados + ITBIS',
  },
  INVOICES: {
    TYPE_LOCKED_ON_TRANSACTION_SURFACE:
      'Los tipos con factura de origen (notas de crédito/débito) y las órdenes de venta se crean desde Facturas.',
    APPLY_TAX: 'Aplicar ITBIS',
    MARK_TAX_EXEMPT: 'Quitar ITBIS',
    TAX_EXEMPT_SHORT: 'Sin ITBIS',
    TAX_TOGGLE_HINT: 'Presiona este botón para quitar el ITBIS de esta línea',
    CUSTOMER_TAX_EXEMPT_BADGE: 'Cliente exento de ITBIS',
    KDS_ORDER_WILL_BE_CREATED: 'Flujo de KDS activado al guardar esta factura',
    FROM_ORDER_ABONO_TITLE: 'Abono recibido en la orden {{orderNumber}}',
    FROM_ORDER_ABONO_BODY:
      'El abono de {{amount}} ya fue cobrado y aparece aplicado como línea negativa. El restante a cobrar es lo que se muestra en el resumen y en el modal de pago.',
    FROM_ORDER_LABEL: 'Orden de origen',
    APPLIED_DEPOSIT_LABEL: 'Abono aplicado',
    REMAINING_TO_COLLECT: 'Restante a cobrar:',
    TAB_PAYMENTS: 'Pagos',
    TAB_QUOTES: 'Cotización de origen',
    TAB_ACCOUNTING: 'Vista contable',
    PAYMENT_DETAIL_TITLE: 'Detalle del pago',
    PAYMENT_DETAIL_TAB: 'Detalle',
    PAYMENT_DETAIL_BANK: 'Banco',
    PAYMENT_DETAIL_CURRENCY: 'Moneda',
    PAYMENT_FOR_INVOICE: 'Pago de factura {{invoiceNumber}}',
    REF_ABONO_ORDER: 'Abono orden #{{shortId}}',
    REF_VALE: 'Vale de cliente',
    KDS_ORDERS_FOR_INVOICE: 'Órdenes del KDS para la factura',
    KDS_HAS_ORDERS: 'Tiene órdenes en KDS',
    KDS_NO_ORDERS: 'Sin órdenes en KDS',
    KDS_ITEM_STATUS: 'Estado del ítem',
    KDS_VIEW_ORDERS: 'Ver órdenes del KDS',
    KDS_STATUS_ACTIVE: 'Activa',
    KDS_STATUS_COMPLETED: 'Completada',
    KDS_STATUS_CANCELLED: 'Cancelada',
    KDS_CANCELLATION_REASON: 'Motivo de cancelación',
    SAVE_TICKET: 'Guardar',
    TITLE: 'Facturación',
    SUBTITLE: 'Gestiona las facturas de tu negocio',
    QUOTES_TITLE: 'Cotizaciones',
    QUOTES_SUBTITLE: 'Gestiona las cotizaciones de tu negocio',
    CREATE_QUOTE: 'Crear Cotización',
    CONDUCES_TITLE: 'Conduces',
    CONDUCES_SUBTITLE: 'Gestiona los conduces de tu negocio',
    CREATE_CONDUCE: 'Crear Conduce',
    ADD_INVOICE: 'Nueva Factura',
    ADD_QUOTES: 'Nueva Cotización',
    ADD_CONDUCES: 'Nuevo Conduce',
    PRINT_LABEL_INVOICE: 'Factura',
    PRINT_LABEL_QUOTE: 'Cotización',
    PRINT_LABEL_CONDUCE: 'Conduce',
    PRINT_LABEL_CREDIT_NOTE: 'Nota de Crédito',
    PRINT_LABEL_PRE_INVOICE: 'Pre Factura',
    PRINT_LABEL_TERM_INVOICE: 'Término de la Factura',
    PRINT_LABEL_DUE_DATE: 'Fecha de Vencimiento',
    PRINT_LABEL_TERM_QUOTE: 'Término de la Cotización',
    PRINT_LABEL_STATUS: 'Estado',
    PRINT_LABEL_STATUS_UNPAID: 'PENDIENTE DE PAGO',
    FREE_ITEM_CREATE_CTA: 'Crear ítem libre',
    FREE_ITEM_BADGE: 'Libre',
    FREE_ITEM_DESCRIPTION_PLACEHOLDER: 'Describe el producto o servicio…',
    FREE_ITEM_TAX_REQUIRED: 'Selecciona un impuesto para el ítem libre.',
    SEARCH_BY_QUOTE: 'Buscar por cotización...',
    SEARCH_BY_CONDUCE: 'Buscar por conduce...',
    BULK_IMPORT: 'Importación Masiva de Facturas',
    INVOICE_NUMBER: 'Número de Factura',
    INVOICE_DATE: 'Fecha de Factura',
    INVOICE_DETAILS: 'Detalles de la Factura',
    COMPANY_LOGO: 'Logo de la Empresa',
    NCF: 'NCF',
    PAYMENT_TYPE: 'Tipo de Pago',
    PAYMENT_METHODS: 'Métodos de pago',
    VIEW_PAYMENT_METHODS: 'Ver',
    PAYMENT_METHODS_USED: 'Métodos de pago usados',
    PAYMENT_METHOD_CREDIT_NOTE: 'Nota de crédito',
    SELECT: 'Seleccionar',
    CREDIT_ACCOUNT: 'Cuenta de Crédito',
    PAYMENT_DATE: 'Fecha de Pago',
    DUE_DATE: 'Fecha de Vencimiento',
    DELIVERY_FEE: 'Costo de delivery',
    DELIVERY_TAX: 'ITBIS de entrega',
    DELIVERY_METHOD: 'Método de delivery',
    DELIVERY_DRIVER: 'Chofer de delivery',
    DELIVERY_DRIVER_PHONE: 'Teléfono del chofer',
    DELIVERY_ADDRESS: 'Dirección / coordenadas de delivery',
    DELIVERY_ADDRESS_PLACEHOLDER: 'Ej: Calle 123, Santo Domingo o 18.47,-69.90',
    SHIPPING_ADDRESS: 'Dirección de envío',
    CUSTOMER_RNC: 'RNC del Cliente',
    CUSTOMER_PHONE: 'Teléfono del Cliente',
    CUSTOMER_EMAIL: 'Email del Cliente',
    SUBTOTAL: 'Subtotal',
    DISCOUNT: 'Descuento',
    CUSTOMER_INFORMATION: 'Información del Cliente',
    SEARCH_CUSTOMER: 'Buscar Cliente',
    SEARCH_CUSTOMER_PLACEHOLDER: 'Buscar por nombre, RNC, o teléfono...',
    SEARCH_PLACEHOLDER: 'Buscar por número de factura...',
    SEARCH_BY_CLIENT: 'Buscar por cliente...',
    SEARCH_BY_NCF: 'Buscar por NCF...',
    RNC: 'RNC',
    CEDULA: 'Cédula',
    INVOICE_ITEMS: 'Artículos',
    REF: 'Ref',
    TAX_PERCENT: 'Tax %',
    DISC_PERCENT: 'Desc %',
    SEARCH_PRODUCT: 'Buscar por nombre, código, barcode o variantes...',
    SEARCH_PRODUCT_FREE_ONLY:
      'Describe el ítem en cada línea (sin acceso al inventario)',
    SEARCH_PRODUCT_FREE_ONLY_UPGRADE_TOOLTIP:
      'Activa Inventario avanzado para buscar productos del catálogo',
    SUMMARY_COUPON_LOCKED_TOOLTIP:
      'Activa el addon Promociones en tu plan para aplicar cupones',
    SUMMARY_DISCOUNT_LOCKED_TOOLTIP:
      'Tu rol no tiene el permiso "Aplicar Descuentos en Facturas". Pide a un administrador que lo habilite en Configuración → Roles y Permisos.',
    SUMMARY_DISCOUNT_EMPTY_CART_TOOLTIP:
      'Agrega ítems a la factura para aplicar un descuento.',
    SUMMARY_LINE_DISCOUNT_LABEL: 'Descuento items',
    SUMMARY_INVOICE_DISCOUNT_LABEL: 'Descuento',
    ADD_ITEM: 'Incluir otro ítem',
    SUMMARY_TITLE: 'Resumen',
    BROWSE_PRODUCTS: 'Buscar productos',
    BROWSE_COLUMNS: 'Columnas',
    ADDITIONAL_INFORMATION: 'Información Adicional',
    ADD_NOTES_PLACEHOLDER: 'Agregar notas para esta factura...',
    TERMS_AND_CONDITIONS: 'Términos y Condiciones',
    ADD_TERMS_PLACEHOLDER: 'Agregar términos y condiciones...',
    INVOICE_SUMMARY: 'Resumen',
    TOTAL_ITEMS: 'Total de Artículos',
    TOTAL_QUANTITY: 'Cantidad Total',
    SAVE_AND_PAY: 'Cobrar',
    PAY: 'Cobrar',
    NEW_INVOICE: 'Nueva Factura',
    NEW_QUOTE: 'Nueva Cotización',
    NEW_CONDUCE: 'Nuevo Conduce',
    NEW_CREDIT_NOTE: 'Nueva Nota de Crédito',
    INVOICE_TYPE: 'Tipo de Factura',
    SELECT_TYPE: 'Seleccionar tipo',
    CASH: 'Contado',
    CREDIT_15_DAYS: 'Crédito 15 días',
    CREDIT_30_DAYS: 'Crédito 30 días',
    CREDIT_60_DAYS: 'Crédito 60 días',
    CONSUMO: 'Consumo',
    FISCAL_CREDIT: 'Crédito Fiscal',
    CREDIT_NOTE: 'Nota de Crédito',
    SIMPLE_INVOICE: 'Pre Factura',
    QUOTE: 'Cotización',
    CONDUCE: 'Conduce',
    STATUS: 'Estado',
    SELECT_STATUS: 'Seleccionar estado',
    CLIENT: 'Cliente',
    CASHIER: 'Cajero',
    SELLER: 'Vendedor',
    STATUS_DRAFT: 'Borrador',
    STATUS_DUE: 'Por Pagar',
    STATUS_PARTIAL: 'Parcial',
    STATUS_PENDING: 'Pendiente',
    STATUS_PENDING_PAYMENT: 'Pendiente de Pago',
    KDS_STATUS_DISABLED: 'KDS: Deshabilitado',
    KDS_STATUS_NOT_CREATED: 'KDS: No creado',
    KDS_STATUS_QUEUED: 'KDS: En cola',
    KDS_STATUS_IN_PROGRESS: 'KDS: En preparación',
    KDS_STATUS_DONE: 'KDS: Listo',
    STATUS_PAID: 'Pagada',
    STATUS_CANCELLED: 'Anulada',
    TYPE_TICKET: 'Ticket',
    TYPE_INVOICE: 'Factura',
    ALL_STATUSES: 'Todos los Estados',
    ALL_TYPES: 'Todos los Tipos',
    NO_INVOICES: 'No se encontraron facturas',
    NO_CONDUCES: 'No se encontraron conduces',
    NO_PENDING_INVOICES: 'No hay facturas pendientes',
    NO_PAYMENTS_FOUND: 'No se encontraron pagos',
    DELETE_CONFIRMATION_TITLE: 'Eliminar Factura',
    DELETE_CONFIRMATION_DESCRIPTION:
      '¿Estás seguro de que deseas eliminar la factura {{number}}? Esta acción se puede revertir.',
    INVOICE_TERM: 'Término de la Factura',
    SELECT_DOC_TYPE: 'Seleccionar tipo de documento',
    SELECT_INVOICE_TYPE: 'Seleccionar tipo de factura',
    FINAL_CONSUMER: 'Consumidor Final',
    CREDIT_90_DAYS: 'Crédito 90 días',
    ERROR_SELECT_CUSTOMER: 'Por favor selecciona un cliente',
    ERROR_ADD_ITEMS: 'Por favor agrega al menos un artículo',
    ERROR_SELECT_DELIVERY_DRIVER:
      'Por favor selecciona un repartidor para la entrega',
    ERROR_CREDIT_LIMIT_REQUIRED:
      'El cliente no tiene un límite de crédito asignado. Debe asignar un límite de crédito antes de crear facturas a crédito.',
    CREDIT_LIMIT_HINT:
      'Asigne un límite de crédito al cliente para habilitar opciones de crédito.',
    ASSIGN_CREDIT_HINT:
      'El negocio permite facturas a crédito, pero este cliente no tiene un límite asignado. Haz clic aquí para asignarle un crédito.',
    CREDIT_RNC_MODAL_TITLE: 'RNC requerido para crédito fiscal',
    CREDIT_RNC_MODAL_DESCRIPTION:
      'Esta factura de crédito fiscal requiere el RNC del cliente. Ingrésalo para validarlo y guardarlo en la ficha del cliente.',
    ERROR_SAVE_INVOICE:
      'Error al guardar la factura. Por favor intenta de nuevo.',
    PREVIOUS_INVOICE: 'Factura Anterior',
    ERROR_INVOICE_NUMBER_REQUIRED: 'El número de factura es requerido',
    ERROR_INVOICE_DATE_REQUIRED: 'La fecha de factura es requerida',
    ERROR_ITEMS_REQUIRED:
      'Debe agregar al menos un item con descripción y cantidad mayor a 0',
    ERROR_REMOVE_UNAVAILABLE_ITEMS:
      'Debe eliminar los productos sin stock disponible antes de guardar',
    ERROR_ITEMS_QUANTITY_REQUIRED:
      'Todos los items con descripción deben tener cantidad mayor a 0',
    CONFIRM_UPDATE_DRAFT:
      '¿Está seguro que desea actualizar este borrador de factura?',
    CONFIRM_SAVE_DRAFT:
      '¿Está seguro que desea guardar este borrador de factura?',
    CONFIRM_SAVE_QUOTE: '¿Está seguro que desea guardar esta cotización?',
    CONFIRM_SAVE_CONDUCE: '¿Está seguro que desea guardar este conduce?',
    CONDUCE_RETURN_TITLE: 'Devolver ítems del conduce',
    CONDUCE_RETURN_SUBTITLE:
      'Devuelve mercancía al inventario. Si devuelves todas las cantidades el conduce queda anulado.',
    CONDUCE_RETURN_ACTION: 'Devolver / Anular',
    CONDUCE_STATUS_PENDING: 'Pendiente',
    CONDUCE_STATUS_INVOICED: 'Factura creada',
    CONDUCE_CREATE_SIMILAR_INVOICE: 'Crear factura similar',
    CONDUCE_NUMBER: 'Número de Conduce',
    CONDUCE_DATE: 'Fecha de Conduce',
    CONDUCE_TYPE_LABEL: 'Tipo de Conduce',
    CONDUCE_STATUS_PROCESSED: 'Procesada',
    CONDUCE_STATUS_CANCELLED: 'Anulado',
    CONDUCE_CONVERTED_USE_CREDIT_NOTE:
      'Este conduce ya está asociado a una factura. Para devolver mercancía, emite una nota de crédito sobre la factura.',
    CONDUCE_RETURN_QTY: 'Cantidad a devolver',
    CONDUCE_DISPATCHED: 'Despachado',
    CONDUCE_RETURN_ALL: 'Devolver todo (anular conduce)',
    CONDUCE_RETURN_SUBMIT: 'Devolver',
    CONDUCE_RETURN_SUCCESS: 'Ítems devueltos al inventario.',
    CONDUCE_CANCELLED_SUCCESS:
      'Conduce anulado; mercancía devuelta al inventario.',
    CONDUCE_RETURN_NONE:
      'Ingresa una cantidad a devolver en al menos una línea.',
    VALID_UNTIL: 'Válida Hasta',
    VALID_UNTIL_PLACEHOLDER: 'Sin vencimiento',
    QUOTE_VALID: 'Vigente',
    QUOTE_EXPIRED: 'Vencida',
    QUOTE_NO_EXPIRY: 'Sin Vencimiento',
    ITEM_OUT_OF_STOCK:
      'Producto sin stock disponible — no será incluido en el total',
    CONVERTED_TO_INVOICE: 'Factura generada',
    CREATE_INVOICE_FROM_QUOTE: 'Crear Factura',
    CREATE_INVOICE_FROM_CONDUCE: 'Convertir a Factura',
    CREATE_INVOICE: 'Crear Factura',
    CONFIRM_CREATE_CREDIT_INVOICE:
      '¿Está seguro que desea crear esta factura a crédito? El cliente tendrá un plazo para realizar el pago.',
    ERROR_INVOICE_NOT_FOUND: 'No se encontró la factura',
    ERROR_INVOICE_FULLY_PAID: 'Esta factura ya está pagada completamente',
    INVOICE_TOTAL: 'Total de la Factura',
    AMOUNT_DUE: 'Monto Pendiente',
    NEXT_INVOICE: 'Factura Siguiente',
    NCF_NOT_AVAILABLE: 'No hay NCF disponible para este tipo de factura',
    NCF_SETUP_REQUIRED: 'Es necesario configurar las secuencias de NCF',
    NCF_SETUP_DESCRIPTION: 'Configure las secuencias para generar facturas',
    SETUP_NCF: 'Configurar NCF',
    TAX: 'Impuesto',
    // Modifier selection
    HAS_MODIFIERS: 'Con modificadores',
    SELECT_MODIFIERS_MODAL_TITLE: 'Seleccionar Modificadores',
    SELECT_MODIFIERS_MODAL_DESCRIPTION:
      'Seleccione las opciones de modificadores para este producto',
    REQUIRED_MODIFIER: 'Requerido',
    OPTIONAL_MODIFIER: 'Opcional',
    MIN_SELECTIONS_LABEL: 'Mín: {{min}}',
    MAX_SELECTIONS_LABEL: 'Máx: {{max}}',
    MODIFIER_PRICE_DELTA: '+{{price}}',
    NO_MODIFIER_SELECTED: 'Sin seleccionar',
    SELECTED_MODIFIERS_SUMMARY: 'Modificadores: {{count}}',
    MODIFIER_VALIDATION_MIN:
      'Debe seleccionar al menos {{min}} opción(es) en "{{group}}"',
    MODIFIER_VALIDATION_MAX:
      'Puede seleccionar máximo {{max}} opción(es) en "{{group}}"',
    // Cancellation
    CANCEL_INVOICE: 'Anular Factura',
    CANCELLING_INVOICE: 'Anulando...',
    CANCEL_INVOICE_TITLE: 'Anular Factura',
    CANCEL_DRAFT_DESCRIPTION:
      'Esta factura en borrador será eliminada permanentemente. Esta acción no se puede deshacer.',
    CANCEL_NON_DRAFT_DESCRIPTION:
      'Esta factura será anulada y los productos serán devueltos al inventario. Deberá proporcionar una razón para la anulación.',
    CONFIRM_CANCEL_PENDING_PAYMENT:
      'Esta factura está pendiente de pago. Será anulada y los productos serán devueltos al inventario. ¿Desea continuar?',
    CONFIRM_ANNUL_DESCRIPTION:
      'Al anular esta factura se anulará también su e-CF: se restaurará el inventario, se liberarán los pagos y el comprobante ya NO será enviado a la DGII por el proceso de lote e-CF. Esta acción no se puede deshacer. ¿Desea continuar?',
    CANCELLATION_REASON: 'Razón de la anulación',
    CANCELLATION_REASON_PLACEHOLDER:
      'Escriba la razón por la cual se está anulando esta factura...',
    CANCELLATION_REASON_REQUIRED:
      'Debe proporcionar una razón para la anulación',
    CANCEL_SUCCESS_DRAFT: 'Factura eliminada permanentemente',
    CANCEL_SUCCESS_NON_DRAFT: 'Factura anulada y stock devuelto al inventario',
    CANCELLING: 'Anulando...',
    CANCELLED_BY: 'Anulada por',
    CANCELLED_AT: 'Fecha de anulación',
    DRAFT_CANCELLED_BY_USER: 'Cliente decidió no continuar con la compra',
    // Cashier workflow
    PENDING_PAYMENT_CREATED:
      'Factura creada y pendiente de pago. El cajero procesará el cobro.',
    PROCESS_PAYMENT: 'Procesar Pago',
    SEND_TO_CASHIER: 'Enviar a Caja',
    SEND_TO_CASHIER_CONFIRM_TITLE: 'Enviar factura a caja',
    SEND_TO_CASHIER_CONFIRM_DESCRIPTION:
      '¿Enviar esta factura a caja? Quedará como pendiente de pago; la caja completará el cobro.',
    SENT_TO_CASHIER_TOAST:
      'Ticket enviado a caja. El número fiscal se generará al cobrar.',
    INVOICE_ALREADY_WITH_CASHIER:
      'Este ticket ya fue enviado a caja o procesado; no puede cargarse en POS.',
    PENDING_CASHIER_TAB: 'Pendiente de Cobro Contado',
    FINALIZE_INVOICE: 'Cobrar factura',
    SAVE_AND_PRINT: 'Guardar e Imprimir',
    SAVE_AND_PRINT_CONFIRM_TITLE: 'Guardar factura e imprimir',
    SAVE_AND_PRINT_CONFIRM_DESCRIPTION:
      '¿Guardar esta factura como pendiente de pago e imprimirla? El recibo llevará la marca "PENDIENTE DE PAGO" hasta que la caja registre el cobro.',
    RETURN_TO_SELLER: 'Devolver al vendedor',
    RETURN_TO_SELLER_CONFIRM_TITLE: '¿Devolver el ticket al vendedor?',
    RETURN_TO_SELLER_CONFIRM_DESCRIPTION_POS:
      'El vendedor podrá editar el ticket. Se removerá de tu cola y reaparecerá en sus pestañas de POS. Se le notificará por la bandeja.',
    RETURN_TO_SELLER_CONFIRM_DESCRIPTION_INVOICE:
      'El vendedor podrá editar la factura. Se removerá de tu cola y reaparecerá en Facturas para su edición. Se le notificará por la bandeja.',
    RETURN_TO_SELLER_CONFIRM_DESCRIPTION_RESTAURANT:
      'El mesero podrá editar la cuenta. Se removerá de tu cola y volverá a estar disponible en el módulo de restaurante. Se le notificará por la bandeja.',
    RETURN_TO_SELLER_CONFIRM_DESCRIPTION_DEFAULT:
      'El vendedor podrá editar el ticket. Se removerá de tu cola y se le notificará por la bandeja.',
    RETURN_TO_SELLER_SUCCESS: 'Ticket devuelto al vendedor.',
    RETURN_TO_SELLER_ERROR: 'No se pudo devolver el ticket al vendedor.',
    PENDING_CASHIER_REJECT_ALL: 'Rechazar todos',
    PENDING_CASHIER_REJECT_ALL_CONFIRM_TITLE:
      '¿Rechazar todos los tickets pendientes?',
    PENDING_CASHIER_REJECT_ALL_CONFIRM_DESCRIPTION:
      'Eliminará los {{count}} tickets en borrador de esta página. Esta acción no se puede deshacer.',
    PENDING_CASHIER_REJECT_ALL_SUCCESS:
      '{{count}} tickets pendientes eliminados.',
    PENDING_CASHIER_REJECT_ALL_ERROR:
      'No se pudieron eliminar algunos tickets ({{failed}} de {{total}}).',
    PENDING_PAYMENT_PRINTED:
      'Factura impresa y pendiente de pago. Caja completará el cobro.',
    RNC_VALIDATION_ERROR: 'El RNC/Cédula del cliente no es válido',
    DELETE_SUCCESS: 'Factura eliminada exitosamente',
    DELETE_ERROR: 'Error al eliminar la factura',
    CREATE_SUCCESS: 'Factura creada exitosamente',
    CREATE_ERROR: 'Error al crear la factura',
    UPDATE_SUCCESS: 'Factura actualizada exitosamente',
    UPDATE_ERROR: 'Error al actualizar la factura',
    ECF_OVERAGE_TOAST_TITLE: 'e-CF creado · excedente facturado',
    ECF_OVERAGE_TOAST_DESCRIPTION:
      'Superaste el límite de tu plan. Se acumularán {{units}} × RD${{rate}} = RD${{amount}} en tu próxima factura.',
    DEBIT_NOTE: 'Nota de Débito',
    DEBIT_NOTE_PARENT_TITLE: 'Factura de Origen',
    DEBIT_NOTE_PARENT_HELP:
      'Selecciona la factura original a la que se le aplica este cargo adicional. La nota de débito quedará fiscalmente vinculada al NCF original.',
    DEBIT_NOTE_PARENT_PLACEHOLDER: 'Buscar por NCF o número de factura…',
    DEBIT_NOTE_PARENT_REQUIRED:
      'Selecciona la factura original antes de guardar la nota de débito.',
    DEBIT_NOTE_INVOICE_NOT_FOUND:
      'No se encontró ninguna factura con ese NCF / número.',
    WRITE_OFF_ACTION: 'Ajustar saldo y/o pago de Factura',
    WRITE_OFF_TITLE: 'Ajustar saldo y/o pago pendiente',
    WRITE_OFF_DESCRIPTION:
      'Ajusta el saldo pendiente de esta factura. Según el estado contable la acción emite un asiento contable o solo corrige el pago; se detalla debajo antes de aplicar.',
    WRITE_OFF_DESCRIPTION_WRITE_OFF:
      'Esta factura tiene un saldo por cobrar real en la contabilidad — típicamente una factura a crédito que el cliente ya no pagará (cliente inubicable, disputa cerrada, deuda incobrable). Al aplicar, se emite un asiento contable Dr Gasto por Incobrables / Cr Cuentas por Cobrar por el monto indicado, reduciendo el saldo del subledger de AR. Queda un log inmutable con tu usuario, la razón y el comentario.',
    WRITE_OFF_DESCRIPTION_WRITE_OFF_NO_ACCOUNTING:
      'Esta factura tiene un saldo pendiente que el cliente ya no pagará (cliente inubicable, disputa cerrada, deuda incobrable). Al aplicar, se cierra el saldo de la factura por el monto indicado y queda un log inmutable con tu usuario, la razón y el comentario.',
    WRITE_OFF_DESCRIPTION_PAYMENT_CORRECTION:
      'La contabilidad de esta factura ya está balanceada — el pago se registró por debajo del total por un ajuste de céntimos. Al aplicar, se corrige el monto del pago para que coincida con lo cobrado. No se emite ningún asiento contable adicional; queda un log de auditoría con tu usuario.',
    WRITE_OFF_DESCRIPTION_MULTI_ALLOCATION_FALLBACK:
      'La contabilidad ya está balanceada, pero el pago cubre múltiples facturas. Por seguridad, no se modifica el pago original — solo se registra un log de auditoría que cierra el saldo pendiente. No se emite ningún asiento contable.',
    WRITE_OFF_CREDIT_NOTE_HINT:
      'Esta factura ya tiene notas de crédito aplicadas por {{amount}}. El saldo pendiente que ves abajo es el neto después de la nota de crédito; la baja solo cubre lo que queda por cobrar.',
    WRITE_OFF_OUTSTANDING: 'Saldo pendiente',
    WRITE_OFF_AMOUNT: 'Monto a dar de baja',
    WRITE_OFF_REASON: 'Razón',
    WRITE_OFF_REASON_DISPUTE_SETTLED: 'Disputa resuelta',
    WRITE_OFF_REASON_SMALL_BALANCE: 'Saldo pequeño',
    WRITE_OFF_REASON_BAD_DEBT: 'Deuda incobrable',
    WRITE_OFF_REASON_GOODWILL: 'Cortesía / buena voluntad',
    WRITE_OFF_REASON_OTHER: 'Otra',
    WRITE_OFF_COMMENT: 'Comentario (obligatorio)',
    WRITE_OFF_COMMENT_PLACEHOLDER:
      'Ej: Cliente acordó por vía legal el 2026-06-30; se perdona el saldo restante.',
    WRITE_OFF_CONFIRM_VERIFY_LABEL:
      'Confirmo que revisé el saldo y este monto es correcto',
    WRITE_OFF_CONFIRM_TYPE_LABEL:
      'Escribe {{token}} para confirmar la baja de saldo',
    WRITE_OFF_CONFIRM_TYPE_TOKEN: 'DAR DE BAJA',
    WRITE_OFF_SUBMIT: 'Aplicar baja de saldo',
    WRITE_OFF_SUCCESS_PAID:
      'Saldo dado de baja — la factura queda como pagada.',
    WRITE_OFF_SUCCESS_PARTIAL:
      'Saldo dado de baja parcialmente — la factura sigue pendiente.',
    SETTLEMENT_ADJUSTMENTS: 'Ajustes (redondeo / baja de saldo)',
    PRICE_BELOW_MIN_CHIP: 'Precio bajo mínimo',
    PRICE_BELOW_MIN_TOOLTIP_TIER:
      'Precio {{price}} está por debajo del mínimo {{floor}} de la tarifa "{{tier}}" para este cliente.',
    PRICE_BELOW_MIN_TOOLTIP_SELLING:
      'Precio {{price}} está por debajo del precio de venta {{floor}} del producto.',
    PRICE_BELOW_MIN_BANNER_TITLE:
      'Uno o más ítems tienen precio por debajo del mínimo',
    PRICE_BELOW_MIN_BANNER_SUBTITLE:
      'Sin el permiso "Vender por debajo del precio mínimo" no puedes guardar la factura. Ajusta los precios o solicita autorización.',
    PRICE_BELOW_MIN_ADMIN_SUBTITLE:
      'Tu permiso te permite continuar, pero este precio queda registrado como venta bajo el mínimo del cliente.',
    PRICE_BELOW_MIN_BANNER_LINE:
      '{{code}} — {{name}}: RD${{submitted}} < mínimo RD${{floor}}{{tierSuffix}}',
    PRICE_BELOW_MIN_BLOCK_TOAST:
      'Ajusta los precios por debajo del mínimo o solicita autorización antes de continuar.',
    SOURCE_BADGE_BULK_IMPORT: 'Importado',
  },
  INVOICE_BULK_IMPORT: {
    TITLE: 'Importación Masiva de Facturas',
    SUBTITLE: 'Importa facturas desde un archivo CSV para migración',
    DOWNLOAD_TEMPLATE: 'Descargar Plantilla',
    UPLOAD_CSV: 'Subir Archivo CSV',
    UPLOAD_DESCRIPTION:
      'Selecciona un archivo CSV con los datos de las facturas a importar',
    STEP_UPLOAD: 'Subir Archivo',
    STEP_SETTINGS: 'Configuración',
    STEP_MAPPING: 'Mapear Campos',
    STEP_VALIDATION: 'Validación',
    STEP_PROCESSING: 'Procesando',
    STEP_COMPLETE: 'Completado',
    INVOICE_DATE: 'Fecha de Factura',
    INVOICE_DATE_DESCRIPTION:
      'Fecha que se asignará a todas las facturas importadas en caso de no especificarse en el archivo',
    BRANCH_OFFICE: 'Sucursal',
    BRANCH_OFFICE_DESCRIPTION: 'Sucursal donde se crearán las facturas',
    ALLOW_NEGATIVE_STOCK: 'Permitir Stock Negativo',
    ALLOW_NEGATIVE_STOCK_DESCRIPTION:
      'Si está activado, se permitirá importar facturas aunque el inventario quede en negativo',
    DEDUCT_INVENTORY: 'Descontar Inventario',
    DEDUCT_INVENTORY_DESCRIPTION:
      'Si está activado, las facturas importadas descontarán las cantidades del inventario. Si está desactivado, el inventario no se verá afectado (útil para facturas históricas ya reconciliadas).',
    USE_GENERIC_CLIENT: 'Usar Cliente Genérico',
    USE_GENERIC_CLIENT_DESCRIPTION:
      'Si está activado, las filas sin información de cliente se asignarán al Cliente Genérico',
    PAID_AMOUNT: 'Monto Pagado',
    PAYMENT_METHOD: 'Método de Pago',
    PRODUCT_CODE: 'Código del Producto (SKU / Código de Barras)',
    VALIDATING: 'Validando datos...',
    VALIDATING_DESCRIPTION: 'Estamos verificando los datos del archivo CSV',
    VALIDATION_COMPLETE: 'Validación Completada',
    TOTAL_ROWS: 'Filas Totales',
    VALID_ROWS: 'Filas Válidas',
    INVALID_ROWS: 'Filas con Errores',
    WARNING_ROWS: 'Filas con Advertencias',
    TOTAL_INVOICES: 'Facturas a Crear',
    INVOICE_GROUPS: 'Grupos de Facturas',
    NEW_CUSTOMERS: 'Clientes Nuevos',
    EXISTING_CUSTOMERS: 'Clientes Existentes',
    MISSING_PRODUCTS: 'Productos No Encontrados',
    INSUFFICIENT_STOCK: 'Productos con Stock Insuficiente',
    SERIAL_REQUIRED_NOT_ALLOWED:
      'Productos con serial requerido (no se permiten en importación masiva)',
    TOTAL_PAID: 'Total a Pagar',
    ITEMS_COUNT: '{{count}} artículos',
    HAS_ERRORS: 'Con errores',
    NO_ERRORS: 'Sin errores',
    CONFIRM_IMPORT: 'Confirmar Importación',
    CONFIRM_IMPORT_DESCRIPTION:
      '¿Estás seguro de que deseas importar {{count}} facturas? Esta acción no se puede deshacer.',
    PROCESSING: 'Procesando importación...',
    PROCESSING_DESCRIPTION:
      'Las facturas se están creando. Por favor, no cierre esta ventana.',
    IMPORT_COMPLETE: 'Importación Completada',
    IMPORT_COMPLETE_DESCRIPTION: 'Se han importado exitosamente las facturas.',
    ITEMS_SUCCEEDED: 'Facturas creadas exitosamente',
    ITEMS_FAILED: 'Facturas con errores',
    IMPORT_FAILED:
      'La importación falló. Por favor, revise los errores e intente nuevamente.',
    CANCEL_IMPORT: 'Cancelar Importación',
    CANCEL_IMPORT_CONFIRM:
      '¿Estás seguro de que deseas cancelar la importación en progreso?',
    NO_VALID_ROWS:
      'No hay filas válidas para importar. Por favor, revise los errores.',
    MAP_FIELDS: 'Mapear Campos',
    MAP_FIELDS_DESCRIPTION:
      'Asocia las columnas de tu CSV con los campos del sistema',
    CSV_COLUMN: 'Columna CSV',
    TARGET_FIELD: 'Campo del Sistema',
    UNMAPPED: 'Sin asignar',
    REQUIRED_FIELD: 'Campo requerido',
    CUSTOMER_KEY: 'Clave del Cliente',
    DEFAULT_INVOICE_TYPE: 'Tipo de Factura Predeterminado',
    DEFAULT_INVOICE_TYPE_DESCRIPTION:
      'Se usará este tipo para las filas que no especifiquen uno en el archivo',
    DEFAULT_PAYMENT_METHOD: 'Método de Pago Predeterminado',
    DEFAULT_PAYMENT_METHOD_DESCRIPTION:
      'Se usará este método para las filas que no especifiquen uno en el archivo',
    SELECT_INVOICE_TYPE: 'Seleccionar tipo de factura',
    SELECT_PAYMENT_METHOD: 'Seleccionar método de pago',
    INVOICE_TYPE_CONSUMO: 'Consumo',
    INVOICE_TYPE_FISCAL_CREDIT: 'Crédito Fiscal',
    INVOICE_TYPE_INVOICE: 'Pre Factura',
    INVOICE_TYPE_QUOTE: 'Cotización',
    QUOTE_NOT_SUPPORTED_YET:
      'La importación masiva de cotizaciones aún no está disponible. Contacta a soporte.',
    RNC_REQUIRED_FOR_FISCAL_CREDIT:
      'RNC del cliente es requerido para Crédito Fiscal (B01/E31).',
    INVALID_TYPE:
      'Tipo de factura inválido "{{type}}". Valores permitidos: {{types}}.',
    NCF_MISSING_TITLE: 'No hay NCF registrados para este tipo',
    NCF_MISSING_DESCRIPTION:
      'Registra al menos un rango de NCF (B01/B02 para papel o E31/E32 para e-CF) antes de continuar con la importación.',
    NCF_MISSING_CTA: 'Ir a configuración de NCF',
    PM_CASH: 'Efectivo',
    PM_CREDIT_CARD: 'Tarjeta de Crédito',
    PM_DEBIT_CARD: 'Tarjeta de Débito',
    PM_TRANSFER: 'Transferencia',
    PM_CHECK: 'Cheque',
    PM_PAYPAL: 'PayPal',
    PM_ZELLE: 'Zelle',
    PM_CRYPTO: 'Criptomoneda',
    PM_OTHER: 'Otro',
    TEMPLATE_COL_RECEIPT_NUMBER: 'Número de Factura',
    TEMPLATE_COL_INVOICE_DATE: 'Fecha',
    RECEIPT_NUMBER: 'Número de Factura',
    TEMPLATE_COL_CUSTOMER_RNC: 'RNC del Cliente',
    TEMPLATE_COL_CUSTOMER_NAME: 'Nombre del Cliente',
    TEMPLATE_COL_INVOICE_TYPE: 'Tipo de Factura',
    TEMPLATE_COL_PRODUCT_CODE: 'Código del Producto',
    TEMPLATE_COL_PRODUCT_DESCRIPTION: 'Descripción del Producto',
    TEMPLATE_COL_QUANTITY: 'Cantidad',
    TEMPLATE_COL_UNIT_PRICE: 'Precio Unitario',
    TEMPLATE_COL_DISCOUNT: 'Descuento',
    TEMPLATE_COL_TAX_RATE: 'Tasa de Impuesto',
    TEMPLATE_COL_PAID_AMOUNT: 'Monto Pagado',
    TEMPLATE_COL_PAYMENT_METHOD: 'Método de Pago',
    TEMPLATE_COL_CREDIT_TERM: 'Término de Factura',
    CREDIT_TERM: 'Término de Factura',
    TEMPLATE_COL_SALESPERSON: 'Vendedor',
    SALESPERSON: 'Vendedor',
    TEMPLATE_COL_NOTES: 'Notas',
    // Validation error/warning messages
    VALIDATION_CUSTOMER_REQUIRED:
      'Se requiere el RNC del Cliente o el Nombre del Cliente',
    VALIDATION_PRODUCT_CODE_REQUIRED:
      'Se requiere el Código del Producto (código de barras, SKU o nombre)',
    VALIDATION_INVOICE_TYPE_REQUIRED: 'Se requiere el Tipo de Factura',
    VALIDATION_INVALID_INVOICE_TYPE:
      'Tipo de factura inválido "{{type}}". Válidos: {{valid}}',
    VALIDATION_QUANTITY_REQUIRED: 'La cantidad debe ser un número positivo',
    VALIDATION_UNIT_PRICE_REQUIRED:
      'El precio unitario debe ser un número no negativo',
    VALIDATION_DISCOUNT_INVALID: 'El descuento debe ser un número válido',
    VALIDATION_TAX_RATE_INVALID:
      'La tasa de impuesto debe ser un número válido',
    VALIDATION_PAID_AMOUNT_INVALID: 'El monto pagado debe ser un número válido',
    VALIDATION_INVALID_PAYMENT_METHOD:
      'Método de pago inválido "{{method}}". Válidos: {{valid}}',
    VALIDATION_PRODUCT_NOT_FOUND: 'Producto no encontrado: "{{code}}"',
    VALIDATION_CUSTOMER_RNC_NOT_FOUND:
      'Cliente con RNC "{{rnc}}" no encontrado — será auto-creado',
    VALIDATION_CUSTOMER_NAME_NOT_FOUND:
      'Cliente "{{name}}" no encontrado — será auto-creado',
    VALIDATION_INSUFFICIENT_STOCK:
      'Stock insuficiente para "{{product}}": actual {{current}}, necesario {{needed}}',
    VALIDATION_INSUFFICIENT_STOCK_ENABLE:
      'Stock insuficiente — active "Permitir stock negativo" para continuar',
    VALIDATION_NCF_NOT_CONFIGURED:
      'No hay NCF configurados para el tipo "{{type}}". Configúralos en Configuración → NCF antes de importar.',
    VALIDATION_ROW: 'Fila {{rowId}}',
    PROGRESS_INVOICE: 'Procesando factura {{processed}} de {{total}}',
    PROGRESS_COMPLETE: 'Completado',
  },
  PUBLIC_INVOICE: {
    INVOICE_NOT_FOUND: 'Factura no encontrada',
    INVALID_LINK: 'El enlace de la factura no es válido o ha expirado.',
    ERROR_LOADING:
      'No se pudo cargar la factura. Por favor, intente nuevamente.',
    THANK_YOU_MESSAGE: 'Gracias por su preferencia',
    POWERED_BY: 'Powered by Neogeniux',
    RENDERING_PDF: 'Preparando factura…',
    VIEW_DOCUMENT: 'Ver documento',
    HIDE_DOCUMENT: 'Ocultar documento',
    SAVE: 'Guardar',
    LABEL_DOCUMENT_NUMBER: 'No. de Documento',
    LABEL_DATE: 'Fecha',
    LABEL_CUSTOMER: 'Cliente',
    LABEL_PROVIDER: 'Proveedor',
    LABEL_TOTAL: 'Total',
  },
  PRODUCT_IMAGE_SEARCH: {
    TITLE: 'Sugerencias de imagen',
    SEARCHING: 'Buscando imágenes…',
    EMPTY: 'No se encontraron imágenes',
    UNVERIFIED: 'No verificado',
    GOOGLE_FALLBACK: 'Buscar en Google Imágenes',
    ADDED: 'Imagen agregada',
    IMPORT_FAILED: 'No se pudo agregar la imagen',
    REPORT: 'Reportar imagen incorrecta',
    REPORTED: 'Imagen reportada',
    REPORT_FAILED: 'No se pudo reportar la imagen',
  },
  CREDIT_INVOICING: {
    TERM_LABEL: 'Término de la Factura',
    TERM_PLACEHOLDER: 'Seleccionar tipo',
    /*
     * Purchases state the same concept as a sale, so the term lives in
     * this concept namespace rather than a third surface-scoped copy.
     * Distinct wording from TERM_LABEL only because the document is a
     * purchase ("de la Factura" reads as the customer invoice).
     *
     * The supplier-invoice control used to be labelled
     * `INVOICES.PAYMENT_TYPE` → "Tipo de Pago", which reads as payment
     * METHOD, not term — the reason the field looked missing.
     */
    PURCHASE_TERM_LABEL: 'Término de pago',
    PURCHASE_TERM_HINT:
      'Define la fecha de vencimiento: Cuentas por Pagar y el calendario la usan para saber cuándo se debe pagar.',
    DUE_DATE_FROM_TERM_HINT:
      'Se calcula a partir del término de pago y la fecha del documento.',
    CREDIT_LIMIT_HINT:
      'Asigne un límite de crédito al cliente para habilitar opciones de crédito.',
    CONFIRM_CREATE_TITLE: 'Confirmar',
    CONFIRM_CREATE_MESSAGE:
      '¿Está seguro que desea crear esta factura a crédito? El cliente tendrá un plazo para realizar el pago.',
    CREATE_INVOICE_BUTTON: 'Crear Factura',
    CREATED_SUCCESS: 'Factura a crédito creada correctamente.',
    CREATING: 'Creando factura...',
  },
  // Cross-surface: the NCF registry, the invoice-type selector and both
  // payment modals all explain the SAME unavailability, so the strings
  // live in one concept namespace rather than per-surface copies.
  NCF_AVAILABILITY: {
    RNC_MISSING_BADGE: 'No utilizable',
    RNC_MISSING_TITLE: 'Falta el RNC del negocio',
    REASON_BUSINESS_RNC_MISSING:
      'La DGII no permite emitir comprobantes sin el RNC del negocio. Esta secuencia quedará deshabilitada hasta que lo registres.',
    REASON_ECF_DISABLED:
      'La facturación electrónica (e-CF) está desactivada, por lo que los comprobantes electrónicos (E) no están disponibles.',
    REASON_NO_TAXES:
      'No hay impuestos configurados, por lo que no se puede emitir un comprobante fiscal.',
    REASON_NO_SEQUENCE:
      'El negocio no tiene una secuencia registrada para este comprobante.',
    CONFIGURE_RNC_CTA: 'Configurar el RNC',
    CONTACT_ADMIN: 'Pídele a un administrador que registre el RNC del negocio.',
    // Shown on /configuration/business itself — the one screen where
    // the operator can actually fix it.
    BUSINESS_PAGE_TITLE: 'Falta el RNC/Cédula',
    BUSINESS_PAGE_BODY:
      'Tienes {{count}} secuencia(s) de NCF, pero sin RNC/Cédula no podrás emitir comprobantes. Complétalo abajo.',
  },
  TAXPAYER_PICKER: {
    DETECTED_RNC: 'Detectamos un RNC',
    DETECTED_CEDULA: 'Detectamos una Cédula',
    VALIDATING: 'Validando contra DGII…',
    DGII_VALID_TITLE: 'DGII: {{name}}',
    DGII_INVALID_TITLE: 'No encontrado en DGII',
    DGII_INVALID_BODY:
      'Ese identificador no está registrado en DGII. Puedes usarlo sin registrar de todos modos.',
    ALREADY_REGISTERED: 'Ya registrado — seleccionar',
    REGISTER_FROM_DGII: 'Registrar',
    USE_ANYWAY: 'Usar sin registrar',
    NETWORK_ERROR: 'No se pudo validar con DGII. Reintenta.',
  },
  RECIPES: {
    RECIPE: 'Receta',
    RECIPES: 'Recetas',
    RAW_MATERIAL: 'Materia prima',
    RAW_MATERIALS: 'Materias primas',
    STOCK_UNIT_LABEL: 'Unidad de compra / inventario',
    STOCK_UNIT_HELP:
      'Cómo compras y almacenas este producto (LB, kg, litro, caja).',
    CONSUMPTION_UNIT_LABEL: 'Unidad de consumo',
    CONSUMPTION_UNIT_HELP:
      'Cómo lo consumes en tus recetas (g, ml, unidad). Si es igual a la unidad de compra, déjalo en blanco.',
    CONVERSION_FACTOR_LABEL: 'Factor de conversión',
    CONVERSION_FACTOR_HELP:
      'Cuántas unidades de consumo hay en 1 unidad de compra. Ej: 1 LB = 453.592 g, 1 kg = 1000 g, 1 caja = 24 unidades.',
    CONVERSION_EXAMPLE: '1 {{stock}} = {{factor}} {{consumption}}',
    UNITS_SECTION_TITLE: 'Unidades e inventario',
    UNITS_SECTION_HELP:
      'Configura cómo compras, almacenas, consumes y vendes este producto. El inventario siempre se lleva en la unidad de stock.',
    PURCHASE_UNIT_LABEL: 'Unidad de compra',
    PURCHASE_UNIT_HELP:
      'Cómo lo compras al proveedor (caja, saco, bulto). Déjalo en blanco si compras en la unidad de stock.',
    PURCHASE_CONVERSION_LABEL: 'Unidades de stock por 1 unidad de compra',
    PURCHASE_CONVERSION_HELP:
      'Ej: 1 caja = 24 botellas, 1 saco = 25 kg. Se usa al recibir una orden de compra para convertir a unidades de stock.',
    SALE_UNIT_LABEL: 'Unidad de venta',
    SALE_UNIT_HELP:
      'Cómo lo vendes directamente (oz, g, unidad). Si es igual a la unidad de stock, déjalo en blanco.',
    SALE_CONVERSION_LABEL: 'Unidades de venta por 1 unidad de stock',
    SALE_CONVERSION_HELP:
      'Ej: 1 L = 33.814 oz, 1 caja = 24 unidades. Se usa al vender directamente para descontar el equivalente en stock.',
    UNIT_ROW_STOCK: 'Stock',
    UNIT_ROW_PURCHASE: 'Compra',
    UNIT_ROW_CONSUMPTION: 'Consumo (recetas)',
    UNIT_ROW_SALE: 'Venta directa',
    MOVEMENT_STOCK_EQUIVALENT: 'Equivalente en stock',
    INGREDIENT: 'Ingrediente',
    INGREDIENTS: 'Ingredientes',
    HAS_RECIPE: 'Tiene receta',
    THIS_PRODUCT_IS_A_RECIPE: '¿Quieres vender este producto como receta?',
    THIS_PRODUCT_IS_A_RECIPE_HELP:
      'Actívalo si al vender este producto quieres descontar materias primas del inventario según los ingredientes que definas.',
    SAVE_PRODUCT_FIRST_TO_ADD_INGREDIENTS:
      'Guarda el producto primero. Al volver a esta página podrás agregar los ingredientes de la receta.',
    YIELD_LABEL: 'Rendimiento',
    YIELD_HELP:
      'Cuántas unidades vendibles produce una preparación de esta receta.',
    ADD_INGREDIENT: 'Agregar ingrediente',
    NO_INGREDIENTS_YET: 'Aún no hay ingredientes.',
    PICK_INGREDIENT: 'Elegir materia prima',
    ONLY_RAW_MATERIALS_HELP:
      'Aquí aparecerán solo los productos marcados como materia prima.',
    UNIT_LABEL: 'Unidad',
    QUANTITY_LABEL: 'Cantidad',
    EDIT_INGREDIENT: 'Editar ingrediente',
    CHOOSE_PRESENTATION: 'Presentación',
    CHOOSE_PRESENTATION_HELP:
      'Elige cómo consumes esta materia prima. La cantidad multiplica esta presentación.',
    PRESENTATION_EQUIVALENCE: '{{qty}} × {{label}} = {{stock}} {{unit}}',
    COST_PER_UNIT: 'Costo por unidad',
    TOTAL_COST: 'Costo total',
    COST_EXCEEDS_PRICE_WARN:
      'El costo por unidad supera el precio de venta. Considera actualizar el precio.',
    COST_EXCEEDS_PRICE_BLOCKED:
      'No se puede vender esta receta: el costo supera el precio de venta.',
    MANUFACTURE_TO_STOCK_LABEL:
      'Al vender también quiero reducir la cantidad de recetas',
    MANUFACTURE_TO_STOCK_HELP:
      'Al activarlo, cada venta de esta receta descuenta TANTO el inventario de la receta COMO su materia prima (ingredientes). Así sabes cuántas recetas has vendido además de consumir los ingredientes. Si lo dejas desactivado, al vender solo se descuentan los ingredientes (materia prima) como hasta ahora.',
    SUGGESTED_PRICE: 'Precio sugerido',
    APPLY_SUGGESTED_PRICE: 'Usar precio sugerido',
    APPLY_COMPUTED_COST: 'Actualizar costo del producto',
    USED_IN_N_RECIPES: 'Usada en {{count}} receta(s)',
    INGREDIENT_NOT_STOCK_TRACKED:
      'Un servicio no puede ser ingrediente de una receta.',
    CANNOT_REFERENCE_SELF:
      'Una receta no puede incluirse a sí misma como ingrediente.',
    INGREDIENT_CANNOT_BE_RECIPE:
      'Un ingrediente no puede ser a su vez una receta.',
    INGREDIENT_UNIT_MISMATCH:
      'La unidad del ingrediente debe coincidir con su unidad de inventario.',
    INGREDIENT_NOT_FOUND: 'El ingrediente seleccionado no existe.',
    BLOCK_SALE_BELOW_COST_LABEL:
      'Bloquear ventas cuando el costo de la receta supere el precio',
    BLOCK_SALE_BELOW_COST_HELP:
      'Al activarlo, no se pueden emitir facturas con recetas cuyo costo sea mayor al precio de venta.',
    SHOW_INGREDIENTS_ON_RECEIPT_LABEL:
      'Mostrar ingredientes en el recibo impreso',
    SHOW_INGREDIENTS_ON_RECEIPT_HELP:
      'Cuando está activo, cada ingrediente de la receta aparece como subtotal en el recibo.',
    SAVED_SUCCESS: 'Receta guardada correctamente.',
    DELETED_SUCCESS: 'Receta eliminada.',
    CONFIRM_DELETE_TITLE: 'Eliminar receta',
    CONFIRM_DELETE_MESSAGE:
      '¿Seguro que quieres eliminar la receta de este producto? El producto seguirá existiendo.',
    INGREDIENTS_PAGE_TITLE: 'Ingredientes de la receta',
    INGREDIENTS_PAGE_SUBTITLE:
      'Materias primas que se descuentan del inventario al vender este producto.',
    NO_RECIPE_YET:
      'Este producto aún no tiene receta configurada. Puedes definir sus ingredientes para descontar materias primas del inventario cuando lo vendas.',
    CONFIGURE_RECIPE_ACTION: 'Configurar receta',
    VIEW_INGREDIENTS_ACTION: 'Ver ingredientes',
    TUTORIAL_TITLE: '¿Cómo funcionan las recetas?',
    TUTORIAL_INTRO:
      'Convierte un producto en una receta cuando su venta descuenta materias primas del inventario (una hamburguesa consume pan, carne, queso).',
    TUTORIAL_STEP_1:
      '1. Activa "Este producto es una receta" y define el rendimiento (cuántas unidades vendibles produce una preparación).',
    TUTORIAL_STEP_2:
      '2. Agrega cada ingrediente con la cantidad exacta que consume una preparación (ej. 2 rebanadas de pan, 150 g de carne).',
    TUTORIAL_STEP_3:
      '3. Al vender, el sistema descuenta automáticamente cada ingrediente del inventario según la cantidad vendida.',
    TUTORIAL_EXAMPLE:
      'Ejemplo: Hamburguesa (rendimiento 1) = 2 pan + 150 g carne + 1 queso. Al vender 3 hamburguesas descuenta 6 pan, 450 g carne y 3 queso.',
    COMPOSITION_SUMMARY: '{{count}} {{label}}{{cost}}',
    COMPOSITION_COST: 'costo {{amount}}',
    EDIT_COMPOSITION: 'Editar composición',
  },
  PRICE_ENTRY: {
    MODE_LABEL: 'Modo de entrada de precios',
    MODE_HELP:
      'Cuando está activo, el precio ingresado ya incluye el impuesto (ITBIS). El sistema calcula el subtotal y el impuesto automáticamente.',
    MODE_NET: 'Precios netos (impuesto se suma)',
    MODE_GROSS: 'Precios con impuesto incluido',
    LINE_HEADER_GROSS: 'Precio (con ITBIS)',
  },
  PRODUCT_UNITS: {
    SECTION_TITLE: 'Presentaciones del producto',
    SECTION_HELP:
      'Configura cómo se compra, vende o consume el producto en distintas unidades. El inventario siempre se lleva en la unidad de stock; el sistema convierte cada operación al momento de facturar o recibir.',
    ADD_UNIT: 'Agregar presentación',
    NO_UNITS_YET:
      'Aún no has configurado presentaciones. La unidad de stock se agrega automáticamente.',
    UNIT_COLUMN: 'Unidad',
    IDENTITY_BADGE: 'Unidad base',
    IDENTITY_HELPER:
      'Esta fila coincide con la Unidad de compra / inventario configurada arriba. Cambia ese campo para editarla.',
    TUTORIAL_IDENTITY_HEADING: 'Unidad base',
    TUTORIAL_IDENTITY_BODY:
      'La primera fila (marcada como "Unidad base") refleja la Unidad de compra / inventario configurada arriba en el formulario. Sus campos (unidad, factor, costo, precio, código de barras, SKU, tarifas) no son editables aquí — se toman del producto. Para cambiarlos edita los campos correspondientes en la parte superior.',
    TUTORIAL_ROLES_HEADING: 'Casillas Comprar / Vender / Recetas',
    TUTORIAL_ROLES_BODY:
      'Cada rol es un interruptor de visibilidad: si Comprar está desmarcado, esta presentación no aparecerá en el selector de unidades de una orden de compra; si Vender está desmarcado, no aparecerá en POS ni en la factura; si Recetas está desmarcado, no podrás usarla como ingrediente. Marca solo los roles donde tenga sentido usar esta presentación.',
    TUTORIAL_DEFAULTS_HEADING: 'Estrella "por defecto"',
    TUTORIAL_DEFAULTS_BODY:
      'Dentro de cada rol, la estrella indica cuál presentación se selecciona automáticamente. Solo una fila puede ser predeterminada por rol (funciona como radio). Ejemplo: la estrella en Vender define qué presentación aparece al agregar el producto al carrito en POS; el operador puede cambiarla en la línea si vende en otra.',
    FACTOR_COLUMN: 'Equivalencia',
    ROLES_COLUMN: 'Usar para',
    PRICE_COLUMN: 'Precio',
    COST_COLUMN: 'Costo',
    BARCODE_COLUMN: 'Código de barras',
    ACTIONS_COLUMN: 'Acciones',
    ROLE_PURCHASE: 'Comprar',
    ROLE_SELL: 'Vender',
    ROLE_CONSUME: 'Recetas',
    DEFAULT_PURCHASE: 'Compra por defecto',
    DEFAULT_SELL: 'Venta por defecto',
    DEFAULT_CONSUME: 'Receta por defecto',
    FACTOR_HELPER_TEMPLATE: '1 {{unit}} = {{factor}} {{stockUnit}}',
    UNITS_PER_STOCK_HELPER: '1 {{stockUnit}} = {{qty}} {{unit}}',
    NAME_COLUMN: 'Nombre',
    NAME_PLACEHOLDER: 'Ej. 8 OZ',
    DUPLICATE_PRESENTATION:
      'Hay dos presentaciones con la unidad «{{unit}}» sin nombres distintos. Asigna un nombre único a cada una (ej. «16 OZ», «8 OZ») para diferenciarlas.',
    CONTAINS_COLUMN: 'Contiene ({{unit}})',
    CONTAINS_HELPER:
      '1 {{stockUnit}} = {{perStock}} {{unit}} · paquete de {{qty}} {{unit}}',
    CONTAINS_HELPER_MANUAL: 'Paquete de {{qty}} {{unit}}',
    CONTAINS_PER_BASE_LABEL: 'Cantidad por {{base}}',
    CONTAINS_PER_BASE_HELPER:
      '1 {{base}} = {{qty}} {{unit}} · el precio por {{unit}} se calcula solo',
    FACTOR_PLACEHOLDER: '1',
    PRICE_PLACEHOLDER: 'Deriva del precio base',
    COST_PLACEHOLDER: 'Deriva del costo base',
    COST_PER_CONTENT: 'Costo por {{unit}}: {{amount}}',
    PRICE_PER_CONTENT: 'Precio por {{unit}}: {{amount}}',
    BARCODE_PLACEHOLDER: 'Opcional',
    SKU_SUFFIX_LABEL: 'Sufijo SKU',
    SKU_SUFFIX_PLACEHOLDER: '-cj',
    PRICE_TIERS_LABEL: 'Tarifas por cantidad ({{count}})',
    PRICE_TIERS_MODAL_TITLE: 'Tarifas por cantidad — {{unit}}',
    BRANCH_PRICES_TITLE: 'Precios por sucursal',
    BRANCH_PRICES_HELP:
      'Deja la sucursal en blanco para un precio de respaldo aplicado a todas.',
    ADD_BRANCH_PRICE: 'Agregar precio por sucursal',
    BRANCH_FALLBACK: 'Todas las sucursales (respaldo)',
    REMOVE_UNIT: 'Eliminar presentación',
    REMOVE_UNIT_CONFIRM:
      '¿Eliminar esta presentación? No podrá seleccionarse en nuevas ventas o compras.',
    UNIT_ALREADY_USED:
      'Esta unidad ya existe en la tabla. Cada presentación debe ser única.',
    DUPLICATE_BARCODE:
      'Este código de barras ya está en uso por otra presentación.',
    MISSING_DEFAULT_ROLE:
      'Debes marcar al menos una presentación como predeterminada para cada rol activo.',
    AVAILABLE_UNITS_TEMPLATE: '{{count}} presentaciones disponibles',
    UNIT_DROPDOWN_LABEL: 'Unidad',
    LEGACY_SECTION_HINT:
      'Los campos rápidos abajo se sincronizan automáticamente con la presentación predeterminada de cada rol.',
    PRESENTATION_LABEL: 'Presentación: {{unit}} · 1 = {{factor}} {{stockUnit}}',
    SALE_PRESENTATION_LABEL: 'Vendido en {{unit}}',
    TUTORIAL_TITLE: '¿Cómo funcionan las presentaciones?',
    TUTORIAL_INTRO:
      'Configura las distintas formas de comprar, vender o consumir el mismo producto (caja, unidad, litro, oz) sin duplicar el catálogo.',
    TUTORIAL_STEP_1:
      '1. Agrega una presentación por cada forma que uses. Define su factor de conversión respecto a la unidad de stock (1 caja = 12 litros).',
    TUTORIAL_STEP_2:
      '2. Marca los roles activos: Comprar (aparece al recibir mercancía), Vender (aparece en POS/factura), Recetas (se puede usar como ingrediente). Elige el predeterminado de cada rol.',
    TUTORIAL_STEP_3:
      '3. Opcional: pon precio + costo específico por presentación (ej. caja RD$13,000 aunque el litro cueste RD$1,200), tarifas por cantidad y precios por sucursal.',
    TUTORIAL_EXAMPLE:
      'Ejemplo: Whisky JW — stock en litro, comprar en caja (1=12), vender en litro o en shot (1=0.03). Al recibir 1 caja suma 12 litros; al vender 1 shot descuenta 0.03 litros.',
  },
  PURCHASE_UNIT_CHOOSER: {
    TITLE: 'Selecciona la presentación — {{name}}',
    DESCRIPTION:
      'Este producto tiene varias presentaciones de compra. Elige la que corresponde a esta línea.',
    DEFAULT_BADGE: 'Predeterminada',
    CONVERSION_HINT: '1 = {{factor}} {{stockUnit}}',
    PER_UNIT: 'por {{unit}}',
  },
  // Original-document reference for credit/debit notes — cross-surface label
  // that switches by fiscal class: electronic notes match by e-NCF, B-series
  // notes by invoice number.
  NOTE_REFERENCE: {
    ORIGINAL_NCF_LABEL: 'Número de NCF Original',
    ORIGINAL_INVOICE_LABEL: 'Número de Factura Original',
    SEARCH_BY_NCF_PLACEHOLDER: 'Buscar por e-NCF (E…)',
    SEARCH_BY_INVOICE_PLACEHOLDER: 'Buscar por número de factura o NCF…',
  },
  INVOICE_REFERENCE_PICKER: {
    TITLE: 'Seleccionar factura de referencia',
    DESCRIPTION: 'Busca la factura original por número o NCF y selecciónala.',
    TYPE_TO_SEARCH: 'Escribe para buscar una factura.',
    SEARCHING: 'Buscando…',
    NO_RESULTS: 'No se encontraron facturas.',
    SERIES_MISMATCH:
      'El NCF original debe ser de la serie {{series}} para esta nota.',
  },
  // DGII e-CF modification code (Información de Referencia) — cross-surface:
  // credit-note modal, invoice page credit/debit modes, and note print.
  ECF_MODIFICATION: {
    LABEL: 'Código de modificación (DGII)',
    REASON_LABEL: 'Razón de la modificación',
    REASON_PLACEHOLDER: 'Describe el motivo (ej: error en precio)',
    NOTA_80_HINT:
      'Para notas de crédito/débito electrónicas la DGII solo admite los códigos 1, 2 y 3.',
    CODE_1: 'Anula el NCF modificado',
    CODE_2: 'Corrige el texto del comprobante modificado',
    CODE_3: 'Corrige montos del NCF modificado',
    CODE_4: 'Reemplazo de NCF emitido en contingencia',
    CODE_5: 'Referencia a Factura de Consumo Electrónica',
  },
  CREDIT_NOTES: {
    TITLE: 'Notas de Crédito',
    SUBTITLE: 'Gestiona las notas de crédito y devoluciones de productos',
    APPROVAL_CODE: 'Código de Aprobación',
    INVOICE: 'Factura',
    CUSTOMER: 'Cliente',
    STATUS: 'Estado',
    APPROVAL_STATUS: 'Estado de Aprobación',
    TOTAL_AMOUNT: 'Monto Total',
    APPLIED_AMOUNT: 'Monto Aplicado',
    REMAINING_AMOUNT: 'Monto Restante',
    CREATED_AT: 'Fecha de Creación',
    CREATED_BY: 'Creado por',
    APPROVED_BY: 'Aprobado por',
    APPROVED_AT: 'Fecha de Aprobación',
    NOTES: 'Notas',
    SEARCH_PLACEHOLDER: 'Buscar por código, factura o cliente...',
    NO_CREDIT_NOTES: 'No hay notas de crédito',
    VIEW_DETAILS: 'Ver Detalles',
    APPROVE: 'Aprobar',
    REJECT: 'Rechazar',
    APPLY: 'Aplicar',
    CANCEL: 'Cancelar',
    STATUS_DRAFT: 'Borrador',
    STATUS_PENDING_APPROVAL: 'Pendiente de Aprobación',
    STATUS_APPROVED: 'Aprobada',
    STATUS_APPLIED: 'Aplicada',
    STATUS_PARTIALLY_APPLIED: 'Parcialmente Aplicada',
    STATUS_EXPIRED: 'Expirada',
    STATUS_CANCELLED: 'Cancelada',
    APPROVAL_NOT_REQUIRED: 'No Requiere Aprobación',
    APPROVAL_PENDING: 'Pendiente',
    APPROVAL_APPROVED: 'Aprobado',
    APPROVAL_REJECTED: 'Rechazado',
    RESOLUTION_CUSTOMER_CREDIT: 'Crédito del Cliente',
    RESOLUTION_REFUND: 'Reembolso',
    RESOLUTION_MIXED: 'Mixto',
    REASON_DEFECTIVE_PRODUCT: 'Producto Defectuoso',
    REASON_WRONG_PRODUCT: 'Producto Incorrecto',
    REASON_CUSTOMER_REQUEST: 'Solicitud del Cliente',
    REASON_PRICING_ERROR: 'Error de Precio',
    REASON_DUPLICATE_CHARGE: 'Cargo Duplicado',
    REASON_SERVICE_ISSUE: 'Problema de Servicio',
    REASON_OTHER: 'Otro',
    CREATE_CREDIT_NOTE: 'Crear Nota de Crédito',
    CREATE_CONFIRM_TITLE: '¿Crear Nota de Crédito?',
    CREATE_CONFIRM_DESCRIPTION:
      'Al confirmar se creará la Nota de Crédito y se revertirá la factura afectada. Esta acción no se puede deshacer desde aquí. ¿Deseas continuar?',
    CREATE_TITLE: 'Nueva Nota de Crédito',
    CREATE_SUBTITLE: 'Selecciona los artículos a devolver y el motivo',
    SELECT_INVOICE: 'Seleccionar Factura',
    SELECT_ITEMS: 'Seleccionar Artículos',
    CANCELLED_ITEMS: 'Artículos Cancelados',
    ORIGINAL_QUANTITY: 'Cantidad Original',
    CANCELLED_QUANTITY: 'Cantidad a Cancelar',
    CANCELLATION_REASON: 'Motivo de Cancelación',
    REASON_NOTES: 'Notas del Motivo',
    RETURN_TO_STOCK: 'Devolver al Inventario',
    ADD_ITEM: 'Agregar Artículo',
    REMOVE_ITEM: 'Quitar Artículo',
    APPLY_CREDIT_NOTE: 'Aplicar Nota de Crédito',
    APPLY_TITLE: 'Aplicar Crédito',
    APPLY_SUBTITLE: 'Selecciona cómo deseas aplicar el crédito',
    RESOLUTION_TYPE: 'Tipo de Resolución',
    CREDIT_AMOUNT: 'Monto a Crédito',
    REFUND_AMOUNT: 'Monto a Reembolsar',
    REFUND_METHOD: 'Método de Reembolso',
    REFUND_NOTES: 'Notas del Reembolso',
    APPROVE_CREDIT_NOTE: 'Aprobar Nota de Crédito',
    APPROVE_TITLE: 'Confirmar Aprobación',
    APPROVE_CONFIRM: '¿Estás seguro de aprobar esta nota de crédito?',
    REJECT_CREDIT_NOTE: 'Rechazar Nota de Crédito',
    REJECT_TITLE: 'Rechazar Nota de Crédito',
    REJECTION_REASON: 'Motivo del Rechazo',
    REJECTION_REASON_REQUIRED: 'Debes indicar el motivo del rechazo',
    CANCEL_CREDIT_NOTE: 'Cancelar Nota de Crédito',
    CANCEL_TITLE: 'Confirmar Cancelación',
    CANCEL_CONFIRM: '¿Estás seguro de cancelar esta nota de crédito?',
    CREATED_SUCCESS: 'Nota de crédito creada exitosamente',
    APPROVED_SUCCESS: 'Nota de crédito aprobada',
    REJECTED_SUCCESS: 'Nota de crédito rechazada',
    APPLIED_SUCCESS: 'Nota de crédito aplicada exitosamente',
    CANCELLED_SUCCESS: 'Nota de crédito cancelada',
    ERROR_CREATING: 'Error al crear la nota de crédito',
    ERROR_APPROVING: 'Error al aprobar la nota de crédito',
    ERROR_REJECTING: 'Error al rechazar la nota de crédito',
    ERROR_APPLYING: 'Error al aplicar la nota de crédito',
    ERROR_CANCELLING: 'Error al cancelar la nota de crédito',
    CREDIT_NOTE_DETAILS: 'Detalles de la Nota de Crédito',
    INVOICE_INFORMATION: 'Información de la Factura',
    CANCELLED_ITEMS_LIST: 'Lista de Artículos Cancelados',
    APPROVAL_INFORMATION: 'Información de Aprobación',
    DAYS_SINCE_PURCHASE: 'Días desde la Compra',
    STOCK_RETURNED: 'Stock Devuelto',
    CREDIT_EXPIRATION: 'Vencimiento del Crédito',
    PENDING_APPROVAL_COUNT: 'Notas Pendientes de Aprobación',
    PENDING_APPROVAL_AMOUNT: 'Monto Pendiente de Aprobación',
    TOTAL_CREDIT_NOTES: 'Total Notas de Crédito',
    APPLIED: 'Aplicado',
    CREATE_CREDIT_NOTE_DESCRIPTION:
      'Selecciona los artículos que deseas cancelar de esta factura. Puedes cancelar cantidades parciales.',
    SELECT_ITEMS_ERROR:
      'Debes seleccionar al menos un artículo para crear la nota de crédito',
    SELECT_RETURNED_SERIALS:
      'Debes seleccionar los seriales devueltos antes de crear la Nota de Crédito.',
    SELECT_RETURNED_SERIALS_LABEL: 'Seriales devueltos',
    SERIALS_REQUIRED_BADGE: 'Faltan seriales',
    REASON_REQUIRED: 'El motivo de la nota de crédito es requerido',
    CREATE_SUCCESS: 'Nota de crédito creada exitosamente',
    CREATE_ERROR: 'Error al crear la nota de crédito',
    NO_NCF_AVAILABLE:
      'No hay NCF de tipo B04 (Nota de Crédito) disponible. Debe agregar una secuencia NCF antes de continuar.',
    CANCEL_QUANTITY: 'Cantidad a Cancelar',
    ALREADY_CANCELLED: 'Ya cancelado',
    AVAILABLE: 'Disponible',
    FULLY_CANCELLED: 'Este artículo ya fue completamente cancelado',
    INVOICE_ALREADY_FULLY_CREDIT_NOTED:
      'Esta factura ya fue completamente reversada por una nota de crédito. No se puede emitir otra.',
    REASON_NOTES_PLACEHOLDER: 'Describe el motivo de la cancelación...',
    GENERAL_NOTES: 'Notas Generales',
    GENERAL_NOTES_PLACEHOLDER:
      'Notas adicionales sobre esta nota de crédito...',
    ITEMS_SELECTED: 'Artículos seleccionados',
    TOTAL_CREDIT: 'Total del Crédito',
    // Invoice list indicators
    HAS_CREDIT_NOTE: 'Con nota de crédito',
    TOTAL_REFUNDED: 'Total devuelto',
    CANNOT_EDIT_HAS_CREDIT_NOTE:
      'No se puede editar una factura con notas de crédito',
    PARTIAL_RETURN: 'Devolución parcial',
    FULL_RETURN: 'Devolución total',
    ITEM_RETURNED: 'Artículo devuelto',
    ITEM_PARTIALLY_RETURNED: 'Artículo parcialmente devuelto',
    ORIGINAL_INVOICE: 'Factura original',
    ORIGINAL_INVOICE_REFERENCE: 'Referencia de factura original',
    REASON: 'Motivo',
    ORIGINAL_INVOICE_NUMBER: 'Número de Factura Original',
    SEARCH_INVOICE_PLACEHOLDER: 'Ej: FAC-1-0000001...',
    INVOICE_NOT_FOUND: 'Factura no encontrada. Verifica el número.',
    NEW_CREDIT_NOTE: 'Nueva Nota de Crédito',
    NCF_ORIGINAL_LABEL: 'NCF Original',
    NCF_CREDIT_NOTE_LABEL: 'Nota de Crédito',
    NCF_E_CREDIT_NOTE_LABEL: 'e-Nota de Crédito',
    REASON_PRESETS_LABEL: 'Motivos frecuentes',
    REASON_PRESET_CUSTOMER_RETURN: 'Devolución del cliente',
    REASON_PRESET_BILLING_ERROR: 'Error de facturación',
    REASON_PRESET_DAMAGED_PRODUCT: 'Producto dañado',
    REASON_PRESET_PRICE_ADJUSTMENT: 'Ajuste de precio',
    REASON_PRESET_DEFECTIVE_OR_MISBILLED: 'Defectuoso o mal facturado',
    REASON_CHAR_COUNTER: '{{count}} / {{max}}',
    SUMMARY_TITLE: 'Resumen del crédito',
    SUMMARY_ITEMS_UNITS: '{{items}} artículo(s) · {{units}} unidad(es)',
    SUMMARY_TAX_WITH_RATE: 'ITBIS {{rate}}%',
  },
  REFUNDS: {
    TITLE: 'Reembolsos',
    SUBTITLE: 'Gestiona los reembolsos a clientes',
    REFUND_NUMBER: 'Número de Reembolso',
    CREDIT_NOTE: 'Nota de Crédito',
    CUSTOMER: 'Cliente',
    INVOICE: 'Factura',
    STATUS: 'Estado',
    METHOD: 'Método',
    AMOUNT: 'Monto',
    PROCESSED_AMOUNT: 'Monto Procesado',
    PROCESSED_AT: 'Fecha de Procesamiento',
    PROCESSED_BY: 'Procesado por',
    CREATED_AT: 'Fecha de Creación',
    CREATED_BY: 'Creado por',
    NOTES: 'Notas',
    SEARCH_PLACEHOLDER: 'Buscar por número, cliente o factura...',
    NO_REFUNDS: 'No hay reembolsos',
    VIEW_DETAILS: 'Ver Detalles',
    PROCESS: 'Procesar',
    CANCEL: 'Cancelar',
    MARK_FAILED: 'Marcar como Fallido',
    STATUS_PENDING: 'Pendiente',
    STATUS_PROCESSING: 'Procesando',
    STATUS_COMPLETED: 'Completado',
    STATUS_FAILED: 'Fallido',
    STATUS_CANCELLED: 'Cancelado',
    METHOD_CASH: 'Efectivo',
    METHOD_BANK_TRANSFER: 'Transferencia Bancaria',
    METHOD_CARD_REVERSAL: 'Reversión de Tarjeta',
    METHOD_CHECK: 'Cheque',
    METHOD_STORE_CREDIT: 'Crédito de Tienda',
    CREATE_REFUND: 'Crear Reembolso',
    CREATE_TITLE: 'Nuevo Reembolso',
    CREATE_SUBTITLE: 'Crea un nuevo reembolso para el cliente',
    SELECT_CUSTOMER: 'Cliente',
    SELECT_METHOD: 'Método',
    BANK_ACCOUNT_DETAILS: 'Datos de la Cuenta Bancaria',
    PROCESS_REFUND: 'Procesar Reembolso',
    PROCESS_TITLE: 'Confirmar Procesamiento',
    PROCESS_CONFIRM: '¿Estás seguro de procesar este reembolso?',
    TRANSACTION_REFERENCE: 'Referencia de Transacción',
    CANCEL_REFUND: 'Cancelar Reembolso',
    CANCEL_TITLE: 'Confirmar Cancelación',
    CANCEL_REASON: 'Motivo de Cancelación',
    CANCEL_REASON_REQUIRED: 'Debes indicar el motivo de la cancelación',
    FAIL_REFUND: 'Marcar como Fallido',
    FAIL_TITLE: 'Confirmar Fallo',
    FAILURE_REASON: 'Motivo del Fallo',
    FAILURE_REASON_REQUIRED: 'Debes indicar el motivo del fallo',
    CREATED_SUCCESS: 'Reembolso creado exitosamente',
    PROCESSED_SUCCESS: 'Reembolso procesado exitosamente',
    CANCELLED_SUCCESS: 'Reembolso cancelado',
    FAILED_SUCCESS: 'Reembolso marcado como fallido',
    ERROR_CREATING: 'Error al crear el reembolso',
    ERROR_PROCESSING: 'Error al procesar el reembolso',
    ERROR_CANCELLING: 'Error al cancelar el reembolso',
    ERROR_FAILING: 'Error al marcar el reembolso como fallido',
    REFUND_DETAILS: 'Detalles del Reembolso',
    CUSTOMER_INFORMATION: 'Información del Cliente',
    PAYMENT_INFORMATION: 'Información del Pago',
    PENDING_COUNT: 'Reembolsos Pendientes',
    PENDING_AMOUNT: 'Monto Pendiente',
    PROCESSED_TODAY: 'Procesados Hoy',
    PROCESSED_AMOUNT_TODAY: 'Monto Procesado Hoy',
    TOTAL_REFUNDS: 'Total Reembolsos',
  },
  PAYMENTS: {
    TITLE: 'Pagos',
    CHANGE: 'Cambio',
    SUBTITLE: 'Gestiona los pagos recibidos de tus clientes',
    PAYMENT_NUMBER: 'Número de Pago',
    PAYMENT_DATE: 'Fecha de Pago',
    PAYMENT_METHOD: 'Método de Pago',
    PAYMENT_STATUS: 'Estado del Pago',
    AMOUNT: 'Monto',
    CURRENCY: 'Moneda',
    REFERENCE_NUMBER: 'Número de Referencia',
    REFERENCE_LABEL: 'Ref',
    NOTES: 'Notas',
    DESCRIPTION: 'Descripción',
    CUSTOMER: 'Cliente',
    INVOICE: 'Factura',
    PROCESSED_BY: 'Procesado por',
    // Direction
    DIRECTION: 'Tipo',
    DIRECTION_INCOME: 'Ingreso',
    DIRECTION_EXPENSE: 'Egreso',
    DIRECTION_ALL: 'Todos',
    FILTER_BY_DIRECTION: 'Filtrar por tipo',
    PAYMENT_ALLOCATIONS: 'Facturas afectadas',
    // Provider and Purchase Order
    PROVIDER: 'Proveedor',
    PURCHASE_ORDER: 'Orden de Compra',
    SELECT_PROVIDER: 'Proveedor',
    SELECT_PROVIDER_PLACEHOLDER: 'Buscar proveedor...',
    SELECT_PURCHASE_ORDER: 'Orden de Compra',
    SELECT_PURCHASE_ORDER_PLACEHOLDER: 'Selecciona una orden de compra',
    NO_PENDING_PURCHASE_ORDERS: 'No hay órdenes de compra pendientes',
    // Actions
    RECEIVE_PAYMENT: 'Recibir Pago',
    MAKE_EXPENSE: 'Registrar Egreso',
    EDIT_PAYMENT: 'Editar Pago',
    DELETE_PAYMENT: 'Eliminar Pago',
    VIEW_DETAILS: 'Ver Detalles',
    EXPORT_PAYMENTS: 'Exportar Pagos',
    // Filters
    SEARCH_PLACEHOLDER: 'Buscar por número de pago, cliente o referencia...',
    FILTER_BY_METHOD: 'Filtrar por método',
    FILTER_BY_STATUS: 'Filtrar por estado',
    FILTER_BY_DATE: 'Filtrar por fecha',
    ALL_METHODS: 'Todos los métodos',
    ALL_STATUSES: 'Todos los estados',
    START_DATE: 'Fecha inicio',
    END_DATE: 'Fecha fin',
    CLEAR_FILTERS: 'Limpiar filtros',
    // Payment methods
    METHOD_CASH: 'Efectivo',
    METHOD_CREDIT_CARD: 'Tarjeta de Crédito',
    METHOD_DEBIT_CARD: 'Tarjeta de Débito',
    METHOD_TRANSFER: 'Transferencia',
    METHOD_CHECK: 'Cheque',
    METHOD_PAYPAL: 'PayPal',
    METHOD_ZELLE: 'Zelle',
    METHOD_CRYPTO: 'Criptomoneda',
    METHOD_OTHER: 'Otro',
    METHOD_CUSTOMER_CREDIT: 'Crédito del Cliente',
    // Payment statuses
    STATUS_PENDING: 'Pendiente',
    STATUS_COMPLETED: 'Completado',
    STATUS_FAILED: 'Fallido',
    STATUS_REFUNDED: 'Reembolsado',
    STATUS_CANCELLED: 'Cancelado',
    // Modal
    RECEIVE_PAYMENT_TITLE: 'Recibir Pago',
    RECEIVE_PAYMENT_SUBTITLE: 'Registra un nuevo pago de cliente',
    MAKE_EXPENSE_TITLE: 'Registrar Egreso',
    MAKE_EXPENSE_SUBTITLE: 'Registra un pago a proveedor o gasto general',
    EDIT_PAYMENT_TITLE: 'Editar Pago',
    EDIT_PAYMENT_SUBTITLE: 'Modifica los detalles del pago',
    VIEW_PAYMENT_TITLE: 'Detalle del Pago',
    VIEW_PAYMENT_SUBTITLE: 'Visualiza la información del pago registrado',
    SELECT_CUSTOMER: 'Cliente',
    SELECT_INVOICE: 'Factura',
    SEARCH_TAB_INVOICE: 'N° de factura',
    SEARCH_TAB_ORDER: 'N° de orden',
    SEARCH_TAB_CUSTOMER: 'Cliente',
    SEARCH_PLACEHOLDER_INVOICE: 'Buscar por número o NCF...',
    SEARCH_PLACEHOLDER_ORDER: 'Buscar por número de orden...',
    SEARCH_PLACEHOLDER_CUSTOMER: 'Buscar por nombre, RNC o email...',
    SEARCH_EMPTY: 'Sin resultados. Verifica el número o cambia de modo.',
    SEARCH_START: 'Comienza escribiendo para buscar.',
    DOC_TOTAL: 'Total',
    DOC_PAID: 'Pagado',
    DOC_REMAINING: 'Restante',
    DOC_STATUS_PAID: 'Pagada',
    DOC_PAYMENT_HISTORY: 'Historial de pagos',
    DOC_CREDIT_NOTE_HISTORY: 'Historial de notas de crédito',
    DOC_CN_ITEMS_COUNT: '{{count}} art. devueltos',
    DOC_PAY_REMAINING: 'Pagar restante',
    DOC_ADD_PARTIAL: 'Agregar pago parcial',
    DOC_ADD_PAYMENT: 'Agregar pago',
    DOC_CUSTOMER_INVOICES_SECTION: 'Facturas',
    DOC_CUSTOMER_ORDERS_SECTION: 'Órdenes',
    DOC_CUSTOMER_NO_OPEN:
      'Este cliente no tiene facturas ni órdenes con saldo pendiente.',
    PAYMENT_REGISTERED: 'Pago registrado.',
    SELECT_INVOICE_PLACEHOLDER: 'Selecciona una factura para aplicar el pago',
    NO_PENDING_INVOICES: 'No hay facturas pendientes para este cliente',
    ENTER_AMOUNT: 'Ingrese el monto',
    SELECT_METHOD: 'Seleccione método de pago',
    ENTER_REFERENCE: 'Número de referencia (opcional)',
    ADD_NOTES: 'Agregar notas (opcional)',
    CARD_LAST_FOUR: 'Últimos 4 dígitos',
    CARD_TYPE: 'Tipo de tarjeta',
    BANK_NAME: 'Nombre del banco',
    ACCOUNT_NUMBER: 'Número de cuenta',
    // Allocations
    ALLOCATIONS: 'Asignaciones',
    ADD_ALLOCATION: 'Agregar asignación',
    REMOVE_ALLOCATION: 'Eliminar asignación',
    ALLOCATED_AMOUNT: 'Monto asignado',
    REMAINING_BALANCE: 'Saldo restante',
    INVOICE_TOTAL: 'Total de factura',
    INVOICE_BALANCE: 'Saldo de factura',
    // Multi-payment
    ADD_PAYMENT_METHOD: 'Agregar Método de Pago',
    PAYMENT_METHOD_MANAGE_HINT:
      'Solo se listan los métodos de pago que tu negocio tiene activos. Actualiza la lista en Configuración → Negocio → Tipos de Pago.',
    PAYMENT_METHOD_MANAGE_CTA: 'Administrar tipos de pago',
    TOTAL_TO_PAY: 'Total a Pagar',
    TOTAL_PAID: 'Total Pagado',
    CHANGE_AMOUNT: 'Cambio',
    PAYMENT_SUMMARY: 'Resumen de Pago',
    DISTRIBUTE_REMAINING: 'Distribuir Restante',
    CONFIRM_STEP_TITLE: 'Confirmar distribución',
    CONFIRM_STEP_SUBTITLE:
      'Revisa los métodos de pago y el destino antes de guardar.',
    CONFIRM_CONTINUE: 'Continuar',
    CONFIRM_BACK: 'Volver',
    CONFIRM_SUBMIT: 'Confirmar y guardar',
    CONFIRM_DOC_TARGET: 'Se aplicará a',
    CONFIRM_STANDALONE_TARGET: 'Pago sin asociar',
    // Standalone payments
    STANDALONE_PAYMENT: 'Pago sin asociar',
    STANDALONE_PAYMENT_DESCRIPTION:
      'Ingreso o egreso sin factura o proveedor asociado',
    ASSOCIATE_WITH_ENTITY: 'Asociar con',
    NO_ASSOCIATION: 'Sin asociar (pago independiente)',
    DESCRIPTION_PLACEHOLDER: 'Descripción del pago o gasto',
    // Messages
    CREATED_SUCCESS: 'Pago registrado exitosamente',
    UPDATED_SUCCESS: 'Pago actualizado exitosamente',
    DELETED_SUCCESS: 'Pago eliminado exitosamente',
    ERROR_CREATING: 'Error al registrar el pago',
    ERROR_UPDATING: 'Error al actualizar el pago',
    ERROR_DELETING: 'Error al eliminar el pago',
    ERROR_OVERPAYMENT:
      'El monto del pago excede el balance pendiente de la factura',
    ORIGINAL_TOTAL: 'Total original',
    // Empty states
    NO_PAYMENTS: 'No hay pagos registrados',
    NO_PAYMENTS_DESCRIPTION: 'Los pagos que recibas aparecerán aquí',
    // Table columns
    COLUMN_NUMBER: 'No. Pago',
    COLUMN_DATE: 'Fecha',
    COLUMN_CUSTOMER: 'Cliente',
    COLUMN_INVOICE: 'Factura',
    COLUMN_METHOD: 'Método',
    COLUMN_STATUS: 'Estado',
    COLUMN_AMOUNT: 'Monto',
    COLUMN_ACTIONS: 'Acciones',
    // Summary
    TOTAL_PAYMENTS: 'Total de Pagos',
    TOTAL_AMOUNT: 'Monto Total',
    PAYMENTS_TODAY: 'Pagos Hoy',
    AMOUNT_TODAY: 'Monto Hoy',
    // Delete confirmation
    DELETE_TITLE: '¿Eliminar pago?',
    DELETE_DESCRIPTION:
      '¿Estás seguro de que deseas eliminar este pago? Esta acción no se puede deshacer.',
    DELETE_CONFIRM: 'Sí, eliminar',
    // Receipt printing
    PRINT_RECEIPT: 'Imprimir Recibo',
    RECEIPT_TITLE: 'Recibo de Pago',
    RECEIPT_INCOME_TITLE: 'Recibo de Cobro',
    RECEIPT_EXPENSE_TITLE: 'Comprobante de Egreso',
    INVOICE_REFERENCE: 'Referencia de Factura',
    PURCHASE_ORDER_REFERENCE: 'Referencia de Orden de Compra',
    RECEIPT_AMOUNT: 'Monto Recibido',
    RECEIPT_DATE: 'Fecha',
    RECEIPT_NUMBER: 'No. Recibo',
    RECEIPT_PROCESSED_BY: 'Procesado por',
    RECEIPT_THANK_YOU: '¡Gracias por su pago!',
    RECEIPT_STANDALONE_PAYMENT: 'Pago Independiente',
    PAYMENT_CREATED_PRINT: 'Pago registrado. ¿Desea imprimir el recibo?',
    SELECT_PAPER_SIZE: 'Tamaño de papel',
  },
  PROCUREMENT_ORDERS: {
    // Save confirmation — mirrors the supplier-invoice flow
    // (PURCHASE_ORDERS.SAVE_CONFIRM_*). Kept in this namespace because
    // the wording names the procurement document, not the invoice.
    SAVE_CONFIRM_TITLE: '¿Guardar la orden de compra?',
    SAVE_CONFIRM_DESCRIPTION:
      'Se guardarán el proveedor, las líneas y los totales de la orden. Podrás seguir editándola después.',
    TITLE: 'Órdenes de Compra',
    SUBTITLE:
      'Solicitudes de compra a proveedores. Al recibir los productos se convertirán en una factura de proveedor.',
    CREATE: 'Crear Orden',
    EDIT: 'Editar Orden',
    NO_ITEMS: 'No hay órdenes de compra',
    PO_NUMBER: 'No. Orden',
    STATUS: 'Estado',
    PROVIDER: 'Proveedor',
    ORDER_DATE: 'Fecha de Orden',
    EXPECTED_DELIVERY: 'Entrega Esperada',
    TOTAL: 'Total',
    ACTIONS: 'Acciones',
    STATUS_DRAFT: 'Borrador',
    STATUS_SENT: 'Enviada',
    STATUS_PARTIAL: 'Parcial',
    STATUS_RECEIVED: 'Recibida',
    STATUS_CANCELLED: 'Cancelada',
    DELETE_TITLE: '¿Eliminar orden de compra?',
    DELETE_DESCRIPTION:
      '¿Estás seguro que deseas eliminar la orden de compra {{number}}?',
    SAVED: 'Orden de compra guardada exitosamente',
    DELETED: 'Orden eliminada',
    CONVERTED_BADGE: 'Convertida a Factura',
    RECEIVE: 'Recibir Items',
    RECEIVE_TITLE: 'Recibir productos de la orden',
    RECEIVE_SUBTITLE:
      'Registra las cantidades recibidas. Al completar todos los items la orden se convertirá automáticamente en una factura de proveedor.',
    RECEIVE_QUANTITY: 'Cantidad a Recibir',
    PENDING_QUANTITY: 'Pendiente',
    ORDERED_QUANTITY: 'Ordenada',
    RECEIVED_SUCCESS: 'Recepción registrada',
    RECEIVE_AT_LEAST_ONE:
      'Debes ingresar al menos una cantidad recibida mayor que 0',
    CONVERTED_TOAST:
      'Orden recibida completamente — se generó automáticamente una factura de proveedor',
    AFTER_CREATE_TITLE: 'Orden de compra creada',
    AFTER_CREATE_DESCRIPTION:
      'Imprime la orden {{poNumber}} o envíala al proveedor por correo o WhatsApp.',
    SHARE_TITLE: 'Compartir orden de compra',
    VIEW_SUPPLIER_INVOICE: 'Ver factura {{number}}',
    REFERENCE: 'Referencia',
    ORDER_DATE_REQ: 'La fecha de orden es requerida',
    NOTES: 'Notas',
    TERMS_AND_CONDITIONS: 'Términos y condiciones',
    PAYMENT_TERMS: 'Términos de pago',
    PAYMENT_TERMS_CONTADO: 'Contado',
    PAYMENT_TERMS_NET_30: 'Neto 30 días',
    PAYMENT_TERMS_NET_60: 'Neto 60 días',
    PAYMENT_TERMS_NET_90: 'Neto 90 días',
    PAYMENT_TERMS_CUSTOM: 'Personalizado',
    SUMMARY: 'Resumen',
    SUBTOTAL: 'Subtotal',
    DISCOUNT: 'Descuento',
    TAX: 'Impuesto',
    TAX_TYPE: 'Tipo de Impuesto',
    TAX_NONE: 'Sin impuesto',
    ADD_PRODUCTS: 'Productos',
    ADD_PRODUCTS_SUBTITLE: 'Agrega los productos solicitados al proveedor',
    BROWSE_PRODUCTS: 'Buscar productos',
    BACK_TO_LIST: 'Volver a órdenes',
    PRODUCT: 'Producto',
    PRODUCT_CODE: 'Código',
    QUANTITY: 'Cantidad',
    UNIT_PRICE: 'Precio unit.',
    LINE_TOTAL: 'Total',
    ADD_AT_LEAST_ONE_PRODUCT: 'Debes agregar al menos un producto',
    CREATE_ORDER: 'Crear Orden',
    EDIT_ORDER: 'Editar Orden',
    GENERAL_INFO: 'Información general',
    GENERAL_INFO_SUBTITLE: 'Datos del proveedor y la entrega',
    PROVIDER_PLACEHOLDER: 'Selecciona un proveedor',
    PAYMENT_TERMS_PLACEHOLDER: 'Selecciona los términos de pago',
    EMPTY_ITEMS_HINT:
      'Aún no has agregado productos. Usa el botón "Buscar productos" o escribe en una nueva línea.',
    PRINT: 'Imprimir',
    SEND_EMAIL: 'Enviar por Email',
    SEND_WHATSAPP: 'Enviar por WhatsApp',
    RESPONSIBLE: 'Responsable',
    EMAIL_SUBJECT: 'Orden de Compra {{poNumber}}',
    EMAIL_BODY:
      'Estimado proveedor,\n\nAdjuntamos la orden de compra {{poNumber}}. Puede consultarla aquí: {{poUrl}}\n\nPor favor confirme la recepción y la fecha de entrega.\n\nSaludos cordiales.',
    WHATSAPP_BODY:
      'Hola, le compartimos la orden de compra {{poNumber}}: {{poUrl}}. Por favor confirme recepción y fecha de entrega. Gracias.',
    PRINT_ERROR: 'No se pudo generar el PDF',
    EMAIL_NO_PROVIDER_EMAIL: 'El proveedor no tiene un correo registrado',
    WHATSAPP_NO_PROVIDER_PHONE: 'El proveedor no tiene un teléfono registrado',
    FILTER_STATUS_PLACEHOLDER: 'Estado',
    FILTER_PROVIDER_PLACEHOLDER: 'Proveedor',
    FILTER_DATE_RANGE: 'Rango de fechas',
    FILTER_CLEAR: 'Limpiar filtros',
    FILTER_ALL_STATUSES: 'Todos los estados',
    FILTER_ALL_PROVIDERS: 'Todos los proveedores',
    EXPORT: 'Exportar',
    EMAIL_SENT: 'Correo enviado al proveedor',
  },
  PURCHASE_ORDERS: {
    ADD_AT_LEAST_ONE_PRODUCT: 'Debes agregar al menos un producto a la factura',
    TITLE: 'Facturas de Proveedor',
    TAB_PURCHASE_ORDERS: 'Facturas de Proveedor',
    TAB_DEBIT_NOTES: 'Notas de Débito',
    SUBTITLE: 'Crea una nueva factura de proveedor',
    GENERAL_INFO: 'Información General',
    GENERAL_INFO_SUBTITLE:
      'Aquí puedes gestionar las facturas de proveedor de tu inventario',
    PROVIDER: 'Proveedor',
    REFERENCE: 'Referencia/Número',
    ORDER_DATE: 'Fecha de Factura',
    DUE_DATE: 'Fecha de Vencimiento',
    NOTES: 'Notas',
    SELECT_PROVIDER: 'Selecciona un proveedor',
    ENTER_REFERENCE: 'Ingresa el Número o referencia de la factura',
    SELECT_ORDER_DATE: 'Selecciona la fecha de factura',
    SELECT_DUE_DATE: 'Selecciona la fecha de vencimiento',
    ADD_NOTES: 'Agrega notas o comentarios a tu factura de proveedor',
    ADD_PRODUCTS: 'Agregar Productos',
    ADD_PRODUCTS_SUBTITLE: 'Lista de productos para la factura de proveedor',
    ADD_PRODUCT: 'Agregar Producto',
    PRODUCT: 'Producto',
    CATEGORY: 'Categoría',
    PRICE: 'Precio',
    COST: 'Costo',
    QUANTITY: 'Cantidad',
    ACTIONS: 'Acciones',
    NO_PRODUCTS_ADDED: 'Aún no hay productos agregados',
    ORDER_SUMMARY: 'Resumen de la Factura',
    ORDER_SAVED: 'Factura de proveedor guardada exitosamente',
    ITEMS: 'Items',
    TOTAL_QUANTITY: 'Cantidad total',
    SUBTOTAL: 'Subtotal',
    DISCOUNT: 'Descuento',
    TAX: 'Impuesto',
    TAX_RATE: 'Tasa',
    TOTAL: 'Total',
    CREATE_ORDER: 'Crear Factura',
    EDIT_ORDER: 'Editar Factura',
    SELECT_PRODUCT: 'Seleccionar Producto',
    SELECT_PRODUCT_DESCRIPTION:
      'Busca y selecciona un producto para agregar a la orden',
    SEARCH_PRODUCTS:
      'Buscar por nombre, código, código de barras o modificador...',
    STOCK: 'Cantidad',
    ADD: 'Agregar',
    DELETE: 'Eliminar',
    PROVIDER_REQUIRED: 'Proveedor es requerido',
    PURCHASE_ORDER_NUMBER_REQUIRED: 'Número de factura es requerido',
    REFERENCE_REQUIRED: 'Referencia es requerida',
    ORDER_DATE_REQUIRED: 'Fecha de factura es requerida',
    DUE_DATE_REQUIRED: 'Fecha de vencimiento es requerida',
    PRODUCT_REQUIRED: 'Producto es requerido',
    PRODUCT_REFERENCE_REQUIRED: 'Referencia es requerida',
    PRODUCT_NAME_REQUIRED: 'Nombre del producto es requerido',
    CATEGORY_REQUIRED: 'Categoría es requerida',
    QUANTITY_MIN: 'Cantidad debe ser al menos 1',
    PRICE_MIN: 'Precio debe ser mayor o igual a 0',
    MIN_ONE_PRODUCT: 'Debe agregar al menos un producto a la factura',
    DESCRIPTION: 'Descripción',
    // List View
    PO_NUMBER: 'No. Factura',
    STATUS: 'Estado',
    EXPECTED_DELIVERY: 'Entrega Esperada',
    SEARCH_PLACEHOLDER: 'Buscar por número, proveedor...',
    ALL_STATUSES: 'Todos los estados',
    STATUS_DRAFT: 'Borrador',
    STATUS_PENDING: 'Pendiente',
    STATUS_SENT: 'Pendiente',
    STATUS_CONFIRMED: 'Confirmada',
    STATUS_CREATED: 'Creada',
    STATUS_PARTIAL: 'Parcial',
    STATUS_RECEIVED: 'Recibida',
    STATUS_CANCELLED: 'Cancelada',
    VIEW: 'Ver',
    EDIT: 'Editar',
    RESTORE: 'Restaurar',
    NO_PURCHASE_ORDERS: 'No hay facturas de proveedor',
    DELETE_CONFIRMATION_TITLE: '¿Eliminar factura de proveedor?',
    DELETE_CONFIRMATION_DESCRIPTION:
      '¿Estás seguro de que deseas eliminar la factura {{number}}?',
    VOID: 'Anular',
    VOID_SUCCESS: 'Factura anulada: inventario revertido y excluida del 606.',
    VOID_CONFIRMATION_TITLE: '¿Anular factura de proveedor?',
    VOID_CONFIRMATION_DESCRIPTION:
      'Se revertirá el inventario de {{number}}, se excluirá del 606 y quedará anulada. Esta acción no se puede deshacer.',
    ADD_ONLY_EDIT_NOTICE:
      'Esta factura ya afectó el inventario. Solo puedes agregar nuevos productos y editar el NCF y los datos del 606. Las líneas existentes y el resto de los campos están bloqueados.',
    SAVE_CONFIRM_TITLE: '¿Guardar cambios en la factura?',
    SAVE_CONFIRM_DESCRIPTION:
      'Se guardarán los cambios de esta factura de proveedor. ¿Deseas continuar?',
    START_DATE: 'Fecha inicial',
    END_DATE: 'Fecha final',
    PROCESS_ORDER: 'Procesar Factura',
    PROCESSING: 'Procesando...',
    CONFIRM_PROCESS_TITLE: '¿Procesar factura de proveedor?',
    CONFIRM_PROCESS_DESCRIPTION:
      '¿Estás seguro de que deseas procesar esta factura? Todos los productos serán marcados como recibidos y se actualizará el inventario si aplica.',
    CONFIRM_PROCESS: 'Sí, Procesar',
    PURCHASE_ORDER_NUMBER: 'No. Factura',
    DISC_PERCENT: 'Desc %',
    TAX_PERCENT: 'Impuesto %',
    REFERENCE_NUMBER: 'Número de Referencia',
    PROVIDER_PHONE: 'Teléfono Proveedor',
    PROVIDER_EMAIL: 'Email Proveedor',
    ACTUAL_DELIVERY: 'Entrega Real',
    PAYMENT_TERMS: 'Términos de Pago',
    CURRENCY: 'Moneda',
    TRACKING_NUMBER: 'Número de Rastreo',
    CREATE_DEBIT_NOTE: 'Crear Nota de Débito',
    CREATE_DEBIT_NOTE_SUBTITLE:
      'Registra cargos adicionales como intereses, fletes u otros costos posteriores a la facturación',
    DEBIT_NOTE_TITLE: 'Nota de Débito',
    DEBIT_NOTE_REFERENCE_INVOICE: 'NCF de Referencia',
    DEBIT_NOTE_REFERENCE_INVOICE_PLACEHOLDER:
      'Ingresa el NCF del comprobante original',
    DEBIT_NOTE_REASON: 'Motivo',
    DEBIT_NOTE_REASON_PLACEHOLDER:
      'Describe el motivo del cargo adicional (intereses, fletes, etc.)',
    DEBIT_NOTE_SAVED: 'Nota de débito creada exitosamente',
    DEBIT_NOTE_CONFIRM_TITLE: 'Confirmar creación de nota de débito',
    DEBIT_NOTE_CONFIRM_DESCRIPTION:
      'Las notas de débito son documentos fiscales que no pueden ser editados ni eliminados después de creadas. ¿Está seguro que desea continuar?',
    DEBIT_NOTE_CONFIRM_OK: 'Crear nota de débito',
    TAB_SUPPLIER_CREDIT_NOTES: 'Notas de Crédito',
    CREATE_SUPPLIER_CREDIT_NOTE: 'Crear Nota de Crédito',
    SUPPLIER_CREDIT_NOTE_TITLE: 'Nueva Nota de Crédito de Proveedor',
    SUPPLIER_CREDIT_NOTE_SUBTITLE:
      'Registra una devolución o reverso de una factura de proveedor. Al procesarse descuenta el inventario si la factura original afectó stock.',
    CREDIT_NOTES_COLUMN: 'Notas de Crédito',
    EDIT_SUPPLIER_CREDIT_NOTE: 'Editar Nota de Crédito',
    SUPPLIER_CREDIT_NOTE_BADGE: 'Nota de Crédito',
    PROCESS_CREDIT_NOTE: 'Procesar',
    PARENT_SUPPLIER_INVOICE_REQUIRED:
      'Selecciona la factura de proveedor a la que pertenece esta nota de crédito',
    IMPORT_FROM_SUPPLIER_INVOICE: 'Importar desde Factura de Proveedor',
    IMPORT_FROM_SUPPLIER_INVOICE_PLACEHOLDER:
      'Buscar factura de proveedor (SI-...)',
    SUPPLIER_CREDIT_NOTE_SAVED: 'Nota de crédito guardada',
    SUPPLIER_CREDIT_NOTE_PROCESSED: 'Nota de crédito procesada',
    // One list for invoices + credit notes (supplier-credit-note-lifecycle)
    DOCUMENT_TYPE: 'Tipo de documento',
    ALL_DOCUMENT_TYPES: 'Todos los documentos',
    CREDIT_NOTE_FOR_INVOICE: 'Acredita {{number}}',
    // The verb alone, for the list cell that makes the invoice NUMBER the
    // clickable part and so cannot interpolate it into the sentence.
    CREDIT_NOTE_CREDITS: 'Acredita',
    CREDIT_NOTE_MISMATCH_TITLE:
      'Algunas líneas no coinciden con la factura acreditada',
    CREDIT_NOTE_MISMATCH_HELP:
      'Puedes guardar la nota de todos modos. Revisa que sea intencional: acreditar más de lo facturado descuadra la cuenta por pagar y el 606.',
    CREDIT_NOTE_MISMATCH_TAX:
      'ITBIS {{invoice}}% en la factura, {{note}}% en la nota',
    CREDIT_NOTE_MISMATCH_PRICE:
      'precio {{invoice}} en la factura, {{note}} en la nota',
    CREDIT_NOTE_MISMATCH_QTY:
      'cantidad {{invoice}} en la factura, {{note}} en la nota',
    CREDIT_NOTE_AFFECT_INVENTORY_HELP:
      'Devuelve al proveedor las cantidades de esta nota y descuenta el inventario. Desactívalo si la nota es solo financiera (precio, descuento o impuesto).',
    STATUS_PROCESSED: 'Procesada',
    PROCESS_CREDIT_NOTE_CONFIRM_TITLE: '¿Procesar nota de crédito?',
    PROCESS_CREDIT_NOTE_CONFIRM_DESCRIPTION:
      'Se aplicará {{number}}: reduce el balance de la factura y, si la factura afectó el inventario, descuenta del stock lo devuelto.',
    VOID_CREDIT_NOTE_TITLE: '¿Anular nota de crédito?',
    VOID_CREDIT_NOTE_PENDING_DESCRIPTION:
      'Se anulará {{number}}. Aún no se ha procesado, así que no afecta inventario, balance ni contabilidad.',
    VOID_CREDIT_NOTE_PROCESSED_DESCRIPTION:
      'Se anulará {{number}}: el stock devuelto vuelve al inventario, el balance de la factura se restablece y se registra el asiento de reverso.',
    CREDIT_NOTE_VOIDED: 'Nota de crédito anulada',
    PRINT_PURCHASE_ORDER: 'Imprimir',
    PRINT_ERROR: 'Error al imprimir',
    NCF_PREVIEW: 'próximo disponible',
    NCF_NOT_AVAILABLE: 'NCF {{ncfType}} no disponible',
    NCF_SETUP_REQUIRED: 'Configuración de NCF requerida',
    NCF_SETUP_DESCRIPTION:
      'No hay secuencia de NCF tipo {{ncfType}} (Nota de Débito) configurada. Configure una secuencia antes de crear notas de débito.',
    SETUP_NCF: 'Configurar NCF',
    SUPPLIER_NCF: 'NCF del Proveedor',
    SUPPLIER_NCF_HELP:
      'NCF que aparece en la factura del proveedor. Requerido para que la factura aparezca en el Formato 606.',
    PROVIDER_RNC: 'RNC del Proveedor',
    PROVIDER_RNC_MISSING_HINT:
      'El proveedor seleccionado no tiene RNC registrado. Ingresa el RNC aquí — es requerido para el Formato 606 (Compras).',
    PROVIDER_RNC_BACKFILL_HELP:
      'Este RNC se guardará también en el perfil del proveedor, así no tendrás que volver a ingresarlo.',
    PROVIDER_RNC_VALIDATE: 'Validar',
    PROVIDER_RNC_VALID: 'RNC válido — {{name}}',
    PROVIDER_RNC_REQUIRED_FOR_NCF:
      'El RNC del proveedor es requerido cuando se registra un NCF.',
    DGII_606_LABEL: 'Clasificación DGII 606',
    DGII_606_FIELDS_HINT:
      'Esta factura aparece en el Formato 606 cuando tiene NCF y RNC del proveedor, y la orden ha sido procesada. "Tipo de Bienes y Servicios" clasifica el gasto y "Forma de Pago" indica cómo se pactó la compra (usa "Compra a Crédito" si se paga después). Ambos se reportan tal como los selecciones aquí. Las retenciones de ITBIS/ISR no se capturan aquí: se registran al pagar, en Documentos Fiscales Proveedor.',
    DGII_GOODS_SERVICES_TYPE: 'Tipo de Bienes y Servicios Comprados',
    DGII_PAYMENT_FORM: 'Forma de Pago',
    DGII_ITBIS_RETENIDO: 'ITBIS Retenido',
    DGII_ISR_RETENIDO: 'ISR Retenido',
    DGII_GOODS_SERVICES_TYPES: {
      GASTOS_PERSONAL: 'Gastos de Personal',
      GASTOS_TRABAJO: 'Gastos por Trabajos, Suministros y Servicios',
      GASTOS_ARRENDAMIENTO: 'Arrendamientos',
      GASTOS_ACTIVO_FIJO: 'Gastos de Activos Fijos',
      GASTOS_REPRESENTACION: 'Gastos de Representación',
      GASTOS_FINANCIEROS: 'Gastos Financieros',
      GASTOS_SEGUROS: 'Gastos de Seguros',
      GASTOS_COMBUSTIBLES: 'Gastos de Combustibles',
      GASTOS_OTROS: 'Otros Gastos',
      COMPRAS_INVENTARIO:
        'Compras y Gastos que formarán parte del Costo de Venta',
      COMPRAS_ACTIVOS: 'Adquisiciones de Activos',
    },
    DGII_PAYMENT_FORMS: {
      EFECTIVO: 'Efectivo',
      CHEQUE_TRANSFERENCIA: 'Cheques / Transferencias / Depósitos',
      TARJETA_CREDITO_DEBITO: 'Tarjeta Crédito / Débito',
      COMPRA_CREDITO: 'Compra a Crédito',
      PERMUTA: 'Permuta',
      NOTA_CREDITO: 'Nota de Crédito',
      MIXTO: 'Mixto',
    },
    IMPORT_FROM_PO: 'Importar desde Orden de Compra',
    SEARCH_PROCUREMENT_PO_PLACEHOLDER: 'Buscar por número de orden (PO-...)',
    AFFECT_INVENTORY: 'Afectar inventario al crear la factura',
    AFFECT_INVENTORY_HELP:
      'Crea movimientos de inventario y marca todos los items como recibidos.',
    IMPORTED_FROM_PO: 'Importada desde {{poNumber}}',
    IMPORT_PO_ERROR: 'No se pudo cargar la orden de compra seleccionada',
  },
  SUPPLIER_TAX_DOCUMENTS: {
    TITLE: 'Documentos Fiscales de Proveedores',
    SUBTITLE:
      'Registro inmutable de comprobantes fiscales (fuente del DGII 606)',
    DETAIL_TITLE: 'Documento Fiscal',
    NCF: 'NCF',
    SUPPLIER: 'Proveedor',
    ISSUE_DATE: 'Fecha del comprobante',
    EXPENSE_TYPE: 'Tipo de Bienes/Servicios',
    SUBTOTAL: 'Subtotal',
    ITBIS: 'ITBIS facturado',
    TOTAL: 'Total',
    PAID: 'Pagado',
    BALANCE_DUE: 'Saldo pendiente',
    CREDITED_LABEL: 'Acreditado por notas de crédito',
    CURRENCY: 'Moneda',
    STATUS: 'Estado fiscal',
    STATUS_ISSUED: 'Emitida',
    STATUS_ANNULLED: 'Anulada',
    STATUS_SUPERSEDED: 'Reemplazada',
    FISCAL_SNAPSHOT: 'Registro fiscal',
    PAYMENTS: 'Pagos',
    PAYMENT_DATE: 'Fecha de pago',
    PAYMENT_METHOD: 'Forma de pago',
    AMOUNT_PAID: 'Monto pagado',
    ITBIS_RETAINED: 'ITBIS retenido',
    ISR_RETAINED: 'ISR retenido',
    ISR_TOGGLE_LABEL: 'Aplicar retención ISR',
    ISR_TOGGLE_HELP:
      'Actívalo cuando este pago debe retener ISR (servicios profesionales, alquileres, publicidad, pagos al exterior).',
    ISR_CATEGORY: 'Categoría de retención',
    ISR_AUTOCOMPUTED:
      'Calculado automáticamente desde subtotal × tasa DGII. Puedes ajustarlo si es necesario.',
    ISR_CAT_PERSONA_FISICA_15:
      'Honorarios Persona Física (15%) — Norma 14-2025',
    ISR_CAT_PERSONA_FISICA_10:
      'Honorarios Persona Física (10%) — Legado pre-2026',
    ISR_CAT_PERSONA_JURIDICA_27: 'Persona Jurídica No Residente (27%)',
    ISR_CAT_PUBLICIDAD_2: 'Publicidad y Promoción (2%)',
    ISR_CAT_FOREIGN_27: 'Pago al Exterior (27%)',
    ISR_CAT_OTHER: 'Otra (monto manual)',
    BANK_REFERENCE: 'Referencia bancaria',
    EXTERNAL_REFERENCE: 'Referencia externa',
    EXTERNAL_REFERENCE_HELP:
      'Único por negocio. Útil para imports automáticos del banco.',
    ADD_PAYMENT: 'Registrar pago',
    NO_PAYMENTS: 'No hay pagos registrados todavía.',
    CREDIT_NOTE: 'Nota de Crédito',
    CONFIRM_DELETE_PAYMENT_TITLE: '¿Eliminar el pago?',
    CONFIRM_DELETE_PAYMENT_DESCRIPTION:
      'El monto regresará al saldo pendiente del documento. Esta acción se puede revertir reingresando el pago.',
    PAYMENT_CREATED: 'Pago registrado',
    PAYMENT_DELETED: 'Pago eliminado',
    EMPTY_STATE: 'No hay documentos fiscales todavía.',
    NOT_FOUND: 'Documento no encontrado.',
    STATUSES: {
      ISSUED: 'Emitido',
      ANNULLED: 'Anulado',
      SUPERSEDED: 'Reemplazado',
    },
  },
  ACCEPTED_CURRENCIES: {
    TITLE: 'Monedas aceptadas al cobrar',
    DESCRIPTION:
      'Configura las monedas que la caja acepta en el modal de pago además de la moneda base ({{base}}). Cada moneda lleva su tasa actual de cambio; se congela sobre cada pago para que el histórico sobreviva a cambios futuros.',
    ADD: 'Agregar moneda',
    EMPTY:
      'Aún no has agregado monedas. Agrega USD, EUR u otra para aceptarla al cobrar.',
    CURRENCY_LABEL: 'Moneda',
    CURRENCY_PLACEHOLDER: 'Selecciona una moneda…',
    RATE_LABEL: 'Tasa: 1 {{code}} = ? {{base}}',
    INVALID_RATE: 'La tasa de cambio debe ser un número positivo.',
  },
  PAYMENT_CURRENCY: {
    CURRENCY_LABEL: 'Moneda',
    RECEIVED_LABEL: 'Recibido ({{code}})',
    RATE_APPLIED:
      'Recibido: {{received}} {{code}} × {{rate}} = {{base}} {{baseAmount}}',
    MODIFY_RATE: 'Modificar tasa',
    MODIFY_RATE_TITLE: 'Modificar tasa {{code}}',
    MODIFY_RATE_DESCRIPTION:
      'Actualiza la tasa de cambio 1 {{code}} = ? {{base}}. Se aplica al pago actual y queda como nueva tasa por defecto (fuente: MANUAL).',
    NEW_RATE_LABEL: '1 {{code}} = ? {{base}}',
  },
  /**
   * Next-invoice estimate card. Concept namespace because the SAME
   * card renders on the tenant's /account and on
   * /sadmin/billing?tab=invoices — a surface-scoped copy of these
   * three strings would drift the moment one is reworded.
   */
  NEXT_INVOICE_PREVIEW: {
    TITLE: 'Próxima factura (estimada)',
    TOTAL: 'Total aproximado',
    PERIOD: 'Periodo',
  },
  ACCOUNT: {
    PREPAID_TITLE: 'Capacidad prepagada',
    PREPAID_BUY_CTA: 'Comprar e-CF prepagados',
    PREPAID_BUY_DIALOG_TITLE: 'Comprar comprobantes prepagados',
    PREPAID_BUY_DIALOG_DESC:
      'Se consumen solo cuando se agota tu cupo mensual. No se reinician cada mes ni modifican tu suscripción.',
    PREPAID_QUANTITY_LABEL: 'Cantidad de comprobantes',
    PREPAID_UNIT_PRICE: 'Precio por comprobante',
    PREPAID_TOTAL_TO_CHARGE: 'Total a pagar',
    PREPAID_CHARGE_NOW_NOTICE:
      'Se cobrará ahora a tu método de pago. Es un cargo único, no recurrente.',
    PREPAID_EXPIRY_PREVIEW: 'Vence el {{date}} (12 meses).',
    PREPAID_CONFIRM_BUY: 'Confirmar compra',
    PREPAID_BUY_SUCCESS:
      'Compra realizada. Tu capacidad prepagada ya está disponible.',
    PREPAID_ALREADY_PROCESSED:
      'Esta compra ya se había procesado. No se realizó un segundo cobro.',
    PREPAID_EMPTY_CTA_HINT:
      'Compra comprobantes por adelantado para seguir facturando cuando se agote tu cupo mensual.',
    PREPAID_SUBTITLE:
      'Comprobantes e-CF comprados por adelantado. Se consumen solo cuando se agota tu cupo mensual y no se reinician cada mes.',
    PREPAID_TOTAL_REMAINING: 'Disponible',
    PREPAID_PURCHASED: 'Comprado',
    PREPAID_CONSUMED: 'Consumido',
    PREPAID_REMAINING: 'Disponible',
    PREPAID_EXPIRES_ON: 'Vence el {{date}}',
    PREPAID_NO_EXPIRY: 'Sin vencimiento',
    PREPAID_FORFEIT_NOTICE:
      'Los comprobantes no utilizados se pierden al vencer el paquete y no son reembolsables.',
    PREPAID_CONSUMPTION_ORDER: 'Se consume primero el paquete que vence antes.',
    PAYMENT_FAILED_BANNER_TITLE: 'Cobro rechazado',
    PAYMENT_FAILED_BANNER_DESC:
      'No pudimos cobrar la factura {{invoiceNumber}} ({{amount}}). Próximo intento: {{nextAttemptAt}}. Actualiza tu tarjeta antes de esa fecha para evitar la suspensión.',
    PAYMENT_FAILED_BANNER_EXHAUSTED_DESC:
      'Agotamos los intentos automáticos para {{invoiceNumber}} ({{amount}}). Actualiza tu método de pago hoy para evitar la suspensión.',
    PAYMENT_FAILED_BANNER_CTA: 'Actualizar tarjeta',
    PAYMENT_FAILED_BANNER_NEXT_ATTEMPT_SOON: 'en las próximas horas',
    PAY_INVOICE_SAVE_CARD_LABEL: 'Guardar tarjeta para futuros pagos',
    PAY_INVOICE_SAVE_CARD_HINT:
      'Marca esta opción si quieres que Neogeniux cobre tus facturas automáticamente. Sin marcar, solo pagas esta factura una vez.',
    PAY_INVOICE_SAVE_CARD_UNAVAILABLE:
      'No podemos guardar la tarjeta en este momento. El pago se procesará como una sola vez. Escríbenos a soporte@neogeniux.com si necesitas dejarla guardada.',
    PAY_INVOICE_SAVE_CARD_MANDATE:
      'Al marcar esta opción autorizas a Neogeniux SRL a guardar tu tarjeta y cargar automáticamente tus próximas facturas de suscripción. Puedes retirar esta autorización en cualquier momento desde /account.',
    PREPAY_BLOCKED_OUTSTANDING_TITLE: 'Tienes una factura pendiente',
    PREPAY_BLOCKED_OUTSTANDING_BODY:
      'Antes de adelantar tu próximo cobro debes pagar la factura pendiente {{invoiceNumber}}. Salda el saldo actual y vuelve a intentar prepagar.',
    PAYMENT_HISTORY_TITLE: 'Historial de pagos',
    PAYMENT_HISTORY_SUBTITLE:
      'Cargos exitosos, intentos rechazados y facturas programadas.',
    PAYMENT_HISTORY_EMPTY: 'Sin actividad de pagos todavía.',
    PAYMENT_HISTORY_COL_DATE: 'Fecha Factura',
    PAYMENT_HISTORY_COL_ISSUED: 'Fecha Emisión',
    PAYMENTS_TAX_ID_PREFIX: 'RNC',
    PAYMENTS_PRICE_BEFORE: 'antes',
    PAYMENT_HISTORY_DOWNLOAD_PDF: 'Descargar PDF',
    PAYMENT_HISTORY_PDF_FAILED: 'No se pudo generar el PDF',
    PAYMENT_HISTORY_COL_ID: 'ID',
    PAYMENT_HISTORY_COL_METHOD: 'Método de pago',
    PAYMENT_HISTORY_COL_AMOUNT: 'Monto',
    PAYMENT_HISTORY_COL_STATUS: 'Estado',
    PAYMENT_HISTORY_COL_RECEIPT: 'Recibo',
    PAYMENT_HISTORY_COL_INVOICE: 'Comprobante',
    PAYMENT_HISTORY_STATUS_SUCCESS: 'Éxito',
    PAYMENT_HISTORY_STATUS_DECLINED: 'Rechazado',
    PAYMENT_HISTORY_STATUS_REFUND: 'Reembolso',
    PAYMENT_HISTORY_STATUS_SCHEDULED: 'Programado',
    PAYMENT_HISTORY_STATUS_OVERDUE: 'Vencido',
    PAYMENT_HISTORY_SCHEDULED_DUE: 'Vence {{date}}',
    PAYMENT_HISTORY_OVERDUE_SINCE: 'Venció {{date}}',
    PAYMENT_HISTORY_FAILURE_INSUFFICIENT_FUNDS:
      'Fondos insuficientes en la tarjeta.',
    PAYMENT_HISTORY_FAILURE_CARD_DECLINED:
      'La tarjeta fue rechazada por el banco.',
    PAYMENT_HISTORY_FAILURE_EXPIRED_CARD: 'La tarjeta está vencida.',
    PAYMENT_HISTORY_FAILURE_INVALID_PAYMENT_METHOD:
      'Método de pago inválido. Actualiza los datos de tu tarjeta.',
    PAYMENT_HISTORY_FAILURE_REQUIRES_ACTION:
      'Tu banco pidió verificación (3DS). Completa la autenticación para procesar el pago.',
    PAYMENT_HISTORY_FAILURE_NETWORK_AMBIGUOUS:
      'Respuesta ambigua del procesador. Estamos reconciliando el cargo.',
    PAYMENT_HISTORY_FAILURE_PROVIDER_ERROR:
      'Error del procesador de pagos. Contáctanos si persiste.',
    PAYMENT_HISTORY_FAILURE_UNKNOWN: 'Error desconocido al procesar el pago.',
    PAYMENT_HISTORY_COL_ACTIONS: 'Acciones',
    PAYMENT_HISTORY_PAY_NOW: 'Pagar',
    PAYMENT_HISTORY_PAY_NOW_TOOLTIP: 'Pagar ahora',
    TITLE: 'Configuración de Cuenta',
    SUBTITLE: 'Administra tu cuenta y preferencias',
    PENDING_INVOICES_TITLE: 'Facturas pendientes',
    PENDING_INVOICES_OVERDUE: 'Vencida',
    PENDING_INVOICES_PAY_NOW: 'Pagar ahora',
    PENDING_INVOICES_NEEDS_PM: 'Agrega un método de pago para cobrar',
    PREPAY_TITLE: 'Adelanta tu próximo pago',
    PREPAY_DESCRIPTION:
      'Cobra ahora la próxima factura con tu tarjeta guardada. Elige cuántos meses adelantar.',
    PREPAY_MONTH_SELECTOR_LABEL: 'Meses a prepagar',
    PREPAY_MONTHS_OPTION: '{{count}} meses',
    PREPAY_BUTTON: 'Pagar {{amount}}',
    PREPAY_ANNUAL_DISCOUNT_BADGE: 'Ahorra 7% al pagar 12 meses',
    PREPAY_SUCCESS_TOAST: 'Pago aplicado. Cubre hasta {{until}}.',
    PREPAY_REQUIRES_ACTION_TOAST:
      'La tarjeta requiere autenticación 3DS. Stripe enviará un correo al titular para completar el pago.',
    PREPAY_FAILED_TOAST:
      'El prepago falló. La factura queda abierta y se reintentará automáticamente.',
    PREPAY_LOADING_PREVIEW: 'Calculando total…',
    PREPAY_ANNUAL_ONLY_HINT:
      'Tu suscripción es anual. Solo puedes prepagar el próximo ciclo (12 meses).',
    PREPAY_CONFIRMATION_TITLE: 'Pago aplicado',
    PREPAY_CONFIRMATION_DESC:
      'Tu próximo ciclo se ha adelantado. Los siguientes detalles quedan en tu historial de pagos.',
    PREPAY_CONFIRMATION_AMOUNT_LABEL: 'Monto cobrado',
    PREPAY_CONFIRMATION_COVERAGE_LABEL: 'Cobertura',
    PREPAY_CONFIRMATION_INVOICE_LABEL: 'Factura',
    PREPAY_CONFIRMATION_NEXT_CYCLE_LABEL: 'Próximo ciclo',
    CHARGE_SAVED_CTA: 'Cobrar con tarjeta guardada •••• {{last4}}',
    CHARGE_SAVED_SUCCESS: 'Cobro exitoso con tarjeta •••• {{last4}}.',
    CHARGE_SAVED_REQUIRES_ACTION:
      'La tarjeta requiere autenticación 3DS. Stripe enviará un correo al titular para completar el pago.',
    CHARGE_SAVED_FAILED: 'El cobro falló. Verifica la tarjeta o intenta otra.',
    UPDATE_CARD_CTA: 'Actualizar tarjeta para completar el cobro',
    COLLECTION_CHIP_PROCESSING: 'Cobrando…',
    COLLECTION_CHIP_DUNNING: 'Reintentando',
    COLLECTION_CHIP_ACTION_REQUIRED: 'Requiere acción',
    CHARGE_SAVED_ALREADY_PAID: 'La factura {{number}} ya se pagó el {{date}}',
    CHARGE_SAVED_NEXT_PENDING:
      'Próxima factura pendiente: {{number}} por {{amount}}',
    PAY_INVOICE_TITLE: 'Pagar factura #{{number}}',
    PAY_INVOICE_INTRO:
      'Vas a cobrar {{amount}} con tu tarjeta guardada o una nueva. Continúa para ingresar los datos de pago.',
    PAY_INVOICE_START: 'Continuar',
    PAY_INVOICE_CONFIRM: 'Pagar {{amount}}',
    PAY_INVOICE_SUCCESS: 'Pago procesado. La factura se marcará como pagada.',
    PAY_INVOICE_SUCCESS_PENDING_SETTLE:
      'Pago aprobado por el banco. Estamos confirmando la factura — actualiza la página en unos segundos si no ves el cambio.',
    PAY_INVOICE_MISSING_SECRET:
      'Stripe no devolvió un secreto de cliente. Intenta de nuevo o contacta soporte.',
    AVAILABLE_ADDONS_TITLE: 'Módulos y addons disponibles',
    AVAILABLE_ADDONS_SUBTITLE:
      'Agrega funcionalidad a tu plan al mismo precio que ves en el constructor.',
    ADDON_KIND_MODULE: 'Módulo',
    ADDON_KIND_ADDON: 'Addon',
    ADDON_ADD_CTA: 'Agregar',
    ADDON_REMOVE_CTA: 'Eliminar',
    ADDON_OWNED_BADGE: 'Activo',
    ADDON_KIND_LABEL: 'Tipo',
    ADDON_REMOVE_SCHEDULED_BADGE: 'Eliminación programada',
    ADDON_REQUIRES_TITLE: '{{label}} necesita lo siguiente para funcionar',
    ADDON_REQUIRES_HINT:
      'Los ítems marcados se agregan al mismo tiempo. Puedes desmarcar alguno, pero {{label}} podría no funcionar correctamente sin ellos.',
    PER_MONTH_SHORT: 'mes',
    HISTORY_ACTOR_ROLE_SIGNUP: 'Registro',
    HISTORY_ACTOR_ROLE_SUPPORT: 'Soporte',
    SCHEDULED_TITLE: 'Bajas programadas',
    SCHEDULED_SUBTITLE:
      'Estos cambios reducen tu suscripción. Mantienes el acceso completo hasta la fecha indicada.',
    SCHEDULED_KEEP_ACCESS_UNTIL: 'Acceso disponible hasta el {{date}}.',
    SCHEDULED_KEEP_CTA: 'Mantener acceso',
    SCHEDULED_KEEP_CONFIRM_TITLE: '¿Cancelar la baja programada?',
    SCHEDULED_KEEP_CONFIRM_DESCRIPTION:
      'Conservarás el acceso al módulo o addon y la cuota se incluirá en tu próximo cobro.',
    SCHEDULED_CANCEL_SUCCESS: 'Baja cancelada. Acceso mantenido.',
    SCHEDULED_CANCEL_ERROR: 'No se pudo cancelar la baja programada.',
    SCHEDULED_APPLY_NOW_CTA: 'Activar ahora',
    SCHEDULED_APPLY_NOW_CONFIRM_TITLE: '¿Activar el cambio inmediatamente?',
    SCHEDULED_APPLY_NOW_CONFIRM_DESCRIPTION:
      'Perderás acceso al plan actual ahora mismo (no al final del ciclo). Tu suscripción se ajusta al nuevo plan y sus módulos, addons y cuotas se aplican de inmediato. Esta acción no se puede deshacer.',
    SCHEDULED_APPLY_NOW_SUCCESS: 'Cambio activado. Nuevo plan en vigor.',
    SCHEDULED_COMPLIANCE_BLOCKED: 'Bloqueada por compliance',
    SCHEDULED_COMPLIANCE_BREACH_LINE:
      '{{resource}}: tienes {{current}}, el plan permite {{limit}}.',
    SCHEDULED_SUMMARY_REMOVE: 'Quitar {{name}}',
    SCHEDULED_SUMMARY_ADD: 'Agregar {{name}}',
    SCHEDULED_SUMMARY_PLAN_CHANGE: 'Cambiar a {{name}}',
    OWNED_TITLE: 'Mis módulos y addons',
    OWNED_SUBTITLE:
      'Todo lo que tu plan incluye hoy. Quita lo que ya no necesitas; el cambio aplica al final del ciclo de facturación.',
    OWNED_REMOVE_CTA: 'Quitar',
    OWNED_REMOVE_CONFIRM_TITLE: '¿Quitar este módulo de tu plan?',
    OWNED_REMOVE_CONFIRM_DESCRIPTION:
      'Vas a quitar "{{name}}". El acceso se desactiva inmediatamente.',
    OWNED_REMOVE_CONFIRM_OK: 'Sí, quitar ahora',
    OWNED_REMOVE_SCHEDULED: 'Ítem removido. Acceso desactivado.',
    OWNED_REMOVE_ERROR: 'No se pudo quitar el ítem.',
    OWNED_REMOVE_BLOCKED_TITLE: 'No se puede quitar',
    OWNED_REMOVE_PLAN_INCLUDED:
      'Este ítem viene incluido con tu plan contratado. Cambia de plan para dejar de usarlo.',
    OWNED_REMOVE_INVOICE_ADJUST_TITLE: 'Tu factura pendiente se ajustará',
    OWNED_REMOVE_INVOICE_ADJUST_BODY:
      'Tu factura {{invoice}} se ajustará de RD${{original}} a RD${{updated}} — usaste {{daysUsed}} de {{daysCoverage}} días.',
    OWNED_REMOVE_NO_INVOICE_HINT:
      'No hay factura pendiente por ajustar. La baja se aplicará sin cargos adicionales.',
    OWNED_REMOVE_BLOCKED_BY_PLAN_CHANGE:
      'Tu plan tiene un cambio programado. Cancela primero esa baja desde "Bajas programadas" si quieres ajustar módulos individuales.',
    OWNED_REMOVE_SCHEDULED_TITLE: 'Se aplicará al finalizar el ciclo actual',
    OWNED_REMOVE_SCHEDULED_BODY:
      'Conservas el acceso hasta el {{effectiveAt}}. En el próximo cobro se reducirá {{delta}}. No hay reembolso — pagaste el ciclo completo.',
    OWNED_REMOVE_COURTESY_WARNING_TITLE:
      'Este ítem tiene precio especial por cortesía',
    OWNED_REMOVE_COURTESY_WARNING_BODY:
      'El equipo aplicó un precio especial ({{delta}}/mes) sobre este ítem. Al removerlo perderás ese descuento — si vuelves a contratarlo será al precio de catálogo. Confirma sólo si estás seguro.',
    OWNED_REMOVE_COURTESY_PERPETUAL_BADGE: 'Cortesía indefinida',
    OWNED_GRANDFATHERED: 'Heredado',
    PAYPAL_VAULT_TITLE: 'Pagar con tarjeta vía PayPal',
    PAYPAL_VAULT_SUBTITLE:
      'Guarda tu tarjeta de forma segura mediante PayPal. Los cobros se procesan automáticamente al renovar.',
    PAYPAL_VAULT_OPTION: 'Tarjeta vía PayPal',
    PAYPAL_VAULT_SAVE: 'Guardar tarjeta',
    PAYPAL_VAULT_REMOVE: 'Quitar tarjeta',
    PAYPAL_VAULT_SAVED:
      'Tarjeta guardada. Los próximos cobros aplican automáticamente.',
    PAYPAL_VAULT_REMOVED: 'Tarjeta eliminada.',
    PAYPAL_VAULT_ERROR: 'No pudimos procesar tu tarjeta. Intenta de nuevo.',
    PAYPAL_VAULT_INELIGIBLE:
      'Tu cuenta PayPal no admite captura de tarjeta. Conecta PayPal directamente.',
    PAYPAL_VAULT_CLIENT_ID_MISSING:
      'Configura VITE_PAYPAL_CLIENT_ID para habilitar este método.',
    PAYPAL_VAULT_STORED_CARD: '{{brand}} terminada en {{last4}}',
    PAYMENT_METHOD_COMING_SOON: {
      BADGE: 'Próximamente',
      TITLE: '{{method}} estará disponible muy pronto',
      DESCRIPTION:
        'Mientras tanto, nuestro equipo puede habilitarte el pago manualmente. Escríbenos por WhatsApp o correo y coordinamos la activación.',
      EMAIL_LABEL: 'Correo',
      WHATSAPP_MESSAGE:
        'Hola Neogeniux, quiero activar {{method}} como método de pago en mi suscripción.',
      EMAIL_SUBJECT: 'Activación de método de pago — {{method}}',
      EMAIL_BODY:
        'Hola equipo Neogeniux,\n\nMe interesa activar {{method}} como método de pago en mi cuenta. Por favor, contáctenme para coordinar los pasos.\n\nGracias.',
      CONTACT_TITLE: 'Contáctanos para habilitar tu método de pago',
      CONTACT_DESCRIPTION:
        'Los métodos de pago automáticos aún no están disponibles en tu cuenta. Escríbenos por WhatsApp o correo y activamos el cobro manualmente para tu suscripción.',
      CONTACT_WHATSAPP_MESSAGE:
        'Hola Neogeniux, necesito habilitar un método de pago para mi suscripción.',
      CONTACT_EMAIL_SUBJECT: 'Habilitar método de pago para mi suscripción',
      CONTACT_EMAIL_BODY:
        'Hola equipo Neogeniux,\n\nNecesito habilitar un método de pago para mi suscripción. Por favor, contáctenme para coordinar los pasos.\n\nGracias.',
      EMPTY_STATE_TITLE: 'Aún no hay métodos de pago disponibles',
      EMPTY_STATE_DESC:
        'Nuestro equipo puede habilitarte el cobro manualmente mientras activamos los métodos automáticos.',
      EMPTY_STATE_CTA: 'Contactar soporte',
    },
    PAYMENTS_NEXT_PAYMENT_BADGE: 'Próximo',
    PAYMENTS_NEXT_PAYMENT_DESC: 'Próximo cobro',
    PAYMENTS_STATUS_UPCOMING: 'Estimado',
    PAYMENTS_COL_DESCRIPTION: 'Concepto',
    PAYMENTS_SUBSCRIPTION_PREFIX: 'Suscripción',
    PAYMENTS_EXPORT_PDF: 'PDF',
    PAYMENTS_EXPORT_CSV: 'CSV',
    PAYMENTS_PRINT_ROW: 'Imprimir',
    PAYMENTS_GENERATED_AT: 'Generado',
    PAYMENTS_COL_INVOICE: 'Factura',
    PAYMENTS_PRINT_DOC_LABEL: 'Pre Factura',
    PAYMENTS_COL_PERIOD: 'Fecha Vencimiento',
    PAYMENTS_DRAFT_LABEL: 'Borrador',
    PAYMENTS_PREVIEW_LABEL: 'Vista previa',
    PAYMENTS_FISCAL_TOGGLE: 'Generar mis facturas con comprobante fiscal',
    PAYMENTS_FISCAL_TOGGLE_HINT:
      'Se aplica a facturas nuevas y a las pendientes. Facturas ya cobradas no se pueden modificar.',
    PAYMENTS_FISCAL_RNC_INLINE_BANNER_TITLE: 'Necesitamos un RNC válido',
    PAYMENTS_FISCAL_RNC_INLINE_BANNER_BODY:
      'Para activar tus facturas fiscales, ingresa tu RNC (9 dígitos) o Cédula (11 dígitos). Lo validaremos contra el registro DGII antes de activar.',
    PAYMENTS_FISCAL_TOGGLE_SAVED: 'Preferencia guardada.',
    PAYMENTS_FISCAL_TOGGLE_UPDATED:
      '{{count}} factura(s) pendiente(s) actualizada(s)',
    PAYMENTS_FISCAL_TOGGLE_LOCKED:
      '{{count}} factura(s) ya cobrada(s) — no se modificaron',
    PAYMENTS_FISCAL_TOGGLE_RETRO:
      'Se emitió factura de ITBIS retroactivo para la última pagada',
    PAYMENTS_FISCAL_RNC_MODAL_TITLE: 'RNC o Cédula requerido para facturar',
    PAYMENTS_FISCAL_RNC_MODAL_DESCRIPTION:
      'Para emitir tus facturas con comprobante fiscal necesitamos tu RNC (9 dígitos) o Cédula (11 dígitos). Lo validamos contra el registro DGII y queda guardado para los próximos cobros.',
    PAYMENTS_FISCAL_RNC_LABEL: 'RNC o Cédula',
    PAYMENTS_FISCAL_RNC_PLACEHOLDER: 'Ingrese el RNC o Cédula (ej: 123456789)',
    PAYMENTS_FISCAL_RNC_LEGAL_NAME_LABEL: 'Razón social',
    PAYMENTS_FISCAL_RNC_VALIDATE_CTA: 'Validar',
    PAYMENTS_FISCAL_RNC_SAVE_CTA: 'Guardar y activar',
    PAYMENTS_FISCAL_DOWNLOAD: 'Descargar comprobante',
    PAYMENTS_FROM: 'De',
    PAYMENTS_BILLED_TO: 'Facturado a',
    PAYMENTS_FOOTER_NOTE:
      'Documento generado automáticamente por Neogeniux. Para consultas escribe a support@neogeniux.com.',
    PAYMENTS_CREDIT_FROM_CLAIM: 'Crédito por reclamación — {{name}}',
    PAYMENTS_AUTO_CHARGE_PREFIX: 'Cobro automático',
    PAYMENTS_AUTO_CHARGE_METHOD_CARD: 'Tarjeta',
    PAYMENTS_AUTO_CHARGE_METHOD_PAYPAL: 'PayPal',
    PAYMENTS_SOURCE_MANUAL: 'Pago manual',
    PAYMENTS_SOURCE_BANK_TRANSFER: 'Transferencia bancaria',
    PAYMENTS_SOURCE_CARD_PREPAYMENT: 'Prepago con tarjeta',
    PAYMENTS_SOURCE_ADJUSTMENT: 'Ajuste',
    PAYMENTS_SOURCE_RECURRING: 'Suscripción recurrente',
    PAYMENTS_SOURCE_CHANGE_REQUEST: 'Cambio de plan',
    PAYMENTS_UPCOMING_TITLE: 'Facturas por pagar',
    PAYMENTS_UPCOMING_SUBTITLE:
      'Facturas emitidas pendientes de pago. Si no se pagan antes del vencimiento, la suscripción entra en mora y luego se suspende.',
    PAYMENTS_UPCOMING_EMPTY: 'No tienes facturas por pagar.',
    PAYMENTS_PREVIEW_TITLE: 'Próxima factura estimada',
    // Tenant-facing hint for the next-invoice card. Deliberately says
    // nothing about crons or tables — the sadmin copy of that hint
    // lives in SADMIN_BILLING and is operator-only.
    NEXT_INVOICE_PREVIEW_HINT:
      'Estimado del próximo ciclo. El monto final puede variar si cambias tu plan o tus complementos antes del cierre.',
    PAYMENTS_PREVIEW_SUBTITLE:
      'Vista previa de tu próximo cobro. NO se cobra hoy — se emite automáticamente cuando termine tu ciclo pagado actual.',
    PAYMENTS_PREVIEW_ISSUE_ON: 'Se emite el {{date}}',
    PAYMENTS_PREVIEW_NO_PAYMENT: 'No requiere pago ahora',
    PAYMENTS_URGENCY_OVERDUE: 'Vencida hace {{days}} d',
    PAYMENTS_URGENCY_TODAY: 'Vence hoy',
    PAYMENTS_URGENCY_DUE_SOON: 'Vence en {{days}} d',
    PAYMENTS_PAID_TITLE: 'Pagos realizados',
    PAYMENTS_PAID_SUBTITLE:
      'Historial de cargos confirmados. Descarga el comprobante fiscal desde la columna Concepto.',
    CLAIM_CTA: 'Reclamar',
    CLAIM_TITLE: 'Reclamación de cargo',
    CLAIM_DESCRIPTION:
      'Selecciona la línea que deseas reclamar y describe el motivo. El equipo de soporte revisará la solicitud.',
    CLAIM_FIELD_ITEM: 'Línea a reclamar',
    CLAIM_FIELD_REASON: 'Motivo',
    CLAIM_PLACEHOLDER_REASON:
      'Describe por qué reclamas este cargo (mínimo 5 caracteres).',
    CLAIM_SUBMIT: 'Enviar reclamación',
    CLAIM_SUCCESS: 'Reclamación enviada. El equipo te responderá pronto.',
    CLAIM_ERROR: 'No se pudo enviar la reclamación.',
    CLAIM_STATUS_PENDING: 'Pendiente',
    CLAIM_STATUS_APPROVED: 'Aprobada',
    CLAIM_STATUS_REJECTED: 'Rechazada',
    CLAIM_BADGE_FILED: 'Reclamada',
    SADMIN_CLAIMS_TITLE: 'Reclamaciones',
    SADMIN_CLAIMS_EMPTY: 'Sin reclamaciones para este negocio.',
    SADMIN_CLAIMS_COL_DATE: 'Fecha',
    SADMIN_CLAIMS_COL_INVOICE: 'Factura',
    SADMIN_CLAIMS_COL_ITEM: 'Línea',
    SADMIN_CLAIMS_COL_AMOUNT: 'Monto',
    SADMIN_CLAIMS_COL_STATUS: 'Estado',
    SADMIN_CLAIMS_COL_REASON: 'Motivo',
    SADMIN_CLAIMS_APPROVE: 'Aprobar',
    SADMIN_CLAIMS_REJECT: 'Rechazar',
    SADMIN_CLAIMS_REJECT_REASON_REQUIRED:
      'El motivo del rechazo es obligatorio.',
    SADMIN_CLAIMS_REVIEWER_REASON: 'Motivo del revisor (opcional)',
    SADMIN_CLAIMS_APPROVE_CONFIRM:
      'Al aprobar, se generará un crédito de {{amount}} en el próximo cobro.',
    SADMIN_CLAIMS_REJECT_CONFIRM:
      'El cliente verá el motivo del rechazo en su Historial de pagos.',
    SADMIN_CLAIMS_APPROVED_TOAST: 'Reclamación aprobada con crédito aplicado.',
    SADMIN_CLAIMS_REJECTED_TOAST: 'Reclamación rechazada.',
    TAB_OVERVIEW: 'Inicio',
    TAB_HISTORY: 'Historial',
    HISTORY_TITLE: 'Historial de suscripción',
    HISTORY_SUBTITLE:
      'Auditoría cronológica de cada cambio en tu plan, módulos, addons, capacidades y pagos.',
    HISTORY_EMPTY: 'Aún no hay eventos para mostrar.',
    HISTORY_ACTOR: 'Por: {{role}}',
    HISTORY_ACTOR_ROLE_SELF: 'Titular de la cuenta',
    HISTORY_ACTOR_ROLE_SADMIN: 'Administrador Neogeniux',
    HISTORY_ACTOR_ROLE_CUSTOMER: 'Cliente',
    HISTORY_ACTOR_ROLE_SYSTEM: 'Sistema',
    HISTORY_ACTOR_ROLE_BILLING: 'Facturación automática',
    HISTORY_EXPAND: 'Ver detalles',
    HISTORY_COLLAPSE: 'Ocultar detalles',
    HISTORY_BEFORE: 'Antes',
    HISTORY_AFTER: 'Después',
    HISTORY_METADATA: 'Detalles',
    HISTORY_EVENT_CREATED: 'Suscripción creada',
    HISTORY_EVENT_UPGRADED: 'Plan ampliado',
    HISTORY_EVENT_DOWNGRADED: 'Plan reducido',
    HISTORY_EVENT_QUOTA_INCREASED: 'Capacidad ampliada',
    HISTORY_EVENT_QUOTA_DECREASED: 'Capacidad reducida',
    HISTORY_EVENT_GRANT_ADDED: 'Cortesía otorgada',
    HISTORY_EVENT_GRANT_REMOVED: 'Cortesía retirada',
    HISTORY_EVENT_MODULE_ADDED: 'Módulo agregado',
    HISTORY_EVENT_MODULE_REMOVED: 'Módulo retirado',
    HISTORY_EVENT_ADDON_ADDED: 'Addon agregado',
    HISTORY_EVENT_ADDON_REMOVED: 'Addon retirado',
    HISTORY_EVENT_PRICE_CHANGED: 'Precio actualizado',
    HISTORY_EVENT_STATUS_CHANGED: 'Estado actualizado',
    HISTORY_EVENT_BILLING_ADJUSTED: 'Ajuste de facturación',
    HISTORY_EVENT_BILLING_PREPAYMENT_ONDEMAND: 'Pago on-demand',
    HISTORY_EVENT_CHANGE_REQUESTED: 'Solicitud de cambio enviada',
    HISTORY_EVENT_CHANGE_APPROVED: 'Cambio aprobado',
    HISTORY_EVENT_CHANGE_REJECTED: 'Cambio rechazado',
    HISTORY_EVENT_CHANGE_CANCELLED: 'Cambio cancelado',
    HISTORY_EVENT_CHANGE_APPLIED: 'Cambio aplicado',
    HISTORY_REASON_SELF_SERVICE_IMMEDIATE:
      'Aprobación automática (aplicada de inmediato)',
    HISTORY_REASON_SELF_SERVICE_SCHEDULED:
      'Aprobación automática (programada para fin de ciclo)',
    HISTORY_REASON_PLAN_DOWNGRADE_SCHEDULED:
      'Cambio de plan programado para fin de ciclo',
    HISTORY_REASON_CLAIM_FILED: 'Reclamación abierta: {{name}}',
    HISTORY_REASON_CLAIM_APPROVED:
      'Reclamación aprobada — crédito aplicado en el próximo cobro',
    HISTORY_REASON_CLAIM_REJECTED: 'Reclamación rechazada: {{name}}',
    HISTORY_REASON_BILLING_INTERVAL_CHANGED:
      'Ciclo de facturación cambiado: {{from}} → {{to}}',
    HISTORY_REASON_RECURRING_INVOICE:
      'Factura {{number}} emitida — período {{from}} a {{to}}',
    HISTORY_REASON_INVOICE_PAID: 'Factura {{number}} pagada',
    HISTORY_REASON_INVOICE_FAILED: 'Factura {{number}} falló',
    HISTORY_REASON_INVOICE_FAILED_WITH_REASON:
      'Factura {{number}} falló: {{reason}}',
    HISTORY_REASON_CANCEL_AT_PERIOD_END:
      'Suscripción cancelada al cierre del período actual',
    HISTORY_REASON_CANCEL_IMMEDIATE: 'Suscripción cancelada de inmediato',
    HISTORY_REASON_RESUME: 'Suscripción reanudada',
    HISTORY_REASON_CANCEL_REVERTED: 'Cancelación revertida',
    HISTORY_REASON_APPLIED_CATALOG_PLAN:
      'Plan {{plan}} aplicado ({{count}} funcionalidades)',
    HISTORY_REASON_APPLIED_CUSTOM_PLAN:
      'Selección personalizada aplicada ({{count}} funcionalidades)',
    HISTORY_REASON_SCHEDULED_REDUCTION:
      'Reducción programada: {{count}}× {{label}} vigente desde {{effectiveAt}}',
    HISTORY_REASON_SCHEDULED_REMOVAL:
      'Baja programada: {{label}} vigente desde {{effectiveAt}}',
    HISTORY_REASON_IMMEDIATE_REMOVAL:
      'Baja inmediata: {{label}} (sin ciclo de facturación activo)',
    HISTORY_ADDON_LABEL_EXTRA_BRANCH: 'sucursal extra',
    HISTORY_ADDON_LABEL_EXTRA_USER: 'usuario extra',
    HISTORY_ADDON_LABEL_EXTRA_WAREHOUSE: 'almacén adicional',
    HISTORY_ADDON_LABEL_EXTRA_PRODUCT: 'producto adicional',
    HISTORY_ADDON_LABEL_EXTRA_KDS_SCREEN: 'pantalla KDS adicional',
    HISTORY_ADDON_LABEL_EXTRA_ECF: 'e-CF adicional',
    QUOTA_TITLE: 'Capacidad de tu negocio',
    QUOTA_SUBTITLE:
      'Uso actual vs límites de tu suscripción. Aumenta la capacidad agregando módulos o addons.',
    QUOTA_USERS: 'Usuarios',
    QUOTA_BRANCHES: 'Sucursales',
    QUOTA_WAREHOUSES: 'Almacenes',
    QUOTA_PRODUCTS: 'Productos',
    QUOTA_INVOICES_MONTHLY: 'e-CF (este mes)',
    QUOTA_INVOICES_TOTAL: 'Facturas (este mes)',
    QUOTA_KDS_SCREENS: 'Pantallas KDS',
    QUOTA_UNLIMITED: 'Ilimitado',
    QUOTA_ECF_OVERAGE_RATE: 'Excedente: RD${{rate}} por e-CF adicional',
    QUOTA_ECF_OVERAGE_ACCRUED:
      'Acumulado este mes: {{units}} × RD${{rate}} = RD${{amount}}',
    COURTESY_BANNER_TITLE: 'Ofertas activas para ti',
    COURTESY_FOREVER: 'Para siempre',
    COURTESY_TEMPORAL: 'temporal',
    COURTESY_UNTIL: 'Vence el {{date}}',
    COURTESY_OPEN_ENDED: 'sin vencimiento',
    COURTESY_FREE: 'gratis',
    COURTESY_AT_PRICE: 'a {{price}} c/u',
    COURTESY_COMP_VALUE: 'Valor: {{value}}',
    COURTESY_ROW_BADGE: 'Cortesía: +{{qty}} unidades ({{tag}})',
    QUOTA_INCREASE_CTA: 'Aumentar',
    QUOTA_INCREASE_CONFIRM_TITLE: 'Aumentar capacidad de {{label}}',
    QUOTA_INCREASE_CONFIRM_DESCRIPTION:
      'Confirma los detalles antes de aplicar el cambio. El cargo se reflejará en tu próximo pago y en el historial.',
    QUOTA_REDUCE_CTA: 'Reducir',
    QUOTA_REDUCE_IN_USE_SUMMARY:
      'Actualmente usas {{inUse}} de {{purchased}} espacio(s) adicional(es) contratado(s).',
    QUOTA_REDUCE_MODAL_TITLE: 'Reducir capacidad de {{label}}',
    QUOTA_REDUCE_MODAL_DESCRIPTION:
      'La capacidad se mantiene hasta el {{effectiveAt}} (fin del ciclo actual). En el próximo cobro se aplicará el ahorro reflejado abajo. El total no se ajusta hoy — no hay reembolso porque conservas la capacidad hasta el fin del ciclo.',
    QUOTA_REDUCE_EFFECTIVE_AT: 'Vigente desde',
    QUOTA_REDUCE_NEXT_INVOICE_SAVINGS: 'Ahorro en próximo cobro',
    QUOTA_REDUCE_CURRENT_MONTHLY: 'Mensual actual',
    QUOTA_REDUCE_NEXT_MONTHLY: 'Mensual después',
    QUOTA_REDUCE_CONFIRM: 'Confirmar reducción',
    QUOTA_REDUCE_SUCCESS_TOAST:
      'Reducción programada. Se aplicará el {{effectiveAt}}.',
    QUOTA_REDUCE_LOADING: 'Calculando...',
    QUOTA_INCREASE_CONFIRM_CTA: 'Confirmar y aplicar',
    QUOTA_INCREASE_RESOURCE: 'Recurso',
    QUOTA_INCREASE_CHANGE: 'Capacidad',
    QUOTA_INCREASE_MONTHLY_PRICE: 'Total mensual',
    QUOTA_INCREASE_UNIT_PRICE: 'Precio por unidad',
    QUOTA_INCREASE_QUANTITY: 'Cantidad',
    QUOTA_INCREASE_QTY_PLUS: 'Aumentar cantidad',
    QUOTA_INCREASE_QTY_MINUS: 'Reducir cantidad',
    QUOTA_INCREASE_BILLING_NOTE:
      'El monto se sumará a tu próximo recibo y aparecerá en Historial de pagos.',
    QUOTA_INCREASE_ANNUAL_BADGE: 'Facturación anual',
    QUOTA_INCREASE_TOTAL_CHARGE: 'Total a cobrar',
    QUOTA_INCREASE_ANNUAL_NOTE_DATED:
      'Tu plan se factura anualmente: este monto cubre los meses restantes del ciclo y se sumará a tu próximo recibo del {{date}}. Aparecerá en Historial de pagos.',
    QUOTA_INCREASE_ANNUAL_NOTE:
      'Tu plan se factura anualmente: este monto cubre los meses restantes del ciclo y se sumará a tu próximo recibo anual. Aparecerá en Historial de pagos.',
    QUOTA_INCREASE_ANNUAL_FORMULA:
      '{{monthly}}/mes × {{months}} meses restantes = {{total}}',
    QUOTA_INCREASE_TIERS_TITLE: 'Niveles de precio por cantidad',
    QUOTA_INCREASE_SUCCESS_TITLE: 'Capacidad aumentada',
    QUOTA_INCREASE_SUCCESS_DESCRIPTION:
      'La capacidad de {{label}} se actualizó y el cargo se reflejará en tu próximo pago.',
    NEXT_PAYMENT_TITLE: 'Próximo cobro',
    NEXT_PAYMENT_SUBTITLE:
      'Resumen estimado del próximo pago según tus módulos, addons y consumo.',
    NEXT_PAYMENT_SUBTITLE_DATE:
      'Próximo cobro estimado para el {{date}}, según tus módulos, addons y consumo.',
    NEXT_PAYMENT_EMPTY: 'No hay cargos previstos para tu próxima facturación.',
    NEXT_PAYMENT_OVERAGES: 'Excedentes por consumo',
    NEXT_PAYMENT_GRANDFATHERED: 'Cortesía',
    NEXT_PAYMENT_MONTHLY_TOTAL: 'Total mensual',
    NEXT_PAYMENT_ANNUAL_TOTAL: 'Total anual',
    NEXT_PAYMENT_COUPON_DISCOUNT: 'Cupón aplicado',
    NEXT_PAYMENT_TAX_NOTE:
      'Los impuestos se incluyen solo si la factura se encuentra no paga y/o en estatus pendiente. Una vez pagada, la factura no se puede modificar y los impuestos se reflejarán según la configuración fiscal de tu cuenta.',
    NEXT_PAYMENT_PLAN_INCLUDES: 'Incluye:',
    NEXT_PAYMENT_PLAN_INCLUDED_LABEL: 'incluido',
    NEXT_PAYMENT_ACTIVE_COMPLIMENTARY: 'Cortesías activas',
    NEXT_PAYMENT_REDUNDANT_CORTESIA: 'No aplicada',
    NEXT_PAYMENT_REDUNDANT_CORTESIA_TOOLTIP:
      'Tu plan actual ya incluye esta capacidad. La cortesía no otorga acceso adicional.',
    NEXT_PAYMENT_ANNUAL_EQUIVALENT: 'Equivalente anual (con 10% descuento)',
    NEXT_PAYMENT_INCLUDES_OVERAGES: 'incluye {{amount}} de excedentes',
    QUOTA_INCREASE_TOOLTIP:
      'Aumenta la capacidad de tu negocio agregando módulos o addons. El cargo se reflejará en tu próximo pago.',
    QUOTA_UPGRADE_PLAN_TOOLTIP: 'Cambia a un plan con mayor capacidad incluida',
    PLANS: {
      FREE: {
        NAME: 'Plan Gratuito',
        DESCRIPTION:
          'Ideal para empezar y explorar las funcionalidades básicas',
        BENEFITS: [
          'Hasta 1 usuario',
          'Funcionalidad POS básica',
          'Límite de 100 productos',
          'Reportes básicos',
          'Soporte por correo',
        ],
      },
      STARTUP: {
        NAME: 'Plan Startup',
        DESCRIPTION: 'Perfecto para pequeños negocios en crecimiento',
        BENEFITS: [
          'Hasta 5 usuarios',
          'Funciones POS avanzadas',
          'Límite de 1,000 productos',
          'Reportes avanzados',
          'Gestión de inventario',
          'Soporte prioritario por correo',
          'Múltiples almacenes (2)',
        ],
      },
      PROFESSIONAL: {
        NAME: 'Plan Profesional',
        DESCRIPTION: 'Solución empresarial completa con todas las funciones',
        BENEFITS: [
          'Usuarios ilimitados',
          'Capacidades POS completas',
          'Productos ilimitados',
          'Reportes personalizados y análisis',
          'Gestión avanzada de inventario',
          'Soporte prioritario 24/7',
          'Almacenes ilimitados',
          'Acceso a API',
          'Integraciones personalizadas',
        ],
      },
    },
    LABELS: {
      CURRENT_PLAN: 'Plan actual',
      UPGRADE_PLAN: 'Mejorar plan',
      DOWNGRADE_PLAN: 'Cambiar a plan inferior',
      CHANGE_PLAN: 'Cambiar plan',
      PLAN_BENEFITS: 'Beneficios del plan',
      ACCOUNT_INFORMATION: 'Información de la cuenta',
      SECURITY_SETTINGS: 'Configuración de seguridad',
      NOTIFICATION_PREFERENCES: 'Preferencias de notificación',
      EMAIL: 'Correo electrónico',
      CURRENT_PASSWORD: 'contraseña actual',
      NEW_PASSWORD: 'Nueva contraseña',
      CONFIRM_PASSWORD: 'Confirmar contraseña',
      TWO_FACTOR_AUTH: 'Autenticación de dos factores',
      EMAIL_NOTIFICATIONS: 'Notificaciones por correo',
      SMS_NOTIFICATIONS: 'Notificaciones por SMS',
      PUSH_NOTIFICATIONS: 'Notificaciones push',
      PUSH_NOTIFICATIONS_DESCRIPTION:
        'Recibe notificaciones en tiempo real en tu dispositivo.',
      EMAIL_NOTIFICATIONS_DESCRIPTION:
        'Recibe notificaciones y actualizaciones importantes en tu correo electrónico.',
      NO_CARD_ENROLLED: 'No hay tarjeta inscrita',
      CARD_EXPIRING_BADGE: 'Vence {{exp}}',
      CARD_EXPIRED_BADGE: 'Vencida',
      PAYPAL_CONNECTED: 'PayPal conectado',
      PAYPAL_NOT_CONNECTED: 'PayPal no conectado',
      MAKE_PAYMENT: 'Realizar pago',
      AVAILABLE_PLANS: 'Planes disponibles',
      CREDIT_CARD: 'Tarjeta de crédito',
      INCLUDED_FEATURES: 'Funcionalidades incluidas',
      ACTIVE_SUBSCRIPTION: 'Suscripción activa',
      INACTIVE_SUBSCRIPTION: 'Sin suscripción',
      YEARLY_PAYMENT: 'Pago anual',
      YEARLY_PAYMENT_DESCRIPTION: 'Total anual a pagar con este plan',
      NEXT_PAYMENT: 'Próximo pago',
      NO_NEXT_PAYMENT: 'Sin próximo pago programado',
      PREFERRED_PAYMENT_PROVIDER: 'Método de pago preferido',
      PREFERRED_PAYMENT_PROVIDER_HINT:
        'Se usará este método al procesar cobros de suscripción.',
      PLAN_CHANGE_COOLDOWN:
        'Debes esperar {{hours}} hora(s) más antes de cambiar tu plan nuevamente',
      SPONSORED: 'Patrocinado',
      SPONSORED_DESCRIPTION:
        'Tu empresa tiene acceso completo sin facturación.',
      BILLING_INTERVAL_MONTHLY: 'Mensual',
      BILLING_INTERVAL_ANNUAL: 'Anual',
      BILLING_INTERVAL_SAVE: 'Ahorra {{percent}}%',
      BILLING_INTERVAL_BILLED_ANNUALLY: 'Facturado anualmente',
      BILLING_INTERVAL_SECTION_TITLE: 'Ciclo de facturación',
      BILLING_INTERVAL_SECTION_SUBTITLE:
        'Elige entre pago mensual o anual. Con el plan anual ahorras 7% y solo recibes una factura al año.',
      BILLING_INTERVAL_CONFIRM_TITLE: '¿Cambiar ciclo de facturación?',
      BILLING_INTERVAL_CONFIRM_DESC_TO_ANNUAL:
        'Cambiarás a facturación anual. Anularemos el borrador en curso y emitiremos una nueva factura cubriendo los meses restantes del año, con 7% de descuento. No volveremos a facturar hasta {{nextDate}}.',
      BILLING_INTERVAL_CONFIRM_DESC_TO_MONTHLY:
        'Volverás a facturación mensual. Anularemos el borrador anual y emitiremos una factura mensual. La próxima factura se generará el {{nextDate}}.',
      BILLING_INTERVAL_CONFIRM_LOCK_NOTICE:
        'Importante: no podrás volver a cambiar el ciclo hasta pagar esta factura.',
      BILLING_INTERVAL_NEXT_INVOICE: 'Próxima factura: {{date}}',
      BILLING_INTERVAL_TOAST_SUCCESS: 'Ciclo de facturación actualizado.',
      BILLING_INTERVAL_TOAST_ERROR: 'No se pudo cambiar el ciclo.',
      BILLING_INTERVAL_CURRENT: 'Ciclo actual',
      BILLING_INTERVAL_LOCKED_CANCELLED:
        'La suscripción está cancelada. Reanúdala para cambiar el ciclo.',
      BILLING_INTERVAL_LOCKED_UNPAID:
        'Paga la factura {{invoice}} antes de cambiar el ciclo nuevamente.',
    },
    PLACEHOLDERS: {
      EMAIL: 'Ingrese su correo electrónico',
      CURRENT_PASSWORD: 'Ingrese su contraseña actual',
      NEW_PASSWORD: 'Ingrese una nueva contraseña',
      CONFIRM_PASSWORD: 'Confirme su nueva contraseña',
    },
    VALIDATION: {
      EMAIL_REQUIRED: 'El correo electrónico es requerido',
      EMAIL_INVALID: 'El correo electrónico no es válido',
      PASSWORD_MIN_LENGTH: 'La contraseña debe tener al menos 8 caracteres',
      PASSWORDS_DONT_MATCH: 'Las contraseñas no coinciden',
      CURRENT_PASSWORD_REQUIRED: 'La contraseña actual es requerida',
    },
    MESSAGES: {
      SETTINGS_SAVED: 'Configuración guardada exitosamente',
      SETTINGS_ERROR: 'Error al guardar la configuración',
      PLAN_UPGRADED: 'Plan actualizado exitosamente',
      PLAN_UPGRADE_ERROR: 'Error al actualizar el plan',
      CONFIRM_PLAN_CHANGE: '¿Está seguro de que desea cambiar su plan?',
      CONFIRM_DOWNGRADE:
        '¿Está seguro de que desea cambiar a un plan inferior? Algunas funciones podrían no estar disponibles.',
      DOWNGRADE_PENDING_MESSAGE:
        'Tu plan actual ({{currentPlan}}) seguirá activo hasta el {{endDate}}. Después cambiarás automáticamente al plan {{pendingPlan}}.',
    },
    BUTTONS: {
      EDIT_ACCOUNT: 'Editar cuenta',
      SAVE_CHANGES: 'Guardar cambios',
      CANCEL: 'Cancelar',
      CHANGE_PLAN_TO: 'Cambiar plan a {{plan}}',
    },
    SUBSCRIPTION: {
      PAYMENTS_HISTORY_SUBTITLE: 'Pagos registrados para tu suscripción',
    },
    CANCEL_SUBSCRIPTION: {
      TITLE: 'Cancelar suscripción',
      DESCRIPTION:
        'Si cancelas tu suscripción, tu cuenta pasará automáticamente al plan gratuito al finalizar el período de facturación actual.',
      WARNING:
        '⚠️ Solo el administrador podrá acceder a la plataforma después del cambio.',
      CONSEQUENCES: [
        'Todos los usuarios excepto el administrador serán bloqueados al finalizar el período.',
        'Se aplicarán los límites del plan gratuito (facturas, productos, usuarios).',
        'El acceso de pago se mantendrá hasta la fecha de vencimiento del período actual.',
        'Puedes reactivar tu suscripción en cualquier momento. Sin embargo, si hay saldo pendiente se deberá pagar el monto correspondiente al plan actual.',
      ],
      CONFIRM_TITLE: '¿Estás seguro de que deseas cancelar tu suscripción?',
      CONFIRM_DESCRIPTION:
        'Tu suscripción cambiará al plan gratuito al finalizar el período actual. Solo el administrador podrá acceder a la plataforma.',
      BUTTON: 'Cancelar mi suscripción',
      SUCCESS:
        'Tu suscripción ha sido cancelada. Tu cuenta pasará al plan gratuito al finalizar el período de facturación.',
      ERROR: 'Error al cancelar la suscripción. Intenta nuevamente.',
    },
    CANCEL_ACCOUNT: {
      TITLE: 'Cancelar cuenta',
      DESCRIPTION:
        'Si cancelas tu cuenta, esta será desactivada y ningún usuario de tu empresa podrá acceder a la plataforma.',
      WARNING: '⚠️ Esta acción no se puede deshacer fácilmente.',
      CONSEQUENCES: [
        'Todos los usuarios de tu empresa serán bloqueados inmediatamente.',
        'No podrás acceder a tus datos ni generar facturas.',
        'Para reactivar tu cuenta, deberás contactar a soporte en info@neogeniux.com.',
        'Tus datos se conservarán durante 90 días.',
      ],
      CONFIRM_TITLE: '¿Estás seguro de que deseas cancelar tu cuenta?',
      CONFIRM_DESCRIPTION:
        'Esta acción desactivará tu cuenta y bloqueará el acceso a todos los usuarios de tu empresa. Deberás contactar a soporte en info@neogeniux.com para reactivarla.',
      BUTTON: 'Cancelar mi cuenta',
      SUCCESS: 'Tu cuenta ha sido cancelada. Tu sesión ha finalizado.',
      ERROR: 'Error al cancelar la cuenta. Intenta nuevamente.',
      PROVIDER_CANCELLATION_FAILED:
        'Tu cuenta ha sido cancelada, pero no pudimos cancelar tu facturación con el proveedor de pago. Por favor contacta al soporte en info@neogeniux.com.',
    },
    CATALOG: {
      SECTION_TITLE: 'Catálogo de planes',
      SECTION_SUBTITLE:
        'Planes compuestos desde el catálogo (productos + funcionalidades + add-ons). Activa el que mejor se adapte y agrega complementos cuando lo necesites.',
      EMPTY: 'No hay planes públicos disponibles en este momento.',
      FEATURED: 'Recomendado',
      PER_MONTH: 'mes',
      PER_YEAR: 'año',
      MORE_ITEMS: '+ {{n}} más — Ver detalle',
      VIEW_ALL_TITLE: 'Capacidades del plan {{plan}}',
      VIEW_ALL_DESCRIPTION:
        'Todo lo que está incluido en este plan, sin recortes.',
      TRIAL_DAYS: '{{days}} días de prueba gratuita',
      CURRENT_BANNER:
        'Tu plan activo es {{planCode}} ({{status}}). Cambia de plan abajo o cancela toda la suscripción al final del periodo.',
      ACTION_SUBSCRIBE: 'Suscribirme',
      ACTION_CHANGE_TO_THIS: 'Cambiar a este plan',
      ACTION_CURRENT: 'Plan actual',
      ACTION_SCHEDULED: 'Próximo plan',
      BADGE_CURRENT: 'Plan actual',
      BADGE_SCHEDULED: 'Próximo plan',
      CUSTOM_PLAN_TITLE: 'Personalizado',
      CUSTOM_PLAN_DESCRIPTION:
        'Tu plan fue creado a tu medida. Ajusta cuotas en "Capacidad de tu negocio" o suma módulos en "Módulos y addons disponibles" más abajo.',
      CUSTOM_PLAN_OPEN_BUILDER: 'Editar plan personalizado',
      BADGE_SCHEDULED_FOR: 'Se activa el {{date}}',
      ACTION_CANCEL: 'Cancelar',
      ACTION_RESUME: 'Reactivar',
      CANCEL_BLOCKED_BY_SCHEDULED:
        'Tienes un cambio programado en tu plan. Cancela primero esa baja desde "Bajas programadas" antes de cancelar toda la suscripción.',
      CHANGE_BLOCKED_BY_SCHEDULED:
        'Ya tienes un cambio de plan programado. Cancélalo primero desde "Bajas programadas" antes de elegir otro plan.',
      CHANGE_BLOCKED_BY_COOLDOWN:
        'Espera unos minutos antes de cambiar de plan otra vez. Este breve periodo evita cargos duplicados por doble clic.',
      CONFIRM_CANCEL_TITLE: '¿Cancelar toda la suscripción?',
      CONFIRM_CANCEL_DESC:
        'Al final del periodo actual tu negocio perderá acceso a todos los módulos, addons y datos asociados al plan. Antes de esa fecha puedes pulsar "Reactivar" para mantener el servicio. ¿Confirmas?',
      ADDONS_TITLE: 'Complementos disponibles',
      ADDONS_SUBTITLE:
        'Funcionalidades opcionales que puedes activar individualmente sobre tu plan.',
      ADDON_OWNED: 'Activo',
      ADDON_BUY: 'Comprar',
      TOAST_CHECKOUT_FAILED: 'No se pudo iniciar el proceso de pago.',
      TOAST_PLAN_CHANGED: 'Plan actualizado.',
      TOAST_PLAN_CHANGE_FAILED: 'No se pudo cambiar el plan.',
      TOAST_USE_PAYPAL:
        'Stripe está deshabilitado temporalmente. Conecta PayPal en "Métodos de pago" y vuelve a intentarlo.',
      TOAST_PLAN_DOWNGRADE_SCHEDULED:
        'Cambio programado. Conservas el plan actual hasta tu próximo cobro.',
      PLAN_CHANGE_CONFIRM_TITLE: 'Confirmar cambio de plan',
      PLAN_CHANGE_CONFIRM_UPGRADE_DESC:
        'Vas a cambiar a este plan. El cambio se aplica de inmediato y tendrás acceso a sus módulos y addons.',
      PLAN_CHANGE_CONFIRM_DOWNGRADE_DESC:
        'Vas a reducir tu plan. Conservas el plan actual hasta tu próximo cobro; después de esa fecha se aplica el plan nuevo.',
      PLAN_CHANGE_CONFIRM_UPGRADE_CTA: 'Sí, cambiar ahora',
      PLAN_CHANGE_CONFIRM_DOWNGRADE_CTA: 'Sí, programar cambio',
      PLAN_CHANGE_INCLUDES: 'Incluye',
      PLAN_CHANGE_MORE_ITEMS: '+ {{count}} elementos más',
      PLAN_CHANGE_DOWNGRADE_NOTE:
        'Mantienes acceso al plan actual hasta la fecha de tu próximo cobro. Puedes revertir esta baja desde "Bajas programadas".',
      PLAN_CHANGE_PRORATION_TITLE: 'Ajuste por prorrateo',
      PLAN_CHANGE_PRORATION_DAYS: 'Días restantes del ciclo',
      PLAN_CHANGE_PRORATION_UNUSED: 'Crédito no consumido (plan actual)',
      PLAN_CHANGE_PRORATION_CHARGE: 'Cargo prorrateado (plan nuevo)',
      PLAN_CHANGE_PRORATION_NET: 'Cargo neto hoy',
      PLAN_CHANGE_PRORATION_CREDIT: 'Crédito a próxima factura',
      PLAN_CHANGE_PRORATION_NET_HINT:
        'Al confirmar generamos UNA sola factura hoy: el ciclo mensual completo del nuevo plan menos el crédito por los días no usados del plan actual. Reiniciamos tu ciclo a hoy — la próxima factura recurrente se emite en 30 días.',
      PLAN_CHANGE_PRORATION_CREDIT_HINT:
        'La diferencia se acredita a tu próxima factura. No se emite reembolso automático.',
      PLAN_CHANGE_TODAY_INVOICE_TITLE: 'Factura a generar hoy',
      PLAN_CHANGE_TODAY_INVOICE_ADJUSTMENT:
        'Ajuste al Plan {{plan}} (diferencia por días restantes)',
      PLAN_CHANGE_TODAY_INVOICE_TAX: 'ITBIS ({{rate}}%)',
      PLAN_CHANGE_TODAY_INVOICE_TOTAL: 'Total a pagar hoy',
      PLAN_CHANGE_TODAY_INVOICE_HINT:
        'Solo pagas la diferencia por los días restantes del ciclo actual. Tu fecha de próxima factura NO cambia — el plan nuevo se cobra completo en tu próximo cobro habitual.',
      PLAN_CHANGE_SCHEDULED_SUMMARY_TITLE: 'Resumen del cambio programado',
      PLAN_CHANGE_SCHEDULED_SUMMARY_CURRENT: 'Plan actual (hasta la fecha)',
      PLAN_CHANGE_SCHEDULED_SUMMARY_NEW: 'Plan nuevo',
      PLAN_CHANGE_SCHEDULED_SUMMARY_APPLIES_ON: 'Se aplica el',
      PLAN_CHANGE_SCHEDULED_SUMMARY_NEXT_INVOICE: 'Próxima factura recurrente',
      PLAN_CHANGE_SCHEDULED_SUMMARY_HINT:
        'No se genera factura hoy. Conservas el plan actual hasta la fecha indicada; a partir de esa fecha se cobra el nuevo plan al ciclo mensual. Puedes revertir esta baja desde "Bajas programadas".',
      PLAN_CHANGE_GRANTS_TITLE: 'Cortesías que se removerán',
      PLAN_CHANGE_GRANTS_HINT:
        'Las cortesías están vinculadas a tu plan actual. Si necesitas mantenerlas en el nuevo plan, contáctanos antes de continuar.',
      PLAN_CHANGE_GRANTS_PERPETUAL: 'sin vencimiento',
      PLAN_CHANGE_GRANTS_MORE: '+ {{count}} cortesías adicionales',
      PLAN_CHANGE_ACCESS_NOTE:
        'Al confirmar, tus módulos, permisos y menú se actualizarán automáticamente para el nuevo plan.',
      TOAST_CANCELLED: 'Cancelación programada.',
      TOAST_CANCEL_FAILED: 'No se pudo cancelar.',
      TOAST_RESUMED: 'Suscripción reactivada.',
      TOAST_RESUME_FAILED: 'No se pudo reactivar.',
    },
  },

  SUBSCRIPTION: {
    CONTACT_ADMIN_TO_PAY:
      'Comunícate con el administrador del negocio para que gestione el método de pago de la suscripción.',
    PAYMENT_PENDING_TITLE: 'Pago de suscripción pendiente',
    PAYMENT_PENDING_DESCRIPTION_ADMIN:
      'La factura {{invoice}} por {{amount}} vence en {{days}} día(s). Registra el pago antes de esa fecha para evitar la suspensión del servicio.',
    PAYMENT_PENDING_DESCRIPTION_NON_ADMIN:
      'La factura {{invoice}} por {{amount}} de la suscripción vence en {{days}} día(s). Notifica al administrador del negocio para que gestione el pago.',
    PAYMENT_PENDING_ACTION: 'Ver métodos de pago',
    PAYMENTS_HISTORY_TITLE: 'Historial de pagos',
    NEXT_INVOICE_TITLE: 'Próxima factura',
    NEXT_INVOICE_PERIOD: 'Período: {{from}} → {{to}}',
    NEXT_INVOICE_MARK_PAID: 'Marcar pagada',
    NEXT_INVOICE_MARK_PAID_CONFIRM_TITLE: '¿Marcar esta factura como pagada?',
    NEXT_INVOICE_MARK_PAID_CONFIRM_DESCRIPTION:
      'Se generará la factura de {{amount}} para el período que vence el {{dueDate}} y se registrará como pagada. El período de suscripción avanzará al siguiente ciclo.',
    NEXT_INVOICE_MARK_PAID_SUCCESS: 'Factura generada y marcada como pagada.',
    ACCESS: {
      TITLE: 'Actualización requerida',
      DESCRIPTION:
        'Tu plan actual no incluye esta funcionalidad. Necesitas {{plan}} o superior.',
      STEPS:
        'Para actualizar el plan: inscribe una tarjeta de crédito o realiza el pago con PayPal, y luego cambia el plan desde Cuenta.',
      CTA: 'Actualizar plan',
      NON_ADMIN_NOTE:
        'Si no eres administrador, pídele a un administrador que actualice el plan desde Cuenta.',
      REQUIRES_PAYMENT_METHOD:
        'Antes de cambiar el plan, debes inscribir una tarjeta o conectar tu cuenta de PayPal.',
    },
    ACTIONS: {
      ENROLL_CARD: 'Inscribir tarjeta',
      CHANGE_CARD: 'Cambiar tarjeta',
      PAY_WITH_CARD: 'Pagar con tarjeta',
      PAY_WITH_PAYPAL: 'Pagar con PayPal',
      CONNECT_PAYPAL: 'Conectar PayPal',
    },
    LABELS: {
      CARD_ENDING_IN: 'Tarjeta terminada en {{last4}}',
    },
    DIALOGS: {
      ENROLL_CARD_TITLE: 'Inscribir tarjeta',
      ENROLL_CARD_SUBTITLE: 'Guarda una tarjeta para pagos futuros',
      PAY_TITLE: 'Realizar pago',
      PAY_SUBTITLE: 'Paga tu suscripción con tarjeta o PayPal',
      DESCRIPTION: 'Descripción (opcional)',
    },
    SECURITY: {
      POWERED_BY_STRIPE: 'Procesado de forma segura por Stripe',
      NO_CARD_STORED:
        'Nunca almacenamos los datos de tu tarjeta en nuestros servidores.',
      PCI_COMPLIANT: 'Cumple con estándares PCI DSS Nivel 1.',
      SSL_ENCRYPTED: 'Conexión cifrada de extremo a extremo (TLS).',
      ACCEPTED_CARDS:
        'Aceptamos Visa, Mastercard, American Express y Discover.',
      SECURE_PAYMENT_TITLE: 'Pago seguro',
    },
    ERRORS: {
      STRIPE_NOT_CONFIGURED:
        'Stripe no está configurado. Agrega VITE_STRIPE_PUBLISHABLE_KEY.',
      STRIPE_NOT_READY: 'Stripe aún no está listo. Intenta de nuevo.',
      PAYPAL_RETURN_MISSING_TOKEN:
        'No se encontró el token de PayPal para completar el pago.',
    },
    PLANS: {
      FREE_CODE: 'FREE',
    },
    TABLE: {
      DATE: 'Fecha',
      AMOUNT: 'Monto',
      METHOD: 'Método',
      STATUS: 'Estado',
      REFERENCE: 'Referencia',
    },
    FIELDS: {
      AMOUNT: 'Monto',
      PAYMENT_METHOD: 'Método de pago',
      CUSTOM_PAYMENT_METHOD_NAME: 'Nombre del método',
      PAYMENT_STATUS: 'Estado del pago',
      REFERENCE_NUMBER: 'Número de referencia',
      PAYMENT_DATE: 'Fecha de pago',
      NOTES: 'Notas',
    },
    PAYMENT_METHODS: {
      TRANSFER: 'Transferencia',
      CARD: 'Tarjeta',
      CASH: 'Efectivo',
      CHECK: 'Cheque',
      PAYPAL: 'PayPal',
      CUSTOM: 'Personalizado',
    },
    PAYMENT_STATUSES: {
      PAID: 'Pagado',
      PENDING: 'Pendiente',
      FAILED: 'Fallido',
      REFUNDED: 'Reembolsado',
      VOID: 'Anulado',
    },
    SUBSCRIPTION_STATUSES: {
      ACTIVE: 'Activo',
      TRIALING: 'Prueba',
      PAST_DUE: 'Vencido',
      PAUSED: 'Pausado',
      CANCELLED: 'Cancelado',
      SUSPENDED: 'Suspendido',
    },
    VALIDATION: {
      CUSTOM_METHOD_REQUIRED: 'El nombre del método es requerido',
    },
    PAYMENT_FAILURE: {
      BANNER_TITLE: 'Error en el pago de tu suscripción',
      BANNER_DESCRIPTION:
        'No pudimos procesar el pago de tu suscripción. Verifica tu método de pago y reintenta. Si no se resuelve en 24 horas, tu cuenta será desactivada.',
      RETRY_BUTTON: 'Reintentar pago',
      RETRY_SUCCESS: 'Pago procesado exitosamente. Tu suscripción está activa.',
      RETRY_FAILED:
        'No se pudo procesar el pago. Verifica tu método de pago e intenta de nuevo.',
      DEACTIVATED_TITLE: 'Cuenta desactivada por falta de pago',
      DEACTIVATED_DESCRIPTION:
        'Tu cuenta ha sido desactivada porque no se pudo cobrar tu suscripción. Para reactivar todas las funcionalidades, actualiza tu método de pago y reintenta.',
      REASON_CARD_DECLINED:
        'Tu tarjeta fue rechazada. Verifica los datos o usa otra tarjeta.',
      REASON_INSUFFICIENT_FUNDS:
        'Fondos insuficientes en tu tarjeta o cuenta de PayPal.',
      REASON_GENERIC:
        'Hubo un problema al procesar tu pago. Contacta a info@neogeniux.com si el problema persiste.',
    },
    LIFECYCLE: {
      GRACE_TITLE: 'Tu período de prueba está por terminar',
      GRACE_DESC:
        'Te quedan {{days}} día(s) para agregar un método de pago. Sin pago, tu cuenta pasará a vencida.',
      GRACE_ENDED_TITLE: 'Tu período de prueba terminó',
      GRACE_ENDED_DESC:
        'Agrega un método de pago hoy para continuar usando la plataforma sin interrupciones.',
      RENEWAL_TITLE: 'Tu próxima factura está por vencer',
      RENEWAL_DESC:
        'Te quedan {{days}} día(s) para agregar un método de pago. Sin pago, tu cuenta pasará a vencida.',
      RENEWAL_DUE_TITLE: 'Tu próxima factura vence hoy',
      RENEWAL_DUE_DESC:
        'Agrega un método de pago hoy para evitar interrupciones en tu cuenta.',
      COMBINED_TRIAL_TITLE: 'Tu prueba termina — pago pendiente',
      COMBINED_TRIAL_DESC:
        'Tu período de prueba está por terminar. La factura {{invoice}} por {{amount}} vence en {{dueDays}} día(s). Agrega un método de pago antes de esa fecha para evitar la suspensión del servicio.',
      COMBINED_RENEWAL_TITLE: 'Tu renovación vence — pago pendiente',
      COMBINED_RENEWAL_DESC:
        'Tu suscripción está por renovar. La factura {{invoice}} por {{amount}} vence en {{dueDays}} día(s). Agrega un método de pago antes de esa fecha para evitar la suspensión del servicio.',
      PAST_DUE_TITLE: 'Tu suscripción está vencida',
      // Copy hardcodes "1 día" to match the new default of
      // `sadmin_settings.past_due_to_suspend_days=1` (2026-09-16
      // review — previously 7, previously-previously 14, both
      // too generous once past_due). TODO(banner-days-dynamic):
      // plumb the setting through `/billing/me` and interpolate
      // `{{days}}` here so sadmin changes reflect without redeploy.
      PAST_DUE_DESC:
        'No podrás registrar nuevas operaciones hasta que actualices tu facturación. Tu cuenta será suspendida si no se resuelve en 1 día.',
      SUSPENDED_TITLE: 'Tu suscripción está suspendida',
      SUSPENDED_DESC:
        'Actualiza tu facturación para restaurar el acceso completo a la plataforma.',
    },
    SPONSORSHIP_ENDED: {
      BANNER_TITLE: 'Tu patrocinio terminó',
      BANNER_DESCRIPTION:
        'Tienes 14 días de prueba para configurar un método de pago antes de que se interrumpa el servicio. Agrega tu tarjeta ahora para evitar cortes.',
      ACTION_LABEL: 'Configurar facturación',
    },
    OVERAGE: {
      TITLE: 'Cupo adicional requerido',
      DESCRIPTION:
        'Has alcanzado el límite de {{addonLabel}}. Puedes agregar un cupo adicional por {{price}} {{currency}}/mes.',
      EXTRA_BRANCH: 'sucursales',
      EXTRA_USER: 'usuarios',
      API_ACCESS: 'acceso a la API',
      CONFIRM_CTA: 'Comprar por {{price}} {{currency}}/mes',
      PURCHASE_SUCCESS: 'Cupo adicional agregado exitosamente',
      PURCHASE_ERROR: 'No se pudo agregar el cupo adicional. Intenta de nuevo.',
      ADDON_PRICING: 'Precios adicionales',
      PER_BRANCH: 'por sucursal adicional/mes',
      PER_USER: 'por usuario adicional/mes',
      DELETE_BRANCH_SLOT_WARNING:
        'Esta sucursal utiliza un cupo adicional pagado. Si la eliminas, quedarán {{unused}} cupo(s) pagado(s) sin usar hasta el {{date}}.',
      DELETE_USER_SLOT_WARNING:
        'Este usuario utiliza un cupo adicional pagado. Si lo eliminas, quedarán {{unused}} cupo(s) pagado(s) sin usar hasta el {{date}}.',
      SLOT_EXPIRES_ON: 'Expira el {{date}}',
      UNUSED_SLOTS_REMAINING: '{{count}} cupo(s) pagado(s) sin usar',
      UPGRADE_NUDGE:
        'Con este add-on tu gasto mensual estimado será de {{total}} {{currency}}/mes, igual o superior al plan Profesional ({{proPrice}} {{currency}}/mes) que incluye más funciones como sucursales ilimitadas, más usuarios y soporte 24/7.',
      UPGRADE_NUDGE_CTA: 'Ver plan Profesional',
    },
  },
  TRIAL: {
    BANNER: {
      EXPIRED_TITLE: 'Período de prueba finalizado',
      EXPIRED_DESCRIPTION:
        'Tu período de prueba de 14 días ha terminado. Agrega un método de pago en tu cuenta para no perder acceso a facturación, notas de crédito, reembolsos y movimientos de inventario.',
      WARNING_TITLE: 'Período de prueba próximo a vencer',
      WARNING_DAYS_REMAINING:
        'Te quedan {{days}} día(s) de prueba. Agrega un método de pago en tu cuenta para que tu suscripción continúe sin interrupciones cuando termine la prueba.',
      UPGRADE_BUTTON: 'Actualizar plan',
      ADD_PAYMENT_BUTTON: 'Agregar método de pago',
    },
    PRICING: {
      PER_MONTH: '/mes',
      FREE_PRICE: 'RD$0',
      STARTUP_PRICE: 'RD$999',
      PROFESSIONAL_PRICE: 'RD$2,499',
    },
  },
  PLAN_LIMITS: {
    INVOICES_LIMIT_REACHED:
      'Has alcanzado el límite de {{limit}} facturas mensuales de tu plan. Actualiza para continuar.',
    PRODUCTS_LIMIT_REACHED:
      'Has alcanzado el límite de {{limit}} productos de tu plan. Actualiza para continuar.',
    USERS_LIMIT_REACHED:
      'Has alcanzado el límite de {{limit}} usuario(s) de tu plan. Actualiza para agregar más.',
    WAREHOUSES_LIMIT_REACHED:
      'Has alcanzado el límite de {{limit}} almacén(es) de tu plan. Actualiza para agregar más.',
    BRANCHES_LIMIT_REACHED:
      'Has alcanzado el límite de {{limit}} sucursal(es) de tu plan. Actualiza para agregar más.',
    KDS_NOT_AVAILABLE:
      'El sistema KDS no está disponible en tu plan actual. Actualiza para habilitarlo.',
    POS_NOT_AVAILABLE:
      'El punto de venta (POS) no está disponible en tu plan actual. Actualiza para habilitarlo.',
    UPGRADE_PLAN: 'Actualizar plan',
    LIMIT_REACHED_TITLE: 'Límite de plan alcanzado',
    USAGE_LABEL: '{{current}} de {{limit}}',
    CAPACITY_TITLE_GENERIC: 'Alcanzaste tu capacidad',
    CAPACITY_TITLE_KDS: 'Alcanzaste tu límite de pantallas KDS',
    CAPACITY_TITLE_USERS: 'Alcanzaste tu límite de usuarios',
    CAPACITY_TITLE_BRANCHES: 'Alcanzaste tu límite de sucursales',
    CAPACITY_TITLE_WAREHOUSES: 'Alcanzaste tu límite de almacenes',
    CAPACITY_TITLE_PRODUCTS: 'Alcanzaste tu límite de productos',
    CAPACITY_TITLE_INVOICES: 'Alcanzaste tu límite mensual de facturas',
    CAPACITY_BODY_INCLUDED: 'Tu plan incluye {{maxAllowed}} {{resource}}.',
    CAPACITY_BODY_USAGE: 'Estás usando {{currentUsage}} de {{maxAllowed}}.',
    CAPACITY_BODY_NEED_ONE: 'Para continuar necesitas capacidad adicional.',
    CAPACITY_BODY_NEED_MANY:
      'Necesitas {{required}} unidades adicionales para continuar.',
    CAPACITY_INCREASE: 'Aumentar capacidad',
    CAPACITY_COMPARE_PLANS: 'Comparar planes',
    CAPACITY_NOT_NOW: 'Ahora no',
    USAGE_INLINE_LABEL: '{{used}} / {{limit}} usadas',
    USAGE_FULL_HELPER: 'Necesitas capacidad adicional para crear otra.',
    USAGE_INCREASE_CTA: '+ Aumentar capacidad',
  },
  DELIVERY_CONFIGURATION: {
    METHODS_TITLE: 'Métodos de Delivery',
    METHODS_DESCRIPTION: 'Gestiona los métodos de delivery y su tarifa base',
    CREATE_METHOD: 'Crear método',
    DRIVERS_TITLE: 'Repartidores',
    DRIVERS_DESCRIPTION: 'Gestiona los repartidores disponibles para delivery',
    CREATE_DRIVER: 'Crear repartidor',
    EMPTY_STATE_DESCRIPTION:
      'Configura tus métodos y repartidores para comenzar a usar delivery en las facturas.',
    CREATE_FIRST_METHOD: 'Crear primer método',
    CREATE_FIRST_DRIVER: 'Crear primer repartidor',
    METHOD_NAME: 'Nombre',
    METHOD_FEE: 'Tarifa base',
    REQUIRES_DRIVER: 'Requiere repartidor',
    STATUS: 'Estado',
    NO_METHODS_FOUND: 'No se encontraron métodos.',
    DELETE_METHOD_TITLE: 'Eliminar método',
    DELETE_METHOD_DESCRIPTION:
      '¿Estás seguro de que deseas eliminar el método "{{name}}"? Esta acción no se puede deshacer.',
    DRIVER_NAME: 'Nombre',
    DRIVER_PHONE: 'Teléfono',
    NO_DRIVERS_FOUND: 'No se encontraron repartidores.',
    DELETE_DRIVER_TITLE: 'Eliminar repartidor',
    DELETE_DRIVER_DESCRIPTION:
      '¿Estás seguro de que deseas eliminar el repartidor "{{name}}"? Esta acción no se puede deshacer.',
    NAME_REQUIRED: 'El nombre es requerido',
    PHONE_REQUIRED: 'El teléfono es requerido',
    FEE_MIN_ERROR: 'La tarifa debe ser mayor o igual a 0',
    EDIT_METHOD: 'Editar método',
    EDIT_DRIVER: 'Editar repartidor',
    METHOD_NAME_LABEL: 'Nombre del método',
    METHOD_FEE_LABEL: 'Tarifa base',
    METHOD_FEE_HELP: 'Tarifa mínima sugerida para este método.',
    REQUIRES_DRIVER_LABEL: 'Requiere repartidor',
    DRIVER_NAME_LABEL: 'Nombre del repartidor',
    DRIVER_PHONE_LABEL: 'Teléfono',
    NAME_PLACEHOLDER: 'Ej: Motorizado, Uber, etc.',
    PHONE_PLACEHOLDER: 'Ej: 8090000000',
    DELIVERY_FEE: 'Tarifa de delivery',
    DELIVERY_TITLE: 'Delivery',
    SELECT_METHOD: 'Seleccionar método',
    SELECT_DRIVER: 'Seleccionar repartidor',
    MIN_FEE: 'Mínimo: {{amount}}',
    NO_DELIVERY_CONFIGURED: 'No hay métodos de delivery configurados.',
    CONFIGURE_DELIVERY: 'Configurar delivery',
    FEE_BELOW_MIN:
      'El costo debe ser mayor o igual al mínimo configurado ({{amount}}).',
    DELIVERY_ADDRESS_LABEL: 'Dirección / coordenadas de delivery',
    DELIVERY_ADDRESS_PLACEHOLDER:
      'Dirección exacta, coordenadas GPS, link de Google Maps o instrucciones para el repartidor',
  },
  DELIVERY_ASSOCIATION: {
    METHODS_LABEL: 'Métodos habilitados',
    METHODS_PLACEHOLDER:
      'Selecciona los métodos donde este repartidor está disponible',
    METHODS_HELP:
      'El repartidor sólo aparecerá en el selector de la factura / POS cuando se elija uno de los métodos habilitados.',
    DRIVERS_LABEL: 'Repartidores habilitados',
    DRIVERS_PLACEHOLDER:
      'Selecciona los repartidores disponibles para este método',
    DRIVERS_HELP:
      'Sólo estos repartidores aparecerán en el selector cuando el operador elija este método.',
    NO_METHODS_EMPTY_STATE:
      'No hay métodos que requieran repartidor. Configura primero al menos un método con "Requiere repartidor".',
    NO_DRIVERS_EMPTY_STATE:
      'No hay repartidores creados todavía. Créalos en la sección Repartidores.',
    REQUIRE_AT_LEAST_ONE_METHOD:
      'Debes seleccionar al menos un método de entrega.',
    METHODS_BADGE_COUNT: '{{count}} método(s)',
    DRIVERS_BADGE_COUNT: '{{count}} repartidor(es)',
    NO_DRIVERS_FOR_METHOD:
      'No hay repartidores habilitados para este método. Configúralos en Configuración → Delivery.',
    MODIFY_ACTION_LABEL: 'Modificar delivery',
    MODIFY_MODAL_TITLE: 'Modificar delivery',
    MODIFY_MODAL_DESCRIPTION:
      'Actualiza el método y/o el repartidor asignado. El total de la factura no se modifica — la gestión de cambios de tarifa queda del lado del negocio.',
    MODIFY_METHOD_LABEL: 'Método de entrega',
    MODIFY_DRIVER_LABEL: 'Repartidor',
    HIGHER_FARE_WARNING_TITLE: 'Tarifa superior detectada',
    HIGHER_FARE_WARNING_DESCRIPTION:
      'El nuevo método tiene un mínimo de {{newFee}}, superior al costo de envío guardado en esta factura ({{currentFee}}). El sistema NO ajustará el total de la factura — el negocio debe reconciliar la diferencia por su cuenta. ¿Continuar?',
    HIGHER_FARE_CONFIRM: 'Sí, cambiar el método',
    NO_CHANGES_TOAST: 'No detectamos cambios para guardar.',
    SUCCESS_TOAST: 'Delivery actualizado correctamente.',
    METHOD_REQUIRED: 'Selecciona un método de entrega.',
    DRIVER_REQUIRED: 'Selecciona un repartidor.',
    FEE_PRESERVED_LABEL: 'Costo de envío guardado: {{amount}}',
    FEE_PRESERVED_HELP:
      'Este monto NO se modifica al cambiar el método o el repartidor. El total de la factura permanece igual — la reconciliación de la tarifa queda del lado del negocio.',
  },
  WAREHOUSE_DELETION: {
    TITLE: 'Eliminar almacén {{name}}',
    WARNING:
      'Esta acción es irreversible: el almacén se eliminará de forma permanente.',
    TRANSFER_LABEL: 'Transferir productos al almacén',
    TRANSFER_HINT:
      'Se moverán {{products}} producto(s) y sus conteos al almacén que elijas antes de eliminar este.',
    NO_TARGET:
      'Necesitas otro almacén activo para recibir los productos. Crea o activa uno antes de eliminar este.',
    NO_PRODUCTS: 'Este almacén no tiene productos; se eliminará directamente.',
    CONFIRM_LABEL: 'Escribe "{{name}}" para confirmar',
    CONFIRM: 'Eliminar',
    CONFIRM_MISMATCH: 'El nombre no coincide.',
  },
  BRANCH_DELETION: {
    TITLE: 'Eliminar sucursal {{name}}',
    DESCRIPTION:
      'Todos los datos de esta sucursal se transferirán a la sucursal que elijas y la sucursal dejará de existir. Esta acción no se puede deshacer.',
    TARGET_LABEL: 'Transferir los datos a',
    NO_TARGET: 'Necesitas otra sucursal activa para recibir los datos.',
    GROUPS_LABEL: 'Datos a transferir',
    GROUP_FISCAL:
      'Historial fiscal y transacciones (facturas, NCF, pagos, turnos, contabilidad)',
    GROUP_FISCAL_HINT:
      'Siempre se transfiere: el historial fiscal no se puede eliminar.',
    GROUP_INVENTORY: 'Inventario y categorías',
    GROUP_PARTIES: 'Clientes y proveedores',
    GROUP_USERS: 'Usuarios y roles',
    GROUP_CONFIG: 'Restaurante, KDS y configuración',
    GROUP_COUNT: '{{count}} registros',
    UNCHECKED_HINT:
      'Lo que no marques quedará oculto en la sucursal destino; el historial que lo usa se conserva.',
    USERS_UNCHECKED_HINT: 'Los usuarios de esta sucursal quedarán inactivos.',
    USERS_AFFECTED: 'Usuarios con esta sucursal como principal: {{names}}',
    SKU_CLASHES:
      '{{count}} productos ya existen en la sucursal destino. Se conservarán ambos y al código de los transferidos se le agregará "-{{code}}".',
    BLOCKERS_TITLE: 'Resuelve esto antes de eliminar la sucursal:',
    BLOCKER_OPEN_SHIFT: 'Hay un turno de caja abierto.',
    BLOCKER_OPEN_RESTAURANT_ACCOUNT: 'Hay cuentas de restaurante abiertas.',
    BLOCKER_DRAFT_INVOICE: 'Hay facturas en borrador.',
    BLOCKER_PENDING_ECF: 'Hay e-CF pendientes de envío o validación.',
    BLOCKER_COUNTING_IN_PROGRESS: 'Hay un conteo de inventario en curso.',
    BLOCKER_ACTIVE_DEVICE_RESERVATION:
      'Hay dispositivos móviles con numeración reservada para trabajar sin conexión; sincronízalos primero.',
    BLOCKER_DUPLICATE_USER:
      'Un usuario de esta sucursal tiene el mismo correo o usuario que uno de la sucursal destino; cámbialo o elimínalo primero.',
    NO_DELETE_PERMISSION:
      'Todas las sucursales de pago están en uso. Para reducir, primero hay que eliminar una sucursal, y no tienes permiso para hacerlo.',
    CONFIRM_LABEL: 'Escribe "{{name}}" para confirmar',
    CONFIRM: 'Eliminar sucursal',
    DELETING: 'Eliminando…',
    SUCCESS: 'Sucursal eliminada',
    SUCCESS_DESCRIPTION: 'Sus datos se transfirieron a {{target}}.',
    ORIGIN_BADGE: 'Proveniente de sucursal eliminada: {{name}}',
    ORIGIN_BADGE_UNKNOWN: 'Proveniente de una sucursal eliminada',
    REPORT_NOTE:
      'Incluye datos transferidos de sucursales eliminadas: {{names}}',
    PICK_BRANCH_TITLE: 'Elige la sucursal a eliminar',
    PICK_BRANCH_DESCRIPTION:
      'Todas las sucursales de tu plan están en uso. Para reducir la capacidad, primero elimina una sucursal; sus datos se transferirán a otra.',
  },
  BRANCH_CONFIGURATION: {
    NAME: 'Nombre',
    CODE: 'Código',
    DESCRIPTION: 'Descripción',
    STATUS: 'Estado',
    ACTIVE: 'Activa',
    INACTIVE: 'Inactiva',
    HEADQUARTERS: 'Sede Principal',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    CREATE_BRANCH: 'Nueva Sucursal',
    EDIT_BRANCH: 'Editar Sucursal',
    CREATE_FIRST_BRANCH: 'Crear Primera Sucursal',
    BRANCH_NAME: 'Nombre de la Sucursal',
    BRANCH_CODE: 'Código de la Sucursal',
    BRANCH_DESCRIPTION: 'Descripción de la Sucursal',
    EMAIL: 'Correo Electrónico',
    PHONE: 'Teléfono',
    CITY: 'Ciudad',
    ADDRESS: 'Dirección',
    NAME_PLACEHOLDER: 'Ej: Sucursal Centro, Sucursal Norte',
    CODE_PLACEHOLDER: 'Ej: SUC-001',
    DESCRIPTION_PLACEHOLDER: 'Descripción opcional de la sucursal',
    EMAIL_PLACEHOLDER: 'correo@sucursal.com',
    PHONE_PLACEHOLDER: '809-000-0000',
    CITY_PLACEHOLDER: 'Ej: Santo Domingo',
    ADDRESS_PLACEHOLDER: 'Ej: Av. Winston Churchill #100',
    NAME_REQUIRED: 'El nombre es requerido',
    CODE_REQUIRED: 'El código es requerido',
    IS_HEADQUARTERS: 'Es sede principal',
    CANCEL: 'Cancelar',
    CREATE: 'Crear',
    UPDATE: 'Actualizar',
    SAVING: 'Guardando...',
    EMPTY_STATE_DESCRIPTION:
      'Aquí podrás crear y gestionar las sucursales de tu negocio',
    NO_BRANCHES_FOUND: 'No hay sucursales configuradas.',
    GENERAL_INFO: 'Información General',
    CONTACT_INFO: 'Información de Contacto',
    LOCATION_INFO: 'Ubicación',
    MANAGER: 'Gerente / Encargado',
    MANAGER_PLACEHOLDER: 'Seleccionar encargado',
    CANNOT_DELETE_HEADQUARTERS: 'No se puede eliminar la sede principal',
    ALL_BRANCHES: 'Todas las sucursales',
    SEARCH_BRANCH: 'Buscar sucursal...',
    SELECT_BRANCH: 'Seleccionar sucursal',
    CONSOLIDATED_VIEW: 'Vista consolidada',
    CONFIG_POLICY_TITLE: 'Configuración Compartida con Sucursales',
    CONFIG_POLICY_DESCRIPTION:
      'Define qué configuraciones son gestionadas centralmente desde la sede principal y se comparten con todas las sucursales.',
    CONFIG_AREA_BUSINESS_SETTINGS: 'Ajustes del Negocio',
    CONFIG_AREA_TAXES: 'Impuestos',
    CONFIG_AREA_NCF: 'Comprobantes Fiscales (NCF)',
    CONFIG_AREA_INVOICE_TEMPLATES: 'Plantillas de Factura',
    CONFIG_AREA_PROMOTIONS: 'Promociones',
    CONFIG_AREA_DELIVERY_METHODS: 'Métodos de Entrega',
    CONFIG_SHARED_LABEL: 'Compartida (Sede gestiona)',
    CONFIG_INDEPENDENT_LABEL: 'Independiente (Cada sucursal gestiona)',
    PUSH_TO_BRANCHES: 'Aplicar a Sucursales',
    PUSHING: 'Aplicando...',
    PUSH_SUCCESS:
      'Configuración aplicada a {{count}} sucursal(es) exitosamente.',
    PUSH_ERROR: 'Error al aplicar la configuración a las sucursales.',
    MANAGED_BY_HQ: 'Gestionado por Sede Principal',
    MANAGED_BY_HQ_DESCRIPTION:
      'Esta configuración es controlada por la sede principal. Los cambios deben realizarse desde la sede.',
    HQ_ONLY_SECTION: 'Solo disponible desde la sede principal',
    RELEASE: 'Liberar sucursal',
    RELEASE_TITLE: '¿Liberar esta sucursal?',
    RELEASE_DESCRIPTION:
      'Esta acción convertirá la sucursal en un negocio independiente. Se copiarán los productos, clientes y configuraciones compartidas. Los usuarios asignados a esta sucursal serán migrados al nuevo negocio.',
    RELEASE_WARNING:
      'Esta acción es IRREVERSIBLE. La sucursal dejará de pertenecer a este negocio.',
    RELEASING: 'Liberando...',
    RELEASE_SUCCESS: 'Sucursal liberada',
    RELEASE_SUCCESS_DESCRIPTION:
      'La sucursal ha sido convertida en un negocio independiente exitosamente.',
    RELEASE_PAYMENT_REQUIRED:
      'El nuevo negocio tiene más usuarios de los permitidos en el plan gratuito. Se requiere configurar un método de pago y actualizar el plan.',
    RELEASE_ERROR: 'Error al liberar la sucursal. Intente nuevamente.',
    RELEASE_NO_ADMIN:
      'La sucursal debe tener al menos un usuario administrador activo antes de poder ser liberada.',
    INVENTORY_MODE_LABEL: 'Modo de Inventario',
    INVENTORY_MODE_SHARED: 'Inventario compartido',
    INVENTORY_MODE_SHARED_DESC:
      'Los mismos productos, sin stock independiente por sucursal.',
    INVENTORY_MODE_COPY: 'Copiar inventario actual',
    INVENTORY_MODE_COPY_DESC:
      'Copia los productos, categorías y stock actual de la sede principal a esta sucursal.',
    INVENTORY_MODE_EMPTY: 'Nuevo sin productos',
    INVENTORY_MODE_EMPTY_DESC:
      'La sucursal empieza con su propio inventario vacío, sin productos.',
    INVENTORY_MODE_EMPTY_WAREHOUSE_HINT:
      'Una sucursal sin productos necesita su propio almacén.',
    COPY_INVENTORY: 'Copiar Inventario',
    COPY_INVENTORY_TITLE: '¿Copiar inventario a esta sucursal?',
    COPY_INVENTORY_DESCRIPTION:
      'Esta acción copiará todos los productos, categorías y stock actual de la sede principal a esta sucursal. La sucursal pasará a tener su propio inventario independiente.',
    COPY_INVENTORY_WARNING:
      'Esta acción es IRREVERSIBLE. Una vez copiado, la sucursal gestionará su inventario de forma independiente.',
    COPYING_INVENTORY: 'Copiando inventario...',
    COPY_INVENTORY_SUCCESS: 'Inventario copiado exitosamente',
    COPY_INVENTORY_SUCCESS_DESCRIPTION:
      'Se copiaron {{categories}} categorías, {{products}} productos y {{movements}} movimientos de stock.',
    COPY_INVENTORY_ERROR: 'Error al copiar el inventario',
    WAREHOUSE_SECTION_TITLE: 'Inventario',
    WAREHOUSE_STRATEGY_SHARE_LABEL: 'Usar almacén existente',
    WAREHOUSE_STRATEGY_SHARE_DESC:
      'La nueva sucursal comparte el inventario con un almacén ya creado. No consume cuota de almacenes.',
    WAREHOUSE_STRATEGY_CREATE_LABEL:
      'Crear un nuevo almacén para esta sucursal',
    WAREHOUSE_STRATEGY_CREATE_DESC:
      'Se creará un almacén dedicado para esta sucursal. Puede requerir aumentar tu cuota de almacenes.',
    WAREHOUSE_SELECTOR_LABEL: 'Almacén',
  },
  TAX_CONFIGURATION: {
    NAME: 'Nombre',
    RATE: 'Tasa (%)',
    DESCRIPTION: 'Descripción',
    STATUS: 'Estado',
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    DEFAULT: 'Incluir en factura',
    ROW_DEFAULT: 'Incluir en cada item',
    ACTIONS: 'Acciones',
    EDIT: 'Editar',
    DELETE: 'Eliminar',
    SET_DEFAULT: 'Establecer como predeterminado',
    CREATE_TAX: 'Nuevo Impuesto',
    EDIT_TAX: 'Editar Impuesto',
    CREATE_FIRST_TAX: 'Crear Primer Impuesto',
    TAX_NAME: 'Nombre del Impuesto',
    TAX_RATE: 'Tasa del Impuesto (%)',
    TAX_DESCRIPTION: 'Descripción del Impuesto',
    TAX_STATUS_LABEL: 'Estado',
    TAX_STATUS_DESCRIPTION:
      'El impuesto estará disponible para usar en las facturas',
    NAME_PLACEHOLDER: 'Ej: IVA, ITBIS, etc.',
    RATE_PLACEHOLDER: '0.00',
    DESCRIPTION_PLACEHOLDER: 'Descripción opcional del impuesto',
    NAME_REQUIRED: 'El nombre es requerido',
    RATE_MIN_ERROR: 'La tasa debe ser mayor o igual a 0',
    RATE_MAX_ERROR: 'La tasa no puede ser mayor a 100',
    CANCEL: 'Cancelar',
    CREATE: 'Crear',
    UPDATE: 'Actualizar',
    SAVING: 'Guardando...',
    DELETE_TITLE: 'Inactivar Impuesto',
    DELETE_DESCRIPTION:
      '¿Estás seguro de que deseas inactivar el impuesto "{{name}}"? Los productos que ya tienen este impuesto seguirán calculando su valor con la tasa actual (la referencia no se pierde), pero productos nuevos y líneas nuevas no podrán seleccionarlo.',
    DELETE_BULK_UPDATE_HINT:
      'Recomendamos actualizar los productos que aún usan este impuesto desde Actualización masiva antes o después de inactivarlo.',
    DELETE_BULK_UPDATE_LINK: 'Ir a Actualización masiva de productos',
    EMPTY_STATE_TITLE: 'Configuración de Impuestos',
    EMPTY_STATE_DESCRIPTION:
      'Aquí podrás crear y gestionar los impuestos para las facturas',
    NO_TAXES_FOUND: 'No hay impuestos configurados.',
    ADD_TAX: 'Agregar impuesto',
    CONFIGURE_TAXES: 'Configurar impuestos',
    MANAGE_TAXES: 'Administrar impuestos',
    SORT_ORDER: 'Orden de aplicación',
    SORT_ORDER_HINT: 'Menor número = se aplica primero',
    APPLIES_TO: 'Se aplica sobre',
    APPLIES_TO_GROSS: 'Monto bruto (antes de descuento)',
    APPLIES_TO_NET: 'Monto neto (después de descuento)',
    IS_MANDATORY: 'Obligatorio',
    IS_MANDATORY_HINT: 'Siempre se incluye en la factura',
    VISIBLE_IN_PRODUCT_TAX: 'Mostrar en Tipo de impuesto',
    VISIBLE_IN_PRODUCT_TAX_HINT:
      'Cuando está activo, este impuesto aparece en los selectores de productos (formulario de producto, actualización masiva, línea de factura/POS/mesa). Los impuestos a nivel de factura (como Propina Legal) deben quedar desactivados.',
    VISIBLE_IN_PRODUCT_TAX_BADGE: 'En productos',
    AVAILABLE_AS_INCLUDED_TAX: 'Puede incluirse dentro del precio del producto',
    AVAILABLE_AS_INCLUDED_TAX_HINT:
      'Cuando está activo, los operadores podrán marcar este impuesto como incluido dentro del precio del producto (por ejemplo, Propina 10% sumada al ITBIS en el precio de venta). El sistema despejará el impuesto al facturar.',
    AVAILABLE_AS_INCLUDED_TAX_BADGE: 'Incluible en precio',
    PREDEFINED_TAXES: 'Impuestos predefinidos',
    MANDATORY_TAX_WARNING:
      'Existen impuestos obligatorios configurados ({{taxes}}). La sección de impuestos se mostrará en la factura a pesar de la configuración actual.',
    GROSS: 'Bruto',
    NET: 'Neto',
    ACTIVATE: 'Activar',
    TOAST_SUCCESS_TITLE: 'Éxito',
    TOAST_ERROR_TITLE: 'Error',
    TOAST_CREATED: 'Impuesto creado correctamente',
    TOAST_UPDATED: 'Impuesto actualizado correctamente',
    TOAST_DELETED: 'Impuesto eliminado correctamente',
    TOAST_ACTIVATED: 'Impuesto activado correctamente',
    TOAST_CREATE_ERROR: 'No se pudo crear el impuesto',
    TOAST_UPDATE_ERROR: 'No se pudo actualizar el impuesto',
    TOAST_DELETE_ERROR: 'No se pudo eliminar el impuesto',
    TOAST_ACTIVATE_ERROR: 'No se pudo activar el impuesto',
    DEFAULT_BADGE: 'Predeterminado',
    DEFAULT_BADGE_HINT:
      'Se aplica automáticamente a nuevos productos, facturas y órdenes cuando no se elige otro impuesto.',
    SET_AS_DEFAULT: 'Marcar como predeterminado',
    CANNOT_DELETE_DEFAULT:
      'No puedes eliminar el impuesto predeterminado. Marca otro impuesto como predeterminado antes de eliminarlo.',
    TOAST_DEFAULT_SET: 'Impuesto predeterminado actualizado',
    TOAST_DEFAULT_SET_ERROR: 'No se pudo actualizar el impuesto predeterminado',
    CANONICAL_RATE_HINT:
      'La tasa de este impuesto DGII es fija. Para cambiar la tasa, crea un impuesto nuevo con la nueva tasa y desactiva este.',
    RATE_CHANGE_WARN_TITLE: 'Aviso: productos existentes no cambian',
    RATE_CHANGE_WARN_BODY:
      'Cambiar la tasa afecta SOLO nuevas facturas creadas con productos nuevos o productos editados después del cambio. Facturas existentes y productos ya guardados mantienen su tasa histórica.',
    GOTO_BULK_UPDATE:
      'Actualizar productos afectados en Actualización Masiva → Impuesto',
  },
  MERMA: {
    TITLE: 'Registrar merma',
    SUBTITLE:
      'Registra pérdidas de inventario por rotación, corte, mal manejo o vencimiento.',
    PRODUCT: 'Producto',
    PRODUCT_PLACEHOLDER: 'Busca por nombre, SKU o código de barras',
    QUANTITY: 'Cantidad perdida',
    REASON_CATALOG: 'Motivo (catálogo)',
    REASON_PLACEHOLDER: 'Selecciona un motivo',
    REASON_FREE_TEXT: 'Detalle del motivo (opcional)',
    NOTES: 'Notas',
    RECORD: 'Registrar merma',
    SUCCESS: 'Merma registrada correctamente',
    ACTION_LABEL: 'Registrar merma',
  },
  MERMA_REPORT: {
    TITLE: 'Reporte de merma',
    SUBTITLE:
      'Pérdida por producto en el período. Refleja el filtro activo de la lista.',
    ACTION_LABEL: 'Reporte de merma',
    DATE_FROM: 'Desde',
    DATE_TO: 'Hasta',
    ALL_REASONS: 'Todos los motivos',
    TOTAL_PRODUCTS: 'Productos afectados',
    TOTAL_QUANTITY: 'Cantidad total',
    TOTAL_COST: 'Costo total',
    SKU: 'SKU',
    PRODUCT: 'Producto',
    QUANTITY_LOST: 'Cantidad',
    UNIT_COST: 'Costo unitario',
    EVENTS: 'Eventos',
    LAST_EVENT: 'Último',
    EMPTY: 'Sin mermas registradas en el período.',
  },
  PROMOTIONS: {
    TITLE: 'Promociones',
    SUBTITLE: 'Gestione promociones, descuentos y ofertas especiales',
    CREATE_PROMOTION: 'Crear Promoción',
    EDIT_PROMOTION: 'Editar Promoción',

    NAME: 'Nombre',
    CODE: 'Código',
    DISCOUNT_TYPE: 'Tipo de Descuento',
    DISCOUNT_VALUE: 'Valor',
    STATUS: 'Estado',
    START_DATE: 'Fecha Inicio',
    END_DATE: 'Fecha Fin',
    PRIORITY: 'Prioridad',
    USAGE_COUNT: 'Usos',
    ACTIONS: 'Acciones',

    TYPE_PERCENTAGE: 'Porcentaje',
    TYPE_FIXED_AMOUNT: 'Monto Fijo',
    TYPE_BUY_X_GET_Y: 'Compre X Lleve Y',
    TYPE_BUNDLE_PRICE: 'Precio Combo',

    STATUS_DRAFT: 'Borrador',
    STATUS_ACTIVE: 'Activa',
    STATUS_INACTIVE: 'Desactivada',
    STATUS_SCHEDULED: 'Programada',
    STATUS_EXPIRED: 'Expirada',
    STATUS_CANCELLED: 'Cancelada',

    PROMOTION_NAME: 'Nombre de la Promoción',
    COUPON_CODE: 'Código de Cupón',
    DESCRIPTION: 'Descripción',
    MIN_QUANTITY: 'Cantidad Mínima',
    MIN_PURCHASE_AMOUNT: 'Monto Mínimo de Compra',
    MAX_DISCOUNT_AMOUNT: 'Descuento Máximo',
    MAX_USAGE_COUNT: 'Límite de Usos',
    MAX_USAGE_PER_CUSTOMER: 'Límite por Cliente',
    IS_STACKABLE: 'Acumulable',
    IS_STACKABLE_HELP:
      'Permite combinar esta promoción con otras sobre el mismo producto. Si está desactivado, solo se aplica la primera promoción que coincida.',
    REQUIRES_COUPON_CODE: 'Requiere Código',
    REQUIRES_COUPON_CODE_HELP:
      'El descuento no se aplica automáticamente. El cliente debe ingresar un código de cupón para activarlo.',
    SCOPE: 'Alcance',
    SCOPE_ALL_PRODUCTS: 'Todos los Productos',
    SCOPE_SPECIFIC_PRODUCTS: 'Productos Específicos',
    SCOPE_SPECIFIC_CATEGORIES: 'Categorías Específicas',

    BUNDLES: 'Combos',
    ADD_BUNDLE: 'Agregar Combo',
    BUNDLE_TYPE: 'Tipo de Combo',
    BUNDLE_NAME: 'Nombre del Combo',
    BUNDLE_ITEMS: 'Productos del Combo',
    REQUIRED_QUANTITY: 'Cantidad Requerida',
    DISCOUNTED_QUANTITY: 'Cantidad con Descuento',
    DISCOUNT_PERCENT: 'Porcentaje de Descuento',
    IS_REQUIRED: 'Obligatorio',

    CREATE: 'Crear',
    UPDATE: 'Actualizar',
    DELETE: 'Eliminar',
    ACTIVATE: 'Activar',
    DEACTIVATE: 'Desactivar',
    CANCEL: 'Cancelar',
    SAVING: 'Guardando...',

    DELETE_TITLE: '¿Eliminar promoción?',
    DELETE_DESCRIPTION:
      'Esta acción no se puede deshacer. La promoción será cancelada permanentemente.',
    EMPTY_STATE_TITLE: 'No hay promociones',
    EMPTY_STATE_DESCRIPTION:
      'Crea tu primera promoción para ofrecer descuentos a tus clientes.',
    NO_PROMOTIONS_FOUND: 'No se encontraron promociones',

    NAME_REQUIRED: 'El nombre es requerido',
    DISCOUNT_VALUE_REQUIRED: 'El valor del descuento es requerido',
    DISCOUNT_TYPE_REQUIRED: 'El tipo de descuento es requerido',
    MIN_QUANTITY_MIN: 'La cantidad mínima debe ser al menos 1',
    MIN_PURCHASE_AMOUNT_MIN:
      'El monto mínimo de compra debe ser mayor o igual a 0',
    MAX_DISCOUNT_AMOUNT_MIN:
      'El monto máximo de descuento debe ser mayor o igual a 0',
    PRIORITY_MIN: 'La prioridad debe ser al menos 1',
    PRIORITY_MAX: 'La prioridad no puede ser mayor a 100',
    MAX_USAGE_COUNT_MIN: 'El uso máximo debe ser al menos 1',
    MAX_USAGE_PER_CUSTOMER_MIN: 'El uso máximo por cliente debe ser al menos 1',
    START_DATE_REQUIRED: 'La fecha de inicio es requerida',
    END_DATE_REQUIRED: 'La fecha de fin es requerida',
    END_DATE_AFTER_START:
      'La fecha de fin debe ser posterior a la fecha de inicio',

    SELECT_PRODUCTS: 'Seleccionar Productos',
    SELECT_CATEGORIES: 'Seleccionar Categorías',
    NO_PRODUCTS_SELECTED: 'No hay productos seleccionados',
    NO_CATEGORIES_SELECTED: 'No hay categorías seleccionadas',
    PRODUCTS_SELECTED: '{{count}} producto(s) seleccionado(s)',
    CATEGORIES_SELECTED: '{{count}} categoría(s) seleccionada(s)',
    TYPE_TO_SEARCH_PRODUCTS: 'Escriba para buscar productos',
    SEARCH_PRODUCTS_TO_ADD: 'Buscar productos para agregar...',
    NO_CATEGORIES_AVAILABLE: 'No hay categorías disponibles',

    SEARCH_PLACEHOLDER: 'Buscar promociones...',
    ALL_STATUSES: 'Todos los estados',

    PROMOTION_CREATED: 'Promoción creada correctamente',
    PROMOTION_UPDATED: 'Promoción actualizada correctamente',
    PROMOTION_DELETED: 'Promoción eliminada correctamente',
    PROMOTION_ACTIVATED: 'Promoción activada correctamente',
    PROMOTION_DEACTIVATED: 'Promoción desactivada correctamente',
    PROMOTION_CANCELLED: 'Promoción cancelada correctamente',
    ERROR_CREATING_PROMOTION: 'Error al crear la promoción',
    ERROR_UPDATING_PROMOTION: 'Error al actualizar la promoción',
    ERROR_DELETING_PROMOTION: 'Error al eliminar la promoción',
    ERROR_ACTIVATING_PROMOTION: 'Error al activar la promoción',
    ERROR_DEACTIVATING_PROMOTION: 'Error al desactivar la promoción',
    ERROR_CANCELLING_PROMOTION: 'Error al cancelar la promoción',
    PROMO_DISCOUNT: 'Desc. promoción',
    MIN_QTY_HINT:
      '¡Agrega {{remaining}} más para obtener {{discount}} de descuento!',
    ENTER_COUPON_CODE: 'Ingresa código de cupón',
    APPLY_COUPON: 'Aplicar',
    COUPON_APPLIED: 'Cupón aplicado: {{name}}',
    INVALID_COUPON: 'Cupón inválido o expirado',
    REMOVE_COUPON: 'Quitar cupón',
    COUPON_DISCOUNT: 'Desc. cupón',
    VALIDATING_COUPON: 'Validando...',
  },
  NOT_FOUND: {
    TITLE: 'Página no encontrada',
    DESCRIPTION: 'Lo sentimos, la página que estás buscado no existe.',
    BACK_TO_HOME: 'Volver al inicio',
  },
  CONTRACT: {
    MODAL_TITLE: 'Términos y Condiciones',
    MODAL_SUBTITLE: 'Lee y firma el contrato para continuar',
    VERSION: 'Versión',
    SECTION_TITLE: 'Contrato',
    SECTION_ACCEPTED:
      'Aceptaste "{{title}}" (versión {{version}}). Puedes verlo cuando quieras.',
    SECTION_PENDING:
      'Aún no firmas la versión actual del contrato. Ábrelo para revisarlo y firmar.',
    SECTION_VIEW: 'Ver contrato',
    SECTION_PRINT: 'Imprimir',
    MUST_SIGN_BANNER:
      'Debes leer y firmar el contrato antes de continuar usando la plataforma.',
    NO_CONTENT: 'No hay contenido disponible.',
    FULL_NAME_LABEL: 'Nombre completo',
    FULL_NAME_PLACEHOLDER: 'Escribe tu nombre completo',
    FULL_NAME_REQUIRED: 'Debes escribir tu nombre completo',
    ACCEPT_CHECKBOX:
      'He leído y acepto los términos y condiciones del contrato.',
    MUST_ACCEPT: 'Debes aceptar los términos para continuar',
    SIGN_BUTTON: 'Firmar y continuar',
    SIGNED_SUCCESS: 'Contrato firmado exitosamente',
    SADMIN_BYPASS: 'Saltar firma (solo sadmin, esta sesión)',
    SADMIN_PAGE_TITLE: 'Contratos de la plataforma',
    SADMIN_PAGE_SUBTITLE:
      'Gestiona las versiones del contrato que los clientes deben firmar al usar la plataforma',
    SADMIN_CREATE_VERSION: 'Crear nueva versión',
    SADMIN_ACTIVATE_VERSION: 'Activar esta versión',
    SADMIN_ACTIVATE_WARNING:
      'Al activar esta versión, todos los usuarios deberán firmar nuevamente al ingresar.',
    SADMIN_VERSION_LABEL: 'Versión (ej. 1.0.0)',
    SADMIN_TITLE_LABEL: 'Título',
    SADMIN_CONTENT_LABEL: 'Contenido (español)',
    SADMIN_SUMMARY_LABEL: 'Resumen interno (opcional)',
    SADMIN_MAKE_CURRENT: 'Marcar como versión actual',
    SADMIN_CURRENT_BADGE: 'Actual',
    SADMIN_EMPTY_LIST:
      'Aún no hay versiones de contrato. Crea la primera con el botón de arriba.',
    SADMIN_EMPTY_DETAIL:
      'Selecciona una versión de la lista para ver su contenido.',
    SADMIN_AUDIENCE_LABEL: 'Audiencia',
    SADMIN_AUDIENCE_HINT:
      'Define qué app consume esta versión: negocios (T&C estándar), resellers (partner platform) o ambas.',
    SADMIN_AUDIENCE_BUSINESS: 'Negocio',
    SADMIN_AUDIENCE_RESELLER: 'Reseller',
    SADMIN_AUDIENCE_ALL: 'Ambos',
  },
  COUPON: {
    PAGE_TITLE: 'Cupones',
    PAGE_SUBTITLE: 'Crea y administra cupones para clientes nuevos',
    CREATE: 'Crear cupón',
    EDIT: 'Editar cupón',
    DELETE_CONFIRM:
      '¿Eliminar este cupón? No podrá usarse en nuevos registros.',
    EMPTY_LIST: 'Aún no hay cupones. Crea el primero con el botón de arriba.',
    CODE: 'Código',
    CODE_HINT:
      'Solo mayúsculas, dígitos, guion (-) y guion bajo (_). Mínimo 3 caracteres.',
    CODE_TOO_SHORT: 'El código debe tener al menos 3 caracteres.',
    DESCRIPTION: 'Descripción',
    DISCOUNT_TYPE: 'Tipo de descuento',
    DISCOUNT_VALUE: 'Valor del descuento',
    DISCOUNT_TYPE_PERCENT: 'Porcentaje',
    DISCOUNT_TYPE_FIXED: 'Monto fijo',
    APPLIES_TO: 'Aplica a',
    APPLIES_TO_ACCOUNT_UPGRADE: 'Mejora de plan',
    VALID_FROM: 'Válido desde',
    VALID_UNTIL: 'Válido hasta',
    NO_EXPIRY: 'Sin fecha de expiración',
    MAX_USES: 'Usos máximos',
    UNLIMITED: 'Ilimitado',
    CURRENT_USES: 'Usos actuales',
    MODE: 'Modo de uso',
    MODE_SINGLE_USE: 'Un solo uso',
    MODE_RECURRING: 'Recurrente',
    MODE_SINGLE_USE_HINT:
      'Se aplica a la siguiente factura únicamente. Después se desactiva.',
    MODE_RECURRING_HINT:
      'Se aplica a todas las facturas siguientes hasta que el sadmin lo revoque o se alcance el límite.',
    MAX_APPLICATIONS: 'Aplicaciones máximas por canje',
    MAX_APPLICATIONS_HINT:
      'Cuántas facturas recibirán el descuento por cada negocio que canjea el código. Dejar vacío = ilimitado hasta que sadmin revoque.',
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    TOGGLE_ACTIVE: 'Activar/Desactivar',
    SEARCH_PLACEHOLDER: 'Buscar cupón...',
    VALIDATE_LABEL: 'Cupón (opcional)',
    VALIDATE_BUTTON: 'Validar',
    VALID_BADGE: 'Cupón válido',
    INVALID_NOT_FOUND: 'Cupón no encontrado',
    INVALID_INACTIVE: 'Cupón inactivo',
    INVALID_NOT_YET_VALID: 'Cupón aún no válido',
    INVALID_EXPIRED: 'Cupón expirado',
    INVALID_MAX_USES: 'Cupón agotado',
    APPLIED_DISCOUNT_PREVIEW: 'Descuento aplicado: {{value}}',
    STATUS: 'Estado',
    STATUS_VALID: 'Vigente',
    STATUS_EXPIRED: 'Expirado',
  },
  ONBOARDING: {
    TITLE: '¡Bienvenido a Neo App!',
    SUBTITLE:
      'Configuremos tu negocio en pocos pasos para que puedas comenzar a vender de inmediato.',
    PROGRESS_TITLE: 'Progreso',
    CONTINUE: 'Continuar',
    BACK: 'Atrás',
    SKIP_STEP: 'Omitir paso',
    COMPLETE_SETUP: 'Completar configuración',
    FOOTER_TEXT:
      'Puedes cambiar cualquiera de estas configuraciones más tarde en el panel de administración.',
    GO_TO_LOGIN: 'Ir a la página de inicio de sesión',
    STEPS: {
      BUSINESS: {
        TITLE: 'Información del Negocio',
        DESCRIPTION: 'Configuremos la información básica de tu empresa',
        SECTION_GENERAL: 'Información General',
        SECTION_ADDRESS: 'Información de Dirección',
        NAME_LABEL: 'Nombre del Negocio',
        LEGAL_NAME_LABEL: 'Razón Social',
        BUSINESS_TYPE_LABEL: 'Tipo de Negocio',
        TAX_ID_LABEL: 'RNC / Cédula',
        PHONE_LABEL: 'Teléfono',
        EMAIL_LABEL: 'Email de Contacto',
        CURRENCY_LABEL: 'Moneda Principal',
        CURRENCY_HELPER: 'Código ISO de 3 letras (ej: USD, EUR, DOP)',
        ADDRESS_LINE1_LABEL: 'Dirección Principal',
        ADDRESS_LINE2_LABEL: 'Dirección Secundaria',
        CITY_LABEL: 'Ciudad',
        STATE_LABEL: 'Provincia/Estado',
        COUNTRY_LABEL: 'País',
      },
      INVOICE: {
        TITLE: 'Configuración de Facturas',
        DESCRIPTION: 'Personaliza cómo se generan y muestran tus facturas',
        SECTION_CONFIG: 'Configuración de Facturas',
        ENABLE_INVOICES: 'Habilitar Facturas',
        ENABLE_CREDIT_INVOICES_DESC:
          'Permite generar facturas a crédito a tus clientes',
        PAYMENT_TERMS_LABEL: 'Términos de Pago por Defecto',
        INCLUDE_LOGO: 'Incluir Logo de la Empresa',
        INCLUDE_LOGO_DESC: 'Mostrar el logo de tu empresa en las facturas',
        FOOTER_NOTES_LABEL: 'Notas del Pie de Página',
        FOOTER_NOTES_DESC:
          'Texto que aparecerá en la parte inferior de todas las facturas',
        SKIP_INFO_TITLE: 'Las facturas están deshabilitadas',
        SKIP_INFO_DESC:
          'Puedes habilitar las facturas más tarde desde la configuración del sistema.',
        TERMS_AND_CONDITIONS_LABEL: 'Términos y Condiciones',
        TERMS_AND_CONDITIONS_DESC:
          'Define los términos y condiciones estándar para tus ventas',
        ENABLE_CREDIT_INVOICES_LABEL: 'Habilitar Facturas de Crédito',
      },
      NCF: {
        TITLE: 'Números de Comprobante Fiscal (NCF)',
        DESCRIPTION:
          'Configura los NCF requeridos para facturación en República Dominicana (opcional)',
        SECTION_CONFIG: 'Configuración de NCF',
        INFO_TITLE: '¿Qué son los NCF?',
        INFO_DESC:
          'Los NCF son números únicos requeridos por la DGII para validar comprobantes fiscales en República Dominicana.',
        ENABLE_NCF: 'Habilitar NCF',
        ENABLE_NCF_DESC:
          'Requerido si tu negocio está ubicado en República Dominicana',
        NCF_TYPE_LABEL: 'Tipo de NCF',
        NCF_TYPE_PLACEHOLDER: 'Selecciona el tipo de NCF',
        SERIE_LABEL: 'Serie',
        PREFIX_LABEL: 'Prefijo',
        AUTO_FILL_DESCRIPTION:
          'Se asigna automáticamente, pero puede modificarse',
        SEQUENCE_LABEL: 'Secuencia Inicial',
        FINAL_SEQUENCE_LABEL: 'Secuencia Final',
        EXPIRATION_DATE_LABEL: 'Fecha de Expiración',
        NCF_PREVIEW: 'Vista previa del NCF:',
        SKIP_INFO_TITLE: 'NCF no configurado',
        SKIP_INFO_DESC:
          'Los NCF son opcionales si tu negocio no está en República Dominicana.',
      },
      TAX: {
        TITLE: 'Configuración de Impuestos',
        DESCRIPTION:
          'Configura los impuestos que se aplicarán a tus productos y servicios',
        SECTION_CONFIG: 'Configuración de Impuestos',
        ENABLE_TAXES: 'Habilitar Impuestos',
        ENABLE_TAXES_DESC: 'Aplica impuestos automáticamente a tus ventas',
        TAX_NAME_LABEL: 'Nombre del Impuesto',
        DEFAULT_TAX_RATE_LABEL: 'Tasa de Impuesto (%)',
        ENABLE_ITBIS: 'Habilitar ITBIS',
        ENABLE_ITBIS_DESC:
          'Impuesto sobre Transferencias de Bienes Industrializados y Servicios',
        ITBIS_RATE_LABEL: 'Tasa de ITBIS (%)',
        SKIP_INFO_TITLE: 'Los impuestos están deshabilitados',
        SKIP_INFO_DESC:
          'Puedes configurar los impuestos más tarde desde la configuración del sistema.',
      },
      ADMIN_USER: {
        TITLE: 'Queremos conocerte mejor',
        HEADING: 'Datos personales del administrador',
        DESCRIPTION:
          'Por favor completa la siguiente información para configurar tu cuenta de administrador.',
        DESCRIPTION_GOOGLE:
          'Te registraste con Google. Completa tus datos y crea una contraseña para acceder también con email.',
        FIRST_NAME_LABEL: 'Nombre',
        FIRST_NAME_PLACEHOLDER: 'Tu primer nombre',
        LAST_NAME_LABEL: 'Apellido',
        LAST_NAME_PLACEHOLDER: 'Tu apellido',
        PHONE_LABEL: 'Teléfono',
        PHONE_PLACEHOLDER: '+1 (555) 000-0000',
        PHONE_DESCRIPTION: 'Formato: +1 (555) 000-0000',
        EMAIL_LABEL: 'Correo Electrónico',
        EMAIL_PLACEHOLDER: 'tu.email@example.com',
        PASSWORD_LABEL: 'contraseña',
        PASSWORD_PLACEHOLDER: 'Tu contraseña',
        PASSWORD_LABEL_GOOGLE: 'Crear contraseña',
        PASSWORD_PLACEHOLDER_GOOGLE: 'Crea una contraseña para tu cuenta',
      },
      MONTHLY_VOLUME: {
        TITLE: 'Volumen de Facturación',
        DESCRIPTION: 'Esta información nos ayuda a personalizar tu experiencia',
        QUESTION: '¿Cuánto factura tu empresa mensualmente?',
        OPTION_50K: 'Hasta RD$ 50,000',
        OPTION_500K: 'Hasta RD$ 500,000',
        OPTION_1M: 'Hasta RD$ 1,000,000',
        OPTION_10M_PLUS: 'Más de RD$ 10,000,000',
      },
    },
    COMPLETION: {
      TITLE: '¡Configuración Completa!',
      DESC: 'Tu negocio está listo para comenzar a operar. Serás redirigido al panel de control.',
    },
    VALIDATING_TOKEN: 'Validando enlace de activación...',
    SETTING_UP_ACCOUNT: 'Configurando tu cuenta...',
    PLEASE_WAIT: 'Por favor espera mientras preparamos todo para ti.',
    INVALID_TOKEN_TITLE: 'Enlace Inválido',
    INVALID_TOKEN_DESCRIPTION:
      'El enlace de activación no es válido o ha expirado. Por favor solicita uno nuevo o inicia sesión si ya completaste el proceso.',
  },
  SYSTEM_OPTIONS: {
    TIMEZONES: {
      SANTO_DOMINGO: 'Santo Domingo (GMT-4)',
      NEW_YORK: 'New York (GMT-5)',
      CHICAGO: 'Chicago (GMT-6)',
      LOS_ANGELES: 'Los Angeles (GMT-8)',
      MADRID: 'Madrid (GMT+1)',
    },
    TIME_FORMATS: {
      '12H': '12 horas (02:30 PM)',
      '24H': '24 horas (14:30)',
    },
    BUSINESS_TYPES: {
      RESTAURANT: 'Restaurante',
      RETAIL: 'Tienda al por menor',
      SERVICES: 'Servicios',
      FOOD_BEVERAGES: 'Alimentos y bebidas',
      AUTO_PARTS: 'Autopartes y Repuestos',
      EDUCATION: 'Educación',
      PHARMACY_WELLNESS: 'Farmacias / Bienestar',
      HARDWARE_STORE: 'Ferretería',
      GASTRONOMY: 'Gastronomía',
      CLOTHING_SHOES: 'Ropa / Calzado',
      VETERINARY: 'Veterinaria',
      OTHER: 'Otro',
    },
    CURRENCIES: {
      DOP: 'Peso Dominicano (RD$)',
      USD: 'Dólar Estadounidense ($)',
      EUR: 'Euro (€)',
      MXN: 'Peso Mexicano ($)',
      COP: 'Peso Colombiano ($)',
      ARS: 'Peso Argentino ($)',
      BRL: 'Real Brasileño (R$)',
      CLP: 'Peso Chileno ($)',
      PEN: 'Sol Peruano (S/)',
      VES: 'Bolívar Venezolano (Bs.)',
      BOB: 'Boliviano (Bs.)',
      PYG: 'Guaraní Paraguayo (₲)',
      UYU: 'Peso Uruguayo ($U)',
      GTQ: 'Quetzal Guatemalteco (Q)',
      HNL: 'Lempira Hondureño (L)',
      NIO: 'Córdoba Nicaragüense (C$)',
      CRC: 'Colón Costarricense (₡)',
      PAB: 'Balboa Panameño (B/.)',
      CUP: 'Peso Cubano ($)',
    },
    PLANS: {
      FREE: 'Gratis',
      STARTUP: 'Startup',
      PROFESIONAL: 'Profesional',
    },
    CUSTOMER_TYPES: {
      INDIVIDUAL: 'Persona',
      COMPANY: 'Empresa',
      ANONYMOUS: 'Anónimo',
    },
    NCF_TYPES: {
      B01: 'B01 - Crédito Fiscal',
      B02: 'B02 - Consumidor Final',
      B03: 'B03 - Nota de Débito',
      B04: 'B04 - Nota de Crédito',
      B14: 'B14 - Regímenes Especiales',
      B15: 'B15 - Gubernamental',
      B16: 'B16 - Exportaciones',
      E31: 'E31 - e-Crédito Fiscal',
      E32: 'E32 - e-Consumidor Final',
      E33: 'E33 - e-Nota de Débito',
      E34: 'E34 - e-Nota de Crédito',
      E41: 'E41 - e-Comprobante de Compras',
      E42: 'E42 - e-Registro Único de Ingresos',
      E43: 'E43 - e-Gastos Menores',
      E44: 'E44 - e-Regímenes Especiales',
      E45: 'E45 - e-Gubernamental',
      E46: 'E46 - e-Exportaciones',
      E47: 'E47 - e-Pagos al Exterior',
    },
    CREDIT_TERMS: {
      CONTADO: 'Contado',
      CONTADO_DELIVERY: 'Contado - Pago contra entrega',
      CREDITO_15: 'Crédito 15 días',
      CREDITO_30: 'Crédito 30 días',
      CREDITO_60: 'Crédito 60 días',
      CREDITO_90: 'Crédito 90 días',
    },
    PAYMENT_TERMS: {
      CONTADO: 'Contado',
      NET_30: 'Neto 30 días',
      NET_60: 'Neto 60 días',
      NET_90: 'Neto 90 días',
      CUSTOM: 'Personalizado',
    },
    INVOICE_STATUS: {
      DRAFT: 'Borrador',
      PENDING_PAYMENT: 'Pendiente de Pago',
      DUE: 'Por Pagar',
      PARTIAL: 'Parcial',
      PAID: 'Pagado',
      CANCELLED: 'Anulada',
    },
    INVOICE_TYPE: {
      CONSUMO: 'Consumo',
      FISCAL_CREDIT: 'Crédito Fiscal',
      CREDIT_NOTE: 'Nota de Crédito',
      DEBIT_NOTE: 'Nota de Débito',
      INVOICE: 'Factura',
      GOVERNMENTAL: 'Gubernamental',
      SPECIAL_REGIMES: 'Regímenes Especiales',
      PURCHASES: 'Compras',
      MINOR_EXPENSES: 'Gastos Menores',
      UNIQUE_INCOME: 'Ingresos Únicos',
      EXPORTS: 'Exportaciones',
      FOREIGN_PAYMENTS: 'Pagos al Exterior',
    },
    TAX_SYSTEMS: {
      GENERAL: 'Régimen General',
      SIMPLIFIED: 'Régimen Simplificado',
    },
    COMPANY_TYPES: {
      SMALL: 'Pequeña',
      MEDIUM: 'Mediana',
      LARGE: 'Grande',
    },
    FISCAL_YEARS: {
      CALENDAR: 'Año Calendario',
      CUSTOM: 'Año Fiscal Personalizado',
    },
    IMPORT_PLATFORMS: {
      LOYVERSE: 'Loyverse',
      SHOPIFY: 'Shopify',
      WOOCOMMERCE: 'WooCommerce',
      CUSTOM: 'Personalizado',
    },
    PRICE_TIERS: {
      WHOLESALE: 'Mayorista',
      DISTRIBUTOR: 'Distribuidor',
      VIP: 'VIP',
      RETAIL: 'Minorista',
    },
    UNIT_OF_MEASURE: {
      UNIDAD: 'Unidad',
      KG: 'Kilogramo (kg)',
      G: 'Gramo (g)',
      LB: 'Libra (lb)',
      OZ: 'Onza (oz)',
      LITRO: 'Litro (L)',
      ML: 'Mililitro (ml)',
      PAQUETE: 'Paquete',
      CAJA: 'Caja',
      DOCENA: 'Docena',
      MG: 'Miligramo (mg)',
      TONE: 'Tonelada (t)',
      Q: 'Quintal (q)',
      OZT: 'Onza troy (oz t)',
      GL: 'Galón (gal)',
      M3: 'Metro cúbico (m³)',
      PZA: 'Pieza (pza)',
      EA: 'Elemento (ea)',
      PAR: 'Par',
      MILLAR: 'Millar',
      M: 'Metro (m)',
      CM: 'Centímetro (cm)',
      PULG: 'Pulgada (pulg)',
      PIE: 'Pie',
      YD: 'Yarda (yd)',
      M2: 'Metro cuadrado (m²)',
      YD2: 'Yarda cuadrada (yd²)',
      P2: 'Pie cuadrado (p²)',
      HA: 'Hectárea (ha)',
      SEG: 'Segundo (s)',
      MIN: 'Minuto (min)',
      HOR: 'Hora (h)',
      DIA: 'Día',
      KWH: 'Kilovatio hora (kWh)',
      MMBTU: 'Millones de BTU (MMBTU)',
      BARR: 'Barril',
      BOL: 'Bolsa',
      BOT: 'Bote',
      BOTELLA: 'Botella',
      CAJETILLA: 'Cajetilla',
      CIL: 'Cilindro',
      CONJ: 'Conjunto',
      CONT: 'Contenedor',
      FARD: 'Fardo',
      HUAC: 'Huacal',
      LOT: 'Lote',
      ROL: 'Rollo',
      SOBR: 'Sobre',
      TANQUE: 'Tanque',
      TUB: 'Tubo',
      SAC: 'Saco',
      LAT: 'Lata',
      DIS: 'Display',
      BID: 'Bidón',
      RAC: 'Ración',
      BDJ: 'Bandeja',
      BULTO: 'Bultos',
      GRAN: 'Granel',
      GRAD: 'Grado',
      PAX: 'Pasajero',
      GRT: 'Tonelada de registro bruto (GRT)',
      STAY: 'Estadía en muelle',
    },
    TAX_ID_TYPES: {
      SSN: 'SSN - Número de Seguro Social (EE.UU.)',
      EIN: 'EIN - Número de Identificación del Empleador (EE.UU.)',
      RFC: 'RFC - Registro Federal de Contribuyentes (México)',
      RNC: 'RNC - Registro Nacional de Contribuyentes (RD)',
      CUIT: 'CUIT - Clave Única de Identificación Tributaria (Argentina)',
      CPF: 'CPF - Cadastro de Pessoas Físicas (Brasil)',
      NIT: 'NIT - Número de Identificación Tributaria (Colombia)',
      RUN: 'RUN - Rol Único Nacional (Chile)',
      DNI: 'DNI - Documento Nacional de Identidad (España)',
      ID: 'ID - Documento de Identificación General',
      CEDULA: 'Cédula - Documento de Identidad (varios países)',
      OTHER: 'Otro Tipo de ID Fiscal',
    },
    COUNTRIES: {
      DOMINICAN_REPUBLIC: 'República Dominicana',
      UNITED_STATES: 'Estados Unidos',
      MEXICO: 'México',
      COLOMBIA: 'Colombia',
      ARGENTINA: 'Argentina',
      BRAZIL: 'Brasil',
      CHILE: 'Chile',
      PERU: 'Perú',
      VENEZUELA: 'Venezuela',
      ECUADOR: 'Ecuador',
      BOLIVIA: 'Bolivia',
      PARAGUAY: 'Paraguay',
      URUGUAY: 'Uruguay',
      GUATEMALA: 'Guatemala',
      EL_SALVADOR: 'El Salvador',
      HONDURAS: 'Honduras',
      NICARAGUA: 'Nicaragua',
      COSTA_RICA: 'Costa Rica',
      PANAMA: 'Panamá',
      CUBA: 'Cuba',
      PUERTO_RICO: 'Puerto Rico',
      SPAIN: 'España',
    },
    BANKS: {
      BHD: 'BHD León',
      POPULAR: 'Banco Popular',
      BANRESERVAS: 'Banreservas',
      SCOTIABANK: 'Scotiabank',
    },
  },
  REGISTER: {
    TITLE: 'Registrarse en Neo App',
    DESCRIPTION: 'Crea tu cuenta para gestionar tu negocio',
    EMAIL: 'Correo Electrónico',
    EMAIL_PLACEHOLDER: 'tu@email.com',
    PASSWORD: 'contraseña',
    PASSWORD_PLACEHOLDER: 'Crea una contraseña fuerte',
    CONFIRM_PASSWORD: 'Confirmar contraseña',
    CONFIRM_PASSWORD_PLACEHOLDER: 'Confirma tu contraseña',
    COUNTRY: 'País',
    SELECT_COUNTRY: 'Selecciona tu país',
    PICK_PLAN: 'Elegir plan',
    PICK_PLAN_REQUIRED: 'Debes elegir un plan para continuar.',
    PICK_PLAN_HINT: 'Elige el plan con el que quieres empezar.',
    SELECTED_PLAN: 'Plan seleccionado',
    CHANGE_PLAN: 'Cambiar plan',
    INTERVAL_MONTHLY: 'Mensual',
    INTERVAL_ANNUAL: 'Anual',
    PLAN_PICKER_TITLE: 'Elige el plan que mejor se adapta a tu negocio',
    PLAN_PICKER_DESC: 'Puedes cambiarlo después desde Cuenta → Facturación.',
    PLAN_PICKER_CHOOSE: 'Elegir',
    PLAN_PICKER_EMPTY: 'No hay planes disponibles en este momento.',
    RECOMMENDED_BADGE: 'Recomendado',
    REGISTER_BUTTON: 'Registrarse',
    REGISTERING: 'Registrando...',
    ALREADY_HAVE_ACCOUNT: '¿Ya tienes una cuenta?',
    LOGIN_HERE: 'Inicia sesión aquí',
    SIGN_UP_WITH_GOOGLE: 'Registrarse con Google',
    SIGN_IN_WITH_GOOGLE: 'Iniciar sesión con Google',
    TERMS_AND_CONDITIONS: 'Términos y Condiciones',
    PRIVACY_POLICY: 'Política de Privacidad',
    I_AGREE_TO: 'Acepto los Términos y Condiciones y la Política de Privacidad',
    ACCEPT_TERMS: 'Debes aceptar los términos y condiciones para continuar',
    PASSWORD_REQUIREMENTS: 'Requisitos de contraseña',
    PASSWORD_REQUIREMENTS_DESC:
      'Tu contraseña debe cumplir con los siguientes requisitos:',
    MINIMUM_8_CHARACTERS: 'Mínimo 8 caracteres',
    ONE_UPPERCASE_LETTER: 'Al menos una letra mayúscula (A-Z)',
    ONE_LOWERCASE_LETTER: 'Al menos una letra minúscula (a-z)',
    ONE_SPECIAL_CHARACTER: 'Al menos un carácter especial (!@#$%^&*)',
    ONE_NUMBER: 'Al menos un número (0-9)',
    TERMS_TITLE: 'Términos y Condiciones de Uso',
    PRIVACY_TITLE: 'Política de Privacidad y Protección de Datos',
    TERMS_SECTION_1: 'Descripción del Servicio y Definiciones',
    TERMS_SECTION_1_DESC:
      'Neogeniux SRL (en adelante "la Empresa") es la sociedad comercial titular y desarrolladora del producto de software denominado Neo App (en adelante "la Plataforma" o "el Servicio"). La Plataforma es un sistema de gestión empresarial SaaS que proporciona herramientas para la administración de inventario, ventas, facturación electrónica (e-CF) conforme a la normativa de la Dirección General de Impuestos Internos (DGII) de la República Dominicana, cobros, análisis empresarial y otros módulos de gestión. En estos Términos, cualquier referencia a "Neogeniux" o "la Empresa" alude a Neogeniux SRL, y cualquier referencia a "Neo App", "la Plataforma" o "el Servicio" alude al producto de software licenciado al usuario. La Plataforma opera bajo las leyes y regulaciones de la República Dominicana.',
    TERMS_SECTION_2: 'Cuenta de Usuario y Responsabilidades',
    TERMS_SECTION_2_DESC:
      'Eres responsable de mantener la confidencialidad de tus credenciales de acceso y de toda la actividad que ocurra bajo tu cuenta. Debes notificarnos inmediatamente ante cualquier uso no autorizado. Cada usuario es personalmente responsable del cumplimiento de las obligaciones fiscales derivadas del uso de la Plataforma, incluyendo la correcta emisión de Comprobantes Fiscales Electrónicos (e-CF) conforme a la normativa de la DGII.',
    TERMS_SECTION_3: 'Datos, Contenido y Facturación Electrónica',
    TERMS_SECTION_3_DESC:
      'Retienes todos los derechos sobre los datos que cargues en la Plataforma. Nos autorizas a almacenar, procesar, transmitir y mantener copias de seguridad de tus datos en servidores seguros. En el marco de la Facturación Electrónica, autorizas expresamente a Neogeniux a actuar como Proveedor de Servicios de Facturación Electrónica (PSP) ante la DGII, generando, firmando digitalmente y enviando los e-CF en tu nombre conforme al marco normativo vigente.',
    TERMS_SECTION_4: 'Obligaciones Fiscales y Cumplimiento DGII',
    TERMS_SECTION_4_DESC:
      'El usuario es el único responsable ante la DGII por el contenido de sus comprobantes fiscales. Neogeniux actúa como intermediario tecnológico y no asume responsabilidad por errores en los datos ingresados por el usuario, rechazos de e-CF derivados de información incorrecta, incumplimientos de las obligaciones tributarias del contribuyente, ni por interrupciones en los sistemas de la DGII fuera del control de Neogeniux.',
    TERMS_SECTION_5: 'Limitación de Responsabilidad',
    TERMS_SECTION_5_DESC:
      'La Plataforma se proporciona "tal como está". No seremos responsables de daños indirectos, incidentales, especiales o consecuentes derivados del uso del servicio, incluyendo pérdidas de negocio, datos o ganancias. Nuestra responsabilidad máxima ante cualquier reclamación se limita al monto pagado por el servicio en los últimos tres (3) meses.',
    TERMS_SECTION_6: 'Modificaciones del Servicio',
    TERMS_SECTION_6_DESC:
      'Nos reservamos el derecho de modificar, actualizar o descontinuar funcionalidades del servicio en cualquier momento. Te notificaremos de cambios significativos con al menos quince (15) días de anticipación mediante correo electrónico o notificación dentro de la Plataforma.',
    TERMS_SECTION_7: 'Terminación del Servicio',
    TERMS_SECTION_7_DESC:
      'Puedes solicitar la cancelación de tu cuenta en cualquier momento contactando a nuestro equipo de soporte. Conservaremos tus datos durante el período establecido en nuestra política de retención, conforme a los requisitos legales aplicables, incluyendo los plazos de conservación de registros fiscales exigidos por la DGII y el Código Tributario de la República Dominicana.',
    TERMS_SECTION_8: 'Tarifas y Pagos',
    TERMS_SECTION_8_DESC:
      'El uso de la Plataforma está sujeto a los planes de suscripción vigentes publicados en nuestro sitio web. Los pagos son procesados de forma segura. En caso de mora, nos reservamos el derecho de suspender el acceso hasta regularizar el estado de la cuenta, previa notificación.',
    TERMS_SECTION_9: 'Propiedad Intelectual',
    TERMS_SECTION_9_DESC:
      'Todos los derechos de propiedad intelectual sobre la Plataforma, su código, diseño, marcas y documentación pertenecen a Neogeniux SRL. Se concede al usuario una licencia limitada, no exclusiva e intransferible para usar la Plataforma conforme a estos Términos. Queda prohibida la ingeniería inversa, copia o distribución del software.',
    TERMS_SECTION_10: 'Ley Aplicable y Jurisdicción',
    TERMS_SECTION_10_DESC:
      'Estos Términos y Condiciones se rigen por las leyes de la República Dominicana, incluyendo el Código Civil, la Ley 126-02 sobre Comercio Electrónico y la normativa de la DGII. Para cualquier controversia, las partes se someten a la jurisdicción de los tribunales competentes de Santo Domingo, República Dominicana.',
    TERMS_LAST_UPDATED: 'Última actualización: Julio 2025',
    PRIVACY_SECTION_1: 'Responsable del Tratamiento',
    PRIVACY_SECTION_1_DESC:
      'Neogeniux SRL (en adelante "la Empresa"), con domicilio en la República Dominicana, es la sociedad titular del producto Neo App y la responsable del tratamiento de sus datos personales conforme a la Ley núm. 172-13 sobre Protección de Datos de Carácter Personal. Los datos recopilados a través de Neo App son tratados exclusivamente por Neogeniux SRL. Para cualquier consulta relacionada con el tratamiento de sus datos, puede contactarnos a través de los canales indicados al final de este documento.',
    PRIVACY_SECTION_2: 'Información que Recopilamos',
    PRIVACY_SECTION_2_DESC:
      'Recopilamos: (a) Datos que usted proporciona directamente: nombre completo, correo electrónico, número de teléfono, Registro Nacional del Contribuyente (RNC) o Cédula de Identidad, información de su empresa, datos de facturación y de sus clientes/proveedores. (b) Datos recopilados automáticamente: dirección IP, tipo de navegador, páginas visitadas, duración de sesiones y datos de uso de la Plataforma mediante cookies y tecnologías similares.',
    PRIVACY_SECTION_3: 'Finalidad y Base Legal del Tratamiento',
    PRIVACY_SECTION_3_DESC:
      'Sus datos son tratados para: proveer y mejorar los servicios de la Plataforma; emitir Comprobantes Fiscales Electrónicos (e-CF) ante la DGII en su nombre; cumplir con obligaciones legales y tributarias de la República Dominicana; comunicarnos con usted sobre actualizaciones y soporte; prevenir fraudes y garantizar la seguridad. La base legal es la ejecución del contrato de servicio, el cumplimiento de obligaciones legales y el interés legítimo de Neogeniux.',
    PRIVACY_SECTION_4: 'Almacenamiento y Seguridad de Datos',
    PRIVACY_SECTION_4_DESC:
      'Sus datos se almacenan en servidores seguros con cifrado en reposo (AES-256) y en tránsito (TLS 1.2+). Implementamos controles de acceso basados en roles, autenticación multifactor para accesos administrativos y backups diarios. Los datos de e-CF y registros fiscales se conservan por el período mínimo exigido por la normativa tributaria dominicana.',
    PRIVACY_SECTION_5: 'Transferencia y Compartición de Datos',
    PRIVACY_SECTION_5_DESC:
      'No compartimos sus datos personales con terceros con fines comerciales. Podremos compartir datos con: (a) La DGII, en cumplimiento de la normativa de Facturación Electrónica; (b) Proveedores de servicios de infraestructura tecnológica bajo estrictos acuerdos de confidencialidad; (c) Autoridades competentes cuando sea legalmente requerido. Cualquier transferencia internacional de datos se realiza bajo garantías adecuadas conforme a la Ley 172-13.',
    PRIVACY_SECTION_6: 'Sus Derechos (ARCO)',
    PRIVACY_SECTION_6_DESC:
      'Conforme a la Ley núm. 172-13, usted tiene derecho a: Acceso (conocer qué datos tratamos), Rectificación (corregir datos inexactos), Cancelación (solicitar la eliminación de sus datos) y Oposición (oponerse al tratamiento para determinadas finalidades). Para ejercer estos derechos, contáctenos indicando su solicitud y adjuntando documento de identidad. Responderemos en un plazo máximo de 30 días hábiles.',
    PRIVACY_SECTION_7: 'Cookies y Tecnologías de Seguimiento',
    PRIVACY_SECTION_7_DESC:
      'Utilizamos cookies esenciales (necesarias para el funcionamiento de la Plataforma), cookies de rendimiento (para analizar el uso y mejorar la experiencia) y cookies de sesión (para mantener su sesión activa). Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad de la Plataforma. No utilizamos cookies para publicidad de terceros.',
    PRIVACY_SECTION_8: 'Retención de Datos',
    PRIVACY_SECTION_8_DESC:
      'Conservamos sus datos personales mientras mantenga una cuenta activa en la Plataforma. Tras la cancelación de su cuenta, conservaremos los datos necesarios para cumplir con obligaciones legales (incluyendo registros fiscales exigidos por la DGII), prevenir fraudes y resolver disputas, por un período no mayor a diez (10) años conforme al Código Tributario de la República Dominicana, o el período aplicable según la naturaleza del dato.',
    PRIVACY_SECTION_9: 'Menores de Edad',
    PRIVACY_SECTION_9_DESC:
      'La Plataforma está dirigida exclusivamente a personas jurídicas y personas físicas mayores de 18 años que actúen en calidad de contribuyentes o representantes de empresas. No recopilamos conscientemente datos personales de menores de edad.',
    PRIVACY_SECTION_10: 'Contacto y Reclamaciones',
    PRIVACY_SECTION_10_DESC:
      'Para ejercer sus derechos, realizar consultas o presentar reclamaciones sobre el tratamiento de sus datos, puede contactarnos en: soporte@neogeniux.com. Si considera que sus derechos no han sido atendidos adecuadamente, tiene derecho a presentar una reclamación ante el Instituto Dominicano de Telecomunicaciones (INDOTEL) u organismo competente conforme a la Ley 172-13.',
    PRIVACY_LAST_UPDATED:
      'Última actualización: Julio 2025 | Ley núm. 172-13 — República Dominicana',
    OR: 'O',
    REGISTRATION_FAILED: 'El registro falló. Por favor, intenta nuevamente.',
    REGISTRATION_SUCCESS:
      'Registro exitoso. Se ha enviado un correo de confirmación a {{email}}. Por favor, verifica tu correo para completar el registro.',
    REGISTRATION_SUCCESS_TITLE: 'Registro Exitoso',
    EMAIL_ERROR_TRY_AGAIN:
      'Ha ocurrido un error, por favor inténtalo de nuevo más tarde.',
    RESEND_VERIFICATION_EMAIL: 'Reenviar correo de verificación',
    RESEND_EMAIL_SUCCESS: 'Correo de verificación reenviado exitosamente.',
    RESEND_EMAIL_SENDING: 'Enviando...',
    RESEND_EMAIL_COOLDOWN: 'Esperar {{seconds}}s',
    DIDNT_RECEIVE_EMAIL: '¿No recibiste el correo?',
    SELECT_COUNTRY_FOR_GOOGLE:
      'Selecciona tu país primero para registrarte con Google',
    GOOGLE_AUTH_FAILED:
      'Error al autenticar con Google. Por favor, intenta nuevamente.',
    GOOGLE_NOT_CONFIGURED: 'Google Sign-In no está configurado',
    GOOGLE_REGISTRATION_SUCCESS:
      '¡Registro exitoso con Google! Tu cuenta ha sido creada con el correo {{email}}. Ahora puedes continuar con la configuración de tu negocio.',
    CONTINUE_TO_ONBOARDING: 'Continuar con la configuración',
    COMMING_SOON: 'Pronto disponible en más países de LATAM',
  },
  APPROVAL: {
    // Credit limit exceeded
    CREDIT_LIMIT_EXCEEDED_TITLE: 'Límite de Crédito Excedido',
    CREDIT_LIMIT_EXCEEDED_DESCRIPTION:
      'Este cliente ha excedido su límite de crédito. Se requiere aprobación de un gerente para continuar.',
    CREDIT_LIMIT_WARNING: 'Límite de crédito excedido',
    OUTSTANDING_BALANCE: 'Saldo Pendiente',
    CREDIT_NOTE_BALANCE: 'Notas de crédito disponibles',
    NEW_INVOICE_TOTAL: 'Total Nueva Factura',
    TOTAL_AFTER_INVOICE: 'Total Después de Factura',
    CREDIT_LIMIT: 'Límite de Crédito',
    AVAILABLE_CREDIT: 'Crédito Disponible',
    EXCEEDED_BY: 'Excede por',
    VIEW_PENDING_INVOICES: 'Ver facturas pendientes',
    // Actions
    REQUEST_APPROVAL: 'Solicitar Aprobación',
    ENTER_APPROVAL_CODE: 'Ingresar Código de Aprobación',
    APPROVAL_CODE: 'Código de Aprobación',
    APPROVAL_CODE_PLACEHOLDER: 'Ingresa el código de 8 caracteres',
    VALIDATING_CODE: 'Validando código...',
    INVALID_CODE: 'Código inválido o no encontrado',
    CODE_EXPIRED: 'El código ha expirado',
    CODE_ALREADY_USED: 'Este código ya ha sido utilizado',
    CODE_VALID: 'Código válido',
    CODE_ERROR_FORMAT_INVALID: 'Formato de código inválido',
    CODE_ERROR_NOT_FOUND: 'Código inválido o expirado',
    CODE_ERROR_CUSTOMER_MISMATCH:
      'Este código de aprobación pertenece a otro cliente. Solicita una nueva aprobación para el cliente actual.',
    CODE_ERROR_AMOUNT_INSUFFICIENT:
      'Este código de aprobación no cubre el total de la factura actual. Solicita una nueva aprobación por el monto mayor.',
    CODE_ERROR_TERMS_MISMATCH:
      'Este código fue otorgado para un término de crédito diferente. Solicita una nueva aprobación con el término actual.',
    MANAGER_PIN: 'PIN del gerente',
    MANAGER_PIN_TAB: 'PIN del gerente',
    PIN_HINT:
      'Pídele al gerente que ingrese su PIN personal de 4 dígitos para aprobar esta solicitud al instante.',
    CARD_OR_PIN_HINT:
      'El gerente puede ingresar su PIN o pasar su tarjeta de acceso para aprobar.',
    CARD_SCAN_AFTER_VALUE:
      'Primero ingresa el valor y luego pasa la tarjeta del gerente.',
    PIN_FORMAT_INVALID: 'El PIN debe tener exactamente 4 dígitos',
    PIN_VALUE_INVALID: 'Ingresa un valor válido para esta aprobación',
    PIN_APPROVED: 'Aprobado por el gerente',
    PIN_INVALID: 'PIN inválido o sin permisos de aprobación',
    CARD_INVALID: 'Tarjeta inválida o sin permisos de aprobación',
    APPROVED_VALUE: 'Valor aprobado',
    DISCOUNT_APPROVAL_TITLE: 'Aplicar descuento',
    DISCOUNT_APPROVAL_DESCRIPTION:
      'Pide al gerente que apruebe un descuento sobre el total de la factura. Una vez aprobado se aplica automáticamente.',
    DISCOUNT_APPROVAL_BUTTON: 'Aplicar descuento',
    DISCOUNT_PERCENT: 'Porcentaje de descuento (%)',
    DISCOUNT_PERCENT_REQUESTED: 'Porcentaje solicitado (%)',
    DISCOUNT_PREVIEW: 'Descuento aproximado: {{amount}}',
    DISCOUNT_AMOUNT: 'Monto de descuento (RD$)',
    DISCOUNT_PREVIEW_PERCENT: 'Equivale a {{percent}}% del subtotal',
    DISCOUNT_SUBTOTAL_MISSING:
      'Subtotal no disponible. Agrega artículos antes de aplicar un descuento.',
    DISCOUNT_APPLIED: 'Descuento aplicado ({{percent}}%)',
    MANAGER_PIN_SETTINGS_TITLE: 'PIN de aprobación del gerente',
    MANAGER_PIN_SETTINGS_DESCRIPTION:
      'Configura un PIN personal de 4 dígitos. Los cajeros podrán pedírtelo para aprobar solicitudes al instante (sin enviar la solicitud a través del sistema).',
    MANAGER_PIN_SAVED: 'PIN guardado',
    MANAGER_PIN_CLEARED: 'PIN eliminado',
    STAFF_PIN: 'PIN de identificación',
    STAFF_PIN_SETTINGS_TITLE: 'Configura tu PIN de aprobación',
    STAFF_PIN_SETTINGS_DESCRIPTION:
      'Tu PIN personal de 4 dígitos te identifica al abrir o atender mesas. Solo tú lo conoces y queda registrado para fines de auditoría.',
    PROCEED_WITH_CODE: 'Continuar con código',
    // Request approval flow
    APPROVAL_REQUESTED: 'Aprobación Solicitada',
    APPROVAL_REQUEST_SENT:
      'Se ha enviado una solicitud de aprobación al gerente. Recibirás un código cuando sea aprobada.',
    WAITING_FOR_APPROVAL: 'Esperando aprobación...',
    APPROVAL_REQUEST_PENDING: 'Solicitud pendiente de aprobación',
    APPROVAL_REQUEST_APPROVED: 'Solicitud aprobada',
    APPROVAL_REQUEST_REJECTED: 'Solicitud rechazada',
    APPROVAL_REQUEST_REJECTED_DESCRIPTION:
      'El gerente rechazó esta solicitud de aprobación. Cierra este diálogo y contacta al gerente si necesitas más información.',
    // Manager view
    PENDING_APPROVALS: 'Aprobaciones Pendientes',
    NO_PENDING_APPROVALS: 'No hay aprobaciones pendientes',
    APPROVE: 'Aprobar',
    REJECT: 'Rechazar',
    REJECTION_REASON: 'Motivo de Rechazo',
    REJECTION_REASON_PLACEHOLDER: 'Ingresa el motivo del rechazo',
    APPROVAL_CODE_GENERATED: 'Código de aprobación generado',
    SHARE_APPROVAL_CODE:
      'Comparte este código con el solicitante. El código expirará en 1 hora.',
    CODE_EXPIRES_IN: 'El código expira en',
    // General
    CANCEL: 'Cancelar',
    CLOSE: 'Cerrar',
    CUSTOMER: 'Cliente',
    REQUESTED_BY: 'Solicitado por',
    REQUESTED_AT: 'Fecha de solicitud',
    APPROVED_BY: 'Aprobado por',
    APPROVED_AT: 'Fecha de aprobación',
    REJECTED_BY: 'Rechazado por',
    REJECTED_AT: 'Fecha de rechazo',
    // New translations for notification bell and polling
    NEW_APPROVAL_REQUESTS: '{{count}} nueva(s) solicitud(es) de aprobación',
    VIEW_ALL_APPROVALS: 'Ver todas las aprobaciones',
    PENDING_APPROVAL_COUNT: '{{count}} aprobación(es) pendiente(s)',
    SAVE_DRAFT_CONFIRMATION_TITLE: 'Aprobación pendiente',
    SAVE_DRAFT_CONFIRMATION_DESCRIPTION:
      'Tienes una solicitud de aprobación pendiente. ¿Deseas guardar la factura como borrador?',
    SAVE_AS_DRAFT: 'Guardar como borrador',
    DISCARD_AND_CLOSE: 'Descartar y cerrar',
    APPROVAL_RECEIVED: '¡Aprobación recibida!',
    // Approvals page
    APPROVALS_PAGE_TITLE: 'Aprobaciones',
    APPROVALS_PAGE_DESCRIPTION:
      'Gestiona las solicitudes de aprobación pendientes',
    NO_APPROVALS_FOUND: 'No se encontraron aprobaciones',
    APPROVAL_DETAILS: 'Detalles de aprobación',
    INVOICE_TOTAL: 'Total de Factura',
    EXCEEDS_BY: 'Excede por',
    STATUS: 'Estado',
    ACTIONS: 'Acciones',
    // Approval types
    TYPES: {
      CREDIT_LIMIT_EXCEEDED: 'Límite de crédito excedido',
      CREDIT_LIMIT_OVERRIDE: 'Sobrescribir límite de crédito',
      REFUND_OUTSIDE_WINDOW: 'Reembolso fuera de plazo',
      DISCOUNT_OVERRIDE: 'Sobrescribir descuento',
      PRICE_OVERRIDE: 'Sobrescribir precio',
      OTHER: 'Otro',
    },
    // Additional page translations
    REQUESTER: 'Solicitante',
    APPROVED_SUCCESS: 'Solicitud aprobada exitosamente',
    APPROVE_ERROR: 'Error al aprobar la solicitud',
    REJECTED_SUCCESS: 'Solicitud rechazada exitosamente',
    REJECT_ERROR: 'Error al rechazar la solicitud',
    COLUMN_DATE: 'Fecha',
    COLUMN_TYPE: 'Tipo',
    COLUMN_STATUS: 'Estado',
    COLUMN_ACTIONS: 'Acciones',
    INVOICE_ITEMS: 'Productos solicitados',
    INVOICE_ITEMS_COUNT: '{{count}} producto(s)',
    VIEW_DETAILS: 'Ver detalles',
    CUSTOMER_NAME: 'Cliente',
    EXCEED_AMOUNT: 'Monto excedido',
    PRODUCT: 'Producto',
    QUANTITY: 'Cant.',
    UNIT_PRICE: 'Precio',
    TOTAL: 'Total',
    NO_ITEMS: 'No se incluyeron productos en la solicitud',
    MY_REQUESTS: 'Mis Solicitudes',
    MY_REQUESTS_DESCRIPTION:
      'Historial de tus solicitudes de aprobación enviadas a un gerente.',
    NO_REQUESTS: 'No tienes solicitudes recientes',
    SELF_APPROVED_REASON: 'Auto-aprobado por usuario autorizado',
    APPROVAL_PENDING: 'Pendiente',
    APPROVAL_APPROVED: 'Aprobado',
    APPROVAL_REJECTED: 'Rechazado',
    APPROVAL_EXPIRED: 'Expirado',
    APPROVAL_USED: 'Usado',
    CREDIT_LIMIT_OVERRIDE: 'Override de límite de crédito',
    REFUND_OUTSIDE_WINDOW: 'Reembolso fuera de ventana',
    DISCOUNT_OVERRIDE: 'Override de descuento',
    PRICE_OVERRIDE: 'Override de precio',
    POS_ITEM_REMOVAL: 'Remoción de artículo (POS)',
    COMMANDA_REPRINT: 'Reimpresión de comanda',
    OTHER_REASON: 'Otra solicitud',
  },
  ECF_ONBOARDING: {
    TITLE: 'Facturación Electrónica (e-CF)',
    SUBTITLE:
      'Configura tu negocio para emitir comprobantes fiscales electrónicos con la DGII',
    ALREADY_ACTIVE_TITLE: 'Facturación electrónica activa',
    ALREADY_ACTIVE_DESCRIPTION:
      'Tu negocio ya tiene habilitada la facturación electrónica. Puedes gestionar la configuración desde los ajustes del negocio.',
    GO_TO_SETTINGS: 'Ir a Configuración',
    STEP_WELCOME: 'Información',
    STEP_CERTIFICATE: 'Certificado',
    STEP_NCF: 'Secuencias NCF',
    STEP_ACTIVATION: 'Activación',
    STEP_POSTULATION: 'Postulación',
    STEP_DATA_TESTS: 'Pruebas de Datos',
    STEP_APPROVAL: 'Aprobación Comercial',
    STEP_SIMULATION: 'Simulación e-CF',
    STEP_PRINTED_REP: 'Representación Impresa',
    STEP_SERVICE_URLS: 'URL Servicios',
    STEP_RECEPTION_TEST: 'Prueba Recepción',
    STEP_APPROVAL_TEST: 'Prueba Aprobación',
    STEP_PRODUCTION_URLS: 'URL Producción',
    STEP_SWORN_DECLARATION: 'Declaración Jurada',
    STEP_WELCOME_DESC: 'Beneficios y requisitos',
    STEP_CERTIFICATE_DESC: 'Sube tu certificado digital',
    STEP_NCF_DESC: 'Configura tus e-NCF',
    STEP_POSTULATION_DESC: 'Firma y envía tu postulación',
    STEP_DATA_TESTS_DESC: 'Ejecuta las pruebas de datos DGII',
    STEP_APPROVAL_DESC: 'Pruebas de aprobación comercial',
    STEP_SIMULATION_DESC: 'Simulación de comprobantes e-CF',
    STEP_PRINTED_REP_DESC: 'Genera representaciones impresas',
    STEP_SERVICE_URLS_DESC: 'Registra las URLs de prueba en DGII',
    STEP_RECEPTION_TEST_DESC: 'Inicio prueba recepción e-CF',
    STEP_APPROVAL_TEST_DESC: 'Inicio prueba aprobación comercial',
    STEP_PRODUCTION_URLS_DESC: 'Registra las URLs de producción en DGII',
    STEP_SWORN_DECLARATION_DESC: 'Firma y sube la declaración jurada',
    STEP_ACTIVATION_DESC: 'Activa la facturación electrónica',
    STEP_COMPLETED: 'Completado',
    RESUME_MESSAGE:
      'Tienes progreso guardado. Puedes continuar desde donde lo dejaste.',
    RESET_PROGRESS: 'Reiniciar progreso',
    RESET_CONFIRM:
      '¿Estás seguro de que deseas reiniciar todo el progreso de certificación? Esta acción no se puede deshacer.',
    NEXT: 'Siguiente',
    BACK: 'Atrás',
    SKIP: 'Omitir',
    FINISH: 'Finalizar',
    WELCOME: {
      HERO_TITLE:
        '¡Pásate a la facturación electrónica y recibe beneficios de la DGII!',
      HERO_SUBTITLE:
        'Emite comprobantes fiscales electrónicos (e-CF) de forma rápida, segura y cumpliendo con las normativas de la DGII.',
      CTA_BUTTON: 'Comenzar configuración',
      BENEFITS_TITLE: 'Beneficios de la facturación electrónica',
      BENEFIT_ECONOMIC_TITLE: 'Ahorro económico',
      BENEFIT_ECONOMIC_DESC:
        'Reduce costos de impresión, almacenamiento y envío de comprobantes fiscales en papel.',
      BENEFIT_AGILITY_TITLE: 'Mayor agilidad',
      BENEFIT_AGILITY_DESC:
        'Envía y procesa tus comprobantes al instante, sin intermediarios ni retrasos.',
      BENEFIT_COMPLIANCE_TITLE: 'Cumplimiento fiscal',
      BENEFIT_COMPLIANCE_DESC:
        'Cumple automáticamente con los requisitos de la DGII para la emisión de comprobantes electrónicos.',
      BENEFIT_ENVIRONMENT_TITLE: 'Cuidado ambiental',
      BENEFIT_ENVIRONMENT_DESC:
        'Contribuye al medio ambiente eliminando el uso de papel en tus comprobantes fiscales.',
      REQUIREMENTS_TITLE: 'Requisitos para comenzar',
      REQUIREMENT_RNC: 'Tener un RNC activo y registrado en la DGII',
      REQUIREMENT_DGII_PORTAL:
        'Acceso al portal de la Oficina Virtual de la DGII',
      REQUIREMENT_CERTIFICATE:
        'Certificado digital (archivo P12/PFX) emitido por la DGII',
      REQUIREMENT_NCF:
        'Secuencias de e-NCF asignadas por la DGII (E31, E32, E34)',
    },
    CERTIFICATE: {
      TITLE: 'Certificado Digital',
      DESCRIPTION:
        'Sube el certificado digital (P12/PFX) que te proporcionó la DGII para firmar tus comprobantes electrónicos.',
      UPLOAD_LABEL: 'Archivo del certificado',
      UPLOAD_PLACEHOLDER: 'Selecciona tu archivo .p12 o .pfx',
      UPLOAD_HINT: 'Archivo P12 o PFX proporcionado por la DGII. Máximo 5MB.',
      UPLOAD_FANOUT_HINT:
        'Sube el certificado una sola vez: se guardará automáticamente en los 3 ambientes (Pre-Certificación, Certificación y Producción) para agilizar tu configuración. Podrás probar la autenticación con DGII por ambiente desde Certificados DGII cuando finalices el onboarding.',
      PASSPHRASE_LABEL: 'contraseña del certificado',
      PASSPHRASE_PLACEHOLDER: 'Ingresa la contraseña de tu certificado',
      ENVIRONMENT_LABEL: 'Ambiente DGII',
      ENVIRONMENT_ALL: 'Todos los ambientes (Recomendado)',
      ENVIRONMENT_TESTECF: 'Pre-Certificación (TesteCF)',
      ENVIRONMENT_CERTECF: 'Certificación (CerteCF)',
      ENVIRONMENT_ECF: 'Producción (eCF)',
      UPLOAD_BUTTON: 'Subir certificado',
      UPLOADING: 'Subiendo certificado...',
      UPLOAD_SUCCESS: 'Certificado subido exitosamente',
      UPLOAD_ERROR: 'Error al subir el certificado',
      UPLOAD_ERROR_SHOW_DETAIL: 'Ver más',
      UPLOAD_ERROR_HIDE_DETAIL: 'Ver menos',
      TEST_AUTH_BUTTON: 'Probar autenticación con DGII',
      TESTING_AUTH: 'Probando autenticación...',
      AUTH_SUCCESS: 'Autenticación con DGII exitosa',
      AUTH_ERROR: 'Error en la autenticación con DGII',
      CERT_INFO_TITLE: 'Información del certificado',
      CERT_SUBJECT: 'Titular',
      CERT_RNC: 'RNC',
      CERT_VALID_FROM: 'Válido desde',
      CERT_VALID_TO: 'Válido hasta',
      HOW_TO_GET_TITLE: '¿Cómo obtener tu certificado digital?',
      HOW_TO_GET_STEP1:
        'Solicita tu certificado digital (e-Firma) a través de ViaFirma o DigiFirma',
      HOW_TO_GET_STEP2:
        'Completa el proceso de verificación de identidad con el proveedor',
      HOW_TO_GET_STEP3:
        'Descarga el archivo P12 que recibirás por correo o en la plataforma',
      HOW_TO_GET_STEP4:
        'Guarda la contraseña del P12 de forma segura — la necesitarás para subirlo aquí',
      MANAGE_TITLE: 'Certificados registrados',
      MANAGE_NO_CERTS: 'No tienes certificados registrados',
      MANAGE_DELETE_CONFIRM:
        '¿Estás seguro de que deseas eliminar este certificado?',
      MANAGE_ACTIVE: 'Activo',
      MANAGE_INACTIVE: 'Inactivo',
      STATUS_READY:
        'Certificado válido y activo detectado. Puede continuar al siguiente paso.',
      STATUS_BLOCKED: 'No puede continuar al siguiente paso',
      STATUS_NO_CERTS:
        'No tiene certificados registrados. Suba un certificado P12 para continuar.',
      STATUS_ALL_INACTIVE:
        'Todos los certificados están inactivos. Active al menos un certificado o suba uno nuevo.',
      STATUS_ALL_EXPIRED:
        'Todos los certificados activos están vencidos. Suba un certificado vigente para continuar.',
      STATUS_NO_VALID:
        'No hay un certificado válido para el ambiente seleccionado. Suba un certificado P12 vigente para ese ambiente para continuar.',
      ENV_STATUS_HEADER: 'Estado por ambiente DGII',
      ENV_GUIDE_BUTTON: '¿Cuál seleccionar?',
      ENV_GUIDE_TITLE: 'Cómo elegir el ambiente DGII',
      ENV_GUIDE_DESCRIPTION:
        'DGII te asignará una URL para tu proceso de postulación (por ejemplo: TesteCF, CerteCF o eCF). Selecciona aquí abajo el mismo ambiente que aparece en la URL que DGII te presentó. Si estás en el proceso de certificación con DGII, típicamente será CerteCF.',
      ENV_STATUS_VALID: 'Vigente',
      ENV_STATUS_MISSING: 'Sin certificado',
      ENV_STATUS_EXPIRED: 'Vencido',
      ENV_STATUS_EXPIRES_SOON: 'Vence en {{days}} días',
      ENV_STATUS_PENDING_ACTIVATION: 'Pendiente (se activa al finalizar)',
      UPLOAD_ALL_ENVIRONMENTS_TOGGLE:
        'Subir a los 3 ambientes (Pre-Certificación + Certificación + Producción)',
      UPLOAD_ALL_ENVIRONMENTS_HELP:
        'Guarda el certificado en Pre-Certificación, Certificación y Producción en una sola acción. Después de subirlo puedes probar la autenticación con DGII por ambiente desde los botones "Probar autenticación con DGII".',
      UPLOAD_REPLACE_TITLE: 'Reemplazar certificados existentes',
      UPLOAD_REPLACE_DESCRIPTION:
        'Ya tienes un certificado válido en {{existing}}. Puedes reemplazarlo con el nuevo o subir solamente a los ambientes que están vacíos o vencidos ({{missing}}).',
      UPLOAD_REPLACE_ALL: 'Reemplazar todos los ambientes',
      UPLOAD_REPLACE_ONLY_MISSING: 'Solo subir a los faltantes',
      UPLOAD_REPLACE_NONE_MISSING:
        'Todos los ambientes ya tienen un certificado válido. Confirma para reemplazarlos con el nuevo.',
      UPLOAD_ATOMIC_HELP:
        'El certificado se guardará en cada ambiente seleccionado. La autenticación con DGII se prueba después con el botón "Probar autenticación con DGII" por ambiente.',
      UPLOAD_TESTECF_ONLY_HELP:
        'Certecf es naturalmente el ambiente de certificación con DGII.',
      PROMOTE_TITLE: 'Preparar Certificación y Producción',
      PROMOTE_DESCRIPTION:
        'Antes de activar la facturación electrónica, copiaremos el certificado a los ambientes de Certificación y Producción y verificaremos la conexión con la DGII.',
      PROMOTE_RUN_BUTTON: 'Preparar ambientes',
      PROMOTE_RUNNING: 'Validando contra DGII…',
      PROMOTE_ENV_OK: 'Conectado con DGII',
      PROMOTE_ENV_FAIL: 'DGII rechazó la autenticación',
      PROMOTE_CONTACT_DGII:
        'Contacta a la DGII y comparte el mensaje anterior. Una vez resuelto, vuelve aquí y presiona Reintentar.',
      PROMOTE_RETRY: 'Reintentar',
      PROMOTE_ALL_OK:
        'Todos los ambientes están conectados. Ya puedes activar la facturación electrónica.',
    },
    NCF: {
      TITLE: 'Secuencias de e-NCF',
      DESCRIPTION:
        'Configura las secuencias de Números de Comprobante Fiscal Electrónico (e-NCF) asignadas por la DGII.',
      INFO_TEXT:
        'Las secuencias e-NCF son rangos numéricos que la DGII asigna a tu negocio para los diferentes tipos de comprobantes electrónicos (E31, E32, E34).',
      CONFIGURE_BUTTON: 'Configurar secuencias NCF',
      EXISTING_SEQUENCES: 'Secuencias e-NCF configuradas',
      NO_SEQUENCES:
        'No tienes secuencias e-NCF configuradas. Presiona el botón para crear tus secuencias.',
      TYPE: 'Tipo',
      SERIE: 'Serie',
      FROM: 'Desde',
      TO: 'Hasta',
      CURRENT: 'Actual',
    },
    ACTIVATION: {
      TITLE: 'Activar facturación electrónica',
      DESCRIPTION:
        'Revisa que todo esté configurado correctamente y activa la facturación electrónica para tu negocio.',
      CHECKLIST_TITLE: 'Lista de verificación',
      CHECK_CERTIFICATE: 'Certificado digital subido y verificado',
      CHECK_NCF: 'Secuencias e-NCF configuradas',
      CHECK_ENVIRONMENT: 'Ambiente de DGII seleccionado',
      ENVIRONMENT_LABEL: 'Ambiente',
      ACTIVATE_BUTTON: 'Activar facturación electrónica',
      ACTIVATING: 'Activando...',
      SUCCESS_TITLE: '¡Facturación electrónica activada!',
      SUCCESS_DESCRIPTION:
        'Tu negocio ahora puede emitir comprobantes fiscales electrónicos. Las nuevas facturas se enviarán automáticamente a la DGII.',
      GO_TO_INVOICES: 'Ir a Facturas',
      GUIDE_TITLE: 'Cómo completar la activación',
      GUIDE_STEP1:
        'Si la declaración fue firmada correctamente podrás visualizar en el paso #13 la validación de RNC por unos minutos',
      GUIDE_STEP2:
        'Luego la DGII al validar correctamente, mostrará un mensaje final confirmando la activación',
      SHOW_FINAL: 'Ver mensaje final DGII',
    },
    SERVICE_URLS: {
      TITLE: 'URL Servicios de Prueba',
      DESCRIPTION:
        'Registra estas URLs en el portal de certificación de la DGII para que puedan comunicarse con tu sistema.',
      AUTH_URL_LABEL: 'Servicio de Autenticación',
      RECEPTION_URL_LABEL: 'Servicio de Recepción',
      APPROVAL_URL_LABEL: 'Servicio de Aprobación Comercial',
      COPIED: 'URL copiada al portapapeles',
      GUIDE_TITLE: 'Cómo completar Servicios de prueba',
      GUIDE_STEP1:
        'Ingresa al Portal de Certificación de la DGII y selecciona "URL Servicios de prueba"',
      GUIDE_STEP2:
        'Copia cada URL de abajo y pégala en el campo correspondiente del portal DGII',
      GUIDE_STEP3:
        'Verifica que las 3 URLs estén correctamente registradas en el portal',
      GUIDE_STEP4:
        'Haz click en Confirmar URLs en el portal DGII para completar esta etapa',
      SHOW_STEPS: 'Ver pasos en DGII',
    },
    RECEPTION_TEST: {
      TITLE: 'Inicio Prueba Recepción e-CF',
      DESCRIPTION:
        'Confirma las URLs de recepción en el portal de certificación de la DGII para iniciar las pruebas.',
      AUTH_URL_LABEL: 'Servicio de Autenticación',
      RECEPTION_URL_LABEL: 'Servicio de Recepción',
      COPIED: 'URL copiada al portapapeles',
      GUIDE_TITLE: 'Cómo completar Prueba de Recepción',
      GUIDE_STEP1: 'Confirma las URLs de Prueba de Recepción',
      GUIDE_STEP2: 'Haz click en el botón Enviar Prueba de Comprobantes',
      GUIDE_STEP3:
        'Al hacer click DGII ejecutará las pruebas y mostrará un mensaje como este',
      GUIDE_STEP4:
        "Si DGII valida de forma exitosa nuestros servicios automáticamente pasará al paso #10 y podrás hacer click en 'DGII validó los servicios de Recepción'",
      SHOW_STEPS: 'Ver pasos en DGII',
      SHOW_TEST_RESULT: 'Ver resultado de prueba',
      CONFIRM_DESCRIPTION:
        'Una vez DGII haya validado exitosamente los servicios de recepción, confirma aquí para continuar.',
      CONFIRM_BUTTON: 'DGII validó los servicios de Recepción',
    },
    APPROVAL_TEST: {
      TITLE: 'Inicio Prueba Recepción Aprobación Comercial',
      DESCRIPTION:
        'Enviar Prueba de Aprobaciones comerciales para completar esta etapa.',
      AUTH_URL_LABEL: 'Servicio de Autenticación',
      APPROVAL_URL_LABEL: 'Servicio de Aprobación Comercial',
      COPIED: 'URL copiada al portapapeles',
      GUIDE_TITLE: 'Cómo completar Prueba de Aprobación Comercial',
      GUIDE_STEP1: 'Confirma las URLs de Prueba de Aprobación Comercial',
      GUIDE_STEP2:
        'Haz click en el botón Enviar Prueba de Aprobaciones Comerciales',
      GUIDE_STEP3:
        'Al hacer click DGII ejecutará las pruebas y mostrará un mensaje como este',
      GUIDE_STEP4:
        "Si DGII valida de forma exitosa nuestros servicios automáticamente pasará al siguiente paso y podrás hacer click en 'DGII validó los servicios de Aprobación Comercial'",
      SHOW_STEPS: 'Ver pasos en DGII',
      SHOW_TEST_RESULT: 'Ver resultado de prueba',
      CONFIRM_DESCRIPTION:
        'Una vez DGII haya validado exitosamente los servicios de aprobación comercial, confirma aquí para continuar.',
      CONFIRM_BUTTON: 'DGII validó los servicios de Aprobación Comercial',
    },
    PRODUCTION_URLS: {
      TITLE: 'URL Servicios Producción',
      DESCRIPTION:
        'Registra las mismas URLs en el ambiente de producción de la DGII y confirma con el botón "Confirmar URL".',
      AUTH_URL_LABEL: 'Servicio de Autenticación',
      RECEPTION_URL_LABEL: 'Servicio de Recepción',
      APPROVAL_URL_LABEL: 'Servicio de Aprobación Comercial',
      COPIED: 'URL copiada al portapapeles',
      GUIDE_TITLE: 'Cómo completar URL Servicios Producción',
      GUIDE_STEP1:
        'Ingresa al Portal de Producción de la DGII y selecciona "URL Servicios"',
      GUIDE_STEP2:
        'Copia cada URL de abajo y pégala en el campo correspondiente del portal DGII (son las mismas URLs de prueba)',
      GUIDE_STEP3:
        'Haz click en el botón "Confirmar URL" en el portal DGII para guardar los cambios',
      SHOW_STEPS: 'Ver pasos en DGII',
      CONFIRM_BUTTON: 'URLs de producción confirmadas',
    },
    SWORN_DECLARATION: {
      TITLE: 'Declaración Jurada',
      DESCRIPTION:
        'Descarga el XML de declaración jurada desde el portal DGII, fírmalo con tu certificado digital y súbelo de vuelta.',
      GUIDE_TITLE: 'Cómo completar la Declaración Jurada',
      GUIDE_STEP1:
        'En el portal DGII, haz click en "Generar Archivo" para descargar el XML de declaración jurada',
      GUIDE_STEP2:
        'Sube el archivo XML descargado usando el campo de archivo de abajo',
      GUIDE_STEP3:
        'Haz click en "Firmar XML" para firmar el documento con tu certificado digital',
      GUIDE_STEP4:
        'Descarga el XML firmado y súbelo al portal DGII para completar la declaración, luego has click en Enviar Archivo',
      SHOW_GENERATE: 'Ver paso generar archivo',
      SHOW_SIGNED: 'Ver paso archivo firmado',
      UPLOAD_XML: 'Archivo XML de declaración jurada',
      UPLOAD_XML_HINT:
        'Sube el archivo XML descargado del portal DGII (Generar Archivo)',
      SIGN_BUTTON: 'Firmar XML',
      SIGNING: 'Firmando...',
      SIGN_SUCCESS: 'XML firmado exitosamente',
      SIGN_ERROR: 'Error al firmar el XML',
      DOWNLOAD_SIGNED: 'Descargar XML firmado',
      NO_XML_ERROR: 'Sube un archivo XML primero',
    },
    ECF_REQUIRED_TOOLTIP:
      'Completa la configuración de facturación electrónica para usar este tipo de comprobante.',
  },
  ECF_SUBMISSION: {
    VIEW_SUBMISSION: 'Ver e-CF',
    SUBMISSION_DETAILS: 'Detalles de Envío e-CF',
    TRACK_ID: 'Track ID',
    SIGNED_XML: 'XML Firmado',
    DGII_RESPONSE: 'Respuesta DGII',
    SUBMITTED_AT: 'Fecha de Envío',
    RECEIVED_AT: 'Fecha de Recepción DGII',
    COPY_XML: 'Copiar XML',
    XML_COPIED: 'XML copiado al portapapeles',
    NO_SUBMISSION: 'Sin envío e-CF registrado',
    STATUS: 'Estado',
    ENVIRONMENT: 'Ambiente',
    SUBMISSION_TYPE: 'Tipo de Envío',
    RESPONSE_ERROR: 'Respuesta DGII',
    ENCF: 'e-NCF',
    DGII_QR: 'Código QR DGII',
    COPY_QR_URL: 'Copiar URL',
    QR_URL_COPIED: 'URL del QR copiada al portapapeles',
    SECURITY_CODE: 'Código de Seguridad',
    SIGNING_DATE: 'Fecha Firma Digital',
    QR_SCAN_OR_CLICK: 'Escanea o has click para verificar en DGII',
  },
  SADMIN_NOTIFICATIONS: {
    TITLE: 'Notificaciones',
    SUBTITLE: '{{count}} notificaciones en el sistema',
    DETAIL_TITLE: 'Detalle de la notificación',
    COL_DATE: 'Fecha',
    COL_TYPE: 'Tipo',
    COL_PRIORITY: 'Prioridad',
    COL_BUSINESS: 'Empresa',
    COL_RECIPIENT: 'Destinatario',
    COL_STATUS: 'Estado',
    STATUS_UNREAD: 'No leída',
    STATUS_READ: 'Leída',
    STATUS_DISMISSED: 'Descartada',
    STATUS_RESOLVED: 'Resuelta',
    STATUS_EXPIRED: 'Expirada',
    STATUS_ALL: 'Todas',
    FILTER_BUSINESS: 'Empresa',
    FILTER_USER: 'Usuario (ID)',
    FILTER_EVENT_TYPE: 'Tipo de evento',
    FILTER_STATUS: 'Estado',
    RECIPIENT_ROLE: 'rol',
    DETAIL_TYPE_LABEL: 'Tipo',
    DETAIL_PAYLOAD_LABEL: 'Payload',
    DETAIL_DELIVERIES_LABEL: 'Intentos de entrega',
    DETAIL_NO_DELIVERIES: 'Sin registros de entrega.',
    VIEW_DETAILS: 'Ver detalle',
    SELECT_BUSINESS_PLACEHOLDER: 'Todas las empresas',
    SELECT_BUSINESS_SEARCH: 'Buscar empresa…',
    SELECT_BUSINESS_EMPTY: 'Sin resultados',
    TAB_INBOX: 'En la app',
    TAB_EMAILS: 'Correos',
    EMAIL_TO: 'Para',
    EMAIL_SUBJECT: 'Asunto',
    EMAIL_STATUS_SENT: 'Enviado',
    EMAIL_STATUS_FAILED: 'Fallido',
    EMAIL_STATUS_SUPPRESSED: 'Suprimido por preferencia',
    EMAIL_MESSAGE_ID: 'Message ID',
    EMAIL_EVENT_KIND: 'Evento',
    EMAIL_COPY_HTML: 'Copiar HTML',
    EMAIL_READ_UNKNOWN_TOOLTIP:
      'No detectamos aperturas — solo confirmamos envío al proveedor.',
    EMAIL_REDACTED_BADGE: 'Contenido con secretos redactados',
    EMAIL_REDACTED_BANNER:
      'Contenido generado por Neo al momento del envío. Los tokens de autenticación se ocultan aquí por seguridad; no representa exactamente lo que Brevo entregó al buzón.',
    EMAIL_BODY_PURGED:
      'Contenido eliminado por política de retención (>90 días). Los metadatos se conservan hasta 18 meses.',
    EMAIL_DETAIL_TITLE: 'Detalle del correo',
    EMAIL_GENERATED_NOTE:
      'Contenido generado por Neo al momento del envío. No representa exactamente lo que Brevo entregó al buzón (los tokens de autenticación se ocultan aquí por seguridad).',
    EMAIL_FILTER_EVENT_KIND: 'Evento (código)',
    EMAIL_FILTER_STATUS: 'Estado de envío',
    EMAIL_EMPTY: 'Sin correos que coincidan con los filtros.',
  },
  SADMIN: {
    // Platform-fiscal comprobantes — per-tenant config the superadmin sets
    // for the comprobante the platform issues on subscription payments.
    PLATFORM_FISCAL_TITLE: 'Comprobante fiscal del cliente',
    PLATFORM_FISCAL_SUBTITLE:
      'Define qué comprobante emite la plataforma a este cliente al cobrar su suscripción.',
    PLATFORM_FISCAL_COMPROBANTE_LABEL: 'Tipo de comprobante',
    PLATFORM_FISCAL_PRE_FACTURA_OPTION: 'Pre-Factura (sin comprobante fiscal)',
    PLATFORM_FISCAL_EMISSION_LABEL: 'Emisión',
    PLATFORM_FISCAL_EMISSION_AUTO: 'Automática al cobrar',
    PLATFORM_FISCAL_EMISSION_MANUAL: 'Manual (sadmin genera)',
    PLATFORM_FISCAL_RNC_REQUIRED_HINT:
      'Este tipo requiere el RNC del cliente. Sin RNC no se emitirá y recibirás un aviso.',
    PLATFORM_FISCAL_TENANT_REQUESTED_HINT:
      'Este cliente pidió recibir comprobante fiscal en sus facturas. Elige el tipo de comprobante abajo; la decisión es tuya.',
    PLATFORM_FISCAL_SAVE: 'Guardar comprobante',
    PLATFORM_FISCAL_SAVE_SUCCESS: 'Configuración de comprobante actualizada',
    PLATFORM_FISCAL_GENERATE_PENDING: 'Generar comprobantes pendientes',
    PLATFORM_FISCAL_GENERATE_RESULT:
      '{{issued}} comprobante(s) emitido(s) de {{scanned}} factura(s) pendiente(s).',
    PLATFORM_FISCAL_GENERATE_NONE:
      'No hay facturas pagadas pendientes de comprobante para este cliente.',
    PLATFORM_FISCAL_GENERATE_BLOCKED:
      '{{blocked}} comprobante(s) bloqueado(s) (falta RNC, secuencia NCF o impuesto ITBIS). Revisa la configuración fiscal.',
    PLATFORM_FISCAL_REVERSE_PENDING: 'Emitir notas de crédito pendientes',
    PLATFORM_FISCAL_REVERSE_RESULT:
      '{{issued}} nota(s) de crédito emitida(s) de {{scanned}} factura(s) reembolsada(s).',
    PLATFORM_FISCAL_REVERSE_NONE:
      'No hay facturas reembolsadas pendientes de nota de crédito para este cliente.',
    PLATFORM_FISCAL_REVERSE_BLOCKED:
      '{{blocked}} nota(s) de crédito bloqueada(s) (sin secuencia B04/E34 o comprobante no fiscal). Revisa la configuración fiscal.',
    PLATFORM_FISCAL_MENU: 'Comprobantes fiscales',
    ENTER_BUSINESS_NAV: 'Entrar a negocio',
    BACK_TO_PANEL_NAV: 'Volver al panel',
    BUSINESS_MANAGEMENT: 'Gestión de Empresas',
    SYSTEM_ANNOUNCEMENTS_NAV: 'Avisos del Sistema',
    CLOCK_TAMPER_NAV: 'Reloj sospechoso',
    BUSINESS_MANAGEMENT_SUBTITLE: 'Administrar todas las empresas registradas',
    CREATE_BUSINESS_BUTTON: 'Crear negocio',
    CREATE_BUSINESS_TITLE: 'Crear nuevo negocio',
    CREATE_BUSINESS_SUBTITLE:
      'Onboarding directo sin verificación de email. Las credenciales se generan al final.',
    CREATE_BUSINESS_SUCCESS:
      'Negocio creado. Comparte estas credenciales con el cliente:',
    SEND_CREDENTIALS_BUTTON: 'Enviar credenciales por email',
    CREDENTIALS_EMAIL_SENT: 'Email con credenciales enviado al usuario',
    CREDENTIALS_EMAIL_SENT_LABEL: '✓ Email enviado',
    IMPERSONATE_TITLE: 'Impersonar Usuario',
    IMPERSONATE_CONFIRM:
      'Estás a punto de ver esta empresa como su administrador. ¿Deseas continuar?',
    IMPERSONATE_SUCCESS: 'Impersonación iniciada exitosamente',
    IMPERSONATE_ERROR: 'Error al impersonar usuario',
    FINANCIAL_EVENTS_TITLE: 'Eventos Financieros',
    ADMIN_LAST_LOGIN: 'Último acceso',
    SUBSCRIPTION_STATUS: 'Estado suscripción',
    CURRENT_PERIOD_END: 'Fin de ciclo',
    SPONSORED: 'Patrocinado',
    NO_SUBSCRIPTION: 'Sin suscripción',
    NO_BILLING_PERIOD: 'Sin ciclo',
    SUB_STATUS_ACTIVE: 'Activa',
    SUB_STATUS_TRIALING: 'En prueba',
    SUB_STATUS_PAST_DUE: 'Vencida',
    SUB_STATUS_SUSPENDED: 'Suspendida',
    SUB_STATUS_CANCELLED: 'Cancelada',
    SUB_STATUS_INACTIVE: 'Inactiva',
    ACTIVATION_EMAIL: 'Email de Activación',
    SENT: 'Enviado',
    NOT_SENT: 'No Enviado',
    RETURN_TO_ADMIN: 'Regresar a Admin',
    VIEWING_AS: 'Viendo como',
    BACK_TO_REGULAR: 'Volver a Vista Regular',
    END_IMPERSONATION: 'Finalizar Impersonación',
    NEVER_LOGGED_IN: 'Nunca',
    CONTRACT_VERSION: 'Contrato',
    NO_CONTRACT_SIGNED: 'Sin firmar',
    APPLIED_COUPON: 'Cupón',
    NO_COUPON_APPLIED: 'Sin cupón',
    BUSINESS_NAME: 'Nombre de Empresa',
    EMAIL: 'Email',
    ACTIONS: 'Acciones',
    EDIT_BUSINESS: 'Editar Empresa',
    SEARCH_BUSINESSES: 'Buscar empresas...',
    COL_INVOICE_NUMBER: '# Factura',
    COL_BUSINESS: 'Negocio',
    COL_STATUS: 'Estado',
    COL_TOTAL: 'Total',
    COL_ISSUED: 'Emitida',
    COL_DUE: 'Vence',
    COL_COMPROBANTE: 'Comprobante',
    CREATED_AT: 'Fecha de Creación',
    PLAN: 'Plan',
    TAB_BUSINESSES: 'Empresas',
    TAB_INCOMPLETE_REGISTRATIONS: 'Registros Incompletos',
    INCOMPLETE_REGISTRATIONS_SUBTITLE:
      'Usuarios que iniciaron el registro pero no completaron el proceso',
    SEARCH_REGISTRATIONS: 'Buscar por email o país...',
    REGISTRATION_SOURCE: 'Origen',
    SOURCE_EMAIL: 'Email',
    SOURCE_GOOGLE: 'Google',
    STATUS_PENDING_VERIFICATION: 'Pendiente de verificación',
    STATUS_PENDING: 'Pendiente',
    STATUS_VERIFIED: 'Email verificado',
    EMAIL_VERIFIED: 'Email verificado',
    COUNTRY: 'País',
    REGISTRATION_IP: 'IP',
    REGISTRATION_COORDS: 'Coordenadas',
    SUBSCRIPTION: {
      OPEN_TITLE: 'Suscripción',
      TITLE: 'Suscripción - {{businessName}}',
      SUBTITLE: 'Ver suscripción y pagos registrados',
      CURRENT_STATUS: 'Estado actual',
      PROVIDER: 'Proveedor',
      PLAN: 'Plan',
      SUBSCRIPTION_STATUS: 'Estado de suscripción',
      TRIAL_END: 'Fin del período de prueba',
      TRIAL_END_HINT:
        'Mueve esta fecha hacia adelante para empresas que preparan su plataforma pero aún no inician. Si la prueba ya venció, al extenderla se reactiva automáticamente.',
      REGISTER_PAYMENT_TITLE: 'Registrar pago manual',
      REGISTER_PAYMENT: 'Registrar',
      REGISTER_PAYMENT_SUBTITLE:
        'Registra un pago recibido por transferencia, efectivo u otro método',
      ONLINE_PAYMENTS_TITLE: 'Cobro en línea',
      ONLINE_PAYMENTS_SUBTITLE:
        'Cobrar a la empresa usando su tarjeta inscrita o PayPal',
      PAYMENT_METHODS_TITLE: 'Métodos de pago de la empresa',
      PRIMARY_PAYMENT_METHOD: 'Método principal',
      CHARGE_CARD: 'Cobrar con tarjeta',
      CHARGE_PAYPAL: 'Cobrar con PayPal',
      SPONSORED: 'Patrocinado',
      SPONSORED_DESCRIPTION:
        'La empresa tiene acceso completo sin facturación. Solo puede ser activado por un super administrador.',
      PROVIDER_BILLING_ACTIVE:
        '⚠️ Facturación del proveedor pendiente de cancelar',
      PROVIDER_BILLING_ACTIVE_DESCRIPTION:
        'Esta cuenta fue cancelada pero la suscripción con el proveedor de pago ({{provider}}) sigue activa. Es posible que aún se esté cobrando al cliente.',
      ADDONS_TITLE: 'Addons y Cupos',
      ADDONS_SUBTITLE: 'Resumen de addons activos, uso de cupos y vencimientos',
      NO_ADDONS: 'Esta empresa no tiene addons activos',
      SLOTS_PURCHASED: 'Comprados',
      SLOTS_IN_USE: 'En uso',
      SLOTS_UNUSED: 'Sin usar',
      SLOTS_EXPIRES: 'Expira',
      ADDON_ACTIVE: 'Activo',
      SCHEDULED_TITLE: 'Bajas programadas',
      SCHEDULED_SUBTITLE:
        'Cambios que reducen la suscripción. El cliente mantiene acceso completo hasta la fecha indicada.',
      SCHEDULED_APPLY_NOW: 'Aplicar ahora',
      SCHEDULED_APPLY_NOW_CONFIRM_TITLE: '¿Aplicar la baja de inmediato?',
      SCHEDULED_APPLY_NOW_CONFIRM_DESCRIPTION:
        'El cambio estaba programado para el {{date}}. Al aplicarlo ahora, el negocio pierde el acceso al plan actual inmediatamente y no se emite reembolso automático por el tiempo restante. Si el negocio excede los límites del plan destino la operación será rechazada.',
      SCHEDULED_APPLY_NOW_SUCCESS: 'Baja aplicada.',
    },
    PLAN_FEATURES: {
      TITLE: 'Planes y Funcionalidades',
      SUBTITLE:
        'Administrar los planes disponibles, límites y funcionalidades incluidas',
      CREATE_PLAN: 'Crear Plan',
      EDIT_PLAN: 'Editar Plan',
      DIALOG_SUBTITLE:
        'Configura los límites, precios y funcionalidades del plan',
      PLAN_CODE: 'Código del Plan',
      DISPLAY_NAME: 'Nombre Visible',
      PRICING: 'Precios',
      PRICE: 'Precio',
      ANNUAL_PRICE: 'Precio Anual (equivalente mensual)',
      ANNUAL_TOTAL_HINT:
        'El usuario pagará {{currency}} {{total}} como pago único anual',
      CURRENCY: 'Moneda',
      PERIOD: 'Período',
      DISPLAY_OPTIONS: 'Opciones de Visualización',
      SORT_ORDER: 'Orden',
      HIGHLIGHTED: 'Destacado',
      RESOURCE_LIMITS: 'Límites de Recursos',
      UNLIMITED_HINT: 'Usa -1 para indicar ilimitado',
      MAX_INVOICES: 'Facturas / mes',
      MAX_PRODUCTS: 'Productos',
      MAX_USERS: 'Usuarios',
      MAX_WAREHOUSES: 'Almacenes',
      MAX_BRANCHES: 'Sucursales',
      FEATURE_TOGGLES: 'Funcionalidades',
      INVENTORY: 'Inventario',
      CUSTOM_INTEGRATIONS: 'Integraciones',
      REPORTS_LEVEL: 'Nivel de Reportes',
      SUPPORT_LEVEL: 'Nivel de Soporte',
      PRIORITY_EMAIL: 'Email Prioritario',
      NO_PLANS: 'No hay planes configurados',
      NO_PLANS_DESCRIPTION:
        'Crea tu primer plan para definir los límites y funcionalidades disponibles',
      PAYMENT_PROVIDERS: 'Proveedores de Pago',
      STRIPE_CONFIGURATION: 'Configuración de Stripe',
      STRIPE_PRODUCT_ID: 'Stripe Product ID',
      STRIPE_PRICE_ID: 'Stripe Price ID',
      STRIPE_SPECIAL_OFFER_PRICE_ID: 'Stripe Special Offer Price ID',
      STRIPE_ANNUAL_PRICE_ID: 'Stripe Annual Price ID',
      PAYPAL_CONFIGURATION: 'Configuración de PayPal',
      PAYPAL_PRODUCT_ID: 'PayPal Product ID',
      PAYPAL_PLAN_ID: 'PayPal Plan ID',
      PAYPAL_SPECIAL_OFFER_PLAN_ID: 'PayPal Special Offer Plan ID',
      PAYPAL_ANNUAL_PLAN_ID: 'PayPal Annual Plan ID',
      SPECIAL_OFFER: 'Oferta Especial',
      SPECIAL_OFFER_HINT:
        'Configura un precio promocional que se mostrará con el precio original tachado. Deja el precio vacío para desactivar.',
      SPECIAL_OFFER_PRICE: 'Precio de Oferta',
      SPECIAL_OFFER_LABEL: 'Etiqueta de Oferta',
      SPECIAL_OFFER_START: 'Fecha de Inicio',
      SPECIAL_OFFER_END: 'Fecha de Fin',
      ADDON_PRICING: 'Precios de Cupos Adicionales',
      ADDON_PRICING_HINT:
        'Precio por cupo adicional de sucursal o usuario. Deja en blanco si el plan no permite cupos adicionales.',
      EXTRA_BRANCH_PRICE: 'Precio por Sucursal Adicional',
      EXTRA_USER_PRICE: 'Precio por Usuario Adicional',
      API_ACCESS_MONTHLY_PRICE: 'Precio de Acceso API (mensual)',
    },
    USER_MANAGEMENT: {
      TITLE: 'Gestión de Usuarios',
      DESCRIPTION:
        'Administrar usuarios de todas las empresas y desbloquear cuentas bloqueadas',
      UNLOCK_SUCCESS: 'Usuario {{username}} ha sido desbloqueado',
      UNLOCK_ERROR: 'No se pudo desbloquear el usuario',
      BLOCK_SUCCESS: 'Usuario bloqueado exitosamente',
      BLOCK_ERROR: 'No se pudo bloquear el usuario',
      RESET_PASSWORD_SUCCESS: 'Enlace de restablecimiento enviado al usuario',
      RESET_PASSWORD_ERROR: 'No se pudo enviar el enlace de restablecimiento',
      SEARCH_USERS: 'Buscar usuarios por nombre o email...',
      FILTER_ALL: 'Todos',
      FILTER_ACTIVE: 'Activos',
      FILTER_LOCKED: 'Bloqueados',
      FILTER_INACTIVE: 'Inactivos',
      COLUMN_NAME: 'Nombre',
      COLUMN_EMAIL: 'Email',
      COLUMN_STATUS: 'Estado',
      COLUMN_ROLE: 'Rol',
      COLUMN_BRANCH: 'Sucursal',
      COLUMN_LAST_LOGIN: 'Último acceso',
      COLUMN_CREATED: 'Creado',
      STATUS_ACTIVE: 'Activo',
      STATUS_LOCKED: 'Bloqueado',
      STATUS_INACTIVE: 'Inactivo',
      ACTION_RESET_PASSWORD: 'Restablecer contraseña',
      ACTION_BLOCK: 'Bloquear usuario',
      ACTION_UNLOCK: 'Desbloquear usuario',
      ACTION_DELETE: 'Eliminar usuario',
      CONFIRM_BLOCK_TITLE: 'Bloquear usuario',
      CONFIRM_BLOCK_DESCRIPTION:
        '¿Estás seguro de que deseas bloquear este usuario? No podrá iniciar sesión hasta ser desbloqueado.',
      CONFIRM_RESET_TITLE: 'Restablecer contraseña',
      CONFIRM_RESET_DESCRIPTION:
        'Se enviará un enlace de restablecimiento de contraseña al email del usuario.',
      CONFIRM_DELETE_TITLE: 'Eliminar usuario',
      CONFIRM_DELETE_DESCRIPTION:
        '¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer.',
      NEVER: 'Nunca',
    },
    ECF_CERTIFICATION: {
      TITLE: 'Certificación e-CF (DGII)',
      SUBTITLE:
        'Ejecutar los 25 casos de prueba DGII desde el XLSX de certificación',
      SELECT_BUSINESS: 'Seleccionar empresa',
      UPLOAD_XLSX: 'Archivo XLSX de certificación',
      UPLOAD_XLSX_HINT:
        'Sube el archivo XLSX proporcionado por DGII con los 25 casos de prueba',
      UPLOAD_P12: 'Certificado P12 (opcional)',
      UPLOAD_P12_HINT:
        'Si no se provee, se usará el certificado guardado de la empresa',
      PASSPHRASE: 'contraseña del certificado',
      PASSPHRASE_HINT: 'Dejar vacío si el certificado no tiene contraseña',
      ENVIRONMENT: 'Ambiente DGII',
      PARSE: 'Analizar XLSX',
      PARSING: 'Analizando...',
      RUN_ALL: 'Ejecutar todos',
      RUN_ALL_STOPPED:
        'Ejecución detenida en Caso {{casoPrueba}}: {{reason}}. Corrige el error antes de continuar.',
      RUNNING: 'Ejecutando...',
      RUNNING_PROGRESS: 'Ejecutando {{current}} / {{total}}',
      SIMULATION_LABEL_COLUMN: 'Descripción',
      SIMULATION_STATE_COMPLETED: 'Completado',
      SIMULATION_STATE_PENDING: 'Pendiente',
      SIMULATION_LABEL_E31: 'Factura Crédito Fiscal',
      SIMULATION_LABEL_E32_HIGH: 'Factura Consumo ≥250k',
      SIMULATION_LABEL_E32_LOW: 'Consumo <250k',
      SIMULATION_LABEL_E33: 'Nota de Débito',
      SIMULATION_LABEL_E34: 'Nota de Crédito',
      SIMULATION_LABEL_E41: 'Compras',
      SIMULATION_LABEL_E43: 'Gastos Menores',
      SIMULATION_LABEL_E44: 'Regímenes Especiales',
      SIMULATION_LABEL_E45: 'Gubernamental',
      SIMULATION_LABEL_E46: 'Exportaciones',
      SIMULATION_LABEL_E47: 'Pagos al Exterior',
      RETRY: 'Reintentar',
      RETRYING: 'Reintentando...',
      PREVIEW_TITLE: 'Vista previa de casos de prueba',
      RESULTS_TITLE: 'Resultados de certificación',
      RFCE_TITLE: 'Resumen RFCE',
      CASO: 'Caso',
      ENCF: 'e-NCF',
      TIPO_ECF: 'Tipo',
      RNC_COMPRADOR: 'RNC Comprador',
      MONTO_TOTAL: 'Monto Total',
      TOTAL_ITBIS: 'Total ITBIS',
      ITEMS: 'Items',
      STATUS: 'Estado',
      STATUS_ACCEPTED: 'Aceptado',
      STATUS_ACCEPTED_CONDITIONAL: 'Aceptado condicional',
      STATUS_REJECTED: 'Rechazado',
      STATUS_IN_PROCESS: 'En proceso',
      STATUS_SUBMITTED: 'Enviado',
      STATUS_GENERATED: 'Generado',
      STATUS_PENDING: 'Pendiente',
      STATUS_SKIPPED: 'Omitido',
      STATUS_ERROR: 'Error',
      STATUS_UNKNOWN: 'Desconocido',
      TRACK_ID: 'TrackId',
      ERROR: 'Error',
      PASSED: 'Aprobados',
      FAILED: 'Fallidos',
      TOTAL_TESTS: 'Total de pruebas',
      NO_FILE_SELECTED: 'No se ha seleccionado ningún archivo',
      PARSE_SUCCESS: 'XLSX analizado correctamente',
      PARSE_ERROR:
        'No pudimos analizar el XLSX. Verifica que estés subiendo el archivo correcto e inténtalo de nuevo.',
      RUN_SUCCESS: 'Certificación completada: {{passed}}/{{total}} aprobados',
      RUN_ERROR: 'Error al ejecutar la certificación',
      EMISOR_RNC: 'RNC Emisor',
      EMISOR_NAME: 'Razón Social Emisor',
      ENV_PRE_CERT: 'Pre-Certificación (testecf)',
      ENV_CERT: 'Certificación (certecf)',
      ENV_PROD: 'Producción (ecf)',
      STAGE_PRIMERO: 'Primero — Facturas y Comprobantes',
      STAGE_SEGUNDO: 'Segundo — Notas de Débito y Crédito',
      STAGE_TERCERO: 'Tercero — Resumen Factura de Consumo < RD$250,000.00',
      STAGE_CUARTO: 'Cuarto — Factura de Consumo < RD$250,000.00',
      STAGE_FALLBACK: 'Fase {{stage}}',
      ECF_TYPE_31: 'Factura de Crédito Fiscal Electrónica',
      ECF_TYPE_32: 'Factura de Consumo Electrónica',
      ECF_TYPE_32_MAYOR: 'Factura de Consumo Electrónica >= RD$250,000.00',
      ECF_TYPE_32_MENOR: 'Factura de Consumo Electrónica < RD$250,000.00',
      ECF_TYPE_32_RFCE:
        'Resumen Factura de Consumo Electrónica < RD$250,000.00',
      ECF_TYPE_33: 'Nota de Débito Electrónica',
      ECF_TYPE_34: 'Nota de Crédito Electrónica',
      ECF_TYPE_41: 'Compras Electrónico',
      ECF_TYPE_43: 'Gastos Menores Electrónico',
      ECF_TYPE_44: 'Regímenes Especiales Electrónico',
      ECF_TYPE_45: 'Gubernamental Electrónico',
      ECF_TYPE_46: 'Comprobante de Exportaciones Electrónico',
      ECF_TYPE_47: 'Comprobante para Pagos al Exterior Electrónico',
      SEND: 'Enviar',
      SENDING: 'Enviando...',
      SEND_CASE_SUCCESS: 'Caso {{casoPrueba}}: {{status}}',
      SEND_CASE_ERROR: 'Caso {{casoPrueba}}: {{reason}}',
      COLUMNS_DETECTED: 'Columnas detectadas ({{count}})',
      DOWNLOAD_XML: 'XML',
      PENDING_UPLOAD: 'Subir al portal',
      DOWNLOAD_ECF: 'Descargar ECF',
      DGII_ENDPOINT: 'Endpoint DGII',
      TAB_PRUEBAS_DATOS: 'Pruebas de Datos e-CF',
      TAB_APROBACION_COMERCIAL: 'Pruebas de Datos Aprobación Comercial',
      TAB_SIMULACION_ECF: 'Pruebas Simulación e-CF',
      TAB_SIMULACION_IMPRESA: 'Pruebas Simulación Representación Impresa',
      APPROVAL_SUBTITLE:
        'Sube el archivo XLSX de la DGII con los casos de prueba de aprobación comercial',
      APPROVAL_RNC_COMPRADOR: 'RNC Comprador',
      APPROVAL_RNC_EMISOR: 'RNC Emisor',
      APPROVAL_FECHA_EMISION: 'Fecha Emisión',
      APPROVAL_ESTADO: 'Estado',
      APPROVAL_ESTADO_APROBADO: 'Aprobado',
      APPROVAL_ESTADO_RECHAZADO: 'Rechazado',
      APPROVAL_MONTO_TOTAL: 'Monto Total',
      APPROVAL_ENCF: 'eNCF',
      APPROVAL_FECHA_HORA: 'Fecha/Hora Aprobación',
      APPROVAL_SUBMISSION_STATUS: 'Estado envío',
      LOG_TITLE: 'Log de Certificación',
      LOG_STEP_OBTENER_SEMILLA: 'Obtener semilla',
      LOG_STEP_FIRMA_SEMILLA: 'Firma de semilla',
      LOG_STEP_TOKEN_OBTENIDO: 'Token obtenido',
      LOG_STEP_FIRMA_ECF: 'Firma ECF',
      LOG_STEP_CARGA_XML: 'Carga XML',
      LOG_STEP_ENVIO_DGII: 'Envío a DGII',
      LOG_STEP_GENERATE_DATA: 'Generar datos',
      LOG_STEP_BUILD_XML: 'Construir XML',
      LOG_STEP_BUILD_AND_SIGN: 'Construir y firmar',
      LOG_STEP_SUBMIT_ECF: 'Enviar ECF',
      LOG_STEP_SUBMIT_FC: 'Enviar FC resumen',
      LOG_STEP_ERROR: 'Error',
      VIEW_LOG: 'Ver Log',
      TAB_POSTULACION: 'Postulación',
      POSTULACION_SUBTITLE:
        'Firma el archivo XML de postulación con tu certificado P12 para enviar a la DGII',
      POSTULACION_UPLOAD_XML: 'Archivo XML de postulación generado por DGII',
      POSTULACION_UPLOAD_XML_HINT:
        'Sube el archivo XML descargado del portal de la DGII',
      POSTULACION_SIGN: 'Firmar XML',
      POSTULACION_SIGNING: 'Firmando...',
      POSTULACION_DOWNLOAD: 'Descargar XML firmado',
      POSTULACION_SUCCESS: 'Postulación firmada correctamente',
      POSTULACION_ERROR: 'Error al firmar la postulación',
      POSTULACION_NO_XML: 'Selecciona un archivo XML de postulación',
      POSTULACION_NO_CERT: 'Se requiere certificado P12 y contraseña',
      POSTULACION_GUIDE_TITLE: 'Cómo realizar la postulación',
      POSTULACION_GUIDE_STEP1:
        'Ingresa al portal de certificación DGII con tu usuario y contraseña.',
      POSTULACION_GUIDE_STEP2:
        'Completa los datos del software con la información proporcionada abajo.',
      POSTULACION_GUIDE_STEP3:
        'Completa las URLs de recepción con los valores proporcionados abajo.',
      POSTULACION_GUIDE_STEP4:
        'Haz clic en "Generar archivo" en el portal DGII para descargar el XML de postulación.',
      POSTULACION_GUIDE_STEP5:
        'Sube el archivo XML descargado en la sección "Envío de archivo de postulación firmado" más abajo para firmarlo.',
      POSTULACION_GUIDE_STEP6:
        'Descarga el XML firmado y súbelo en el portal DGII haciendo clic en "Enviar Archivo".',
      POSTULACION_GUIDE_SOFTWARE_TITLE: 'Datos del software a utilizar',
      POSTULACION_GUIDE_SOFTWARE_TYPE: 'Tipo de software',
      POSTULACION_GUIDE_SOFTWARE_NAME: 'Nombre del software',
      POSTULACION_GUIDE_SOFTWARE_VERSION: 'Versión del software',
      POSTULACION_GUIDE_URL_TITLE: 'URLs de recepción',
      POSTULACION_GUIDE_URL_RECEPTION: 'URL de recepción',
      POSTULACION_GUIDE_URL_APPROVAL: 'URL de aprobación comercial',
      POSTULACION_GUIDE_URL_AUTH: 'URL de autenticación',
      POSTULACION_GUIDE_COPIED: 'Copiado al portapapeles',
      POSTULACION_GUIDE_PROVIDER_TITLE: 'Datos del proveedor',
      POSTULACION_GUIDE_PROVIDER_RNC: 'RNC/Cédula',
      POSTULACION_GUIDE_PROVIDER_LEGAL_NAME: 'Nombre/Razón social',
      POSTULACION_GUIDE_PROVIDER_COMMERCIAL_NAME: 'Nombre comercial',
      POSTULACION_GUIDE_GENERATE_FILE:
        'Una vez completados los datos, haz clic en el botón "Generar Archivo" para descargar el XML de postulación.',
      POSTULACION_GUIDE_SHOW_STEPS: 'Ver pasos en el portal',
      POSTULACION_GUIDE_SHOW_DOWNLOAD: 'Ver cómo descargar el XML',
      // Data Tests Guide
      DATA_TESTS_GUIDE_TITLE: '¿Cómo obtener el set de datos?',
      DATA_TESTS_GUIDE_STEP1:
        'Descarga el set de datos haciendo click en el botón "Descargar Comprobantes" en el portal de certificación DGII.',
      DATA_TESTS_GUIDE_STEP2:
        'Una vez descargado, selecciona el archivo y súbelo. Luego haz click en "Analizar" para verificar los datos antes de ejecutar.',
      DATA_TESTS_GUIDE_STEP3:
        'Podrás validar todos los registros en un solo click (recomendado) o hacerlo uno a uno.',
      DATA_TESTS_GUIDE_STEP4:
        'Luego de validar y enviar todos los datos, DGII debe actualizar el conteo de comprobantes aceptados y resúmenes de consumos aceptados.',
      DATA_TESTS_GUIDE_STEP5:
        'Luego deberás descargar los 4 XML del listado en el apartado Resumen RFCE y subirlos a la DGII.',
      DATA_TESTS_GUIDE_STEP6:
        'A medida que subas cada XML, DGII actualizará el conteo de archivos aceptados, y una vez completado te permitirá pasar al siguiente paso.',
      HINT_DATA_TESTS_GUIDE_STEP7:
        'Nota: Si DGII rechaza algún XML o numeración, deberás descargar el XML de rechazo y subirlo nuevamente al portal para que sea aceptado. Si una de las facturas es rechazada, para reiniciar el proceso, haz click en una factura previamente enviada, DGII reiniciará el conteo de facturas aceptadas y deberás subir nuevamente todos los XML.',
      DATA_TESTS_GUIDE_SHOW_PORTAL:
        'Ver cómo se ve el portal luego de aceptadas las facturas',
      DATA_TESTS_GUIDE_SHOW_UPLOAD: 'Ver dónde subir los archivos',
      // Approval Guide
      APPROVAL_GUIDE_TITLE: '¿Cómo obtener las aprobaciones comerciales?',
      APPROVAL_GUIDE_STEP1:
        'Descarga el set de datos haciendo click en el botón "Descargar Aprobaciones Comerciales" dentro del set de datos a utilizar.',
      APPROVAL_GUIDE_STEP2:
        'Una vez descargado, selecciona el archivo y súbelo. Luego haz click en "Analizar XLSX" para verificar los datos antes de ejecutar.',
      APPROVAL_GUIDE_STEP3:
        'Podrás validar todos los registros en un solo click (recomendado) o hacerlo uno a uno.',
      // Simulation Guide
      SIMULATION_GUIDE_TITLE: '¿Cómo completar la simulación?',
      SIMULATION_GUIDE_STEP1:
        'Los datos ya se encuentran cargados de forma automática.',
      SIMULATION_GUIDE_STEP2:
        'Podrás hacer click en los botones de "Enviar Etapa" de cada sección y DGII irá actualizando cada sección según la respuesta.',
      SIMULATION_GUIDE_STEP3:
        'Una vez completadas todas las etapas, deberás descargar los 4 archivos XML del apartado Tercero — Resumen Factura de Consumo < RD$250,000.00.',
      SIMULATION_GUIDE_STEP4:
        'Sube los archivos a la DGII, una vez completada esta etapa haz click en siguiente.',
      SIMULATION_GUIDE_SHOW_PORTAL: 'Ver cómo DGII actualiza los estados',
      SIMULATION_GUIDE_SHOW_UPLOAD: 'Ver cómo subir los XML a DGII',
      // Simulation (Step 4)
      SIMULATION_SUBTITLE:
        'Genera y envía e-CF con datos aleatorios para completar la certificación DGII',
      SIMULATION_PROGRESS: 'Progreso de Simulación',
      SIMULATION_REQUIRED: 'Requeridos',
      SIMULATION_ACCEPTED: 'Aceptados',
      SIMULATION_SUBMIT_ONE: 'Enviar',
      SIMULATION_SUBMIT_STAGE: 'Enviar Etapa',
      SIMULATION_STAGE_LOCKED: 'Completa la etapa anterior',
      SIMULATION_SUBMITTING: 'Enviando...',
      SIMULATION_SUCCESS: 'ECF de simulación enviado exitosamente',
      SIMULATION_ERROR: 'Error al enviar ECF de simulación',
      SIMULATION_STAGE_COMPLETE:
        'Etapa {{stage}} completada: {{accepted}}/{{total}} aceptados',
      SIMULATION_ALL_COMPLETE: 'Todas las pruebas de simulación completadas',
      SIMULATION_FECHA_VENC: 'Fecha Venc. Secuencia (de autorización DGII)',
      // Printed Representation (Step 5)
      PRINTED_REP_TITLE: 'Representación Impresa',
      PRINTED_REP_SUBTITLE:
        'Genere las representaciones impresas de los e-CF enviados en la simulación. Cada documento incluye código QR, código de seguridad y fecha de firma digital.',
      PRINTED_REP_NO_RESULTS:
        'No hay resultados de simulación. Complete primero las pruebas de simulación e-CF.',
      PRINTED_REP_PRINT: 'Imprimir',
      PRINTED_REP_PRINT_ALL: 'Imprimir Todos',
      PRINTED_REP_REFRESH: 'Actualizar Resultados',
      PRINTED_REP_REFRESH_SUCCESS: 'Documentos actualizados correctamente',
      PRINTED_REP_SECURITY_CODE: 'Código de Seguridad',
      PRINTED_REP_SIGNING_DATE: 'Fecha Firma Digital',
      PRINTED_REP_BUYER: 'Comprador',
      PRINTED_REP_AMOUNT: 'Monto Total',
      PRINTED_REP_QR_VERIFICATION: 'Verificación e-CF DGII',
      PRINTED_REP_DIGITAL_SIGNATURE: 'Firma Digital',
      PRINTED_REP_DOCUMENT_TYPE: 'Tipo Documento',
      PRINTED_REP_ENCF: 'e-NCF',
      PRINTED_REP_ISSUER: 'Emisor',
      // Printed Rep Guide
      PRINTED_REP_GUIDE_TITLE: '¿Cómo completar la representación impresa?',
      PRINTED_REP_GUIDE_STEP1:
        'Debes descargar todos los archivos listados en este apartado. Cada archivo está representado por su tipo (E33, E34, E32) y monto según el tipo de archivo que requiere DGII.',
      PRINTED_REP_GUIDE_STEP2:
        'Una vez subida cada prueba de representación impresa a la DGII, la DGII mostrará un mensaje indicando que estará sometiendo a aprobación estos archivos. Este proceso suele tardar de 24 a 48 horas.',
      PRINTED_REP_GUIDE_STEP3:
        'Una vez DGII emite la aprobación, deberás indicarlo haciendo click en el botón "DGII aprobó la Representación Impresa". Esto habilitará el botón siguiente.',
      PRINTED_REP_DGII_APPROVED: 'DGII aprobó la Representación Impresa',
      DOWNLOAD_REP_PRINT_ALL: 'Descargar todas las Representaciones Impresas',
    },
    DEVICES: {
      REGISTRATION_KEYS: {
        NAV: 'Llaves de Registro',
        TITLE: 'Llaves de registro de terminales',
        SUBTITLE:
          'Cree códigos de un solo uso para enrolar terminales NeoPOS. Solo se muestra el código al momento de emitirlo — no se vuelve a mostrar nunca.',
        ISSUE_BUTTON: 'Generar llave',
        ISSUE_TITLE: 'Generar nueva llave de registro',
        ISSUE_SUBTITLE:
          'Esta llave permitirá enrolar un terminal NeoPOS. El operador debe ingresarla durante la primera ejecución.',
        LABEL_FIELD: 'Etiqueta',
        LABEL_PLACEHOLDER: 'Ej. Caja 3 — Sucursal Bonao',
        BRANCH_FIELD: 'Sucursal',
        BRANCH_PLACEHOLDER_ANY: 'Cualquier sucursal',
        EXPIRES_AT_FIELD: 'Expira',
        EXPIRES_AT_HINT: 'Dejar vacío para que no expire',
        MAX_USES_FIELD: 'Usos permitidos',
        MAX_USES_HINT: 'Recomendado: 1 (uso único). Máximo: 50.',
        MODE_FIELD: 'Modo del terminal',
        MODE_HINT:
          'Modo solo offline: el terminal solo podrá conectarse para activación, latido, anuncios y plan. Útil para cajas que no deben sincronizar facturas (ej. caja secundaria).',
        MODE_FULL: 'Sincronización completa',
        MODE_OFFLINE_ONLY: 'Modo solo offline',
        COL_MODE: 'Modo',
        NOTES_FIELD: 'Notas internas',
        NOTES_PLACEHOLDER: 'Notas para sadmin (no visibles al operador)',
        CONFIRM_ISSUE_BUTTON: 'Generar',
        ONE_TIME_REVEAL_TITLE: 'Llave generada — copie ahora',
        ONE_TIME_REVEAL_DESC:
          'Esta es la única vez que verá el código completo. Guárdelo o cópielo antes de cerrar este diálogo. El servidor no lo almacena en claro.',
        COPY_CODE: 'Copiar código',
        CODE_COPIED: 'Código copiado al portapapeles',
        CLOSE_REVEAL: 'Cerrar',
        ACKNOWLEDGE_REVEAL: 'Entendido — ya guardé el código',
        COL_LABEL: 'Etiqueta',
        COL_PREFIX: 'Prefijo',
        COL_USES: 'Usos',
        COL_EXPIRES: 'Expira',
        COL_STATUS: 'Estado',
        COL_ISSUED_AT: 'Generada',
        COL_ACTIONS: 'Acciones',
        STATUS_LIVE: 'Activa',
        STATUS_REVOKED: 'Revocada',
        STATUS_EXPIRED: 'Expirada',
        STATUS_CONSUMED: 'Consumida',
        REVOKE_ACTION: 'Revocar',
        REVOKE_CONFIRM_TITLE: '¿Revocar llave?',
        REVOKE_CONFIRM_DESC:
          'La llave dejará de funcionar inmediatamente. Esta acción no se puede deshacer.',
        REVOKE_REASON_FIELD: 'Motivo (opcional)',
        REVOKE_REASON_PLACEHOLDER: 'Ej. Boleta extraviada',
        DELETE_ACTION: 'Eliminar',
        DELETE_CONFIRM_TITLE: '¿Eliminar llave por completo?',
        DELETE_CONFIRM_DESC:
          'La llave será borrada de la base de datos. Esta acción no se puede deshacer. El terminal enrolado con esta llave NO se ve afectado — para detener el terminal usa la revocación de fleet.',
        TOAST_ISSUED: 'Llave generada',
        TOAST_REVOKED: 'Llave revocada',
        TOAST_DELETED: 'Llave eliminada',
        TOAST_ISSUE_FAILED: 'No se pudo generar la llave',
        TOAST_REVOKE_FAILED: 'No se pudo revocar la llave',
        TOAST_DELETE_FAILED: 'No se pudo eliminar la llave',
        EMPTY_TITLE: 'Aún no hay llaves emitidas',
        EMPTY_SUBTITLE:
          'Use el botón "Generar llave" para emitir un código de enrolamiento.',
        INCLUDE_REVOKED_FILTER: 'Incluir revocadas',
        INCLUDE_CONSUMED_FILTER: 'Incluir consumidas',
        WEB_ONLY_BANNER:
          'Este módulo solo está disponible desde el navegador web. Los terminales NeoPOS no muestran esta pantalla, ni siquiera con conexión.',
        BUSINESS_FIELD: 'Negocio',
        BUSINESS_FIELD_PLACEHOLDER: 'Seleccione el negocio',
        BUSINESS_FIELD_HINT:
          'El código solo podrá ser usado por un terminal que inicie sesión con una cuenta de este negocio.',
        BUSINESS_FILTER: 'Negocio',
        BUSINESS_FILTER_ALL: 'Todos los negocios',
        BRANCH_FIELD_HINT:
          'Opcional. Cuando se especifica, el código solo permite enrolar terminales para esta sucursal.',
        EDIT_ACTION: 'Editar',
        EDIT_TITLE: 'Editar llave de registro',
        EDIT_SUBTITLE:
          'Ajuste la expiración y los usos permitidos. El código no cambia.',
        EDIT_SAVE_BUTTON: 'Guardar cambios',
        TOAST_EDITED: 'Llave actualizada',
        TOAST_EDIT_FAILED: 'No se pudo actualizar la llave',
        CLEAR_EXPIRY: 'Quitar expiración',
      },
      FLEET: {
        NAV: 'Terminales Enroladas',
        TITLE: 'Flota de terminales NeoPOS',
        SUBTITLE:
          'Vea cada terminal enrolada, revoque accesos comprometidos y revise el historial de sincronización.',
        COL_LABEL: 'Etiqueta',
        COL_PLATFORM: 'Plataforma',
        COL_BRANCH: 'Sucursal',
        COL_STATUS: 'Estado',
        COL_LAST_SEEN: 'Última conexión',
        COL_VERSION: 'Versión',
        COL_ACTIONS: 'Acciones',
        STATUS_ACTIVE: 'Activa',
        STATUS_SUSPENDED: 'Suspendida',
        STATUS_REVOKED: 'Revocada',
        STATUS_LOST: 'Extraviada',
        STATUS_DUPLICATE: 'Duplicada',
        REVOKE_ACTION: 'Revocar',
        REVOKE_CONFIRM_TITLE: '¿Revocar este terminal?',
        REVOKE_CONFIRM_DESC:
          'El terminal dejará de funcionar inmediatamente. Para volver a usarlo deberá enrolarse con un nuevo código.',
        REVOKE_REASON_FIELD: 'Motivo (opcional)',
        REVOKE_REASON_PLACEHOLDER: 'Ej. Robo, falla de hardware, baja',
        EVENTS_DRAWER_TITLE: 'Historial de sincronización',
        EVENTS_EMPTY: 'Sin eventos recientes',
        TOAST_REVOKED: 'Terminal revocada',
        TOAST_REVOKE_FAILED: 'No se pudo revocar el terminal',
        FILTER_STATUS_ALL: 'Todos los estados',
        EMPTY_TITLE: 'No hay terminales enroladas',
        EMPTY_SUBTITLE:
          'Genere una llave de registro para enrolar la primera terminal.',
        VIEW_EVENTS: 'Ver historial',
        NEVER_SEEN: 'Sin conexiones registradas',
      },
    },
    BUSINESS_FEATURES: {
      NAV: 'Módulos y cortesías',
      TITLE: 'Módulos y cortesías',
      SUBTITLE:
        'Gestiona qué módulos están disponibles para cada negocio (gate) y otorga cortesías / overrides.',
      TAB_FEATURES: 'Módulos (gate)',
      TAB_GRANTS: 'Cortesías y overrides',
      TAB_ADJUST_PLAN: 'Ajustar plan',
      ADJUST_HEADING: 'Ajustar el plan del negocio',
      ADJUST_EMPTY:
        'Agrega uno o más cambios (módulo, addon o plantilla de plan) para previsualizar y aplicar.',
      ADJUST_KIND: 'Tipo',
      ADJUST_ACTION: 'Acción',
      ADJUST_CODE: 'Código catálogo',
      ADJUST_QTY: 'Cantidad',
      ADJUST_ADD_ROW: 'Agregar cambio',
      ADJUST_REASON: 'Razón del ajuste',
      ADJUST_REASON_HINT:
        'Motivo o referencia del ajuste (visible en el timeline del cliente).',
      ADJUST_PREVIEW_CTA: 'Vista previa',
      ADJUST_APPLY_CTA: 'Aplicar ajuste',
      ADJUST_PREVIEW_HEADING: 'Vista previa del ajuste',
      ADJUST_PREVIEW_CURRENT: 'Items actuales',
      ADJUST_PREVIEW_PROPOSED: 'Cambios propuestos',
      ADJUST_APPLIED: 'Ajuste aplicado correctamente',
      ADJUST_CODE_PLACEHOLDER: 'Selecciona un ítem del catálogo',
      ADJUST_REMOVE_ROW: 'Eliminar',
      ADJUST_DIFF_ITEM: 'Ítem',
      ADJUST_DIFF_CURRENT: 'Actual',
      ADJUST_DIFF_NEXT: 'Nuevo',
      ADJUST_DIFF_PRICE: 'Precio mensual',
      SELECT_BUSINESS_FIRST: 'Selecciona un negocio para ajustar su plan.',
      BUSINESS_PICKER_LABEL: 'Negocio',
      BUSINESS_PICKER_PLACEHOLDER: 'Seleccione un negocio',
      GRANT_SECTION_TITLE: 'Conceder módulo',
      GRANT_MODULE_LABEL: 'Módulo',
      GRANT_MODULE_PLACEHOLDER: 'Seleccione un módulo',
      GRANT_NOTES_LABEL: 'Notas',
      GRANT_NOTES_PLACEHOLDER: 'Motivo o referencia (opcional)',
      GRANT_BUTTON: 'Conceder',
      ACTIVE_GRANTS_TITLE: 'Módulos activos',
      COL_MODULE: 'Módulo',
      COL_GRANTED_AT: 'Otorgado',
      COL_GRANTED_BY: 'Por',
      COL_NOTES: 'Notas',
      COL_ACTIONS: 'Acciones',
      REVOKE_ACTION: 'Revocar',
      REVOKE_CONFIRM_TITLE: '¿Revocar módulo?',
      REVOKE_CONFIRM_DESC:
        'Los terminales perderán acceso a este módulo en el próximo heartbeat.',
      REVOKE_REASON_LABEL: 'Motivo (opcional)',
      REVOKE_REASON_PLACEHOLDER: 'Por qué se revoca este módulo',
      PERMISSIONS_COUNT_HINT: '{{count}} permisos',
      EMPTY_TITLE: 'Sin módulos otorgados',
      EMPTY_SUBTITLE:
        'Use el formulario arriba para conceder el primer módulo a este negocio.',
      TOAST_GRANTED: 'Módulo otorgado',
      TOAST_REVOKED: 'Módulo revocado',
      TOAST_GRANT_FAILED: 'No se pudo otorgar el módulo',
      TOAST_REVOKE_FAILED: 'No se pudo revocar el módulo',
      TOAST_GRANTED_ALL: 'Todos los módulos otorgados',
      GRANT_ALL_BUTTON: 'Otorgar todos',
      GRANT_ALL_HINT:
        'Otorga al negocio todos los módulos del catálogo que aún no tiene.',
      CLEAR_ALL_BUTTON: 'Revocar todos',
      CATALOG_HEADING: 'Catálogo de módulos',
      CATALOG_HINT:
        'Marque los módulos que el negocio tiene acceso. Desmarcar revoca el módulo en el próximo heartbeat.',
      RAW_CODE_LABEL: 'Código',
      BRANCH_PICKER_LABEL: 'Alcance',
      BRANCH_PICKER_BUSINESS_WIDE: 'Todas las sucursales (negocio completo)',
      BRANCH_PICKER_BUSINESS_HINT:
        'Los módulos marcados aplican a todas las sucursales del negocio.',
      BRANCH_PICKER_BRANCH_HINT:
        'Los módulos marcados aplican solo a esta sucursal, sumándose a los módulos del negocio.',
      EXPAND_FEATURE: 'Mostrar sub-funcionalidades',
      COLLAPSE_FEATURE: 'Ocultar sub-funcionalidades',
      CHILDREN_COUNT_HINT:
        '{{granted}} de {{total}} sub-funcionalidades activas',
      GRANT_ALL_CHILDREN: 'Activar todas',
      REVOKE_ALL_CHILDREN: 'Desactivar todas',
      TAB_PERMISSIONS: 'Módulos y permisos',
      PERMISSIONS_OVERRIDE_HINT:
        'Activa o desactiva módulos (gate) y permisos individuales para este negocio. Los cambios aparecen inmediatamente en la asignación de roles.',
      PERMISSION_REVOKED_BADGE: 'Desactivado',
      PARTIAL_LABEL: 'Parcial',
      MODULE_NOT_GRANTED: 'Módulo no otorgado',
      ACTIVATE_ALL_BUTTON: 'Activar todo',
      DEACTIVATE_ALL_BUTTON: 'Desactivar todo',
      ACTIVATE_ALL_CONFIRM_TITLE: 'Activar todos los módulos y permisos',
      ACTIVATE_ALL_CONFIRM_DESC:
        'Otorga cada módulo del catálogo a este negocio y limpia todas las anulaciones. ¿Continuar?',
      DEACTIVATE_ALL_CONFIRM_TITLE: 'Desactivar todos los módulos',
      DEACTIVATE_ALL_CONFIRM_DESC:
        'Revoca todos los módulos otorgados. El negocio no podrá usar ninguna función hasta que se le vuelva a otorgar acceso. ¿Continuar?',
    },
    FIN_KPI_PENDING: 'Pendientes',
    FIN_KPI_PENDING_HINT: 'Aún sin procesar por el Posting Processor',
    FIN_KPI_POSTED: 'Registrados',
    FIN_KPI_POSTED_HINT: 'Convertidos en asiento contable',
    FIN_KPI_FAILED: 'Fallidos',
    FIN_KPI_FAILED_HINT: 'Requieren intervención',
    FIN_KPI_THROUGHPUT: 'Volumen 24 h',
    FIN_KPI_THROUGHPUT_HINT: 'Eventos creados en últimas 24 h',
    FIN_KPI_OLDEST: 'Más antiguo pendiente',
    FIN_KPI_OLDEST_HINT: 'Edad del pendiente más antiguo',
    SETTINGS_TITLE: 'Configuración de la plataforma',
    SETTINGS_SUBTITLE:
      'Ajustes globales controlados por superadministradores. Cambian el comportamiento de la plataforma sin desplegar código.',
    SETTINGS_NAV: 'Configuración',
    SETTINGS_SECTION_ECF: 'e-CF (DGII)',
    SETTINGS_ECF_SEQUENCE_EXPIRY_LABEL:
      'Fecha de vencimiento de secuencia NCF (certificación)',
    SETTINGS_ECF_SEQUENCE_EXPIRY_HELP:
      'Formato DD-MM-YYYY. Usado por el asistente de certificación e-CF como valor por defecto de FechaVencimientoSecuencia. Actualízalo cuando DGII emita un nuevo período de certificación.',
    SETTINGS_ITBIS_HINT:
      'Tasas ITBIS DGII. Fuente única de verdad para toda la plataforma. Al guardar, la nueva tasa se aplica automáticamente a TODOS los negocios (existentes y nuevos) — las próximas facturas y submisiones e-CF usarán el valor actualizado. Las facturas históricas mantienen su tasa original.',
    SETTINGS_ITBIS_RATE_LABEL: 'Tasa ITBIS general (ITBIS_18)',
    SETTINGS_ITBIS_RATE_HELP:
      'Tasa ITBIS canónica DGII. Cambiar aquí actualiza el rate en la fila ITBIS_18 de cada negocio (existente y nuevo) inmediatamente.',
    SETTINGS_ITBIS_REDUCED_RATE_LABEL: 'Tasa ITBIS reducida (ITBIS_16)',
    SETTINGS_ITBIS_REDUCED_RATE_HELP:
      'Tasa ITBIS reducida DGII. Cambiar aquí actualiza el rate en la fila ITBIS_16 de cada negocio (existente y nuevo) inmediatamente.',
    SETTINGS_BILLING_GRACE_HINT:
      'Ventana de gracia global aplicada a la fecha de vencimiento de cada factura de suscripción recurrente. Se lee al emitir la factura; no modifica facturas ya abiertas.',
    SETTINGS_BILLING_GRACE_LABEL: 'Días de gracia para facturas de suscripción',
    SETTINGS_BILLING_GRACE_HELP:
      'Rango 0 a 30. 0 = vence el mismo día de emisión (sin gracia). 3 = por defecto. 30 = un ciclo completo de gracia.',
    SETTINGS_SAVE_SUCCESS: 'Configuración actualizada',
    SETTINGS_SAVE_ERROR: 'No se pudo actualizar la configuración',
    SETTINGS_VALIDATION_FORMAT: 'Formato requerido: DD-MM-YYYY',
  },
  PLAN_TEMPLATE: {
    NAV: 'Catálogo de planes',
    TITLE: 'Catálogo de planes',
    SUBTITLE:
      'Planes vendibles al cliente. Componen desde el catálogo (producto + paquete base + extras). Reemplaza el plan-features hardcodeado.',
    NEW_PLAN: 'Nuevo plan',
    EDIT_PLAN: 'Editar plan',
    DELETE_PLAN: 'Eliminar plan',
    DELETE_CONFIRM_TITLE: '¿Eliminar plan?',
    DELETE_CONFIRM_DESC:
      'El plan se ocultará. La eliminación es lógica y reversible.',
    SEARCH_PLACEHOLDER: 'Buscar por código, nombre o eslogan…',
    FILTER_CATEGORY: 'Categoría',
    FILTER_PUBLIC: 'Público',
    FILTER_FEATURED: 'Destacado',
    FILTER_SELLABLE: 'Vendible',
    FILTER_ACTIVE: 'Activo',
    FILTER_ALL: 'Todos',
    COL_CODE: 'Código',
    COL_NAME: 'Nombre',
    COL_CATEGORY: 'Categoría',
    COL_BUNDLE: 'Paquete base',
    COL_TRIAL_DAYS: 'Días de prueba',
    COL_PUBLIC: 'Público',
    COL_FEATURED: 'Destacado',
    COL_SELLABLE: 'Vendible',
    COL_ACTIVE: 'Activo',
    COL_SORT_ORDER: 'Orden',
    COL_ACTIONS: 'Acciones',
    EMPTY_TITLE: 'Sin planes en el catálogo',
    EMPTY_SUBTITLE: 'Crea el primer plan para empezar a vender.',
    CATEGORY_BASIC: 'Básico',
    CATEGORY_PRO: 'Profesional',
    CATEGORY_ENTERPRISE: 'Empresarial',
    CATEGORY_CUSTOM: 'Personalizado',
    FIELD_CODE: 'Código',
    FIELD_CODE_HINT:
      'Identificador globalmente único (ej. profesional). Inmutable después de crear.',
    FIELD_NAME: 'Nombre',
    FIELD_TAGLINE: 'Eslogan',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_ICON: 'Ícono (Lucide)',
    FIELD_CATEGORY: 'Categoría',
    FIELD_BUNDLE: 'Paquete base',
    FIELD_BUNDLE_HINT:
      'Opcional. Si seleccionas un paquete, el plan hereda sus elementos como base y los extras se agregan encima.',
    FIELD_BUNDLE_NONE: 'Sin paquete (compone desde cero)',
    FIELD_TRIAL_DAYS: 'Días de prueba',
    FIELD_TRIAL_DAYS_HINT:
      'Cantidad de días que el cliente puede usar el plan gratis antes de cobrar.',
    FIELD_IS_PUBLIC: 'Público',
    FIELD_IS_PUBLIC_HINT:
      'Si está activo, el plan se muestra en el landing. Desactiva para planes grandfathered / legacy.',
    FIELD_IS_FEATURED: 'Destacado',
    FIELD_IS_SELLABLE: 'Vendible',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_SORT_ORDER: 'Orden de presentación',
    YES: 'Sí',
    NO: 'No',
    TOAST_CREATED: 'Plan creado',
    TOAST_UPDATED: 'Plan actualizado',
    TOAST_DELETED: 'Plan eliminado',
    TOAST_CREATE_FAILED: 'No se pudo crear el plan',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el plan',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el plan',
    CODE_IMMUTABLE_HINT:
      'El código no se puede editar después de crear el plan.',
    VIEW_ITEMS_ACTION: 'Ver contenido',
    ITEMS_TITLE: 'Contenido del plan',
    ITEMS_SUBTITLE:
      'Elementos adicionales del plan (encima del paquete base, si lo hay).',
    BREADCRUMB_BACK: 'Volver a planes',
    ADD_ITEM: 'Agregar elemento',
    REMOVE_ITEM: 'Quitar elemento',
    REMOVE_ITEM_CONFIRM_TITLE: '¿Quitar elemento del plan?',
    REMOVE_ITEM_CONFIRM_DESC: 'El elemento se quitará de este plan.',
    ITEMS_EMPTY_TITLE: 'Plan sin contenido extra',
    ITEMS_EMPTY_SUBTITLE: 'Agrega elementos para complementar el paquete base.',
    COL_ITEM_KIND: 'Tipo',
    COL_ITEM_CODE: 'Código',
    COL_QUANTITY: 'Cantidad',
    COL_INCLUDED: 'Incluido',
    ITEM_KIND_PRODUCT: 'Producto',
    ITEM_KIND_MODULE: 'Módulo',
    ITEM_KIND_FEATURE: 'Funcionalidad',
    ITEM_KIND_ADDON: 'Add-on',
    ITEM_KIND_LIMIT: 'Límite',
    ITEM_KIND_QUOTA: 'Cuota',
    ITEM_KIND_BUNDLE: 'Bundle',
    FILTER_ALL_KINDS: 'Todos los tipos',
    FILTER_BY_KIND: 'Filtrar por tipo',
    FIELD_ITEM_KIND: 'Tipo',
    FIELD_ITEM: 'Elemento',
    FIELD_QUANTITY: 'Cantidad',
    FIELD_IS_INCLUDED: 'Incluido por defecto',
    FIELD_IS_INCLUDED_HINT:
      'Si está activo, el elemento se incluye en el plan automáticamente. Si está desactivado, aparece como upsell opcional al suscribirse.',
    TOAST_ITEM_ADDED: 'Elemento agregado',
    TOAST_ITEM_REMOVED: 'Elemento quitado',
    TOAST_ITEM_REORDERED: 'Orden actualizado',
    TOAST_ITEM_ADD_FAILED: 'No se pudo agregar el elemento',
    TOAST_ITEM_REMOVE_FAILED: 'No se pudo quitar el elemento',
    TOAST_ITEM_REORDER_FAILED: 'No se pudo actualizar el orden',
    MOVE_UP: 'Subir',
    MOVE_DOWN: 'Bajar',
  },
  BILLING: {
    TITLE: 'Facturación',
    SUBTITLE: 'Gestiona la suscripción y los pagos de tu negocio.',
    CURRENT_PLAN_TITLE: 'Plan actual',
    NO_SUBSCRIPTION:
      'Aún no tienes una suscripción activa. Elige un plan para comenzar.',
    PERIOD_ENDS: 'Periodo actual termina el {{date}}',
    CANCEL_SCHEDULED:
      'Cancelación programada al final del periodo actual. Reactivar antes para mantener el servicio.',
    CHANGE_PLAN_TITLE: 'Cambiar de plan',
    START_SUBSCRIPTION_TITLE: 'Comenzar suscripción',
    SELECT_PLAN_PLACEHOLDER: 'Selecciona un plan…',
    ACTION_CHANGE_PLAN: 'Cambiar plan',
    ACTION_CHECKOUT: 'Suscribirme',
    ACTION_CANCEL: 'Cancelar suscripción',
    ACTION_RESUME: 'Reactivar',
    PAYMENTS_TITLE: 'Historial de pagos',
    PAYMENTS_EMPTY: 'Aún no hay pagos registrados.',
    COL_DATE: 'Fecha',
    COL_AMOUNT: 'Monto',
    COL_STATUS: 'Estado',
    COL_INVOICE: 'Factura',
    PAYMENT_PAID: 'Pagado',
    PAYMENT_FAILED: 'Fallido',
    PAYMENT_REFUNDED: 'Reembolsado',
    VIEW_RECEIPT: 'Ver recibo',
    STATUS_TRIALING: 'En prueba',
    STATUS_ACTIVE: 'Activa',
    STATUS_PAST_DUE: 'Pago pendiente',
    STATUS_PAUSED: 'Pausada',
    STATUS_CANCELLED: 'Cancelada',
    INTERVAL_MONTHLY: 'Mensual',
    INTERVAL_ANNUAL: 'Anual',
    CONFIRM_CANCEL_TITLE: '¿Cancelar suscripción?',
    CONFIRM_CANCEL_DESC:
      'La cancelación se aplicará al final del periodo. Puedes reactivar antes para mantener el servicio.',
    TOAST_CHECKOUT_FAILED: 'No se pudo iniciar el proceso de pago.',
    TOAST_CHANGED: 'Plan actualizado.',
    TOAST_CHANGE_FAILED: 'No se pudo cambiar el plan.',
    TOAST_CANCELLED: 'Cancelación programada.',
    TOAST_CANCEL_FAILED: 'No se pudo cancelar.',
    TOAST_RESUMED: 'Suscripción reactivada.',
    TOAST_RESUME_FAILED: 'No se pudo reactivar.',
    ADDONS_TITLE: 'Complementos',
    ADDONS_EMPTY: 'No hay complementos disponibles en este momento.',
    ADDON_OWNED: 'Activo',
    ADDON_BUY: 'Comprar',
  },
  USAGE: {
    TITLE: 'Uso por negocio',
    SUBTITLE:
      'Consulta eventos de uso medidos por negocio + agregados del periodo actual.',
    SELECT_BUSINESS: 'Selecciona un negocio…',
    PICK_BUSINESS_HINT: 'Elige un negocio para ver su uso.',
    FILTER_METRIC_PLACEHOLDER: 'Filtrar por métrica (ej. invoices)…',
    AGGREGATE_TITLE: 'Agregados del periodo actual',
    AGGREGATE_EMPTY: 'No hay eventos en el periodo seleccionado.',
    EVENTS_TITLE: 'Eventos recientes',
    EVENTS_EMPTY: 'No hay eventos registrados.',
    MANUAL_RECORD_HINT:
      'Para registrar eventos manuales (correcciones / backfills) usa la API.',
    COL_DATE: 'Fecha',
    COL_METRIC: 'Métrica',
    COL_QUANTITY: 'Cantidad',
    COL_SOURCE: 'Origen',
    COL_REF: 'Ref',
    COL_TOTAL: 'Total',
    COL_EVENT_COUNT: 'Eventos',
    COL_PERIOD: 'Periodo',
  },
  SUBSCRIPTION_CATALOG: {
    NAV: 'Suscripciones',
    TITLE: 'Suscripciones de catálogo',
    SUBTITLE:
      'Negocios suscritos al catálogo de planes. Gestiona el ciclo de vida (alta, cambio de plan, cancelación, reactivación).',
    EMPTY: 'No hay negocios suscritos al catálogo todavía.',
    COL_BUSINESS: 'Negocio',
    COL_PLAN: 'Plan',
    COL_STATUS: 'Estado',
    COL_INTERVAL: 'Periodo',
    COL_TRIAL_ENDS: 'Trial hasta',
    COL_PERIOD_ENDS: 'Próximo pago',
    COL_ACTIONS: 'Acciones',
    STATUS_TRIALING: 'En prueba',
    STATUS_ACTIVE: 'Activa',
    STATUS_PAST_DUE: 'Vencida',
    STATUS_PAUSED: 'Pausada',
    STATUS_CANCELLED: 'Cancelada',
    INTERVAL_MONTHLY: 'Mensual',
    INTERVAL_ANNUAL: 'Anual',
    FILTER_ALL: 'Todos',
    FILTER_STATUS: 'Estado',
    SEARCH_PLACEHOLDER: 'Buscar negocio o plan…',
    ACTION_SUBSCRIBE: 'Suscribir',
    ACTION_CHANGE_PLAN: 'Cambiar plan',
    ACTION_CANCEL_IMMEDIATE: 'Cancelar de inmediato',
    ACTION_CANCEL_AT_PERIOD_END: 'Cancelar al fin del periodo',
    ACTION_RESUME: 'Reactivar',
    MODAL_SUBSCRIBE_TITLE: 'Suscribir negocio al catálogo',
    MODAL_CHANGE_TITLE: 'Cambiar plan del negocio',
    MODAL_CANCEL_TITLE: '¿Cancelar suscripción?',
    MODAL_CANCEL_DESC:
      'Confirma cancelación. El negocio perderá acceso a las características del plan al final del periodo (o de inmediato si así lo eliges).',
    FIELD_BUSINESS: 'Negocio',
    FIELD_PLAN_TEMPLATE: 'Plan',
    FIELD_BILLING_INTERVAL: 'Periodo de facturación',
    FIELD_CURRENCY: 'Moneda',
    FIELD_TRIAL_DAYS: 'Días de prueba',
    FIELD_TRIAL_DAYS_HINT: 'Si se omite, se usa el valor por defecto del plan.',
    FIELD_NOTES: 'Notas internas',
    TOAST_SUBSCRIBED: 'Negocio suscrito.',
    TOAST_SUBSCRIBE_FAILED: 'No se pudo suscribir al negocio.',
    TOAST_CHANGED: 'Plan actualizado.',
    TOAST_CHANGE_FAILED: 'No se pudo cambiar el plan.',
    TOAST_CANCELLED: 'Suscripción cancelada.',
    TOAST_CANCEL_FAILED: 'No se pudo cancelar la suscripción.',
    TOAST_RESUMED: 'Suscripción reactivada.',
    TOAST_RESUME_FAILED: 'No se pudo reactivar la suscripción.',
    ACTION_VIEW_PAYMENTS: 'Ver pagos',
    MODAL_PAYMENTS_TITLE: 'Historial de pagos',
    PAYMENTS_EMPTY: 'No hay pagos registrados para este negocio.',
    COL_DATE: 'Fecha',
    COL_AMOUNT: 'Monto',
    COL_FAILURE_REASON: 'Motivo de fallo',
    COL_INVOICE: 'Factura',
    PAYMENT_STATUS_PAID: 'Pagado',
    PAYMENT_STATUS_FAILED: 'Fallido',
    PAYMENT_STATUS_REFUNDED: 'Reembolsado',
    PAYMENT_STATUS_PENDING: 'Pendiente',
    PAYMENT_FILTER_FROM: 'Desde',
    PAYMENT_FILTER_TO: 'Hasta',
    VIEW_RECEIPT: 'Ver recibo',
  },
  BUNDLE_CATALOG: {
    NAV: 'Catálogo de paquetes',
    TITLE: 'Catálogo de paquetes',
    SUBTITLE:
      'Paquetes vendibles que cruzan productos, módulos, funcionalidades, add-ons y límites. El landing muestra los paquetes destacados.',
    NEW_BUNDLE: 'Nuevo paquete',
    EDIT_BUNDLE: 'Editar paquete',
    DELETE_BUNDLE: 'Eliminar paquete',
    DELETE_CONFIRM_TITLE: '¿Eliminar paquete?',
    DELETE_CONFIRM_DESC:
      'El paquete se ocultará. La eliminación es lógica y reversible.',
    SEARCH_PLACEHOLDER: 'Buscar por código, nombre o eslogan…',
    FILTER_CATEGORY: 'Categoría',
    FILTER_FEATURED: 'Destacado',
    FILTER_SELLABLE: 'Vendible',
    FILTER_ACTIVE: 'Activo',
    FILTER_ALL: 'Todos',
    COL_CODE: 'Código',
    COL_NAME: 'Nombre',
    COL_CATEGORY: 'Categoría',
    COL_FEATURED: 'Destacado',
    COL_SELLABLE: 'Vendible',
    COL_ACTIVE: 'Activo',
    COL_SORT_ORDER: 'Orden',
    COL_ACTIONS: 'Acciones',
    EMPTY_TITLE: 'Sin paquetes en el catálogo',
    EMPTY_SUBTITLE: 'Crea el primer paquete para componer la oferta.',
    CATEGORY_STARTER: 'Inicial',
    CATEGORY_GROWTH: 'Crecimiento',
    CATEGORY_ENTERPRISE: 'Empresarial',
    CATEGORY_CUSTOM: 'Personalizado',
    FIELD_CODE: 'Código',
    FIELD_CODE_HINT:
      'Identificador globalmente único (ej. negocio). Inmutable después de crear.',
    FIELD_NAME: 'Nombre',
    FIELD_TAGLINE: 'Eslogan',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_ICON: 'Ícono (Lucide)',
    FIELD_CATEGORY: 'Categoría',
    FIELD_IS_FEATURED: 'Destacado',
    FIELD_IS_FEATURED_HINT:
      'Marca este paquete como popular para resaltarlo en el landing.',
    FIELD_IS_SELLABLE: 'Vendible',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_SORT_ORDER: 'Orden de presentación',
    YES: 'Sí',
    NO: 'No',
    TOAST_CREATED: 'Paquete creado',
    TOAST_UPDATED: 'Paquete actualizado',
    TOAST_DELETED: 'Paquete eliminado',
    TOAST_CREATE_FAILED: 'No se pudo crear el paquete',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el paquete',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el paquete',
    CODE_IMMUTABLE_HINT:
      'El código no se puede editar después de crear el paquete.',
    VIEW_ITEMS_ACTION: 'Ver contenido',
    ITEMS_TITLE: 'Contenido del paquete',
    ITEMS_SUBTITLE:
      'Productos, módulos, funcionalidades y add-ons que se incluyen al comprar este paquete.',
    BREADCRUMB_BACK: 'Volver a paquetes',
    ADD_ITEM: 'Agregar elemento',
    REMOVE_ITEM: 'Quitar elemento',
    REMOVE_ITEM_CONFIRM_TITLE: '¿Quitar elemento del paquete?',
    REMOVE_ITEM_CONFIRM_DESC: 'El elemento se quitará de este paquete.',
    ITEMS_EMPTY_TITLE: 'Paquete sin contenido',
    ITEMS_EMPTY_SUBTITLE: 'Agrega el primer elemento para empezar.',
    COL_ITEM_KIND: 'Tipo',
    COL_ITEM_CODE: 'Código',
    COL_QUANTITY: 'Cantidad',
    ITEM_KIND_PRODUCT: 'Producto',
    ITEM_KIND_MODULE: 'Módulo',
    ITEM_KIND_FEATURE: 'Funcionalidad',
    ITEM_KIND_ADDON: 'Add-on',
    ITEM_KIND_LIMIT: 'Límite',
    FIELD_ITEM_KIND: 'Tipo',
    FIELD_ITEM: 'Elemento',
    FIELD_QUANTITY: 'Cantidad',
    FIELD_QUANTITY_HINT:
      'Aplica a límites (ej. "5 usuarios extra") y add-ons; ignorada para producto / módulo / funcionalidad.',
    TOAST_ITEM_ADDED: 'Elemento agregado',
    TOAST_ITEM_REMOVED: 'Elemento quitado',
    TOAST_ITEM_REORDERED: 'Orden actualizado',
    TOAST_ITEM_ADD_FAILED: 'No se pudo agregar el elemento',
    TOAST_ITEM_REMOVE_FAILED: 'No se pudo quitar el elemento',
    TOAST_ITEM_REORDER_FAILED: 'No se pudo actualizar el orden',
    MOVE_UP: 'Subir',
    MOVE_DOWN: 'Bajar',
  },
  PRICING_CATALOG: {
    NAV: 'Catálogo de precios',
    TITLE: 'Catálogo de precios',
    SUBTITLE:
      'Precios por producto / módulo / funcionalidad / add-on. Múltiples monedas y períodos por entidad — solo uno marcado como predeterminado por combinación.',
    NEW_PRICE: 'Nuevo precio',
    EDIT_PRICE: 'Editar precio',
    DELETE_PRICE: 'Eliminar precio',
    DELETE_CONFIRM_TITLE: '¿Eliminar precio?',
    DELETE_CONFIRM_DESC:
      'El precio se ocultará. La eliminación es lógica y reversible.',
    SEARCH_PLACEHOLDER: 'Buscar por código de entidad…',
    FILTER_ENTITY_KIND: 'Tipo',
    FILTER_CURRENCY: 'Moneda',
    FILTER_PERIOD: 'Período',
    FILTER_DEFAULT: 'Predeterminado',
    FILTER_ACTIVE: 'Activo',
    FILTER_ALL: 'Todos',
    COL_KIND: 'Tipo',
    COL_ENTITY: 'Entidad',
    COL_CURRENCY: 'Moneda',
    COL_AMOUNT: 'Monto',
    COL_PERIOD: 'Período',
    COL_DEFAULT: 'Predet.',
    COL_ACTIVE: 'Activo',
    COL_EFFECTIVE: 'Vigencia',
    COL_TAG: 'Etiqueta',
    COL_ACTIONS: 'Acciones',
    EMPTY_TITLE: 'Sin precios',
    EMPTY_SUBTITLE: 'Crea el primer precio del catálogo.',
    KIND_PRODUCT: 'Producto',
    KIND_MODULE: 'Módulo',
    KIND_FEATURE: 'Funcionalidad',
    KIND_ADDON: 'Add-on',
    KIND_BUNDLE: 'Paquete',
    KIND_LIMIT: 'Límite',
    PERIOD_MONTHLY: 'Mensual',
    PERIOD_YEARLY: 'Anual',
    PERIOD_ONE_TIME: 'Pago único',
    FIELD_ENTITY_KIND: 'Tipo de entidad',
    FIELD_ENTITY: 'Entidad',
    FIELD_ENTITY_HINT: 'Selecciona la entidad a la que se conecta el precio.',
    FIELD_CURRENCY: 'Moneda',
    FIELD_AMOUNT: 'Monto',
    FIELD_PERIOD: 'Período',
    FIELD_IS_DEFAULT: 'Predeterminado',
    FIELD_IS_DEFAULT_HINT:
      'Solo un precio puede ser predeterminado por combinación de tipo + entidad + moneda + período. Activar este lo demota a los demás.',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_EFFECTIVE_FROM: 'Vigente desde',
    FIELD_EFFECTIVE_UNTIL: 'Vigente hasta',
    FIELD_TAG: 'Etiqueta',
    SECTION_PAYMENT_PROVIDERS: 'IDs de procesadores de pago',
    PAYMENT_PROVIDERS_HINT:
      'Se llenan automáticamente cuando el Billing Engine (PR15-F) sincroniza el precio con Stripe / PayPal.',
    FIELD_STRIPE_PRODUCT_ID: 'Stripe Product ID',
    FIELD_STRIPE_PRICE_ID: 'Stripe Price ID',
    FIELD_PAYPAL_PRODUCT_ID: 'PayPal Product ID',
    FIELD_PAYPAL_PLAN_ID: 'PayPal Plan ID',
    FIELD_SORT_ORDER: 'Orden',
    YES: 'Sí',
    NO: 'No',
    NO_EFFECTIVE_BOUND: 'sin límite',
    TOAST_CREATED: 'Precio creado',
    TOAST_UPDATED: 'Precio actualizado',
    TOAST_DELETED: 'Precio eliminado',
    TOAST_CREATE_FAILED: 'No se pudo crear el precio',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el precio',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el precio',
    PARENT_IMMUTABLE_HINT:
      'El tipo y la entidad no se pueden cambiar después de crear. Para reasignar, elimina y crea nuevo.',
  },
  ADDON_CATALOG: {
    NAV: 'Catálogo de add-ons',
    TITLE: 'Catálogo de add-ons',
    SUBTITLE:
      'Add-ons vendibles de la plataforma. Eje orgánico — se venden por separado y se conectan a uno o más productos.',
    NEW_ADDON: 'Nuevo add-on',
    EDIT_ADDON: 'Editar add-on',
    DELETE_ADDON: 'Eliminar add-on',
    DELETE_CONFIRM_TITLE: '¿Eliminar add-on?',
    DELETE_CONFIRM_DESC:
      'El add-on se ocultará del catálogo. La eliminación es lógica y reversible.',
    SEARCH_PLACEHOLDER: 'Buscar por código, nombre o eslogan…',
    FILTER_CATEGORY: 'Categoría',
    FILTER_SELLABLE: 'Vendible',
    FILTER_ACTIVE: 'Activo',
    FILTER_PRODUCT: 'Producto',
    FILTER_ALL: 'Todos',
    COL_CODE: 'Código',
    COL_NAME: 'Nombre',
    COL_CATEGORY: 'Categoría',
    COL_COMPATIBLE: 'Compatible con',
    COL_SELLABLE: 'Vendible',
    COL_ACTIVE: 'Activo',
    COL_SORT_ORDER: 'Orden',
    COL_ACTIONS: 'Acciones',
    EMPTY_TITLE: 'Sin add-ons en el catálogo',
    EMPTY_SUBTITLE: 'Crea el primer add-on para empezar a venderlo.',
    CATEGORY_COMPLIANCE: 'Cumplimiento',
    CATEGORY_COMMUNICATION: 'Comunicación',
    CATEGORY_INTELLIGENCE: 'Inteligencia',
    CATEGORY_INTEGRATION: 'Integración',
    CATEGORY_BRANDING: 'Marca',
    FIELD_CODE: 'Código',
    FIELD_CODE_HINT:
      'Identificador globalmente único (ej. ecf). Inmutable después de crear.',
    FIELD_NAME: 'Nombre',
    FIELD_TAGLINE: 'Eslogan',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_ICON: 'Ícono (Lucide)',
    FIELD_CATEGORY: 'Categoría',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_IS_SELLABLE: 'Vendible',
    FIELD_SORT_ORDER: 'Orden de presentación',
    SECTION_COMPATIBLE_PRODUCTS: 'Productos compatibles',
    COMPATIBLE_PRODUCTS_HINT:
      'Selecciona los productos a los que se conecta el add-on. Vacío = universal.',
    SECTION_REQUIRES: 'Funcionalidades requeridas',
    REQUIRES_HINT:
      'Códigos de funcionalidades (catalog_feature.code) que deben estar activas en la suscripción para comprar este add-on.',
    REQUIRES_PLACEHOLDER: 'invoicing.returns',
    REQUIRES_ADD: 'Agregar',
    SECTION_ENTITLEMENTS: 'Capacidades (override)',
    YES: 'Sí',
    NO: 'No',
    TOAST_CREATED: 'Add-on creado',
    TOAST_UPDATED: 'Add-on actualizado',
    TOAST_DELETED: 'Add-on eliminado',
    TOAST_REORDERED: 'Orden actualizado',
    TOAST_CREATE_FAILED: 'No se pudo crear el add-on',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el add-on',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el add-on',
    TOAST_REORDER_FAILED: 'No se pudo actualizar el orden',
    CODE_IMMUTABLE_HINT:
      'El código no se puede editar después de crear el add-on.',
    MOVE_UP: 'Subir',
    MOVE_DOWN: 'Bajar',
  },
  MODULE_FEATURES: {
    NAV: 'Funcionalidades',
    TITLE: 'Funcionalidades',
    SUBTITLE:
      'Funcionalidades vendibles dentro del módulo. Las suscripciones agregan o quitan funcionalidades marcadas como vendibles.',
    BREADCRUMB_BACK: 'Volver a módulos',
    NEW_FEATURE: 'Nueva funcionalidad',
    EDIT_FEATURE: 'Editar funcionalidad',
    DELETE_FEATURE: 'Eliminar funcionalidad',
    DELETE_CONFIRM_TITLE: '¿Eliminar funcionalidad?',
    DELETE_CONFIRM_DESC:
      'La funcionalidad se ocultará. La eliminación es lógica y reversible.',
    COL_CODE: 'Código',
    COL_NAME: 'Nombre',
    COL_ACTIVE: 'Activa',
    COL_SELLABLE: 'Vendible',
    COL_SORT_ORDER: 'Orden',
    COL_ACTIONS: 'Acciones',
    EMPTY_TITLE: 'Sin funcionalidades para este módulo',
    EMPTY_SUBTITLE: 'Crea la primera funcionalidad para estructurar el módulo.',
    FIELD_CODE: 'Código',
    FIELD_CODE_HINT:
      'Único dentro del módulo (ej. returns). Inmutable después de crear.',
    FIELD_NAME: 'Nombre',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_ICON: 'Ícono (Lucide)',
    FIELD_IS_ACTIVE: 'Activa',
    FIELD_IS_SELLABLE: 'Vendible',
    FIELD_IS_SELLABLE_HINT:
      'Si está desactivada, la funcionalidad viene incluida con el módulo y no se puede agregar o quitar por separado.',
    FIELD_SORT_ORDER: 'Orden de presentación',
    SECTION_ENTITLEMENTS_OVERRIDE: 'Capacidades (override)',
    ENTITLEMENTS_OVERRIDE_HINT:
      'Banderas que sobreescriben las del módulo. Vacío = hereda del módulo.',
    YES: 'Sí',
    NO: 'No',
    TOAST_CREATED: 'Funcionalidad creada',
    TOAST_UPDATED: 'Funcionalidad actualizada',
    TOAST_DELETED: 'Funcionalidad eliminada',
    TOAST_REORDERED: 'Orden actualizado',
    TOAST_CREATE_FAILED: 'No se pudo crear la funcionalidad',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar la funcionalidad',
    TOAST_DELETE_FAILED: 'No se pudo eliminar la funcionalidad',
    TOAST_REORDER_FAILED: 'No se pudo actualizar el orden',
    CODE_IMMUTABLE_HINT:
      'El código no se puede editar después de crear la funcionalidad.',
    VIEW_FEATURES_ACTION: 'Ver funcionalidades',
    TAB_HINT:
      'Vista plana de todas las funcionalidades. Para crear nuevas, ve al módulo correspondiente.',
    SEARCH_PLACEHOLDER: 'Buscar por código o nombre…',
    FILTER_MODULE: 'Módulo',
    MOVE_UP: 'Subir',
    MOVE_DOWN: 'Bajar',
  },
  PRODUCT_MODULES: {
    NAV: 'Módulos',
    TITLE: 'Módulos',
    SUBTITLE:
      'Agrupaciones funcionales dentro del producto. Las funcionalidades cuelgan de cada módulo.',
    BREADCRUMB_BACK: 'Volver al catálogo',
    BREADCRUMB_PRODUCT: 'Producto',
    NEW_MODULE: 'Nuevo módulo',
    EDIT_MODULE: 'Editar módulo',
    DELETE_MODULE: 'Eliminar módulo',
    DELETE_CONFIRM_TITLE: '¿Eliminar módulo?',
    DELETE_CONFIRM_DESC:
      'El módulo se ocultará. La eliminación es lógica y reversible.',
    COL_CODE: 'Código',
    COL_NAME: 'Nombre',
    COL_ACTIVE: 'Activo',
    COL_SORT_ORDER: 'Orden',
    COL_ACTIONS: 'Acciones',
    EMPTY_TITLE: 'Sin módulos para este producto',
    EMPTY_SUBTITLE:
      'Crea el primer módulo para estructurar la funcionalidad del producto.',
    FIELD_CODE: 'Código',
    FIELD_CODE_HINT:
      'Único dentro del producto (ej. invoicing). Inmutable después de crear.',
    FIELD_NAME: 'Nombre',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_ICON: 'Ícono (Lucide)',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_SORT_ORDER: 'Orden de presentación',
    SECTION_ENTITLEMENTS_OVERRIDE: 'Capacidades (override)',
    ENTITLEMENTS_OVERRIDE_HINT:
      'Banderas que sobreescriben las del producto. Vacío = hereda del producto.',
    YES: 'Sí',
    NO: 'No',
    TOAST_CREATED: 'Módulo creado',
    TOAST_UPDATED: 'Módulo actualizado',
    TOAST_DELETED: 'Módulo eliminado',
    TOAST_REORDERED: 'Orden actualizado',
    TOAST_CREATE_FAILED: 'No se pudo crear el módulo',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el módulo',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el módulo',
    TOAST_REORDER_FAILED: 'No se pudo actualizar el orden',
    CODE_IMMUTABLE_HINT:
      'El código no se puede editar después de crear el módulo.',
    VIEW_MODULES_ACTION: 'Ver módulos',
    MOVE_UP: 'Subir',
    MOVE_DOWN: 'Bajar',
    PRODUCT_NOT_FOUND: 'Producto no encontrado',
    FIELD_CATEGORY: 'Categoría comercial',
    FIELD_IS_SELLABLE: 'Vendible',
    COL_CATEGORY: 'Categoría',
    COL_IS_SELLABLE: 'Vendible',
    COL_IS_ACTIVE: 'Activo',
    COL_PRODUCT: 'Producto',
    TAB_HINT:
      'Vista plana de todos los módulos. Para crear nuevos módulos sin afectar al endpoint público, ajusta `Vendible`.',
    SEARCH_PLACEHOLDER: 'Buscar por código o nombre…',
    FILTER_PRODUCT: 'Producto',
    FILTER_CATEGORY: 'Categoría',
    PICK_PRODUCT_TITLE: 'Selecciona el producto padre',
    PICK_PRODUCT_HINT:
      'Cada módulo pertenece a un producto comercial (Neo App, NeoPOS, …). Selecciona el destino antes de crear el módulo.',
    PICK_PRODUCT_PLACEHOLDER: 'Producto…',
  },
  CATALOG_QUOTAS: {
    NEW_QUOTA: 'Nueva cuota',
    EDIT_QUOTA: 'Editar cuota',
    DELETE_QUOTA: 'Eliminar cuota',
    DELETE_CONFIRM_TITLE: '¿Eliminar cuota?',
    DELETE_CONFIRM_DESC:
      'La cuota se ocultará. La eliminación es lógica y reversible.',
    SEARCH_PLACEHOLDER: 'Buscar por código o nombre…',
    COL_CODE: 'Código',
    COL_NAME: 'Nombre',
    COL_UNIT: 'Unidad',
    COL_ACTIVE: 'Activo',
    COL_SORT_ORDER: 'Orden',
    EMPTY_TITLE: 'Sin cuotas',
    FIELD_CODE: 'Código',
    FIELD_CODE_HINT:
      'Snake-case ASCII. Permite puntos y guiones (ej. ecf_invoices). Inmutable después de crear.',
    FIELD_NAME: 'Nombre',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_ICON: 'Ícono (Lucide)',
    FIELD_UNIT: 'Unidad (singular)',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_SORT_ORDER: 'Orden de presentación',
    CODE_IMMUTABLE_HINT: 'El código no se puede editar después de crear.',
    TOAST_CREATED: 'Cuota creada',
    TOAST_UPDATED: 'Cuota actualizada',
    TOAST_DELETED: 'Cuota eliminada',
    TOAST_CREATE_FAILED: 'No se pudo crear la cuota',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar la cuota',
    TOAST_DELETE_FAILED: 'No se pudo eliminar la cuota',
  },
  CATALOG_MAPPINGS: {
    NEW_MAPPING: 'Nuevo mapeo',
    EDIT_MAPPING: 'Editar mapeo',
    DELETE_MAPPING: 'Eliminar mapeo',
    DELETE_CONFIRM_TITLE: '¿Eliminar mapeo?',
    DELETE_CONFIRM_DESC:
      'El mapeo se ocultará. La eliminación es lógica y reversible.',
    EMPTY_TITLE: 'Sin mapeos',
    COL_MODULE: 'Módulo',
    COL_FEATURE: 'Funcionalidad',
    COL_QUOTA: 'Cuota',
    COL_INCLUDED_QTY: 'Cantidad incluida',
    FIELD_MODULE: 'Módulo',
    FIELD_FEATURE: 'Funcionalidad',
    FIELD_QUOTA: 'Cuota',
    FIELD_INCLUDED_QTY: 'Cantidad incluida con el módulo',
    PICK_MODULE: 'Selecciona un módulo…',
    PICK_FEATURE: 'Selecciona una funcionalidad…',
    PICK_QUOTA: 'Selecciona una cuota…',
    TOAST_CREATED: 'Mapeo creado',
    TOAST_UPDATED: 'Mapeo actualizado',
    TOAST_DELETED: 'Mapeo eliminado',
    TOAST_CREATE_FAILED: 'No se pudo crear el mapeo',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el mapeo',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el mapeo',
  },
  CATALOG_QUANTITY_SCALES: {
    NEW_SCALE: 'Nuevo tramo',
    EDIT_SCALE: 'Editar tramo',
    DELETE_SCALE: 'Eliminar tramo',
    DELETE_CONFIRM_TITLE: '¿Eliminar tramo?',
    DELETE_CONFIRM_DESC:
      'El tramo se ocultará. La eliminación es lógica y reversible.',
    FILTER_QUOTA: 'Cuota',
    COL_QUOTA: 'Cuota',
    COL_TIER: 'Tramo',
    COL_RANGE: 'Rango',
    COL_PER_UNIT: 'Precio por unidad',
    COL_FLAT: 'Precio fijo',
    COL_CURRENCY: 'Moneda',
    COL_ACTIVE: 'Activo',
    EMPTY_TITLE: 'Sin tramos',
    FIELD_QUOTA: 'Cuota padre',
    FIELD_TIER_LABEL: 'Etiqueta del tramo',
    FIELD_MIN_QUANTITY: 'Cantidad mínima',
    FIELD_MAX_QUANTITY: 'Cantidad máxima',
    FIELD_PER_UNIT: 'Precio mensual por unidad (USD)',
    FIELD_FLAT: 'Precio fijo mensual (USD)',
    FIELD_CURRENCY: 'Moneda',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_SORT_ORDER: 'Orden',
    RANGE_INVALID: 'La cantidad máxima debe ser ≥ a la mínima.',
    TOAST_CREATED: 'Tramo creado',
    TOAST_UPDATED: 'Tramo actualizado',
    TOAST_DELETED: 'Tramo eliminado',
    TOAST_CREATE_FAILED: 'No se pudo crear el tramo',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el tramo',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el tramo',
  },
  SUBSCRIPTION_CHANGES: {
    TITLE: 'Cambios de suscripción',
    SUBTITLE:
      'Propón cambios a tu suscripción — el equipo los revisa y aplica.',
    REQUESTS: 'Solicitudes',
    NEW_REQUEST: 'Nueva solicitud',
    ITEMS: 'Items',
    ADD_ITEM: 'Añadir item',
    SUMMARY: 'Resumen',
    SUMMARY_PLACEHOLDER: 'Ej. Añadir 2 sucursales',
    NOTE: 'Nota',
    NOTE_PLACEHOLDER: 'Detalles adicionales para el equipo',
    SUBMIT: 'Enviar solicitud',
    SUBMITTED: 'Solicitud enviada',
    CANCELLED: 'Solicitud cancelada',
    CANCEL_CONFIRM_TITLE: '¿Cancelar la solicitud?',
    COL_CREATED: 'Creada',
    COL_SUMMARY: 'Resumen',
    COL_STATUS: 'Estado',
    COL_NOTE: 'Nota',
  },
  CUSTOMER_BILLING: {
    TITLE: 'Facturación',
    SUBTITLE: 'Tus facturas, líneas y pagos recientes.',
    INVOICES: 'Facturas',
    INVOICE: 'Factura',
    LINES: 'Líneas',
    PAYMENTS: 'Pagos',
    LOADING: 'Cargando…',
    COL_NUMBER: 'Número',
    COL_STATUS: 'Estado',
    COL_ISSUED: 'Emitida',
    COL_DUE: 'Vencimiento',
    COL_TOTAL: 'Total',
    COL_DESCRIPTION: 'Descripción',
    COL_QUANTITY: 'Cantidad',
    COL_UNIT_PRICE: 'Precio unitario',
    COL_AMOUNT: 'Monto',
    COL_PROVIDER: 'Proveedor',
    COL_CAPTURED: 'Capturado',
  },
  SADMIN_BILLING: {
    TITLE: 'Billing (sadmin)',
    SUBTITLE: 'Facturación de tenants — invoices, cobros y procesos.',
    CRON_TITLE: 'Procesos',
    CRON_GROUP_GLOBAL: 'Acciones globales',
    CRON_GROUP_BUSINESS: 'Acciones por empresa',
    INV_STATUS_DRAFT: 'Borrador',
    INV_STATUS_OPEN: 'Abierta',
    INV_STATUS_PAID: 'Pagada',
    INV_STATUS_FAILED: 'Fallida',
    INV_STATUS_VOID: 'Anulada',
    INV_SOURCE_RECURRING: 'Recurrente',
    INV_SOURCE_MANUAL: 'Manual',
    INV_SOURCE_ADJUSTMENT: 'Ajuste',
    INV_SOURCE_CHANGE_REQUEST: 'Cambio de plan',
    RUN_RECURRING: 'Generar facturas recurrentes (todas las empresas)',
    RUN_RECURRING_TOOLTIP:
      'Global — genera la factura del próximo ciclo para TODAS las empresas cuyo período ya venció. Simula el barrido mensual del 1ro sin esperar al cron.',
    RUN_DUNNING: 'Enviar recordatorios de pago vencido',
    RUN_DUNNING_TOOLTIP:
      'Global — recorre facturas VENCIDAS sin pagar, marca como "failed" y envía recordatorios por correo. No crea facturas nuevas.',
    RUN_RECURRING_BUSINESS: 'Ejecutar cobro recurrente para esta empresa',
    RUN_RECURRING_BUSINESS_TOOLTIP:
      'Sólo esta empresa — genera la factura recurrente sin importar la fecha del ciclo. Idempotente: reutiliza factura pendiente si ya existe.',
    RUN_RECURRING_BUSINESS_HINT_PICK:
      'Selecciona una empresa abajo para habilitar esta acción.',
    RUN_RECURRING_BUSINESS_CREATED:
      'Factura {{invoice}} creada — período avanzado.',
    RUN_RECURRING_BUSINESS_EXISTING:
      'Ya existe factura sin pagar ({{invoice}}). No se generó una nueva.',
    PICK_BUSINESS: 'Selecciona un negocio…',
    INVOICES: 'Facturas',
    COL_NUMBER: 'Número',
    COL_FISCAL: 'Comprobante',
    FISCAL_RNC_LABEL: 'RNC {{rnc}}',
    FISCAL_NONE: 'Sin comprobante',
    COL_STATUS: 'Estado',
    COL_SOURCE: 'Origen',
    COL_ISSUED: 'Emitida',
    COL_DUE: 'Vencimiento',
    COL_TOTAL: 'Total',
    ACTION_ISSUE: 'Emitir',
    ACTION_MARK_PAID: 'Marcar pagada',
    ACTION_ADJUST_AND_SETTLE: 'Ajustar y liquidar',
    ACTION_MARK_COMMISSION_PAID: 'Marcar comisión pagada',
    COMMISSION_STATUS_PAID: 'Comisión pagada',
    COMMISSION_STATUS_PENDING: 'Comisión pendiente',
    SETTLE_COMMISSION_TITLE: 'Marcar comisión pagada',
    SETTLE_COMMISSION_DESC:
      'Se marcará la comisión de {{amount}} del reseller {{reseller}} como pagada. Solo afecta esta factura.',
    SETTLE_COMMISSION_DONE: 'Comisión marcada como pagada.',
    ADJUST_SETTLE_TITLE: 'Ajustar y liquidar',
    ADJUST_SETTLE_DESCRIPTION:
      'Elige el intervalo, aplica un descuento y liquida la comisión del reseller en un solo paso.',
    ADJUST_SETTLE_INTERVAL_LABEL: 'Intervalo',
    ADJUST_SETTLE_INTERVAL_MONTHLY: 'Mantener mensual',
    ADJUST_SETTLE_INTERVAL_ANNUAL: 'Convertir a anual',
    ADJUST_SETTLE_DISCOUNT_LABEL: 'Descuento (%)',
    ADJUST_SETTLE_BASE_SUBTOTAL: 'Subtotal base',
    ADJUST_SETTLE_ANNUAL_SUBTOTAL: 'Subtotal anual',
    ADJUST_SETTLE_DISCOUNT: 'Descuento {{percent}}%',
    ADJUST_SETTLE_ANNUAL_TOTAL: 'Total',
    ADJUST_SETTLE_COMMISSION: 'Comisión del reseller (estimada)',
    ADJUST_SETTLE_COMMISSION_HINT:
      'Al liquidar se paga el saldo pendiente total del reseller en esta moneda, que puede incluir comisiones de otras facturas.',
    ADJUST_SETTLE_CONFIRM_WARNING:
      'Esta acción anula la factura pendiente, aplica el intervalo y el descuento elegidos, regenera y cobra la factura, y liquida la comisión del reseller. La captura y la liquidación no se revierten automáticamente.',
    RESELLER_METHOD_LABEL: 'Método de pago al reseller',
    RESELLER_METHOD_BANK_TRANSFER: 'Transferencia bancaria',
    RESELLER_METHOD_ZELLE: 'Zelle',
    RESELLER_METHOD_OTHER: 'Otro',
    RESELLER_EXTERNAL_REF_LABEL: 'Referencia de pago al reseller',
    RESELLER_EXTERNAL_REF_PLACEHOLDER: 'Opcional',
    ACTION_MARK_FAILED: 'Marcar fallida',
    ACTION_VOID: 'Anular',
    ACTION_ADJUST: 'Ajustar',
    ACTION_EDIT_ISSUED_AT: 'Editar fecha de emisión',
    ACTION_DOWNLOAD_PDF: 'Descargar PDF',
    ISSUED_AT_TITLE: 'Fecha de emisión — {{number}}',
    ISSUED_AT_DESCRIPTION:
      'Corrige la fecha en que se emitió la factura. Es la fecha que el cliente ve en la columna Emitida y en el PDF. No cambia el vencimiento ni la cobranza.',
    ISSUED_AT_LABEL: 'Fecha de emisión',
    ISSUED_AT_CURRENT: 'Fecha actual',
    ISSUED_AT_REQUIRED: 'Selecciona una fecha.',
    ISSUED_AT_SUCCESS: 'Fecha de emisión actualizada.',
    DOWNLOAD_PDF_FAILED: 'No se pudo generar el PDF.',
    ACTION_REFUND: 'Reembolsar',
    REFUND_TITLE: 'Reembolsar factura {{invoice}}',
    REFUND_DESC:
      'Se reembolsarán {{amount}} al método de pago original y la factura pasará a estado anulada. Los reembolsos en Stripe pueden tardar entre 5 y 10 días hábiles en reflejarse.',
    REFUND_REASON_LABEL: 'Motivo del reembolso',
    REFUND_REASON_PLACEHOLDER:
      'Ej. Cobro duplicado, cliente solicitó cancelación',
    REFUND_REASON_REQUIRED:
      'El motivo del reembolso es obligatorio (mín. 3 caracteres).',
    REFUND_CONFIRM_CTA: 'Reembolsar',
    REFUND_SUCCESS: 'Reembolso emitido correctamente.',
    MARKED_PAID: 'Factura marcada como pagada',
    MARKED_FAILED: 'Factura marcada como fallida',
    VOIDED: 'Factura anulada',
    CONFIRM_TITLE: '¿Confirmar acción?',
    CONFIRM_PAID_TITLE: '¿Marcar la factura como pagada?',
    CONFIRM_PAID_MESSAGE:
      'La factura pasará a estado pagada y se registrará el pago. ¿Deseas continuar?',
    CONFIRM_PAID_NO_GL_WARNING:
      'Aviso: esta acción no genera asiento contable (COMISIONES POR PAGAR u otras cuentas). La comisión del revendedor sí se acumula en el submayor.',
    MARK_PAID_REFERENCE_HELP:
      'Opcional. Si el pago fue por transferencia, ingresa la referencia bancaria.',
    SETTLED_VIA: 'Cobrada vía',
    CONFIRM_FAILED_TITLE: '¿Marcar la factura como fallida?',
    CONFIRM_FAILED_MESSAGE:
      'La factura quedará marcada como fallida y podrá ser reintentada. ¿Deseas continuar?',
    CONFIRM_VOID_TITLE: '¿Anular la factura?',
    CONFIRM_VOID_MESSAGE:
      'La factura será anulada. Esta acción no se puede deshacer. ¿Deseas continuar?',
    FISCAL_UPLOAD: 'Cargar comprobante',
    FISCAL_REPLACE: 'Reemplazar comprobante',
    FISCAL_UPLOADED: 'Comprobante fiscal cargado',
    FISCAL_DOWNLOAD: 'Descargar comprobante',
    // Per-row manual comprobante emission (Pagos + Facturas tabs).
    COMPROBANTE_COL: 'Comprobante',
    COMPROBANTE_STATUS_ISSUED: 'Emitido',
    COMPROBANTE_STATUS_PENDING: 'Pendiente',
    COMPROBANTE_GENERATE_ACTION: 'Generar comprobante',
    COMPROBANTE_GENERATED: 'Comprobante generado correctamente.',
    COMPROBANTE_PRE_FACTURA_DONE:
      'Pre-Factura generada (documento no fiscal, sin NCF).',
    COMPROBANTE_SKIPPED: 'Esta factura ya tenía su comprobante generado.',
    PAYMENT_TYPE_LABEL: 'Tipo de pago',
    PAYMENT_TYPE_PLACEHOLDER: 'Selecciona el tipo de pago',
    COMPROBANTE_PICK_TYPE: 'Elige el tipo de comprobante',
    COMPROBANTE_OPEN_LINK: 'Abrir comprobante',
    COMPROBANTE_DATE_LABEL: 'Fecha del comprobante (opcional)',
    COMPROBANTE_DATE_HELP:
      'Si no seleccionas una fecha, se usará la de hoy. La factura quedará registrada en la fecha elegida.',
    COMPROBANTE_TENANT_PREFERENCE: 'Preferencia del cliente',
    COMPROBANTE_PRE_FACTURA_LABEL: 'Pre-Factura (no fiscal)',
    COMPROBANTE_CONFIRM_TITLE: '¿Generar este comprobante?',
    COMPROBANTE_CONFIRM_FISCAL:
      'Se emitirá un comprobante fiscal de tipo {{type}} para esta factura. El NCF se asignará y el cliente podrá descargarlo desde su cuenta. ¿Continuar?',
    COMPROBANTE_CONFIRM_PREFACTURA:
      'Se emitirá una {{type}}: un documento NO fiscal, sin NCF. El cliente podrá descargarlo desde su cuenta. ¿Continuar?',
    ADJUST_TITLE: 'Ajustar factura {{number}}',
    ADJUST_DESCRIPTION:
      'Agrega una línea de ajuste (crédito o cargo) a esta misma factura y recalcula el total. La razón queda registrada en las notas y en el historial.',
    ADJUST_CURRENT_TOTAL: 'Total actual',
    ADJUST_TARGET_TOTAL: 'Total objetivo ({{currency}})',
    ADJUST_DELTA_HINT: 'Diferencia a facturar: {{delta}}',
    ADJUST_REASON_LABEL: 'Razón del ajuste',
    ADJUST_REASON_PLACEHOLDER:
      'Ej. Downgrade aplicado inmediatamente — realinear factura al plan nuevo',
    ADJUST_ZERO_DELTA: 'El total objetivo es igual al actual, no hay ajuste.',
    ADJUST_REASON_REQUIRED: 'La razón es requerida (mínimo 3 caracteres).',
    ADJUST_SUCCESS: 'Ajuste aplicado a la factura.',
    ADJUST_CONFIRM_CTA: 'Aplicar ajuste',
    PREPAY_ACTION: 'Registrar prepago',
    PREPAY_TITLE: 'Registrar prepago por transferencia',
    PREPAY_DESCRIPTION:
      'Registra un pago por transferencia bancaria que cubre varios ciclos de facturación como una única factura pagada. La suscripción avanza automáticamente al final del período cubierto.',
    PREPAY_MONTHS_LABEL: 'Cantidad de meses',
    PREPAY_MONTHS_HELP: 'Entre 1 y 24 meses.',
    PREPAY_AMOUNT_LABEL: 'Monto recibido',
    PREPAY_CURRENCY_LABEL: 'Moneda',
    PREPAY_AMOUNT_HELP:
      'Monto real transferido. Puede diferir del esperado por descuentos o ajustes.',
    PREPAY_CURRENCY_MISMATCH:
      'La moneda recibida ({{received}}) difiere de la moneda de la factura ({{invoice}}). Reconciliar FX manualmente.',
    PREPAY_REFERENCE_LABEL: 'Referencia bancaria',
    PREPAY_REFERENCE_PLACEHOLDER: 'ej. TRX-20260811-ABC123',
    PREPAY_DATE_LABEL: 'Fecha del pago',
    PREPAY_NOTES_LABEL: 'Notas (opcional)',
    PREPAY_NOTES_PLACEHOLDER:
      'Contexto extra: banco, cuenta, razón del ajuste…',
    PREPAY_PREVIEW_TITLE: 'Este prepago cubrirá:',
    PREPAY_PREVIEW_PERIOD: 'Período',
    PREPAY_PREVIEW_NEXT_BILLING: 'Próxima fecha de facturación',
    PREPAY_PREVIEW_EXPECTED: 'Monto esperado',
    PREPAY_PREVIEW_RECEIVED: 'Monto recibido',
    PREPAY_PREVIEW_DELTA: 'Delta',
    PREPAY_CONFIRM_TITLE: 'Confirmar prepago',
    PREPAY_CONFIRM_DESCRIPTION:
      'Esta acción registra un pago irreversible y avanza el período de facturación. Verifica la referencia bancaria y el monto antes de continuar.',
    PREPAY_CONFIRM_CTA: 'Registrar prepago',
    PREPAY_SUCCESS: 'Prepago registrado correctamente.',
    PREPAY_INVALID_MONTHS:
      'La cantidad de meses debe ser un entero entre 1 y 24.',
    PREPAY_AMOUNT_MIN: 'El monto debe ser mayor que cero.',
    PREPAY_REFERENCE_REQUIRED: 'La referencia bancaria es requerida.',
    PREPAY_DATE_REQUIRED: 'La fecha del pago es requerida.',
    // Title / total / period now live in the NEXT_INVOICE_PREVIEW
    // concept namespace — the card renders on sadmin AND on the
    // tenant's /account, and one string must not drift between them.
    // The hint stays here: it names internal plumbing and is for
    // operators only.
    NEXT_PREVIEW_HINT:
      'Aún no existe una fila en billing_invoice — este cálculo lo emite el cron al llegar el fin de ciclo.',
    TILE_NEEDS_TO_PAY: 'Por cobrar',
    TILE_PAST_DUE: 'Vencidos',
    TILE_COMMISSIONS_DUE: 'Comisiones por pagar',
    TAB_SUBSCRIPTIONS: 'Suscripciones',
    TAB_INVOICES: 'Facturas',
    TAB_PAYMENTS: 'Pagos',
    PAYMENTS_FILTER_FROM: 'Desde',
    PAYMENTS_FILTER_TO: 'Hasta',
    PAYMENTS_FILTER_PROVIDER: 'Método',
    PAYMENTS_FILTER_STATUS: 'Estado',
    PAYMENTS_ROWS_LABEL: '{{n}} pagos',
    PAYMENTS_EMPTY: 'Sin pagos para los filtros seleccionados.',
    PAYMENTS_COL_BUSINESS: 'Negocio',
    PAYMENTS_COL_INVOICE: 'Factura',
    PAYMENTS_COL_METHOD: 'Método',
    PAYMENTS_COL_AMOUNT: 'Monto',
    PAYMENTS_COL_STATUS: 'Estado',
    PAYMENTS_COL_COMMISSION: 'Comisión',
    PAYMENTS_COL_CAPTURED_AT: 'Cobrado',
    PAYMENT_STATUS_SUCCEEDED: 'Cobrado',
    PAYMENT_STATUS_FAILED: 'Fallido',
    PAYMENT_STATUS_REFUNDED: 'Reembolsado',
    PAYMENT_STATUS_PENDING: 'Pendiente',
    PAYMENT_COMMISSION_YES: 'Generó comisión',
    PAYMENT_COMMISSION_NO: 'Sin comisión',
    KPI_REVENUE_MTD: 'Ingresos MTD',
    KPI_REVENUE_YTD: 'Ingresos YTD',
    KPI_MRR: 'MRR estimado',
    KPI_PAYMENTS_TODAY: 'Pagos hoy',
    KPI_OUTSTANDING_INVOICES: 'Facturas por cobrar',
    KPI_HINT_MTD: 'Cobros de este mes.',
    KPI_HINT_YTD: 'Cobros del año en curso.',
    KPI_HINT_MRR: 'Suma mensual estimada por suscripciones activas.',
    KPI_HINT_TODAY: 'Total contado hoy.',
    KPI_HINT_OUTSTANDING:
      'Total pendiente de cobro (Borrador + Abierta + Fallida).',
    KPI_HINT_COMMISSIONS: 'Saldo neto por pagar a resellers.',
    KPI_DELTA_VS_PRIOR: 'vs. periodo anterior',
    KPI_COUNT_LABEL: '{{count}} pagos',
    KPI_NET_AFTER_COMMISSION: 'Neto tras comisión: {{amount}}',
    KPI_NET_AFTER_COMMISSION_EST: 'Neto estimado: {{amount}}',
    KPI_MRR_SUBLABEL: '{{count}} suscripciones activas',
    KPI_TILE_TITLE: 'Panel financiero',
    KPI_TILE_SUBTITLE:
      'Ingresos, pagos y comisiones cross-tenant. Toca una métrica para explorar el detalle.',
    ACTIONS_GLOBAL_TITLE: 'Acciones globales',
    ACTIONS_BUSINESS_TITLE: 'Acciones para {{name}}',
    ACTION_VIEW_BUSINESS_DASHBOARD: 'Ver panel del negocio',
    ACTION_GOTO_SUBSCRIPTIONS: 'Ir a suscripciones',
    ACTION_RECORD_PREPAYMENT: 'Registrar prepago',
    PAYMENTS_ADD_FILTER: 'Añadir filtro',
    PAYMENTS_DAY_HEADER: '{{date}} · {{count}} pagos · {{total}}',
    SUB_CARD_MRR: 'MRR',
    SUB_CARD_NEXT_PAYMENT: 'Próximo pago',
    SUB_CARD_LAST_PAYMENT: 'Último pago',
    SUB_CARD_VIEW_DETAILS: 'Ver detalles',
    SUB_HEALTH_GOOD: 'Al día',
    SUB_HEALTH_WARN: 'En prueba',
    SUB_HEALTH_RISK: 'En riesgo',
    SUB_FILTER_PAST_DUE: 'Solo vencidos',
    SUB_FILTER_TRIAL: 'Solo prueba',
    PICK_BUSINESS_FIRST: 'Selecciona una empresa para ver sus facturas.',
    EXPORT_SUBSCRIPTIONS: 'Exportar suscripciones',
    EXPORT_INVOICES: 'Exportar facturas',
    EXPORT_GENERIC: 'Exportar',
    EXPORT_DATE_FROM: 'Desde',
    EXPORT_DATE_TO: 'Hasta',
    EXPORT_DATE_BASIS: 'Filtrar por',
    EXPORT_DATE_BASIS_ISSUED: 'Fecha de emisión',
    EXPORT_DATE_BASIS_PAID: 'Fecha de pago',
    EXPORT_DATE_BASIS_CREATED: 'Fecha de creación',
    EXPORT_DATE_BASIS_PERIOD_END: 'Fin de periodo',
    EXPORT_FORMAT: 'Formato',
    EXPORT_FORMAT_EXCEL: 'Excel',
    EXPORT_FORMAT_PDF: 'PDF',
    EXPORT_CONFIRM: 'Descargar',
    EXPORT_EMPTY: 'No hay filas en el rango seleccionado.',
    EXPORT_SUBSCRIPTIONS_TITLE: 'Suscripciones',
    EXPORT_INVOICES_TITLE: 'Facturas',
    EXPORT_COL_BUSINESS: 'Empresa',
    EXPORT_COL_PLAN: 'Plan',
    EXPORT_COL_STATUS: 'Estado',
    EXPORT_COL_INTERVAL: 'Ciclo',
    EXPORT_COL_TRIAL_ENDS: 'Fin de trial',
    EXPORT_COL_PERIOD_ENDS: 'Fin de periodo',
    EXPORT_COL_CREATED_AT: 'Alta',
    EXPORT_COL_INVOICE_NUMBER: 'Factura #',
    EXPORT_COL_FISCAL_RNC: 'RNC fiscal',
    EXPORT_COL_FISCAL_NAME: 'Razón social',
    EXPORT_COL_SOURCE: 'Origen',
    EXPORT_COL_ISSUED_AT: 'Emitida',
    EXPORT_COL_DUE_DATE: 'Vence',
    EXPORT_COL_PAID_AT: 'Pagada',
    EXPORT_COL_TOTAL: 'Total',
    EXPORT_COL_CURRENCY: 'Moneda',
  },
  SADMIN_DASHBOARD: {
    SECTION_BUSINESSES: 'Negocios',
    SECTION_INVOICES: 'Facturas (últimos 30 días)',
    TILE_ACTIVE: 'Activos',
    TILE_INACTIVE: 'Inactivos',
    TILE_SUSPENDED: 'Suspendidos',
    TILE_TRIALING: 'En prueba',
    TILE_SPONSORED: 'Cortesía',
    TILE_INVOICES_PENDING: 'Pendientes',
    TILE_INVOICES_PAID: 'Pagadas',
    TILE_INVOICES_FAILED: 'Fallidas',
    FILTER_CLEAR: 'Quitar filtro',
    EXPORT_CSV: 'Exportar CSV',
  },
  BUSINESS_COMPOSITION: {
    NAV: 'Composición del negocio',
    TITLE: 'Composición del negocio',
    SUBTITLE:
      'Suscripción + cortesías + capacidades efectivas + uso por cuota.',
    PICK_BUSINESS: 'Selecciona un negocio…',
    HEADER: 'Suscripción',
    STATUS: 'Estado',
    CURRENCY: 'Moneda',
    PERIOD_START: 'Inicio del período',
    PERIOD_END: 'Fin del período',
    TAB_SUBSCRIPTION: 'Suscripción',
    TAB_GRANTS: 'Cortesías',
    TAB_ENTITLEMENTS: 'Capacidades efectivas',
    TAB_USAGE: 'Uso',
    SUBSCRIPTION_ITEMS: 'Items de suscripción',
    GRANDFATHERED_COUNT: '{{count}} heredados',
    COMPLIMENTARY_GRANTS: 'Cortesías activas',
    BULK_CORTESIA_OFF_CTA: 'Desactivar todas',
    BULK_CORTESIA_ON_CTA: 'Marcar todas como cortesía',
    BULK_CORTESIA_CONFIRM_TITLE: '¿Aplicar a todos los ítems?',
    BULK_CORTESIA_OFF_CONFIRM:
      'Se quitará la cortesía de TODOS los ítems de la suscripción y cada uno pasará a ser facturable. Un negocio totalmente cortesía quedaría totalmente facturado. Esto no reduce la capacidad: las cuotas conservan su cantidad.',
    BULK_CORTESIA_ON_CONFIRM:
      'Se marcarán como cortesía TODOS los ítems de la suscripción y dejarán de facturarse.',
    BULK_CORTESIA_SUCCESS:
      '{{changed}} ítem(s) actualizados, {{skipped}} sin cambios.',
    BULK_CORTESIA_PARTIAL:
      '{{changed}} actualizados, {{failed}} fallaron. Revisa los ítems restantes.',
    BULK_REVOKE_CTA: 'Revocar todas',
    BULK_REVOKE_CONFIRM_TITLE: '¿Revocar todas las cortesías?',
    BULK_REVOKE_CONFIRM:
      'Se revocarán TODAS las cortesías activas de este negocio. Los módulos que solo provenían de una cortesía dejarán de estar disponibles; los que incluye el plan se mantienen.',
    BULK_REVOKE_SUCCESS: '{{revoked}} cortesía(s) revocada(s).',
    BULK_REVOKE_PARTIAL:
      '{{revoked}} revocada(s), {{failed}} fallaron. Vuelve a intentarlo.',
    BULK_ERROR: 'No se pudo completar la operación.',
    EFFECTIVE_CAPABILITIES: 'Códigos de capacidad efectivos',
    QUOTA_USAGE: 'Uso por cuota',
    COL_KIND: 'Tipo',
    COL_CODE: 'Código',
    COL_QUANTITY: 'Cantidad',
    COL_SOURCE: 'Origen',
    COL_GRANDFATHERED: 'Heredado',
    COL_CORTESIA: 'Cortesía',
    CORTESIA_HELP:
      'Al activar, el ítem deja de facturarse en la próxima renovación y se marca como Cortesía. Desactivar lo devuelve a estado pagado.',
    COL_REASON: 'Motivo',
    COL_USAGE: 'Usado',
    COL_LIMIT: 'Límite',
    COL_PERCENT: '%',
  },
  PRODUCT_CATALOG: {
    NAV: 'Catálogo de productos',
    TITLE: 'Catálogo de productos',
    SUBTITLE:
      'Productos comerciales de la plataforma. Cada producto agrupa módulos y funcionalidades vendibles.',
    NEW_PRODUCT: 'Nuevo producto',
    EDIT_PRODUCT: 'Editar producto',
    DELETE_PRODUCT: 'Eliminar producto',
    DELETE_CONFIRM_TITLE: '¿Eliminar producto?',
    DELETE_CONFIRM_DESC:
      'El producto se ocultará del catálogo. La eliminación es lógica y reversible.',
    SEARCH_PLACEHOLDER: 'Buscar por código, nombre o eslogan…',
    FILTER_CATEGORY: 'Categoría',
    FILTER_SELLABLE: 'Vendible',
    FILTER_ACTIVE: 'Activo',
    FILTER_ALL: 'Todos',
    COL_CODE: 'Código',
    COL_NAME: 'Nombre',
    COL_CATEGORY: 'Categoría',
    COL_SELLABLE: 'Vendible',
    COL_ACTIVE: 'Activo',
    COL_DEPLOYMENT: 'Despliegue',
    COL_SORT_ORDER: 'Orden',
    COL_ACTIONS: 'Acciones',
    EMPTY_TITLE: 'Sin productos en el catálogo',
    EMPTY_SUBTITLE:
      'Crea el primer producto para empezar a estructurar la oferta comercial.',
    CATEGORY_CORE: 'Núcleo',
    CATEGORY_COMPANION: 'Complementario',
    CATEGORY_PLATFORM: 'Plataforma',
    FIELD_CODE: 'Código',
    FIELD_CODE_HINT:
      'Identificador estable (ej. neo_pos). Inmutable después de crear.',
    FIELD_NAME: 'Nombre',
    FIELD_TAGLINE: 'Eslogan',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_ICON: 'Ícono (Lucide)',
    FIELD_CATEGORY: 'Categoría',
    FIELD_IS_SELLABLE: 'Vendible al público',
    FIELD_IS_ACTIVE: 'Activo',
    FIELD_MIN_CLIENT_VERSION: 'Versión mínima del cliente',
    FIELD_SORT_ORDER: 'Orden de presentación',
    SECTION_DEPLOYMENT_MODES: 'Modos de despliegue',
    DEPLOYMENT_SAAS: 'SaaS (NeoCloud)',
    DEPLOYMENT_HYBRID: 'Híbrido (Cloud + caché local)',
    DEPLOYMENT_OFFLINE: 'Offline (sin conexión)',
    DEPLOYMENT_ONPREM: 'On-Prem (servidor del cliente)',
    SECTION_ENTITLEMENTS: 'Capacidades (entitlements)',
    ENTITLEMENT_KEY_PLACEHOLDER: 'allowOfflineMode',
    ENTITLEMENT_ADD: 'Agregar capacidad',
    ENTITLEMENT_REMOVE: 'Quitar',
    ENTITLEMENT_HINT:
      'Banderas booleanas que controlan funcionalidades del producto. Se leen vía CapabilityService.',
    YES: 'Sí',
    NO: 'No',
    TOAST_CREATED: 'Producto creado',
    TOAST_UPDATED: 'Producto actualizado',
    TOAST_DELETED: 'Producto eliminado',
    TOAST_CREATE_FAILED: 'No se pudo crear el producto',
    TOAST_UPDATE_FAILED: 'No se pudo actualizar el producto',
    TOAST_DELETE_FAILED: 'No se pudo eliminar el producto',
    CODE_IMMUTABLE_HINT:
      'El código no se puede editar después de crear el producto.',
    TAB_PRODUCTS: 'Productos',
    TAB_MODULES: 'Módulos',
    TAB_ADDONS: 'Add-ons',
    TAB_FEATURES: 'Funcionalidades',
    TAB_QUOTAS: 'Cuotas',
    TAB_PRICES: 'Precios',
    TAB_QUANTITY_SCALES: 'Escalas',
    TAB_MODULE_FEATURES: 'Mapeo módulo → funcionalidad',
    TAB_MODULE_QUOTAS: 'Mapeo módulo → cuota',
    PLACEHOLDER_FOOTER:
      'Esta pestaña se completa en un PR posterior del plan de monetización.',
    PLACEHOLDER_ADDONS_TITLE: 'Add-ons',
    PLACEHOLDER_ADDONS_BODY:
      'Gestión completa de add-ons (compatibilidad, requisitos, entitlements) en la página dedicada. Pronto se integrará dentro de esta pestaña.',
    PLACEHOLDER_ADDONS_CTA: 'Abrir catálogo de add-ons',
    PLACEHOLDER_FEATURES_TITLE: 'Funcionalidades',
    PLACEHOLDER_FEATURES_BODY:
      'Listado plano de funcionalidades para gating con CapabilityService.',
    PLACEHOLDER_QUOTAS_TITLE: 'Cuotas',
    PLACEHOLDER_QUOTAS_BODY:
      'Dimensiones cuantitativas (usuarios, sucursales, almacenes, productos, e-CF/mes, …) con precios por tramo.',
    PLACEHOLDER_PRICES_TITLE: 'Precios',
    PLACEHOLDER_PRICES_BODY:
      'Tabla polimórfica de precios por moneda, periodo y vigencia. Sincroniza con Stripe / PayPal cuando se activa el motor de cobros.',
    PLACEHOLDER_PRICES_CTA: 'Abrir catálogo de precios',
    PLACEHOLDER_QUANTITY_SCALES_TITLE: 'Escalas de cantidad',
    PLACEHOLDER_QUANTITY_SCALES_BODY:
      'Tramos por cuota: ej. usuarios 1-5, 6-10, 11-20 con precio por tramo.',
    PLACEHOLDER_MODULE_FEATURES_TITLE: 'Módulo → Funcionalidad',
    PLACEHOLDER_MODULE_FEATURES_BODY:
      'Qué funcionalidades se desbloquean cuando un módulo entra en la suscripción.',
    PLACEHOLDER_MODULE_QUOTAS_TITLE: 'Módulo → Cuota',
    PLACEHOLDER_MODULE_QUOTAS_BODY:
      'Qué dimensiones cuantitativas habilita o aporta un módulo.',
  },
  DESKTOP_ENROLLMENT: {
    TITLE: 'Enrolar este terminal',
    SUBTITLE:
      'Este terminal aún no está registrado. Ingrese el código de enrolamiento emitido por el administrador.',
    CODE_FIELD: 'Código de enrolamiento',
    LABEL_FIELD: 'Nombre del terminal (opcional)',
    ENROLL_BUTTON: 'Enrolar terminal',
    OFFLINE_WARNING:
      'El enrolamiento requiere conexión a internet. Si no tiene conexión, conéctese antes de continuar.',
    LOGOUT_BUTTON: 'Salir',
    ERROR_INVALID_CODE: 'Código inválido. Verifique con el administrador.',
    ERROR_WRONG_BUSINESS:
      'Este código pertenece a otro negocio. Salga e inicie sesión con una cuenta del negocio correcto, o pida al administrador que emita un código para su negocio.',
    ERROR_WRONG_BRANCH:
      'Este código está asignado a otra sucursal. Solicite uno nuevo.',
    ERROR_EXPIRED:
      'El código ha expirado. Solicite uno nuevo al administrador.',
    ERROR_REVOKED: 'El código fue revocado. Solicite uno nuevo.',
    ERROR_EXHAUSTED: 'El código ya fue utilizado. Solicite uno nuevo.',
    ERROR_UNAUTHORIZED: 'Su sesión no tiene permiso para enrolar terminales.',
    ERROR_ALREADY_ENROLLED:
      'Este terminal ya está enrolado en este negocio. Pida al administrador que revoque el enrolamiento actual antes de usar un nuevo código.',
    ERROR_FINGERPRINT_CROSS_BUSINESS:
      'Este equipo aparece enrolado en otro negocio. Contacte al administrador del otro negocio o use un equipo distinto.',
    ERROR_NETWORK:
      'No se pudo contactar al servidor. Verifique su conexión y reintente.',
    ERROR_SERVER: 'Error del servidor. Intente nuevamente en unos minutos.',
    SESSION_CONTEXT_LABEL: 'Conectado como',
    SESSION_CONTEXT_SWITCH: 'Cambiar',
  },
  ECF_STATUS: {
    PROCESSING_TITLE: 'Procesando comprobante electrónico',
    PROCESSING_DESC: 'Generando y enviando e-CF a la DGII...',
    PENDING: 'Pendiente',
    ACCEPTED: 'Aceptado por DGII',
    REJECTED: 'Rechazado por DGII',
    ERROR: 'Error al procesar',
    IN_PROCESS: 'En proceso',
    ACCEPTED_CONDITIONAL: 'Aceptado con condiciones',
    TRACK_ID: 'Tracking ID',
    SECURITY_CODE: 'Código de seguridad',
    RETRY: 'Reintentar',
    TIMEOUT:
      'La verificación está tomando más tiempo del esperado. Puede consultar el estado luego.',
    FETCH_ERROR:
      'Error al consultar el estado del comprobante. Intente nuevamente.',
    ENCF: 'e-NCF',
    RESUBMIT_SUCCESS: 'e-CF reenviado exitosamente',
    RESUBMIT_ERROR: 'Error al reenviar e-CF',
    SIGNED: 'Firmado · Pendiente DGII',
    PENDING_RETRY: 'En cola para reintento',
    ANNULLED: 'Anulado',
    RETRY_CONFIRM_TITLE: 'Confirmar reintento e-CF',
    RETRY_CONFIRM_DESCRIPTION:
      'Antes de reintentar, verifica en el portal de la DGII que este comprobante no haya sido registrado. Reintentar sin verificar puede generar un envío duplicado. ¿Deseas continuar?',
    RETRY_CONFIRM_CTA: 'Sí, reintentar',
    REASSIGN_CONFIRM_TITLE: 'Confirmar reasignación de NCF',
    REASSIGN_CONFIRM_DESCRIPTION:
      'Antes de reasignar el NCF, verifica en el portal de la DGII que este comprobante no haya sido registrado con el NCF actual. Reasignar sin verificar puede generar duplicidad fiscal. ¿Deseas continuar?',
    REASSIGN_CONFIRM_CTA: 'Sí, reasignar',
    MARK_ACCEPTED_TITLE: 'Marcar e-CF como aceptado',
    MARK_ACCEPTED_DESCRIPTION:
      'Solo usa esta opción si ya verificaste en el portal de la DGII que este comprobante fue aceptado. Se registrará como aceptado con tu nota de auditoría y se detendrán los reintentos.',
    MARK_ACCEPTED_NOTE_LABEL: 'Nota de verificación (obligatorio)',
    MARK_ACCEPTED_NOTE_PLACEHOLDER:
      'Ej: Verificado en DGII el 15/07/2026 — comprobante aparece aceptado con TrackId 1234567',
    MARK_ACCEPTED_NOTE_REQUIRED:
      'Debes escribir una nota explicando la verificación.',
    MARK_ACCEPTED_CTA: 'Sí, marcar como aceptado',
    MARK_ACCEPTED_ACTION: 'Marcar como aceptado en DGII',
    MARK_ACCEPTED_SUCCESS: 'e-CF marcado como aceptado.',
    MARK_ACCEPTED_ERROR: 'No pudimos marcar el e-CF como aceptado.',
    RETRY_CONFIRM_MARK_ACCEPTED_HINT:
      '¿Ya verificaste en DGII que este comprobante fue aceptado?',
    RETRY_CONFIRM_MARK_ACCEPTED_LINK: 'Márcalo como aceptado sin reenviarlo',
  },
  ECF_CONFIG_BANNER: {
    MISSING_RNC_TITLE: 'RNC no configurado',
    MISSING_RNC_DESC:
      'Para emitir comprobantes electrónicos (e-CF) necesitas configurar el RNC de tu empresa. Ve a Configuración > Empresa para agregarlo.',
    MISSING_TAXES_TITLE: 'Impuestos no configurados',
    MISSING_TAXES_DESC:
      'No tienes impuestos configurados. Para generar comprobantes fiscales correctamente, necesitas configurar al menos el ITBIS (18%).',
    GO_TO_SETTINGS: 'Ir a Configuración',
    GO_TO_TAXES: 'Configurar Impuestos',
    MISSING_CERT_TITLE: 'Certificado DGII no válido',
    MISSING_CERT_DESC_ADMIN:
      'No hay un certificado DGII vigente para el ambiente configurado. Sube uno nuevo para continuar emitiendo comprobantes electrónicos.',
    MISSING_CERT_DESC_USER:
      'No hay un certificado DGII vigente para el ambiente configurado. Contacta al administrador para restaurar la emisión de comprobantes electrónicos.',
    GO_TO_CERTIFICATES: 'Ir a Certificados DGII',
  },
  RECEIVED_ECF: {
    TITLE: 'Comprobantes Recibidos',
    REGISTER: 'Registrar Comprobante',
    ISSUER_RNC: 'RNC Emisor',
    ISSUER_NAME: 'Nombre Emisor',
    ENCF: 'e-NCF',
    INVOICE_DATE: 'Fecha Emisión',
    TOTAL: 'Monto Total',
    SUBTOTAL: 'Subtotal',
    TAX: 'ITBIS',
    DESCRIPTION: 'Descripción',
    STATUS: 'Estado',
    PENDING: 'Pendiente',
    APPROVED: 'Aprobado',
    REJECTED: 'Rechazado',
    APPROVE: 'Aprobar',
    TAB_COMPLETED: 'Procesados',
    REJECT: 'Rechazar',
    DELETE: 'Eliminar',
    APPROVE_CONFIRM_TITLE: 'Aprobar Comprobante',
    APPROVE_CONFIRM_DESC:
      '¿Está seguro que desea aprobar este comprobante? Se enviará la aceptación comercial a la DGII.',
    REJECT_CONFIRM_TITLE: 'Rechazar Comprobante',
    REJECT_CONFIRM_DESC:
      '¿Está seguro que desea rechazar este comprobante? Se enviará el rechazo comercial a la DGII.',
    DELETE_CONFIRM_TITLE: 'Eliminar Comprobante',
    DELETE_CONFIRM_DESC:
      '¿Está seguro que desea eliminar este comprobante? Solo se pueden eliminar comprobantes pendientes.',
    SUCCESS_CREATED: 'Comprobante registrado exitosamente',
    SUCCESS_APPROVED: 'Comprobante aprobado exitosamente',
    SUCCESS_REJECTED: 'Comprobante rechazado exitosamente',
    SUCCESS_DELETED: 'Comprobante eliminado exitosamente',
    ERROR_APPROVE: 'Error al aprobar el comprobante',
    ERROR_REJECT: 'Error al rechazar el comprobante',
    FILTER_ALL: 'Todos',
    EMPTY: 'No hay comprobantes recibidos',
    EMPTY_DESC: 'Registre un comprobante recibido para comenzar',
    PROVIDER: 'Proveedor',
    DATE_FROM: 'Desde',
    DATE_TO: 'Hasta',
    TAB_RECEIVED: 'Recibidos',
    REJECTION_REASON: 'Motivo de rechazo',
    REJECTION_REASON_PLACEHOLDER: 'Ingrese el motivo del rechazo...',
    RECEIPT_STATUS: 'Acuse de Recibo',
    RECEIPT_PENDING: 'Sin acuse',
    RECEIPT_RECEIVED: 'Recibido',
    RECEIPT_NOT_RECEIVED: 'No recibido',
    ACKNOWLEDGE_RECEIPT: 'Acusar recibo',
    REJECT_RECEIPT: 'No recibido',
    ACKNOWLEDGE_CONFIRM_TITLE: 'Confirmar acuse de recibo',
    ACKNOWLEDGE_CONFIRM_DESC:
      'Se enviará un acuse de recibo (ARECF) a la DGII confirmando la recepción de este comprobante.',
    REJECT_RECEIPT_CONFIRM_TITLE: 'Rechazar recepción',
    REJECT_RECEIPT_CONFIRM_DESC:
      'Se enviará un acuse de recibo (ARECF) a la DGII indicando que este comprobante NO fue recibido.',
    SUCCESS_ACKNOWLEDGED: 'Acuse de recibo enviado exitosamente',
    SUCCESS_RECEIPT_REJECTED: 'Rechazo de recepción enviado exitosamente',
    ERROR_ACKNOWLEDGE: 'Error al enviar acuse de recibo',
    ERROR_REJECT_RECEIPT: 'Error al rechazar recepción',
    CERT_XLSX_LABEL:
      'FechaHoraAprobacionComercial (solo pruebas de certificación DGII)',
    CERT_XLSX_HELP:
      'Adjunta el XLSX de prueba de DGII (RNC-DDMMYYYYHHMMSS.xlsx) o escribe el valor manualmente en formato DD-MM-YYYY HH:mm:ss (24 h). Déjalo vacío en producción — se enviará la fecha/hora actual del servidor.',
    CERT_XLSX_PARSED: 'FechaHoraAprobacionComercial derivada: {{value}}',
    CERT_XLSX_INVALID_NAME:
      'El nombre del archivo no coincide con RNC-DDMMYYYYHHMMSS.xlsx. Escribe el valor manualmente.',
  },
  USER_PREFERENCES: {
    COLUMN_VISIBILITY: 'Visibilidad de columnas',
    SHOW_TAX_COLUMN: 'Impuesto',
    SHOW_DISCOUNT_COLUMN: 'Descuento',
    SHOW_SUBTOTAL_COLUMN: 'Subtotal',
    SHOW_TOTAL_COLUMN: 'Total',
  },
  FOLLOW_UP_REMINDERS: {
    TITLE: 'Recordatorios de Seguimiento',
    SUBTITLE: 'Gestiona el seguimiento a clientes después de una venta o cita',
    NEW_REMINDER: 'Nuevo recordatorio',
    NEW_REMINDER_DESC:
      'Crea un recordatorio para contactar a un cliente en una fecha futura',
    EMPTY: 'No hay recordatorios pendientes',
    STATUS_PENDING: 'Pendiente',
    STATUS_CONTACTED: 'Contactado',
    STATUS_DISMISSED: 'Descartado',
    SOURCE_INVOICE: 'Factura',
    SOURCE_MANUAL: 'Manual',
    CHANNEL_PHONE: 'Teléfono',
    CHANNEL_EMAIL: 'Email',
    CHANNEL_WHATSAPP: 'WhatsApp',
    COL_CUSTOMER: 'Cliente',
    COL_PRODUCT: 'Producto / Servicio',
    COL_SOURCE: 'Origen',
    COL_DUE: 'Vence',
    COL_STATUS: 'Estado',
    COL_CHANNEL: 'Canal',
    COL_ACTIONS: 'Acciones',
    OVERDUE: 'Vencido',
    DUE_TODAY: 'Vence hoy',
    NO_CUSTOMER: 'Cliente sin asignar',
    FILTER_STATUS: 'Estado',
    FILTER_SOURCE: 'Origen',
    FILTER_CHANNEL: 'Canal',
    FILTER_SEARCH: 'Buscar cliente, producto o título',
    FILTER_DUE_FROM: 'Vence desde',
    FILTER_DUE_TO: 'Vence hasta',
    MARK_CONTACTED: 'Marcar como contactado',
    MARK_CONTACTED_DESC:
      'Registra el canal usado y una nota opcional sobre la conversación',
    MARK_CONTACTED_CHANNEL: 'Canal de contacto',
    MARK_CONTACTED_NOTE: 'Nota (opcional)',
    DISMISS: 'Descartar',
    DISMISS_DESC: 'El recordatorio no aparecerá más en pendientes',
    DISMISS_REASON: 'Motivo (opcional)',
    CREATE_TITLE: 'Nuevo recordatorio',
    CREATE_DESC:
      'Programa un seguimiento para un cliente. Útil para citas y servicios recurrentes.',
    FIELD_CUSTOMER: 'Cliente',
    FIELD_PRODUCT: 'Producto / Servicio (opcional)',
    FIELD_TITLE: 'Título',
    FIELD_DUE_AT: 'Fecha y hora del recordatorio',
    FIELD_NOTE: 'Nota',
    FIELD_ALLOW_PAST_DUE: 'Permitir fecha en el pasado',
    REQUIRED_CUSTOMER: 'Selecciona un cliente',
    REQUIRED_DUE_AT: 'Selecciona una fecha',
    REQUIRES_REAL_CUSTOMER:
      'El recordatorio requiere un cliente específico. Selecciona un cliente o quita el recordatorio.',
    WHATSAPP_TEMPLATE:
      'Hola {customerName}! Es {businessName}. Queríamos saber si te gustaría agendar otra visita.',
    DASHBOARD_TITLE: 'Recordatorios de Seguimiento',
    DASHBOARD_PENDING: 'Pendientes',
    DASHBOARD_DUE_TODAY: 'Vencen hoy',
    DASHBOARD_OVERDUE: 'Vencidos',
    DASHBOARD_VIEW_ALL: 'Ver todos',
    SUCCESS_CREATED: 'Recordatorio creado',
    SUCCESS_CONTACTED: 'Recordatorio marcado como contactado',
    SUCCESS_DISMISSED: 'Recordatorio descartado',
    ERROR_GENERIC: 'Ocurrió un error',
  },
  BUSINESS_SETTINGS_FOLLOW_UP: {
    TITLE: 'Recordatorios de Seguimiento',
    DESCRIPTION:
      'Crea automáticamente un recordatorio para contactar al cliente después y asegurarte de que no se pierdan oportunidades de seguimiento.',
    ENABLED_LABEL: 'Activar recordatorios de seguimiento',
    DAYS_LABEL: 'Días después de la venta',
    DAYS_HINT:
      'Cantidad de días tras la factura cuando el recordatorio se vuelve pendiente (1-365)',
  },
  CONFIGURATION_BUSINESS: {
    FREE_INVOICING_LABEL: 'Habilitar facturación libre',
    FREE_INVOICING_DESCRIPTION:
      'Permite crear líneas de factura o cotización sin tener que registrar el producto primero. Útil para servicios o ventas ocasionales.',
    INCLUDE_ITEM_NOTES_ON_PRINT_LABEL:
      'Incluir notas por línea en la factura impresa y compartida',
    INCLUDE_ITEM_NOTES_ON_PRINT_DESCRIPTION:
      'Cuando está activado, las notas capturadas en cada línea (chip "+ Notas") se muestran debajo de la descripción en la factura impresa, PDF, correo y enlace compartido. Desactivado por defecto para que las notas internas del operador (cocina, empaque) no lleguen al cliente.',
    ITEM_NOTES_SURFACES_LABEL: 'Superficies con notas por línea permitidas',
    ITEM_NOTES_SURFACES_HELP:
      'Selecciona en qué superficies se muestra el chip "+ Notas" por línea (Facturación, Restaurante, POS). La impresión de esas notas se controla aparte con el interruptor de arriba.',
    OPERATIONAL_TRANSACTIONS_LABEL: 'Habilitar transacciones operacionales',
    OPERATIONAL_TRANSACTIONS_DESCRIPTION:
      'Permite clasificar ciertos tipos de documento como operacionales: se procesan normalmente (inventario, caja, reportes de ventas) pero NO se contabilizan (no afectan Mayor, Balance, Estado de Resultados ni reportes fiscales). Al activarlo se cargan valores por defecto razonables que puedes editar después.',
  },
  CLOCK_DRIFT: {
    TITLE: 'Reloj de tu computadora desviado por {{minutes}} min.',
    WARN_DESCRIPTION:
      'Verifica tu fecha y hora del sistema. Algunas funciones (facturación, sesión) pueden empezar a fallar.',
    BLOCK_DESCRIPTION:
      'Corrige tu fecha y hora antes de continuar. No se pueden crear facturas mientras el reloj esté desviado más de 15 minutos.',
    BLOCKED_SAVE:
      'No se puede guardar mientras el reloj de tu computadora esté desviado. Corrige la fecha y hora del sistema y vuelve a intentar.',
  },
  ACCOUNTING_HEALTH: {
    BANNER_TITLE: 'Hay {{count}} evento(s) de contabilidad detenido(s).',
    BANNER_DESCRIPTION:
      'Resuélvelos para que tus reportes contables queden completos.',
    BANNER_CTA: 'Revisar',
    PAGE_HINT_TAX_MAP:
      'Falta el mapeo contable para este impuesto. Selecciona la cuenta GL en la que se debe acreditar.',
    PAGE_HINT_CONFIG:
      'Falta una cuenta predeterminada en la Configuración Contable. Selecciónala para continuar.',
    PAGE_HINT_RULE:
      'No hay regla de contabilización activa para este tipo de evento.',
    PAGE_HINT_PERIOD:
      'No hay período contable abierto que cubra la fecha del evento. Abre el período correspondiente.',
    PAGE_HINT_FY:
      'El año fiscal está cerrado. Reabre el año fiscal antes de reintentar.',
    PAGE_HINT_GENERIC: 'Error técnico al procesar el evento.',
    PAGE_RETRY: 'Reintentar',
    PAGE_RETRY_GROUP: 'Reintentar todos los similares',
    PAGE_SAVE_AND_RETRY: 'Guardar y reintentar',
    PAGE_ATTEMPTS: 'Intentos',
    PAGE_LAST_ATTEMPT: 'Último intento',
    PAGE_LAST_ERROR: 'Último error',
    PAGE_OPEN_SOURCE: 'Abrir {{label}}',
    PAGE_PICK_ACCOUNT: 'Selecciona la cuenta',
    PAGE_RETRIED: 'Evento(s) reintentado(s). El cron los procesará en breve.',
    PAGE_RETRY_FAILED: 'No se pudo reintentar el evento.',
    PAGE_RULE_HELP:
      'No hay regla de contabilización activa para {{module}} ({{subtype}}). Inserta las reglas por defecto o crea una específica.',
    PAGE_RULE_HELP_STALE:
      'La regla de contabilización para {{module}} ({{subtype}}) ya existe. Este evento falló antes de que la regla estuviera activa — reinténtalo para procesarlo.',
    PAGE_RULE_SEED: 'Insertar reglas por defecto',
    PAGE_RULE_GO: 'Ir a Reglas Contables',
    PAGE_RULE_SEED_CTA: 'Instalar regla por defecto',
    PAGE_RULE_SEED_SUCCESS:
      'Reglas por defecto instaladas. El próximo intento debería procesarse automáticamente.',
    PAGE_RULE_SEED_NOOP:
      'Todas las reglas por defecto ya están instaladas para este negocio.',
    PAGE_RULE_SEED_FAILED:
      'No se pudieron instalar las reglas por defecto. Intenta manualmente desde Configuración → Contabilidad → Reglas Contables.',
    PAGE_RULE_SEEDED:
      'Reglas insertadas. Creadas: {{created}} · Existentes: {{skipped}}',
    RAW_NO_RULE:
      'No hay regla de contabilización activa para {{module}} ({{subtype}}).',
    RAW_NO_PERIOD: 'No hay período contable abierto que cubra {{date}}.',
    RAW_TAX_MAP_MISSING: 'Falta el mapeo contable del impuesto.',
    RAW_CONFIG_MISSING: 'Falta la cuenta predeterminada {{configKey}}.',
    RAW_FY_CLOSED: 'El año fiscal {{code}} está cerrado.',
    RULES_ACCOUNT_RESOLVER_UNKNOWN:
      'Resolución de cuenta desconocida en la regla. Revisa la línea afectada en la regla contable.',
    RULES_AMOUNT_NOT_NUMERIC:
      'El monto resuelto no es un número válido. Revisa el campo origen del payload.',
    RULES_AMOUNT_PATH_MISSING:
      'Falta la ruta JSON para el monto. Define `amountPath` en la línea de la regla.',
    RULES_AMOUNT_RESOLVER_UNKNOWN:
      'Resolución de monto desconocida en la regla. Revisa la línea afectada.',
    RULES_CONFIG_KEY_MISSING:
      'La línea requiere `configKey` pero no fue provisto.',
    RULES_CONFIG_MISSING:
      'Configuración contable inexistente para este negocio.',
    RULES_CONFIG_VALUE_MISSING:
      'La cuenta predeterminada no está configurada. Ve a Configuración contable.',
    RULES_FA_CATEGORY_ACCOUNT_MISSING:
      'La categoría de activo fijo no tiene cuenta contable asignada.',
    RULES_FA_CATEGORY_CONFIG_INVALID:
      'El `configKey` apuntado no es una columna válida de la categoría de activo fijo.',
    RULES_FA_CATEGORY_CONFIG_MISSING:
      'Falta `configKey` para resolver la cuenta de categoría de activo fijo.',
    RULES_FA_CATEGORY_NOT_FOUND:
      'No se encontró la categoría de activo fijo referenciada por el evento.',
    RULES_FA_CATEGORY_PATH_INVALID:
      'La ruta JSON no resolvió un UUID válido para la categoría del activo fijo. Verifica que el payload incluya `fixedAssetId` correcto.',
    RULES_FA_CATEGORY_PATH_MISSING:
      'Falta la ruta JSON para localizar la categoría del activo fijo.',
    RULES_FA_CATEGORY_UNASSIGNED:
      'El activo fijo no tiene categoría asignada. Asigna una desde Activos fijos.',
    RULES_FA_NOT_FOUND:
      'No se encontró el activo fijo referenciado por el evento.',
    RULES_JSONPATH_ACCOUNT_INVALID:
      'La ruta JSON para la cuenta no devolvió un UUID válido.',
    RULES_JSONPATH_ACCOUNT_MISSING:
      'Falta la ruta JSON para resolver la cuenta. Define `jsonPath` en la línea.',
    RULES_PAYMENT_TYPE_ACCOUNT_MISSING:
      'El método de pago no tiene cuenta contable asignada. Ve a Métodos de pago.',
    RULES_PAYMENT_TYPE_INVALID:
      'El método de pago referenciado no existe o está inactivo.',
    RULES_PAYMENT_TYPE_PATH_MISSING:
      'Falta la ruta JSON para localizar el método de pago.',
    RULES_STATIC_ACCOUNT_MISSING:
      'La línea estática no especifica `accountStaticId`.',
    RULES_STATIC_AMOUNT_MISSING:
      'La línea estática no especifica `amountStatic`.',
    RULES_SUM_PATH_MISSING:
      'La ruta JSON para sumar montos está vacía o es inválida.',
    RULES_TAX_ACCOUNT_MISSING:
      'Falta cuenta contable del impuesto. Mapea el impuesto desde Mapeo de cuentas.',
    RULES_TAX_ID_INVALID:
      'El identificador de impuesto en el payload no existe.',
    RULES_TAX_MAP_MISSING:
      'Falta el mapeo contable del impuesto. Ve a Mapeo de cuentas.',
    RULES_TAX_PATH_MISSING:
      'Falta la ruta JSON para localizar los impuestos en el payload.',
    RULES_TOO_FEW_LINES:
      'La regla necesita al menos dos líneas (débito + crédito).',
    RULES_UNBALANCED:
      'Los débitos y créditos resueltos no cuadran. Revisa los montos.',
    RULES_UNBALANCED_DETAIL: 'débitos {{debits}} vs créditos {{credits}}',
  },
  DESKTOP_OFFLINE: {
    OFFLINE: 'Sin conexión',
    OFFLINE_WITH_PENDING: 'Sin conexión · {{count}} venta(s) en cola',
    SYNCING: 'Sincronizando…',
    SYNC_PENDING: '{{count}} venta(s) por sincronizar',
    ONLINE: 'En línea',
    ONLINE_SYNCED: 'En línea · Sincronizado',
    CANCEL_REMOTE_TITLE: 'No se puede cancelar offline desde este terminal',
    CANCEL_REMOTE_BODY:
      'Esta factura no fue creada en este terminal o ya está en la nube. Para cancelarla, elige una opción:',
    CANCEL_REMOTE_OPTION_TERMINAL:
      'Pide al cajero del terminal donde se emitió la factura que la cancele desde allí.',
    CANCEL_REMOTE_OPTION_RECONNECT:
      'Conéctate a internet en este terminal para cancelarla directamente en la nube.',
    CANCEL_REMOTE_CLOSE: 'Entendido',
    REPRINT_REQUIRED_BADGE: 'Recibo desactualizado',
    REPRINT_REQUIRED_TOOLTIP:
      'El servidor reasignó el NCF al sincronizar. Reimprime y entrega el nuevo recibo al cliente.',
    REPRINT_DONE_TOAST: 'Marcado como reimpreso',
    QUEUE_TITLE: 'Cola sin sincronizar',
    QUEUE_PENDING_LABEL: 'pendientes',
    QUEUE_INVOICES: 'Facturas',
    QUEUE_CUSTOMERS: 'Clientes nuevos',
    QUEUE_STOCK: 'Movimientos de inventario',
    QUEUE_TABS: 'Cuentas de mesa',
    QUEUE_EMPTY: 'No hay nada pendiente. Todo sincronizado.',
    QUEUE_OPEN_BTN: 'Ver cola',
    NCF_LOW_TITLE: 'NCF por agotarse',
    NCF_LOW_BODY:
      'Quedan pocos comprobantes en la reserva local. Conéctate para reservar más antes de que se agoten.',
    SESSION_OFFLINE_BADGE: 'Sesión offline',
    SAVED_OFFLINE_TOAST: 'Guardado offline · se sincronizará al volver',
    LAN_PAIR_TITLE: 'Conectar a Caja Principal (LAN)',
    LAN_PAIR_HELP:
      'Detecta otros terminales POS en esta red para coordinar mesas y enviar facturas. Solo necesitas emparejar una vez.',
    LAN_DISCOVERED_LABEL: 'Cajas detectadas',
    LAN_SCAN_BTN: 'Buscar',
    LAN_SCANNING: 'Buscando…',
    LAN_NONE_FOUND: 'Ninguna caja detectada en esta red',
    LAN_PAIR_BTN: 'Emparejar',
    LAN_PAIRING: 'Emparejando…',
    LAN_PAIRED_BADGE: 'Emparejado',
    LAN_UNPAIR_BTN: 'Desemparejar',
    LAN_OPEN_DRAWER_BTN: 'LAN',
    TABLE_OWNED_BY: 'Atendida por {{terminal}}',
    // System announcements — sadmin + render strings
    ANNOUNCE_TITLE: 'Avisos del Sistema',
    ANNOUNCE_INTRO:
      'Mensajes y banderas que aplican a todas las apps. Úsalos para notificar mantenimiento, forzar modo offline o requerir actualizaciones.',
    ANNOUNCE_FORM_CREATE: 'Crear nuevo aviso',
    ANNOUNCE_FORM_EDIT: 'Editar aviso',
    ANNOUNCE_FIELD_MESSAGE_ES: 'Mensaje (español) *',
    ANNOUNCE_FIELD_MESSAGE_EN: 'Mensaje (inglés, opcional)',
    ANNOUNCE_FIELD_SEVERITY: 'Severidad',
    ANNOUNCE_FIELD_SCOPE: 'Alcance',
    ANNOUNCE_FIELD_ACTION: 'Acción',
    ANNOUNCE_FIELD_MIN_VERSION: 'Versión mínima',
    ANNOUNCE_FIELD_ACTIVE_FROM: 'Activo desde',
    ANNOUNCE_FIELD_ACTIVE_UNTIL: 'Activo hasta (opcional)',
    ANNOUNCE_FIELD_ROLLOUT: 'Rollout (%)',
    ANNOUNCE_ROLLOUT_HELP:
      '100 = todos los dispositivos. 10 = canary del 10%. Combina con "Negocios objetivo" para limitar la audiencia.',
    ANNOUNCE_FIELD_TARGET_BUSINESSES: 'Negocios objetivo',
    ANNOUNCE_TARGET_BUSINESSES_HELP:
      'Vacío = todos los negocios reciben el aviso. Selecciona negocios específicos para limitar la audiencia (combina con Rollout para canary por negocio).',
    ANNOUNCE_TARGET_BUSINESSES_ALL: 'Todos los negocios',
    ANNOUNCE_TARGET_BUSINESSES_PICKER_PLACEHOLDER: 'Buscar negocio…',
    ANNOUNCE_TARGET_BUSINESSES_SELECTED_COUNT: '{{count}} seleccionado(s)',
    ANNOUNCE_TARGET_BUSINESSES_CLEAR: 'Limpiar selección',
    ANNOUNCE_BTN_CANCEL: 'Cancelar',
    ANNOUNCE_BTN_CREATE: 'Crear aviso',
    ANNOUNCE_BTN_SAVE: 'Guardar cambios',
    ANNOUNCE_BTN_EDIT: 'Editar',
    ANNOUNCE_BTN_DELETE: 'Eliminar',
    ANNOUNCE_LIST_TITLE: 'Avisos existentes',
    ANNOUNCE_LOADING: 'Cargando…',
    ANNOUNCE_ACTIVE_RANGE_NO_END: 'sin fin',
    ANNOUNCE_TOAST_CREATED: 'Aviso creado',
    ANNOUNCE_TOAST_UPDATED: 'Aviso actualizado',
    ANNOUNCE_TOAST_DELETED: 'Aviso eliminado',
    ANNOUNCE_TOAST_MESSAGE_REQUIRED: 'El mensaje en español es obligatorio',
    ANNOUNCE_TOAST_CREATE_ERROR: 'No se pudo crear el aviso',
    ANNOUNCE_TOAST_UPDATE_ERROR: 'No se pudo actualizar',
    ANNOUNCE_GATE_TITLE: 'Actualización obligatoria',
    ANNOUNCE_GATE_MIN_VERSION_LABEL: 'Versión mínima requerida',
    ANNOUNCE_GATE_DRAINING:
      'Sincronizando datos pendientes antes de actualizar…',
    ANNOUNCE_GATE_PENDING:
      'Quedan {{count}} datos sin sincronizar. Conéctate a internet para terminar antes de actualizar.',
    ANNOUNCE_GATE_ERROR:
      'No se pudo iniciar la actualización. Reintenta en unos minutos.',
    ANNOUNCE_GATE_BTN_INSTALL: 'Sincronizar y actualizar ahora',
    ANNOUNCE_GATE_BTN_PROCESSING: 'Procesando…',
    ANNOUNCE_GATE_BTN_RETRY: 'Reintentar',
    ANNOUNCE_GATE_BROWSER_NOTE:
      'Esta versión del navegador necesita ser actualizada manualmente.',
    ANNOUNCE_LOGIN_BLOCKED_TITLE: 'Acceso temporalmente suspendido',
    ANNOUNCE_MIN_VERSION_BADGE: 'Versión mínima: {{version}}',
    ANNOUNCE_PENDING_LABEL: 'pendientes',
    ANNOUNCE_BANNER_DISMISS_OK: 'OK',
    ANNOUNCE_BANNER_DISMISS_LATER: 'Recordar más tarde',
    CIRCUIT_OPEN:
      'Sincronización pausada por errores repetidos. Intenta nuevamente o revisa tu conexión.',
    RETRY_NOW: 'Reintentar ahora',
    RETRYING: 'Reintentando…',
    LOGIN_DENIED_TRIAL:
      'El modo sin conexión no está disponible en cuentas de prueba. Actualiza a un plan pagado para activarlo.',
    LOGIN_DENIED_NO_SUB:
      'Tu negocio no tiene una suscripción activa. Actualiza tu plan para usar el modo sin conexión.',
    LOGIN_DENIED_EXPIRED:
      'La suscripción de tu negocio ha vencido. Renuévala para usar el modo sin conexión.',
    BOOTSTRAP_INCOMPLETE:
      'Sincronización inicial pendiente. Conéctate a internet para descargar la información necesaria.',
    CLOCK_BANNER_MESSAGE:
      '⚠ El reloj del equipo cambió. La actividad creada ahora quedará marcada para revisión. Conéctate a internet para revalidar antes del cierre de turno.',
    CLOCK_BANNER_CLOSE: 'Entendido',
    CLOCK_RECEIPT_FOOTER: '⚠ Hora del equipo no verificada',
    CLOCK_LOCK_TITLE: 'Reloj del equipo no válido',
    CLOCK_LOCK_BODY:
      'El reloj del sistema fue modificado o no se ha podido validar contra el servidor. Para evitar registros con fechas incorrectas, esta terminal queda bloqueada hasta que un administrador corrija la hora.',
    CLOCK_LOCK_INSTRUCTIONS:
      'Pasos: 1) Cierra esta sesión. 2) Solicita al administrador del local que ajuste la hora del sistema operativo. 3) Verifica que la terminal sincronice con el servidor. Una vez validado el reloj, podrás continuar.',
    CLOCK_LOCK_REASON_LABEL: 'Motivo',
    CLOCK_LOCK_LOGOUT: 'Cerrar sesión',
    CLOCK_TAMPER_PAGE_TITLE: 'Actividad con reloj sospechoso',
    CLOCK_TAMPER_PAGE_INTRO:
      'Lista de actividad (facturas, turnos, movimientos de inventario, etc.) creada en una terminal cuyo reloj del sistema cambió entre dos sincronizaciones. Revisar para confirmar fecha contable.',
    CLOCK_TAMPER_COLUMN_INVOICE: 'Factura',
    CLOCK_TAMPER_COLUMN_NCF: 'NCF',
    CLOCK_TAMPER_COLUMN_INVOICE_DATE: 'Fecha cliente',
    CLOCK_TAMPER_COLUMN_RECEIVED_AT: 'Recibida por servidor',
    CLOCK_TAMPER_COLUMN_DRIFT: 'Diferencia',
    CLOCK_TAMPER_COLUMN_TERMINAL: 'Terminal',
    CLOCK_TAMPER_EMPTY: 'Sin facturas marcadas — todo en orden.',
    CLOCK_EVENT_COLUMN_OBSERVED_AT: 'Observado',
    CLOCK_EVENT_COLUMN_KIND: 'Tipo',
    CLOCK_EVENT_COLUMN_BUSINESS: 'Negocio',
    CLOCK_EVENT_COLUMN_TERMINAL: 'Terminal',
    CLOCK_EVENT_COLUMN_SEVERITY: 'Severidad',
    CLOCK_EVENT_EMPTY:
      'Sin eventos registrados — ningún terminal reportó cambios de reloj.',
    CLOCK_EVENT_DETAIL_TITLE: 'Registros asociados',
    CLOCK_EVENT_DETAIL_PICK_HINT:
      'Selecciona un evento para ver las facturas, turnos y movimientos de inventario capturados durante esa ventana.',
    CLOCK_EVENT_DETAIL_WINDOW: 'Ventana',
    CLOCK_EVENT_RELATED_INVOICES: 'Facturas',
    CLOCK_EVENT_RELATED_SHIFTS: 'Turnos',
    CLOCK_EVENT_RELATED_STOCK_MOVEMENTS: 'Movimientos de inventario',
    CLOCK_EVENT_RELATED_NONE: 'Sin registros para este evento.',
    CLOCK_EVENT_ORPHANS_TITLE: 'Sin evento asociado',
    CLOCK_EVENT_ORPHANS_INTRO:
      'Registros con reloj sospechoso cuyo terminal nunca subió un evento del registro — útiles para auditar terminales que perdieron la conexión antes de reportar.',
    CLOCK_EVENT_ORPHANS_EMPTY: 'No hay registros huérfanos.',
    CLOCK_EVENT_SHIFT_OPEN: 'abierto',
    CLOCK_EVENT_SHIFT_CLOSED: 'cerrado',
    CLOCK_EVENT_PAGE_INDICATOR:
      'Página {{page}} de {{totalPages}} · {{total}} eventos',
    CLOCK_EVENT_PAGE_PREV: 'Anterior',
    CLOCK_EVENT_PAGE_NEXT: 'Siguiente',
    CLOCK_EVENT_OPEN_DETAIL: 'Ver registros',
    CLOCK_EVENT_OPEN_ORPHANS: 'Sin evento asociado',
    CLOCK_EVENT_CLOSE: 'Cerrar',
    CLOCK_EVENT_COLUMN_ACTIONS: 'Acciones',
    LOGIN_GATE_TITLE: 'Tienes cambios sin sincronizar',
    LOGIN_GATE_BODY:
      'Mientras estuviste sin conexión se guardaron cambios localmente. Sincroniza ahora o ve a la reconciliación para revisarlos antes de continuar.',
    LOGIN_GATE_COUNTS_LINE:
      'Pendientes: {{pending}} · Conflictos: {{conflicts}}',
    LOGIN_GATE_GO_RECONCILE: 'Ir a reconciliación',
    LOGIN_GATE_SYNC_NOW: 'Sincronizar ahora',
    LOGIN_GATE_DISMISS: 'Más tarde',
    LOGIN_GATE_SYNCING: 'Sincronizando…',
    SYNCING_PAGE_TITLE: 'Reconciliación de sincronización',
    SYNCING_PAGE_SUBTITLE:
      'Revisa los registros con conflictos contra el servidor y elige cómo resolverlos.',
    SYNCING_PAGE_MENU_LABEL: 'Sincronización',
    SYNCING_PAGE_EMPTY: 'No hay conflictos pendientes.',
    SYNCING_PAGE_COL_ENTITY: 'Tipo',
    SYNCING_PAGE_COL_LABEL: 'Registro',
    SYNCING_PAGE_COL_REVISIONS: 'Revisiones',
    SYNCING_PAGE_COL_UPDATED: 'Actualizado',
    SYNCING_PAGE_COL_ACTIONS: 'Acciones',
    SYNCING_PAGE_KEEP_LOCAL: 'Conservar local',
    SYNCING_PAGE_KEEP_SERVER: 'Conservar servidor',
    SYNCING_PAGE_FORK_NEW: 'Crear copia',
    SYNCING_PAGE_RESOLVED_TOAST: 'Conflicto resuelto.',
    SYNCING_PAGE_ERROR_TOAST: 'No se pudo resolver el conflicto.',
    SYNCING_PAGE_ENTITY_CUSTOMER: 'Cliente',
    SYNCING_PAGE_ENTITY_PROVIDER: 'Proveedor',
    SYNCING_PAGE_ENTITY_INVOICE: 'Factura',
    SYNCING_PAGE_REVISION_LOCAL: 'Local',
    SYNCING_PAGE_REVISION_SERVER: 'Servidor',
    SYNCING_PAGE_REFRESH: 'Actualizar',
    SYNCING_PAGE_PENDING_SUMMARY:
      'Pendientes de envío: {{pending}} · Conflictos: {{conflicts}}',
    SYNCING_PAGE_BROWSER_HINT:
      'Esta vista solo muestra conflictos cuando NeoGeniux corre dentro de la app de escritorio. En el navegador la lista siempre está vacía.',
  },
  MODULE_PAYWALL: {
    DEFAULT_PITCH:
      'Esta sección requiere un módulo o addon que tu plan actual no incluye. Agrégalo para desbloquear las herramientas de esta página.',
    PRICE_LABEL: 'Precio mensual',
    PER_MONTH_SHORT: 'mes',
    ADD_CTA: 'Agregar a mi plan',
    BILLING_NOTE:
      'El nuevo cargo se prorratea con tu próxima factura. Puedes cancelar en cualquier momento desde /account.',
    CONFIRM_TITLE: 'Confirmar adición al plan',
    CONFIRM_DESCRIPTION:
      'Vas a agregar "{{name}}" por {{amount}}/mes. Confirma para aplicar el cambio. El cargo se prorratea con tu próxima factura.',
    CONFIRM_CTA: 'Sí, agregar',
    SUCCESS_TOAST: 'Cambio aplicado. Recarga para ver el nuevo acceso.',
    ERROR_TOAST: 'No se pudo aplicar el cambio.',
  },
  QUOTA_PAYWALL: {
    UPGRADE_CTA: 'Mejorar plan',
    BILLING_NOTE:
      'Cambia de plan o agrega más usuarios desde /account. El cargo se prorratea con tu próxima factura.',
    USERS_TITLE: 'Más usuarios',
    USERS_PITCH:
      'Tu plan actual solo permite 1 usuario. Mejora tu plan para sumar al equipo y delegar operaciones.',
    USERS_FEATURE_TEAM:
      'Invita a tu equipo con accesos diferenciados por sucursal.',
    USERS_FEATURE_ROLES:
      'Crea roles personalizados (cajero, gerente, contador) con permisos a la medida.',
    USERS_FEATURE_AUDIT:
      'Audita quién creó, editó o eliminó cada factura, pago o producto.',
    ROLES_TITLE: 'Roles y permisos',
    ROLES_PITCH:
      'Tu plan actual solo permite 1 usuario, así que no hay roles para configurar. Mejora tu plan para crear roles, asignar permisos y delegar con confianza.',
    ROLES_FEATURE_PER_ROLE:
      'Define roles específicos para cajero, vendedor, gerente o contador.',
    ROLES_FEATURE_LEAST_PRIVILEGE:
      'Aplica el principio de menor privilegio: cada usuario ve solo lo que necesita.',
    ROLES_FEATURE_AUDIT:
      'Cumple controles internos con un registro auditable de cambios por rol.',
  },
  SADMIN_COUPON_GRANTS: {
    TAB_TITLE: 'Cupones recurrentes',
    SELECT_BUSINESS: 'Selecciona un negocio para gestionar sus cupones.',
    CREATE_TITLE: 'Otorgar cupón',
    CREATE_HINT:
      'Aplica un cupón a este negocio. El descuento se calcula sobre el subtotal actual y se congela en el momento del canje — los addons agregados después no lo aumentan.',
    COUPON_CODE: 'Código del cupón',
    APPLICATIONS_OVERRIDE: 'Aplicaciones máximas (opcional)',
    APPLICATIONS_HINT: 'Ilimitado si vacío',
    EXPIRES_AT: 'Expira el (opcional)',
    ACTIVE_TITLE: 'Cupones activos',
    HISTORY_TITLE: 'Historial',
    EMPTY_ACTIVE: 'Este negocio no tiene cupones activos.',
    COL_DISCOUNT: 'Descuento',
    COL_APPLIED: 'Aplicado',
    COL_REMAINING: 'Restantes',
    COL_EXPIRES: 'Expira',
    COL_STATUS: 'Estado',
    STATUS_REVOKED: 'Revocado',
    STATUS_EXHAUSTED: 'Agotado',
    REVOKE_TITLE: 'Revocar cupón',
    REVOKE_CONFIRM:
      'El cupón dejará de aplicarse en facturas futuras. Las facturas ya emitidas conservan el descuento.',
  },
  SADMIN_GRANTS: {
    TITLE: 'Cortesías y overrides',
    SUBTITLE:
      'Otorga módulos, addons, capacidades o ajustes de cuota a un negocio específico (Cortesía). Cada cambio queda registrado en la línea de tiempo de la suscripción.',
    BUSINESS_LABEL: 'Negocio',
    BUSINESS_PLACEHOLDER: 'Selecciona un negocio…',
    EMPTY_PICK_BUSINESS:
      'Selecciona un negocio para ver sus cortesías activas.',
    NO_GRANTS: 'Este negocio no tiene cortesías ni overrides activos.',
    GRANT_CTA: 'Otorgar',
    COL_KIND: 'Tipo',
    COL_TARGET: 'Código',
    COL_REASON: 'Razón',
    COL_COMP_VALUE: 'Valor cortesía',
    COL_SPECIAL_PRICE: 'Precio especial',
    COL_EXPIRES: 'Vence',
    COL_STATUS: 'Estado',
    STATUS_ACTIVE: 'Activa',
    STATUS_REVOKED: 'Revocada',
    STATUS_PERPETUAL: 'Para siempre',
    STATUS_REDUNDANT: 'No aplicada',
    STATUS_REDUNDANT_TOOLTIP:
      'El plan pagado ya incluye esta capacidad. La cortesía no otorga acceso adicional.',
    DIALOG_TITLE: 'Otorgar cortesía',
    DIALOG_DESCRIPTION:
      'Esto crea un override en la suscripción + (para módulos/addons) lo refleja como Cortesía en /account del negocio.',
    FIELD_KIND: 'Tipo',
    FIELD_TARGET_CODE: 'Código del módulo / addon / feature',
    FIELD_TARGET_PLACEHOLDER: 'Busca por nombre o código…',
    FIELD_PRICE_CURRENT: 'Precio actual del catálogo: RD${{amount}}/mes',
    FIELD_PRICE_NONE:
      'Esta capacidad no tiene precio directo (se desbloquea con el módulo/addon padre).',
    FIELD_QUANTITY: 'Cantidad (solo para cuotas)',
    FIELD_REASON: 'Razón',
    FIELD_EXPIRES_AT: 'Vence el',
    FIELD_COMP_VALUE: 'Valor cortesía (RD$)',
    FIELD_PERPETUAL: 'Para siempre',
    FIELD_PERPETUAL_HELP:
      'La cortesía no vence. Ignora la fecha de vencimiento.',
    FIELD_SPECIAL_PRICE: 'Precio especial (RD$/unidad)',
    FIELD_SPECIAL_PRICE_HELP:
      'Si se establece, las unidades de esta cortesía se cobran a este precio en lugar del plan.',
    SAVE_GRANT: 'Otorgar',
    GRANTED: 'Cortesía otorgada.',
    REVOKED: 'Cortesía revocada.',
    REVOKE_CONFIRM_TITLE: 'Revocar cortesía',
    REVOKE_CONFIRM_DESCRIPTION:
      '¿Confirmas que deseas revocar esta cortesía? Si era un módulo o addon, también se quitará del /account del negocio.',
    ERR_PICK_BUSINESS: 'Selecciona un negocio primero.',
    ERR_TARGET_REQUIRED: 'Debes indicar el código a otorgar.',
    ERR_GRANT_FAILED: 'No se pudo otorgar la cortesía.',
    ERR_REVOKE_FAILED: 'No se pudo revocar la cortesía.',
    CONVERT_PAID_CTA: 'Convertir ítem pagado en Cortesía',
    CONVERT_PAID_TITLE: 'Convertir a Cortesía',
    CONVERT_PAID_DESCRIPTION:
      'Selecciona un ítem que el negocio esté pagando actualmente. Se marcará como Cortesía y dejará de facturarse en la próxima renovación. Es reversible.',
    CONVERT_PAID_PICK_ITEM: 'Elige el ítem pagado',
    CONVERT_PAID_CONFIRM: 'Convertir en Cortesía',
    CONVERT_PAID_SUCCESS: 'Ítem convertido en Cortesía.',
  },
  backend: {
    RESTAURANT_ENVIRONMENT_HAS_OPEN_ACCOUNT:
      'No se puede eliminar el ambiente: al menos una mesa tiene una cuenta abierta. Cierra las cuentas antes de eliminar el ambiente.',
    RESTAURANT_TABLE_HAS_OPEN_ACCOUNT:
      'No se puede eliminar la mesa: tiene una cuenta abierta u órdenes pendientes. Cierra la cuenta antes de eliminar la mesa.',
  },
  RESET_INVENTORY: {
    SECTION_TITLE: 'Zona de peligro',
    SECTION_DESCRIPTION:
      'Restablece el catálogo del negocio (o de una sucursal específica) como si estuviera recién creado. Los productos, categorías, almacenes y suplidores actuales se archivan, pero toda la historia (facturas, pagos, movimientos, contabilidad) se conserva intacta.',
    ACTION_BUTTON: 'Restablecer inventario',
    DIALOG_TITLE: 'Restablecer inventario',
    DIALOG_INTRO:
      'Esta acción es irreversible desde la aplicación. Elige la sucursal sobre la que quieres actuar. La información histórica siempre se conserva intacta.',
    BRANCH_PICKER_LABEL: 'Sucursal a restablecer',
    BRANCH_PICKER_HELP:
      'La casa matriz gestiona el catálogo compartido; una sucursal en modo copia solo afecta sus propios productos.',
    BRANCH_HQ_LABEL: 'Casa matriz',
    BRANCH_SHARED_TAG: 'inventario compartido',
    BRANCH_COPY_TAG: 'inventario copiado',
    BRANCH_EMPTY_TAG: 'inventario propio',
    MODE_HQ_TITLE: 'Reinicio de la casa matriz',
    MODE_HQ_DESCRIPTION:
      'Se archivarán todos los productos, categorías, almacenes y suplidores propiedad de la casa matriz. Las sucursales en modo copia mantienen su catálogo independiente.',
    MODE_HQ_SHARED_BRANCHES_HEADER:
      'Sucursales que también quedarán sin inventario (usan el inventario compartido)',
    MODE_HQ_NO_SHARED_BRANCHES:
      'No hay sucursales usando inventario compartido — solo se afecta la casa matriz.',
    MODE_COPY_TITLE: 'Reinicio de sucursal (modo copia)',
    MODE_COPY_DESCRIPTION:
      'Se archivarán únicamente los productos, categorías, almacenes y suplidores propios de esta sucursal. La casa matriz y las demás sucursales no se ven afectadas.',
    MODE_SHARED_TITLE: 'Sucursal en modo compartido',
    MODE_SHARED_DESCRIPTION:
      'Esta sucursal lee el catálogo de la casa matriz. Antes de reiniciar tienes dos opciones:',
    MODE_SHARED_COPY_FIRST_TITLE: 'Opción 1 · Copiar primero',
    MODE_SHARED_COPY_FIRST_DESCRIPTION:
      'Duplica el catálogo actual de la casa matriz en esta sucursal para que quede en modo copia con su propia información. Luego puedes reiniciar solo esta sucursal sin afectar la casa matriz.',
    MODE_SHARED_COPY_FIRST_ACTION: 'Copiar inventario de la casa matriz',
    MODE_SHARED_COPY_FIRST_SUCCESS:
      'Catálogo copiado. La sucursal ahora está en modo copia; puedes reiniciarla sin afectar a otras sucursales.',
    MODE_SHARED_COPY_FIRST_ERROR:
      'No se pudo copiar el catálogo. Intenta de nuevo.',
    MODE_SHARED_DETACH_TITLE: 'Opción 2 · Desconectar y comenzar vacío',
    MODE_SHARED_DETACH_DESCRIPTION:
      'La sucursal se desconecta del inventario compartido y arranca con catálogo vacío. La casa matriz no se ve afectada. Esta opción es irreversible.',
    WIPES_HEADER: 'Se archivarán',
    WIPES_PRODUCTS: 'Productos y sus variantes',
    WIPES_CATEGORIES: 'Categorías',
    WIPES_WAREHOUSES: 'Almacenes',
    WIPES_SUPPLIERS: 'Suplidores / proveedores',
    WIPES_OPEN_COUNTS: 'Conteos físicos en progreso',
    KEEPS_HEADER: 'Se conservan',
    KEEPS_INVOICES: 'Facturas y notas fiscales',
    KEEPS_PAYMENTS: 'Pagos recibidos y realizados',
    KEEPS_STOCK_MOVEMENTS: 'Movimientos históricos de inventario',
    KEEPS_ACCOUNTING: 'Asientos contables y reportes',
    DESTRUCTIVE_TOGGLE_LABEL: 'Eliminar completamente los productos',
    DESTRUCTIVE_TOGGLE_HINT:
      'Marca esta casilla si además de archivar quieres BORRAR físicamente los productos y categorías. Los almacenes y suplidores se conservan porque son infraestructura del negocio. Las facturas históricas y movimientos de inventario se conservan intactos con nombre/SKU/código de barras congelados. Esta opción es completamente irreversible.',
    CONFIRMATION_LABEL: 'Escribe RESET para confirmar',
    CONFIRMATION_HELP:
      'Debes escribir la palabra RESET (mayúsculas) exactamente para habilitar el botón.',
    CONFIRMATION_MISMATCH: 'La palabra debe ser RESET en mayúsculas.',
    SUBMIT: 'Restablecer inventario',
    SUBMITTING: 'Restableciendo…',
    CANCEL: 'Cancelar',
    SUCCESS_TOAST_HQ:
      'Casa matriz restablecida. Las sucursales que usaban el inventario compartido también aparecen vacías.',
    SUCCESS_TOAST_COPY_BRANCH:
      'Sucursal restablecida. La casa matriz y las demás sucursales no se ven afectadas.',
    SUCCESS_TOAST_DETACH:
      'Sucursal desconectada del inventario compartido. Ahora tiene su propio catálogo vacío.',
    ERROR_TOAST: 'No pudimos restablecer el inventario. Intenta de nuevo.',
    NO_PERMISSION:
      'No tienes permisos para restablecer el inventario. Contacta a un administrador.',
  },
  RESET_OPERATIONS: {
    SECTION_TITLE: 'Eliminar operaciones del negocio',
    SECTION_DESCRIPTION:
      'Borra toda la operación transaccional del negocio (facturas, pagos, órdenes de compra, turnos, contabilidad, historial de inventario y suscripción) dejando la configuración intacta. Úsalo cuando quieras comenzar de cero conservando tu catálogo y clientes.',
    ACTION_BUTTON: 'Eliminar operaciones',
    DIALOG_TITLE: 'Eliminar operaciones del negocio',
    DIALOG_INTRO:
      'Esta acción borra todo el historial transaccional del negocio y no se puede deshacer desde la aplicación. Antes de continuar te recomendamos descargar un respaldo (backup) de la base de datos. Lee con atención qué se elimina y qué se conserva.',
    IRREVERSIBLE_WARNING:
      '⚠️ Acción irreversible. Se borran facturas, pagos y contabilidad del negocio. Solicita un respaldo antes de continuar.',
    WIPES_HEADER: 'Se eliminarán',
    WIPES_INVOICES:
      'Facturas, cotizaciones, notas de crédito y todos sus renglones e impuestos',
    WIPES_PAYMENTS: 'Pagos recibidos, aplicaciones y anticipos',
    WIPES_REFUNDS: 'Reembolsos y devoluciones',
    WIPES_CASH_VOUCHERS: 'Vales de caja y créditos de cliente',
    WIPES_PURCHASE_ORDERS:
      'Órdenes de compra (fiscales y operacionales) con sus renglones',
    WIPES_SHIFTS: 'Turnos de caja y sus reconciliaciones',
    WIPES_STOCK_MOVEMENTS:
      'Movimientos de inventario (excepto balances iniciales)',
    WIPES_ACCOUNTING:
      'Asientos contables, subledger AR/AP, conciliaciones bancarias, activos fijos y gastos',
    WIPES_ECF_SUBMISSIONS:
      'Envíos e-CF salientes (los comprobantes recibidos para el 606 se conservan)',
    WIPES_ACTIVITY:
      'Registro de actividad, auditoría, sincronización y trabajos de importación',
    WIPES_SUBSCRIPTION_HISTORY:
      'Historial de pagos y eventos de la suscripción (la suscripción activa se conserva)',
    KEEPS_HEADER: 'Se conservan',
    KEEPS_CATALOG: 'Productos, categorías, almacenes y modificadores',
    KEEPS_CUSTOMERS: 'Clientes y sus datos fiscales',
    KEEPS_PROVIDERS: 'Suplidores / proveedores',
    KEEPS_TAXES: 'Impuestos, promociones y precios configurados',
    KEEPS_SEQUENCES:
      'Secuencias NCF, secuencias de facturación y certificados DGII',
    KEEPS_SUBSCRIPTION:
      'Suscripción activa y plan contratado (solo se limpia el historial de eventos)',
    KEEPS_USERS_ROLES: 'Usuarios, roles y permisos',
    KEEPS_BRANCHES: 'Sucursales y configuración por sucursal',
    KEEPS_INITIAL_STOCK:
      'Balances iniciales de inventario (para no perder el punto de partida)',
    CONFIRMATION_KEYWORD: 'RESET',
    CONFIRMATION_LABEL: 'Escribe RESET para confirmar la eliminación',
    CONFIRMATION_HELP:
      'Debes escribir RESET (mayúsculas) exactamente para habilitar el botón.',
    CONFIRMATION_MISMATCH: 'La palabra debe ser RESET en mayúsculas.',
    SUBMIT: 'Eliminar operaciones',
    SUBMITTING: 'Eliminando…',
    CANCEL: 'Cancelar',
    SUCCESS_TOAST:
      'Operaciones eliminadas correctamente. El negocio quedó listo para comenzar de cero conservando la configuración.',
    ERROR_TOAST:
      'No pudimos eliminar las operaciones. Intenta de nuevo o contacta a soporte si el problema persiste.',
    NO_PERMISSION:
      'No tienes permisos para eliminar las operaciones del negocio. Contacta a un administrador.',
  },
  CONTACTS_MERGE: {
    TITLE_CUSTOMERS: 'Combinar clientes',
    TITLE_PROVIDERS: 'Combinar proveedores',
    BUTTON: 'Combinar contactos',
    ROW_ACTION: 'Combinar',
    PICK_A: 'Primer contacto',
    PICK_B: 'Segundo contacto',
    SURVIVOR: 'Contacto que se conserva',
    SURVIVOR_HELP:
      'El otro contacto se elimina de forma permanente y todo su historial se reasigna al conservado.',
    FIELD: 'Campo',
    CREDIT_NOTE: 'El saldo a favor de ambos se sumará al contacto conservado.',
    EMPTY: '—',
    CONFIRM_TITLE: '¿Combinar contactos?',
    CONFIRM_DESC:
      'Esto reasigna todo el historial de «{{loser}}» a «{{winner}}» y elimina «{{loser}}» de forma permanente. No se puede deshacer.',
    CONFIRM_OK: 'Combinar',
    SUCCESS: 'Contactos combinados correctamente.',
  },
  CONTACTS_DELETE: {
    PENDING_INVOICES: 'factura(s)',
    PENDING_REFUNDS: 'reembolso(s)',
    PENDING_SALES_ORDERS: 'orden(es) de venta',
    PENDING_CREDIT_TRANSACTIONS: 'movimiento(s) de crédito',
    PENDING_OPEN_TABS: 'cuenta(s) abierta(s)',
    PENDING_STORE_CREDIT: 'saldo a favor pendiente',
    PENDING_PURCHASE_ORDERS: 'orden(es) de compra',
    PENDING_PROCUREMENT_ORDERS: 'orden(es) de aprovisionamiento',
    PENDING_EXPENSES: 'gasto(s)',
    PENDING_PAYMENTS: 'pago(s)',
    PENDING_TAX_DOCUMENTS: 'comprobante(s) fiscal(es)',
  },
  BULK_PRICE_UPDATE: {
    TAB_LABEL: 'Actualización masiva',
    TAB_DESCRIPTION:
      'Sube o baja precios de venta a muchos productos a la vez — por categoría, por selección o a todo el catálogo.',
    STEP_FILTER_TITLE: '1. Selecciona productos',
    STEP_FILTER_DESCRIPTION:
      '¿A qué productos aplicar la actualización de precio?',
    STEP_OPERATION_TITLE: '2. Elige la operación',
    STEP_OPERATION_DESCRIPTION:
      'Define cómo se ajustará el precio de venta de los productos seleccionados.',
    STEP_PREVIEW_TITLE: '3. Revisa la vista previa',
    STEP_PREVIEW_DESCRIPTION:
      'Confirma la cantidad de productos afectados y una muestra de precios antes de aplicar.',
    FILTER_TYPE_ALL: 'Todo el catálogo',
    FILTER_TYPE_ALL_DESCRIPTION:
      'Se aplicará a todos los productos activos del negocio.',
    FILTER_TYPE_CATEGORIES: 'Por categorías',
    FILTER_TYPE_CATEGORIES_DESCRIPTION:
      'Ej. subir 5% a “Cervezas” y “Refrescos”.',
    FILTER_TYPE_PRODUCTS: 'Productos específicos',
    FILTER_TYPE_PRODUCTS_DESCRIPTION:
      'Selecciona uno o varios productos por nombre o SKU.',
    FILTER_TYPE_WAREHOUSES: 'Por almacén',
    FILTER_TYPE_WAREHOUSES_DESCRIPTION:
      'Ej. subir 5% a todo lo del almacén “Nevera”.',
    SELECT_CATEGORIES_PLACEHOLDER: 'Elige una o más categorías…',
    SELECT_PRODUCTS_PLACEHOLDER: 'Elige uno o más productos…',
    SELECT_WAREHOUSES_PLACEHOLDER: 'Elige uno o más almacenes…',
    OPERATION_INCREASE_PERCENT: 'Aumentar por porcentaje (%)',
    OPERATION_DECREASE_PERCENT: 'Disminuir por porcentaje (%)',
    OPERATION_INCREASE_FIXED: 'Aumentar valor fijo (RD$)',
    OPERATION_DECREASE_FIXED: 'Disminuir valor fijo (RD$)',
    OPERATION_SET_PRICE: 'Fijar precio (RD$)',
    OPERATION_UPDATE_WAREHOUSE: 'Actualizar almacén',
    OPERATION_VALUE_LABEL: 'Valor',
    OPERATION_VALUE_PLACEHOLDER: '0.00',
    OPERATION_WAREHOUSE_LABEL: 'Almacén destino',
    OPERATION_WAREHOUSE_PLACEHOLDER: 'Elige el almacén…',
    OPERATION_WAREHOUSE_HELP:
      'Todos los productos que coincidan con el filtro serán reubicados en el almacén seleccionado.',
    PREVIEW_AFFECTED: 'Productos afectados',
    PREVIEW_AVERAGE_BEFORE: 'Precio promedio actual',
    PREVIEW_AVERAGE_AFTER: 'Precio promedio nuevo',
    PREVIEW_SAMPLE_HEADER: 'Muestra ({{count}} de {{total}})',
    PREVIEW_SAMPLE_COL_PRODUCT: 'Producto',
    PREVIEW_SAMPLE_COL_SKU: 'SKU',
    PREVIEW_SAMPLE_COL_CURRENT: 'Precio actual',
    PREVIEW_SAMPLE_COL_NEW: 'Precio nuevo',
    PREVIEW_NO_PRODUCTS: 'Ningún producto coincide con el filtro seleccionado.',
    PREVIEW_LOADING: 'Calculando vista previa…',
    PREVIEW_SEARCH_PLACEHOLDER: 'Buscar por nombre o SKU…',
    PREVIEW_PAGE_OF: 'Página {{page}} de {{totalPages}}',
    NEXT: 'Siguiente',
    BACK: 'Atrás',
    RUN: 'Aplicar cambios',
    RUNNING: 'Aplicando…',
    CONFIRM_TITLE: '¿Aplicar la actualización masiva?',
    CONFIRM_DESCRIPTION:
      'Se actualizará el precio de venta de {{count}} productos. Esta acción no se puede deshacer.',
    CONFIRM_WAREHOUSE_DESCRIPTION:
      'Los productos que coincidan con el filtro se moverán al almacén seleccionado. Esta acción no se puede deshacer.',
    CONFIRM_CTA: 'Sí, aplicar',
    CANCEL: 'Cancelar',
    SUCCESS_TOAST: 'Se actualizaron {{count}} productos correctamente.',
    SUCCESS_WAREHOUSE_TOAST:
      'Se reubicaron {{count}} productos en el nuevo almacén.',
    ERROR_TOAST:
      'No pudimos actualizar los precios. Verifica los datos e intenta nuevamente.',
    NO_PERMISSION:
      'No tienes permisos para actualizar precios masivamente. Contacta a un administrador.',
    VALIDATION_VALUE_REQUIRED: 'Ingresa un valor.',
    VALIDATION_VALUE_NON_NEGATIVE: 'El valor debe ser mayor o igual a 0.',
    VALIDATION_WAREHOUSE_REQUIRED: 'Selecciona un almacén destino.',
    VALIDATION_SELECT_AT_LEAST_ONE: 'Selecciona al menos un elemento.',
  },
  BULK_UPDATE: {
    OPERATION_UPDATE_TAX: 'Actualizar Impuesto',
    OPERATION_UPDATE_UNIT_OF_MEASURE: 'Unidad de medida',
    OPERATION_UPDATE_PROVIDER: 'Proveedor',
    OPERATION_UPDATE_CATEGORY: 'Categoría',
    OPERATION_UPDATE_TAX_INCLUDED: 'Precio incluye impuesto',
    OPERATION_UPDATE_INCLUDED_TAXES: 'Impuestos adicionales incluidos',
    INCLUDED_TAXES_BULK_HELP:
      'Reemplaza los impuestos incluidos en el precio de los productos seleccionados (solo los que ya tienen "precio incluye impuesto"). Vacío los elimina.',
    OPERATION_UPDATE_MIN_STOCK: 'Stock Mínimo',
    OPERATION_UPDATE_MAX_STOCK: 'Stock Máximo',
    OPERATION_UPDATE_REORDER_POINT: 'Punto de Reorden',
    LABEL_TAX: 'Nuevo tipo de impuesto',
    LABEL_UNIT_OF_MEASURE: 'Nueva unidad de medida',
    LABEL_PROVIDER: 'Nuevo proveedor',
    LABEL_CATEGORY: 'Nueva categoría',
    LABEL_TAX_INCLUDED: 'El precio incluye impuesto',
    LABEL_MIN_STOCK: 'Nuevo stock mínimo',
    LABEL_MAX_STOCK: 'Nuevo stock máximo',
    LABEL_REORDER_POINT: 'Nuevo punto de reorden',
    HELP_TAX:
      'El nuevo impuesto se aplicará a todos los productos seleccionados y se estampará junto con su tasa DGII.',
    LABEL_TAX_MODE: 'Modo de aplicación',
    TAX_MODE_KEEP_BASE: 'Mantener la base (subir el precio si es necesario)',
    TAX_MODE_KEEP_BASE_HINT:
      'En productos con "precio incluye impuesto" se recalcula el precio de venta para que la base (lo que gana el negocio) se mantenga y el cliente pague el nuevo total.',
    TAX_MODE_KEEP_GROSS: 'Mantener el total al cliente (el negocio absorbe)',
    TAX_MODE_KEEP_GROSS_HINT:
      'El precio de venta no cambia. En productos con "precio incluye impuesto" la base baja para acomodar la nueva tasa; en productos sin impuesto incluido se recalcula el precio para que el total al cliente no cambie.',
    HELP_TAX_INCLUDED:
      'Cuando está activado, el precio de venta del producto ya incluye el impuesto.',
    HELP_PROVIDER:
      'El nombre del proveedor se copiará en cada producto (snapshot).',
    PLACEHOLDER_SELECT_TAX: 'Selecciona un impuesto',
    PLACEHOLDER_SELECT_UNIT_OF_MEASURE: 'Selecciona una unidad',
    PLACEHOLDER_SELECT_CATEGORY: 'Selecciona una categoría',
    CONFIRM_DESCRIPTION:
      'Vas a actualizar {{count}} productos. Esta acción no se puede deshacer.',
    SUCCESS_TOAST: 'Se actualizaron {{count}} productos.',
    ERROR_TOAST:
      'No pudimos actualizar los productos. Verifica los datos e intenta nuevamente.',
    NO_PERMISSION:
      'No tienes permisos para actualizar productos masivamente. Contacta a un administrador.',
    VALIDATION_REQUIRED: 'Selecciona un valor.',
    VALIDATION_NON_NEGATIVE: 'El valor debe ser mayor o igual a 0.',
    PREVIEW_SAMPLE_COL_CURRENT: 'Valor actual',
    PREVIEW_SAMPLE_COL_NEW: 'Nuevo valor',
  },
  EDIT_PRODUCT_HISTORY: {
    SECTION_TITLE: 'Historial del producto',
    SECTION_SUBTITLE:
      'Cambios de precio, movimientos de inventario, ventas y compras registrados para este producto.',
  },
  BARCODE_LABEL: {
    PAGE_TITLE: 'Etiquetas de código de barras',
    PAGE_SUBTITLE:
      'Diseña cómo se imprimen las etiquetas de precio con código de barras.',
    CONFIG_CARD_TITLE: 'Etiquetas de código de barras',
    CONFIG_CARD_DESCRIPTION:
      'Diseña e imprime etiquetas de precio con código de barras para tus productos.',
    CONTENT_SECTION_TITLE: 'Contenido',
    SHOW_NAME: 'Mostrar nombre',
    SHOW_PRICE: 'Mostrar precio',
    SHOW_SKU: 'Mostrar SKU',
    SHOW_BARCODE: 'Mostrar código de barras',
    SHOW_BUSINESS_NAME: 'Mostrar nombre del negocio',
    SHOW_DATE: 'Mostrar fecha',
    FIELDS_TITLE: 'Campos',
    FIELDS_HINT:
      'Marca los datos a incluir, luego arrástralos en la vista previa.',
    ELEMENT_SETTINGS: 'Ajustes del campo',
    SELECT_FIELD_HINT: 'Selecciona un campo para ajustar tamaño y alineación.',
    DRAG_HINT: 'Arrastra cada dato para colocarlo donde quieras.',
    FONT_SIZE: 'Tamaño de letra',
    ALIGN: 'Alineación',
    ALIGN_LEFT: 'Izquierda',
    ALIGN_CENTER: 'Centro',
    ALIGN_RIGHT: 'Derecha',
    BOLD: 'Negrita',
    FIELD_NAME: 'Nombre',
    FIELD_PRICE: 'Precio',
    FIELD_SKU: 'SKU',
    FIELD_BARCODE: 'Código de barras',
    FIELD_BUSINESS_NAME: 'Nombre del negocio',
    FIELD_DATE: 'Fecha',
    FIELD_DESCRIPTION: 'Descripción',
    FIELD_BRAND: 'Marca',
    FIELD_CATEGORY: 'Categoría',
    FIELD_UNIT: 'Unidad de medida',
    FIELD_WEIGHT: 'Peso',
    FIELD_COLOR: 'Color',
    FIELD_SIZE: 'Tamaño',
    LAYOUT_SECTION_TITLE: 'Tamaño de etiqueta',
    LABEL_SIZE: 'Tamaño',
    SIZE_30X20: '30 × 20 mm',
    SIZE_40X30: '40 × 30 mm',
    SIZE_50X30: '50 × 30 mm',
    SIZE_A4_SHEET: 'Hoja A4 (cuadrícula)',
    BARCODE_SECTION_TITLE: 'Código de barras',
    SYMBOLOGY: 'Tipo de código',
    SYMBOLOGY_CODE128: 'Code 128',
    SYMBOLOGY_EAN13: 'EAN-13',
    SYMBOLOGY_QR: 'Código QR',
    BARCODE_SOURCE: 'Dato del código',
    SOURCE_BARCODE: 'Código de barras del producto',
    SOURCE_SKU: 'SKU del producto',
    PREVIEW_TITLE: 'Vista previa',
    PREVIEW_SAMPLE_NAME: 'Producto de ejemplo',
    SAVE: 'Guardar diseño',
    SAVED_TOAST: 'Diseño de etiqueta guardado.',
    SAVE_ERROR_TOAST: 'No se pudo guardar el diseño de etiqueta.',
    PRINT_TITLE: 'Imprimir etiquetas',
    PRINT_SUBTITLE:
      'Selecciona productos o categorías e imprime sus etiquetas de precio.',
    OPEN_DRAWER: 'Etiquetas',
    STEP_DESIGN_TITLE: '3. Diseño (opcional)',
    DESIGN_HINT:
      'Vista previa con un producto real. Selecciona una plantilla, arrastra los elementos y ajusta el tamaño de fuente; los cambios aquí aplican solo a esta impresión salvo que los guardes en la plantilla.',
    SAVE_DESIGN: 'Guardar diseño',
    DEFAULT_TEMPLATE_NAME: 'Predeterminada',
    TEMPLATES_SECTION_TITLE: 'Plantillas',
    TEMPLATE_SELECT_LABEL: 'Plantilla',
    TEMPLATE_NAME_LABEL: 'Nombre',
    NEW_TEMPLATE: 'Nueva',
    DUPLICATE_TEMPLATE: 'Duplicar',
    DELETE_TEMPLATE: 'Eliminar',
    DELETE_TEMPLATE_CONFIRM: '¿Eliminar esta plantilla?',
    SET_AS_DEFAULT: 'Marcar como predeterminada',
    TEMPLATE_DEFAULT_BADGE: 'Predeterminada',
    SAVE_IN_TEMPLATE: 'Guardar en plantilla',
    STEP_SELECT_TITLE: '1. Selecciona productos',
    STEP_SELECT_DESCRIPTION: '¿Para qué productos imprimir etiquetas?',
    STEP_COPIES_TITLE: '2. Copias',
    STEP_COPIES_DESCRIPTION: '¿Cuántas etiquetas por producto?',
    FILTER_TYPE_ALL: 'Todos los productos',
    FILTER_TYPE_CATEGORIES: 'Por categoría',
    FILTER_TYPE_PRODUCTS: 'Por producto',
    SELECT_CATEGORIES_PLACEHOLDER: 'Selecciona categorías',
    SELECT_PRODUCTS_PLACEHOLDER: 'Selecciona productos',
    COPIES_MODE: 'Cantidad de copias',
    COPIES_FIXED: 'Cantidad fija',
    COPIES_STOCK: 'Según existencia actual',
    COPIES_COUNT: 'Copias por producto',
    PRINT_BUTTON: 'Imprimir',
    TEST_PRINT: 'Imprimir prueba',
    NO_PRODUCTS: 'No hay productos para imprimir con esta selección.',
    NO_STOCK: 'Revisa la cantidad del producto: no tiene existencia.',
    PRINT_ERROR: 'No se pudieron generar las etiquetas.',
    NEXT: 'Siguiente',
    BACK: 'Atrás',
  },
  PRICE_HISTORY: {
    TAB_LABEL: 'Historial de precios',
    COL_DATE: 'Fecha',
    COL_SOURCE: 'Origen',
    COL_OPERATION: 'Operación',
    COL_PREVIOUS: 'Precio anterior',
    COL_NEW: 'Precio nuevo',
    COL_DELTA: 'Cambio',
    MANUAL: 'Manual',
    BULK_UPDATE: 'Actualización masiva',
    OP_INCREASE_PERCENT: 'Aumento {{value}}%',
    OP_DECREASE_PERCENT: 'Descuento {{value}}%',
    OP_INCREASE_FIXED: 'Aumento fijo {{value}}',
    OP_DECREASE_FIXED: 'Descuento fijo {{value}}',
    OP_SET_PRICE: 'Fijar {{value}}',
    EMPTY: 'Aún no se han registrado cambios de precio.',
  },
  SADMIN_CRONS: {
    PAGE_TITLE: 'Panel de Cron Jobs',
    PAGE_SUBTITLE:
      'Programación, ejecución manual e historial de las tareas automáticas del sistema.',
    TAB_SCHEDULE: 'Programación',
    TAB_HISTORY: 'Historial',
    COL_NAME: 'Nombre',
    COL_DESCRIPTION: 'Descripción',
    COL_EXPRESSION: 'Cadencia',
    COL_ENABLED: 'Habilitado',
    COL_NEXT_TICK: 'Próxima ejecución',
    COL_LAST_RUN: 'Última ejecución',
    COL_LAST_STATUS: 'Último estado',
    COL_LAST_24H: 'Últimas 24 h',
    COL_ACTIONS: 'Acciones',
    COL_STARTED_AT: 'Iniciado',
    COL_DURATION: 'Duración',
    COL_STATUS: 'Estado',
    COL_TRIGGER: 'Origen',
    COL_ERROR: 'Error',
    COL_TENANT: 'Tenant',
    ACTION_EDIT: 'Editar',
    ACTION_RUN_NOW: 'Ejecutar ahora',
    ACTION_RESET_DEFAULT: 'Restaurar defaults',
    MODAL_EDIT_TITLE: 'Editar programación — {{name}}',
    MODE_PRESET: 'Preset',
    MODE_CUSTOM: 'Personalizado',
    FIELD_PRESET: 'Preset',
    FIELD_EXPRESSION: 'Expresión cron',
    FIELD_ENABLED: 'Habilitado',
    FIELD_REASON: 'Motivo del cambio (opcional)',
    HINT_MIN_INTERVAL: 'Intervalo mínimo: {{seconds}}s.',
    HINT_CUSTOM_NOT_ALLOWED:
      'Este cron requiere un preset — no permite expresiones personalizadas.',
    HINT_NEXT_RUNS: 'Próximas ejecuciones estimadas:',
    STATUS_RUNNING: 'En curso',
    STATUS_SUCCESS: 'Éxito',
    STATUS_ERROR: 'Error',
    STATUS_SKIPPED: 'Omitido',
    TRIGGER_SCHEDULED: 'Programado',
    TRIGGER_MANUAL: 'Manual',
    TOAST_UPDATED: 'Programación actualizada.',
    TOAST_RESET: 'Programación restaurada a los valores por defecto.',
    TOAST_RUN_STARTED: 'Ejecución iniciada.',
    TOAST_CONFLICT:
      'Otro administrador editó este cron. Refresca para ver el estado actual.',
    RUN_NOW_CONFIRM_TITLE: 'Ejecutar {{name}} ahora',
    RUN_NOW_CONFIRM_DESC:
      'La tarea se ejecutará de forma manual, ignorando el estado habilitado/deshabilitado.',
    RESET_CONFIRM_TITLE: 'Restaurar defaults de {{name}}',
    RESET_CONFIRM_DESC:
      'La expresión, el preset y el estado habilitado volverán a los valores del manifiesto de código.',
    EMPTY_RUNS: 'Sin ejecuciones registradas en este rango.',
    FILTER_CRON: 'Cron',
    FILTER_STATUS: 'Estado',
    FILTER_TRIGGER: 'Origen',
    FILTER_ALL: 'Todos',
    RUNTIME_DISABLED_TITLE: 'Ejecución de crons deshabilitada por entorno',
    RUNTIME_DISABLED_DESC:
      'CRON_DISABLED=true en el .env de la API. Los cambios de programación se guardan pero ninguna tarea se ejecutará hasta que se quite la variable y se reinicie la API. Ejecuciones manuales también están bloqueadas.',
  },
  NEOPRINT: {
    OUTDATED_TITLE: 'Hay una versión nueva de neoprint',
    OUTDATED_HINT:
      'La app neoprint instalada en esta terminal es antigua y puede fallar al imprimir. Descarga el instalador y ábrelo para actualizarla.',
    CTA_TITLE: 'Instalar la app neoprint',
    CTA_HINT:
      'Descarga la app para imprimir en silencio desde esta terminal. Se instala en segundos, queda en segundo plano y arranca solo con el sistema.',
    CTA_DOWNLOAD: 'Descargar neoprint (Windows)',
    CTA_INSTRUCTIONS_TITLE: '¿Cómo se instala?',
    CTA_INSTRUCTIONS_STEP_1: '1. Descarga el instalador neoprint-setup.exe.',
    CTA_INSTRUCTIONS_STEP_2:
      '2. Ábrelo con doble clic. Si Windows muestra SmartScreen, elige "Más información" → "Ejecutar de todas formas". No necesita permisos de administrador.',
    CTA_INSTRUCTIONS_STEP_3:
      '3. Listo: neoprint queda en segundo plano (ícono junto al reloj) y arranca solo cada vez que inicias sesión en Windows. No hay que abrir nada más.',
    CTA_NON_WINDOWS_HINT:
      'neoprint solo tiene instalador para Windows y macOS. En otros sistemas ejecuta el binario manualmente.',
    CTA_URL_MISSING:
      'La URL de descarga de neoprint no está configurada (VITE_NEOPRINT_DOWNLOAD_URL). Contacta al administrador.',
    CTA_DOWNLOAD_MAC: 'Descargar neoprint (macOS)',
    CTA_INSTRUCTIONS_MAC_STEP_1:
      '1. Descarga el instalador neoprint-macos.pkg.',
    CTA_INSTRUCTIONS_MAC_STEP_2:
      '2. Ábrelo con doble clic y sigue el asistente (te pedirá tu contraseña de macOS).',
    CTA_INSTRUCTIONS_MAC_STEP_3:
      '3. Listo: neoprint queda en la barra de menú y arranca solo cada vez que inicias sesión. No hay que usar Terminal.',
  },
  SADMIN_RESELLERS: {
    PAGE_TITLE: 'Resellers',
    PAGE_SUBTITLE:
      'Socios revendedores, sus asignaciones de negocio y comisiones acumuladas.',
    CREATE_TITLE: 'Crear reseller',
    EDIT_TITLE: 'Editar reseller',
    DISPLAY_NAME: 'Nombre',
    CONTACT_EMAIL: 'Correo de contacto',
    PHONE: 'Teléfono',
    DEFAULT_CURRENCY: 'Moneda de payout',
    DEFAULT_USERNAME: 'Nombre de usuario',
    DEFAULT_USERNAME_HINT:
      'Este será el usuario que el reseller usará para iniciar sesión. Letras, números y . _ - (3-50 caracteres).',
    USERNAME_CHECKING: 'Comprobando disponibilidad…',
    USERNAME_AVAILABLE: 'Disponible',
    USERNAME_TAKEN: 'Ya está en uso',
    FIRST_NAME: 'Nombre',
    LAST_NAME: 'Apellido',
    DEFAULT_COMMISSION_TYPE: 'Tipo de comisión',
    DEFAULT_COMMISSION_VALUE: 'Valor',
    DEFAULT_COMMISSION_HINT:
      'Comisión por defecto para negocios que este reseller cree. Sadmin puede sobreescribir por negocio después.',
    STATUS: 'Estado',
    STATUS_PENDING: 'Pendiente',
    STATUS_ACTIVE: 'Activo',
    STATUS_SUSPENDED: 'Suspendido',
    STATUS_ARCHIVED: 'Archivado',
    BALANCE_UNPAID: 'Balance sin pagar',
    ACTIONS: 'Acciones',
    ACTION_SUSPEND: 'Suspender',
    ACTION_REACTIVATE: 'Reactivar',
    ACTION_BACKFILL: 'Reprocesar comisiones',
    BACKFILL_SUCCESS: 'Se reprocesaron {{count}} eventos.',
    BACKFILL_DESCRIPTION:
      'Reproduce las comisiones desde la fecha seleccionada. Rangos amplios pueden tardar.',
    BACKFILL_SINCE: 'Desde',
    ACTION_CREATE: 'Crear reseller',
    ACTION_DELETE: 'Eliminar',
    ACTION_RESET_PASSWORD: 'Enviar reset de contraseña',
    ACTION_ASSIGN_BUSINESS: 'Asignar negocio',
    ACTION_END_ASSIGNMENT: 'Finalizar asignación',
    ACTION_EDIT_COMMISSION: 'Editar comisión',
    EDIT_COMMISSION_TITLE: 'Editar comisión de asignación',
    EDIT_COMMISSION_HINT:
      'Se crea una nueva versión de la asignación con los valores ingresados. La versión actual queda cerrada automáticamente; los devengos previos no se recalculan.',
    TOAST_ASSIGNMENT_UPDATED: 'Comisión actualizada',
    UNPAID_ACCRUALS_TITLE: 'Comisiones pendientes — {{amount}}',
    UNPAID_ACCRUALS_HINT:
      'Cada fila es una comisión devengada que aún no se ha liquidado en un payout. Vincula al pago SaaS y factura de origen que la generó.',
    UNPAID_ACCRUALS_EMPTY: 'Sin comisiones pendientes en esta moneda.',
    UNPAID_ACCRUALS_COL_DATE: 'Fecha',
    UNPAID_ACCRUALS_COL_BUSINESS: 'Negocio',
    UNPAID_ACCRUALS_COL_INVOICE: 'Factura',
    UNPAID_ACCRUALS_COL_PROVIDER: 'Proveedor',
    UNPAID_ACCRUALS_COL_BASIS: 'Base',
    UNPAID_ACCRUALS_COL_COMMISSION: 'Comisión',
    ASSIGNMENTS_TITLE: 'Asignaciones',
    ASSIGNMENT_BUSINESS: 'Negocio',
    ASSIGNMENT_COMMISSION: 'Comisión',
    ASSIGNMENT_ACTIVE_FROM: 'Vigente desde',
    ASSIGNMENT_ACTIVE_TO: 'Vigente hasta',
    ASSIGNMENT_ACTIVE_BADGE: 'Activa',
    ASSIGNMENT_ENDED_BADGE: 'Finalizada',
    COMMISSION_TYPE_FIXED: 'Monto fijo',
    COMMISSION_TYPE_PERCENT: 'Porcentaje',
    ASSIGN_BUSINESS_TITLE: 'Asignar negocio al reseller',
    ASSIGN_BUSINESS_HINT:
      'Crea una nueva versión de asignación. Si el negocio ya tiene una asignación activa, se cierra automáticamente.',
    EMPTY_LIST: 'Aún no hay resellers registrados.',
    EMPTY_ASSIGNMENTS: 'Este reseller aún no tiene asignaciones.',
    TOAST_CREATED: 'Reseller creado',
    TOAST_UPDATED: 'Reseller actualizado',
    TOAST_SUSPENDED: 'Reseller suspendido',
    TOAST_REACTIVATED: 'Reseller reactivado',
    TOAST_ASSIGNMENT_CREATED: 'Asignación creada',
    TOAST_ASSIGNMENT_ENDED: 'Asignación finalizada',
    TOAST_DELETED_HARD: 'Reseller eliminado permanentemente.',
    TOAST_DELETED_SOFT:
      'Reseller archivado. Se cerraron {{endedAssignments}} asignación(es) y se desactivaron {{deactivatedUsers}} usuario(s). El historial financiero se conserva.',
    TOAST_PASSWORD_RESET_SENT:
      'Link de reset enviado a {{email}}. Expira en 60 minutos.',
    CONFIRM_PASSWORD_RESET:
      '¿Enviar link de reset de contraseña a {{name}}? Recibirá un email con instrucciones.',
    CONFIRM_END_ASSIGNMENT:
      '¿Finalizar esta asignación? El reseller dejará de acumular comisión sobre nuevos pagos del negocio.',
    CONFIRM_DELETE_HARD_TITLE: '¿Eliminar reseller?',
    CONFIRM_DELETE_HARD_BODY:
      'Este reseller nunca fue activado y no tiene historial. La cuenta se eliminará permanentemente. Esta acción es irreversible.',
    CONFIRM_DELETE_SOFT_TITLE: '¿Archivar reseller?',
    CONFIRM_DELETE_SOFT_BODY:
      'Este reseller tiene historial financiero (comisiones, payouts o asignaciones). Se archivará (bloqueo de login + cierre de asignaciones activas + desactivación de usuarios) y el ledger permanecerá intacto. No hay vuelta atrás.',
    CONFIRM_DELETE_ACCEPT: 'Sí, eliminar',
    CONFIRM_DELETE_CANCEL: 'Cancelar',
    DELETE_ERROR_ALREADY_ARCHIVED: 'Este reseller ya está archivado.',
  },
  SADMIN_RESELLER_PAYOUTS: {
    TITLE: 'Payouts de Resellers',
    SUBTITLE:
      'Balances positivos pendientes de pago por (reseller, moneda). Generar → crea payout en estado draft.',
    EMPTY: 'No hay balances pendientes de pago.',
    BREAKDOWN: 'Bruto: {{gross}} · Refunds: {{refunds}}',
    GENERATE: 'Generar payout',
    CREATED: 'Payout creado en estado draft.',
    ZERO_BALANCE:
      'No se creó payout: balance neto ≤ 0. El ledger conserva los rows para el próximo intento.',
    QUEUE_TITLE: 'Balances pendientes',
    HISTORY_TITLE: 'Historial de payouts',
    HISTORY_EMPTY: 'Aún no hay payouts registrados.',
    RESELLER: 'Reseller',
    NET_AMOUNT: 'Monto neto',
    PAID_AT: 'Fecha pagado',
    STATUS_DRAFT: 'Borrador',
    STATUS_APPROVED: 'Aprobado',
    STATUS_PAID: 'Pagado',
    STATUS_CANCELLED: 'Cancelado',
    STATUS_WRITTEN_OFF: 'Anulado',
    APPROVE: 'Aprobar',
    MARK_PAID: 'Marcar pagado',
    MARK_PAID_TITLE: 'Registrar pago',
    PAYMENT_METHOD: 'Método de pago',
    METHOD_BANK_TRANSFER: 'Transferencia bancaria',
    METHOD_ZELLE: 'Zelle',
    METHOD_OTHER: 'Otro',
    EXTERNAL_REF: 'Referencia externa',
    MEMO: 'Memo',
    CONFIRM_APPROVE_TITLE: '¿Aprobar payout?',
    CONFIRM_APPROVE_BODY:
      'Se aprobará el payout por {{amount}} {{currency}}. Después podrás marcarlo como pagado.',
    CANCEL_TITLE: '¿Cancelar payout?',
    CANCEL_BODY:
      'Se cancelará el payout por {{amount}} {{currency}}. Las comisiones volverán a estado pendiente para el próximo intento.',
    CANCEL_REASON: 'Motivo (opcional)',
    CANCEL_CONFIRM: 'Cancelar payout',
    WRITE_OFF: 'Anular',
    WRITE_OFF_TITLE: '¿Anular payout?',
    WRITE_OFF_BODY:
      'Se anulará el payout por {{amount}} {{currency}} y sus comisiones NO se pagarán ni volverán a la cola. Úsalo solo para fraude, factura duplicada o comisiones que no deban liquidarse.',
    WRITE_OFF_REASON: 'Motivo (obligatorio)',
    WRITE_OFF_REASON_REQUIRED: 'Debes indicar un motivo para anular el payout.',
    WRITE_OFF_CONFIRM: 'Anular payout',
    VOID_ACCRUAL_TITLE: '¿Anular esta comisión?',
    VOID_ACCRUAL_BODY:
      'Se anulará la comisión de {{business}} por {{amount}}. La comisión NO se pagará ni volverá a la cola. Úsalo solo cuando el pago original fue fraude / duplicado o no debe generar comisión.',
    VOID_ACCRUAL_REASON: 'Motivo (obligatorio)',
    VOID_ACCRUAL_REASON_REQUIRED:
      'Debes indicar un motivo para anular la comisión.',
    VOID_ACCRUAL_CONFIRM: 'Anular comisión',
    TOAST_APPROVED: 'Payout aprobado.',
    TOAST_MARKED_PAID: 'Payout marcado como pagado.',
    TOAST_CANCELLED: 'Payout cancelado.',
    TOAST_WRITTEN_OFF: 'Payout anulado y comisiones anuladas.',
    TOAST_ACCRUAL_VOIDED: 'Comisión anulada.',
    ITEMS_TITLE: 'Comisiones incluidas',
    ITEMS_EMPTY: 'Sin comisiones asociadas a este payout.',
    BASIS_LABEL: 'Base {{basis}}',
    QUICK_PAY: 'Pagar ahora',
    QUICK_PAY_TITLE: 'Pagar comisión al reseller',
    QUICK_PAY_DESCRIPTION:
      'Se creará el payout, se aprobará y se marcará como pagado en una sola acción.',
    QUICK_PAY_CONFIRM: 'Pagar',
    TOAST_QUICK_PAY_OK: 'Payout pagado al reseller.',
  },
  SADMIN_DEAD_LETTER: {
    TITLE: 'Dead-letter de comisiones',
    SUBTITLE:
      'Eventos de comisión que fallaron. El cron reintenta cada hora hasta 5 veces; después queda para triage manual.',
    EMPTY: 'Sin rows en la cola.',
    SHOW_RESOLVED: 'Incluir resueltos',
    STATUS_RESOLVED: 'Resuelto',
    STATUS_PENDING: 'Pendiente ({{n}}/5)',
    RETRY: 'Reintentar ahora',
    RESOLVED: 'Row resuelto.',
    STILL_FAILING: 'Reintentar aún falla; revisa el error.',
  },
  HISTORY: {
    TAB_LABEL: 'Historial',
    TITLE: 'Historial de cambios',
    SUBTITLE: 'Todas las modificaciones realizadas a este documento.',
    EMPTY: 'Aún no hay cambios registrados en este documento.',
    LOADING: 'Cargando historial…',
    BEFORE: 'Antes',
    AFTER: 'Después',
    RAW_JSON_TOGGLE: 'Ver JSON crudo',
    ACTOR: 'Por: {{name}}',
    ACTOR_ROLE_SYSTEM: 'Sistema',
    NO_FIELD_CHANGES: 'Sin cambios a nivel de campo.',
    EVENT_CREATED: 'Creado',
    EVENT_UPDATED: 'Actualizado',
    EVENT_STATUS_CHANGED: 'Cambio de estado',
    EVENT_VOIDED: 'Anulado',
    EVENT_CANCELLED: 'Cancelado',
    EVENT_CONVERTED: 'Convertido',
    EVENT_PARTIALLY_RECEIVED: 'Recibido parcial',
    EVENT_RECEIVED: 'Recibido',
    EVENT_PAID: 'Pagado',
    EVENT_REFUNDED: 'Reembolsado',
    EVENT_DELETED_SOFT: 'Eliminado',
    EVENT_HTTP_FALLBACK: 'Cambio API',
    DIFF_ADDED: '+{{count}} agregado(s)',
    DIFF_REMOVED: '-{{count}} eliminado(s)',
    REASON_LABEL: 'Motivo: {{reason}}',
    PERMISSION_DENIED:
      'No tienes permiso para ver el historial de este documento.',
    ACTION_UPDATED_FIELD: 'actualizó {{field}}',
    ACTION_CREATED: 'creó el documento',
    ACTION_DELETED: 'eliminó el documento',
    ACTION_STATUS_CHANGED: 'cambió el estado',
    ACTION_CANCELLED: 'canceló el documento',
    ACTION_VOIDED: 'anuló el documento',
    ACTION_PAID: 'marcó como pagado',
    ACTION_REFUNDED: 'reembolsó el documento',
    ACTION_CONVERTED: 'convirtió el documento',
    ACTION_RECEIVED: 'recibió',
    ACTION_PARTIALLY_RECEIVED: 'recibió parcialmente',
    ACTION_ITEM_ADDED: 'agregó un ítem: {{name}}',
    ACTION_ITEM_REMOVED: 'eliminó el ítem: {{name}}',
    ACTION_ITEM_UPDATED_FIELD: 'actualizó {{field}} en el ítem: {{name}}',
    EMPTY_VALUE: 'Ninguno',
    BEFORE_AFTER: '{{before}} → {{after}}',
  },
  /*
   * Enum VALUES on the Historial. `renderValue` prints strings verbatim, so
   * a code like `credito_15` reached the operator raw. Keyed by field, then
   * by stored value.
   *
   * Only covers fields whose values are codes. A field absent here keeps
   * rendering verbatim, which is right for free text (notes, references).
   */
  HISTORY_VALUES: {
    // Both columns read the same CREDIT_TERM catalog.
    creditTerms: {
      contado: 'Contado',
      contado_delivery: 'Contado contra entrega',
      credito_15: 'Crédito 15 días',
      credito_30: 'Crédito 30 días',
      credito_60: 'Crédito 60 días',
      credito_90: 'Crédito 90 días',
    },
    paymentTerms: {
      contado: 'Contado',
      contado_delivery: 'Contado contra entrega',
      credito_15: 'Crédito 15 días',
      credito_30: 'Crédito 30 días',
      credito_60: 'Crédito 60 días',
      credito_90: 'Crédito 90 días',
      // Retired purchase vocabulary — migration 0981 moved stored values to
      // `credito_*`, but history rows SNAPSHOT what the field held at the
      // time, so old events still carry these and must stay readable.
      net_30: 'Crédito 30 días',
      net_60: 'Crédito 60 días',
      net_90: 'Crédito 90 días',
      custom: 'Personalizado',
    },
  },

  HISTORY_FIELDS: {
    invoiceNumber: 'el número de factura',
    ncf: 'el NCF',
    invoiceType: 'el tipo de comprobante',
    invoiceStatus: 'el estado',
    invoiceDate: 'la fecha del comprobante',
    paymentDate: 'la fecha de pago',
    dueDate: 'la fecha de vencimiento',
    validUntil: 'la validez',
    subtotal: 'el subtotal',
    tax: 'el impuesto',
    taxRate: 'la tasa de impuesto',
    total: 'el total',
    discount: 'el descuento',
    creditTerms: 'el término de pago',
    // Purchases state the same concept under their own column name. Both
    // read the CREDIT_TERM catalog, so both resolve through HISTORY_VALUES
    // below — the Historial used to print "actualizó paymentTerms contado →
    // credito_15", raw field name and raw codes.
    paymentTerms: 'el término de pago',
    shippingCost: 'el costo de envío',
    customerId: 'el cliente',
    customerName: 'el cliente',
    customerRnc: 'el RNC del cliente',
    notes: 'las notas',
    termsAndConditions: 'los términos',
    deliveryFee: 'el cargo de entrega',
    deliveryAddress: 'la dirección de entrega',
    deliveryMethodId: 'el método de entrega',
    deliveryMethodName: 'el método de entrega',
    deliveryDriverId: 'el repartidor',
    deliveryDriverName: 'el repartidor',
    deliveryDriverPhone: 'el teléfono del repartidor',
    creditNoteReason: 'el motivo de la nota de crédito',
    approvalStatus: 'el estado de aprobación',
    manualDiscount: 'el descuento manual',
    branchOfficeId: 'la sucursal',
    cashierId: 'el cajero',
    intendedInvoiceType: 'el tipo de comprobante deseado',
    intendedNcfType: 'el tipo de NCF deseado',
    // Product document fields
    name: 'el nombre',
    sku: 'el SKU',
    barcode: 'el código de barras',
    description: 'la descripción',
    descriptionInvoice: 'la descripción de factura',
    brand: 'la marca',
    productType: 'el tipo de producto',
    status: 'el estado',
    sellingPrice: 'el precio de venta',
    costPrice: 'el costo',
    lastPurchaseCost: 'el último costo de compra',
    priceTiers: 'los niveles de precio',
    taxId: 'el impuesto',
    taxName: 'el impuesto',
    taxType: 'el tipo de impuesto',
    taxIncluded: 'el impuesto incluido',
    categoryId: 'la categoría',
    categoryName: 'la categoría',
    providerName: 'el proveedor',
    warehouseId: 'el almacén',
    warehouseName: 'el almacén',
    unitOfMeasure: 'la unidad de medida',
    minStockLevel: 'el stock mínimo',
    maxStockLevel: 'el stock máximo',
    reorderPoint: 'el punto de reorden',
    requiresSerial: 'el requerimiento de serie',
    allowManualDiscount: 'el descuento manual',
    maxDiscountPercent: 'el descuento máximo',
    discountApprovalRequired: 'la aprobación de descuento',
  } as Record<string, string>,
  HISTORY_ITEM_FIELDS: {
    quantity: 'la cantidad',
    unitPrice: 'el precio unitario',
    discount: 'el descuento',
    discountPercent: 'el porcentaje de descuento',
    taxRate: 'la tasa de impuesto',
    taxAmount: 'el impuesto',
    baseAmount: 'la base imponible',
    total: 'el total',
    productDescription: 'la descripción',
    descriptionInvoice: 'la descripción de factura',
    notes: 'las notas',
    productCode: 'el código',
    unitCost: 'el costo unitario',
    taxIncluded: 'el impuesto incluido',
    taxType: 'el tipo de impuesto',
    quantityOrdered: 'la cantidad',
    quantityReceived: 'la cantidad recibida',
    purchaseUnit: 'la presentación',
    purchaseConversionFactor: 'el factor de conversión',
    productUnitOfMeasure: 'la unidad de medida',
    productId: 'el producto',
    productName: 'el nombre del producto',
    totalAmount: 'el total',
    // Product presentations (units) + recipe ingredients children
    unit: 'la unidad',
    name: 'el nombre',
    conversionToStock: 'el factor de conversión',
    cost: 'el costo',
    price: 'el precio',
    skuSuffix: 'el sufijo de SKU',
    canSell: 'la venta',
    canPurchase: 'la compra',
    canConsume: 'el consumo',
    isDefaultSell: 'la venta por defecto',
    isDefaultPurchase: 'la compra por defecto',
    isDefaultConsume: 'el consumo por defecto',
    sortOrder: 'el orden',
    ingredientProductId: 'el ingrediente',
    ingredientProductName: 'el ingrediente',
    unitName: 'la presentación',
    consumptionUnit: 'la unidad de consumo',
    conversionToStockSnapshot: 'el factor de conversión',
    unitCostSnapshot: 'el costo unitario',
  } as Record<string, string>,
  // Paying a supplier document (supplier invoice or gasto on credit) —
  // shared by Gastos, Cuentas por Pagar, the 606 document page and the
  // payment modal.
  SUPPLIER_DOCUMENT_PAYMENT: {
    REGISTER_PAYMENT: 'Registrar pago',
    PAYMENT_STATUS: 'Estado de Pago',
    PAYING: 'Pagando',
    BALANCE_DUE: 'Saldo pendiente',
    PAYMENT_REGISTERED: 'Pago registrado',
    VOID_ACTION: 'Anular pago',
    VOID_TITLE: '¿Anular el pago?',
    VOID_DESCRIPTION:
      'El saldo del documento se restablece y se registra el asiento inverso. El pago queda en el historial como anulado.',
    VOID_REASON_LABEL: 'Motivo',
    VOID_REASON_PLACEHOLDER: 'Ej. pago duplicado',
    VOIDED: 'Pago anulado',
    VOIDED_BADGE: 'Anulado',
    PAYMENTS: 'Pagos',
    NO_PAYMENTS: 'Sin pagos registrados.',
  },
  SUPPLIER_INVOICE_PAYMENT: {
    STATUS_PENDING_PAYMENT: 'Pendiente de Pago',
    STATUS_PARTIAL: 'Pago Parcial',
    STATUS_PAID: 'Pagada',
    STATUS_CANCELLED: 'Anulada',
    ALL_PAYMENT_STATUSES: 'Todos los estados de pago',
    PAYMENT_STATUS_LABEL: 'Estado de Pago',
    BALANCE_DUE: 'Balance pendiente',
    AMOUNT_PAID: 'Monto pagado',
    PICKER_HINT: 'Solo facturas con balance pendiente',
    SI_KIND_LABEL: 'Factura Proveedor',
    // Void / cancel-preflight dialog
    VOID_DIALOG_TITLE: 'Anular factura de proveedor',
    VOID_DIALOG_UNPAID_DESC:
      'Se anulará la factura {{number}}. Se reversará el inventario y la entrada contable. Esta acción es irreversible.',
    VOID_DIALOG_PAID_TITLE: 'Anular factura pagada',
    VOID_DIALOG_PAID_DESC:
      'La factura {{number}} tiene pagos registrados. Se creará una nota de crédito automáticamente para reversar el balance pendiente.',
    VOID_REVERSE_PAYMENTS: 'También revertir los pagos',
    VOID_REVERSE_PAYMENTS_HINT:
      'Los registros de pago serán eliminados y su asiento contable reversado.',
    VOID_PERIOD_WARNING: 'Períodos contables afectados:',
    VOID_PERIOD_CLOSED: 'cerrado',
    VOID_PERIOD_LOCKED: 'bloqueado',
    VOID_PERIOD_CONSEQUENCE:
      'Este pago cae en un período {{status}} — requiere corrección manual.',
    VOID_LOADING: 'Verificando estado de la factura…',
    VOID_CONFIRM: 'Anular',
  },
};
